import { trackSelfDescribingEvent, CommonEventProperties, SelfDescribingJson } from '@snowplow/browser-tracker';
// Automatically generated by Snowtype

/**
 * Tracks evidence clicked event
 */
export type EvidenceClicked = {
    /**
     * Chat Id
     */
    chat_id?: null | string;
    /**
     * Citation Source
     */
    citation_source?: null | string;
    /**
     * Citation Title
     */
    citation_title?: null | string;
    /**
     * Citation Url
     */
    citation_url?: null | string;
    /**
     * Click Action
     */
    click_action?: null | string;
    /**
     * Evidence Location
     */
    evidence_location?: null | string;
    /**
     * Is Evidence Pro
     */
    is_evidence_pro?: boolean | null;
    /**
     * Is Managed
     */
    is_managed?: boolean | null;
    /**
     * Is Team Member
     */
    is_team_member?: boolean | null;
    /**
     * Region
     */
    region?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the source control popover is toggled
 */
export type EvidenceSourceControlToggled = {
    /**
     * Number of active source filters when toggled
     */
    active_filters_count?: number | null;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether the popover is now open or closed
     */
    is_open?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * What triggered the toggle (manual, auto)
     */
    trigger?: null | string;
    [property: string]: any;
}

/**
 * Tracks integration submitted event
 */
export type IntegrationSubmitted = {
    /**
     * Integration Type
     */
    integration_type?: null | string;
    /**
     * Request Type
     */
    request_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks document printed event
 */
export type DocumentPrinted = {
    /**
     * Document Id
     */
    document_id?: null | string;
    /**
     * Is Pinned Template
     */
    is_pinned_template?: boolean | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a message fails with an error
 */
export type MessageFailed = {
    /**
     * Reason for attachment errors
     */
    attachment_reason?: null | string;
    /**
     * Block type for block-level errors
     */
    block_type?: null | string;
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Time until error occurred in milliseconds
     */
    duration_ms?: number | null;
    /**
     * Error code if available
     */
    error_code?: null | string;
    /**
     * Error message
     */
    error_message?: null | string;
    /**
     * Discriminates which error path fired this event
     */
    error_type?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * HTTP status code when available
     */
    http_status?: number | null;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Backend message ID (msg_*) of the in-flight response that failed. Present for
     * stream-phase errors (once message_info was emitted); null for pre-stream errors
     * (create_chat, attachment, block, stream_start). Joins to the backend
     * evidence.stream.outcome for the same request.
     */
    message_id?: null | string;
    /**
     * Query length in characters
     */
    query_length?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Source of the message event (evidence, ask_heidi)
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when integration data is successfully retrieved
 */
export type IntegrationDataRetrieved = {
    /**
     * Type of data retrieved from the integration
     */
    data_type?: null | string;
    /**
     * Type of integration
     */
    integration_type?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an AI objective execution completes successfully
 */
export type ObjectiveExecutionCompleted = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Objective execution duration in seconds
     */
    duration_seconds: number;
    /**
     * Unique objective identifier
     */
    objective_id: string;
    /**
     * Number of conversation turns during execution
     */
    turn_count: number;
    [property: string]: any;
}

/**
 * Fired when the desktop app auto-updater fails during the check or install step. Covers
 * the region-aware updater (APP-6113, is_regional=true) and the legacy global updater
 * (is_regional=false).
 */
export type DesktopUpdateFailed = {
    /**
     * Machine error code from UpdaterBridgeError.errorCode (Rust SCREAMING_SNAKE_CASE); used
     * for dashboard grouping
     */
    error_code?: null | string;
    /**
     * Raw error.message for debugging; never surfaced to users
     */
    error_message?: null | string;
    /**
     * Currently installed app version
     */
    from_version?: null | string;
    /**
     * Whether the region-aware updater path was active (IS_REGIONAL_UPDATER_ENABLED). true
     * isolates compliance-sensitive regional failures from the legacy global updater
     */
    is_regional: boolean;
    /**
     * Which updater effect's catch block fired. Known values: check (update-check step),
     * install (download/install step)
     */
    phase: DesktopUpdateFailedPhase;
    /**
     * Resolved updater region (resolveUpdaterRegion); null on the legacy global path. Known
     * values: UK, CA, US, AU, EU, JP-AZ1, AU-AZ1, UAE-AZ1, US-AZ1, QAT-AZ1
     */
    region?: Region | null;
    /**
     * Target update version; known on install, null on check
     */
    to_version?: null | string;
}

/**
 * Which updater effect's catch block fired. Known values: check (update-check step),
 * install (download/install step)
 *
 * Which updater step skipped. Known values: check (update-check step), install
 * (download/install step)
 */
export type DesktopUpdateFailedPhase = "check" | "install";

export type Region = "UK" | "CA" | "US" | "AU" | "EU" | "JP-AZ1" | "AU-AZ1" | "UAE-AZ1" | "US-AZ1" | "QAT-AZ1";

/**
 * Tracks when a share button is clicked
 */
export type ShareButtonClicked = {
    /**
     * Screen where the share button was clicked
     */
    screen_name?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Type of share
     */
    share_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks force update deferred event
 */
export type ForceUpdateDeferred = {
    /**
     * Current Version
     */
    current_version?: null | string;
    /**
     * Deadline At
     */
    deadline_at?: null | string;
    /**
     * Min Supported Version
     */
    min_supported_version?: null | string;
    [property: string]: any;
}

/**
 * Tracks state transitions for asynchronous and background processes
 */
export type ProcessLifecycle = {
    /**
     * Duration in milliseconds on terminal transitions
     */
    duration_ms?: number | null;
    /**
     * Opaque root entity identifier; patient extraction uses the non-reversible review_id
     */
    entity_id?: string;
    /**
     * Type of entity used to correlate this process
     */
    entity_type?: EntityTypeEnum;
    /**
     * Safe coded error classification on failed transitions; never a raw error message
     */
    error_type?: ErrorTypeEnum | null;
    /**
     * Lifecycle state entered by the process
     */
    process_status: ProcessStatus;
    /**
     * Controlled identifier for the process
     */
    process_type: ProcessType;
    /**
     * Controlled terminal result; patient extraction uses fields_found or no_fields_found
     */
    result_type?: ResultTypeEnum | null;
}

/**
 * Task key (e.g. detail, artefacts, evidence)
 *
 * A single evaluation error type belonging to the event's stage
 */
export type EntityTypeEnum = "patient_extraction_review";

/**
 * Task key (e.g. detail, artefacts, evidence)
 *
 * A single evaluation error type belonging to the event's stage
 */
export type ErrorTypeEnum = "patient_profile_not_found" | "no_content_found" | "parsing_error" | "unexpected_error";

/**
 * Lifecycle state entered by the process
 */
export type ProcessStatus = "started" | "completed" | "failed" | "paused" | "resumed" | "cancelled" | "updated" | "ping";

/**
 * Controlled identifier for the process
 */
export type ProcessType = "transcription" | "note_generation" | "backup_upload" | "session_sync" | "audio_upload" | "context_upload" | "personalisation" | "code_processing" | "tool_call" | "form_fill" | "document_generation" | "app_update_check" | "schedule_upload" | "patient_extraction" | "compliance_check" | "page_heartbeat";

/**
 * Task key (e.g. detail, artefacts, evidence)
 *
 * A single evaluation error type belonging to the event's stage
 */
export type ResultTypeEnum = "fields_found" | "no_fields_found";

/**
 * Fires for any step in an onboarding or setup flow, including sub-step task interactions
 * inside a step (note styling, evidence, sandbox documents)
 */
export type OnboardingStep = {
    /**
     * step_name note_style only. Whether the user turned abbreviations on for the sample note.
     */
    abbreviations_enabled?: boolean | null;
    /**
     * step_name document_created only. Slug of the sandbox document generated (e.g.
     * referral_letter).
     */
    document_type?: null | string;
    /**
     * Milliseconds spent on the enclosing step before this action
     */
    duration_ms?: number | null;
    /**
     * Which onboarding flow (main_onboarding | product_tour | demo | hardware_setup |
     * comms_setup | evidence_onboarding | book_demo | coach_mark | learn_basics |
     * template_onboarding). learn_basics is the guided tour inside onboarding-v2; product_tour
     * is the in-app tour outside onboarding.
     */
    flow_name: string;
    /**
     * step_name evidence_results only. Number of evidence items in the decision.
     */
    items_count?: number | null;
    /**
     * Nested arm inside onboarding_variant experimental-2 (guided_tour | sample_conversation).
     * Absent outside the guided-tour eligible cohort, so its presence also marks cohort
     * membership.
     */
    onboarding_sub_variant?: null | string;
    /**
     * A/B variant for onboarding-v2 flows (e.g. control, experimental, experimental-2).
     */
    onboarding_variant?: null | string;
    /**
     * step_name evidence_queried only. Character count of the question. Stands in for the raw
     * query text, which is never sent — this is a pre-consent flow.
     */
    query_length?: number | null;
    /**
     * step_name evidence_queried only. Whether the question came from a suggested prompt or was
     * typed (suggested | custom).
     */
    query_type?: null | string;
    /**
     * Onboarding funnel identifier (e.g. dictate_onboarding, onboarding_v2). Disambiguates
     * parallel onboarding flows sharing this event.
     */
    source?: null | string;
    /**
     * What happened at this step (viewed | started | completed | skipped | dismissed |
     * abandoned | updated | error_viewed | variant_viewed | reviewed).
     */
    step_action: string;
    /**
     * Zero-based position of the step in the visible flow order
     */
    step_index?: number | null;
    /**
     * Specific step identifier (e.g. note_style, evidence_queried, evidence_results,
     * document_created, documents_selected). Absent when the action applies to the flow as a
     * whole rather than one step within it.
     */
    step_name?: null | string;
    /**
     * flow-level completion only. Keys of the tasks the user actually exercised, so an arm can
     * be compared on which tasks it drove, not just how many.
     */
    tasks_completed?: string[] | null;
    /**
     * flow-level completion only. Depth-of-engagement dimension, duplicated from
     * tasks_completed so it can be aggregated without unnesting the array. Matches the property
     * of the same name on onboarding_step_skipped.
     */
    tasks_completed_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks when transcription is stopped or paused
 */
export type TranscriptionStopped = {
    /**
     * Duration of the recording in seconds
     */
    duration_seconds?: number | null;
    /**
     * Integration type if triggered from an integration
     */
    integration_type?: null | string;
    /**
     * Whether recording v2 is enabled
     */
    is_recording_v2_enabled?: boolean | null;
    /**
     * Whether tab sharing is active
     */
    is_tab_sharing?: boolean | null;
    /**
     * Platform for mobile integrations
     */
    platform?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Transcription mode
     */
    transcribe_mode?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a new share link is generated
 */
export type ShareLinkGenerated = {
    /**
     * Access level for the generated link
     */
    access_level?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Type of share
     */
    share_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user views, clicks, or dismisses a Work / Agents paywall
 */
export type WorkUpgradeActioned = {
    /**
     * Paywall interaction (viewed, cta_clicked, or dismissed)
     */
    action: WorkUpgradeActionedAction;
    /**
     * Account SKU string (e.g. pro, pro_plus, clinician_pro). Not an enum; SKUs change.
     */
    current_plan?: null | string;
    /**
     * This user's seat type (assistant, standard, or max)
     */
    current_seat_type?: TSeatType | null;
    /**
     * Why the paywall appeared (quota, desktop_required, cua_blocked, or settings)
     */
    entry_point?: WorkUpgradeActionedEntryPoint | null;
    /**
     * Whether the upgrade is gated by seat availability
     */
    is_seat_gated?: boolean | null;
    /**
     * Seats on the billing account (allowed on upgrade surfaces)
     */
    seat_count?: number | null;
    /**
     * Seats currently in use
     */
    seats_used?: number | null;
    /**
     * UI surface where the paywall appeared
     */
    surface?: null | string;
    /**
     * Target account SKU the paywall is promoting
     */
    target_plan?: null | string;
    /**
     * Seat type the paywall is promoting (assistant, standard, or max)
     */
    target_seat_type?: TSeatType | null;
}

/**
 * Paywall interaction (viewed, cta_clicked, or dismissed)
 *
 * Suggestion interaction (viewed, cta_clicked, or dismissed)
 */
export type WorkUpgradeActionedAction = "viewed" | "cta_clicked" | "dismissed";

export type TSeatType = "assistant" | "standard" | "max";

export type WorkUpgradeActionedEntryPoint = "quota" | "desktop_required" | "cua_blocked" | "settings";

/**
 * Tracks when note is selected
 */
export type NoteSelected = {
    /**
     * IDs of candidate notes available for selection
     */
    candidate_note_ids?: string[] | null;
    /**
     * Experiment name
     */
    experiment_name?: null | string;
    /**
     * Note identifier
     */
    note_id?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Template identifier
     */
    template_id?: null | string;
    /**
     * Note type
     */
    type?: null | string;
    /**
     * Untyped experiment name from backend
     */
    untyped_experiment_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks a successful patient-summary email dispatch in the send-to-patient flow
 */
export type PatientSummaryEmailSent = {
    /**
     * Document Id
     */
    document_id?: null | string;
    /**
     * Allowlist pattern the document name matched (e.g. patient summary, discharge summary);
     * null when unmatched. Bounded non-PII set — never the raw clinician-entered title
     */
    document_name_pattern?: null | string;
    /**
     * Patient-facing document type used in the email copy (visit summary | document)
     */
    document_type?: null | string;
    /**
     * Whether the session had a linked patient profile when the send flow opened. Surfaced from
     * the frontend rather than resolved from a patient profile id server-side
     */
    had_patient_profile?: boolean | null;
    /**
     * Which affordance launched the send flow (canvas_chip | more_menu | share_sheet |
     * split_button_dropdown). canvas_chip is the patient-summary send chip on the document
     * canvas; more_menu is the kebab menu on a generated document; share_sheet is the mobile
     * session-detail share bottom sheet entry point — an in-app Compose bottom sheet, not the
     * OS share sheet; split_button_dropdown is the send item inside the desktop split button
     * chevron dropdown. Carried on the send events themselves so "which path do clinicians
     * prefer" needs no join back to the opening button_clicked. Bounded non-PII set; null when
     * the opener declared none
     */
    location?: null | string;
    /**
     * How the recipient details were obtained (profile | profile_edited | manual). profile is a
     * linked profile's prefill sent unedited; profile_edited is a prefill the clinician
     * amended; manual is hand-entered with no linked profile. Distinguishes "we had data and it
     * was right" from "we had data and it was wrong". Bounded non-PII set
     */
    patient_details_source?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Send-to-patient flow variant the event belongs to (e.g. patient_m0_email_only). Bounded
     * non-PII set used to compare M0 email-only against future SMS/email variants; null when
     * unset
     */
    variant?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user clicks a reference to scroll to its inline citation
 */
export type EvidenceReferenceScrollClicked = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Library collection(s) the document belongs to (only set when source_type is user_document)
     */
    collections?: string[] | null;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Backend message ID (msg_*) of the assistant response whose reference was clicked. Same
     * join key as evidence_source_clicked so scroll engagement attributes to the producing
     * model.
     */
    message_id?: null | string;
    /**
     * Position of the reference in the list
     */
    reference_index?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Whether a matching inline citation was found and scrolled to
     */
    scroll_succeeded?: boolean | null;
    /**
     * Domain of the source
     */
    source_domain?: null | string;
    /**
     * Display title of the reference
     */
    source_title?: null | string;
    /**
     * Citation source classification (e.g. web_search, user_document, integration_partner)
     */
    source_type?: null | string;
    /**
     * URL of the source
     */
    source_url?: null | string;
    [property: string]: any;
}

/**
 * Tracks snippets uploaded event
 */
export type SnippetsUploaded = {
    /**
     * Count
     */
    count?: number | null;
    /**
     * Source
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks context previewed event
 */
export type ContextPreviewed = {
    /**
     * Context Id
     */
    context_id?: null | string;
    /**
     * Context Type
     */
    context_type?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks export updated event
 */
export type ExportUpdated = {
    /**
     * Scope
     */
    scope?: null | string;
    /**
     * Team Id
     */
    team_id?: null | string;
    /**
     * Update Action
     */
    update_action?: null | string;
    [property: string]: any;
}

/**
 * Fired when a bulk audio upload fails during processing
 */
export type TranslatorBulkUploadFailed = {
    /**
     * Bulk upload identifier
     */
    bulk_upload_id: string;
    /**
     * Machine-readable error code categorising the failure
     */
    error_code?: TranslatorBulkUploadFailedErrorCode | null;
    /**
     * The processing stage where the failure occurred
     */
    failed_stage: FailedStage;
    /**
     * MIME type of the uploaded audio file
     */
    file_mime_type?: null | string;
    /**
     * Size of the uploaded file in bytes
     */
    file_size_bytes?: number | null;
    /**
     * Translator session identifier
     */
    session_id: string;
    /**
     * Total wall-clock processing time before failure in milliseconds
     */
    total_duration_ms?: number | null;
}

export type TranslatorBulkUploadFailedErrorCode = "no_speech_detected" | "recording_too_short" | "unsupported_format" | "invalid_audio" | "message_persist_failed" | "unknown";

/**
 * The processing stage where the failure occurred
 */
export type FailedStage = "diarization" | "audio_split" | "processing";

/**
 * Tracks document shared event
 */
export type DocumentShared = {
    /**
     * Document Id
     */
    document_id?: null | string;
    /**
     * Is Pinned Template
     */
    is_pinned_template?: boolean | null;
    /**
     * Is Starter Pack Template
     */
    is_starter_pack_template?: boolean | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the post-note-generation Evidence summary suggestion banner is shown
 */
export type EvidenceSummarySuggestionShown = {
    /**
     * Relevance score used to decide whether to show the banner
     */
    score?: number | null;
    /**
     * Scribe session identifier
     */
    session_id?: null | string;
    /**
     * Signal names that contributed to the suggestion score
     */
    signals?: string[] | null;
    [property: string]: any;
}

/**
 * Fired when a new translator session is created
 */
export type TranslatorSessionCreated = {
    /**
     * Snapshot of user preference for auto-reading translations
     */
    auto_read_translations?: null | string;
    /**
     * Snapshot of user preference for clinician gender
     */
    clinician_gender?: null | string;
    /**
     * Language code for the clinician, e.g. en
     */
    clinician_language: string;
    /**
     * Whether a patient age was provided
     */
    has_patient_age: boolean;
    /**
     * Whether a patient date of birth was provided
     */
    has_patient_dob: boolean;
    /**
     * Whether a patient gender was provided
     */
    has_patient_gender: boolean;
    /**
     * Whether a patient name was provided
     */
    has_patient_name: boolean;
    /**
     * Whether a reason for visit was provided
     */
    has_reason_for_visit: boolean;
    /**
     * Language code for the patient, e.g. vi
     */
    patient_language: string;
    /**
     * Patient type for this session (new or returning)
     */
    patient_type?: null | string;
    /**
     * Recording mode for the session (defaults to ACCURATE if unset)
     */
    recording_mode: RecordingMode;
    /**
     * Translator session identifier
     */
    session_id: string;
    /**
     * Snapshot of user preference for translation model
     */
    translation_model?: null | string;
}

/**
 * Recording mode for the session (defaults to ACCURATE if unset)
 *
 * Recording mode active when the message was captured; omitted when not applicable
 */
export type RecordingMode = "ACCURATE" | "HANDS_FREE";

/**
 * Tracks replacements uploaded event
 */
export type ReplacementsUploaded = {
    /**
     * Count
     */
    count?: number | null;
    /**
     * Source
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the user accepts the silent-capture warning's one-click fix
 */
export type AudioWarningAccepted = {
    /**
     * Whether the warning carried a one-click fix (always true for accepts)
     */
    actionable?: boolean | null;
    /**
     * Matched telehealth app for actionable speaker warnings (e.g. "AircallWorkspace"). Null
     * when no app was matched.
     */
    app_name?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Which capture channel was silent (one of: silent_speaker, silent_mic)
     */
    warning_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks a recipient search performed while addressing a letter
 */
export type LetterRecipientSearched = {
    /**
     * Letter being addressed
     */
    letter_id: string;
    /**
     * Character count of the search query; the query text is excluded as potential PHI
     */
    query_length: number;
    /**
     * Recipient field where the search was performed
     */
    recipient_role: RecipientRole;
    /**
     * Number of recipient results returned
     */
    results_count: number;
    /**
     * Session containing the letter
     */
    session_id: string;
}

/**
 * Recipient field where the search was performed
 *
 * Whether the removed recipient was primary or a copy recipient
 *
 * Whether the recipient is the primary recipient or a copy recipient
 */
export type RecipientRole = "primary" | "copy";

/**
 * Tracks evidence suggestion clicked event
 */
export type EvidenceSuggestionClicked = {
    /**
     * Category
     */
    category?: null | string;
    /**
     * Evidence Location
     */
    evidence_location?: null | string;
    /**
     * Is Evidence Pro
     */
    is_evidence_pro?: boolean | null;
    /**
     * Is Managed
     */
    is_managed?: boolean | null;
    /**
     * Is Team Member
     */
    is_team_member?: boolean | null;
    /**
     * Question
     */
    question?: null | string;
    /**
     * Region
     */
    region?: null | string;
    [property: string]: any;
}

/**
 * Tracks session updated event
 */
export type SessionUpdated = {
    /**
     * Session update action
     */
    action?: null | string;
    /**
     * Created Time Local
     */
    created_time_local?: null | string;
    /**
     * Microphone
     */
    microphone?: null | string;
    /**
     * Note structure
     */
    note_structure?: null | string;
    /**
     * For update_action="output_source_changed" (desktop speaker/output selection) — the
     * selected device name, app display name (e.g. "AircallWorkspace"), "system_default", or
     * "system_wide" (whole-system "System audio" capture).
     */
    output_source?: null | string;
    /**
     * Kind of output source selected (one of: device, app, system_default, system_wide).
     */
    output_source_type?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Update Action
     */
    update_action?: null | string;
    /**
     * New value after the update. For update_action="session_status_updated" this is the new
     * SessionStatus (one of: EMPTY, DRAFT, REVIEWED, APPROVED, SENT).
     */
    value?: null | string;
    /**
     * Voice setting
     */
    voice?: null | string;
    /**
     * Word Count
     */
    word_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks when an inline field save succeeds on the patient profile
 */
export type PatientFieldEdited = {
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown.
     */
    feature_flag_variant?: null | string;
    /**
     * Name of the field that was edited. Field name only — never the value (PII). Known values:
     * dob, name, gender, pronouns, allergies, medications, medical_history. FE may extend
     * without a schema bump.
     */
    field?: null | string;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * UI surface that produced the event. Known values: drawer, full_page.
     */
    surface?: null | string;
    /**
     * UUID of the patient profile view this event belongs to. Inherited from
     * patient_profile_opened.
     */
    view_id?: null | string;
}

/**
 * Tracks when note or Evidence chat content is copied to the clipboard. PHI-free — carries
 * no copied body. surface/chat_id/message_id attribute Evidence copies to the producing
 * response so copy adoption can be compared with note_insert_applied.
 */
export type NoteCopied = {
    /**
     * Chat identifier the copied content came from
     */
    chat_id?: null | string;
    /**
     * Type of copy action (e.g. ask_ai_copy, evidence_copy, paste_note_section, paste_note_full)
     */
    copy_action?: null | string;
    /**
     * Backend message ID (msg_*) of the assistant response being copied. Joins the copy to the
     * specific model that produced the response.
     */
    message_id?: null | string;
    /**
     * Chat surface mode when known (e.g. evidence)
     */
    mode?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Evidence copy button surface — reply_actions is the per-response copy button; user_notes
     * is the copy button on a usernotes draft block
     */
    surface?: NoteCopiedSurface;
    [property: string]: any;
}

/**
 * Evidence copy button surface — reply_actions is the per-response copy button; user_notes
 * is the copy button on a usernotes draft block
 *
 * Which copy button surface succeeded — reply_actions is the per-response copy button;
 * user_notes is the copy button on a usernotes draft block
 *
 * Which copy button surface triggered the gate
 */
export type NoteCopiedSurface = "reply_actions" | "user_notes";

/**
 * Tracks feedback updated event
 */
export type FeedbackUpdated = {
    /**
     * Update Action
     */
    update_action?: null | string;
    /**
     * Value
     */
    value?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a subscription is created
 */
export type SubscriptionCreated = {
    /**
     * Subscription amount
     */
    amount?: number | null;
    /**
     * Billing period (month/year)
     */
    billing_period?: null | string;
    /**
     * Currency code
     */
    currency?: null | string;
    /**
     * Product the subscription is for (e.g. scribe, evidence)
     */
    feature?: null | string;
    /**
     * Subscription plan type
     */
    plan_type?: null | string;
    /**
     * Subscription identifier
     */
    subscription_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when user account details are updated
 */
export type AccountUpdated = {
    /**
     * Account update action
     */
    action?: null | string;
    [property: string]: any;
}

/**
 * Tracks presentations and outcomes of the UK/EU template quality attestation gate
 */
export type TemplateQualityGate = {
    /**
     * Lifecycle point for one gate evaluation. shown = the dialog rendered. accepted / declined
     * = the clinician's decision. superseded = a second save replaced this one while it waited,
     * so it is not a clinician decline. bypassed = the kill switch suppressed a save that would
     * otherwise have been gated, so any bypassed event means the control is off. Out-of-region
     * saves emit nothing at all: the control does not apply there, so its absence is not a gap,
     * and reporting it would outnumber the gated traffic several times over and bury the switch.
     */
    gate_status?: null | string;
    /**
     * Whether the break-glass kill switch was suppressing the gate. Only ever true alongside
     * gate_status bypassed; on every other status the control was live by definition.
     */
    is_kill_switch_on?: boolean | null;
    /**
     * Whether user_region is trustworthy (resolved | pending | error). The gate treats pending
     * and error as in-region so an unresolved region prompts rather than waving a save through,
     * so this is required to tell a genuine UK/EU presentation from a fail-open one.
     */
    region_status?: null | string;
    /**
     * Entry point whose save triggered the gate (template_builder | legacy_template_editor |
     * onboarding | visibility | ask_heidi_approval | ask_heidi_approval_overlay). The two
     * ask_heidi values are separate because the overlay is a distinct desktop WebView with its
     * own dialog host.
     */
    surface?: null | string;
    /**
     * Heidi template identifier where one exists at gate time. Absent for pure creates and for
     * agent-tool approvals, which run before an id exists. Never the template name, which
     * clinicians write freely and can carry a patient identifier.
     */
    template_id?: null | string;
    /**
     * Authoritative Kinde region for the user. Deliberately not the `region` property found on
     * other events, which is derived from a spoofable source that must not gate a
     * medical-device decision. Null when the region has not resolved. SE-AZ1 and CH are out of
     * scope for this control by design and will show no gate events.
     */
    user_region?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a recipient is removed from a letter
 */
export type LetterRecipientRemoved = {
    /**
     * Letter being addressed
     */
    letter_id: string;
    /**
     * Whether the removed recipient was primary or a copy recipient
     */
    recipient_role: RecipientRole;
    /**
     * Persisted letter recipient variant
     */
    recipient_type: RecipientType;
    /**
     * Session containing the letter
     */
    session_id: string;
}

/**
 * Persisted letter recipient variant
 */
export type RecipientType = "sealed_message_delivery" | "email" | "directory" | "fax";

/**
 * Tracks team member accepted event
 */
export type TeamMemberAccepted = {
    /**
     * Acceptance Action
     */
    acceptance_action?: null | string;
    /**
     * Email of invited team member
     */
    invited_email?: null | string;
    /**
     * Join Method
     */
    join_method?: null | string;
    /**
     * Team Id
     */
    team_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a community template request is approved
 */
export type CommunityAccepted = {
    /**
     * Template identifier
     */
    template_id?: null | string;
    /**
     * Template revision name
     */
    template_revision_name?: null | string;
    /**
     * Template revision number
     */
    template_revision_number?: number | null;
    [property: string]: any;
}

/**
 * Tracks when the pre-chart tab becomes active in a session
 */
export type PrechartViewed = {
    /**
     * The pre-chart document the tab is rendering. Resolved from the active tab entity
     * (getTabEntityId(prechartTabItem)).
     */
    document_id?: null | string;
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown. Pre-GA the pre-chart surface is fully behind release-prechart so every event
     * will stamp treated or unknown; kept as a property anyway for cheap consistency when an
     * A/B rollout starts later.
     */
    feature_flag_variant?: null | string;
    /**
     * Session the view occurred in
     */
    session_id?: null | string;
    /**
     * The template the pre-chart was generated with, if any. Lets the headline funnel slice
     * "did using a custom template affect view-to-context conversion". Null when no template
     * was applied.
     */
    template_id?: null | string;
    /**
     * How the view was triggered. Known values: manual (real click — engagement signal),
     * auto_default (tab auto-selected on session open — exposure baseline). Engagement
     * dashboards should filter to manual; auto_default is the denominator for "did clinicians
     * ever click around to it" cohort questions. Named view_trigger rather than trigger to
     * disambiguate from prechart_generation_completed.generation_trigger.
     */
    view_trigger?: null | string;
}

/**
 * Tracks user started event
 */
export type UserStarted = {
    /**
     * Source
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user changes their Light / Dark / System appearance preference
 */
export type AppearanceModeChanged = {
    /**
     * Where the user changed appearance mode from
     */
    entry_point: AppearanceModeChangedEntryPoint;
    /**
     * Whether this is the first time the user set an appearance preference on this device
     */
    is_first_change_on_device: boolean;
    /**
     * The appearance mode the user selected
     */
    mode: Mode;
    /**
     * The appearance mode before the change
     */
    previous_mode: Mode;
    /**
     * The theme the user actually sees after the change (system mode resolves to light or dark)
     */
    resolved_theme: ResolvedTheme;
}

/**
 * Where the user changed appearance mode from
 */
export type AppearanceModeChangedEntryPoint = "user_menu" | "spotlight";

/**
 * The appearance mode the user selected
 *
 * The appearance mode before the change
 */
export type Mode = "light" | "dark" | "system";

/**
 * The theme the user actually sees after the change (system mode resolves to light or dark)
 */
export type ResolvedTheme = "light" | "dark";

/**
 * Fired when a session tab stops being active, reporting how long it was on screen.
 */
export type SessionTabViewed = {
    /**
     * The document rendered in the tab. Absent for non-document tabs.
     */
    document_id?: null | string;
    /**
     * Milliseconds the tab was the active one. Never below 750.
     */
    duration_ms: number;
    /**
     * switched = the clinician moved to another tab. unmounted = the session page went away
     * (route change, close, reload).
     */
    exit_reason: ExitReason;
    /**
     * The session the tab belongs to.
     */
    session_id: string;
    /**
     * Kind of tab (context, note, transcript, document, letter, research_note, session_merge).
     * Free-form string so a new tab kind needs no schema bump.
     */
    tab_type: string;
}

/**
 * switched = the clinician moved to another tab. unmounted = the session page went away
 * (route change, close, reload).
 */
export type ExitReason = "switched" | "unmounted";

/**
 * Fired when a new translated message is created in a session
 */
export type TranslatorMessageCreated = {
    /**
     * Bulk upload identifier; only set when source is bulk_upload
     */
    bulk_upload_id?: null | string;
    /**
     * Translator message identifier
     */
    message_id: string;
    /**
     * Character count of the original (source language) text
     */
    original_text_length: number;
    /**
     * Recording mode active when the message was captured; omitted when not applicable
     */
    recording_mode?: RecordingMode;
    /**
     * Translator session identifier
     */
    session_id: string;
    /**
     * How the message was created
     */
    source: TranslatorMessageCreatedSource;
    /**
     * Source language code for the original message, e.g. en
     */
    source_language: string;
    /**
     * Who spoke the message
     */
    speaker: Speaker;
    /**
     * Client-measured speech-to-text processing time in milliseconds
     */
    stt_duration_ms?: number | null;
    /**
     * Identifier of the speech-to-text model used
     */
    stt_model?: null | string;
    /**
     * Target language code for the translated message, e.g. vi
     */
    target_language: string;
    /**
     * Character count of the translated (target language) text
     */
    translated_text_length: number;
    /**
     * Client-measured translation processing time in milliseconds
     */
    translation_duration_ms?: number | null;
    /**
     * Identifier of the translation model used
     */
    translation_model?: null | string;
    /**
     * Client-measured text-to-speech processing time in milliseconds
     */
    tts_duration_ms?: number | null;
    /**
     * Identifier of the text-to-speech model used
     */
    tts_model?: null | string;
}

/**
 * How the message was created
 */
export type TranslatorMessageCreatedSource = "live" | "bulk_upload" | "onboarding";

/**
 * Who spoke the message
 */
export type Speaker = "patient" | "clinician" | "heidi";

/**
 * Tracks evidence onboarding dismissed event
 */
export type EvidenceOnboardingDismissed = {
    /**
     * Source that triggered the event (e.g. frontend, backend)
     */
    event_source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user applies a filter in the inbox
 */
export type InboxFilterApplied = {
    /**
     * Type of filter applied
     */
    filter_type: string;
    /**
     * Value of the filter (null when filter is cleared)
     */
    filter_value?: null | string;
    [property: string]: any;
}

/**
 * Tracks evidence onboarding skipped event
 */
export type EvidenceOnboardingSkipped = {
    /**
     * Source that triggered the event (e.g. frontend, backend)
     */
    event_source?: null | string;
    [property: string]: any;
}

/**
 * Tracks a queue drain deferred because a required prerequisite is unavailable
 */
export type OfflineSyncDeferred = {
    /**
     * Backend reachability state at deferral time
     */
    connectivity_state: State;
    /**
     * Offline recording feature-flag state
     */
    feature_flag_state: FeatureFlagState;
    /**
     * Number of recordings pending when the drain was deferred
     */
    pending_count: number;
    /**
     * Bounded reason the drain could not start or continue
     */
    reason: OfflineSyncDeferredReason;
    /**
     * Recording source
     */
    source: OfflineSyncDeferredSource;
}

/**
 * Backend reachability state at deferral time
 *
 * Connectivity state before the transition
 *
 * Backend reachability state when the drain started
 */
export type State = "unknown" | "offline" | "backend_unreachable" | "online_ready_to_sync";

/**
 * Offline recording feature-flag state
 */
export type FeatureFlagState = "enabled" | "disabled" | "unknown";

/**
 * Bounded reason the drain could not start or continue
 */
export type OfflineSyncDeferredReason = "token_unavailable" | "identity_unavailable" | "app_data_unavailable" | "backend_unreachable" | "region_unresolved";

/**
 * Recording source
 */
export type OfflineSyncDeferredSource = "offline_recording";

/**
 * Tracks when an AI objective hands off to a human agent
 */
export type ObjectiveHandoffTriggered = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * SHA-256 hash of the handoff destination phone number
     */
    handoff_phone_hash?: null | string;
    /**
     * Reason for the handoff
     */
    handoff_reason: string;
    /**
     * Unique objective identifier
     */
    objective_id: string;
    [property: string]: any;
}

/**
 * Tracks when the patient recap hover popover opens on the in-session linked-patient chip
 */
export type PatientRecapPopoverOpened = {
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown.
     */
    feature_flag_variant?: null | string;
    /**
     * Patient identifier the recap is for
     */
    patient_id?: null | string;
    /**
     * Session the recap was triggered from
     */
    session_id?: null | string;
    /**
     * UI surface that produced the event. Known values: in_session_chip.
     */
    surface?: null | string;
}

/**
 * Tracks when document is deleted
 */
export type DocumentDeleted = {
    /**
     * Document ID
     */
    document_id?: null | string;
    /**
     * Whether the template used is pinned
     */
    is_pinned_template?: boolean | null;
    /**
     * Whether the template is from the starter pack
     */
    is_starter_pack_template?: boolean | null;
    /**
     * Session ID
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Fired when a schedule file passes client-side validation and the upload begins, before
 * the processing request is sent
 */
export type ScheduleUploadStarted = {
    /**
     * Which upload surface accepted the file — sidebar or modal
     */
    entry_point?: null | string;
    /**
     * Normalized lowercase file extension without the dot (e.g. csv, pdf, png)
     */
    file_type?: null | string;
    /**
     * Active sessions-list variant when the upload started — v1, v2, or consolidated.
     * Disambiguates a dead input method from a surface removed by a list-variant rollout (the
     * consolidated list has no sidebar dropzone)
     */
    session_list_version?: null | string;
    /**
     * How the file reached the dropzone — drag_drop, browse, or paste
     */
    upload_method?: null | string;
}

/**
 * Tracks when a template is selected
 */
export type TemplateSelected = {
    /**
     * Consult note identifier
     */
    consult_note_id?: null | string;
    /**
     * Document identifier
     */
    document_id?: null | string;
    /**
     * Integration context
     */
    integration_type?: null | string;
    /**
     * Whether this selection triggers a note regeneration
     */
    is_regeneration?: boolean | null;
    /**
     * Platform for mobile integrations
     */
    platform?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * ID of the selected template
     */
    template_id?: null | string;
    /**
     * Usage timestamp
     */
    used_at?: null | string;
    [property: string]: any;
}

/**
 * Tracks when onboarding is completed
 */
export type OnboardingCompleted = {
    /**
     * Platform where onboarding completed
     */
    client_platform?: null | string;
    /**
     * Nested arm inside onboarding_variant experimental-2 (guided_tour | sample_conversation).
     * Absent outside the guided-tour eligible cohort, so its presence also marks cohort
     * membership.
     */
    onboarding_sub_variant?: null | string;
    /**
     * A/B variant for onboarding-v2 flows (e.g. control, experimental, experimental-2).
     * Previously arrived undeclared via additionalProperties.
     */
    onboarding_variant?: null | string;
    /**
     * Onboarding funnel identifier (e.g. dictate_onboarding, onboarding_v2). Disambiguates
     * parallel onboarding flows sharing this event.
     */
    source?: null | string;
    /**
     * Count of unique wizard steps completed before the funnel ended
     */
    steps_completed?: number | null;
    /**
     * Milliseconds from onboarding_started to completion across the full funnel
     */
    total_duration_ms?: number | null;
    [property: string]: any;
}

/**
 * Tracks when onboarding is initiated
 */
export type OnboardingStarted = {
    /**
     * Source that triggered the event (e.g. frontend, backend)
     */
    event_source?: null | string;
    /**
     * Nested arm inside onboarding_variant experimental-2 (guided_tour | sample_conversation).
     * Absent outside the guided-tour eligible cohort, so its presence also marks cohort
     * membership.
     */
    onboarding_sub_variant?: null | string;
    /**
     * A/B variant for onboarding-v2 flows (e.g. control, experimental, experimental-2).
     * Previously arrived undeclared via additionalProperties.
     */
    onboarding_variant?: null | string;
    /**
     * Dictate onboarding only. True when the funnel resumed from persisted pre-auth progress
     * rather than starting fresh.
     */
    resumed_from_disk?: boolean | null;
    /**
     * Onboarding funnel identifier (e.g. dictate_onboarding, onboarding_v2). Disambiguates
     * parallel onboarding flows sharing this event.
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks follow up question clicked event
 */
export type FollowUpQuestionClicked = {
    /**
     * Chat Id
     */
    chat_id?: null | string;
    /**
     * Backend message ID (msg_*) of the assistant response the follow-up chips render under.
     * Joins thread-continuation engagement to the specific model that produced the response.
     */
    message_id?: null | string;
    /**
     * Question
     */
    question?: null | string;
    /**
     * Question Index
     */
    question_index?: number | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Total Questions
     */
    total_questions?: number | null;
    [property: string]: any;
}

/**
 * Tracks when an AI objective execution starts
 */
export type ObjectiveExecutionStarted = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Call direction
     */
    direction: Direction;
    /**
     * Unique objective identifier
     */
    objective_id: string;
    [property: string]: any;
}

/**
 * Call direction
 */
export type Direction = "inbound" | "outbound";

/**
 * Tracks chronicle device disconnected event
 */
export type ChronicleDeviceDisconnected = {
    /**
     * Connection Duration Seconds
     */
    connection_duration_seconds?: number | null;
    /**
     * Device Id
     */
    device_id?: null | string;
    /**
     * Disconnect Reason
     */
    disconnect_reason?: null | string;
    /**
     * Device battery level percent when available — device-snapshot enrichment
     */
    hardware_battery_level?: number | null;
    /**
     * Device firmware version when available — device-snapshot enrichment
     */
    hardware_fw_version?: null | string;
    /**
     * Connected-link BLE RSSI in dBm when available (desktop macOS + mobile; no connected-RSSI
     * API on Windows) — device-snapshot enrichment
     */
    hardware_rssi?: number | null;
    /**
     * Chronicle device serial (full BLE peripheral name) when connected — device-snapshot
     * enrichment
     */
    hardware_serial_number?: null | string;
    /**
     * Serial Number
     */
    serial_number?: null | string;
    /**
     * Whether a recording was in progress when the device disconnected — set on deliberate
     * low-battery power-off (disconnect_reason poweroff_batlow)
     */
    was_recording?: boolean | null;
    [property: string]: any;
}

/**
 * Tracks audio failed event
 */
export type AudioFailed = {
    /**
     * Error Message
     */
    error_message?: null | string;
    /**
     * File Size
     */
    file_size?: number | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks deletion of a local offline recording before or after sync
 */
export type OfflineRecordingDeleted = {
    /**
     * Number of sync attempts made before deletion
     */
    attempt_count: number;
    /**
     * Mechanism that deleted the local recording
     */
    deletion_source: DeletionSource;
    /**
     * Desktop-generated local session identifier
     */
    local_session_id: string;
    /**
     * Recording source
     */
    source: OfflineSyncDeferredSource;
    /**
     * Last known offline sync status at deletion time
     */
    status: Status;
}

/**
 * Mechanism that deleted the local recording
 */
export type DeletionSource = "manual" | "retention_purge" | "abandoned_shell_gc";

/**
 * Last known offline sync status at deletion time
 */
export type Status = "recording" | "pending_sync" | "creating_session" | "uploading_audio" | "processing" | "synced" | "sync_failed" | "auth_required" | "upload_failed" | "unrecoverable" | "unknown";

/**
 * Tracks when Evidence chat content is successfully copied to the clipboard. PHI-free —
 * carries no copied body. surface/chat_id/message_id attribute the copy to the producing
 * response so copy adoption can be compared with note_insert_applied
 * (add/replace/create-document).
 */
export type EvidenceCopied = {
    /**
     * Chat identifier the copied content came from
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Backend message ID (msg_*) of the assistant response being copied. Joins the copy to the
     * specific model that produced the response.
     */
    message_id?: null | string;
    /**
     * Chat surface mode when known (e.g. evidence)
     */
    mode?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier when the chat is linked to a session (in-session vs standalone)
     */
    session_id?: null | string;
    /**
     * Which copy button surface succeeded — reply_actions is the per-response copy button;
     * user_notes is the copy button on a usernotes draft block
     */
    surface?: NoteCopiedSurface;
    [property: string]: any;
}

/**
 * Tracks EHR launch token generation
 */
export type EhrLaunchCreated = {
    /**
     * EHR provider
     */
    ehr_provider?: null | string;
    [property: string]: any;
}

/**
 * Tracks EHR section selection
 */
export type EhrSectionSelected = {
    /**
     * True when the clinician saw the unlinked-sections push warning earlier in this browser
     * session, whichever way they resolved it. Same definition as after_push_warning on
     * note_synced.
     */
    after_push_warning?: boolean | null;
    /**
     * Where the link was made (note | template)
     */
    editor_context?: EditorContext | null;
    /**
     * EHR system name
     */
    ehr_provider?: null | string;
    /**
     * The EHR catalogue section id linked to, never the heading text
     */
    ehr_section_id?: null | string;
    /**
     * What happened (linked | unlinked | template_snapshot_saved)
     */
    link_action?: LinkAction | null;
    /**
     * Template snapshot only; headings with a non-empty link in the saved mapping
     */
    linked_section_count?: number | null;
    /**
     * Template snapshot only; headings in the saved mapping
     */
    section_count?: number | null;
    /**
     * Section name (legacy emit only; the V2 leg leaves it null and sends ehr_section_id)
     */
    section_name?: null | string;
    /**
     * Opaque clinical-session identifier when linked in a note
     */
    session_id?: null | string;
    /**
     * Template whose section mapping was changed
     */
    template_id?: null | string;
    [property: string]: any;
}

export type EditorContext = "note" | "template";

export type LinkAction = "linked" | "unlinked" | "template_snapshot_saved";

/**
 * Tracks when session is deleted
 */
export type SessionDeleted = {
    /**
     * Type of delete action
     */
    delete_action?: null | string;
    /**
     * Number of sessions deleted
     */
    session_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks when user changes find/replace scope
 */
export type FindReplaceScopeUpdated = {
    /**
     * Find/replace scope
     */
    scope?: null | string;
    [property: string]: any;
}

/**
 * Tracks when Ask Heidi shortcut search is used. PHI-free — never carries search_query.
 */
export type EvidenceShortcutSearchUsed = {
    /**
     * Whether the shortcut search returned matches
     */
    has_results?: boolean | null;
    /**
     * Length of the search query; raw query text is never collected
     */
    query_length?: number | null;
    /**
     * Number of shortcut search results
     */
    results_count?: number | null;
    /**
     * Which Ask Heidi surface the search happened on (floating_bar | side_panel | chat_input)
     */
    surface?: null | string;
    [property: string]: any;
}

/**
 * Tracks when recording consent is declined by a participant
 */
export type RecordingConsentDeclined = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Legal jurisdiction for the consent
     */
    jurisdiction: string;
    [property: string]: any;
}

/**
 * Tracks a confirmed Send click in the send-to-patient email flow, before the dispatch
 * result
 */
export type PatientSummaryEmailSendAttempted = {
    /**
     * Document Id
     */
    document_id?: null | string;
    /**
     * Allowlist pattern the document name matched (e.g. patient summary, discharge summary);
     * null when unmatched. Bounded non-PII set — never the raw clinician-entered title
     */
    document_name_pattern?: null | string;
    /**
     * Patient-facing document type used in the email copy (visit summary | document)
     */
    document_type?: null | string;
    /**
     * Whether the session had a linked patient profile when the send flow opened. Surfaced from
     * the frontend rather than resolved from a patient profile id server-side
     */
    had_patient_profile?: boolean | null;
    /**
     * Which affordance launched the send flow (canvas_chip | more_menu | share_sheet |
     * split_button_dropdown). canvas_chip is the patient-summary send chip on the document
     * canvas; more_menu is the kebab menu on a generated document; share_sheet is the mobile
     * session-detail share bottom sheet entry point — an in-app Compose bottom sheet, not the
     * OS share sheet; split_button_dropdown is the send item inside the desktop split button
     * chevron dropdown. Carried on the send events themselves so "which path do clinicians
     * prefer" needs no join back to the opening button_clicked. Bounded non-PII set; null when
     * the opener declared none
     */
    location?: null | string;
    /**
     * How the recipient details were obtained (profile | profile_edited | manual). profile is a
     * linked profile's prefill sent unedited; profile_edited is a prefill the clinician
     * amended; manual is hand-entered with no linked profile. Distinguishes "we had data and it
     * was right" from "we had data and it was wrong". Bounded non-PII set
     */
    patient_details_source?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Send-to-patient flow variant the event belongs to (e.g. patient_m0_email_only). Bounded
     * non-PII set used to compare M0 email-only against future SMS/email variants; null when
     * unset
     */
    variant?: null | string;
    [property: string]: any;
}

/**
 * The note-generation auto-retry layer decided what to do after one attempt: retry once
 * quietly, or stop and surface the outcome. One event per attempt.
 */
export type NoteAutoRetryDecided = {
    /**
     * Which generation attempt this decision follows (1 or 2).
     */
    attempt: number;
    /**
     * Whether to retry once quietly or stop and surface the outcome (retry, stop).
     */
    decision: Decision;
    /**
     * Classified failure code when outcome is failed, from the frontend NoteFailedErrorCode
     * (for example TypeError, HTTP_502, empty_note, ASKAIQuotaExceedError). Never a raw
     * exception message or clinical content; the pattern rejects anything sentence-shaped at
     * the collector. Not an enum because HTTP_<status> is open-ended.
     */
    error_code?: null | string;
    /**
     * Whether a completed attempt produced zero chunks. Null for other outcomes.
     */
    is_empty?: boolean | null;
    /**
     * The attempt's outcome kind (completed, delegated, skipped, aborted, failed).
     */
    outcome: NoteAutoRetryDecidedOutcome;
    /**
     * Opaque identifier of the clinical session whose note was generated, not the analytics
     * session.
     */
    session_id: string;
    /**
     * Why the layer stopped, when present (completed, empty-completed, delegated, skipped,
     * aborted, deterministic, not-stream-stage, personalisation-owned, offline, stale,
     * budget-exhausted). Null when retrying.
     */
    stop_reason?: StopReason | null;
}

/**
 * Whether to retry once quietly or stop and surface the outcome (retry, stop).
 */
export type Decision = "retry" | "stop";

/**
 * The attempt's outcome kind (completed, delegated, skipped, aborted, failed).
 */
export type NoteAutoRetryDecidedOutcome = "completed" | "delegated" | "skipped" | "aborted" | "failed";

export type StopReason = "completed" | "empty-completed" | "delegated" | "skipped" | "aborted" | "deterministic" | "not-stream-stage" | "personalisation-owned" | "offline" | "stale" | "budget-exhausted";

/**
 * Tracks when an outbound call is answered by the recipient
 */
export type CallAnswered = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Time from initiation to answer in milliseconds
     */
    time_to_answer_ms: number;
    [property: string]: any;
}

/**
 * Tracks schedule upload promo clicked event
 */
export type ScheduleUploadPromoClicked = {
    /**
     * Display
     */
    display?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a patient is matched to an EHR record
 */
export type EhrPatientMatched = {
    /**
     * EHR system provider name
     */
    ehr_provider: string;
    /**
     * Method used for matching
     */
    match_method: string;
    /**
     * Whether a match was found
     */
    matched: boolean;
    [property: string]: any;
}

/**
 * Tracks context file uploaded event
 */
export type ContextFileUploaded = {
    /**
     * File Id
     */
    file_id?: null | string;
    /**
     * File Size Bytes
     */
    file_size_bytes?: number | null;
    /**
     * File Type
     */
    file_type?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Upload Source
     */
    upload_source?: null | string;
    [property: string]: any;
}

/**
 * Tracks user signed up event
 */
export type UserSignedUp = {
    /**
     * Auth Provider
     */
    auth_provider?: null | string;
    /**
     * Baa Required
     */
    baa_required?: boolean | null;
    /**
     * Compliance Kit Required
     */
    compliance_kit_required?: boolean | null;
    /**
     * Custom Utm Source
     */
    custom_utm_source?: null | string;
    /**
     * Integration Type
     */
    integration_type?: null | string;
    /**
     * Phone Number Provided
     */
    phone_number_provided?: boolean | null;
    /**
     * Sign In Method
     */
    sign_in_method?: null | string;
    /**
     * Product that the signup is attributed to (e.g. scribe, evidence)
     */
    signup_attributed_product?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a message attachment upload fails
 */
export type MessageAttachmentUploadFailed = {
    /**
     * Unique conversation identifier
     */
    conversation_id: string;
    /**
     * Human-readable error description
     */
    error_description?: null | string;
    /**
     * Category of the upload error
     */
    error_type: string;
    /**
     * MIME type of the file that failed
     */
    mime_type: string;
    [property: string]: any;
}

/**
 * Tracks when the Ask Heidi Sources picker is opened
 */
export type EvidenceSourcesOpened = {
    /**
     * Whether the Sources button is on the default corpus selection
     */
    is_default?: boolean | null;
    /**
     * Sources selected beyond the default corpus
     */
    selection_total?: number | null;
    /**
     * Which Ask Heidi surface the Sources picker opened on (floating_bar | side_panel |
     * chat_input)
     */
    surface?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a patient drawer or full-page profile mounts for a patient
 */
export type PatientProfileOpened = {
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown.
     */
    feature_flag_variant?: null | string;
    /**
     * True if this patient has at least one prior session for the same user. FE may stamp from
     * a cached count; otherwise leave null and derive server-side.
     */
    is_returning_patient?: boolean | null;
    /**
     * Where the open was triggered from. Known values: list_row (clicked a row in /patients),
     * in_session_chip (clicked the linked-patient chip in an active session), direct_link (deep
     * link or browser nav), other.
     */
    opened_from?: null | string;
    /**
     * Session that triggered the open when opened_from = in_session_chip. Enables attribution
     * of in-session chip clicks back to the session they came from. Null for all other
     * opened_from values.
     */
    originating_session_id?: null | string;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * UI surface that produced the event. Known values: drawer, full_page.
     */
    surface?: null | string;
    /**
     * UUID generated FE-side at profile mount. Persists for the whole open-to-close of the view
     * and is propagated to every event in that view (tab switches, session expands, field
     * edits, document uploads, and the eventual session_created when a session starts from this
     * profile). The stitching primitive for surface-side funnels. FE must populate every emit;
     * nullable in schema only as a safety net.
     */
    view_id?: null | string;
}

/**
 * Tracks cpd draft deleted event
 */
export type CpdDraftDeleted = {
    /**
     * Draft Id
     */
    draft_id?: null | string;
    /**
     * Removed Count
     */
    removed_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks integration started event
 */
export type IntegrationStarted = {
    /**
     * Integration Type
     */
    integration_type?: null | string;
    /**
     * Where the integration action began. Open string, not an enum. Null when the surface is
     * unknown.
     */
    setup_source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an appointment sync with the EHR fails
 */
export type EhrAppointmentSyncFailed = {
    /**
     * EHR system provider name
     */
    ehr_provider: string;
    /**
     * Human-readable error description
     */
    error_description?: null | string;
    /**
     * Category of the sync error
     */
    error_type: string;
    /**
     * Source that triggered the sync
     */
    sync_source: string;
    [property: string]: any;
}

/**
 * Fired once on a desktop app's first launch after a fresh install.
 */
export type ApplicationInstall = {
    /**
     * CI build short SHA; absent on local / non-publish builds
     */
    build?: null | string;
    /**
     * Platform identifier; "desktop" for this client. Free-form string (not a hard enum) so the
     * schema can take other values later without a version bump.
     */
    platform: string;
    /**
     * Desktop app version (semver), e.g. getDesktopVersion() on the FE
     */
    version: string;
}

/**
 * Tracks deletion of a letter
 */
export type LetterDeleted = {
    /**
     * Number of attachments when the letter was deleted
     */
    attachment_count: number;
    /**
     * Whether the letter was deleted after a fully failed send attempt
     */
    had_failed_send: boolean;
    /**
     * Whether the letter had a signature when deleted
     */
    had_signature: boolean;
    /**
     * Deleted letter identifier
     */
    letter_id: string;
    /**
     * Number of configured recipients when the letter was deleted
     */
    recipient_count: number;
    /**
     * Session that contained the letter
     */
    session_id: string;
}

/**
 * Tracks when modal is viewed
 */
export type ModalViewed = {
    /**
     * Review confirmation popover (screen_name review_confirmation_popover) — the deferred note
     * action the review gate protects: copy | export | push.
     */
    action_type?: null | string;
    /**
     * Advanced evidence credits remaining at impression time
     */
    credits_remaining?: number | null;
    /**
     * User's current subscription tier at impression time
     */
    current_plan?: null | string;
    /**
     * Days remaining on relevant trial at impression time (trial upgrade nudge surfaces).
     * Distinct from paywall_viewed.days_left_on_trial — different call sites and FE property
     * names.
     */
    days_remaining?: number | null;
    /**
     * Upstream surface that opened this modal (e.g. side_nav_credit_usage_popover)
     */
    entry_point?: null | string;
    /**
     * Present when viewer belongs to a team — whether they are team admin
     */
    is_admin?: boolean | null;
    /**
     * Trial reminder context — low usage at impression time
     */
    is_low_usage?: boolean | null;
    /**
     * Session layout version at impression time (v1 or v2). Mirrors
     * session_layout.layout_version enum so analysts can join without casing drift.
     */
    layout_version?: LayoutVersion | null;
    /**
     * Side-nav or header mount point for trial nudge or credit popover (sidebar or top_right)
     */
    nudge_placement?: NudgePlacement | null;
    /**
     * Trial upgrade nudge display state (icon_only or countdown)
     */
    nudge_state?: NudgeState | null;
    /**
     * Previous plan before trial (for trial-related modals)
     */
    previous_plan_before_trial?: null | string;
    /**
     * Name of modal/screen viewed
     */
    screen_name?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Upgrade destination tier for trial and credit surfaces
     */
    target_plan?: null | string;
    /**
     * Present when viewer belongs to a team — whether team billing is Stripe-managed
     */
    team_is_stripe_managed?: boolean | null;
    /**
     * Trial upgrade nudge trial type (clinician or practice)
     */
    trial_type?: TrialType | null;
    /**
     * Whole-number credit usage percentage (0–100) for credit usage popovers
     */
    usage_percent?: number | null;
    [property: string]: any;
}

export type LayoutVersion = "v1" | "v2";

export type NudgePlacement = "sidebar" | "top_right";

export type NudgeState = "icon_only" | "countdown";

export type TrialType = "clinician" | "practice";

/**
 * Tracks when a task is explicitly opened — task detail panel or in-session card expand
 */
export type TaskViewed = {
    /**
     * Associated session ID if viewed in session context
     */
    session_id?: null | string;
    /**
     * Unique identifier of the task viewed
     */
    task_id?: null | string;
    /**
     * Where the task was opened. Known values: task_detail, in_session_card
     */
    view_context?: null | string;
    [property: string]: any;
}

/**
 * Session Detail Resume Started — a session-detail Resume began capturing again;
 * recording_device says whether the Heidi Remote or the app captures
 */
export type SessionDetailResumeStarted = {
    /**
     * Accumulated recording duration in seconds before this resume, read from local store
     * pre-start
     */
    previous_duration?: number | null;
    /**
     * Which device captures the resumed audio — remote (Heidi Remote) or app (phone / desktop
     * microphone)
     */
    recording_device?: RecordingDevice | null;
    /**
     * Clinical session identifier (server id; clients fall back to the local id when the
     * session is not yet synced)
     */
    session_id?: null | string;
    /**
     * Local (client-side) session identifier
     */
    session_id_local?: null | string;
    [property: string]: any;
}

export type RecordingDevice = "remote" | "app";

/**
 * Tracks form filling downloaded event
 */
export type FormFillingDownloaded = {
    /**
     * Document Id
     */
    document_id?: null | string;
    /**
     * Whether download was automatic or manual
     */
    download_trigger?: DownloadTrigger | null;
    /**
     * Whether the document was edited before download
     */
    edited_in_app?: boolean | null;
    /**
     * Generation Method
     */
    generation_method?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Time elapsed between generation and export in milliseconds
     */
    time_to_export_ms?: number | null;
}

/**
 * How the card came to exist. Known values: auto (classifier-surfaced), manual (clinician
 * added from the protocol library). A manual add is expected to be manual.
 *
 * How the card came to exist. Known values: auto (classifier-surfaced), manual (clinician
 * added from the protocol library)
 */
export type DownloadTrigger = "auto" | "manual";

/**
 * Tracks when a user selects an objective
 */
export type ObjectiveSelected = {
    /**
     * Objective direction (inbound or outbound)
     */
    direction?: null | string;
    /**
     * Selection mode (create or edit)
     */
    mode: string;
    /**
     * Objective identifier
     */
    objective_id?: null | string;
    /**
     * Display name of the objective
     */
    objective_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user submits a message
 */
export type MessageSubmitted = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * How the message was initiated
     */
    entry_method?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Backend message ID (msg_*) for the submitted turn. Usually null at submit time (assigned
     * mid-stream); joins to the backend evidence.stream.outcome for the same request when
     * present.
     */
    message_id?: null | string;
    /**
     * Preset local guidelines enabled
     */
    preset_local_guidelines?: boolean | null;
    /**
     * Preset primary evidence enabled
     */
    preset_primary_evidence?: boolean | null;
    /**
     * Preset web search enabled
     */
    preset_web_search?: boolean | null;
    /**
     * Length of the query in characters
     */
    query_length?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Selection mode (default, preset, custom)
     */
    selection_mode?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Source of the message event (evidence, ask_heidi)
     */
    source?: null | string;
    /**
     * List of source domains selected
     */
    sources?: string[] | null;
    /**
     * Number of sources selected
     */
    sources_count?: number | null;
    /**
     * Length of the query in words
     */
    word_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks book demo clinicians selected event
 */
export type BookDemoCliniciansSelected = {
    /**
     * Number Of Clinicians
     */
    number_of_clinicians?: null | string;
    /**
     * Organisation Size
     */
    organisation_size?: null | string;
    [property: string]: any;
}

/**
 * Tracks onboarding updated event
 */
export type OnboardingUpdated = {
    /**
     * Step
     */
    step?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the data extraction modal is opened
 */
export type ExtractionModalOpened = {
    /**
     * Entry point that triggered the extraction modal
     */
    entry_point?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when live suggestions are toggled on or off
 */
export type EvidenceLiveSuggestionsToggled = {
    /**
     * Number of enabled categories when toggled
     */
    enabled_categories_count?: number | null;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether suggestions are now enabled or disabled
     */
    is_enabled?: boolean | null;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * User region
     */
    region?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a Chronicle device firmware upgrade is started
 */
export type ChronicleFirmwareUpgradeStarted = {
    /**
     * Chronicle device identifier
     */
    device_id?: null | string;
    /**
     * Target firmware version for the upgrade
     */
    firmware_version?: null | string;
    [property: string]: any;
}

/**
 * Fired when a user updates a translator preference setting
 */
export type TranslatorPreferencesUpdated = {
    /**
     * The previous value of the setting, null if previously unset
     */
    previous_value?: null | string;
    /**
     * The preference setting that was changed
     */
    setting: Setting;
    /**
     * The new value of the setting, stringified
     */
    value: string;
}

/**
 * The preference setting that was changed
 */
export type Setting = "recording_mode" | "clinician_language" | "clinician_gender" | "translation_model" | "auto_read_translations";

/**
 * Tracks when a conversation is automatically resolved
 */
export type ConversationAutoResolved = {
    /**
     * Reason for automatic resolution
     */
    auto_resolve_reason: string;
    /**
     * Unique conversation identifier
     */
    conversation_id: string;
    /**
     * Time the conversation was inactive before resolution in seconds
     */
    inactive_for_seconds?: number | null;
    [property: string]: any;
}

/**
 * Tracks force update initiated event
 */
export type ForceUpdateInitiated = {
    /**
     * Current Version
     */
    current_version?: null | string;
    /**
     * Min Supported Version
     */
    min_supported_version?: null | string;
    /**
     * Update Type
     */
    update_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks integration widget auth step completed event
 */
export type IntegrationWidgetAuthStepCompleted = {
    /**
     * Integration Type
     */
    integration_type?: null | string;
    /**
     * Step
     */
    step?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user account is created via the internal admin tool
 */
export type UserCreatedViaInternalAdmin = {
    /**
     * Data of the created user account
     */
    user_data?: { [key: string]: any } | null;
    [property: string]: any;
}

/**
 * Tracks when integration data retrieval fails
 */
export type IntegrationDataFailed = {
    /**
     * Reason for the data retrieval failure
     */
    failure_reason?: null | string;
    /**
     * Type of integration
     */
    integration_type?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks patient details retrieval
 */
export type EhrLaunchPatientCompleted = {
    /**
     * EHR provider
     */
    ehr_provider?: null | string;
    [property: string]: any;
}

/**
 * Tracks when extracted patient fields are first shown to the user on the profile
 */
export type PatientFieldExtractionSurfaced = {
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown.
     */
    feature_flag_variant?: null | string;
    /**
     * Name of the surfaced field. Known values: dob, name, gender, pronouns, allergies,
     * medications, medical_history. Field name only — never the value (PII).
     */
    field?: null | string;
    /**
     * Extraction pipeline that produced the surfaced field. Known values: legacy, temporal.
     * Tagged at the action level rather than the code path so the metric survives the BE-1b
     * migration and gives a free legacy-vs-temporal quality comparison.
     */
    pathway?: null | string;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * What the extraction was drawn from. Known values: note_transcript, document, summary,
     * other.
     */
    source?: null | string;
    /**
     * UI surface that produced the event. Known values: drawer, full_page.
     */
    surface?: null | string;
    /**
     * UUID of the patient profile view this event belongs to. Inherited from
     * patient_profile_opened.
     */
    view_id?: null | string;
}

/**
 * Tracks a successful letter attachment upload
 */
export type LetterAttachmentUploaded = {
    /**
     * Number of attachments on the letter after the upload
     */
    attachment_count: number;
    /**
     * Letter receiving the attachment
     */
    letter_id: string;
    /**
     * MIME type accepted by the letter attachment API
     */
    mime_type: MIMEType;
    /**
     * Session containing the letter
     */
    session_id: string;
    /**
     * Uploaded attachment size in bytes
     */
    size_bytes: number;
}

/**
 * MIME type accepted by the letter attachment API
 *
 * MIME type of the removed attachment
 */
export type MIMEType = "application/pdf";

/**
 * A nudge card was manually added by the clinician from the protocol library (i.e. the
 * classifier did not auto-surface it)
 */
export type NudgeCardAdded = {
    /**
     * Version of the protocol catalogue the card was added from
     */
    catalogue_version: string;
    /**
     * At-add classifier confidence for the protocol (0-1)
     */
    confidence: number;
    /**
     * Join key back to the PHI-safe detection context persisted on NudgeCardState. Typically
     * null for manual adds, where no detection pass produced the card.
     */
    detection_id?: null | string;
    /**
     * Number of matched signals (length of matched_signals)
     */
    matched_signal_count?: number | null;
    /**
     * Named signals (chips) the classifier matched, if any. PHI-free labels only
     */
    matched_signals?: string[];
    /**
     * Identifier of the model in effect at add time
     */
    model_id?: null | string;
    /**
     * Version of the classifier prompt in effect at add time
     */
    prompt_version?: null | string;
    /**
     * Identifier of the protocol the clinician added (e.g. a ChenMed protocol id)
     */
    protocol_id: string;
    /**
     * Heidi session the nudge card belongs to
     */
    session_id: string;
    /**
     * How the card came to exist. Known values: auto (classifier-surfaced), manual (clinician
     * added from the protocol library). A manual add is expected to be manual.
     */
    source?: DownloadTrigger;
    /**
     * Hash of the at-add transcript chunk the clinician was looking at; a join key only, never
     * the raw text
     */
    transcript_chunk_hash?: null | string;
}

/**
 * Tracks when AI response is regenerated
 */
export type ResponseCreated = {
    /**
     * Feature where response was created
     */
    feature?: null | string;
    [property: string]: any;
}

/**
 * Agent tool finished (success or failure)
 */
export type WorkToolCompleted = {
    /**
     * completed
     */
    action: WorkToolCompletedAction;
    /**
     * Durable agent-harness run id
     */
    agent_run_id: string;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * Account SKU string (e.g. pro, pro_plus, clinician_pro). Not an enum; SKUs change.
     */
    current_plan?: null | string;
    /**
     * This user's seat type (assistant, standard, or max)
     */
    current_seat_type?: TSeatType | null;
    /**
     * Duration in milliseconds
     */
    duration_ms?: number | null;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, or research_terms
     */
    entry_point?: null | string;
    /**
     * Safe error code
     */
    error_code?: null | string;
    /**
     * tool or computer_use
     */
    object_type: WorkToolCompletedObjectType;
    /**
     * succeeded, failed, or cancelled
     */
    outcome: WorkToolCompletedOutcome;
    /**
     * Project id when applicable
     */
    project_id?: null | string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * Tool call or CUA task id
     */
    task_id?: null | string;
    /**
     * rpa or other
     */
    tool_category?: null | string;
    /**
     * Tool name with no PHI
     */
    tool_name: string;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
}

/**
 * completed
 */
export type WorkToolCompletedAction = "completed";

/**
 * tool or computer_use
 */
export type WorkToolCompletedObjectType = "tool" | "computer_use";

/**
 * succeeded, failed, or cancelled
 *
 * Terminal job outcome
 *
 * Terminal handoff outcome
 *
 * Terminal run outcome
 */
export type WorkToolCompletedOutcome = "succeeded" | "failed" | "cancelled";

/**
 * Tracks a native client entering a confirmed offline or backend-unreachable state
 */
export type NativeAppOffline = {
    /**
     * Heidi client platform identifier
     */
    client_platform: string;
    /**
     * Desktop application version when emitted by desktop
     */
    desktop_version: null | string;
    /**
     * Offline recording feature-flag state
     */
    feature_flag_state: FeatureFlagState;
    /**
     * Desktop installer format when emitted by desktop
     */
    installer_type: null | string;
    /**
     * Confirmed unavailable state entered by the native client
     */
    offline_state: OfflineState;
    /**
     * Product platform
     */
    platform: NativeAppOfflinePlatform;
    /**
     * Connectivity state before the transition
     */
    previous_state: State;
}

/**
 * Confirmed unavailable state entered by the native client
 */
export type OfflineState = "offline" | "backend_unreachable";

/**
 * Product platform
 */
export type NativeAppOfflinePlatform = "desktop" | "ios" | "android";

/**
 * Tracks library collection created event
 */
export type LibraryCollectionCreated = {
    /**
     * Creation Context
     */
    creation_context?: null | string;
    /**
     * Is Team Collection
     */
    is_team_collection?: boolean | null;
    [property: string]: any;
}

/**
 * Tracks when the global task list is opened, capturing active filters and sort state
 */
export type TaskListViewed = {
    /**
     * Active assignee filter at time of view (null if no assignee filter applied)
     */
    filter_assignee_id?: null | string;
    /**
     * Active status filter at time of view. Known values: todo, done, archived
     */
    filter_status?: null | string;
    /**
     * Associated session ID if viewed in session context
     */
    session_id?: null | string;
    /**
     * Active sort order at time of view. Known values: due_date, created_at, assignee
     */
    sort_by?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an authentication fallback method is used
 */
export type AuthFallbackUsed = {
    /**
     * Authentication method used as fallback
     */
    auth_method?: null | string;
    /**
     * Reason the fallback was triggered
     */
    fallback_reason?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a recipient is added to a letter
 */
export type LetterRecipientAdded = {
    /**
     * Letter being addressed
     */
    letter_id: string;
    /**
     * Whether the recipient is the primary recipient or a copy recipient
     */
    recipient_role: RecipientRole;
    /**
     * Persisted letter recipient variant
     */
    recipient_type: RecipientType;
    /**
     * How the recipient was selected
     */
    selection_source: SelectionSource;
    /**
     * Session containing the letter
     */
    session_id: string;
}

/**
 * How the recipient was selected
 */
export type SelectionSource = "search" | "manual" | "suggested";

/**
 * Tracks contact sales form opened event
 */
export type ContactSalesFormOpened = {
    /**
     * Form Id
     */
    form_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when screen or page is viewed
 */
export type ScreenViewed = {
    /**
     * ISO alpha-2 country code whose country-config payload entry was allotted to the user
     * (null when the payload has no explicit entry). Set on sign-up screens where
     * country-config governs the visible UI.
     */
    country_config?: null | string;
    /**
     * Device type
     */
    device_type?: null | string;
    /**
     * Entry point to the screen
     */
    entry_point?: null | string;
    /**
     * Fixed minutes per activity
     */
    fixed_minutes_per_activity?: number | null;
    /**
     * Fixed minutes per source
     */
    fixed_minutes_per_source?: number | null;
    /**
     * Whether the screen rendered its empty state
     */
    is_empty?: boolean | null;
    /**
     * Configured fallback onboarding CTA from country-config (meeting, webinar, request-demo)
     */
    onboarding_cta_fallback?: null | string;
    /**
     * Configured primary onboarding CTA from country-config (meeting, webinar, request-demo)
     */
    onboarding_cta_primary?: null | string;
    /**
     * Nested arm inside onboarding_variant experimental-2 (guided_tour | sample_conversation).
     * Absent outside the guided-tour eligible cohort, so its presence also marks cohort
     * membership.
     */
    onboarding_sub_variant?: null | string;
    /**
     * A/B variant for onboarding-v2 flows (e.g. control, experimental, experimental-2). Set on
     * onboarding screens only.
     */
    onboarding_variant?: null | string;
    /**
     * Resolved phone-number field state for the country (hidden, required, optional)
     */
    phone_number_variant?: null | string;
    /**
     * Session list scope at impression time (all, me, direct_share)
     */
    scope?: null | string;
    /**
     * Screen name (Amplitude format)
     */
    screen?: null | string;
    /**
     * Name of screen viewed
     */
    screen_name?: null | string;
    /**
     * Time calculation method
     */
    time_calculation_method?: null | string;
    /**
     * URL of the screen
     */
    url?: null | string;
    [property: string]: any;
}

/**
 * Fired when the user answers the App Store review sentiment gate
 */
export type AppStoreReviewSentiment = {
    /**
     * Platform identifier (android, ios, web)
     */
    client_platform?: null | string;
    /**
     * Feature that triggered the prompt. Namespaced rather than `source` because the top-level
     * `source` key is reserved for Amplitude EU routing. Closed set —
     * AppStoreReviewPromptSource on iOS.
     */
    review_source?: ReviewSource | null;
    /**
     * The user's answer. Pinned to a closed set rather than a length bound: this schema is the
     * only enforcement point, and a producer sending POSITIVE or thumbs_up would validate
     * cleanly while silently splitting the prompt_requested → sentiment join the pair exists to
     * measure.
     */
    sentiment?: Sentiment | null;
}

export type ReviewSource = "scribe" | "evidence" | "remote";

export type Sentiment = "positive" | "negative";

/**
 * User viewed or interacted with a Work entry surface
 */
export type WorkSurfaceActioned = {
    /**
     * Work surface interaction
     */
    action: WorkSurfaceActionedAction;
    /**
     * Durable agent-harness run id
     */
    agent_run_id?: null | string;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * Clinical session id when the surface action originated in a session
     */
    clinical_session_id?: null | string;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, research_terms,
     * pro_tip_routines, or pro_tip_desktop
     */
    entry_point?: null | string;
    /**
     * Home focus at render time
     */
    focus_kind?: FocusKind | null;
    /**
     * First Work surface view for this user
     */
    is_first_visit?: boolean | null;
    /**
     * Number of leftover-task rows in this render
     */
    leftover_count?: number | null;
    /**
     * Total open tasks available to ranking, not just rendered. Not UI-bounded; the ceiling is
     * a sanity bound only.
     */
    open_task_count?: number | null;
    /**
     * Projects visible on home when applicable
     */
    project_count?: number | null;
    /**
     * Project id when applicable
     */
    project_id?: null | string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * Ordering arm that produced the shelf: tiered heuristic or the shuffled experiment
     */
    ranking_policy?: RankingPolicy | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * UUID minted once per Suggested-work list render. Joins impressions to clicks.
     */
    render_id?: null | string;
    /**
     * Number of rows in the rendered list
     */
    slot_count?: number | null;
    /**
     * Zero-based position of the row in the rendered list
     */
    slot_index?: number | null;
    /**
     * Ranking tier that produced the row
     */
    suggestion_tier?: SuggestionTier | null;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * Clinical task id (pseudonymous ObjectId) for leftover rows. The join key to clinical_task
     * in the warehouse.
     */
    task_id?: null | string;
    /**
     * Days since the target visit, bucketed like task_age_bucket so no date of service is
     * reconstructable. Omitted when no dated visit.
     */
    visit_age_bucket?: VisitAgeBucket | null;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
}

/**
 * Work surface interaction
 */
export type WorkSurfaceActionedAction = "viewed" | "first_visit" | "return_visit" | "reopened" | "cta_clicked" | "dismissed";

export type FocusKind = "none" | "visit" | "patient";

export type RankingPolicy = "ranked" | "shuffled";

export type SuggestionTier = "contextual" | "derived" | "discovery";

export type VisitAgeBucket = "0" | "1-2" | "3-6" | "7-13" | "14-29" | "30+";

/**
 * Work error surfaced (client or server)
 */
export type WorkErrorEncountered = {
    /**
     * encountered
     */
    action?: null | string;
    /**
     * Durable agent-harness run id
     */
    agent_run_id?: null | string;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, or research_terms
     */
    entry_point?: null | string;
    /**
     * Safe error code
     */
    error_code?: null | string;
    /**
     * Client or transport error class
     */
    error_type?: null | string;
    /**
     * run, turn, tool, stream, rate_limit, upload, or other
     */
    object_type?: null | string;
    /**
     * failed
     */
    outcome?: null | string;
    /**
     * Project id when applicable
     */
    project_id?: null | string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
}

/**
 * Tracks patient searched event
 */
export type PatientSearched = {
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown.
     */
    feature_flag_variant?: null | string;
    /**
     * True when the search returned no matches. Convenience flag derived from result_count ==
     * 0; set explicitly so dashboard queries do not have to coalesce nulls.
     */
    has_zero_results?: boolean | null;
    /**
     * Integration type the search was scoped to, when applicable
     */
    integration_type?: null | string;
    /**
     * Round-trip latency of the search request in milliseconds. Useful for understanding
     * whether slow searches drive abandonment.
     */
    latency_ms?: number | null;
    /**
     * Character count of the typed query at the moment the debounced search request fired.
     * Replaces the raw `query` field from v1-0-1 for HIPAA reasons — clinicians type patient
     * names into this field.
     */
    query_length?: number | null;
    /**
     * Number of results returned by the search endpoint
     */
    result_count?: number | null;
    /**
     * UUID identifying a single field interaction. Generated FE-side on field focus and
     * propagated through field_selected, patient_searched, and the outcome event so the funnel
     * can stitch a single interaction across events.
     */
    search_id?: null | string;
    /**
     * Session identifier the search was performed within
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a task assignee changes, capturing previous and new assignee plus the action
 * type
 */
export type TaskAssignmentUpdated = {
    /**
     * Type of assignment change. Known values: assigned, reassigned, unassigned
     */
    assignment_action?: null | string;
    /**
     * User ID of the previous assignee (null if task was unassigned)
     */
    from_assignee_id?: null | string;
    /**
     * Type of the previous assignee (e.g. user, team). Null if task was unassigned.
     */
    from_assignee_type?: null | string;
    /**
     * Associated session ID if task belongs to a session
     */
    session_id?: null | string;
    /**
     * Unique identifier of the task
     */
    task_id?: null | string;
    /**
     * User ID of the new assignee (null if task was unassigned)
     */
    to_assignee_id?: null | string;
    /**
     * Type of the new assignee (e.g. user, team). Null if task was unassigned.
     */
    to_assignee_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an inbound call is received by the platform
 */
export type InboundCallReceived = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * SHA-256 hash of the caller phone number
     */
    from_phone_hash: string;
    /**
     * Whether the caller is a known patient
     */
    is_known_patient?: boolean | null;
    /**
     * SHA-256 hash of the practice phone number called
     */
    practice_number_hash: string;
    [property: string]: any;
}

/**
 * Tracks template searched event
 */
export type TemplateSearched = {
    /**
     * Creator user identifiers
     */
    creator_user_ids?: string[] | null;
    /**
     * Search keyword
     */
    keyword?: null | string;
    /**
     * Sort order
     */
    order?: null | string;
    /**
     * Organisation identifier
     */
    organization_id?: null | string;
    /**
     * Page number
     */
    page?: number | null;
    /**
     * Page size
     */
    page_size?: number | null;
    /**
     * Query
     */
    query?: null | string;
    /**
     * Number of results returned
     */
    results_returned?: number | null;
    /**
     * Search Action
     */
    search_action?: null | string;
    /**
     * Shared team identifiers
     */
    shared_team_ids?: string[] | null;
    /**
     * Sort field
     */
    sort_by?: null | string;
    /**
     * Total number of results
     */
    total_results?: number | null;
    /**
     * Type filters
     */
    types?: string[] | null;
    /**
     * Visibility filters
     */
    visibilities?: string[] | null;
    [property: string]: any;
}

/**
 * Tracks when an outbound message or call is blocked by a do-not-contact rule
 */
export type DoNotContactBlockedSend = {
    /**
     * Unique conversation identifier
     */
    conversation_id: string;
    /**
     * SHA-256 hash of the blocked phone number
     */
    phone_hash: string;
    [property: string]: any;
}

/**
 * Tracks when a user starts an inbox session
 */
export type InboxSessionStarted = {
    /**
     * How the user entered the inbox
     */
    entry_point?: null | string;
    [property: string]: any;
}

/**
 * Tracks evidence signup wall viewed event
 */
export type EvidenceSignupWallViewed = {
    /**
     * Evidence Location
     */
    evidence_location?: null | string;
    /**
     * Is Evidence Pro
     */
    is_evidence_pro?: boolean | null;
    /**
     * Is Managed
     */
    is_managed?: boolean | null;
    /**
     * Is Team Member
     */
    is_team_member?: boolean | null;
    /**
     * Region
     */
    region?: null | string;
    /**
     * Wall Type
     */
    wall_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks book demo next clicked event
 */
export type BookDemoNextClicked = {
    /**
     * Country
     */
    country?: null | string;
    /**
     * Number Of Clinicians
     */
    number_of_clinicians?: null | string;
    [property: string]: any;
}

/**
 * Consult note generation completed for a session
 */
export type NoteGenerationCompleted = {
    /**
     * 1-based generation attempt number for this session within the current app process
     */
    attempt_number?: number | null;
    /**
     * Platform identifier (android, ios, web)
     */
    client_platform?: null | string;
    /**
     * Chronicle device file name when applicable
     */
    device_file_name?: null | string;
    /**
     * Session recording duration in seconds (server or client convention)
     */
    duration?: number | null;
    /**
     * Recording duration — string from web clients, number (seconds) from native clients
     */
    duration_local?: number | null | string;
    /**
     * Note generation duration in milliseconds
     */
    duration_ms?: number | null;
    /**
     * Action that initiated note generation
     */
    generation_trigger?: GenerationTrigger | null;
    /**
     * Desktop-compatible alias for the local session identifier
     */
    local_session_id?: null | string;
    /**
     * Unix timestamp when generation completed
     */
    note_generation_end_time?: number | null;
    /**
     * Product platform
     */
    platform?: NoteGenerationCompletedPlatform | null;
    /**
     * Seconds since session creation when the event fired
     */
    session_age_seconds?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Mobile-compatible local session identifier
     */
    session_id_local?: null | string;
    /**
     * Analytics source tag (e.g. native)
     */
    source?: null | string;
}

export type GenerationTrigger = "offline_sync_auto_trigger" | "manual_create" | "retry";

export type NoteGenerationCompletedPlatform = "desktop" | "ios" | "android" | "web";

/**
 * Tracks when a team admin clicks contact sales
 */
export type EvidenceContactSalesClicked = {
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Team identifier of the admin
     */
    team_id?: null | string;
    [property: string]: any;
}

/**
 * Fired when a new translator user account is created
 */
export type TranslatorUserCreated = {
    /**
     * User country at account creation
     */
    country?: null | string;
}

/**
 * Tracks when a user ends an inbox session
 */
export type InboxSessionEnded = {
    /**
     * Duration of the inbox session in milliseconds
     */
    duration_ms: number;
    /**
     * Reason the session ended
     */
    reason: string;
    [property: string]: any;
}

/**
 * Tracks the first settled diarised transcript payload for a clinical session
 */
export type DiarisedTranscriptViewed = {
    /**
     * Total t11n chunks across recordings at the time of the first payload.
     */
    chunk_count?: number | null;
    /**
     * Chunks with diarised segments vs chunk_count, from summariseDiarisedPayload. none = no
     * chunk had segments (empty 200). partial = some chunks had segments. full = every chunk
     * had segments. Always sent; omit the key never, never send null.
     */
    outcome?: DiarisedTranscriptViewedOutcome;
    /**
     * Recordings on the session at the time of the first payload.
     */
    recording_count?: number | null;
    /**
     * Clinical session this payload belongs to. Warehouse join key onto note_updated /
     * feedback_submitted / note_created. Not the Snowplow client session id. Always sent; omit
     * the key never, never send null.
     */
    session_id?: string;
    /**
     * Distinct segment.speaker_id across every chunk (what the pane actually renders). Omit
     * only if the producer has no recordings yet.
     */
    speaker_count?: number | null;
    /**
     * Where the payload was observed. web vs desktop because diarisation UI availability
     * differs by surface. Not client_platform (that is a context entity). Always sent; never
     * send null.
     */
    surface?: DiarisedTranscriptViewedSurface;
    /**
     * release-scribe-diarisation-ui. Labels can render without it but are not editable. Omit
     * only if the flag was not evaluated.
     */
    ui_enabled?: boolean | null;
    /**
     * release-diarisation flag variant at emit time (C / D / empty today). Do not enum this
     * field — flag values change. Never send a label or transcript text.
     */
    variant?: null | string;
}

/**
 * Chunks with diarised segments vs chunk_count, from summariseDiarisedPayload. none = no
 * chunk had segments (empty 200). partial = some chunks had segments. full = every chunk
 * had segments. Always sent; omit the key never, never send null.
 */
export type DiarisedTranscriptViewedOutcome = "none" | "partial" | "full";

/**
 * Where the payload was observed. web vs desktop because diarisation UI availability
 * differs by surface. Not client_platform (that is a context entity). Always sent; never
 * send null.
 */
export type DiarisedTranscriptViewedSurface = "web" | "desktop";

/**
 * Tracks when a hardware recording stops
 */
export type HardwareRecordingStop = {
    /**
     * Duration of the recording in seconds
     */
    duration_seconds?: number | null;
    /**
     * Origin of the recording stop action
     */
    origin?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Local session identifier
     */
    session_id_local?: null | string;
    /**
     * Reason for stopping (normal, ble_disconnect, error)
     */
    stop_reason?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a member search is performed in the share modal
 */
export type ShareMemberSearched = {
    /**
     * Search query entered by the user
     */
    search_query?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks successful application of a signature to a letter
 */
export type LetterSigned = {
    /**
     * Whether the user applying the signature owns the session; false identifies delegated
     * workflows without exposing user IDs
     */
    applied_by_session_owner: boolean;
    /**
     * Signed letter identifier
     */
    letter_id: string;
    /**
     * Session containing the letter
     */
    session_id: string;
    /**
     * Signature variant applied to the letter
     */
    signature_type: SignatureType;
}

/**
 * Signature variant applied to the letter
 */
export type SignatureType = "electronically_signed";

/**
 * Tracks when a section is created in the note, template, or document editor
 */
export type SectionCreated = {
    /**
     * How the section was created (convert_selection, add_button, heading_shortcut,
     * slash_command)
     */
    creation_method?: null | string;
    /**
     * Which editor the section was created in (note, template, document)
     */
    editor_context?: null | string;
    /**
     * Which note-editor engine the section was created in (markdown_native, vmarkdown)
     */
    editor_type?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * For creation_method heading_shortcut, where the caret was when it fired (line_start,
     * inline). Null for the other creation methods.
     */
    trigger_position?: null | string;
    [property: string]: any;
}

/**
 * Device Event — consolidated hardware device interactions across recording devices and
 * peripherals
 */
export type DeviceEvent = {
    /**
     * Backlog-lane chunks transferred while this recording was active; empty for legacy 4.x
     * recordings
     */
    backlog_transfers_during_recording?: number | null;
    /**
     * Device battery percentage
     */
    battery_level?: number | null;
    /**
     * Chunks deferred because the device reported an error; empty for legacy 4.x recordings
     */
    chunks_deferred_err?: number | null;
    /**
     * Chunks persisted before stop began; empty for legacy 4.x recordings
     */
    chunks_synced_during_recording?: number | null;
    /**
     * Chunks remaining for the post-stop tail drain; empty for legacy 4.x recordings
     */
    chunks_tail?: number | null;
    /**
     * Total chunks in the Remote recording; empty for legacy 4.x recordings
     */
    chunks_total?: number | null;
    /**
     * What happened to or through the device
     */
    device_action?: DeviceAction | null;
    /**
     * Device category
     */
    device_class?: null | string;
    /**
     * Device serial number
     */
    device_serial?: null | string;
    /**
     * Bounded error classification when the device action fails; never a throwable or raw error
     * message
     */
    error_type?: null | string;
    /**
     * Current device firmware version
     */
    firmware_version?: null | string;
    /**
     * String-encoded level-meter support state; send "true", "false", or not_probed and never
     * send a JSON boolean
     */
    level_meter_supported?: LevelMeterSupported | null;
    /**
     * Result of the action — success or failure apply generally; blocked means a refused
     * recording_started action; finalized, dirty_stop_upload_pending,
     * dirty_stop_tail_incomplete, and all_zero_byte are recording_stopped outcomes
     */
    outcome?: DeviceEventOutcome | null;
    /**
     * Heidi Remote recording pipeline selected or used
     */
    pipeline?: Pipeline | null;
    /**
     * Remote reconnects observed while recording; empty for legacy 4.x recordings
     */
    reconnects_during_recording?: number | null;
    /**
     * Total duration of the Remote recording in seconds
     */
    recording_duration_seconds?: number | null;
    /**
     * Server identifier of the clinical session entity being acted on, not the analytics session
     */
    session_id?: null | string;
    /**
     * Local client identifier of the clinical session entity being acted on, not the analytics
     * session
     */
    session_id_local?: null | string;
    /**
     * Lifecycle path that selected the pipeline or requested the recording start
     */
    trigger?: DeviceEventTrigger | null;
    /**
     * Time in seconds from the stop request until the stop sequence fully finished
     */
    wait_after_stop_seconds?: number | null;
    [property: string]: any;
}

export type DeviceAction = "connected" | "disconnected" | "paired" | "removed" | "scan_started" | "firmware_updated" | "recording_started" | "recording_stopped" | "recording_paused" | "sync_started" | "sync_completed" | "sync_failed" | "setup_started" | "setup_completed" | "ble_disconnected" | "ble_reconnected" | "pipeline_selected";

export type LevelMeterSupported = "true" | "false" | "not_probed";

export type DeviceEventOutcome = "success" | "failure" | "blocked" | "finalized" | "dirty_stop_upload_pending" | "dirty_stop_tail_incomplete" | "all_zero_byte";

export type Pipeline = "chunk_live" | "legacy_mp3_stream" | "unresolved";

export type DeviceEventTrigger = "app_start" | "app_resume" | "device_start" | "recovered_bind";

/**
 * Tracks when the evidence specialty is changed
 */
export type EvidenceSpecialtyChanged = {
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * The new specialty ID selected
     */
    specialty_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a desktop Auth0 sign-in fails (e.g. the deep-link token-exchange could not
 * reach Auth0 on an enterprise network). Emitted by the desktop client; mirrors
 * user_signed_in plus sanitized failure context (APP-6076).
 */
export type UserSignInFailed = {
    /**
     * Authentication provider (e.g. auth0_desktop)
     */
    auth_provider?: null | string;
    /**
     * Auth type (email | google | apple | sso)
     */
    auth_type?: null | string;
    /**
     * User-facing desktop auth error code, e.g. network_error, token_exchange_failed,
     * invalid_client, secure_storage_unavailable, attempt_expired, state_mismatch,
     * callback_replay, access_denied, unknown_error
     */
    error_code?: null | string;
    /**
     * Typed transport cause for network_error; one of: timeout, dns, tls_certificate,
     * tls_config, proxy_connect, proxy_auth_required, client_build, connect, unknown
     */
    network_cause?: null | string;
    /**
     * Auth provider (kinde | auth0)
     */
    provider?: null | string;
    /**
     * Sign in method used (email | google | apple)
     */
    sign_in_method?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an AI objective output fails validation
 */
export type ObjectiveValidationFailed = {
    /**
     * Human-readable error description
     */
    error_description?: null | string;
    /**
     * Category of the validation error
     */
    error_type: string;
    /**
     * Field that failed validation
     */
    field: string;
    /**
     * Unique objective identifier
     */
    objective_id: string;
    [property: string]: any;
}

/**
 * Tracks when a personalisation insight is successfully generated
 */
export type PersonalizationCompleted = {
    /**
     * Whether new insights were generated
     */
    has_new_insights?: boolean | null;
    /**
     * Total number of insights
     */
    total_insights?: number | null;
    [property: string]: any;
}

/**
 * Tracks when the user changes the session list sort
 */
export type SessionsSortChanged = {
    /**
     * Sort field (createdAt, updatedAt, name, status)
     */
    sort_by?: null | string;
    /**
     * Sort direction (asc, desc)
     */
    sort_order?: null | string;
}

/**
 * Tracks when a user snoozes the experience feedback nudge
 */
export type EvidenceExperienceFeedbackSnoozed = {
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Number of completed queries when snoozed
     */
    query_count?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Snooze count after this snooze
     */
    snooze_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks when personalization settings are updated
 */
export type PersonalizationToggled = {
    /**
     * Custom style value
     */
    custom_style?: null | string;
    /**
     * Previous custom style
     */
    old_custom_style?: null | string;
    /**
     * Previous voice style
     */
    old_voice_style?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Source of the update
     */
    source?: null | string;
    /**
     * Type of transition
     */
    transition_type?: null | string;
    /**
     * Type of personalization update
     */
    update_action?: null | string;
    /**
     * Voice style value
     */
    voice_style?: null | string;
    [property: string]: any;
}

/**
 * Tracks integration failed event
 */
export type IntegrationFailed = {
    /**
     * Details
     */
    details?: { [key: string]: any } | null;
    /**
     * Error Message
     */
    error_message?: null | string;
    /**
     * Failure Action
     */
    failure_action?: null | string;
    /**
     * Integration Type
     */
    integration_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks referral created event
 */
export type ReferralCreated = {
    /**
     * Referral Link
     */
    referral_link?: null | string;
    [property: string]: any;
}

/**
 * Tracks cpd page viewed event
 */
export type CpdPageViewed = {
    /**
     * Total Activities
     */
    total_activities?: number | null;
    /**
     * Total Sources
     */
    total_sources?: number | null;
    /**
     * Total Time Minutes
     */
    total_time_minutes?: number | null;
    [property: string]: any;
}

/**
 * Work UMUX-Lite experience feedback nudge lifecycle action
 */
export type WorkExperienceFeedbackActioned = {
    /**
     * Lifecycle step for the Work experience feedback nudge
     */
    action: WorkExperienceFeedbackActionedAction;
    /**
     * The comment text (submitted only; empty string if none provided)
     */
    comment?: null | string;
    /**
     * Length of the comment in characters (submitted only)
     */
    comment_length?: number | null;
    /**
     * Account SKU string (e.g. pro, pro_plus, clinician_pro). Not an enum; SKUs change.
     */
    current_plan?: null | string;
    /**
     * This user's seat type (assistant, standard, or max)
     */
    current_seat_type?: TSeatType | null;
    /**
     * Ease rating 1 to 5 (submitted only)
     */
    ease_rating?: number | null;
    /**
     * Whether the submission failed due to an API error
     */
    error?: boolean | null;
    /**
     * Number of feedback items when action is submitted
     */
    feedback_count?: number | null;
    /**
     * Whether the user provided a comment (submitted only)
     */
    has_comment?: boolean | null;
    /**
     * Work project id where the nudge appeared
     */
    project_id?: null | string;
    /**
     * Completed assistant replies at the time of this action
     */
    query_count?: number | null;
    /**
     * Completed assistant replies when feedback was submitted
     */
    query_count_at_trigger?: number | null;
    /**
     * Star rating 1 to 5 for rating_submitted
     */
    rating?: number | null;
    /**
     * Which UMUX-Lite scale was rated (rating_submitted only)
     */
    rating_type?: RatingType | null;
    /**
     * Snooze count (after this action when action is snoozed)
     */
    snooze_count?: number | null;
    /**
     * Snooze count before a successful submit
     */
    snooze_count_before_submit?: number | null;
    /**
     * Usefulness rating 1 to 5 (submitted only)
     */
    usefulness_rating?: number | null;
}

/**
 * Lifecycle step for the Work experience feedback nudge
 */
export type WorkExperienceFeedbackActionedAction = "viewed" | "rating_submitted" | "submitted" | "snoozed" | "dismissed";

export type RatingType = "ease" | "usefulness";

/**
 * Tracks when a call ends normally
 */
export type CallCompleted = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Total call duration in seconds
     */
    duration_seconds: number;
    /**
     * Reason the call ended
     */
    end_reason: string;
    [property: string]: any;
}

/**
 * Tracks when an external share is confirmed
 */
export type ShareExternalConfirmed = {
    /**
     * Number of recipients for the external share
     */
    recipient_count?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user views an inbox conversation item
 */
export type InboxItemViewed = {
    /**
     * Conversation identifier
     */
    conversation_id: string;
    /**
     * Whether the conversation had unread messages
     */
    has_unread: boolean;
    /**
     * Number of unread messages in the conversation
     */
    unread_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks user clicked event
 */
export type UserClicked = {
    /**
     * Click Action
     */
    click_action?: null | string;
    [property: string]: any;
}

/**
 * Tracks community searched event
 */
export type CommunitySearched = {
    /**
     * Query
     */
    query?: null | string;
    [property: string]: any;
}

/**
 * Tracks successful clinical calculator executions
 */
export type CalculatorExecuted = {
    /**
     * Non-PHI calculator category returned by the calculator definition
     */
    calculator_category?: null | string;
    /**
     * Non-PHI stable identifier for the calculator that was executed
     */
    calculator_key?: null | string;
    /**
     * Surface where the calculator was executed
     */
    entry_point?: CalculatorExecutedEntryPoint | null;
    /**
     * Session identifier when execution occurs in an in-session surface
     */
    session_id?: null | string;
    [property: string]: any;
}

export type CalculatorExecutedEntryPoint = "session_tools_card" | "calculators_page";

/**
 * Tracks when note is pushed to EHR/integration
 */
export type NotePushed = {
    /**
     * EHR provider the note was pushed to
     */
    ehr_provider?: null | string;
    /**
     * Whether this was a sectional write-back
     */
    is_sectional_write_back?: boolean | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user performs an action that requires a paid plan
 */
export type PaidActionTracked = {
    /**
     * Date of the paid action
     */
    action_date?: null | string;
    [property: string]: any;
}

/**
 * Tracks when patient data is successfully extracted from a session
 */
export type PatientExtracted = {
    /**
     * Sources used for extraction
     */
    extraction_sources?: string[] | null;
    /**
     * Fields extracted from patient data
     */
    fields_extracted?: string[] | null;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Extraction timestamp
     */
    timestamp?: null | string;
    [property: string]: any;
}

/**
 * Tracks semantic interactions with UI elements that do not map to a more specific domain
 * event
 */
export type UIInteraction = {
    /**
     * What the user did
     */
    action_type: ActionType;
    /**
     * Eligibility signal that caused the sync-changes confirmation to be offered
     */
    armed_by?: ArmedBy | null;
    /**
     * Controlled reason for a dismiss action; passive_close covers Escape and outside-click
     */
    dismiss_reason?: null | string;
    /**
     * Static UI element identifier; never user-authored or clinical content
     */
    element_name: string;
    /**
     * Type of UI element
     */
    element_type: ElementType;
    /**
     * Static destination URL for links and CTAs; excludes user-authored query strings and
     * fragments
     */
    element_url?: null | string;
    /**
     * Static A/B variant identifier when applicable
     */
    element_variant?: null | string;
    /**
     * Opaque root entity identifier; patient extraction uses the non-reversible review_id
     */
    entity_id?: null | string;
    /**
     * Controlled type of entity associated with the interaction
     */
    entity_type?: null | string;
    /**
     * Resulting state for toggle interactions
     */
    new_value?: NewValue | null;
    /**
     * Static identifier for the current page or screen
     */
    page_name?: null | string;
    /**
     * Static identifier for the section of the current page
     */
    page_section?: null | string;
    /**
     * Opaque clinical-session identifier for the session whose UI was acted on
     */
    session_id?: null | string;
    /**
     * Session canvas tab containing the UI element
     */
    session_tab?: SessionTab | null;
    /**
     * Whether the interaction is for the user's own session or a session under review
     */
    viewer?: Viewer | null;
}

/**
 * What the user did
 */
export type ActionType = "click" | "view" | "dismiss" | "expand" | "collapse" | "scroll" | "select" | "toggle" | "navigate" | "search" | "submit" | "edit" | "configure" | "remove" | "suppress";

export type ArmedBy = "caller" | "staleness_timestamp" | "both" | "none";

/**
 * Type of UI element
 */
export type ElementType = "button" | "link" | "cta" | "tab" | "field" | "banner" | "modal" | "toast" | "coach_mark" | "spotlight" | "dropdown" | "panel" | "control" | "indicator" | "toggle" | "form" | "notepad" | "review_screen";

export type NewValue = "on" | "off";

export type SessionTab = "note" | "document" | "letter" | "research_note" | "transcript" | "context" | "session_merge";

export type Viewer = "self" | "reviewer";

/**
 * Tracks when a personalisation insight is deleted
 */
export type PersonalizationDeleted = {
    /**
     * Insight identifier
     */
    insight_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user requests an integration
 */
export type IntegrationRequested = {
    /**
     * Creation timestamp
     */
    created_at?: null | string;
    /**
     * Integration name
     */
    integration?: null | string;
    /**
     * Whether the user is a clinician decision maker
     */
    is_clinician_decision_maker?: boolean | null;
    /**
     * Other integration specified
     */
    other_integration?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a tab is activated inside an open patient profile
 */
export type PatientProfileTabViewed = {
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown.
     */
    feature_flag_variant?: null | string;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * UI surface that produced the event. Known values: drawer, full_page.
     */
    surface?: null | string;
    /**
     * Tab that was activated. Known values: sessions, details, files.
     */
    tab?: null | string;
    /**
     * UUID of the patient profile view this event belongs to. Inherited from
     * patient_profile_opened.
     */
    view_id?: null | string;
}

/**
 * Tracks form field interactions
 */
export type FieldSelected = {
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown. Populated by call sites under an active rollout; nullable elsewhere.
     */
    feature_flag_variant?: null | string;
    /**
     * Name of the field
     */
    field_name?: null | string;
    /**
     * Type of field
     */
    field_type?: null | string;
    /**
     * Screen containing the field
     */
    screen_name?: null | string;
    /**
     * Section of the screen
     */
    screen_section?: null | string;
    /**
     * UUID identifying a single field interaction. Generated FE-side on field focus and
     * propagated through field_selected, patient_searched, and any outcome event so analytics
     * can stitch a single interaction across events. Only populated by call sites that
     * participate in a stitched funnel (e.g. the patient find-or-link combobox); other
     * field_selected emitters leave this null.
     */
    search_id?: null | string;
    /**
     * Wizard step slug when multiple steps share one screen_name (e.g. dictate onboarding
     * permissions vs about-you under dictate_onboarding).
     */
    step_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks chronicle device search started event
 */
export type ChronicleDeviceSearchStarted = {
    /**
     * Source
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks a letter attachment rejected before or during upload
 */
export type LetterAttachmentRejected = {
    /**
     * Letter that would have received the attachment
     */
    letter_id: string;
    /**
     * MIME type reported for the rejected file, or null when unavailable
     */
    mime_type: null | string;
    /**
     * Stable non-localized rejection category
     */
    rejection_reason: RejectionReason;
    /**
     * Layer that rejected the attachment
     */
    rejection_stage: RejectionStage;
    /**
     * Session containing the letter
     */
    session_id: string;
    /**
     * Rejected file size in bytes, or null when unavailable
     */
    size_bytes: number | null;
}

/**
 * Stable non-localized rejection category
 */
export type RejectionReason = "file_too_large" | "file_invalid_type" | "empty_file" | "count_exceeded" | "total_size_exceeded" | "rule_violation" | "storage_error";

/**
 * Layer that rejected the attachment
 */
export type RejectionStage = "client" | "server";

/**
 * Tracks when a user skips an optional onboarding step
 */
export type OnboardingStepSkipped = {
    /**
     * Nested arm inside onboarding_variant experimental-2 (guided_tour | sample_conversation).
     * Absent outside the guided-tour eligible cohort, so its presence also marks cohort
     * membership.
     */
    onboarding_sub_variant?: null | string;
    /**
     * A/B variant for onboarding-v2 flows (e.g. control, experimental, experimental-2).
     */
    onboarding_variant?: null | string;
    /**
     * Onboarding funnel identifier (e.g. dictate_onboarding, onboarding_v2). Disambiguates
     * parallel onboarding flows sharing this event.
     */
    source?: null | string;
    /**
     * Slug of the skipped wizard step (e.g. languages, note-edit)
     */
    step_name?: null | string;
    /**
     * Note-edit skip depth-of-engagement segment (onboarding-v2 only). Preserves the
     * tasks_completed_count dimension from the legacy onboarding_learn_basics_skipped event.
     */
    tasks_completed_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks when trial settings are updated
 */
export type TrialUpdated = {
    /**
     * Action type (e.g. extended)
     */
    action?: null | string;
    /**
     * Trial activation date (ISO 8601)
     */
    activation_date?: null | string;
    /**
     * Reason for trial cancellation
     */
    cancellation_reason?: null | string;
    /**
     * Trial downgrade date (ISO 8601)
     */
    downgrade_date?: null | string;
    /**
     * Duration of the extension in hours
     */
    duration_in_hours?: number | null;
    /**
     * Trial end date (ISO 8601)
     */
    end_date?: null | string;
    /**
     * Whether the trial was extended
     */
    extended?: boolean | null;
    /**
     * Number of days the trial was extended by
     */
    extension_days?: number | null;
    /**
     * Product the trial is for (e.g. scribe, evidence)
     */
    feature?: null | string;
    /**
     * Trial plan type
     */
    plan_type?: null | string;
    /**
     * Previous trial status
     */
    previous_status?: null | string;
    /**
     * Reverse trial period
     */
    reverse_trial_period?: null | string;
    /**
     * Trial start date (ISO 8601)
     */
    start_date?: null | string;
    /**
     * Current trial status (activated, extended, upgraded, downgraded, cancelled)
     */
    status?: null | string;
    /**
     * Trial end date
     */
    trial_end_date?: null | string;
    /**
     * Trial identifier
     */
    trial_id?: null | string;
    /**
     * Trial plan name (e.g. clinician_pro, evidence_pro)
     */
    trial_plan?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the evidence language is changed
 */
export type EvidenceLanguageChanged = {
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * The new language code selected
     */
    language_code?: null | string;
    /**
     * User region
     */
    region?: null | string;
    [property: string]: any;
}

/**
 * Tracks integration patient selected event
 */
export type IntegrationPatientSelected = {
    /**
     * Integration Type
     */
    integration_type?: null | string;
    /**
     * Patient Id
     */
    patient_id?: null | string;
    [property: string]: any;
}

/**
 * Fired when the region-aware desktop updater intentionally skips an update without
 * attempting it (fail-closed) — e.g. the updater region could not be resolved, so UK/EU
 * users silently receive no update (APP-6113). This is a deliberate skip, NOT a failure;
 * tracked separately from desktop_update_failed to keep failure-rate metrics clean.
 */
export type DesktopUpdateSkipped = {
    /**
     * Which updater step skipped. Known values: check (update-check step), install
     * (download/install step)
     */
    phase: DesktopUpdateFailedPhase;
    /**
     * Why the update was skipped (extensible enum). Known values: region_unresolved
     * (resolveUpdaterRegion returned null on the region-aware path, so the updater fails closed)
     */
    reason: DesktopUpdateSkippedReason;
}

/**
 * Why the update was skipped (extensible enum). Known values: region_unresolved
 * (resolveUpdaterRegion returned null on the region-aware path, so the updater fails closed)
 */
export type DesktopUpdateSkippedReason = "region_unresolved";

/**
 * Tracks when a call summary is emailed to recipients
 */
export type CallSummaryEmailed = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Number of email recipients
     */
    recipient_count: number;
    [property: string]: any;
}

/**
 * Tracks coach mark viewed event
 */
export type CoachMarkViewed = {
    /**
     * Coach Mark Id
     */
    coach_mark_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when dictation completes
 */
export type DictationCompleted = {
    /**
     * Type of dictation
     */
    dictation_type?: null | string;
    /**
     * How long key was held
     */
    hold_duration_ms?: number | null;
    /**
     * Source of dictation
     */
    source?: null | string;
    /**
     * How dictation was triggered
     */
    triggered_by?: null | string;
    [property: string]: any;
}

/**
 * Tracks when scribe style is updated
 */
export type ScribeStyleUpdated = {
    /**
     * Brain/model used
     */
    brain?: null | string;
    /**
     * Whether this was a regeneration
     */
    is_regenerate?: boolean | null;
    /**
     * Previous brain/model
     */
    old_brain?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Tab type context
     */
    tab_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when user clicks on a clinical code
 */
export type CodeClicked = {
    /**
     * Code identifier
     */
    code_id?: null | string;
    /**
     * Type of code
     */
    code_type?: null | string;
    /**
     * Type of code interaction
     */
    interaction_type?: null | string;
    /**
     * Whether the code is billable
     */
    is_billable?: boolean | null;
    /**
     * Note identifier
     */
    note_id?: null | string;
    /**
     * Code relevance level
     */
    relevance?: null | string;
    /**
     * Replacement code identifier
     */
    replacement_code_id?: null | string;
    /**
     * Whether the replacement code is billable
     */
    replacement_code_is_billable?: boolean | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a toggle setting is updated
 */
export type ToggleUpdated = {
    /**
     * Number of enabled categories
     */
    enabled_categories_count?: number | null;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Feature context
     */
    feature?: null | string;
    /**
     * Whether the toggle is now enabled
     */
    is_enabled?: boolean | null;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Type of toggle being updated
     */
    toggle_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when context is unlinked
 */
export type ContextUnlinked = {
    /**
     * ID of unlinked context
     */
    context_id?: null | string;
    /**
     * Type of context unlinked
     */
    context_type?: null | string;
    /**
     * Remaining linked contexts
     */
    linked_count?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an external email address is added to a share
 */
export type ShareExternalEmailAdded = {
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a team member's role or settings are updated
 */
export type TeamMemberUpdated = {
    /**
     * User who made the modification
     */
    modified_by_user_id?: null | string;
    /**
     * New roles
     */
    new_role?: string[] | null;
    /**
     * Previous roles
     */
    old_role?: string[] | null;
    /**
     * Team identifier
     */
    team_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a Chronicle device firmware upgrade fails
 */
export type ChronicleFirmwareUpgradeFailed = {
    /**
     * Chronicle device identifier
     */
    device_id?: null | string;
    /**
     * Reason for the firmware upgrade failure
     */
    failure_reason?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a new organisation is created
 */
export type OrganisationCreated = {
    /**
     * Country
     */
    country?: null | string;
    /**
     * Organisation identifier
     */
    organisation_id?: null | string;
    /**
     * Organisation name
     */
    organisation_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks a user-confirmed attempt to send a letter
 */
export type LetterSendAttempted = {
    /**
     * Number of additional attachments included
     */
    attachment_count: number;
    /**
     * Number of copy recipients targeted by this attempt; previously sent recipients are
     * excluded on partial retry
     */
    copy_recipient_count: number;
    /**
     * Number of directory recipients targeted by this attempt
     */
    directory_recipient_count: number;
    /**
     * Number of direct-email recipients targeted by this attempt
     */
    email_recipient_count: number;
    /**
     * Number of direct-fax recipients targeted by this attempt
     */
    fax_recipient_count: number;
    /**
     * Whether the letter had a signature when send was confirmed
     */
    has_signature: boolean;
    /**
     * Whether the letter had a previous persisted delivery attempt
     */
    is_retry: boolean;
    /**
     * Letter being sent
     */
    letter_id: string;
    /**
     * Number of sealed-message-delivery recipients targeted by this attempt
     */
    sealed_message_delivery_recipient_count: number;
    /**
     * Session containing the letter
     */
    session_id: string;
}

/**
 * Tracks when a user submits feedback on an evidence response
 */
export type EvidenceResponseFeedbackSubmitted = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Feedback type (positive, negative)
     */
    feedback_type?: null | string;
    /**
     * Whether the user provided a comment
     */
    has_comment?: boolean | null;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Message identifier of the response being rated
     */
    message_id?: null | string;
    /**
     * Chat mode (legacy, evidence)
     */
    mode?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Position of the response in the conversation
     */
    response_position?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * User feedback comment
     */
    user_comment?: null | string;
    [property: string]: any;
}

/**
 * Tracks when integration event/record is created
 */
export type IntegrationCreated = {
    /**
     * Type of integration
     */
    integration_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks experiment exposure
 */
export type ExperimentStarted = {
    /**
     * Experiment name
     */
    experiment_name?: null | string;
    /**
     * Experiment name when not predefined on frontend
     */
    untyped_experiment_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks when template creation fails due to quota limit
 */
export type TemplateQuotaFailed = {
    /**
     * Current template count
     */
    current_count?: number | null;
    /**
     * Maximum allowed templates
     */
    quota_limit?: number | null;
    /**
     * Type of quota exceeded
     */
    quota_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks clinical code copied event
 */
export type ClinicalCodeCopied = {
    /**
     * Code
     */
    code?: null | string;
    /**
     * Code System
     */
    code_system?: null | string;
    /**
     * Kinde User Id
     */
    kinde_user_id?: null | string;
    /**
     * Note Id
     */
    note_id?: null | string;
    /**
     * Relevance
     */
    relevance?: null | string;
    /**
     * Relevance Score
     */
    relevance_score?: number | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the universal dictation CSAT prompt is dismissed
 */
export type UniversalDictationCsatDismissed = {
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks one bounded auto-link sessions eligibility evaluation at patient attach
 */
export type AutoLinkSessionsEligibilityEvaluated = {
    /**
     * Whether the user is in the auto-link rollout cohort
     */
    eligible: boolean;
    /**
     * Whether the patient-attach path evaluated the enabled rollout behavior
     */
    exposed: boolean;
    /**
     * Bounded eligibility or ineligibility classification
     */
    reason: AutoLinkSessionsEligibilityEvaluatedReason;
}

/**
 * Bounded eligibility or ineligibility classification
 */
export type AutoLinkSessionsEligibilityEvaluatedReason = "eligible" | "flag_off" | "unsupported_platform" | "managed_policy" | "no_patient" | "no_prior_sessions" | "other_ineligible";

/**
 * Tracks when the manual open button is clicked during authentication
 */
export type AuthManualOpenClicked = {
    /**
     * Authentication flow context
     */
    auth_flow?: null | string;
    [property: string]: any;
}

/**
 * Tracks demo started event
 */
export type DemoStarted = {
    /**
     * Source that triggered the event (e.g. frontend, backend)
     */
    event_source?: null | string;
    [property: string]: any;
}

/**
 * Agent run or turn started (including computer-use or workflow kickoff)
 */
export type WorkRunStarted = {
    /**
     * created, turn_started, computer_use_started, or workflow_started
     */
    action: WorkRunStartedAction;
    /**
     * Durable agent-harness run id
     */
    agent_run_id: string;
    /**
     * Desktop client capabilities were attached (CUA-eligible run)
     */
    client_capabilities_present?: boolean | null;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * Account SKU string (e.g. pro, pro_plus, clinician_pro). Not an enum; SKUs change.
     */
    current_plan?: null | string;
    /**
     * This user's seat type (assistant, standard, or max)
     */
    current_seat_type?: TSeatType | null;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, or research_terms
     */
    entry_point?: null | string;
    /**
     * Turn included attachments
     */
    has_attachments?: boolean | null;
    /**
     * run, turn, computer_use, workflow, or job
     */
    object_type: WorkRunStartedObjectType;
    /**
     * Source preset template id when the workflow was installed from gallery
     */
    preset_id?: null | string;
    /**
     * Project id when applicable
     */
    project_id?: null | string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Workflow requires desktop app (UX flag on the routine)
     */
    requires_desktop?: boolean | null;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * Tool or task id when object_type is computer_use
     */
    task_id?: null | string;
    /**
     * Firing event type when trigger_type is event_rule
     */
    trigger_event?: null | string;
    /**
     * Schedule or event-rule id when trigger_type is schedule or event_rule
     */
    trigger_id?: null | string;
    /**
     * on_demand, schedule, event_rule, or keyboard
     */
    trigger_type?: null | string;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
    /**
     * Workflow id when the run was started from a routine
     */
    workflow_id?: null | string;
}

/**
 * created, turn_started, computer_use_started, or workflow_started
 */
export type WorkRunStartedAction = "created" | "turn_started" | "computer_use_started" | "workflow_started";

/**
 * run, turn, computer_use, workflow, or job
 */
export type WorkRunStartedObjectType = "run" | "turn" | "computer_use" | "workflow" | "job";

/**
 * Tracks when a session is stopped
 */
export type SessionStopped = {
    /**
     * Duration of the session in seconds
     */
    duration_seconds?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Reason the session was stopped
     */
    stop_reason?: null | string;
    [property: string]: any;
}

/**
 * Tracks library document uploaded event
 */
export type LibraryDocumentUploaded = {
    /**
     * Collection Assignment
     */
    collection_assignment?: null | string;
    /**
     * Collection Count
     */
    collection_count?: number | null;
    /**
     * File Type
     */
    file_type?: null | string;
    /**
     * Has Collection
     */
    has_collection?: boolean | null;
    /**
     * Scope
     */
    scope?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a form fill upload fails
 */
export type FormFillFailed = {
    /**
     * Reason for failure
     */
    fail_reason?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the user accepts a post-note Evidence summary suggestion and a query is
 * generated
 */
export type EvidenceSummarySuggestionGenerated = {
    /**
     * Scribe session identifier
     */
    session_id?: null | string;
    /**
     * The Evidence query text generated from the suggestion
     */
    suggested_query?: null | string;
    [property: string]: any;
}

/**
 * Tracks account created event
 */
export type AccountCreated = {
    /**
     * Source that triggered the event (e.g. frontend, backend)
     */
    event_source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user submits experience feedback
 */
export type EvidenceExperienceFeedbackSubmitted = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * The comment text
     */
    comment?: null | string;
    /**
     * Length of the comment in characters
     */
    comment_length?: number | null;
    /**
     * Ease rating 1 to 5
     */
    ease_rating?: number | null;
    /**
     * Whether the submission failed due to an API error
     */
    error?: boolean | null;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether the user provided a comment
     */
    has_comment?: boolean | null;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Number of completed queries when feedback was submitted
     */
    query_count_at_trigger?: number | null;
    /**
     * Deprecated combined rating
     */
    rating?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Number of times the user snoozed before submitting
     */
    snooze_count_before_submit?: number | null;
    /**
     * Usefulness rating 1 to 5
     */
    usefulness_rating?: number | null;
    [property: string]: any;
}

/**
 * Tracks when a user aborts a message response
 */
export type MessageAborted = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Time until user aborted in milliseconds
     */
    duration_ms?: number | null;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Backend message ID (msg_*) of the in-flight assistant response being aborted. Joins to
     * the backend evidence.stream.outcome for the same request.
     */
    message_id?: null | string;
    /**
     * Query length in characters
     */
    query_length?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Source of the message event (evidence, ask_heidi)
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks chat context attach patient clicked event
 */
export type ChatContextAttachPatientClicked = {
    /**
     * Chat Id
     */
    chat_id?: null | string;
    /**
     * Is Mobile
     */
    is_mobile?: boolean | null;
    [property: string]: any;
}

/**
 * Tracks when an evidence tool call completes
 */
export type ToolCallCompleted = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Duration in milliseconds
     */
    duration_ms?: number | null;
    /**
     * Whether the call was during a session
     */
    is_in_session?: boolean | null;
    /**
     * LLM message identifier
     */
    llm_message_id?: null | string;
    /**
     * Tool call mode
     */
    mode?: null | string;
    /**
     * Original mode
     */
    original_mode?: null | string;
    /**
     * Region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Tool identifier
     */
    tool_id?: null | string;
    /**
     * Name of the tool called
     */
    tool_name?: null | string;
    /**
     * Tool version
     */
    version?: number | null;
    [property: string]: any;
}

/**
 * Tracks when the user switches the session list scope
 */
export type SessionsScopeChanged = {
    /**
     * Selected session list scope (all, me, direct_share)
     */
    scope?: null | string;
}

/**
 * Tracks when the introductory dark-mode spotlight auto-opens for an eligible user
 */
export type DarkModeSpotlightShown = {
}

/**
 * Tracks when an outbound call is not answered
 */
export type CallNoAnswer = {
    /**
     * Retry attempt number
     */
    attempt_number?: number | null;
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Outcome of the unanswered call
     */
    outcome: string;
    [property: string]: any;
}

/**
 * Fired when a user rates a translated message with thumbs up or thumbs down
 */
export type TranslatorTranslationRated = {
    /**
     * Which feedback form the rater was shown, so the two can be compared while both run. Known
     * values: simple_categories (flat category chips), monash_rubric (staged error log). Sent
     * by the client rather than inferred, because a categories rating with no chips selected
     * looks identical on the wire. Nullable for clients predating the field.
     */
    evaluation_ui_variant?: EvaluationUIVariant | null;
    /**
     * Translator message identifier
     */
    message_id: string;
    /**
     * The submitted rating. Known values: thumb_up, thumb_down
     */
    rating: Rating;
    /**
     * Translator session identifier
     */
    session_id: string;
    /**
     * Source language code for the original message, e.g. en
     */
    source_language: string;
    /**
     * Identifier of the speech-to-text model used
     */
    stt_model?: null | string;
    /**
     * Identifies this feedback submission; shared with the translator_evaluation_error_logged
     * events emitted for the same submission
     */
    submission_id: string;
    /**
     * Target language code for the translated message, e.g. vi
     */
    target_language: string;
    /**
     * Non-whitespace character count of the translated text. Preferred rate denominator for
     * languages that do not delimit words with spaces, e.g. yue
     */
    translated_char_count?: number | null;
    /**
     * Whitespace-delimited token count of the translated text, used as the denominator for
     * error rate
     */
    translated_word_count?: number | null;
    /**
     * Identifier of the translation model used
     */
    translation_model?: null | string;
    /**
     * Identifier of the text-to-speech model used
     */
    tts_model?: null | string;
}

export type EvaluationUIVariant = "simple_categories" | "monash_rubric";

/**
 * The submitted rating. Known values: thumb_up, thumb_down
 */
export type Rating = "thumb_up" | "thumb_down";

/**
 * Tracks schedule completed event
 */
export type ScheduleCompleted = {
    /**
     * Ehr Provider
     */
    ehr_provider?: null | string;
    [property: string]: any;
}

/**
 * ChenMed PIN protocols were turned on or off for a session (gate modal answer or
 * header/card toggle)
 */
export type NudgeProtocolsToggled = {
    /**
     * True when PIN protocols were turned on, false when turned off or declined
     */
    enabled: boolean;
    /**
     * Heidi session the PIN-protocol answer belongs to
     */
    session_id: string;
    /**
     * Which surface originated the action: session_header (V1 header toggle), session_card (V2
     * session-card row), gate_modal (pre-session PIN gate modal)
     */
    source: NudgeProtocolsToggledSource;
}

/**
 * Which surface originated the action: session_header (V1 header toggle), session_card (V2
 * session-card row), gate_modal (pre-session PIN gate modal)
 */
export type NudgeProtocolsToggledSource = "session_header" | "session_card" | "gate_modal";

/**
 * Tracks when a user successfully signs in
 */
export type SignInSuccessful = {
    /**
     * Authentication method used (e.g. password, social, sso)
     */
    authentication_method?: null | string;
    [property: string]: any;
}

/**
 * Tracks sof launch error event
 */
export type SofLaunchError = {
    /**
     * Ehr Provider
     */
    ehr_provider?: null | string;
    /**
     * Error Code
     */
    error_code?: null | string;
    /**
     * Error Description
     */
    error_description?: null | string;
    /**
     * Error Type
     */
    error_type?: null | string;
    /**
     * Profile Name
     */
    profile_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks document synced event
 */
export type DocumentSynced = {
    /**
     * Document Id
     */
    document_id?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Fired when a bulk audio upload is successfully processed
 */
export type TranslatorBulkUploadCompleted = {
    /**
     * Duration of the uploaded audio in seconds
     */
    audio_duration_seconds?: number | null;
    /**
     * Bulk upload identifier
     */
    bulk_upload_id: string;
    /**
     * Number of segments that failed processing
     */
    failed_segment_count: number;
    /**
     * MIME type of the uploaded audio file
     */
    file_mime_type?: null | string;
    /**
     * Size of the uploaded file in bytes
     */
    file_size_bytes?: number | null;
    /**
     * Number of messages successfully persisted
     */
    persisted_message_count: number;
    /**
     * Total number of audio segments produced by diarization
     */
    segment_count: number;
    /**
     * Translator session identifier
     */
    session_id: string;
    /**
     * Total wall-clock processing time in milliseconds
     */
    total_duration_ms?: number | null;
}

/**
 * Tracks evidence onboarding viewed event
 */
export type EvidenceOnboardingViewed = {
    /**
     * Evidence Location
     */
    evidence_location?: null | string;
    /**
     * Has Existing Jurisdiction
     */
    has_existing_jurisdiction?: boolean | null;
    /**
     * Has Existing Specialty
     */
    has_existing_specialty?: boolean | null;
    /**
     * Is Evidence Pro
     */
    is_evidence_pro?: boolean | null;
    /**
     * Is Managed
     */
    is_managed?: boolean | null;
    /**
     * Is Team Member
     */
    is_team_member?: boolean | null;
    /**
     * Region
     */
    region?: null | string;
    [property: string]: any;
}

/**
 * Tracks when new patient is created
 */
export type PatientCreated = {
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown. Belt-and-braces with the flagsmith_flag_evaluated webhook log; lets dashboards
     * slice without a join. Nullable for legacy call sites.
     */
    feature_flag_variant?: null | string;
    /**
     * Whether patient was created from EHR
     */
    is_from_ehr?: boolean | null;
    /**
     * How the create flow was reached. Known values: create_after_search_results (searched,
     * results shown, user created instead of picking) — disambiguation signal,
     * create_after_zero_results (searched, no results, created) — search quality signal,
     * create_without_search (empty-state create, no query typed). Only meaningful when surface
     * = combobox_session_header; nullable elsewhere.
     */
    match_method?: null | string;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * Number of search results visible in the dropdown at the moment the user opted to create
     * instead. Null when no search occurred.
     */
    result_count_seen?: number | null;
    /**
     * UUID identifying a single field interaction. Generated FE-side on field focus and
     * propagated through field_selected, patient_searched, and the outcome event so the funnel
     * can stitch a single interaction across events. Nullable for legacy call sites.
     */
    search_id?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * UI surface that produced the creation. Known values: combobox_session_header (new patient
     * find-or-link combobox), legacy_session_header (legacy free-text input on the session
     * header), chat_context (create from chat attach-patient flow), integration_select (created
     * via integration / EHR picker). Nullable for legacy call sites that have not yet been
     * re-instrumented.
     */
    surface?: null | string;
    /**
     * Milliseconds from field_selected (focus) to this outcome event. FE-computed; treat as
     * best-effort and apply outlier filtering at query time (e.g. drop > 300_000ms) — values
     * can be inflated by tab switches, device sleep, or the user walking away.
     */
    time_to_outcome_ms?: number | null;
    [property: string]: any;
}

/**
 * Tracks when note is modified
 */
export type NoteUpdated = {
    /**
     * Current word count
     */
    current_word_length?: number | null;
    /**
     * Edit rate metric
     */
    edit_rate?: number | null;
    /**
     * Original word count
     */
    original_word_length?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Source of the note update
     */
    source?: null | string;
    /**
     * Template identifier
     */
    template_id?: null | string;
    /**
     * Type of update when source is evidence
     */
    update_type?: null | string;
    /**
     * Voice style used
     */
    voice_style?: null | string;
    /**
     * Word count difference
     */
    word_diff?: number | null;
    [property: string]: any;
}

/**
 * Tracks which note editor variant was rendered for a session
 */
export type NoteEditorRendered = {
    /**
     * Editor variant rendered (json, markdown_native, block_v1, block_markdown, rich_text,
     * textarea)
     */
    editor_type?: null | string;
    /**
     * Whether the markdown-native editor flag was on for this user. A completed JSON note
     * outranks the flag, so a flagged user can render editor_type json; this field recovers
     * that cohort.
     */
    markdown_native_enabled?: boolean | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a call fails due to a technical error
 */
export type CallFailed = {
    /**
     * Retry attempt number
     */
    attempt_number?: number | null;
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Error code from the telephony provider
     */
    error_code?: null | string;
    /**
     * Human-readable error description
     */
    error_description: string;
    /**
     * Category of the error
     */
    error_type: string;
    [property: string]: any;
}

/**
 * Tracks notification updated event
 */
export type NotificationUpdated = {
    /**
     * Is Inmail Notification Enabled
     */
    is_inmail_notification_enabled?: boolean | null;
    /**
     * Is Session Expiring Notification Enabled
     */
    is_session_expiring_notification_enabled?: boolean | null;
    /**
     * Update Action
     */
    update_action?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user completes their first session
 */
export type FirstSessionCompleted = {
    /**
     * Duration of the session in seconds
     */
    duration_seconds?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks evidence onboarding saved event
 */
export type EvidenceOnboardingSaved = {
    /**
     * Evidence Location
     */
    evidence_location?: null | string;
    /**
     * Is Evidence Pro
     */
    is_evidence_pro?: boolean | null;
    /**
     * Is Managed
     */
    is_managed?: boolean | null;
    /**
     * Is Team Member
     */
    is_team_member?: boolean | null;
    /**
     * Jurisdiction
     */
    jurisdiction?: null | string;
    /**
     * Jurisdiction Changed
     */
    jurisdiction_changed?: boolean | null;
    /**
     * Region
     */
    region?: null | string;
    /**
     * Specialty Changed
     */
    specialty_changed?: boolean | null;
    /**
     * Specialty Id
     */
    specialty_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when entity verification fails for a session
 */
export type VerificationFailed = {
    /**
     * Error message describing the failure
     */
    error_message?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Which step failed (extraction, verification)
     */
    step?: null | string;
    /**
     * LLM model used in the failed step
     */
    step_model?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a chat is deleted
 */
export type ChatDeleted = {
    /**
     * Chat identifier that was deleted
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Location where chat was deleted
     */
    location?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Source of the chat event (evidence, ask_heidi)
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks task completed event
 */
export type TaskCompleted = {
    /**
     * ID of the user the task is assigned to at time of event (null if unassigned)
     */
    assignee_id?: null | string;
    /**
     * Type of assignee (e.g. user, team). Null if unassigned.
     */
    assignee_type?: null | string;
    /**
     * Completion Action
     */
    completion_action?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Task Category. Known values: document, action, communicate, order, coordinate
     */
    task_category?: null | string;
    /**
     * Task Id
     */
    task_id?: null | string;
    /**
     * Task Source
     */
    task_source?: null | string;
    /**
     * Task Time To Completion
     */
    task_time_to_completion?: number | null;
    [property: string]: any;
}

/**
 * Tracks when a subscription is updated (upgrade, downgrade, renewal, plan change, seat
 * change)
 */
export type SubscriptionUpdated = {
    /**
     * Billing interval (month/year)
     */
    billing_interval?: null | string;
    /**
     * Billing period (month/year)
     */
    billing_period?: null | string;
    /**
     * Stripe coupon ID applied to the invoice, if any
     */
    coupon_id?: null | string;
    /**
     * Subscription end date (ISO 8601)
     */
    end_date?: null | string;
    /**
     * Product the subscription is for (e.g. scribe, evidence)
     */
    feature?: null | string;
    /**
     * Plan after the update
     */
    plan?: null | string;
    /**
     * Current subscription plan type
     */
    plan_type?: null | string;
    /**
     * Previous subscription plan (the previous plan; do not add a duplicate previous_plan field)
     */
    previous_plan_type?: null | string;
    /**
     * Number of seats on the billing account before the update
     */
    previous_seat_count?: number | null;
    /**
     * Billing seat type before the update matching BillingSeatType (ASSISTANT, STANDARD, or MAX)
     */
    previous_seat_type?: PreviousSeatTypeEnum | null;
    /**
     * Stripe promotion code ID redeemed for the coupon, if any
     */
    promotion_code?: null | string;
    /**
     * Number of seats on the billing account after the update
     */
    seat_count?: number | null;
    /**
     * Billing seat type after the update matching BillingSeatType (ASSISTANT, STANDARD, or MAX)
     */
    seat_type?: PreviousSeatTypeEnum | null;
    /**
     * Subscription start date (ISO 8601)
     */
    start_date?: null | string;
    /**
     * Subscription identifier
     */
    subscription_id?: null | string;
    /**
     * Billing account / team id (opaque)
     */
    team_id?: null | string;
    /**
     * Type of update: upgrade | downgrade | plan_change | seat_change | renewal
     */
    update_type?: null | string;
    [property: string]: any;
}

export type PreviousSeatTypeEnum = "ASSISTANT" | "STANDARD" | "MAX";

/**
 * Tracks payment initiation
 */
export type PaymentStarted = {
    /**
     * Source of the payment event
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks context file upload started event
 */
export type ContextFileUploadStarted = {
    /**
     * File Count
     */
    file_count?: number | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Upload Source
     */
    upload_source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when session is created
 */
export type SessionCreated = {
    /**
     * Where the session was created from. Known values include patient_surface (session started
     * from the patient list / drawer / profile, added 2026-06). FE may extend without a schema
     * bump.
     */
    entry_point?: null | string;
    /**
     * Whether session was created for another user
     */
    is_for_other_user?: boolean | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * UUID of the originating patient-surface view if the session was started from a patient
     * profile / drawer / list. Inherited from the patient_profile_opened event that bounded the
     * user's interaction with that view. Null when entry_point is anything other than
     * patient_surface. Enables stitching the open → session-created funnel per view rather than
     * relying on time-correlation. Added in 2-0-4.
     */
    view_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when sessions are unlinked from patient
 */
export type PatientUnlinked = {
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * Session identifiers being unlinked
     */
    session_ids?: string[];
    [property: string]: any;
}

/**
 * Tracks one clinician correction of diarised speaker attribution, success or failure
 */
export type SpeakerAttributionUpdated = {
    /**
     * Why the PATCH failed, from diarisationPatchFailureReason. Bounded tokens only: timeout,
     * http_5xx, http_4xx, zod_parse, unknown. Never send Error.message or any user/clinical
     * content. Do not enum this field — new error names must not Iglu-drop. Omit the key unless
     * outcome is failed (producer-enforced).
     */
    failure_reason?: null | string;
    /**
     * Whether the PATCH succeeded. Always sent; omit the key never, never send null.
     */
    outcome?: SpeakerAttributionUpdatedOutcome;
    /**
     * Whether the session has a linked patient at the time of the edit.
     */
    patient_linked?: boolean | null;
    /**
     * Rows in the PATCH. For apply_all this is the expected source-speaker segment-ref count
     * from the caller, not a guessed length. Omit on add_person (roster create has no PATCH
     * rows).
     */
    segment_count?: number | null;
    /**
     * Clinical session this correction belongs to. Warehouse join key onto
     * diarised_transcript_viewed and note_*. Not the Snowplow client session id. Always sent;
     * omit the key never, never send null.
     */
    session_id?: string;
    /**
     * Role of the speaker being corrected before the edit. Known values: patient, clinician.
     * Omit the key when unknown — do not send a display name. Do not enum this field; null and
     * omit must both validate, and Iglu enum+[string,null] without null in the enum list would
     * drop those rows.
     */
    source_role?: null | string;
    /**
     * Roster size at the time of the edit. Omit when the producer has no roster.
     */
    speaker_count?: number | null;
    /**
     * Role after the edit. Known values: patient, clinician. Omit the key when unknown — do not
     * send a display name. Do not enum this field (same null/omit reason as source_role).
     */
    target_role?: null | string;
    /**
     * Kind of correction. reassign / split / apply_all match SegmentSpeakersPatchKind.
     * patient_assigned is the reachable rename path on useMutatePatchSpeakerLabels (not the
     * roles PATCH); the same PATCH sends patient_unlinked when the popover Unlink patient row
     * clears the patient name (rows = speakers cleared), and assignee_removed when the popover
     * Remove assignee row clears one speaker's label (one `{ id: null }` row, segment_count 1).
     * add_person is the speaker-roster POST from useMutateCreateSpeaker (Add someone); the
     * following turn move still emits reassign / apply_all. Always sent; omit the key never,
     * never send null.
     */
    update_action?: SpeakerAttributionUpdatedUpdateAction;
}

/**
 * Whether the PATCH succeeded. Always sent; omit the key never, never send null.
 */
export type SpeakerAttributionUpdatedOutcome = "success" | "failed";

/**
 * Kind of correction. reassign / split / apply_all match SegmentSpeakersPatchKind.
 * patient_assigned is the reachable rename path on useMutatePatchSpeakerLabels (not the
 * roles PATCH); the same PATCH sends patient_unlinked when the popover Unlink patient row
 * clears the patient name (rows = speakers cleared), and assignee_removed when the popover
 * Remove assignee row clears one speaker's label (one `{ id: null }` row, segment_count 1).
 * add_person is the speaker-roster POST from useMutateCreateSpeaker (Add someone); the
 * following turn move still emits reassign / apply_all. Always sent; omit the key never,
 * never send null.
 */
export type SpeakerAttributionUpdatedUpdateAction = "reassign" | "apply_all" | "split" | "patient_assigned" | "patient_unlinked" | "assignee_removed" | "add_person";

/**
 * Tracks when the personalised source recommendation banner is shown to an
 * evidence-activated user. Top of the source-recommendation funnel (shown -> dismissed |
 * applied | customised). `matched_*` fields reflect the BE-selected recommendation, not the
 * user's current profile.
 * 2-0-0: renamed `location` -> `banner_location` (MODEL bump). The 1-0-0 property name
 * caused snowtype to emit a top-level TypeScript `Location` alias that shadowed the DOM
 * `Location` global; the rename scopes the alias to the source-recommendation banner and
 * makes snowtype emit `BannerLocation` instead. No other shape changes vs 1-0-0.
 */
export type SourceRecommendationShown = {
    /**
     * Banner surface that produced the event. Known values: chat (in-chat banner above the
     * floating ask-Heidi input), popover (apply popover surfaced from source control), session
     * (banner inside an active session).
     */
    banner_location?: BannerLocation;
    /**
     * Human-readable name of the country whose tag drove the BE's regional ordering of the
     * recommendation (e.g. "Australia"). Empty string when unresolved (no country tag matched).
     */
    matched_country?: null | string;
    /**
     * BE-matched specialty id used to score the recommendation. May fall back to a
     * generic-practitioner id when the user's profile has no specialty. Null when unresolved.
     */
    matched_specialty_id?: null | string;
    /**
     * Human-readable name of `matched_specialty_id` (e.g. "Cardiology"). Empty string during
     * hydration before the specialties list resolves. Use this for analytics slicing rather
     * than the user's profile specialty so funnels reflect the recommendation that was actually
     * served.
     */
    matched_specialty_name?: null | string;
    /**
     * Number of recommendations offered in the banner (pre-cap, BE `total_domains`). Zero when
     * the recommendation set was empty for this user.
     */
    sources_count?: number | null;
}

/**
 * Banner surface that produced the event. Known values: chat (in-chat banner above the
 * floating ask-Heidi input), popover (apply popover surfaced from source control), session
 * (banner inside an active session).
 */
export type BannerLocation = "chat" | "popover" | "session";

/**
 * Tracks when a pre-chart is sent to the session's context
 */
export type PrechartSentToContext = {
    /**
     * Character count of the pre-chart markdown sent to context. 1MB cap. Never the content
     * itself (PII).
     */
    content_length?: number | null;
    /**
     * The pre-chart document being sent. Should resolve from the same source as on the
     * corresponding prechart_viewed event for clean per-document funnel stitching.
     */
    document_id?: null | string;
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown. Pre-GA stamps treated or unknown.
     */
    feature_flag_variant?: null | string;
    /**
     * True if the session's context already had content before this send (i.e. the pre-chart is
     * augmenting an existing context, not seeding a fresh one).
     */
    had_existing_context?: boolean | null;
    /**
     * Session the context-append occurred in
     */
    session_id?: null | string;
    /**
     * The template applied during the originating generation, if any. Same value as on the
     * corresponding prechart_viewed event; lets the funnel slice end-to-end by template. Null
     * when no template was applied.
     */
    template_id?: null | string;
}

/**
 * Tracks message stopped event
 */
export type MessageStopped = {
    /**
     * Chat Id
     */
    chat_id?: null | string;
    /**
     * Backend message ID (msg_*) of the in-flight response the user stopped. Joins to the
     * backend evidence.stream.outcome for the same request.
     */
    message_id?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a schedule is synced
 */
export type ScheduleSynced = {
    /**
     * EHR provider if applicable
     */
    ehr_provider?: null | string;
    [property: string]: any;
}

/**
 * Tracks snippet updated event
 */
export type SnippetUpdated = {
    /**
     * Content Length
     */
    content_length?: number | null;
    /**
     * Shorthand Length
     */
    shorthand_length?: number | null;
    /**
     * Snippet Id
     */
    snippet_id?: null | string;
    /**
     * Source
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Consult note generation started for a session
 */
export type NoteGenerationStarted = {
    /**
     * 1-based generation attempt number for this session within the current app process
     */
    attempt_number?: number | null;
    /**
     * Platform identifier (android, ios, web)
     */
    client_platform?: null | string;
    /**
     * Chronicle device file name when applicable
     */
    device_file_name?: null | string;
    /**
     * Action that initiated note generation
     */
    generation_trigger?: GenerationTrigger | null;
    /**
     * Desktop-compatible alias for the local session identifier
     */
    local_session_id?: null | string;
    /**
     * Unix timestamp when generation started
     */
    note_generation_start_time?: number | null;
    /**
     * Product platform
     */
    platform?: NoteGenerationCompletedPlatform | null;
    /**
     * Seconds since session creation when the event fired
     */
    session_age_seconds?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Mobile-compatible local session identifier
     */
    session_id_local?: null | string;
    /**
     * Analytics source tag (e.g. native)
     */
    source?: null | string;
    /**
     * Whether a template identifier was available; the identifier itself is not captured
     */
    template_id_present?: boolean | null;
    /**
     * Whether the flow is user-facing (string or boolean depending on client platform)
     */
    user_facing?: boolean | null | string;
}

/**
 * Tracks book demo country selected event
 */
export type BookDemoCountrySelected = {
    /**
     * Country
     */
    country?: null | string;
    /**
     * Country Code
     */
    country_code?: null | string;
    [property: string]: any;
}

/**
 * Tracks when transcription process completes
 */
export type TranscriptionCompleted = {
    /**
     * Duration of transcription
     */
    duration?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Type of transcription
     */
    transcription_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks a retryable or terminal failure in an offline recording sync
 */
export type OfflineSyncFailed = {
    /**
     * Number of sync attempts made
     */
    attempt_count: number | null;
    /**
     * Residency region code used for the sync
     */
    capture_region: string;
    /**
     * Stable machine-readable error code; never a raw exception string
     */
    error_code: null | string;
    /**
     * Desktop-generated local session identifier
     */
    local_session_id: string;
    /**
     * Sync stage or terminal status associated with the failure
     */
    phase: OfflineSyncFailedPhase;
    /**
     * Server session identifier when already created
     */
    session_id: null | string;
    /**
     * Recording source
     */
    source: OfflineSyncDeferredSource;
    /**
     * Whether the client expects to retry automatically
     */
    will_retry: boolean | null;
}

/**
 * Sync stage or terminal status associated with the failure
 */
export type OfflineSyncFailedPhase = "creating_session" | "uploading_audio" | "processing" | "metadata_patch" | "duration_patch" | "sync_failed" | "upload_failed" | "auth_required" | "unrecoverable";

/**
 * Tracks survey updated event
 */
export type SurveyUpdated = {
    /**
     * Update Action
     */
    update_action?: null | string;
    /**
     * Value
     */
    value?: null | string;
    [property: string]: any;
}

/**
 * Tracks when cursor dictation post-processing fails
 */
export type CursorDictationPostProcessingFailed = {
    /**
     * Actual LLM model used (may differ from requested)
     */
    actual_llm_model?: null | string;
    /**
     * Content type of the dictation output
     */
    content_type?: null | string;
    /**
     * Dictation session identifier
     */
    dictation_id?: null | string;
    /**
     * Type of error that caused the failure
     */
    error_type?: null | string;
    /**
     * Reason for the post-processing failure
     */
    failure_reason?: null | string;
    /**
     * Input languages detected in the dictation
     */
    input_languages?: string[] | null;
    /**
     * Whether a fallback model was used
     */
    is_fallback?: boolean | null;
    /**
     * LLM model used for post-processing
     */
    llm_model?: null | string;
    /**
     * Output language for the dictation
     */
    output_language?: null | string;
    /**
     * Duration of post-processing in milliseconds
     */
    processing_duration_ms?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks library link clicked event
 */
export type LibraryLinkClicked = {
    /**
     * Source
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when user clicks to upgrade from trial
 */
export type TrialUpgradeClicked = {
    /**
     * Modal context
     */
    modal_type?: null | string;
    /**
     * Type of trial
     */
    trial_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an evidence chat response completes
 */
export type EvidenceChatCompleted = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Chip category (shortcut/suggestion only)
     */
    chip_category?: null | string;
    /**
     * Chip display label (shortcut/suggestion only)
     */
    chip_label?: null | string;
    /**
     * User billing tier at completion time
     */
    current_plan?: null | string;
    /**
     * Total time from submit to completion in milliseconds
     */
    duration_ms?: number | null;
    /**
     * How the original message was initiated
     */
    entry_method?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Backend message ID (msg_*) of the assistant response. Joins engagement events to the
     * specific model that produced this response (per-message model attribution).
     */
    message_id?: null | string;
    /**
     * Pin origin for shortcut chips
     */
    origin?: Origin | null;
    /**
     * Query length in characters
     */
    query_length?: number | null;
    /**
     * Wait time in the queue before dispatch, in milliseconds
     */
    queue_duration_ms?: number | null;
    /**
     * True when the original message was dispatched from the queue
     */
    queued?: boolean | null;
    /**
     * True when the completed response included a quota-exhausted paywall message
     */
    quota_exhausted_message_shown?: boolean | null;
    /**
     * Number of references in the response
     */
    references_count?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Response length in characters
     */
    response_length?: number | null;
    /**
     * The mode the backend routed to (legacy, evidence)
     */
    routed_mode?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Chat completion state
     */
    state?: null | string;
    /**
     * UI surface that produced the click (chip / banner clicks)
     */
    surface?: null | string;
    /**
     * Number of Tavily web searches performed
     */
    tavily_search_count?: number | null;
    /**
     * Time to first token in milliseconds
     */
    time_to_first_token_ms?: number | null;
    /**
     * Tools used in this response
     */
    tools_used?: string[] | null;
    /**
     * Response length in words
     */
    word_count?: number | null;
    [property: string]: any;
}

export type Origin = "default" | "library" | "custom";

/**
 * Tracks when user successfully completes authentication/sign-in
 */
export type AuthenticationCompleted = {
    /**
     * Authentication flow type
     */
    authentication_flow?: null | string;
    /**
     * Custom UTM source for tracking
     */
    custom_utm_source?: null | string;
    /**
     * Facebook Click ID
     */
    fbclid?: null | string;
    /**
     * Google gbraid parameter
     */
    gbraid?: null | string;
    /**
     * Google Click ID
     */
    gclid?: null | string;
    /**
     * Feature that attributed/drove the user to sign up
     */
    last_attributed_feature?: null | string;
    /**
     * Microsoft Click ID
     */
    msclkid?: null | string;
    /**
     * Reddit Click ID
     */
    rdt_cid?: null | string;
    /**
     * TikTok Click ID
     */
    ttclid?: null | string;
    /**
     * UTM campaign parameter
     */
    utm_campaign?: null | string;
    /**
     * UTM content parameter
     */
    utm_content?: null | string;
    /**
     * UTM medium parameter
     */
    utm_medium?: null | string;
    /**
     * UTM term parameter
     */
    utm_term?: null | string;
    /**
     * Google wbraid parameter
     */
    wbraid?: null | string;
    [property: string]: any;
}

/**
 * Tracks schedule upload promo viewed event
 */
export type ScheduleUploadPromoViewed = {
    /**
     * Display
     */
    display?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a template is viewed
 */
export type TemplateViewed = {
    /**
     * Template identifier
     */
    template_id?: null | string;
    [property: string]: any;
}

/**
 * User submitted a Work message or turn (composer, home, in-session)
 */
export type WorkMessageSubmitted = {
    /**
     * submitted
     */
    action?: null | string;
    /**
     * Durable agent-harness run id
     */
    agent_run_id?: null | string;
    /**
     * Attachments on the turn
     */
    attachment_count?: number | null;
    /**
     * sidebar, header, empty_state, work_home, or in_session
     */
    click_source?: null | string;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * Clinical session id when the turn originated in a session
     */
    clinical_session_id?: null | string;
    /**
     * Account SKU string (e.g. pro, pro_plus, clinician_pro). Not an enum; SKUs change.
     */
    current_plan?: null | string;
    /**
     * This user's seat type (assistant, standard, or max)
     */
    current_seat_type?: TSeatType | null;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, or research_terms
     */
    entry_point?: null | string;
    /**
     * Client capabilities were supplied
     */
    has_client_capabilities?: boolean | null;
    /**
     * Computer-use skill was available
     */
    has_computer_use_skill?: boolean | null;
    /**
     * Prompt started with a slash command
     */
    has_slash_command?: boolean | null;
    /**
     * Project id when applicable
     */
    project_id?: null | string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * Prompt length in characters
     */
    query_length?: number | null;
    /**
     * Count of referenced objects in the prompt
     */
    referenced_object_count?: number | null;
    /**
     * Object types referenced in the prompt (known values: session, patient, file, source,
     * routine). Canonical home for prompt input.
     */
    referenced_object_types?: string[] | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Skills attached
     */
    skill_count?: number | null;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * Clinical task id for turns submitted from a delegated task
     */
    task_id?: null | string;
    /**
     * Zero-based turn index within the run
     */
    turn_index?: number | null;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
}

/**
 * Tracks template creation record created event
 */
export type TemplateCreationRecordCreated = {
    /**
     * Request Methods
     */
    request_methods?: string[] | null;
    [property: string]: any;
}

/**
 * Tracks when a scheduled call is skipped
 */
export type ScheduledCallSkipped = {
    /**
     * Unique scheduled call identifier
     */
    scheduled_call_id: string;
    /**
     * Reason the scheduled call was skipped
     */
    skip_reason: string;
    [property: string]: any;
}

/**
 * Tracks library upload nudge clicked event
 */
export type LibraryUploadNudgeClicked = {
    /**
     * Nudge Type
     */
    nudge_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a task status transitions, capturing from and to status values
 */
export type TaskStatusChanged = {
    /**
     * Status before the transition. Known values: todo, done, archived
     */
    from_status?: null | string;
    /**
     * Associated session ID if task belongs to a session
     */
    session_id?: null | string;
    /**
     * Unique identifier of the task
     */
    task_id?: null | string;
    /**
     * Status after the transition. Known values: todo, done, archived
     */
    to_status?: null | string;
    [property: string]: any;
}

/**
 * Tracks when community template is viewed
 */
export type CommunityTemplateViewed = {
    /**
     * Template ID
     */
    template_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when organisation settings or membership are updated
 */
export type OrganisationUpdated = {
    /**
     * Organisation update action
     */
    action?: null | string;
    /**
     * Member Kinde user identifier
     */
    member_kinde_user_id?: null | string;
    /**
     * MFA information
     */
    mfa_info?: { [key: string]: any } | null;
    /**
     * Organisation identifier
     */
    org_id?: null | string;
    /**
     * Whether patient consent is required
     */
    require_patient_consent?: boolean | null;
    /**
     * Role identifiers
     */
    role_ids?: string[] | null;
    /**
     * Session retention settings
     */
    session_retention_settings?: { [key: string]: any } | null;
    /**
     * Source team identifier
     */
    source_team_id?: null | string;
    /**
     * Source team name
     */
    source_team_name?: null | string;
    /**
     * Target team name
     */
    target_team_name?: null | string;
    /**
     * Team identifier
     */
    team_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a clinician's pre-chart instructions are saved
 */
export type PrechartInstructionsUpdated = {
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown. BE-emitted; stamp unknown pre-GA since the pre-chart surface is fully flag-gated
     * and there is no held-back control.
     */
    feature_flag_variant?: null | string;
    /**
     * True when no existing-doc row was found on the BE upsert check (i.e. this user is
     * creating instructions for the first time). Derivable from previous_rules_length IS NULL
     * but kept as an explicit boolean to save a CASE in every dashboard query.
     */
    is_first_time?: boolean | null;
    /**
     * Character count of the prior instructions text. Null when is_first_time = true.
     */
    previous_rules_length?: number | null;
    /**
     * Character count of the new instructions text. Typical instructions are 100-2000 chars;
     * the 100000 cap is generous headroom. Tighten via REVISION bump if the BE product cap
     * turns out to be smaller.
     */
    rules_length?: number | null;
    /**
     * Whether the AI-assist chat (POST /prechart/instructions/edit) was used to draft this
     * version. Set FE-side and passed through on the save request — the BE persistence handler
     * reads it from the payload rather than deriving from a preceding edit call by the same
     * user. False / null = manual edit only.
     */
    used_ai_assist?: boolean | null;
}

/**
 * Tracks when an Evidence user is active (5+ queries in a week)
 */
export type EvidenceUserActive = {
    /**
     * Number of Evidence queries in the week
     */
    query_count?: number | null;
    /**
     * ISO week number of the activity
     */
    week_number?: number | null;
    [property: string]: any;
}

/**
 * A nudge checklist item was checked
 */
export type NudgeItemChecked = {
    /**
     * Version of the protocol catalogue that produced this card
     */
    catalogue_version: string;
    /**
     * Classifier confidence for the match (0-1)
     */
    confidence: number;
    /**
     * Join key back to the PHI-safe detection context persisted on NudgeCardState. Null when no
     * detection pass produced the card
     */
    detection_id?: null | string;
    /**
     * Identifier of the checklist item that was checked
     */
    item_id: string;
    /**
     * Number of matched signals (length of matched_signals)
     */
    matched_signal_count?: number | null;
    /**
     * Named signals (chips) the classifier matched. PHI-free labels only
     */
    matched_signals?: string[];
    /**
     * Identifier of the model used by the classifier
     */
    model_id?: null | string;
    /**
     * Version of the classifier prompt, for slicing false-positive rates by classifier change
     */
    prompt_version?: null | string;
    /**
     * Identifier of the protocol the item belongs to
     */
    protocol_id: string;
    /**
     * Heidi session the nudge card belongs to
     */
    session_id: string;
    /**
     * How the card came to exist. Known values: auto (classifier-surfaced), manual (clinician
     * added from the protocol library)
     */
    source?: DownloadTrigger;
    /**
     * Hash of the transcript chunk that drove the detection; a join key only, never the raw text
     */
    transcript_chunk_hash?: null | string;
}

/**
 * Server created a Work project (and backing agent run)
 */
export type WorkProjectCreated = {
    /**
     * created
     */
    action: WorkProjectCreatedAction;
    /**
     * Durable agent-harness run id
     */
    agent_run_id: string;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * Account SKU string (e.g. pro, pro_plus, clinician_pro). Not an enum; SKUs change.
     */
    current_plan?: null | string;
    /**
     * This user's seat type (assistant, standard, or max)
     */
    current_seat_type?: TSeatType | null;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, or research_terms
     */
    entry_point?: null | string;
    /**
     * Project id
     */
    project_id: string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * home, voice, workflow, schedule, or in_session
     */
    source?: null | string;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
}

/**
 * created
 */
export type WorkProjectCreatedAction = "created";

/**
 * Tracks snippet deleted event
 */
export type SnippetDeleted = {
    /**
     * Snippet Id
     */
    snippet_id?: null | string;
    /**
     * Source
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when appointments are synced with the EHR
 */
export type EhrAppointmentSynced = {
    /**
     * Number of appointments synced
     */
    appointment_count: number;
    /**
     * Clinic tenant identifier
     */
    clinic_id?: null | string;
    /**
     * EHR system provider name
     */
    ehr_provider: string;
    /**
     * Source that triggered the sync
     */
    sync_source: string;
    [property: string]: any;
}

/**
 * Tracks when session is copied
 */
export type SessionCopied = {
    /**
     * Method used to copy
     */
    copy_method?: null | string;
    /**
     * State of the four copy-formatting toggles at copy time. Attached on every format, so a
     * legacy-cohort copy can be compared against a toggle-cohort copy across a feature-flag
     * flip.
     */
    copy_settings?: null | SessionCopiedCopySettings;
    /**
     * UI source of the copy, such as rhs_copy_button, pane_copy_button, editor_clipboard, or
     * action_center_panel
     */
    copy_source?: null | string;
    /**
     * Which note-editor engine the copy happened in (markdown_native, block_markdown, block_v1,
     * json, rich_text, textarea). Note block_markdown is the vMarkdown editor, which the
     * section_created/section_deleted events instead label vmarkdown. Set only for selection
     * copies made inside an editor; null for whole-note copies from the Copy button.
     */
    editor_type?: null | string;
    /**
     * Copy format
     */
    format?: null | string;
    /**
     * Which formatting the copy removed, per the frontend's shared formatting-loss detector. A
     * key is true only when the copied content contained that formatting and the copy removed
     * it. Null or absent when not computed, e.g. verbatim clipboard copies (Cmd+C, cut, drag
     * handle) that skip copy settings. Sent on successful copies only; failed, empty and
     * superseded copies emit no event. The frontend always sends all four keys or null; extra
     * keys are tolerated so a future formatting category does not land in failed events.
     * Booleans only, never copied content.
     */
    formatting_removed?: null | FormattingRemoved;
    /**
     * Whether a text/html flavor accompanied text/plain on the clipboard. False for
     * plain-text-only copies, including a toggle copy where every formatting toggle was off.
     * Distinguishes a rich paste from a plain one, which the format property alone cannot.
     */
    html_flavor_written?: boolean | null;
    /**
     * Percentage of content copied
     */
    percentage_copied?: number | null;
    /**
     * Number of words in the copied selection
     */
    selected_word_count?: number | null;
    /**
     * Session identifier. Resolved client-side from the active /scribe/session route; null when
     * copied from non-session surfaces (e.g. shared/read-only views).
     */
    session_id?: null | string;
    /**
     * Total number of words in the text editor
     */
    total_word_count?: number | null;
    [property: string]: any;
}

export type SessionCopiedCopySettings = {
    /**
     * Whether bold and italic marks were kept in the copied output
     */
    bold_and_italics?: boolean | null;
    /**
     * Whether bullet points were kept in the copied output
     */
    bullet_points?: boolean | null;
    /**
     * Whether headings were kept in the copied output
     */
    headings?: boolean | null;
    /**
     * Whether tables were kept in the copied output
     */
    tables?: boolean | null;
    [property: string]: any;
}

export type FormattingRemoved = {
    /**
     * Whether the copied content contained bold or italic marks and the copy removed them
     */
    bold_and_italics: boolean;
    /**
     * Whether the copied content contained bullet points and the copy removed them
     */
    bullet_points: boolean;
    /**
     * Whether the copied content contained headings and the copy removed them
     */
    headings: boolean;
    /**
     * Whether the copied content contained tables and the copy removed them
     */
    tables: boolean;
    [property: string]: any;
}

/**
 * Tracks task created event
 */
export type TaskCreated = {
    /**
     * ID of the user the task is assigned to at time of event (null if unassigned)
     */
    assignee_id?: null | string;
    /**
     * Type of assignee (e.g. user, team). Null if unassigned.
     */
    assignee_type?: null | string;
    /**
     * Creation Action
     */
    creation_action?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Task category. Known values: document, action, communicate, order, coordinate
     */
    task_category?: null | string;
    /**
     * Task Id
     */
    task_id?: null | string;
    /**
     * Task Source
     */
    task_source?: null | string;
    /**
     * Status of the task at time of event. Known values: todo, done, archived
     */
    task_status?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the note editor fails to render (hydrate/serialize) content. PHI-free —
 * carries no note body and no raw error string.
 */
export type NoteEditorRenderFailed = {
    /**
     * Character length of the content that failed to render
     */
    content_length?: number | null;
    /**
     * Hydration format attempted (markdown for canonical notes, html for legacy)
     */
    content_type?: null | string;
    /**
     * Editor variant that failed to render (currently only markdown_native)
     */
    editor_type?: null | string;
    /**
     * Stable error code for dashboards/alerts. The raw error stays in the developer log line,
     * never here.
     */
    error_code?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a share notification is clicked
 */
export type ShareNotificationClicked = {
    /**
     * Type of share notification
     */
    notification_type?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a CPD source is updated
 */
export type CpdSourceUpdated = {
    /**
     * Source update action
     */
    action?: null | string;
    /**
     * Actual read time in seconds
     */
    actual_read_time_seconds?: number | null;
    /**
     * Number of appearances marked
     */
    appearances_marked?: number | null;
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Whether the source was created on demand
     */
    created_on_demand?: boolean | null;
    /**
     * Source identifier
     */
    source_id?: null | string;
    /**
     * Source URL
     */
    source_url?: null | string;
    [property: string]: any;
}

/**
 * Tracks EHR launch initiation
 */
export type EhrLaunchStarted = {
    /**
     * EHR provider
     */
    ehr_provider?: null | string;
    /**
     * Launch type
     */
    launch_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when patient profile is updated
 */
export type PatientUpdated = {
    /**
     * Fields reviewed during patient profile extraction
     */
    extraction_fields_reviewed?: ExtractionFieldsReviewed[] | null;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * Session identifier (null if updated outside session context)
     */
    session_id?: null | string;
    /**
     * UTC timestamp in ISO format
     */
    timestamp?: null | string;
    /**
     * Context where the update occurred
     */
    update_context?: null | string;
    /**
     * Source of the update
     */
    update_source?: null | string;
    [property: string]: any;
}

/**
 * Field review details
 */
export type ExtractionFieldsReviewed = {
    /**
     * Action taken on the field
     */
    action: ExtractionFieldsReviewedAction;
    /**
     * Character differences between suggested and final value (only when action is edited)
     */
    character_delta?: number | null;
    /**
     * Name of the reviewed field
     */
    field_name: string;
    /**
     * Whether the field already had data before extraction
     */
    had_existing_value: boolean;
}

/**
 * Action taken on the field
 */
export type ExtractionFieldsReviewedAction = "accepted" | "edited" | "rejected" | "added_manually";

/**
 * Tracks when document is updated/edited
 */
export type DocumentUpdated = {
    /**
     * AI command identifier
     */
    ai_command_id?: null | string;
    /**
     * Current word count
     */
    current_word_length?: number | null;
    /**
     * Document ID
     */
    document_id?: null | string;
    /**
     * Which kind of artifact tab the updated document backs (document | dictation | pdf |
     * prechart | patient_summary | care_path). Bounded non-PII set mirroring the
     * server-authoritative DocumentTabType; null when unset. Set on the patient-summary edit
     * path so M0 dashboards can isolate patient-summary content edits
     */
    document_tab_type?: null | string;
    /**
     * Edit rate metric
     */
    edit_rate?: number | null;
    /**
     * Original word count
     */
    original_word_length?: number | null;
    /**
     * Session ID
     */
    session_id?: null | string;
    /**
     * Template ID
     */
    template_id?: null | string;
    /**
     * Type of update action
     */
    update_action?: null | string;
    /**
     * Voice style
     */
    voice_style?: null | string;
    /**
     * Word count difference
     */
    word_diff?: number | null;
    [property: string]: any;
}

/**
 * Tracks team created event
 */
export type TeamCreated = {
    /**
     * Country
     */
    country?: null | string;
    /**
     * Team Id
     */
    team_id?: null | string;
    /**
     * Team Name
     */
    team_name?: null | string;
    /**
     * Type of team
     */
    team_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a message response is received
 */
export type MessageReceived = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Chip category when entry_method is a chip/suggestion
     */
    chip_category?: null | string;
    /**
     * Chip display label when entry_method is a chip/suggestion
     */
    chip_label?: null | string;
    /**
     * UTM source preserved across OAuth redirects (web)
     */
    custom_utm_source?: null | string;
    /**
     * Total time from submit to completion in milliseconds
     */
    duration_ms?: number | null;
    /**
     * How the original message was initiated (typed, follow_up_chip, suggestion_chip,
     * shortcut_chip, auto_query, document_suggestion_banner, document_suggestion_chip,
     * selection_action, check_other_sources)
     */
    entry_method?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Backend message ID (msg_*) of the response received. Joins to the backend
     * evidence.stream.outcome for the same request.
     */
    message_id?: null | string;
    /**
     * Pin origin for shortcut chips (default, library, custom)
     */
    origin?: null | string;
    /**
     * Query length in characters
     */
    query_length?: number | null;
    /**
     * Wait time in the queue before dispatch, in milliseconds
     */
    queue_duration_ms?: number | null;
    /**
     * True when the original message was dispatched from the message queue
     */
    queued?: boolean | null;
    /**
     * Number of references in the response
     */
    references_count?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Response length in characters
     */
    response_length?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Source of the message event (evidence, ask_heidi)
     */
    source?: null | string;
    /**
     * State of the message (completed, streaming)
     */
    state?: null | string;
    /**
     * UI surface that produced the chip/CTA click (slash_menu, suggestion_bar, etc.)
     */
    surface?: null | string;
    /**
     * Number of Tavily web searches performed
     */
    tavily_search_count?: number | null;
    /**
     * Time to first token in milliseconds
     */
    time_to_first_token_ms?: number | null;
    /**
     * Tools used in this response
     */
    tools_used?: string[] | null;
    /**
     * Whether user notes were generated from the message
     */
    user_notes_generated?: boolean | null;
    /**
     * Response length in words
     */
    word_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks context failed event
 */
export type ContextFailed = {
    /**
     * Context document identifier
     */
    context_document_id?: null | string;
    /**
     * Error details
     */
    error?: null | string;
    /**
     * Error Message
     */
    error_message?: null | string;
    /**
     * File Size
     */
    file_size?: number | null;
    /**
     * Context document name
     */
    name?: null | string;
    /**
     * Reason for context failure
     */
    reason?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Total token count
     */
    total_tokens?: number | null;
    [property: string]: any;
}

/**
 * Tracks account deleted event
 */
export type AccountDeleted = {
    /**
     * Details
     */
    details?: null | string;
    /**
     * Reason
     */
    reason?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a message attachment is uploaded
 */
export type MessageAttachmentUploaded = {
    /**
     * Unique conversation identifier
     */
    conversation_id: string;
    /**
     * MIME type of the uploaded file
     */
    mime_type: string;
    /**
     * File size in bytes
     */
    size_bytes: number;
    [property: string]: any;
}

/**
 * Tracks when a Chronicle device firmware upgrade completes successfully
 */
export type ChronicleFirmwareUpgradeCompleted = {
    /**
     * Chronicle device identifier
     */
    device_id?: null | string;
    /**
     * Firmware version upgraded to
     */
    firmware_version?: null | string;
    [property: string]: any;
}

/**
 * Tracks when patient data extraction fails or returns empty
 */
export type PatientFailed = {
    /**
     * Content length
     */
    content_length?: number | null;
    /**
     * Content sources
     */
    content_sources?: string[] | null;
    /**
     * Error message
     */
    error_message?: null | string;
    /**
     * Type of error
     */
    error_type?: null | string;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Failure status
     */
    status?: null | string;
    /**
     * Failure timestamp
     */
    timestamp?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the patient list page mounts
 */
export type PatientListViewed = {
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown.
     */
    feature_flag_variant?: null | string;
    /**
     * True when a search query was active at mount
     */
    has_search_query?: boolean | null;
    /**
     * Character length of the active search query. Never the raw query text — clinicians may
     * type patient names into the search box (HIPAA).
     */
    query_length?: number | null;
    /**
     * Number of rows visible in the list after any active search
     */
    result_count?: number | null;
    /**
     * UI surface that produced the event. Known values: list.
     */
    surface?: null | string;
}

/**
 * Tracks a patient opening a shared summary link (public download route serves the PDF)
 */
export type PatientSummaryLinkOpened = {
    /**
     * Delivery channel the opened link belongs to (sms | email | unknown). sms when an SMS
     * audit row matches the download token, email when identifiers are derived from the S3 file
     * key, unknown otherwise. Bounded non-PII set
     */
    channel?: null | string;
    /**
     * Document Id of the patient-summary document; join key to the send events. Parsed from the
     * S3 file key for email opens
     */
    document_id?: null | string;
    /**
     * True on the first recorded open of a delivery. For SMS this comes from the audit row
     * (first_downloaded_at was unset), for email from a Redis open counter keyed on the
     * download token. Null when neither source is available (e.g. channel unknown)
     */
    first_open?: boolean | null;
    /**
     * Session Id the summary was sent from; join key to the send events. For SMS it comes from
     * the audit row, for email from the download token payload. Null only for tokens minted
     * before the payload carried it, or when unknown
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user views an objective detail page
 */
export type ObjectiveViewed = {
    /**
     * Objective direction (inbound or outbound)
     */
    direction?: null | string;
    /**
     * Objective identifier
     */
    objective_id: string;
    /**
     * Display name of the objective
     */
    objective_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks when entity verification completes for a session
 */
export type VerificationCompleted = {
    /**
     * Additional information grouped by category
     */
    additional_information_by_category?: null | string;
    /**
     * Additional information grouped by category and severity
     */
    additional_information_by_category_and_severity?: null | string;
    /**
     * Count of additional information items
     */
    additional_information_count?: number | null;
    /**
     * Entity coverage percentage
     */
    coverage_pct?: number | null;
    /**
     * Entity counts grouped by category
     */
    entities_by_category?: { [key: string]: any } | null;
    /**
     * LLM model used for extraction
     */
    extraction_model?: null | string;
    /**
     * Time taken for entity extraction in milliseconds
     */
    extraction_ms?: number | null;
    /**
     * Number of hallucinated entities
     */
    hallucination_count?: number | null;
    /**
     * High severity hallucination count
     */
    hallucination_high_count?: number | null;
    /**
     * Low severity hallucination count
     */
    hallucination_low_count?: number | null;
    /**
     * Medium severity hallucination count
     */
    hallucination_medium_count?: number | null;
    /**
     * Number of inaccurate entities
     */
    inaccurate_count?: number | null;
    /**
     * Whether this is a re-analysis of a previously verified session
     */
    is_re_analysis?: boolean | null;
    /**
     * Missing items grouped by category
     */
    missing_by_category?: null | string;
    /**
     * Missing items grouped by category and relevance
     */
    missing_by_category_and_relevance?: null | string;
    /**
     * Number of missing entities
     */
    missing_count?: number | null;
    /**
     * High severity missing entity count
     */
    missing_high_count?: number | null;
    /**
     * Low severity missing entity count
     */
    missing_low_count?: number | null;
    /**
     * Medium severity missing entity count
     */
    missing_medium_count?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Number of supported inferences
     */
    supported_inferences?: number | null;
    /**
     * Total number of entities extracted
     */
    total_entities?: number | null;
    /**
     * Total processing time in milliseconds
     */
    total_ms?: number | null;
    /**
     * LLM model used for verification
     */
    verification_model?: null | string;
    /**
     * Time taken for verification in milliseconds
     */
    verification_ms?: number | null;
    /**
     * Number of entities verified as accurate
     */
    verified_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks authentication clicked event
 */
export type AuthenticationClicked = {
    /**
     * Click Action
     */
    click_action?: null | string;
    [property: string]: any;
}

/**
 * Tracks note started event
 */
export type NoteStarted = {
    /**
     * Document Id
     */
    document_id?: null | string;
    /**
     * Is Pinned Template
     */
    is_pinned_template?: boolean | null;
    /**
     * Is Recording V2 Enabled
     */
    is_recording_v2_enabled?: boolean | null;
    /**
     * Is Starter Pack Template
     */
    is_starter_pack_template?: boolean | null;
    /**
     * Is Tab Sharing
     */
    is_tab_sharing?: boolean | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Start Time Ms
     */
    start_time_ms?: number | null;
    /**
     * Template Id
     */
    template_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks referral viewed event
 */
export type ReferralViewed = {
    /**
     * Source
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when universal dictation post-processing completes successfully
 */
export type UniversalDictationPostProcessingCompleted = {
    /**
     * Actual LLM model used (may differ from requested)
     */
    actual_llm_model?: null | string;
    /**
     * Content type of the dictation output
     */
    content_type?: null | string;
    /**
     * Dictation session identifier
     */
    dictation_id?: null | string;
    /**
     * Duration of post-processing in milliseconds
     */
    duration_ms?: number | null;
    /**
     * Input languages detected in the dictation
     */
    input_languages?: string[] | null;
    /**
     * Whether a fallback model was used
     */
    is_fallback?: boolean | null;
    /**
     * LLM model used for post-processing
     */
    llm_model?: null | string;
    /**
     * Output language for the dictation
     */
    output_language?: null | string;
    /**
     * Duration of post-processing in milliseconds
     */
    processing_duration_ms?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Whether context-aware prompt was used
     */
    use_context_aware_prompt?: boolean | null;
    [property: string]: any;
}

/**
 * Tracks when a template is modified
 */
export type TemplateUpdated = {
    /**
     * Who changed the template
     */
    changed_by?: null | string;
    /**
     * Number of queued (pinned) templates after this change
     */
    current_queue_size?: number | null;
    /**
     * Whether template is pinned
     */
    is_pinned?: boolean | null;
    /**
     * Organisation identifier
     */
    organization_id?: null | string;
    /**
     * Previous template visibility setting
     */
    previous_template_visibility?: null | string;
    /**
     * Previous visibility setting
     */
    previous_visibility?: null | string;
    /**
     * Surface that initiated the pin/queue toggle (e.g. template_table, document_picker,
     * template_editor)
     */
    queue_source?: null | string;
    /**
     * Shared team identifiers
     */
    shared_team_ids?: string[] | null;
    /**
     * Teams the template is shared with
     */
    shared_teams?: string[] | null;
    /**
     * Source of template update (Amplitude format)
     */
    source?: null | string;
    /**
     * Team identifier
     */
    team_id?: null | string;
    /**
     * Template editor version
     */
    template_editor_version?: null | string;
    /**
     * Template identifier
     */
    template_id?: null | string;
    /**
     * Name of the template
     */
    template_name?: null | string;
    /**
     * Type of template
     */
    template_type?: null | string;
    /**
     * Template visibility setting
     */
    template_visibility?: null | string;
    /**
     * Type of update action
     */
    update_action?: null | string;
    /**
     * Update timestamp
     */
    updated_at?: null | string;
    /**
     * Fields that were updated
     */
    updated_fields?: string[] | null;
    /**
     * Whether the per-template Use sections setting is enabled (distinct from section_count)
     */
    use_sections?: boolean | null;
    /**
     * New visibility setting
     */
    visibility?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the in-session Evidence prompts bar is shown to the user
 */
export type EvidencePromptsBarShown = {
    /**
     * Scribe session identifier
     */
    session_id?: null | string;
    /**
     * Session stage when the bar was shown
     */
    session_stage?: SessionStage | null;
    /**
     * Number of source domains attached to the session context
     */
    source_count?: number | null;
    /**
     * Number of suggestion chips rendered in the bar
     */
    suggestions_count?: number | null;
    [property: string]: any;
}

export type SessionStage = "pre" | "during" | "post";

/**
 * Tracks when user copies a clinical code
 */
export type CodeCopied = {
    /**
     * The clinical code copied
     */
    code?: null | string;
    /**
     * Coding system (ICD-10, SNOMED, etc.)
     */
    code_system?: null | string;
    /**
     * Associated note ID
     */
    note_id?: null | string;
    /**
     * Associated session ID
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks button clicks throughout the application
 */
export type ButtonClicked = {
    /**
     * Dictate onboarding permissions step — accessibility grant state at click time
     */
    accessibility_granted?: boolean | null;
    /**
     * Button action identifier
     */
    action?: null | string;
    /**
     * Review confirmation popover (screen_name review_confirmation_popover) — the deferred note
     * action the review gate protects: copy | export | push.
     */
    action_type?: null | string;
    /**
     * Dictate onboarding sign-in step — auth provider chosen before redirect (e.g. email,
     * google)
     */
    auth_method?: null | string;
    /**
     * Non-PHI stable identifier for the selected clinical calculator
     */
    calculator_key?: null | string;
    /**
     * Category context
     */
    category?: null | string;
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Ask Heidi chip category
     */
    chip_category?: null | string;
    /**
     * Ask Heidi chip label
     */
    chip_label?: null | string;
    /**
     * Source of the click
     */
    click_source?: null | string;
    /**
     * User's current subscription tier at click time
     */
    current_plan?: null | string;
    /**
     * This user's billing seat type matching BillingSeatType (ASSISTANT, STANDARD, or MAX)
     */
    current_seat_type?: PreviousSeatTypeEnum | null;
    /**
     * Number of custom sources
     */
    custom_sources_count?: number | null;
    /**
     * Which note-editor engine the button was clicked in (markdown_native, block_markdown,
     * block_v1, json, rich_text, textarea). Set for shared bubble/text-menu buttons (bold,
     * italic, lists, code block, find & replace, create snippet); null for button_clicked
     * events fired outside an editor.
     */
    editor_type?: null | string;
    /**
     * Upstream surface that opened a downstream modal (e.g. side_nav_credit_usage_popover)
     */
    entry_point?: null | string;
    /**
     * Evidence location context
     */
    evidence_location?: null | string;
    /**
     * Feature context for object_action pattern
     */
    feature?: null | string;
    /**
     * Dictate onboarding key-confirm step — whether the confirmed hotkey is the platform default
     */
    hotkey_is_default?: boolean | null;
    /**
     * Whether feature is enabled
     */
    is_enabled?: boolean | null;
    /**
     * Whether user has Evidence Pro
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether item is pinned
     */
    is_pinned?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Button label
     */
    label?: null | string;
    /**
     * Location context
     */
    location?: null | string;
    /**
     * Dictate onboarding permissions step — microphone grant state at click time
     */
    mic_granted?: boolean | null;
    /**
     * Side-nav or header mount point for trial nudge or credit popover (sidebar or top_right)
     */
    nudge_placement?: NudgePlacement | null;
    /**
     * Payment period context
     */
    period?: null | string;
    /**
     * Dictate onboarding permissions step — which OS permission CTA was clicked (microphone or
     * accessibility)
     */
    permission_type?: null | string;
    /**
     * Number of pinned items
     */
    pinned_count?: number | null;
    /**
     * Preset type
     */
    preset_type?: null | string;
    /**
     * Question text
     */
    question?: null | string;
    /**
     * Region context
     */
    region?: null | string;
    /**
     * Screen where button was clicked
     */
    screen_name?: null | string;
    /**
     * Selected hours
     */
    selected_hours?: number | null;
    /**
     * Character count of the highlighted note selection at click time. Set for selection-menu
     * Ask Heidi actions; null outside that surface.
     */
    selection_length?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Session lifecycle stage at click time (pre, during, post). Set for selection-menu and
     * inline-edit Ask Heidi button_clicked events; null outside that surface.
     */
    session_stage?: SessionStage | null;
    /**
     * Session canvas tab the button interaction happened on (TabType value: note, document,
     * letter, research_note, transcript, context, session_merge). Used by Ask Heidi centred-bar
     * impression and other session-canvas button_clicked events; null outside that surface.
     */
    session_tab?: null | string;
    /**
     * Number of shortcuts
     */
    shortcut_count?: number | null;
    /**
     * Source domain
     */
    source_domain?: null | string;
    /**
     * Source title
     */
    source_title?: null | string;
    /**
     * Source URL
     */
    source_url?: null | string;
    /**
     * Wizard step slug when multiple steps share one screen_name (e.g. dictate onboarding
     * permissions vs sign-in under dictate_onboarding).
     */
    step_name?: null | string;
    /**
     * Number of suggestions
     */
    suggestion_count?: number | null;
    /**
     * Target plan for payment/trial modals
     */
    target_plan?: null | string;
    /**
     * Billing seat type the CTA is promoting matching BillingSeatType (ASSISTANT, STANDARD, or
     * MAX)
     */
    target_seat_type?: PreviousSeatTypeEnum | null;
    /**
     * Task identifier
     */
    task_id?: null | string;
    /**
     * Whole-number credit usage percentage (0–100) for credit usage popover CTAs
     */
    usage_percent?: number | null;
    [property: string]: any;
}

/**
 * Tracks a successfully persisted modification to a business entity
 */
export type EntityUpdated = {
    /**
     * Optional subordinate entity identifier; field reviews use a stable review-and-field
     * identifier
     */
    associated_entity_id?: null | string;
    /**
     * Number of entities included in the bulk operation when applicable
     */
    batch_size?: number | null;
    /**
     * Opaque root entity identifier; both patient extraction grains use the non-reversible
     * review_id
     */
    entity_id: string;
    /**
     * Controlled type of entity that was updated
     */
    entity_type: EntityType;
    /**
     * Controlled field identifier only; never the previous, extracted, or committed value
     */
    field_changed?: FieldChanged;
    /**
     * Whether the field held a value before the update
     */
    had_existing_value?: boolean;
    /**
     * Whether the update was produced by a bulk action such as accept-all
     */
    is_bulk?: boolean;
    /**
     * Whether the committed value replaced a different existing value
     */
    overwrote_existing_value?: boolean;
    /**
     * Terminal outcome on patient_extraction_review events
     */
    review_outcome?: ReviewOutcome | null;
    /**
     * Controlled product surface where the update was committed
     */
    surface?: SurfaceEnum;
    /**
     * Controlled description of the successfully persisted update
     */
    update_action: EntityUpdatedUpdateAction;
}

/**
 * Controlled type of entity that was updated
 */
export type EntityType = "session" | "document" | "note" | "chat" | "template" | "snippet" | "task" | "team" | "referral" | "cpd_draft" | "integration" | "patient" | "organisation" | "letter" | "settings" | "scribe_style" | "voice_style" | "feature_toggle" | "appearance" | "user_preferences" | "patient_extraction_review" | "patient_extraction_field_review";

export type FieldChanged = "name" | "dob" | "gender" | "pronouns" | "allergies" | "medications" | "medical_history" | "phone" | "email";

export type ReviewOutcome = "accept_all" | "manual_review" | "skip";

/**
 * Task key (e.g. detail, artefacts, evidence)
 *
 * A single evaluation error type belonging to the event's stage
 */
export type SurfaceEnum = "context_tab" | "during_session" | "patient_profile_page";

/**
 * Controlled description of the successfully persisted update
 */
export type EntityUpdatedUpdateAction = "edited" | "completed" | "accepted" | "rejected" | "added_manually";

/**
 * Tracks when a user views a tab on the Comms dashboard
 */
export type CommsDashboardTabViewed = {
    /**
     * How the user navigated to this tab
     */
    entry_point?: null | string;
    /**
     * Dashboard tab identifier
     */
    tab: string;
    [property: string]: any;
}

/**
 * Tracks when sharing access is revoked for a session
 */
export type ShareAccessRevoked = {
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Type of share
     */
    share_type?: null | string;
    [property: string]: any;
}

/**
 * HITL, RPA, or CUA approval prompt shown or resolved
 */
export type WorkApprovalActioned = {
    /**
     * Approval lifecycle action
     */
    action: WorkApprovalActionedAction;
    /**
     * Durable agent-harness run id
     */
    agent_run_id?: null | string;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, or research_terms
     */
    entry_point?: null | string;
    /**
     * Safe denial or gate code
     */
    error_code?: null | string;
    /**
     * cua, rpa, generic, or connector
     */
    object_type?: WorkApprovalActionedObjectType | null;
    /**
     * Approval outcome
     */
    outcome?: WorkApprovalActionedOutcome | null;
    /**
     * Project id when applicable
     */
    project_id?: null | string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
}

/**
 * Approval lifecycle action
 */
export type WorkApprovalActionedAction = "shown" | "requested" | "resolved" | "prompted";

export type WorkApprovalActionedObjectType = "cua" | "rpa" | "generic" | "connector";

export type WorkApprovalActionedOutcome = "approved" | "denied" | "timed_out" | "granted" | "cancelled";

/**
 * Tracks when sign up is initiated
 */
export type SignUpStarted = {
    /**
     * Feature where sign up started
     */
    feature?: null | string;
    /**
     * Source of sign up
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks snippet created event
 */
export type SnippetCreated = {
    /**
     * Content Length
     */
    content_length?: number | null;
    /**
     * Shorthand Length
     */
    shorthand_length?: number | null;
    /**
     * Number of snippets created
     */
    snippet_count?: number | null;
    /**
     * Snippet Id
     */
    snippet_id?: null | string;
    /**
     * Source
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a new template is created
 */
export type TemplateCreated = {
    /**
     * AI command text when source is ask_ai
     */
    ai_command_text?: null | string;
    /**
     * Creation timestamp
     */
    created_at?: null | string;
    /**
     * Template description
     */
    description?: null | string;
    /**
     * Whether this is a form-filling template
     */
    form_filling?: boolean | null;
    /**
     * Whether the template is private
     */
    is_private?: boolean | null;
    /**
     * Organisation identifier
     */
    organization_id?: null | string;
    /**
     * Number of sections in the template at save time (0 = not sectional)
     */
    section_count?: number | null;
    /**
     * Shared team identifiers
     */
    shared_team_ids?: string[] | null;
    /**
     * Source of template creation (e.g. default, document, pdf, ask_ai, shared, session)
     */
    source?: null | string;
    /**
     * Source template identifier if cloned from another template
     */
    source_template_id?: null | string;
    /**
     * Team identifier
     */
    team_id?: null | string;
    /**
     * Template author group (e.g. user, heidi, community)
     */
    template_author_group?: null | string;
    /**
     * Template category
     */
    template_category?: null | string;
    /**
     * Template editor version
     */
    template_editor_version?: null | string;
    /**
     * Template identifier
     */
    template_id?: null | string;
    /**
     * Name of the template
     */
    template_name?: null | string;
    /**
     * Template visibility setting
     */
    template_visibility?: null | string;
    /**
     * Tone template used
     */
    tone_template?: null | string;
    /**
     * Whether the per-template Use sections setting is enabled (distinct from section_count)
     */
    use_sections?: boolean | null;
    [property: string]: any;
}

/**
 * Tracks document viewed event
 */
export type DocumentViewed = {
    /**
     * Document Id
     */
    document_id?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * View Action
     */
    view_action?: null | string;
    [property: string]: any;
}

/**
 * Tracks library document scope changed event
 */
export type LibraryDocumentScopeChanged = {
    /**
     * Document Id
     */
    document_id?: null | string;
    /**
     * From Scope
     */
    from_scope?: null | string;
    /**
     * Source
     */
    source?: null | string;
    /**
     * To Scope
     */
    to_scope?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the evidence jurisdiction is changed
 */
export type EvidenceJurisdictionChanged = {
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * The new jurisdiction code selected
     */
    jurisdiction_code?: null | string;
    /**
     * User region
     */
    region?: null | string;
    [property: string]: any;
}

/**
 * Aggregate Work job finished for one agent run, including turns, tools, approvals,
 * references, duration, and credits. Schema 1-0-2 establishes one row per completed
 * agent_run_id; versions through 1-0-1 were emitted per billed tool call, so consumers
 * spanning the cutover must distinguish schema versions. Schema 2-0-0 widens
 * credits_charged from integer to number so fractional charges are preserved.
 */
export type WorkJobCompleted = {
    /**
     * completed or failed
     */
    action?: WorkJobCompletedAction | null;
    /**
     * Durable agent-harness run id
     */
    agent_run_id?: null | string;
    /**
     * Approval mode snapshot for the job: manual (approval required) or auto (approve for me)
     */
    approval_mode?: DownloadTrigger | null;
    /**
     * Average character length of user-authored queries during the completed job
     */
    average_turn_query_length?: number | null;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * Credits charged or reserved
     */
    credits_charged?: number | null;
    /**
     * Account SKU string (e.g. pro, pro_plus, clinician_pro). Not an enum; SKUs change.
     */
    current_plan?: null | string;
    /**
     * This user's seat type (assistant, standard, or max)
     */
    current_seat_type?: TSeatType | null;
    /**
     * Elapsed milliseconds from the job's first query or trigger to terminal completion
     */
    duration_ms?: number | null;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, or research_terms
     */
    entry_point?: null | string;
    /**
     * Safe error code
     */
    error_code?: null | string;
    /**
     * Estimated minutes saved
     */
    estimated_minutes_saved?: number | null;
    /**
     * Whether the completed job required at least one human approval
     */
    had_approval_request?: boolean | null;
    /**
     * Character length of the first user-authored query; raw query text is never included
     */
    initial_query_length?: number | null;
    /**
     * Whether the job executed a tool or delegated to a child agent; independent of credits
     * charged
     */
    is_agentic_use?: boolean | null;
    /**
     * Aggregate job id; for Work agent jobs this identifies the agent run
     */
    job_id?: null | string;
    /**
     * Bounded job classification such as prechart, note_push, form_fill, correspondence,
     * prior_auth, coding, document_generation, routine_execution, or other
     */
    job_type?: null | string;
    /**
     * job
     */
    object_type?: null | string;
    /**
     * Terminal job outcome
     */
    outcome: WorkToolCompletedOutcome;
    /**
     * Project id when applicable
     */
    project_id?: null | string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * Number of distinct object references touched by the completed job
     */
    referenced_object_count?: number | null;
    /**
     * Sorted distinct types of objects referenced by the job; excludes object identifiers,
     * names, and labels
     */
    referenced_object_types?: string[] | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * Number of tool calls made across the completed job
     */
    tool_call_count?: number | null;
    /**
     * Sorted distinct canonical tool names used across the completed job; excludes tool
     * arguments and results
     */
    tools_used?: string[] | null;
    /**
     * Number of user-authored queries sent during the completed job
     */
    turn_count?: number | null;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
}

export type WorkJobCompletedAction = "completed" | "failed";

/**
 * Tracks when a call recording fails to process
 */
export type CallRecordingFailed = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Human-readable error description
     */
    error_description?: null | string;
    /**
     * Category of the error
     */
    error_type: string;
    /**
     * Processing stage where the failure occurred
     */
    stage: string;
    [property: string]: any;
}

/**
 * Tracks when patient profile is viewed
 */
export type PatientViewed = {
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * Percentage of key fields populated (0-100)
     */
    profile_completeness_score?: number | null;
    /**
     * Session identifier (null if viewed outside session context)
     */
    session_id?: null | string;
    /**
     * UTC timestamp in ISO format
     */
    timestamp?: null | string;
    /**
     * Context where profile is viewed
     */
    view_context?: null | string;
    [property: string]: any;
}

/**
 * Tracks when cursor dictation post-processing completes successfully
 */
export type CursorDictationPostProcessingCompleted = {
    /**
     * Actual LLM model used (may differ from requested)
     */
    actual_llm_model?: null | string;
    /**
     * Content type of the dictation output
     */
    content_type?: null | string;
    /**
     * Dictation session identifier
     */
    dictation_id?: null | string;
    /**
     * Duration of post-processing in milliseconds
     */
    duration_ms?: number | null;
    /**
     * Input languages detected in the dictation
     */
    input_languages?: string[] | null;
    /**
     * Whether a fallback model was used
     */
    is_fallback?: boolean | null;
    /**
     * LLM model used for post-processing
     */
    llm_model?: null | string;
    /**
     * Output language for the dictation
     */
    output_language?: null | string;
    /**
     * Duration of post-processing in milliseconds
     */
    processing_duration_ms?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when clinical codes are processed
 */
export type CodeProcessed = {
    /**
     * Count of billable codes
     */
    billable_codes_count?: number | null;
    /**
     * Codes grouped by code set
     */
    codes_by_codeset?: { [key: string]: any } | null;
    /**
     * Codes grouped by relevance
     */
    codes_by_relevance?: { [key: string]: any } | null;
    /**
     * Enabled code sets
     */
    codesets_enabled?: string[] | null;
    /**
     * Whether regeneration was forced
     */
    force_regenerate?: boolean | null;
    /**
     * Note identifier
     */
    note_id?: null | string;
    /**
     * Number of codes generated
     */
    num_codes_generated?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Completion times for each processing step
     */
    step_completion_times?: { [key: string]: any } | null;
    /**
     * Processing completion time
     */
    time_processing_completed?: null | string;
    /**
     * Processing start time
     */
    time_processing_started?: null | string;
    [property: string]: any;
}

/**
 * Tracks export setting toggled event
 */
export type ExportSettingToggled = {
    /**
     * Enabled
     */
    enabled?: boolean | null;
    /**
     * Scope
     */
    scope?: null | string;
    /**
     * Team Id
     */
    team_id?: null | string;
    /**
     * Type
     */
    type?: null | string;
    [property: string]: any;
}

/**
 * Research Preview terms viewed, accepted, or declined
 */
export type WorkTermsActioned = {
    /**
     * Terms interaction
     */
    action: WorkTermsActionedAction;
    /**
     * Durable agent-harness run id
     */
    agent_run_id?: null | string;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * close, outside_click, escape, or decline_button
     */
    dismiss_method?: null | string;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, or research_terms
     */
    entry_point?: null | string;
    /**
     * Project id when applicable
     */
    project_id?: null | string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * Milliseconds from modal open to accept
     */
    time_to_accept_ms?: number | null;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
}

/**
 * Terms interaction
 */
export type WorkTermsActionedAction = "viewed" | "accepted" | "declined";

/**
 * Tracks when a user successfully signs up
 */
export type SignUpSuccessful = {
    /**
     * User's display language preference
     */
    display_language?: null | string;
    /**
     * Referrer URL or identifier
     */
    referrer?: null | string;
    /**
     * Source of the signup
     */
    signup_source?: null | string;
    /**
     * SSO connection ID from the access token (user_properties.sso_connection_id). Present for
     * SSO sign-ups, null for non-SSO (email/social) sign-ups.
     */
    sso_connection_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks template editor updated event
 */
export type TemplateEditorUpdated = {
    /**
     * Tab Type
     */
    tab_type?: null | string;
    /**
     * Template Editor Version
     */
    template_editor_version?: null | string;
    /**
     * Template Id
     */
    template_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a team admin updates Evidence access controls. Emitted on master toggle,
 * access-mode switch, role selection persist, and per-member enable/disable. Count fields
 * are snapshots at persist time for the active access mode only (role_count when by_role,
 * member_enabled_count when by_member).
 */
export type TeamEvidenceAccessUpdated = {
    /**
     * Active access mode after the change. by_role grants via role selection; by_member grants
     * via per-member overrides.
     */
    access_mode: AccessMode;
    /**
     * Which control changed. master_toggle (Evidence on/off), mode_changed (by_role <->
     * by_member), roles_updated (role selection), members_updated (per-member overrides).
     */
    change_type: TeamEvidenceAccessUpdatedChangeType;
    /**
     * Whether Evidence access is enabled for the team after the change
     */
    is_enabled: boolean;
    /**
     * Number of individual members with Evidence access when access_mode is by_member. Null
     * when access_mode is by_role or the count was not applicable to this change_type.
     */
    member_enabled_count?: number | null;
    /**
     * Number of roles with Evidence access when access_mode is by_role. Null when access_mode
     * is by_member or the count was not applicable to this change_type.
     */
    role_count?: number | null;
    /**
     * Team identifier
     */
    team_id: string;
}

/**
 * Active access mode after the change. by_role grants via role selection; by_member grants
 * via per-member overrides.
 */
export type AccessMode = "by_role" | "by_member";

/**
 * Which control changed. master_toggle (Evidence on/off), mode_changed (by_role <->
 * by_member), roles_updated (role selection), members_updated (per-member overrides).
 */
export type TeamEvidenceAccessUpdatedChangeType = "master_toggle" | "mode_changed" | "roles_updated" | "members_updated";

/**
 * Tracks when roadmap is viewed
 */
export type RoadmapViewed = {
    /**
     * Source that triggered the event (e.g. frontend, backend)
     */
    event_source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when panel is viewed or opened
 */
export type PanelViewed = {
    /**
     * Type of panel
     */
    panel_type?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Source of view action
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when find/replace is executed
 */
export type FindReplaceSubmitted = {
    /**
     * Source that triggered the event (e.g. frontend, backend)
     */
    event_source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a CPD activity finishes processing
 */
export type CpdActivityCompleted = {
    /**
     * Access timestamp
     */
    accessed_at?: null | string;
    /**
     * Number of appearances created
     */
    appearances_created?: number | null;
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Number of citations
     */
    citation_count?: number | null;
    /**
     * Number of citations processed
     */
    citations_processed?: number | null;
    /**
     * Number of errors
     */
    errors_count?: number | null;
    /**
     * Message identifier
     */
    message_id?: null | string;
    /**
     * Number of sources created
     */
    sources_created?: number | null;
    /**
     * Number of sources updated
     */
    sources_updated?: number | null;
    /**
     * List of specialties
     */
    specialties?: string[] | null;
    /**
     * Total time in minutes added
     */
    total_time_minutes_added?: number | null;
    /**
     * Count of unique domains
     */
    unique_domain_count?: number | null;
    /**
     * List of unique domains
     */
    unique_domains?: string[] | null;
    /**
     * List of website types
     */
    website_types?: string[] | null;
    [property: string]: any;
}

/**
 * Tracks the authoritative outcome of an auto-link sessions preference update
 */
export type AutoLinkSessionsPreferenceUpdateCompleted = {
    /**
     * Preference value requested by the user
     */
    enabled: boolean;
    /**
     * Definitive persistence outcome
     */
    outcome: AutoLinkSessionsPreferenceUpdateCompletedOutcome;
    /**
     * Bounded persistence or failure classification
     */
    reason: AutoLinkSessionsPreferenceUpdateCompletedReason;
    /**
     * User interface surface that initiated the update
     */
    surface: AutoLinkSessionsPreferenceUpdateCompletedSurface;
}

/**
 * Definitive persistence outcome
 *
 * Definitive mutation outcome
 */
export type AutoLinkSessionsPreferenceUpdateCompletedOutcome = "success" | "failure";

/**
 * Bounded persistence or failure classification
 */
export type AutoLinkSessionsPreferenceUpdateCompletedReason = "persisted" | "flag_off" | "validation_rejected" | "authorization_rejected" | "network_error" | "server_error" | "unknown";

/**
 * User interface surface that initiated the update
 */
export type AutoLinkSessionsPreferenceUpdateCompletedSurface = "settings" | "spotlight_opt_in" | "spotlight_reconfirm" | "unspecified_legacy";

/**
 * Tracks when user clicks to downgrade from trial
 */
export type TrialDowngradeClicked = {
    /**
     * Modal context
     */
    modal_type?: null | string;
    /**
     * Type of trial
     */
    trial_type?: null | string;
    [property: string]: any;
}

/**
 * One in-session nudge detection run was evaluated: every candidate the code-mediated
 * engine considered plus the gate decision for each, so surfaced-vs-suppressed rates are
 * computable. PHI-free: ids, scores, decisions, and counts only; never transcript or quote
 * text.
 */
export type NudgeDetectionEvaluated = {
    /**
     * Number of protocol candidates the detection run evaluated
     */
    candidate_count: number;
    /**
     * Per-candidate PHI-free decision records for every protocol candidate considered
     */
    candidates?: Candidate[] | null;
    /**
     * Stable id for this detection pass; joins to surfaced nudge events from the same run
     */
    detection_id: string;
    /**
     * Detection engine that produced the evaluation. Known value: code_mediated
     */
    engine?: null | string;
    /**
     * Identifier of the model used by the detection run
     */
    model_id?: null | string;
    /**
     * Detection-run outcome
     */
    outcome: NudgeDetectionEvaluatedOutcome;
    /**
     * Version of the classifier prompt used by the detection run
     */
    prompt_version?: null | string;
    /**
     * Heidi session the detection run belongs to
     */
    session_id: string;
    /**
     * Number of candidates that surfaced as nudge cards after gating
     */
    surfaced_count: number;
    /**
     * PHI-free tenant or rollout scope used by the detection engine, when applicable
     */
    tenant_scope?: null | string;
    /**
     * Hash of the transcript chunk evaluated by the detector; a join key only, never raw text
     */
    transcript_chunk_hash?: null | string;
}

/**
 * PHI-free evaluation result for one protocol candidate
 */
export type Candidate = {
    /**
     * Classifier confidence for the candidate (0-1)
     */
    confidence?: number | null;
    /**
     * Gate decision for this candidate. Known values include surfaced, safety_exempt_surfaced,
     * rerank_unsupported, rail_hold_fire_unknown, rerank_support_unknown,
     * weak_evidence_low_confidence, below_sensitivity_cutoff, and confusable_not_independent.
     */
    decision?: null | string;
    /**
     * Whether the transcript chunk matched an active-exclusion avoid term
     */
    has_avoid_hit?: boolean | null;
    /**
     * Avoid-blind code relevance match strength for the candidate (0-1)
     */
    match_strength?: number | null;
    /**
     * Number of PHI-free matched signal labels for the candidate
     */
    matched_signals_count?: number | null;
    /**
     * Identifier of the protocol candidate evaluated
     */
    protocol_id?: null | string;
    /**
     * Number of source quotes considered for the candidate; count only, never quote text
     */
    quotes_count?: number | null;
    /**
     * Whether the candidate was a safety-rail candidate eligible to fail open
     */
    rail?: boolean | null;
    /**
     * Rerank support verdict for this candidate
     */
    supported?: Supported | null;
}

export type Supported = "supported" | "unsupported" | "unknown";

/**
 * Detection-run outcome
 */
export type NudgeDetectionEvaluatedOutcome = "surfaced" | "no_surface" | "no_candidates";

/**
 * Fired when a consult note or document is emailed to one or more recipients.
 */
export type DocumentEmailed = {
    /**
     * The emailed document. Absent when the artifact is the consult note.
     */
    document_id?: null | string;
    /**
     * Recipients the backend reported as failed. 0 when none failed.
     */
    failed_count?: number | null;
    /**
     * How many recipients the clinician submitted.
     */
    recipient_count: number;
    /**
     * Recipients the backend confirmed delivery to. Falls back to recipient_count when the
     * response omits the breakdown.
     */
    sent_count?: number | null;
    /**
     * The session the emailed artifact belongs to.
     */
    session_id: string;
}

/**
 * Tracks when the experience feedback nudge is shown
 */
export type EvidenceExperienceFeedbackViewed = {
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Number of completed queries when nudge was shown
     */
    query_count?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Number of times the user has previously snoozed
     */
    snooze_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks when an evidence chat query is submitted
 */
export type EvidenceChatSubmitted = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Chip category (shortcut/suggestion only)
     */
    chip_category?: null | string;
    /**
     * Chip display label (shortcut/suggestion only)
     */
    chip_label?: null | string;
    /**
     * How the message was initiated (typed, shortcut_chip, suggestion_chip, etc.)
     */
    entry_method?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Backend message ID (msg_*) assigned at submit time
     */
    message_id?: null | string;
    /**
     * Pin origin for shortcut chips
     */
    origin?: Origin | null;
    /**
     * Preset local guidelines enabled
     */
    preset_local_guidelines?: boolean | null;
    /**
     * Preset primary evidence enabled
     */
    preset_primary_evidence?: boolean | null;
    /**
     * Preset web search enabled
     */
    preset_web_search?: boolean | null;
    /**
     * The query text
     */
    query?: null | string;
    /**
     * Length of the query in characters
     */
    query_length?: number | null;
    /**
     * Wait time in the queue before dispatch, in milliseconds
     */
    queue_duration_ms?: number | null;
    /**
     * True when dispatched from the message queue
     */
    queued?: boolean | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Selection mode (default, preset, custom)
     */
    selection_mode?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Session stage for in-session submissions
     */
    session_stage?: SessionStage | null;
    /**
     * List of source domains selected
     */
    sources?: string[] | null;
    /**
     * Number of sources selected
     */
    sources_count?: number | null;
    /**
     * UI surface that produced the click (chip / banner clicks)
     */
    surface?: null | string;
    /**
     * Length of the query in words
     */
    word_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks when a user submits an experience feedback star rating
 */
export type EvidenceExperienceFeedbackRatingSubmitted = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Number of completed queries when rating was submitted
     */
    query_count?: number | null;
    /**
     * Rating from 1 to 5
     */
    rating?: number | null;
    /**
     * Which scale was rated (ease, usefulness)
     */
    rating_type?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Number of times the user snoozed before rating
     */
    snooze_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks when patient documents are linked to a session as context
 */
export type PatientDocumentLinked = {
    /**
     * Identifiers of the documents linked in this save action
     */
    document_ids?: string[];
    /**
     * Patient's count of ready (extractable) documents at link time. This is the denominator
     * for the attach-rate metric — "of sessions with eligible documents, what fraction linked
     * one or more".
     */
    eligible_doc_count?: number | null;
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown.
     */
    feature_flag_variant?: null | string;
    /**
     * Number of documents linked in this save action. Equals len(document_ids); duplicated for
     * cheap aggregation in dashboards. Capped at 3 per BE-1f.
     */
    linked_count?: number | null;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * Identifier of the session the documents are linked to
     */
    session_id?: null | string;
    /**
     * UI surface that produced the event. Known values: in_session.
     */
    surface?: null | string;
}

/**
 * Tracks product tour viewed event
 */
export type ProductTourViewed = {
    /**
     * Tour Action
     */
    tour_action?: null | string;
    [property: string]: any;
}

/**
 * Tracks each successful infinite-scroll fetch of more sessions in the list
 */
export type SessionsListPaginated = {
    /**
     * Total rows loaded in the list after this fetch
     */
    total_sessions_loaded?: number | null;
}

/**
 * Tracks audio uploaded event
 */
export type AudioUploaded = {
    /**
     * File Size
     */
    file_size?: number | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Upload Action
     */
    upload_action?: null | string;
    [property: string]: any;
}

/**
 * Tracks when team settings or information is updated
 */
export type TeamUpdated = {
    /**
     * Team update action
     */
    action?: null | string;
    /**
     * Admin identifier
     */
    admin_id?: null | string;
    /**
     * Whether the team is enabled
     */
    enabled?: boolean | null;
    /**
     * External source configurations
     */
    external_sources?: { [key: string]: any }[] | null;
    /**
     * Whether custom consent text is set
     */
    has_custom_text?: boolean | null;
    /**
     * Whether the toggled setting is enabled
     */
    is_enabled?: boolean | null;
    /**
     * Whether the team is managed
     */
    is_managed?: boolean | null;
    /**
     * Whether this is a pilot team
     */
    is_pilot?: boolean | null;
    /**
     * User who made the modification
     */
    modified_by_user_id?: null | string;
    /**
     * New owner identifier
     */
    new_owner_id?: null | string;
    /**
     * Previous pilot status
     */
    previous_is_pilot?: boolean | null;
    /**
     * Previous owner identifier
     */
    previous_owner_id?: null | string;
    /**
     * Team identifier
     */
    team_id?: null | string;
    /**
     * Team name
     */
    team_name?: null | string;
    /**
     * Team type
     */
    type?: null | string;
    /**
     * Type of team update (deprecated, use action instead)
     */
    update_action?: null | string;
    /**
     * Update timestamp
     */
    updated_at?: null | string;
    /**
     * Date user joined team
     */
    user_joined_team_date?: null | string;
    /**
     * User team name
     */
    user_team_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a hardware device is linked
 */
export type HardwareDeviceLinked = {
    /**
     * Source of the binding code (e.g. qr_code, manual)
     */
    binding_code_source?: null | string;
    /**
     * Device identifier
     */
    device_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks template completed event
 */
export type TemplateCompleted = {
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Template Id
     */
    template_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a voicemail message is left on an outbound call
 */
export type CallVoicemailLeft = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Voicemail message duration in seconds
     */
    duration_seconds: number;
    [property: string]: any;
}

/**
 * Tracks personalization updated event
 */
export type PersonalizationUpdated = {
    /**
     * Custom Style
     */
    custom_style?: null | string;
    /**
     * Whether personalization is enabled
     */
    enabled?: boolean | null;
    /**
     * Old Custom Style
     */
    old_custom_style?: null | string;
    /**
     * Old Voice Style
     */
    old_voice_style?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Source
     */
    source?: null | string;
    /**
     * Transition Type
     */
    transition_type?: null | string;
    /**
     * Update Action
     */
    update_action?: null | string;
    /**
     * Voice Style
     */
    voice_style?: null | string;
    [property: string]: any;
}

/**
 * Tracks context updated event
 */
export type ContextUpdated = {
    /**
     * Context document identifier
     */
    context_document_id?: null | string;
    /**
     * Type of document
     */
    document_type?: null | string;
    /**
     * Context document name
     */
    name?: null | string;
    /**
     * Number of tokens in the document
     */
    number_of_tokens?: number | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks audio stopped event
 */
export type AudioStopped = {
    /**
     * File Size
     */
    file_size?: number | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user completes their first Evidence query within their first week of
 * account creation
 */
export type FirstEvidenceCompleted = {
    /**
     * Kinde user identifier
     */
    user_id: string;
}

/**
 * Tracks creation of a local desktop session shell for offline recording
 */
export type OfflineRecordingCreated = {
    /**
     * Residency region code used for the capture
     */
    capture_region: string;
    /**
     * Heidi client platform identifier attached by the analytics pipeline
     */
    client_platform: string;
    /**
     * Desktop application version
     */
    desktop_version: null | string;
    /**
     * Offline recording feature-flag state
     */
    feature_flag_state: FeatureFlagState;
    /**
     * Whether the local session was linked to a patient; no patient data is captured
     */
    has_patient_link: boolean;
    /**
     * Desktop-generated local session identifier
     */
    local_session_id: string;
    /**
     * Product platform; desktop for this event
     */
    platform: OfflineRecordingCreatedPlatform;
    /**
     * Recording source
     */
    source: OfflineSyncDeferredSource;
    /**
     * Whether a note template identifier was configured; the identifier itself is not captured
     */
    template_id_present: boolean;
}

/**
 * Product platform; desktop for this event
 */
export type OfflineRecordingCreatedPlatform = "desktop";

/**
 * Tracks when Evidence is activated for a user
 */
export type EvidenceActivated = {
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * User region
     */
    region?: null | string;
    [property: string]: any;
}

/**
 * Tracks coach mark action taken event
 */
export type CoachMarkActionTaken = {
    /**
     * Coach Mark Id
     */
    coach_mark_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user is created or added to a team via internal admin tools
 */
export type AdminUserCreated = {
    /**
     * How the user was created or modified (discriminator for originalEvent)
     */
    action?: null | string;
    /**
     * Country assigned to the user (csv_import)
     */
    country?: null | string;
    /**
     * Specialty ID assigned to the user (csv_import)
     */
    specialty_id?: null | string;
    /**
     * Specialty name assigned to the user (csv_import)
     */
    specialty_name?: null | string;
    /**
     * SSO identifier of the created user (csv_import)
     */
    sso?: null | string;
    /**
     * ID of the team the user was added to
     */
    team_id?: null | string;
    /**
     * Name of the team the user was added to
     */
    team_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks note viewed event
 */
export type NoteViewed = {
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Template Id
     */
    template_id?: null | string;
    /**
     * View Action
     */
    view_action?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the user dismisses the in-session Evidence prompts bar
 */
export type EvidencePromptsBarDismissed = {
    /**
     * Scribe session identifier
     */
    session_id?: null | string;
    /**
     * Session stage when the bar was dismissed
     */
    session_stage?: SessionStage | null;
    [property: string]: any;
}

/**
 * Tracks when a call summary fails to generate
 */
export type CallSummaryFailed = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Human-readable error description
     */
    error_description?: null | string;
    /**
     * Category of the error
     */
    error_type: string;
    [property: string]: any;
}

/**
 * Tracks when an outbound message is queued for delivery
 */
export type OutboundMessageQueued = {
    /**
     * Unique conversation identifier
     */
    conversation_id: string;
    /**
     * Whether the message includes an attachment
     */
    has_attachment: boolean;
    /**
     * Length of the message body in characters
     */
    message_length_chars: number;
    /**
     * User who sent the message
     */
    sent_by_user_id: string;
    [property: string]: any;
}

/**
 * Tracks when an AI objective execution fails
 */
export type ObjectiveExecutionFailed = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Human-readable error description
     */
    error_description?: null | string;
    /**
     * Category of the error
     */
    error_type: string;
    /**
     * Stage of execution where the failure occurred
     */
    failure_stage: string;
    /**
     * Unique objective identifier
     */
    objective_id: string;
    [property: string]: any;
}

/**
 * Tracks when a user signs in
 */
export type UserSignedIn = {
    /**
     * Authentication provider
     */
    auth_provider?: null | string;
    /**
     * Integration type if signing in via integration
     */
    integration_type?: null | string;
    /**
     * Sign in method used
     */
    sign_in_method?: null | string;
    [property: string]: any;
}

/**
 * Tracks patient extraction reviewed event
 */
export type PatientExtractionReviewed = {
    /**
     * Action
     */
    action?: null | string;
    /**
     * Patient Id
     */
    patient_id?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Time On Modal
     */
    time_on_modal?: number | null;
    /**
     * Timestamp
     */
    timestamp?: null | string;
    [property: string]: any;
}

/**
 * Tracks when paywall is displayed
 */
export type PaywallViewed = {
    /**
     * User's current subscription tier at paywall impression
     */
    current_plan?: null | string;
    /**
     * Days remaining on current trial
     */
    days_left_on_trial?: number | null;
    /**
     * Upstream surface that opened this paywall (e.g. side_nav_credit_usage_popover)
     */
    entry_point?: null | string;
    /**
     * User's 90-day Clinician reverse trial has ended. Mutually exclusive with
     * is_90_day_trial_user; both null when never on this trial.
     */
    is_90_day_trial_ended?: boolean | null;
    /**
     * User is currently on the 90-day Clinician reverse trial. Mutually exclusive with
     * is_90_day_trial_ended; both null when never on this trial.
     */
    is_90_day_trial_user?: boolean | null;
    /**
     * Present when viewer belongs to a team — whether they are team admin
     */
    is_admin?: boolean | null;
    /**
     * Source that triggered paywall
     */
    source?: null | string;
    /**
     * Target subscription plan
     */
    target_plan?: null | string;
    /**
     * Present when viewer belongs to a team — whether team billing is Stripe-managed
     */
    team_is_stripe_managed?: boolean | null;
    [property: string]: any;
}

/**
 * A nudge checklist item was unchecked
 */
export type NudgeItemUnchecked = {
    /**
     * Version of the protocol catalogue that produced this card
     */
    catalogue_version: string;
    /**
     * Classifier confidence for the match (0-1)
     */
    confidence: number;
    /**
     * Join key back to the PHI-safe detection context persisted on NudgeCardState. Null when no
     * detection pass produced the card
     */
    detection_id?: null | string;
    /**
     * Identifier of the checklist item that was unchecked
     */
    item_id: string;
    /**
     * Number of matched signals (length of matched_signals)
     */
    matched_signal_count?: number | null;
    /**
     * Named signals (chips) the classifier matched. PHI-free labels only
     */
    matched_signals?: string[];
    /**
     * Identifier of the model used by the classifier
     */
    model_id?: null | string;
    /**
     * Version of the classifier prompt, for slicing false-positive rates by classifier change
     */
    prompt_version?: null | string;
    /**
     * Identifier of the protocol the item belongs to
     */
    protocol_id: string;
    /**
     * Heidi session the nudge card belongs to
     */
    session_id: string;
    /**
     * How the card came to exist. Known values: auto (classifier-surfaced), manual (clinician
     * added from the protocol library)
     */
    source?: DownloadTrigger;
    /**
     * Hash of the transcript chunk that drove the detection; a join key only, never the raw text
     */
    transcript_chunk_hash?: null | string;
}

/**
 * Tracks evidence source clicked event
 */
export type EvidenceSourceClicked = {
    /**
     * Backend chat ID, when available
     */
    chat_id?: null | string;
    /**
     * 1-based ordinal of the citation within its surface_block
     */
    citation_number?: number | null;
    /**
     * Library collection(s) the document belongs to (only set when source_type is user_document)
     */
    collections?: string[] | null;
    /**
     * Evidence Location
     */
    evidence_location?: null | string;
    /**
     * Sub-classification of the interaction within the surface (e.g. open_in_viewer,
     * open_in_new_tab, open_library_document)
     */
    interaction?: null | string;
    /**
     * Is Evidence Pro
     */
    is_evidence_pro?: boolean | null;
    /**
     * Is Managed
     */
    is_managed?: boolean | null;
    /**
     * Is Team Member
     */
    is_team_member?: boolean | null;
    /**
     * Backend message ID (msg_*), when available
     */
    message_id?: null | string;
    /**
     * Zero-based position of the reference in the references list
     */
    reference_index?: number | null;
    /**
     * Region
     */
    region?: null | string;
    /**
     * Per-sub-agent query string for clicks emitted from inline_search_block /
     * collapsed_sources_summary
     */
    search_block_query?: null | string;
    /**
     * Scribe session ID if the click happened inside a session
     */
    session_id?: null | string;
    /**
     * Source Domain
     */
    source_domain?: null | string;
    /**
     * Source Title
     */
    source_title?: null | string;
    /**
     * Citation source classification (e.g. web, user_document, integration_partner)
     */
    source_type?: null | string;
    /**
     * Source Url
     */
    source_url?: null | string;
    /**
     * Visual surface that fired the event (e.g. inline_citation_badge, references_section,
     * search_block, source_viewer_sheet)
     */
    surface?: null | string;
    /**
     * Containing block within the assistant message (e.g. prose, key_points,
     * references_section, inline_search_block, collapsed_sources_summary, viewer_sheet)
     */
    surface_block?: null | string;
    [property: string]: any;
}

/**
 * Tracks when share settings are saved
 */
export type ShareSaved = {
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks deeplink triggered event
 */
export type DeeplinkTriggered = {
    /**
     * Deeplink Action
     */
    deeplink_action?: null | string;
    /**
     * Deeplink Utm Campaign
     */
    deeplink_utm_campaign?: null | string;
    /**
     * Deeplink Utm Medium
     */
    deeplink_utm_medium?: null | string;
    /**
     * Deeplink Utm Source
     */
    deeplink_utm_source?: null | string;
    /**
     * Is Requirements Met
     */
    is_requirements_met?: boolean | null;
    /**
     * Kinde User Id
     */
    kinde_user_id?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks demo completed event
 */
export type DemoCompleted = {
    /**
     * Skipped To Results
     */
    skipped_to_results?: boolean | null;
    /**
     * Used Voice
     */
    used_voice?: boolean | null;
    [property: string]: any;
}

/**
 * Tracks when sessions are linked to patient
 */
export type PatientLinked = {
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown. Belt-and-braces with the flagsmith_flag_evaluated webhook log; lets dashboards
     * slice without a join. Nullable for legacy call sites.
     */
    feature_flag_variant?: null | string;
    /**
     * How the patient was matched at link time. Known values: highlighted_default (Enter
     * pressed on the pre-highlighted match), explicit_click (user clicked a specific result),
     * typed_then_picked (user kept typing, then picked a result). Only meaningful when surface
     * = combobox_session_header; nullable elsewhere.
     */
    match_method?: null | string;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * Number of results visible in the dropdown at commit time. Null when no dropdown was shown.
     */
    result_count_seen?: number | null;
    /**
     * Zero-based rank of the chosen match in the dropdown at commit time. 0 = top match. Null
     * when not applicable (e.g. surface other than combobox_session_header, or no dropdown was
     * shown).
     */
    result_position?: number | null;
    /**
     * UUID identifying a single field interaction. Generated FE-side on field focus and
     * propagated through field_selected, patient_searched, and the outcome event so the funnel
     * can stitch a single interaction across events. Nullable for legacy call sites.
     */
    search_id?: null | string;
    /**
     * Session identifiers being linked
     */
    session_ids?: string[];
    /**
     * UI surface that produced the link. Known values: combobox_session_header (new patient
     * find-or-link combobox), legacy_session_header (legacy free-text input on the session
     * header), chat_context (attach-patient action from a chat), integration_select (selected
     * via integration / EHR picker). Nullable for legacy call sites that have not yet been
     * re-instrumented.
     */
    surface?: null | string;
    /**
     * Milliseconds from field_selected (focus) to this outcome event. FE-computed; treat as
     * best-effort and apply outlier filtering at query time (e.g. drop > 300_000ms) — values
     * can be inflated by tab switches, device sleep, or the user walking away.
     */
    time_to_outcome_ms?: number | null;
    [property: string]: any;
}

/**
 * Tracks when an authentication token is set or updated
 */
export type AuthTokenUpdated = {
    /**
     * Platform where token was updated
     */
    client_platform?: null | string;
    /**
     * Type of token (access, refresh, etc.)
     */
    token_type?: null | string;
    [property: string]: any;
}

/**
 * A nudge card was auto-surfaced by the classifier
 */
export type NudgeSurfaced = {
    /**
     * Version of the protocol catalogue that produced this card
     */
    catalogue_version: string;
    /**
     * Classifier confidence for the match (0-1)
     */
    confidence: number;
    /**
     * Stable id for the detection pass that surfaced this card; join key back to the PHI-safe
     * detection context persisted on NudgeCardState. Null when no detection pass produced the
     * card.
     */
    detection_id?: null | string;
    /**
     * Number of matched signals (length of matched_signals)
     */
    matched_signal_count?: number | null;
    /**
     * Named signals (chips) the classifier matched. PHI-free labels only
     */
    matched_signals?: string[];
    /**
     * Identifier of the model used by the classifier
     */
    model_id?: null | string;
    /**
     * Version of the classifier prompt, for slicing false-positive rates by classifier change
     */
    prompt_version?: null | string;
    /**
     * Identifier of the protocol that surfaced (e.g. a ChenMed protocol id)
     */
    protocol_id: string;
    /**
     * Heidi session the nudge card belongs to
     */
    session_id: string;
    /**
     * How the card came to exist. Known values: auto (classifier-surfaced), manual (clinician
     * added from the protocol library)
     */
    source?: DownloadTrigger;
    /**
     * Hash of the transcript chunk that drove the detection; a join key only, never the raw text
     */
    transcript_chunk_hash?: null | string;
}

/**
 * Tracks export content saved event
 */
export type ExportContentSaved = {
    /**
     * Has Logo
     */
    has_logo?: boolean | null;
    /**
     * Has Signature
     */
    has_signature?: boolean | null;
    /**
     * Has Text
     */
    has_text?: boolean | null;
    /**
     * Position
     */
    position?: null | string;
    /**
     * Scope
     */
    scope?: null | string;
    /**
     * Signature Type
     */
    signature_type?: null | string;
    /**
     * Team Id
     */
    team_id?: null | string;
    /**
     * Type
     */
    type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user activates their trial by completing their first session after signup,
 * marking when the trial countdown begins
 */
export type TrialActivated = {
    /**
     * User's current plan before trial
     */
    current_plan?: null | string;
    /**
     * Whether the user's current plan is already a trial
     */
    current_plan_is_trial?: boolean | null;
    /**
     * Trial end date in ISO 8601 format
     */
    end_date?: null | string;
    /**
     * Trial start date in ISO 8601 format
     */
    start_date?: null | string;
    /**
     * Length of trial in days
     */
    trial_period_days?: number | null;
    /**
     * The trial plan name
     */
    trial_plan?: null | string;
    /**
     * Type of trial
     */
    trial_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user completes their first dictation within their first week of account
 * creation
 */
export type FirstDictationCompleted = {
    /**
     * Client platform the dictation was completed on
     */
    client_platform?: null | string;
    /**
     * Whole days elapsed between account creation and the first dictation completion
     */
    days_since_signup?: number | null;
    /**
     * The v2 dictation generation type (DICTATION or LIVE_DICTATION)
     */
    generation_method?: GenerationMethod | null;
    /**
     * Session identifier of the first completed dictation
     */
    session_id?: null | string;
    /**
     * Kinde user identifier
     */
    user_id?: null | string;
    [property: string]: any;
}

export type GenerationMethod = "DICTATION" | "LIVE_DICTATION";

/**
 * Tracks when a call is transferred to another party
 */
export type CallTransferred = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * SHA-256 hash of the transfer destination phone number
     */
    handoff_phone_hash?: null | string;
    /**
     * Reason for the transfer
     */
    transfer_reason: string;
    [property: string]: any;
}

/**
 * Tracks when a checkout is completed (Stripe checkout.session.completed)
 */
export type CheckoutCompleted = {
    /**
     * Stripe coupon ID applied at checkout, if any
     */
    coupon_id?: null | string;
    /**
     * Stripe checkout session ID
     */
    order_id?: null | string;
    /**
     * Category of the product purchased
     */
    product_category?: null | string;
    /**
     * Name of the product purchased
     */
    product_name?: null | string;
    /**
     * Stripe promotion code ID redeemed at checkout, if any
     */
    promotion_code?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user removes an objective
 */
export type ObjectiveRemoved = {
    /**
     * Objective identifier
     */
    objective_id: string;
    [property: string]: any;
}

/**
 * Tracks community created event
 */
export type CommunityCreated = {
    /**
     * Template Id
     */
    template_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an evidence chat encounters an error
 */
export type EvidenceChatError = {
    /**
     * Reason for attachment errors
     */
    attachment_reason?: null | string;
    /**
     * Block type for block-level errors
     */
    block_type?: null | string;
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Time until error occurred in milliseconds
     */
    duration_ms?: number | null;
    /**
     * Error code if available
     */
    error_code?: null | string;
    /**
     * Error message
     */
    error_message?: null | string;
    /**
     * Discriminates which error path fired this event
     */
    error_type?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * HTTP status code when available
     */
    http_status?: number | null;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Query length in characters
     */
    query_length?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * The mode the backend routed to (legacy, evidence)
     */
    routed_mode?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a session is marked as context only
 */
export type SessionMarkedAsContextOnly = {
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when form template fields are updated
 */
export type FormTemplateFieldsUpdated = {
    /**
     * Number of fields in the template
     */
    field_count?: number | null;
    /**
     * Template identifier
     */
    template_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks integration session created event
 */
export type IntegrationSessionCreated = {
    /**
     * Integration Type
     */
    integration_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks survey viewed event
 */
export type SurveyViewed = {
    /**
     * Feedback Type
     */
    feedback_type?: null | string;
    /**
     * Mode
     */
    mode?: null | string;
    /**
     * View Action
     */
    view_action?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user completes the Comms setup flow
 */
export type CommsSetupCompleted = {
    /**
     * Type of setup flow
     */
    setup_type: string;
    /**
     * Total number of steps in the setup flow
     */
    total_steps: number;
    [property: string]: any;
}

/**
 * Tracks successful completion of an offline recording sync
 */
export type OfflineSyncCompleted = {
    /**
     * Number of sync attempts made before completion
     */
    attempt_count: number | null;
    /**
     * Residency region code used for the sync
     */
    capture_region: string;
    /**
     * Encrypted local audio file size in bytes
     */
    file_size_bytes: number | null;
    /**
     * Desktop-generated local session identifier
     */
    local_session_id: string;
    /**
     * Captured audio duration in milliseconds
     */
    recorded_duration_ms: number | null;
    /**
     * Server session identifier when available
     */
    session_id: null | string;
    /**
     * Recording source
     */
    source: OfflineSyncDeferredSource;
}

/**
 * Tracks context uploaded event
 */
export type ContextUploaded = {
    /**
     * File Count
     */
    file_count?: number | null;
    /**
     * File Size
     */
    file_size?: number | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Upload Action
     */
    upload_action?: null | string;
    [property: string]: any;
}

/**
 * Tracks schedule upload processing failed event
 */
export type ScheduleUploadProcessingFailed = {
    /**
     * Stable failure code — a backend ScheduleUploadErrorCode value (e.g. invalid_file,
     * too_many_sessions) or a transport-level code (e.g. ERR_NETWORK, http_500). Unlike
     * error_message, never localized.
     */
    error_code?: null | string;
    /**
     * Error Message
     */
    error_message?: null | string;
    [property: string]: any;
}

/**
 * Tracks task deleted event
 */
export type TaskDeleted = {
    /**
     * ID of the user the task is assigned to at time of event (null if unassigned)
     */
    assignee_id?: null | string;
    /**
     * Type of assignee (e.g. user, team). Null if unassigned.
     */
    assignee_type?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Task category. Known values: document, action, communicate, order, coordinate
     */
    task_category?: null | string;
    /**
     * Task Id
     */
    task_id?: null | string;
    /**
     * Task Source
     */
    task_source?: null | string;
    /**
     * Status of the task at time of event. Known values: todo, done, archived
     */
    task_status?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user applies the personalised source recommendation, either directly or via
 * the customise wizard. Conversion leg of the source-recommendation funnel (shown ->
 * applied). `matched_*` fields reflect the BE-selected recommendation; `removed_*` /
 * `added_*` arrays carry the diff between the offered list and what the user actually saved
 * (only populated on the wizard path).
 * 2-0-0: renamed `location` -> `banner_location` (MODEL bump). The 1-0-0 property name
 * caused snowtype to emit a top-level TypeScript `Location` alias that shadowed the DOM
 * `Location` global; the rename scopes the alias to the source-recommendation banner and
 * makes snowtype emit `BannerLocation` instead. No other shape changes vs 1-0-0.
 */
export type SourceRecommendationApplied = {
    /**
     * Length of `added_domains`.
     */
    added_domain_count?: number | null;
    /**
     * User-added domains not in the original recommendation. Empty array on direct Apply. On
     * the customise path, preserves user-edit order — feeds gap-fill curation in the
     * find_medical_web_domains pipeline.
     */
    added_domains?: string[] | null;
    /**
     * Banner surface that produced the event. Known values: chat (in-chat banner above the
     * floating ask-Heidi input), popover (apply popover surfaced from source control), session
     * (banner inside an active session).
     */
    banner_location?: BannerLocation;
    /**
     * True iff the user went through the customise wizard. False on direct Apply (in which case
     * `removed_domains` / `added_domains` are always empty arrays).
     */
    is_customised?: boolean | null;
    /**
     * Human-readable name of the country whose tag drove the BE's regional ordering of the
     * recommendation. Empty string when unresolved.
     */
    matched_country?: null | string;
    /**
     * BE-matched specialty id used to score the recommendation. May fall back to a
     * generic-practitioner id when the user's profile has no specialty. Null when unresolved.
     */
    matched_specialty_id?: null | string;
    /**
     * Human-readable name of `matched_specialty_id`. Empty string during hydration. Use this
     * for analytics slicing.
     */
    matched_specialty_name?: null | string;
    /**
     * Length of `removed_domains`.
     */
    removed_domain_count?: number | null;
    /**
     * Recommendation domains absent from the persisted entity. Empty array on direct Apply. On
     * the customise path, preserves the recommendation order (best-first authoritativeness) so
     * the curation pipeline can read it as a down-weight signal.
     */
    removed_domains?: string[] | null;
    /**
     * Pre-cap recommendation size (BE `total_domains`). Mirrors `sources_count` on the other
     * banner events so funnels (shown -> applied) compare like-for-like.
     */
    sources_count?: number | null;
    /**
     * Size of the entity now in source control after Apply. Equals `sources_count` when nothing
     * was capped or customised. On the reuse path (existing collection / source list with the
     * same name) this is the existing entity's domain count.
     */
    total_domains_in_collection?: number | null;
}

/**
 * Tracks when an AI objective is published
 */
export type ObjectivePublished = {
    /**
     * Unique objective identifier
     */
    objective_id: string;
    /**
     * User who published the objective
     */
    published_by_user_id: string;
    /**
     * Version of the published objective
     */
    version: string;
    [property: string]: any;
}

/**
 * Tracks when a sign up is attributed to a specific product feature
 */
export type ProductSignUpSuccessful = {
    /**
     * Country selected during onboarding (falls back to the stored user country)
     */
    country?: null | string;
    /**
     * Display language code at onboarding completion (e.g. en, fr, de, ja)
     */
    display_language?: null | string;
    /**
     * EHR/EMR provider name from the backend EHRProvider catalogue (closed set; the onboarding
     * client offers a picker over these values, not free-text entry). Not pinned by enum
     * because the catalogue is large and grows frequently.
     */
    ehr_provider?: null | string;
    /**
     * Whether the onboarding request carried a Rewardful affiliate code (the code itself is not
     * sent)
     */
    has_affiliate_code?: boolean | null;
    /**
     * Role in the organisation
     */
    organisation_role?: OrganisationRole | null;
    /**
     * Organisation size bucket
     */
    organisation_size?: OrganisationSize | null;
    /**
     * Raw signup source from the onboarding request (scribe | evidence | practice | scribe_plus
     * | dictate). Named distinctly from sign_up_successful's signup_source, which carries a
     * different vocabulary (signup | cs-added). Left unpinned by enum because the backend
     * SignupSource set is expected to grow with new product surfaces.
     */
    product_signup_source?: null | string;
    /**
     * Product feature the sign up is attributed to
     */
    signup_attributed_feature?: null | string;
    /**
     * Resolved clinical specialty name from the specialty taxonomy at onboarding completion.
     * Free-text "other" specialties are not sent.
     */
    specialty?: null | string;
    [property: string]: any;
}

export type OrganisationRole = "INDIVIDUAL_CLINICIAN" | "LEAD" | "EXECUTIVE_TEAM" | "OWNER_OR_PRACTICE_MANAGER";

export type OrganisationSize = "JUST_ME" | "TWO_TO_FIVE" | "SIX_TO_TWENTY" | "TWENTY_ONE_TO_FIFTY" | "FIFTY_ONE_PLUS";

/**
 * Tracks when product tour is completed
 */
export type ProductTourCompleted = {
    /**
     * Platform where tour completed
     */
    client_platform?: null | string;
    [property: string]: any;
}

/**
 * Tracks when recording consent is captured from a participant
 */
export type RecordingConsentCaptured = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Method used to capture consent
     */
    consent_method: string;
    /**
     * Legal jurisdiction for the consent
     */
    jurisdiction?: null | string;
    [property: string]: any;
}

/**
 * Tracks when user exports CPD (Continuing Professional Development) data
 */
export type CpdExported = {
    /**
     * Number of activities being exported
     */
    activities_count?: number | null;
    /**
     * Date preset used if in date_range mode
     */
    date_preset?: null | string;
    /**
     * Number of days in the export range
     */
    date_range_days?: number | null;
    /**
     * End date of export range (ISO string)
     */
    end_date?: null | string;
    /**
     * How the export was triggered
     */
    export_mode?: null | string;
    /**
     * Number of activities removed from export (checkout mode only)
     */
    removed_count?: number | null;
    /**
     * Number of sources being exported
     */
    sources_count?: number | null;
    /**
     * Start date of export range (ISO string)
     */
    start_date?: null | string;
    /**
     * Total time in minutes being exported
     */
    total_time_minutes?: number | null;
    [property: string]: any;
}

/**
 * Tracks when user clicks to extend trial
 */
export type TrialExtendClicked = {
    /**
     * Number of days to extend
     */
    extension_days?: number | null;
    /**
     * Type of trial
     */
    trial_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a section is deleted in the note, template, or document editor
 */
export type SectionDeleted = {
    /**
     * Which editor the section was deleted in (note, template, document)
     */
    editor_context?: null | string;
    /**
     * Which note-editor engine the section was deleted in (markdown_native, vmarkdown)
     */
    editor_type?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an Ask Heidi shortcut is run
 */
export type EvidenceShortcutRun = {
    /**
     * Length of the slash search query when run from search; raw query text is never collected
     */
    query_length?: number | null;
    /**
     * Shortcut identifier
     */
    shortcut_id?: null | string;
    /**
     * How the run shortcut was reached (recommended | saved | search)
     */
    shortcut_source?: null | string;
    /**
     * Which Ask Heidi surface the shortcut was run on (floating_bar | side_panel | chat_input)
     */
    surface?: null | string;
    /**
     * Whether the interaction was via click or keyboard
     */
    via?: null | string;
    [property: string]: any;
}

/**
 * Tracks chat context add file clicked event
 */
export type ChatContextAddFileClicked = {
    /**
     * Chat Id
     */
    chat_id?: null | string;
    /**
     * Is Mobile
     */
    is_mobile?: boolean | null;
    [property: string]: any;
}

/**
 * Tracks patient context toggle off event
 */
export type PatientContextToggleOff = {
    /**
     * Patient Id
     */
    patient_id?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when user searches for clinical codes
 */
export type CodeSearched = {
    /**
     * Code sets searched
     */
    codeset_searched?: string[] | null;
    /**
     * Note identifier
     */
    note_id?: null | string;
    /**
     * Number of search results
     */
    search_results_count?: number | null;
    /**
     * Search term used
     */
    search_term?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks settings updated event. For update_action copy_setting_toggled (one of the four
 * copy-formatting toggles flipped), the event records user intent at click time, not
 * confirmed backend persistence.
 */
export type SettingsUpdated = {
    /**
     * For update_action copy_setting_toggled, the full state of the four copy-formatting
     * toggles after the change, as clicked (not confirmed persisted). Same shape as
     * session_copied.copy_settings so one frontend snapshot validates on both events. Omit on
     * other settings_updated producers.
     */
    copy_settings?: null | SettingsUpdatedCopySettings;
    /**
     * Where the setting was changed. Known values for Identify speakers (update_action
     * identify_speakers_toggled): session_gear (in-session transcript gear), session_defaults
     * (Settings). Omit on every other settings_updated producer — do not send null. Do not enum
     * this field; other settings surfaces will add values without a REVISION.
     */
    entry_point?: null | string;
    /**
     * Fixed minutes per activity
     */
    fixed_minutes_per_activity?: number | null;
    /**
     * Fixed minutes per source
     */
    fixed_minutes_per_source?: number | null;
    /**
     * For update_action copy_setting_toggled, the value the toggle was changed to. Omit on
     * other settings_updated producers.
     */
    is_enabled?: boolean | null;
    /**
     * Previous setting value
     */
    previous_value?: null | string;
    /**
     * Setting name. For update_action copy_setting_toggled, one of headings | bold_and_italics
     * | bullet_points | tables.
     */
    setting?: null | string;
    /**
     * Setting Value
     */
    setting_value?: null | string;
    /**
     * Specialty Id
     */
    specialty_id?: null | string;
    /**
     * Update Action. Known values include identify_speakers_toggled and copy_setting_toggled (a
     * copy-formatting toggle was flipped; setting names the toggle, is_enabled its new value,
     * copy_settings the full state after the change).
     */
    update_action?: null | string;
    /**
     * Setting value
     */
    value?: null | string;
    [property: string]: any;
}

export type SettingsUpdatedCopySettings = {
    /**
     * Whether bold and italic marks are kept in copied output
     */
    bold_and_italics?: boolean | null;
    /**
     * Whether bullet points are kept in copied output
     */
    bullet_points?: boolean | null;
    /**
     * Whether headings are kept in copied output
     */
    headings?: boolean | null;
    /**
     * Whether tables are kept in copied output
     */
    tables?: boolean | null;
    [property: string]: any;
}

/**
 * Tracks organisation survey submitted event
 */
export type OrganisationSurveySubmitted = {
    /**
     * Cqc Practice Id
     */
    cqc_practice_id?: null | string;
    /**
     * Ehr Provider
     */
    ehr_provider?: null | string;
    /**
     * Is Official Practice
     */
    is_official_practice?: boolean | null;
    /**
     * Organisation Name
     */
    organisation_name?: null | string;
    /**
     * Organisation Role
     */
    organisation_role?: null | string;
    /**
     * Organisation Size
     */
    organisation_size?: null | string;
    /**
     * Other Specialty
     */
    other_specialty?: null | string;
    /**
     * Specialty Id
     */
    specialty_id?: null | string;
    /**
     * Specialty Name
     */
    specialty_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks when personalisation insight extraction fails
 */
export type PersonalizationFailed = {
    /**
     * Error message
     */
    error_message?: null | string;
    [property: string]: any;
}

/**
 * Tracks integration unlinked event
 */
export type IntegrationUnlinked = {
    /**
     * EHR provider name
     */
    ehr_provider?: null | string;
    /**
     * Integration Type
     */
    integration_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks successful creation of a letter, from a session document or directly with empty
 * content
 */
export type LetterCreated = {
    /**
     * Stored body format when the letter was created
     */
    content_type: ContentType;
    /**
     * Whether an organisation letterhead was attached at creation
     */
    has_letterhead: boolean;
    /**
     * Created letter identifier
     */
    letter_id: string;
    /**
     * Session containing the letter
     */
    session_id: string;
}

/**
 * Stored body format when the letter was created
 */
export type ContentType = "MARKDOWN" | "HTML";

/**
 * Tracks when a patient document upload completes FE-side
 */
export type PatientDocumentUploaded = {
    /**
     * Who initiated the upload. Known values: clinician, assistant (Assistant uploading on a
     * clinician's behalf, per BE-1h / ml-scribe#10204), patient, integration, bulk_import,
     * other. Distinguishing clinician vs assistant is load-bearing — do not hardcode clinician.
     */
    actor?: null | string;
    /**
     * Document identifier
     */
    document_id?: null | string;
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown.
     */
    feature_flag_variant?: null | string;
    /**
     * Uploaded file size in bytes. Useful for failure-mode analysis.
     */
    file_size_bytes?: number | null;
    /**
     * File MIME family or extension. Known values: pdf, image, text, other.
     */
    file_type?: null | string;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * UI surface that produced the event. Known values: drawer, full_page.
     */
    surface?: null | string;
    /**
     * UUID of the patient profile view this event belongs to. Inherited from
     * patient_profile_opened.
     */
    view_id?: null | string;
}

/**
 * Tracks when user searches community templates
 */
export type CommunityTemplateSearched = {
    /**
     * Search query
     */
    query?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a feature is toggled
 */
export type FeatureToggled = {
    /**
     * Code set name
     */
    codeset_name?: null | string;
    /**
     * Whether the feature is enabled
     */
    enabled?: boolean | null;
    /**
     * Feature name
     */
    feature?: null | string;
    /**
     * Whether the feature is now enabled
     */
    is_enabled?: boolean | null;
    /**
     * New mode after toggle
     */
    new_mode?: null | string;
    /**
     * Previous mode before toggle
     */
    previous_mode?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Type of toggle action
     */
    toggle_action?: null | string;
    /**
     * User geography
     */
    user_geography?: null | string;
    /**
     * User specialty
     */
    user_specialty?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user expands or collapses the references section
 */
export type EvidenceReferencesSectionToggled = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * New state after toggle (expanded or collapsed)
     */
    is_expanded?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Backend message ID (msg_*) of the assistant response whose references section was
     * toggled. Joins the expand/collapse engagement to the specific model that produced the
     * response.
     */
    message_id?: null | string;
    /**
     * Number of references in the section
     */
    references_count?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * What triggered the toggle (manual, auto)
     */
    trigger?: null | string;
    [property: string]: any;
}

/**
 * Tracks evidence category clicked event
 */
export type EvidenceCategoryClicked = {
    /**
     * Category
     */
    category?: null | string;
    /**
     * Evidence Location
     */
    evidence_location?: null | string;
    /**
     * Is Evidence Pro
     */
    is_evidence_pro?: boolean | null;
    /**
     * Is Managed
     */
    is_managed?: boolean | null;
    /**
     * Is Selected
     */
    is_selected?: boolean | null;
    /**
     * Is Team Member
     */
    is_team_member?: boolean | null;
    /**
     * Region
     */
    region?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the user dismisses the post-note Evidence summary suggestion banner
 */
export type EvidenceSummarySuggestionDismissed = {
    /**
     * Cumulative number of times the user has dismissed this banner
     */
    dismiss_count?: number | null;
    /**
     * Scribe session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks team member deleted event
 */
export type TeamMemberDeleted = {
    /**
     * Email of deleted team member
     */
    member_email?: null | string;
    /**
     * Member Id
     */
    member_id?: null | string;
    /**
     * Team Id
     */
    team_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks the persisted delivery result of a letter send attempt
 */
export type LetterSendCompleted = {
    /**
     * Overall persisted outcome of the send attempt
     */
    delivery_status: DeliveryStatus;
    /**
     * Targeted recipients with a FAILED outcome in this attempt
     */
    failed_recipient_count: number;
    /**
     * Whether the letter had a previous persisted delivery attempt before this attempt
     */
    is_retry: boolean;
    /**
     * Letter whose send attempt completed
     */
    letter_id: string;
    /**
     * Targeted recipients with a NOT_ATTEMPTED outcome in this attempt
     */
    not_attempted_recipient_count: number;
    /**
     * Total recipients targeted during this attempt; previously sent recipients are excluded on
     * partial retry
     */
    recipient_count: number;
    /**
     * Targeted recipients with a SENT outcome in this attempt
     */
    sent_recipient_count: number;
    /**
     * Session containing the letter
     */
    session_id: string;
}

/**
 * Overall persisted outcome of the send attempt
 */
export type DeliveryStatus = "SENT" | "PARTIALLY_SENT" | "FAILED";

/**
 * Tracks when the user accepts, edits, or rejects a surfaced extracted field
 */
export type PatientFieldExtractionResolved = {
    /**
     * Resolution chosen by the user. Known values: accepted, edited, rejected.
     */
    action?: null | string;
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown.
     */
    feature_flag_variant?: null | string;
    /**
     * Name of the resolved field. Known values: dob, name, gender, pronouns, allergies,
     * medications, medical_history. Field name only — never the value (PII).
     */
    field?: null | string;
    /**
     * Extraction pipeline that produced the resolved field. Known values: legacy, temporal.
     * Mirrors patient_field_extraction_surfaced so the resolution rate survives the BE-1b
     * migration.
     */
    pathway?: null | string;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * What the extraction was drawn from. Known values: note_transcript, document, summary,
     * other.
     */
    source?: null | string;
    /**
     * UI surface that produced the event. Known values: drawer, full_page.
     */
    surface?: null | string;
    /**
     * UUID of the patient profile view this event belongs to. Inherited from
     * patient_profile_opened.
     */
    view_id?: null | string;
}

/**
 * Tracks when output is created
 */
export type OutputCreated = {
    /**
     * Source of output
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a note push to EHR fails
 */
export type NotePushFailed = {
    /**
     * Document Id
     */
    document_id?: null | string;
    /**
     * EHR provider name
     */
    ehr_provider?: null | string;
    /**
     * Error Message
     */
    error_message?: null | string;
    /**
     * Whether this is a sectional write back
     */
    is_sectional_write_back?: boolean | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks EHR launch completion
 */
export type EhrLaunchCompleted = {
    /**
     * EHR provider
     */
    ehr_provider?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a bulk scheduled call upload is processed
 */
export type ScheduledCallBulkUploadProcessed = {
    /**
     * Number of rows accepted
     */
    accepted: number;
    /**
     * Unique bulk upload batch identifier
     */
    bulk_upload_id: string;
    /**
     * Objective assigned to the uploaded calls
     */
    objective_id: string;
    /**
     * Number of rows rejected
     */
    rejected: number;
    /**
     * Total number of rows in the upload
     */
    row_count: number;
    [property: string]: any;
}

/**
 * Tracks schedule upload sessions created event
 */
export type ScheduleUploadSessionsCreated = {
    /**
     * Num Created
     */
    num_created?: number | null;
    /**
     * Num Skipped
     */
    num_skipped?: number | null;
    [property: string]: any;
}

/**
 * Tracks when pre-chart generation reaches a terminal state (success, empty, or failed)
 */
export type PrechartGenerationCompleted = {
    /**
     * BE-computed milliseconds from generation start to terminal state. Ceiling at 600000 (10
     * minutes) gives roughly 6x headroom on the observed worst case before timeout.
     */
    duration_ms?: number | null;
    /**
     * Which EHR the chart was built from, as the EHRProvider enum value (e.g. athenahealth,
     * Veradigm EHR). Null when generation failed before an EHR could be resolved: on the
     * on-demand path resolving the EHR is itself one of the steps that can fail, and such a
     * failure is still worth counting, just not attributable to a provider. Bounded at 64
     * because the value is always an EHRProvider member and the longest is 32 characters.
     */
    ehr_provider?: null | string;
    /**
     * Reason for terminal failure. Non-null only when status = failed; null otherwise
     * (including when status = empty). Known values: athena_fetch_failed (upstream Athena data
     * fetch broke before the agent ran), agent_error (catch-all for any Vertex ADK agent
     * failure before producing a parseable response — internal exception, malformed response,
     * safety-filter block, content-policy rejection, quota / rate-limit), timeout (we waited
     * too long for the agent), other. ml-scribe can split agent_error into finer values (e.g.
     * agent_safety_blocked, agent_invocation_error, agent_response_parse_failed) via
     * description-only updates once the real distribution is visible in prod.
     */
    failure_reason?: null | string;
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown. BE-emitted; stamp unknown pre-GA since the pre-chart surface is fully flag-gated
     * and there is no held-back control. Revisit when a held-back control exists.
     */
    feature_flag_variant?: null | string;
    /**
     * What kicked off this generation. Known values: queue (generation kicked off via the
     * background / queued pipeline — the default automatic path, and the dominant value in
     * practice), on_demand (user action explicitly requested it), regenerate (user asked for a
     * fresh version replacing a prior one). Mirrors the trigger field on the existing
     * PRECHART_WORKFLOW_EXECUTED billing event for cross-check. Named generation_trigger rather
     * than trigger to disambiguate from prechart_viewed.view_trigger.
     */
    generation_trigger?: null | string;
    /**
     * Session the generation was for
     */
    session_id?: null | string;
    /**
     * Terminal state. Known values: success (agent produced usable content), empty (agent ran
     * without error but produced no usable content — a quality signal, not a pipeline-health
     * signal), failed (agent or upstream raised an error). Three-valued by design; bucketing
     * empty under failed would inflate failure-rate dashboards.
     */
    status?: null | string;
    /**
     * The template applied during this generation, if any. Null when no template was used.
     * Whether the template was used can be derived as template_id IS NOT NULL in SQL /
     * Amplitude.
     */
    template_id?: null | string;
}

/**
 * Tracks when an outbound call is ringing at the recipient
 */
export type CallRinging = {
    /**
     * Unique call identifier
     */
    call_id: string;
    [property: string]: any;
}

/**
 * Tracks a Flagsmith feature flag evaluation for a user identity, forwarded via webhook
 */
export type FlagsmithFlagEvaluated = {
    /**
     * Flagsmith environment ID
     */
    environment_id?: number | null;
    /**
     * Whether the flag is enabled for this identity
     */
    flag_enabled?: boolean | null;
    /**
     * Name of the feature flag
     */
    flag_name?: null | string;
    /**
     * Flagsmith feature type (e.g. STANDARD, MULTIVARIATE)
     */
    flag_type?: null | string;
    /**
     * The feature state value (cast to string), if any
     */
    flag_value?: null | string;
    /**
     * The Flagsmith user identity the flags were evaluated for
     */
    identity?: null | string;
    /**
     * Flagsmith segment names the identity belongs to at evaluation time
     */
    segments?: string[] | null;
    /**
     * Flagsmith identity traits as key-value pairs at evaluation time
     */
    traits?: { [key: string]: boolean | number | null | string } | null;
    [property: string]: any;
}

/**
 * Tracks when the BE content-extraction pipeline reaches a terminal state for a patient
 * document
 */
export type PatientDocumentProcessed = {
    /**
     * Document identifier
     */
    document_id?: null | string;
    /**
     * Reason for terminal failure. Null when is_ready = true. Known values: unsupported_format,
     * corrupt, extraction_failed, timeout, size_limit_exceeded, other. BE may extend without a
     * schema bump.
     */
    failure_reason?: null | string;
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown. Pre-GA the surface is fully flag-gated so every uploaded doc is from a treated
     * user by definition — BE stamps unknown until a held-back control exists.
     */
    feature_flag_variant?: null | string;
    /**
     * True on successful processing, false on terminal failure.
     */
    is_ready?: boolean | null;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * Milliseconds from upload received to terminal state. BE-computed. Ceiling at 600000 (10
     * minutes) gives ~6x headroom on BE-1c's ~90s worst case before fail.
     */
    time_to_ready_ms?: number | null;
}

/**
 * Tracks when a subscription is renewed
 */
export type SubscriptionRenewed = {
    /**
     * Renewal amount
     */
    amount?: number | null;
    /**
     * Billing period (month/year)
     */
    billing_period?: null | string;
    /**
     * Currency code
     */
    currency?: null | string;
    /**
     * Product the subscription is for (e.g. scribe, evidence)
     */
    feature?: null | string;
    /**
     * Subscription plan type
     */
    plan_type?: null | string;
    /**
     * Subscription identifier
     */
    subscription_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks integration widget auth step errored event
 */
export type IntegrationWidgetAuthStepErrored = {
    /**
     * Details
     */
    details?: { [key: string]: any } | null;
    /**
     * Integration Type
     */
    integration_type?: null | string;
    /**
     * Step
     */
    step?: null | string;
    [property: string]: any;
}

/**
 * Tracks template opened event
 */
export type TemplateOpened = {
    /**
     * Template Author Group
     */
    template_author_group?: null | string;
    /**
     * Template Editor Version
     */
    template_editor_version?: null | string;
    /**
     * Template Id
     */
    template_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a feature's mode is toggled
 */
export type FeatureModeToggled = {
    /**
     * Feature name
     */
    feature?: null | string;
    /**
     * New mode
     */
    mode?: null | string;
    /**
     * Previous mode
     */
    old_mode?: null | string;
    [property: string]: any;
}

/**
 * Tracks when user clicks on a citation
 */
export type CitationClicked = {
    /**
     * Index of citation
     */
    citation_index?: number | null;
    /**
     * URL of citation
     */
    citation_url?: null | string;
    /**
     * Feature where citation was clicked
     */
    feature?: null | string;
    [property: string]: any;
}

/**
 * Tracks note reverted event
 */
export type NoteReverted = {
    /**
     * Note Id
     */
    note_id?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Target Edit Id
     */
    target_edit_id?: null | string;
    /**
     * Target Note Id
     */
    target_note_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a social sharing button is clicked for Evidence
 */
export type EvidenceShareSocialClicked = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Social platform selected for sharing
     */
    platform?: EvidenceShareSocialClickedPlatform | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

export type EvidenceShareSocialClickedPlatform = "x" | "linkedin" | "reddit" | "whatsapp";

/**
 * Tracks when extracted data is reviewed by the user
 */
export type ExtractionReviewed = {
    /**
     * Number of fields reviewed
     */
    fields_reviewed_count?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a session accordion row expands inside an open patient profile
 */
export type PatientSessionExpanded = {
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown.
     */
    feature_flag_variant?: null | string;
    /**
     * True when this is the most-recent session, which is auto-expanded on profile open.
     */
    is_latest?: boolean | null;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * Identifier of the session whose accordion row expanded
     */
    session_id?: null | string;
    /**
     * UI surface that produced the event. Known values: drawer, full_page.
     */
    surface?: null | string;
    /**
     * UUID of the patient profile view this event belongs to. Inherited from
     * patient_profile_opened.
     */
    view_id?: null | string;
}

/**
 * Tracks when fields are extracted from a form template
 */
export type FormTemplateFieldsExtracted = {
    /**
     * Processing duration in milliseconds
     */
    duration_ms?: number | null;
    /**
     * Number of fields extracted from the PDF
     */
    extracted_field_count?: number | null;
    /**
     * Name of the uploaded file
     */
    file_name?: null | string;
    /**
     * Whether the LLM fallback was used (non-fillable PDF path)
     */
    llm_extraction_used?: boolean | null;
    /**
     * Number of pages in the PDF
     */
    page_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks when patient is selected
 */
export type PatientSelected = {
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Source of patient selection
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Fired once per stage-level evaluation error logged against a Translator message during
 * evaluation
 */
export type TranslatorEvaluationErrorLogged = {
    /**
     * One or more evaluation error types from the selected stage.
     */
    error_types: string[];
    /**
     * Whether the reviewer attached a free-text note to this evaluation error (presence flag
     * only; the note itself is not tracked)
     */
    has_note: boolean;
    /**
     * Whether the evaluation error has the potential to impact the clinical care of the
     * patient. Independent of severity
     */
    is_clinical: boolean;
    /**
     * Translator message identifier
     */
    message_id: string;
    /**
     * Translator session identifier
     */
    session_id: string;
    /**
     * Rubric severity. Known values: minor, major, critical
     */
    severity: Severity;
    /**
     * Source language code for the original message, e.g. en
     */
    source_language: string;
    /**
     * Pipeline stage the evaluation error is attributed to. Known values: recording,
     * speech_to_text, translation, text_to_speech
     */
    stage: Stage;
    /**
     * Identifier of the speech-to-text model used
     */
    stt_model?: null | string;
    /**
     * Groups every evaluation error logged in a single feedback submission; also present on the
     * corresponding translator_translation_rated event
     */
    submission_id: string;
    /**
     * Target language code for the translated message, e.g. vi
     */
    target_language: string;
    /**
     * Non-whitespace character count of the translated text. Preferred rate denominator for
     * languages that do not delimit words with spaces, e.g. yue
     */
    translated_char_count?: number | null;
    /**
     * Whitespace-delimited token count of the translated text, used as the denominator for
     * error rate
     */
    translated_word_count?: number | null;
    /**
     * Identifier of the translation model used
     */
    translation_model?: null | string;
    /**
     * Identifier of the text-to-speech model used
     */
    tts_model?: null | string;
}

/**
 * Rubric severity. Known values: minor, major, critical
 */
export type Severity = "minor" | "major" | "critical";

/**
 * Pipeline stage the evaluation error is attributed to. Known values: recording,
 * speech_to_text, translation, text_to_speech
 */
export type Stage = "recording" | "speech_to_text" | "translation" | "text_to_speech";

/**
 * Tracks integration updated event
 */
export type IntegrationUpdated = {
    /**
     * Integration Type
     */
    integration_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks note sync failed event
 */
export type NoteSyncFailed = {
    /**
     * Details
     */
    details?: { [key: string]: any } | null;
    /**
     * Integration Type
     */
    integration_type?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Sync action performed
     */
    sync_action?: null | string;
    [property: string]: any;
}

/**
 * A dismissed nudge card was reopened
 */
export type NudgeCardReopened = {
    /**
     * Version of the protocol catalogue that produced this card
     */
    catalogue_version: string;
    /**
     * Classifier confidence for the match (0-1)
     */
    confidence: number;
    /**
     * Join key back to the PHI-safe detection context persisted on NudgeCardState. Null when no
     * detection pass produced the card
     */
    detection_id?: null | string;
    /**
     * Number of matched signals (length of matched_signals)
     */
    matched_signal_count?: number | null;
    /**
     * Named signals (chips) the classifier matched. PHI-free labels only
     */
    matched_signals?: string[];
    /**
     * Identifier of the model used by the classifier
     */
    model_id?: null | string;
    /**
     * Version of the classifier prompt, for slicing false-positive rates by classifier change
     */
    prompt_version?: null | string;
    /**
     * Identifier of the protocol that was reopened
     */
    protocol_id: string;
    /**
     * Heidi session the nudge card belongs to
     */
    session_id: string;
    /**
     * How the card came to exist. Known values: auto (classifier-surfaced), manual (clinician
     * added from the protocol library)
     */
    source?: DownloadTrigger;
    /**
     * Hash of the transcript chunk that drove the detection; a join key only, never the raw text
     */
    transcript_chunk_hash?: null | string;
}

/**
 * Tracks when voicemail is detected on an outbound call
 */
export type CallVoicemailDetected = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Voicemail detection confidence score
     */
    confidence?: number | null;
    [property: string]: any;
}

/**
 * Tracks when a user cancels their subscription
 */
export type SubscriptionCancelled = {
    /**
     * Billing interval (month/year)
     */
    billing_interval?: null | string;
    /**
     * Billing period descriptor
     */
    billing_period?: null | string;
    /**
     * Whether subscription ends at period end rather than immediately
     */
    cancel_at_period_end?: boolean | null;
    /**
     * Additional comment from user about cancellation
     */
    cancel_comment?: null | string;
    /**
     * Structured feedback category for cancellation
     */
    cancel_feedback?: null | string;
    /**
     * Reason provided by user for cancellation
     */
    cancellation_reason?: null | string;
    /**
     * Product the subscription is for (e.g. scribe, evidence)
     */
    feature?: null | string;
    /**
     * Reason selected in Heidi cancel-retention flow (from
     * ProductSubscription.cancellation_intent CancellationReason; distinct from Stripe
     * cancellation_reason; free-text detail is never sent)
     */
    heidi_cancellation_reason?: HeidiCancellationReasonEnum | null;
    /**
     * User response to the Heidi save offer
     */
    offer_outcome?: OfferOutcome | null;
    /**
     * Save offer type shown in Heidi cancel-retention flow
     */
    offer_type?: OfferType | null;
    /**
     * The subscription plan that was cancelled
     */
    plan?: null | string;
    /**
     * Type of subscription plan
     */
    plan_type?: null | string;
    /**
     * Stripe price ID
     */
    price_id?: null | string;
    /**
     * Stripe subscription ID
     */
    subscription_id?: null | string;
    [property: string]: any;
}

export type HeidiCancellationReasonEnum = "too_expensive" | "need_more_features" | "found_alternative" | "no_longer_need" | "other";

export type OfferOutcome = "accepted" | "declined";

export type OfferType = "discount" | "clinician_session";

/**
 * Tracks when a user configures an objective
 */
export type ObjectiveConfigured = {
    /**
     * Objective direction (inbound or outbound)
     */
    direction?: null | string;
    /**
     * Objective identifier
     */
    objective_id: string;
    /**
     * Where the configuration was made (create_flow or edit_form)
     */
    source: string;
    [property: string]: any;
}

/**
 * Tracks when OTP verification succeeds for a session link share
 */
export type SessionLinkShareOtpVerified = {
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when help is accessed or viewed
 */
export type HelpViewed = {
    /**
     * URL of the help article viewed
     */
    article_url?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a trial begins
 */
export type TrialStarted = {
    /**
     * Duration of the trial in days
     */
    duration_days?: number | null;
    /**
     * Trial end date (ISO 8601)
     */
    end_date?: null | string;
    /**
     * Product the trial is for (e.g. evidence, scribe)
     */
    feature?: null | string;
    /**
     * Trial plan type
     */
    plan_type?: null | string;
    /**
     * Trial start date (ISO 8601)
     */
    start_date?: null | string;
    /**
     * Trial identifier
     */
    trial_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user submits feedback on a reference
 */
export type EvidenceReferenceFeedbackSubmitted = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Feedback type (positive, negative)
     */
    feedback_type?: null | string;
    /**
     * Whether the user provided a comment
     */
    has_comment?: boolean | null;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Message identifier of the response containing this reference
     */
    message_id?: null | string;
    /**
     * Position of the reference in the list
     */
    reference_index?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Domain of the source
     */
    source_domain?: null | string;
    /**
     * Title of the source
     */
    source_title?: null | string;
    /**
     * URL of the source being rated
     */
    source_url?: null | string;
    /**
     * User comment
     */
    user_comment?: null | string;
    [property: string]: any;
}

/**
 * Tracks the start of a drain for one or more queued offline recordings
 */
export type OfflineSyncStarted = {
    /**
     * Residency region code targeted by the drain
     */
    capture_region: string;
    /**
     * Backend reachability state when the drain started
     */
    connectivity_state: State;
    /**
     * Number of recordings pending when the drain started
     */
    pending_count: number;
    /**
     * Recording source
     */
    source: OfflineSyncDeferredSource;
    /**
     * Condition that started the drain
     */
    trigger: OfflineSyncStartedTrigger;
}

/**
 * Condition that started the drain
 */
export type OfflineSyncStartedTrigger = "online_ready" | "manual_retry" | "token_return" | "app_launch";

/**
 * Tracks task updated event
 */
export type TaskUpdated = {
    /**
     * ID of the user the task is assigned to at time of event (null if unassigned)
     */
    assignee_id?: null | string;
    /**
     * Type of assignee (e.g. user, team). Null if unassigned.
     */
    assignee_type?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Task category. Known values: document, action, communicate, order, coordinate
     */
    task_category?: null | string;
    /**
     * JSON array of field-level diffs applied in this update. Each element is an object with
     * field (string), previous (string), and next (string). Example:
     * [{"field":"status","previous":"todo","next":"done"}]
     */
    task_edits?: null | string;
    /**
     * Task Id
     */
    task_id?: null | string;
    /**
     * Task Source
     */
    task_source?: null | string;
    /**
     * Status of the task at time of event. Known values: todo, done, archived
     */
    task_status?: null | string;
    /**
     * Task Time To Completion
     */
    task_time_to_completion?: number | null;
    /**
     * Update Action
     */
    update_action?: null | string;
    [property: string]: any;
}

/**
 * Tracks when note selection is skipped
 */
export type NoteSelectionSkipped = {
    /**
     * IDs of candidate notes that were available
     */
    candidate_note_ids?: string[];
    /**
     * Experiment name
     */
    experiment_name?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Untyped experiment name from backend
     */
    untyped_experiment_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an inbound call is abandoned by the caller
 */
export type InboundCallAbandoned = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Time the caller waited before abandoning in seconds
     */
    wait_time_seconds: number;
    [property: string]: any;
}

/**
 * Tracks when a clinician attempts to hand a clinical task to Heidi as a Work project
 */
export type TaskDelegated = {
    /**
     * Where Assign to Heidi was clicked. Known values: session_panel, tasks_page
     */
    entry_point?: null | string;
    /**
     * Safe classifier when outcome is failed. Known values: missing_session, create_failed,
     * submit_failed, persist_failed. Never free-text.
     */
    error_code?: TaskDelegatedErrorCode | null;
    /**
     * Whether the Work project was created and the task link persisted
     */
    outcome?: TaskDelegatedOutcome | null;
    /**
     * Work project created for the Heidi run
     */
    project_id?: null | string;
    /**
     * Associated session ID when the task belongs to a session
     */
    session_id?: null | string;
    /**
     * Whether the task is linked to a session
     */
    session_linked?: boolean | null;
    /**
     * Task category. Known values: document, action, communicate, order, coordinate
     */
    task_category?: null | string;
    /**
     * Unique identifier of the clinical task
     */
    task_id?: null | string;
    /**
     * Team identifier
     */
    team_id?: null | string;
}

export type TaskDelegatedErrorCode = "missing_session" | "create_failed" | "submit_failed" | "persist_failed";

export type TaskDelegatedOutcome = "succeeded" | "failed";

/**
 * Tracks schedule viewed event
 */
export type ScheduleViewed = {
    /**
     * Date
     */
    date?: null | string;
    /**
     * Ehr Provider
     */
    ehr_provider?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user abandons the Comms setup flow
 */
export type CommsSetupStepAbandoned = {
    /**
     * Reason the setup was abandoned
     */
    reason?: null | string;
    /**
     * Type of setup flow
     */
    setup_type: string;
    /**
     * Identifier of the step where the user abandoned
     */
    step_id?: null | string;
    /**
     * Zero-based index of the step where the user abandoned
     */
    step_index?: number | null;
    /**
     * Total number of steps in the setup flow
     */
    total_steps: number;
    [property: string]: any;
}

/**
 * Tracks when a user updates an inbox conversation item
 */
export type InboxItemUpdated = {
    /**
     * Update action performed
     */
    action: string;
    /**
     * Conversation identifier
     */
    conversation_id?: null | string;
    /**
     * Whether the update included an attachment
     */
    has_attachment?: boolean | null;
    /**
     * New status of the conversation
     */
    new_status?: null | string;
    [property: string]: any;
}

/**
 * Tracks chat message sent with context event
 */
export type ChatMessageSentWithContext = {
    /**
     * Attachment Count
     */
    attachment_count?: number | null;
    /**
     * Attachment Types
     */
    attachment_types?: string[] | null;
    /**
     * Chat Id
     */
    chat_id?: null | string;
    /**
     * Has Attachments
     */
    has_attachments?: boolean | null;
    /**
     * Has Linked Patient
     */
    has_linked_patient?: boolean | null;
    /**
     * Has Linked Session
     */
    has_linked_session?: boolean | null;
    /**
     * Linked Patient Id
     */
    linked_patient_id?: null | string;
    /**
     * Linked Session Id
     */
    linked_session_id?: null | string;
    /**
     * Message Length
     */
    message_length?: number | null;
    /**
     * Mode
     */
    mode?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Fired when the active tab changes within a session.
 */
export type SessionTabSwitched = {
    /**
     * Document rendered in the tab being left. Absent for non-document tabs.
     */
    from_document_id?: null | string;
    /**
     * Kind of tab being left (context, note, transcript, document, letter, research_note,
     * session_merge). Free-form string so a new tab kind needs no schema bump.
     */
    from_tab_type: string;
    /**
     * The session the switch happened in.
     */
    session_id: string;
    /**
     * Document rendered in the tab being moved to. Absent for non-document tabs.
     */
    to_document_id?: null | string;
    /**
     * Kind of tab being moved to. Same vocabulary as from_tab_type.
     */
    to_tab_type: string;
}

/**
 * Tracks content pasted into the note editor, classified by clipboard flavor. PHI-free —
 * carries lengths and flavor only, never pasted content.
 */
export type NoteContentPasted = {
    /**
     * Character length of the pasted text
     */
    content_length?: number | null;
    /**
     * Editor variant receiving the paste (currently only markdown_native)
     */
    editor_type?: null | string;
    /**
     * Clipboard flavor classification (vscode_markdown, vscode_code, html, plain_text)
     */
    paste_flavor?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks tab sharing not available modal shown event
 */
export type TabSharingNotAvailableModalShown = {
    /**
     * Source that triggered the event (e.g. frontend, backend)
     */
    event_source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user opens the customise wizard from the personalised source recommendation
 * banner. Intermediate step in the funnel (shown -> customised -> applied with
 * `is_customised: true`). `matched_*` fields reflect the BE-selected recommendation, not
 * the user's current profile.
 * 2-0-0: renamed `location` -> `banner_location` (MODEL bump). The 1-0-0 property name
 * caused snowtype to emit a top-level TypeScript `Location` alias that shadowed the DOM
 * `Location` global; the rename scopes the alias to the source-recommendation banner and
 * makes snowtype emit `BannerLocation` instead. No other shape changes vs 1-0-0.
 */
export type SourceRecommendationCustomised = {
    /**
     * Banner surface that produced the event. Known values: chat (in-chat banner above the
     * floating ask-Heidi input), popover (apply popover surfaced from source control), session
     * (banner inside an active session).
     */
    banner_location?: BannerLocation;
    /**
     * Human-readable name of the country whose tag drove the BE's regional ordering of the
     * recommendation. Empty string when unresolved.
     */
    matched_country?: null | string;
    /**
     * BE-matched specialty id used to score the recommendation. May fall back to a
     * generic-practitioner id when the user's profile has no specialty. Null when unresolved.
     */
    matched_specialty_id?: null | string;
    /**
     * Human-readable name of `matched_specialty_id`. Empty string during hydration. Use this
     * for analytics slicing.
     */
    matched_specialty_name?: null | string;
    /**
     * Number of recommendations offered in the banner at the moment the user opened the
     * customise wizard (pre-cap, BE `total_domains`).
     */
    sources_count?: number | null;
}

/**
 * Tracks when a share notification is received
 */
export type ShareNotificationReceived = {
    /**
     * Type of share notification
     */
    notification_type?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a new conversation is created
 */
export type ConversationCreated = {
    /**
     * Unique conversation identifier
     */
    conversation_id: string;
    /**
     * Channel or method that created the conversation
     */
    created_via: string;
    [property: string]: any;
}

/**
 * Tracks when an API or integration token is generated
 */
export type TokenGenerated = {
    /**
     * Integration type the token is for
     */
    integration_type?: null | string;
    /**
     * Type of token generated
     */
    token_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks ehr started event
 */
export type EhrStarted = {
    /**
     * Start Action
     */
    start_action?: null | string;
    [property: string]: any;
}

/**
 * Tracks when integration setup is initiated
 */
export type IntegrationSetupStarted = {
    /**
     * Type of integration
     */
    integration_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when referral link is generated
 */
export type ReferralLinkCreated = {
    /**
     * The referral link that was created
     */
    referral_link?: null | string;
    [property: string]: any;
}

/**
 * Tracks when universal dictation post-processing fails
 */
export type UniversalDictationPostProcessingFailed = {
    /**
     * Actual LLM model used (may differ from requested)
     */
    actual_llm_model?: null | string;
    /**
     * Content type of the dictation output
     */
    content_type?: null | string;
    /**
     * Dictation session identifier
     */
    dictation_id?: null | string;
    /**
     * Type of error that caused the failure
     */
    error_type?: null | string;
    /**
     * Reason for the post-processing failure
     */
    failure_reason?: null | string;
    /**
     * Input languages detected in the dictation
     */
    input_languages?: string[] | null;
    /**
     * Whether a fallback model was used
     */
    is_fallback?: boolean | null;
    /**
     * LLM model used for post-processing
     */
    llm_model?: null | string;
    /**
     * Output language for the dictation
     */
    output_language?: null | string;
    /**
     * Duration of post-processing in milliseconds
     */
    processing_duration_ms?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a scheduled call is added to the queue
 */
export type ScheduledCallEnqueued = {
    /**
     * Bulk upload batch identifier if from a bulk upload
     */
    bulk_upload_id?: null | string;
    /**
     * Objective to execute during the call
     */
    objective_id: string;
    /**
     * Unique scheduled call identifier
     */
    scheduled_call_id: string;
    /**
     * ISO 8601 timestamp for the scheduled call
     */
    scheduled_for_iso: string;
    [property: string]: any;
}

/**
 * Tracks when OTP verification fails for a session link share
 */
export type SessionLinkShareOtpFailed = {
    /**
     * Reason for the OTP verification failure
     */
    failure_reason?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an Ask Heidi shortcut is saved or unsaved
 */
export type EvidenceShortcutSaveToggled = {
    /**
     * Whether the shortcut was saved or unsaved (saved | unsaved)
     */
    action?: EvidenceShortcutSaveToggledAction | null;
    /**
     * Whether the shortcut is team-managed (set on save; null on unsave)
     */
    is_team?: boolean | null;
    /**
     * Shortcut identifier
     */
    shortcut_id?: null | string;
    /**
     * Which Ask Heidi surface the toggle happened on (floating_bar | side_panel | chat_input)
     */
    surface?: null | string;
    [property: string]: any;
}

export type EvidenceShortcutSaveToggledAction = "saved" | "unsaved";

/**
 * Tracks when a user completes a step in the Comms setup flow
 */
export type CommsSetupStepCompleted = {
    /**
     * Type of setup flow
     */
    setup_type: string;
    /**
     * Identifier of the completed step
     */
    step_id: string;
    /**
     * Zero-based index of the completed step
     */
    step_index: number;
    /**
     * Total number of steps in the setup flow
     */
    total_steps: number;
    [property: string]: any;
}

/**
 * Fires for billing, subscription, and payment lifecycle actions. GRO-3835 introduces
 * cancel-retention funnel steps via subscription_action.
 */
export type SubscriptionEvent = {
    /**
     * Billing cycle (EVENT_TAXONOMY.md
     */
    billing_period?: null | string;
    /**
     * Retention offer type for cancel_offer_* actions. Null when no offer applies.
     */
    offer_type?: OfferType | null;
    /**
     * User's subscription tier at cancel-flow entry (ml-scribe SubscriptionTier /
     * new_product_tier). cancel_flow_opened only.
     */
    plan_tier?: PlanTier | null;
    /**
     * Cancellation reason code for cancel_reason_selected and cancel_offer_* actions (mirrors
     * ml-scribe CancellationReason; free-text detail is never sent). Null when not applicable
     * to the action.
     */
    reason?: HeidiCancellationReasonEnum | null;
    /**
     * Billing seat type for subscription_started and subscription_updated actions. Null when
     * not applicable.
     */
    seat_type?: SubscriptionEventSeatType | null;
    /**
     * Lifecycle action. Cancel-retention funnel values listed above; legacy checkout/paywall
     * actions remain on their existing schemas until migrated.
     */
    subscription_action: SubscriptionAction;
    [property: string]: any;
}

export type PlanTier = "PRO" | "PRO_PLUS" | "CLINICIAN_PRO" | "CLINICIAN_PRO_PLUS" | "EVIDENCE";

export type SubscriptionEventSeatType = "STANDARD" | "MAX";

/**
 * Lifecycle action. Cancel-retention funnel values listed above; legacy checkout/paywall
 * actions remain on their existing schemas until migrated.
 */
export type SubscriptionAction = "cancel_flow_opened" | "cancel_reason_selected" | "cancel_offer_shown" | "cancel_offer_accepted" | "cancel_offer_declined";

/**
 * Tracks legacy inline dictation completed event
 */
export type LegacyInlineDictationCompleted = {
    /**
     * Hold Duration Ms
     */
    hold_duration_ms?: number | null;
    /**
     * Source
     */
    source?: null | string;
    /**
     * Triggered By
     */
    triggered_by?: null | string;
    [property: string]: any;
}

/**
 * Tracks chronicle device connected event
 */
export type ChronicleDeviceConnected = {
    /**
     * Device Id
     */
    device_id?: null | string;
    /**
     * Firmware Version
     */
    firmware_version?: null | string;
    /**
     * Is Auto Reconnect
     */
    is_auto_reconnect?: boolean | null;
    /**
     * Is Reconnection
     */
    is_reconnection?: boolean | null;
    /**
     * Serial Number
     */
    serial_number?: null | string;
    [property: string]: any;
}

/**
 * Tracks document exported event
 */
export type DocumentExported = {
    /**
     * State of the four copy-formatting toggles applied to this payload. Present only when
     * settings_pipeline is toggles, since the legacy renderer ignores them.
     */
    copy_settings?: null | DocumentExportedCopySettings;
    /**
     * Document Id
     */
    document_id?: null | string;
    /**
     * Which flow triggered the export or print, such as letter_sender. Emitted by the frontend
     * since the letter-sender download and print paths landed, but absent from the schema until
     * now.
     */
    export_context?: null | string;
    /**
     * Format
     */
    format?: null | string;
    /**
     * How the exported document came to exist in the session (queue, picker, or manual).
     * Emitted by the frontend already, but absent from the schema until now.
     */
    generation_source?: null | string;
    /**
     * Has Custom Footer
     */
    has_custom_footer?: boolean | null;
    /**
     * Has Custom Header
     */
    has_custom_header?: boolean | null;
    /**
     * Has Signature
     */
    has_signature?: boolean | null;
    /**
     * Is Pinned Template
     */
    is_pinned_template?: boolean | null;
    /**
     * Is Starter Pack Template
     */
    is_starter_pack_template?: boolean | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Which renderer actually produced the exported or printed payload. toggles means the
     * copy-formatting toggles were applied, via either the canonical markdown renderer or the
     * improved-copy transform; legacy means the plain markdown-to-HTML fallback ran and no
     * toggle was honoured. Null when the export path never routes through that renderer at all.
     */
    settings_pipeline?: null | string;
    [property: string]: any;
}

export type DocumentExportedCopySettings = {
    /**
     * Whether bold and italic marks were kept in the exported output
     */
    bold_and_italics?: boolean | null;
    /**
     * Whether bullet points were kept in the exported output
     */
    bullet_points?: boolean | null;
    /**
     * Whether headings were kept in the exported output
     */
    headings?: boolean | null;
    /**
     * Whether tables were kept in the exported output
     */
    tables?: boolean | null;
    [property: string]: any;
}

/**
 * Tracks when a chat/Ask-Heidi response is inserted into a note, document, or new tab.
 * Adoption signal for chat responses. PHI-free — carries no note body. chat_id/message_id
 * join the adoption back to the specific model that produced the inserted response
 * (per-message model attribution).
 */
export type NoteInsertApplied = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Format the insert stored — markdown_native (raw markdown) or html (converted-HTML path)
     */
    editor_variant?: EditorVariant;
    /**
     * Which insert action fired
     */
    insert_action?: InsertAction;
    /**
     * Backend message ID (msg_*) of the assistant response being inserted. Joins the adoption
     * event to the specific model that produced this response.
     */
    message_id?: null | string;
    /**
     * Chat surface mode when known (e.g. 'evidence', 'ask-ai')
     */
    mode?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Format the insert stored — markdown_native (raw markdown) or html (converted-HTML path)
 */
export type EditorVariant = "markdown_native" | "html";

/**
 * Which insert action fired
 */
export type InsertAction = "add_to_note" | "replace_note" | "add_as_new_output";

/**
 * Tracks when editing of a share is requested
 */
export type ShareEditRequested = {
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Share identifier
     */
    share_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks the Evidence copy-to-clipboard PHI/PII confirmation popover lifecycle
 * (shown/confirmed/dismissed) — the SWR-88 med-device audit trail. PHI-free.
 * chat_id/message_id attribute the copy (an adoption signal) to the specific model that
 * produced the response.
 */
export type EvidenceCopyConfirmation = {
    /**
     * Lifecycle stage of the confirmation popover
     */
    action?: EvidenceCopyConfirmationAction;
    /**
     * Chat identifier the copy was attempted from
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Backend message ID (msg_*) of the assistant response being copied. Joins the copy to the
     * specific model that produced the response.
     */
    message_id?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Which copy button surface triggered the gate
     */
    surface?: NoteCopiedSurface;
    [property: string]: any;
}

/**
 * Lifecycle stage of the confirmation popover
 */
export type EvidenceCopyConfirmationAction = "shown" | "confirmed" | "dismissed";

/**
 * Tracks tab sharing do show message again event
 */
export type TabSharingDoShowMessageAgain = {
    /**
     * Source that triggered the event (e.g. frontend, backend)
     */
    event_source?: null | string;
    [property: string]: any;
}

/**
 * Tracks schedule upload completed event
 */
export type ScheduleUploadCompleted = {
    /**
     * Num Processed
     */
    num_processed?: number | null;
    /**
     * Processing Duration Ms
     */
    processing_duration_ms?: number | null;
    [property: string]: any;
}

/**
 * Tracks when context is linked to session or chat
 */
export type ContextLinked = {
    /**
     * ID of linked context
     */
    context_id?: null | string;
    /**
     * Type of context linked
     */
    context_type?: null | string;
    /**
     * Total linked contexts after action
     */
    linked_count?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * User identifier
     */
    user_id?: null | string;
    /**
     * Word count of linked context
     */
    word_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks when a form fill upload completes successfully
 */
export type FormFillCompleted = {
    /**
     * Form identifier
     */
    form_id?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an Evidence chat's share link is revoked by its owner
 */
export type EvidenceShareLinkRevoked = {
    /**
     * Chat identifier whose share link was revoked
     */
    chat_id?: null | string;
    /**
     * Surface the revocation was triggered from (evidence)
     */
    source?: EvidenceShareLinkRevokedSource | null;
}

export type EvidenceShareLinkRevokedSource = "evidence";

/**
 * Consult note generation failed for a session
 */
export type NoteGenerationFailed = {
    /**
     * 1-based generation attempt number for this session within the current app process
     */
    attempt_number?: number | null;
    /**
     * Platform identifier (android, ios, web)
     */
    client_platform?: null | string;
    /**
     * Chronicle device file name when applicable
     */
    device_file_name?: null | string;
    /**
     * Error message or code from client
     */
    error?: null | string;
    /**
     * Stable machine-readable failure code; never a raw exception string
     */
    error_code?: null | string;
    /**
     * Action that initiated note generation
     */
    generation_trigger?: GenerationTrigger | null;
    /**
     * Desktop-compatible alias for the local session identifier
     */
    local_session_id?: null | string;
    /**
     * Product platform
     */
    platform?: NoteGenerationCompletedPlatform | null;
    /**
     * Seconds since session creation when the event fired
     */
    session_age_seconds?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Mobile-compatible local session identifier
     */
    session_id_local?: null | string;
    /**
     * Analytics source tag (e.g. native)
     */
    source?: null | string;
    /**
     * Whether the error is user-facing (string or boolean depending on client platform)
     */
    user_facing?: boolean | null | string;
    /**
     * Whether note generation is expected to retry automatically
     */
    will_retry?: boolean | null;
}

/**
 * Tracks when an error is viewed during onboarding
 */
export type OnboardingErrorViewed = {
    /**
     * Type of onboarding error
     */
    error_type?: null | string;
    /**
     * Onboarding step where the error occurred
     */
    step?: null | string;
    [property: string]: any;
}

/**
 * Tracks the authoritative outcome of a linked-session mutation
 */
export type LinkedSessionsMutationCompleted = {
    /**
     * Resulting linked-session count, clamped to the product maximum
     */
    linked_count: number;
    /**
     * Definitive mutation outcome
     */
    outcome: AutoLinkSessionsPreferenceUpdateCompletedOutcome;
    /**
     * Bounded success or failure classification
     */
    reason: LinkedSessionsMutationCompletedReason;
    /**
     * Bounded source of the logical mutation attempt
     */
    source: LinkedSessionsMutationCompletedSource;
}

/**
 * Bounded success or failure classification
 */
export type LinkedSessionsMutationCompletedReason = "applied" | "no_change" | "validation_rejected" | "authorization_rejected" | "not_found" | "conflict" | "upstream_error" | "unknown";

/**
 * Bounded source of the logical mutation attempt
 */
export type LinkedSessionsMutationCompletedSource = "auto" | "manual_individual" | "manual_bulk" | "backend_patient_attach";

/**
 * Tracks when note is created
 */
export type NoteCreated = {
    /**
     * Originating Chronicle device filename when the session came from a Heidi Remote device
     * (device-file→note tracing); absent for mic/upload sessions
     */
    device_file_name?: null | string;
    /**
     * Experiment name
     */
    experiment_name?: null | string;
    /**
     * Whether this note re-ran generation over the same transcript rather than being the
     * session's first note. Null/absent on paths with no regenerate entry point.
     */
    is_regeneration?: boolean | null;
    /**
     * Whether the source template was sectional (had one or more sections)
     */
    is_sectional?: boolean | null;
    /**
     * Note identifier
     */
    note_id?: null | string;
    /**
     * Number of sections in the generated note
     */
    section_count?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Client-side local session identifier when available (joins hardware_* events on mobile)
     */
    session_id_local?: null | string;
    /**
     * Template identifier
     */
    template_id?: null | string;
    /**
     * Time to first token in milliseconds
     */
    time_to_first_token?: number | null;
    /**
     * Time to last token in milliseconds
     */
    time_to_last_token?: number | null;
    /**
     * Note type
     */
    type?: null | string;
    /**
     * Untyped experiment name from backend
     */
    untyped_experiment_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks sessions filter applied event
 */
export type SessionsFilterApplied = {
    /**
     * Date Range Days
     */
    date_range_days?: number | null;
    /**
     * Filter Type
     */
    filter_type?: null | string;
    /**
     * Preset Used
     */
    preset_used?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a call recording is successfully processed
 */
export type CallRecordingProcessed = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Recording duration in seconds
     */
    duration_seconds: number;
    /**
     * Detected language of the recording
     */
    language?: null | string;
    /**
     * Number of words in the transcript
     */
    transcript_word_count: number;
    [property: string]: any;
}

/**
 * Tracks when transcription begins
 */
export type TranscriptionStarted = {
    /**
     * APP-7915/APP-7962: desktop speaker capture mode at recording start (one of: system_wide,
     * default_endpoint_loopback, device_loopback, process_include). Shares one enum definition
     * with capture_summary's initial_/final_capture_mode — dashboards join on it. Null on web.
     */
    capture_mode?: null | string;
    /**
     * GRO-3186: "shortened" when the event fired inside the shortened-demo flow. Already
     * emitted by the frontend; added here so it lands in the warehouse instead of being dropped
     * as an unknown column.
     */
    demo_mode?: null | string;
    /**
     * APP-7915/APP-7962: how the desktop mic selection resolved (one of: explicit,
     * console_default, comms_default). Null on web.
     */
    input_default_kind?: null | string;
    /**
     * Input device name
     */
    input_device?: null | string;
    /**
     * Input device identifier
     */
    input_device_id?: null | string;
    /**
     * Integration type if triggered from an integration
     */
    integration_type?: null | string;
    /**
     * Whether recording v2 is enabled
     */
    is_recording_v2_enabled?: boolean | null;
    /**
     * Whether this is resuming a previous recording
     */
    is_resume?: boolean | null;
    /**
     * Whether tab sharing is active
     */
    is_tab_sharing?: boolean | null;
    /**
     * Platform for mobile integrations
     */
    platform?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Transcription mode
     */
    transcribe_mode?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an Evidence share link is copied to clipboard
 */
export type EvidenceShareLinkCopied = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when onboarding step is completed
 */
export type OnboardingStepCompleted = {
    /**
     * Dictate onboarding permissions step — accessibility grant state at completion
     */
    accessibility_granted?: boolean | null;
    /**
     * Dictate onboarding sign-in step — auth provider chosen before redirect (e.g. email,
     * google)
     */
    auth_method?: null | string;
    /**
     * Authentication provider used
     */
    authentication_provider?: null | string;
    /**
     * Milliseconds spent on the completed step before advancing
     */
    duration_ms?: number | null;
    /**
     * EHR provider
     */
    ehr_provider?: null | string;
    /**
     * Onboarding flow type
     */
    flow?: null | string;
    /**
     * Dictate onboarding key-confirm step — whether the hotkey was confirmed
     */
    hotkey_confirmed?: boolean | null;
    /**
     * Dictate onboarding key-selection step — whether the user chose a non-default hotkey
     */
    hotkey_custom?: boolean | null;
    /**
     * Dictate onboarding languages step — number of languages selected
     */
    languages_count?: number | null;
    /**
     * Dictate onboarding permissions step — microphone grant state at completion
     */
    mic_granted?: boolean | null;
    /**
     * Nested arm inside onboarding_variant experimental-2 (guided_tour | sample_conversation).
     * Absent outside the guided-tour eligible cohort, so its presence also marks cohort
     * membership.
     */
    onboarding_sub_variant?: null | string;
    /**
     * A/B variant for onboarding-v2 flows (e.g. control, experimental, experimental-2).
     * Previously arrived undeclared via additionalProperties.
     */
    onboarding_variant?: null | string;
    /**
     * Organisation role
     */
    organisation_role?: null | string;
    /**
     * Organisation size
     */
    organisation_size?: null | string;
    /**
     * True when the phone number entered on a signup step (role / about-you / sign-up form)
     * passed validation at step completion; false when entered but invalid; absent when the
     * step has no phone field. Supports the release-phone-number-in-signup-* experiment.
     */
    phone_number_valid?: boolean | null;
    /**
     * Onboarding funnel identifier (e.g. dictate_onboarding, onboarding_v2). Disambiguates
     * parallel onboarding flows sharing this event.
     */
    source?: null | string;
    /**
     * Name of the onboarding step
     */
    step?: null | string;
    /**
     * One-based index of the completed step in the visible wizard order
     */
    step_number?: number | null;
    [property: string]: any;
}

/**
 * Tracks note submitted event
 */
export type NoteSubmitted = {
    /**
     * Ehr Provider
     */
    ehr_provider?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Submission Action
     */
    submission_action?: null | string;
    [property: string]: any;
}

/**
 * Tracks sof launch event
 */
export type SofLaunch = {
    /**
     * Action
     */
    action?: null | string;
    /**
     * Ehr Provider
     */
    ehr_provider?: null | string;
    /**
     * Profile Name
     */
    profile_name?: null | string;
    /**
     * With Sdes
     */
    with_sdes?: boolean | null;
    [property: string]: any;
}

/**
 * Fired when a workspace successfully opts into Heidi 2.0 and the complimentary usage
 * period starts
 */
export type Heidi2_Activated = {
    /**
     * ISO 8601 end of the complimentary opt-in cycle
     */
    complimentary_period_end_at?: null | string;
    /**
     * Billing product type after Heidi 2.0 opt-in (e.g. FREE, CLINICIAN_STANDARD,
     * PRACTICE_STANDARD, SCRIBE_PLUS_STANDARD). Heidi 2 remapped SKUs, so this vocab differs
     * from previous_subscription_tier on purpose.
     */
    current_subscription_tier?: null | string;
    /**
     * Billing product type before Heidi 2.0 opt-in (e.g. FREE, CLINICIAN, CLINICIAN_PLUS,
     * SCRIBE, SCRIBE_PLUS). Legacy SKU names, not subscription_event.plan_tier /
     * user_context.effective_tier.
     */
    previous_subscription_tier?: null | string;
    /**
     * Workspace that opted into Heidi 2.0
     */
    workspace_id?: null | string;
}

/**
 * Tracks when a panel is closed
 */
export type PanelClosed = {
    /**
     * Number of active source filters
     */
    active_filters_count?: number | null;
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Feature context
     */
    feature?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Mode context
     */
    mode?: null | string;
    /**
     * Type of panel being opened or closed
     */
    panel_type?: null | string;
    /**
     * Number of references
     */
    references_count?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * What triggered the action (manual, auto)
     */
    trigger?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user clicks a task notification, capturing the notification type
 */
export type TaskNotificationClicked = {
    /**
     * Type of notification clicked. Known values: assigned, due_soon, overdue
     */
    notification_type?: null | string;
    /**
     * Associated session ID if task belongs to a session
     */
    session_id?: null | string;
    /**
     * Unique identifier of the task the notification relates to
     */
    task_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks user updated event
 */
export type UserUpdated = {
    /**
     * Country
     */
    country?: null | string;
    /**
     * Update Action
     */
    update_action?: null | string;
    [property: string]: any;
}

/**
 * Tracks when user submits feedback
 */
export type FeedbackSubmitted = {
    /**
     * Additional thumbs feedback value
     */
    additional_thumbs_feedback?: null | string;
    /**
     * Document name context
     */
    document_name?: null | string;
    /**
     * Ease of use star rating
     */
    ease_stars?: number | null;
    /**
     * Enabled code sets
     */
    enabled_codesets?: string[] | null;
    /**
     * Feedback text for rating type
     */
    feedback?: null | string;
    /**
     * Feedback count
     */
    feedback_count?: number | null;
    /**
     * Feedback rating reasons
     */
    feedback_rating_reasons?: string[] | null;
    /**
     * Tags associated with the feedback
     */
    feedback_tags?: null | string;
    /**
     * Whether feedback text was provided
     */
    has_feedback_text?: boolean | null;
    /**
     * Note category context
     */
    note_category?: null | string;
    /**
     * Note identifier
     */
    note_id?: null | string;
    /**
     * Rating value
     */
    rating?: null | string;
    /**
     * Session ID
     */
    session_id?: null | string;
    /**
     * Feedback source
     */
    source?: null | string;
    /**
     * ID of target entity
     */
    target_id?: null | string;
    /**
     * What feedback is about
     */
    target_type?: null | string;
    /**
     * Timestamp of feedback
     */
    timestamp?: null | string;
    /**
     * Feedback type
     */
    type?: null | string;
    /**
     * Usefulness star rating
     */
    usefulness_stars?: number | null;
    /**
     * Feedback value (up/down for thumbs, text for qualitative, number for rating)
     */
    value?: number | null | string;
    [property: string]: any;
}

/**
 * Tracks when the user dismisses the dark-mode spotlight
 */
export type DarkModeSpotlightDismissed = {
    /**
     * How the user dismissed the spotlight
     */
    dismiss_method: DismissMethod;
}

/**
 * How the user dismissed the spotlight
 */
export type DismissMethod = "close_button" | "got_it_button" | "mode_selected" | "keyboard_escape" | "outside_click";

/**
 * Tracks clinical code clickable event
 */
export type ClinicalCodeClickable = {
    /**
     * Button State
     */
    button_state?: null | string;
    /**
     * Code Systems Enabled
     */
    code_systems_enabled?: string[] | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a universal dictation CSAT rating is submitted
 */
export type UniversalDictationCsatSubmitted = {
    /**
     * CSAT rating value
     */
    rating?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when sharing access level is changed for a session
 */
export type ShareAccessChanged = {
    /**
     * New access level granted
     */
    access_level?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Type of share
     */
    share_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the in-recording silent-capture warning is shown
 */
export type AudioWarningViewed = {
    /**
     * Whether the warning carried a one-click fix (vs. informational only)
     */
    actionable?: boolean | null;
    /**
     * Matched telehealth app for actionable speaker warnings (e.g. "AircallWorkspace"). Null
     * when no app was matched.
     */
    app_name?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Which capture channel is silent (one of: silent_speaker, silent_mic)
     */
    warning_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks integration widget auth step misc event
 */
export type IntegrationWidgetAuthStepMisc = {
    /**
     * Details
     */
    details?: { [key: string]: any } | null;
    /**
     * Integration Type
     */
    integration_type?: null | string;
    /**
     * Step
     */
    step?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a suggested fix from verification is applied
 */
export type VerificationSuggestedFixApplied = {
    /**
     * Document identifier
     */
    document_id?: null | string;
    /**
     * Type of suggested fix that was applied
     */
    fix_type?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks Use sections conversion of an existing consult template between flat and sectioned
 * content
 */
export type TemplateConverted = {
    /**
     * How far the converted content moved from the original once section markers are stripped:
     * Levenshtein distance over the longer string, so 0 = hashes only and 1 = wholly rewritten.
     * The producer rejects a conversion above 0.2. Sent on succeeded with outcome converted,
     * and on failed with failure_reason guard, so the accepted and rejected distributions can
     * both be read and the threshold tuned from data. Omit on restored, flattened, started,
     * cancelled and retried.
     */
    content_change_ratio?: number | null;
    /**
     * Correlates every row of one toggle attempt: started, its terminal row (succeeded |
     * cancelled | failed), and any retried row that follows. A UUID minted when the clinician
     * flips the switch; a retry keeps the same id because it is the same attempt. Bounded at 64
     * to keep it a machine identifier — anything longer is a producer bug.
     */
    conversion_id?: null | string;
    /**
     * Lifecycle of one toggle job. started = the clinician flipped Use sections. succeeded =
     * content was applied. cancelled = the clinician navigated away or closed the editor while
     * the job was in flight. failed = the job aborted after a non-abort error. retried = the
     * clinician retried from the error toast. Always sent; omit the key never, never send null.
     */
    conversion_status?: ConversionStatus;
    /**
     * Wall-clock milliseconds the clinician waited, from the flip (or the retry) to this row.
     * The editor, Share and save are locked for that whole window. Omit on started.
     */
    duration_ms?: number | null;
    /**
     * Why the job failed. Bounded tokens only: the producer sends guard for the content
     * fingerprint, otherwise Error.name, otherwise unknown. Never send Error.message or any
     * user/clinical content. Do not enum this field — new error names must not Iglu-drop. Omit
     * the key unless conversion_status is failed (producer-enforced).
     */
    failure_reason?: null | string;
    /**
     * True only on succeeded convert-on when the one-time Use sections intro toast was
     * scheduled. Omit otherwise.
     */
    intro_shown?: boolean | null;
    /**
     * Intended layout after the toggle. sectioned = Use sections on. flat = Use sections off.
     * Always sent; never send null.
     */
    next_mode?: NextMode;
    /**
     * How succeeded content was produced. converted = LLM rewrite. restored = a prior sectioned
     * version was reused. flattened = hashes were stripped for convert-off. Omit the key on
     * started, failed, and retried — do not send null. Producer-enforced; Iglu does not
     * cross-validate this against conversion_status.
     */
    outcome?: TemplateConvertedOutcome;
    /**
     * Count of "# " section headings in the template structure after the converted content was
     * applied. With section_count_before this is how many sections the model actually produced.
     * Omit on started, cancelled and failed.
     */
    section_count_after?: number | null;
    /**
     * Count of "# " section headings in the template structure when the toggle was flipped.
     * Omit on started, cancelled and failed.
     */
    section_count_before?: number | null;
    /**
     * Where the toggle was flipped. template_editor = the legacy edit dialog.
     * template_details_pane = the right-hand details pane. Always sent by the producer; never
     * send null.
     */
    surface?: TemplateConvertedSurface;
    /**
     * Template creation record the conversion ran against. Omit when the editor has no record
     * id yet. Not a template name.
     */
    template_creation_record_id?: null | string;
    /**
     * Template the conversion ran against. This is the join key for "sections turned on and
     * then off again on the same template"; template_creation_record_id identifies the editing
     * record, not the template. Omit while the editor holds an unsaved template.
     */
    template_id?: null | string;
}

/**
 * Lifecycle of one toggle job. started = the clinician flipped Use sections. succeeded =
 * content was applied. cancelled = the clinician navigated away or closed the editor while
 * the job was in flight. failed = the job aborted after a non-abort error. retried = the
 * clinician retried from the error toast. Always sent; omit the key never, never send null.
 */
export type ConversionStatus = "started" | "succeeded" | "cancelled" | "failed" | "retried";

/**
 * Intended layout after the toggle. sectioned = Use sections on. flat = Use sections off.
 * Always sent; never send null.
 */
export type NextMode = "sectioned" | "flat";

/**
 * How succeeded content was produced. converted = LLM rewrite. restored = a prior sectioned
 * version was reused. flattened = hashes were stripped for convert-off. Omit the key on
 * started, failed, and retried — do not send null. Producer-enforced; Iglu does not
 * cross-validate this against conversion_status.
 */
export type TemplateConvertedOutcome = "converted" | "restored" | "flattened";

/**
 * Where the toggle was flipped. template_editor = the legacy edit dialog.
 * template_details_pane = the right-hand details pane. Always sent by the producer; never
 * send null.
 */
export type TemplateConvertedSurface = "template_editor" | "template_details_pane";

/**
 * Tracks when user initiates checkout process
 */
export type CheckoutStarted = {
    /**
     * Payment period selected
     */
    payment_period?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an outbound message is delivered
 */
export type OutboundMessageDelivered = {
    /**
     * Unique conversation identifier
     */
    conversation_id: string;
    /**
     * Time from queuing to delivery in milliseconds
     */
    delivery_latency_ms: number;
    /**
     * Unique message identifier
     */
    message_id: string;
    [property: string]: any;
}

/**
 * Tracks when a CPD draft is submitted
 */
export type CpdDraftSubmitted = {
    /**
     * Number of activities
     */
    activity_count?: number | null;
    /**
     * Draft identifier
     */
    draft_id?: null | string;
    /**
     * End date
     */
    end_date?: null | string;
    /**
     * Number of sources
     */
    source_count?: number | null;
    /**
     * Start date
     */
    start_date?: null | string;
    /**
     * Submission identifier
     */
    submission_id?: null | string;
    /**
     * Total minutes
     */
    total_minutes?: number | null;
    [property: string]: any;
}

/**
 * Tracks integration data completed event
 */
export type IntegrationDataCompleted = {
    /**
     * Data Type
     */
    data_type?: null | string;
    /**
     * Integration Type
     */
    integration_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a pre-push warning is shown before a note is pushed to the EHR
 */
export type PushWarningViewed = {
    /**
     * EHR system name the note is being pushed to
     */
    ehr_provider?: null | string;
    /**
     * Unlinked sections whose heading is not in the template at all
     */
    note_only_count?: number | null;
    /**
     * Note sections with content, linked or not
     */
    section_count?: number | null;
    /**
     * Opaque clinical-session identifier for the note being pushed
     */
    session_id?: null | string;
    /**
     * Unlinked sections the template maps but the note's own mapping copy does not
     */
    stale_note_copy_count?: number | null;
    /**
     * Unlinked sections whose heading is also unmapped in the template
     */
    template_gap_count?: number | null;
    /**
     * Template the note was generated from
     */
    template_id?: null | string;
    /**
     * Sections with content that have no valid EHR section link and will be dropped
     */
    unlinked_section_count?: number | null;
    /**
     * Opaque id joining this impression to its push_warning_resolved
     */
    warning_id: string;
    /**
     * Which pre-push warning was shown (unlinked_sections)
     */
    warning_type?: WarningType | null;
    [property: string]: any;
}

export type WarningType = "unlinked_sections";

/**
 * Tracks when a user dismisses the personalised source recommendation banner.
 * Negative-outcome leg of the source-recommendation funnel (shown -> dismissed | applied |
 * customised). `matched_*` fields reflect the BE-selected recommendation, not the user's
 * current profile.
 * 2-0-0: renamed `location` -> `banner_location` (MODEL bump). The 1-0-0 property name
 * caused snowtype to emit a top-level TypeScript `Location` alias that shadowed the DOM
 * `Location` global; the rename scopes the alias to the source-recommendation banner and
 * makes snowtype emit `BannerLocation` instead. No other shape changes vs 1-0-0.
 */
export type SourceRecommendationDismissed = {
    /**
     * Banner surface that produced the event. Known values: chat (in-chat banner above the
     * floating ask-Heidi input), popover (apply popover surfaced from source control), session
     * (banner inside an active session).
     */
    banner_location?: BannerLocation;
    /**
     * Human-readable name of the country whose tag drove the BE's regional ordering of the
     * recommendation (e.g. "Australia"). Empty string when unresolved.
     */
    matched_country?: null | string;
    /**
     * BE-matched specialty id used to score the recommendation. May fall back to a
     * generic-practitioner id when the user's profile has no specialty. Null when unresolved.
     */
    matched_specialty_id?: null | string;
    /**
     * Human-readable name of `matched_specialty_id`. Empty string during hydration. Use this
     * for analytics slicing.
     */
    matched_specialty_name?: null | string;
    /**
     * Number of recommendations offered in the banner at the moment of dismissal (pre-cap, BE
     * `total_domains`).
     */
    sources_count?: number | null;
}

/**
 * Tracks an offline recording entering the pending-sync queue after capture
 */
export type OfflineRecordingQueued = {
    /**
     * Residency region code used for the capture
     */
    capture_region: string;
    /**
     * Encrypted local audio file size in bytes
     */
    file_size_bytes: number;
    /**
     * Desktop-generated local session identifier
     */
    local_session_id: string;
    /**
     * Captured audio duration in milliseconds
     */
    recorded_duration_ms: number;
    /**
     * Recording source
     */
    source: OfflineSyncDeferredSource;
}

/**
 * Tracks when a hardware device is removed
 */
export type HardwareDeviceRemoved = {
    /**
     * Device identifier
     */
    device_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when referral link is copied
 */
export type ReferralLinkCopied = {
    /**
     * The referral link that was copied
     */
    referral_link?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user opens the "Check other sources" dropdown on an Evidence chat response
 * to re-run the same query against a different set of sources. chat_id/message_id attribute
 * the click to the specific response (and producing model) the user is unsatisfied with.
 */
export type EvidenceCheckOtherSourcesClicked = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Backend message ID (msg_*) of the assistant response whose "Check other sources" control
     * was clicked. Joins the click to the specific model that produced the response.
     */
    message_id?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier when the click happened inside a scribe session
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks chat context add session clicked event
 */
export type ChatContextAddSessionClicked = {
    /**
     * Chat Id
     */
    chat_id?: null | string;
    /**
     * Is Mobile
     */
    is_mobile?: boolean | null;
    [property: string]: any;
}

/**
 * Tracks note displayed event
 */
export type NoteDisplayed = {
    /**
     * Candidate Note Ids
     */
    candidate_note_ids?: string[] | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks snippet uploaded event
 */
export type SnippetUploaded = {
    /**
     * Count
     */
    count?: number | null;
    /**
     * Source
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks team member created event
 */
export type TeamMemberCreated = {
    /**
     * Creation Action
     */
    creation_action?: null | string;
    /**
     * Email
     */
    email?: null | string;
    /**
     * Source
     */
    source?: null | string;
    /**
     * Team Id
     */
    team_id?: null | string;
    /**
     * Team Link
     */
    team_link?: null | string;
    /**
     * Team Name
     */
    team_name?: null | string;
    /**
     * Total Email Count
     */
    total_email_count?: number | null;
    [property: string]: any;
}

/**
 * Fired when the user begins editing a note
 */
export type NoteEditStarted = {
    /**
     * Platform identifier (android, ios, web)
     */
    client_platform?: null | string;
    /**
     * Web only. Editor variant the edit happened in (json, markdown_native, block_v1,
     * block_markdown, rich_text, textarea). Free-form string rather than an enum so
     * editor-rollout variants can be added without a schema bump — matches
     * note_editor_rendered. Null on mobile.
     */
    editor_type?: null | string;
    /**
     * Length of the note in characters at the time editing started
     */
    note_length?: number | null;
    /**
     * Seconds since session creation when the event fired
     */
    session_age_seconds?: number | null;
    /**
     * Session identifier
     */
    session_id?: null | string;
}

/**
 * Voice to Work handoff completed
 */
export type WorkVoiceHandoffCompleted = {
    /**
     * completed
     */
    action: WorkToolCompletedAction;
    /**
     * Durable agent-harness run id
     */
    agent_run_id?: null | string;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, or research_terms
     */
    entry_point?: null | string;
    /**
     * Terminal handoff outcome
     */
    outcome: WorkToolCompletedOutcome;
    /**
     * Project id when applicable
     */
    project_id?: null | string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Scribe session id when ambient
     */
    session_id?: null | string;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * Handoff task text length
     */
    task_length?: number | null;
    /**
     * Voice session id
     */
    voice_session_id: string;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
}

/**
 * Tracks referral shared event
 */
export type ReferralShared = {
    /**
     * Referral Link
     */
    referral_link?: null | string;
    [property: string]: any;
}

/**
 * Agent run or turn finished (success, failure, or user cancel), including tools, query
 * length, approval state, and agentic use
 */
export type WorkRunCompleted = {
    /**
     * completed or aborted
     */
    action?: WorkRunCompletedAction | null;
    /**
     * Durable agent-harness run id
     */
    agent_run_id?: null | string;
    /**
     * Approval mode snapshot: manual (approval required) or auto (approve for me)
     */
    approval_mode?: DownloadTrigger | null;
    /**
     * Artifacts produced by this run (not Evidence references_count)
     */
    artifact_count?: number | null;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * Account SKU string (e.g. pro, pro_plus, clinician_pro). Not an enum; SKUs change.
     */
    current_plan?: null | string;
    /**
     * This user's seat type (assistant, standard, or max)
     */
    current_seat_type?: TSeatType | null;
    /**
     * Duration in milliseconds
     */
    duration_ms?: number | null;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, or research_terms
     */
    entry_point?: null | string;
    /**
     * Safe error code when outcome is failed
     */
    error_code?: null | string;
    /**
     * Whether at least one human approval was required
     */
    had_approval_request?: boolean | null;
    /**
     * Whether the completed run or turn executed a tool or delegated to a child agent;
     * independent of credits
     */
    is_agentic_use?: boolean | null;
    /**
     * run, turn, computer_use, or workflow
     */
    object_type?: WorkRunCompletedObjectType | null;
    /**
     * Terminal run outcome
     */
    outcome: WorkToolCompletedOutcome;
    /**
     * Project id when applicable
     */
    project_id?: null | string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * Character length of this turn's submitted message; raw query text is never included
     */
    query_length?: number | null;
    /**
     * Count of referenced objects on the matching submit
     */
    referenced_object_count?: number | null;
    /**
     * Object types referenced in the matching submit (known values: session, patient, file,
     * source, routine)
     */
    referenced_object_types?: string[] | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Response length in characters
     */
    response_length?: number | null;
    /**
     * Skills attached on the matching submit
     */
    skill_count?: number | null;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * Milliseconds to first user-visible stream event (not model-internal)
     */
    time_to_first_token_ms?: number | null;
    /**
     * Tools invoked during the turn
     */
    tool_call_count?: number | null;
    /**
     * Sorted distinct canonical tool names used during the completed run or turn; excludes tool
     * arguments and results
     */
    tools_used?: string[] | null;
    /**
     * Zero-based turn index within the run
     */
    turn_index?: number | null;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
}

export type WorkRunCompletedAction = "completed" | "aborted";

export type WorkRunCompletedObjectType = "run" | "turn" | "computer_use" | "workflow";

/**
 * Tracks template failed event
 */
export type TemplateFailed = {
    /**
     * Error Message
     */
    error_message?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Template Id
     */
    template_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks universal dictation completed event
 */
export type UniversalDictationCompleted = {
    /**
     * Client-minted dictation identifier; joins to transcription.dictation._id
     */
    dictation_id?: null | string;
    /**
     * Hold Duration Ms
     */
    hold_duration_ms?: number | null;
    /**
     * Triggered By
     */
    triggered_by?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a team admin updates Heidi 2 access. Emitted after a successful team switch,
 * per-member checkbox, or select-all toggle. change_type splits the team switch from member
 * toggles, which can share the same is_enabled and member_enabled_count. is_enabled is
 * access-on after the change, not the inverted heidi_ii_access_revoked entitlement write.
 * member_enabled_count is an aggregate snapshot.
 */
export type TeamHeidiIiAccessUpdated = {
    /**
     * Which control changed. master_toggle is the team Heidi 2 switch. members_updated is a
     * per-member checkbox or the select-all header. Null when the producer did not record which
     * control changed.
     */
    change_type?: TeamHeidiIiAccessUpdatedChangeType | null;
    /**
     * Whether Heidi 2 access is on after the change. True means the team switch is on, or the
     * member checkbox that just changed is checked. This is not the heidi_ii_access_revoked
     * entitlement flag, which is stored inverted.
     */
    is_enabled: boolean;
    /**
     * Number of members with Heidi 2 access after the change. Aggregate count only — never a
     * member identifier. Null when the producer could not compute the snapshot.
     */
    member_enabled_count?: number | null;
    /**
     * Team identifier
     */
    team_id: string;
}

export type TeamHeidiIiAccessUpdatedChangeType = "master_toggle" | "members_updated";

/**
 * Tracks when a call summary is successfully generated
 */
export type CallSummaryGenerated = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Number of completion tokens generated
     */
    completion_tokens?: number | null;
    /**
     * Summary generation latency in milliseconds
     */
    latency_ms?: number | null;
    /**
     * AI model used for summary generation
     */
    model: string;
    /**
     * Number of prompt tokens used
     */
    prompt_tokens?: number | null;
    /**
     * Length of the generated summary in characters
     */
    summary_length_chars: number;
    [property: string]: any;
}

/**
 * Tracks when a call summary email fails to send
 */
export type CallSummaryEmailFailed = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Category of the error
     */
    error_type: string;
    [property: string]: any;
}

/**
 * Tracks successful removal of a letter attachment
 */
export type LetterAttachmentRemoved = {
    /**
     * Number of attachments remaining on the letter
     */
    attachment_count: number;
    /**
     * Letter that contained the attachment
     */
    letter_id: string;
    /**
     * MIME type of the removed attachment
     */
    mime_type: MIMEType;
    /**
     * Session containing the letter
     */
    session_id: string;
}

/**
 * Tracks context file upload failed event
 */
export type ContextFileUploadFailed = {
    /**
     * Reason for upload failure
     */
    failure_reason?: null | string;
    /**
     * File Size Bytes
     */
    file_size_bytes?: number | null;
    /**
     * File Type
     */
    file_type?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Upload Source
     */
    upload_source?: null | string;
    [property: string]: any;
}

/**
 * Tracks referral completed event
 */
export type ReferralCompleted = {
    /**
     * Coupon Code
     */
    coupon_code?: null | string;
    /**
     * Referral Id
     */
    referral_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the Ask Heidi shortcuts menu is opened
 */
export type EvidenceShortcutsOpened = {
    /**
     * Whether the interaction happened before a note exists, including during transcription
     */
    is_pre_note_session?: boolean | null;
    /**
     * How the shortcuts menu opened (trigger | slash)
     */
    open_method?: null | string;
    /**
     * Number of shortcuts shown when the menu opened
     */
    shortcut_count?: number | null;
    /**
     * Which Ask Heidi surface the menu opened on (floating_bar | side_panel | chat_input)
     */
    surface?: null | string;
    [property: string]: any;
}

/**
 * Tracks template generation event
 */
export type TemplateGeneration = {
    /**
     * Src
     */
    src?: null | string;
    /**
     * Template Id
     */
    template_id?: null | string;
    /**
     * Whether the per-template Use sections setting is enabled (distinct from section_count)
     */
    use_sections?: boolean | null;
    [property: string]: any;
}

/**
 * Tracks voice style updated event
 */
export type VoiceStyleUpdated = {
    /**
     * Is Regenerate
     */
    is_regenerate?: boolean | null;
    /**
     * Old Voice Style
     */
    old_voice_style?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Tab Type
     */
    tab_type?: null | string;
    /**
     * Voice Style
     */
    voice_style?: null | string;
    [property: string]: any;
}

/**
 * Tracks when survey is submitted
 */
export type SurveySubmitted = {
    /**
     * Type of survey submitted
     */
    survey_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the share modal is opened
 */
export type ShareModalOpened = {
    /**
     * Entry point that triggered the share modal
     */
    entry_point?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the user expands the in-session Evidence prompts bar
 */
export type EvidencePromptsBarExpanded = {
    /**
     * Scribe session identifier
     */
    session_id?: null | string;
    /**
     * Session stage when the bar was expanded
     */
    session_stage?: SessionStage | null;
    /**
     * Number of suggestion chips visible after expansion
     */
    suggestions_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks when a user sees, clicks, or dismisses a follow-up suggestion under a Work reply
 */
export type WorkFollowUpActioned = {
    /**
     * Suggestion interaction (viewed, cta_clicked, or dismissed)
     */
    action: WorkUpgradeActionedAction;
    /**
     * Durable agent-harness run id of the reply the suggestion sits under
     */
    agent_run_id?: null | string;
    /**
     * Follow-up type from the backend generator: workflow_capture, workflow_update, action,
     * clarification, tool_suggestion, or general. Not an enum; the generator owns the list.
     */
    follow_up_type: string;
    /**
     * Project id
     */
    project_id?: null | string;
    /**
     * UI surface such as project_chat
     */
    surface?: null | string;
}

/**
 * Tracks when session is viewed
 */
export type SessionViewed = {
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks contact sales form submitted event
 */
export type ContactSalesFormSubmitted = {
    /**
     * Form Id
     */
    form_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks the send-to-patient CTA becoming visible (impression), on whichever surface
 * exposes it — see location
 */
export type PatientSummaryCtaShown = {
    /**
     * Document Id of the patient-summary document; join key to the send events
     */
    document_id?: null | string;
    /**
     * Patient-facing document type (visit summary | document). Bounded non-PII set
     */
    document_type?: null | string;
    /**
     * Surface the CTA rendered in (canvas_chip for the visible dynamic button; share_sheet for
     * the mobile session-detail share bottom sheet — an in-app Compose bottom sheet, not the OS
     * share sheet). Bounded non-PII set
     */
    location?: null | string;
    /**
     * Session Id the document belongs to; join key to the send events
     */
    session_id?: null | string;
    /**
     * Send-to-patient flow variant the impression belongs to (e.g. patient_m0_email_only).
     * Bounded non-PII set; null when unset
     */
    variant?: null | string;
    [property: string]: any;
}

/**
 * Workflow or routine lifecycle action (install, edit, run, share, schedule)
 */
export type WorkWorkflowActioned = {
    /**
     * Workflow lifecycle action
     */
    action: WorkWorkflowActionedAction;
    /**
     * Durable agent-harness run id
     */
    agent_run_id?: null | string;
    /**
     * Desktop client capabilities were attached (CUA-eligible run)
     */
    client_capabilities_present?: boolean | null;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, or research_terms
     */
    entry_point?: null | string;
    /**
     * Single event trigger type for event-rule add/remove actions
     */
    event_type?: null | string;
    /**
     * Comma-separated event trigger types when multiple apply
     */
    event_types?: null | string;
    /**
     * Routine has one or more event triggers configured
     */
    has_event_rules?: boolean | null;
    /**
     * Routine has a time-based schedule configured at create or configure time
     */
    has_schedule?: boolean | null;
    /**
     * gallery, connector, make_it_your_own, or builder
     */
    install_source?: null | string;
    /**
     * workflow, preset, or schedule
     */
    object_type?: WorkWorkflowActionedObjectType | null;
    /**
     * succeeded or failed
     */
    outcome?: TaskDelegatedOutcome | null;
    /**
     * Preset template id when installing from gallery or connector
     */
    preset_id?: null | string;
    /**
     * Project id when applicable
     */
    project_id?: null | string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Workflow requires desktop app (UX flag on the routine)
     */
    requires_desktop?: boolean | null;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * UI disambiguation such as run_button, keyboard_shortcut, work_home_play, preset_try_now,
     * builder_auto_run
     */
    trigger_source?: null | string;
    /**
     * on_demand, schedule, event_rule, or keyboard for run actions
     */
    trigger_type?: null | string;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
    /**
     * Workflow id
     */
    workflow_id?: null | string;
}

/**
 * Workflow lifecycle action
 */
export type WorkWorkflowActionedAction = "installed" | "uninstalled" | "edited" | "run_clicked" | "created" | "shared" | "schedule_created" | "schedule_updated" | "schedule_deleted" | "schedule_paused" | "schedule_resumed" | "shortcut_set" | "shortcut_cleared" | "deleted" | "enabled" | "disabled" | "unshared" | "event_rule_added" | "event_rule_deleted" | "event_rule_replaced";

export type WorkWorkflowActionedObjectType = "workflow" | "preset" | "schedule";

/**
 * Tracks when a community template request is rejected
 */
export type CommunityRejected = {
    /**
     * Template identifier
     */
    template_id?: null | string;
    /**
     * Template name
     */
    template_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks successful payment completion
 */
export type PaymentCompleted = {
    /**
     * Source of the payment event
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a trial period ends
 */
export type TrialCompleted = {
    /**
     * How the trial ended (converted, ended, cancelled)
     */
    completion_type?: null | string;
    /**
     * Whether the trial converted to a paid subscription
     */
    converted_to_paid?: boolean | null;
    /**
     * Product the trial was for (e.g. evidence, scribe)
     */
    feature?: null | string;
    /**
     * Trial plan type
     */
    plan_type?: null | string;
    /**
     * Trial identifier
     */
    trial_id?: null | string;
    /**
     * Trial plan name (e.g. clinician_pro, evidence_pro)
     */
    trial_plan?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a conversation is assigned
 */
export type ConversationAssigned = {
    /**
     * Identifier of the assignee
     */
    assignee_id: string;
    /**
     * Type of assignee (e.g. user, team, bot)
     */
    assignee_type: string;
    /**
     * Unique conversation identifier
     */
    conversation_id: string;
    [property: string]: any;
}

/**
 * Tracks when feedback is submitted for a verification result
 */
export type VerificationFeedbackSubmitted = {
    /**
     * Document identifier
     */
    document_id?: null | string;
    /**
     * Type of feedback submitted
     */
    feedback_type?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks patient context toggle on event
 */
export type PatientContextToggleOn = {
    /**
     * Patient Id
     */
    patient_id?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a user clicks an entry point to the Comms feature
 */
export type CommsEntryPointClicked = {
    /**
     * Entry point identifier
     */
    entry_point: string;
    /**
     * Whether calls are enabled for the user
     */
    is_calls_enabled: boolean;
    [property: string]: any;
}

/**
 * Tracks when a user clicks an inline <heidi-button> CTA in an Evidence chat response.
 * chat_id/message_id attribute the CTA click (an engagement signal) to the specific model
 * that produced the response.
 */
export type EvidenceChatCtaClicked = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Coarse classification of the destination, stable for dashboard grouping
     */
    destination?: Destination | null;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Resolved absolute URL navigated to (after FE resolution + allowlist check)
     */
    href?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Visible button text the LLM emitted between the tag's opening/closing tokens
     */
    label?: null | string;
    /**
     * Backend message ID (msg_*) of the assistant response whose CTA was clicked. Joins the CTA
     * engagement to the specific model that produced the response.
     */
    message_id?: null | string;
    /**
     * window.location.host at click time (splits prod vs dev vs registrar)
     */
    origin_host?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier when the click happened inside a scribe session
     */
    session_id?: null | string;
    [property: string]: any;
}

export type Destination = "billing" | "pricing" | "account" | "evidence" | "templates" | "library" | "cpd" | "marketing" | "other";

/**
 * Tracks cpd draft created event
 */
export type CpdDraftCreated = {
    /**
     * Date Preset
     */
    date_preset?: null | string;
    /**
     * Draft Id
     */
    draft_id?: null | string;
    /**
     * End Date
     */
    end_date?: null | string;
    /**
     * Start Date
     */
    start_date?: null | string;
    [property: string]: any;
}

/**
 * Tracks context deleted event
 */
export type ContextDeleted = {
    /**
     * File Count
     */
    file_count?: number | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when AI response is copied
 */
export type ResponseCopied = {
    /**
     * Feature where response was copied
     */
    feature?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a member is added to a share
 */
export type ShareMemberAdded = {
    /**
     * Role assigned to the added member
     */
    member_role?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an AI objective triggers a safety guardrail
 */
export type ObjectiveGuardrailTriggered = {
    /**
     * Action taken by the guardrail
     */
    action: string;
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Additional details about the guardrail trigger
     */
    details?: null | string;
    /**
     * Name of the guardrail that was triggered
     */
    guardrail_name: string;
    /**
     * Unique objective identifier
     */
    objective_id: string;
    [property: string]: any;
}

/**
 * Tracks book demo form submitted event
 */
export type BookDemoFormSubmitted = {
    /**
     * Country Code
     */
    country_code?: null | string;
    /**
     * Country Name
     */
    country_name?: null | string;
    /**
     * Organisation Size
     */
    organisation_size?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the Evidence sign-up wall is viewed
 */
export type EvidenceSignUpWallViewed = {
    /**
     * Entry point where the sign-up wall was shown
     */
    entry_point?: null | string;
    /**
     * Feature that triggered the sign-up wall
     */
    feature?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a document is created through various methods in the application
 */
export type DocumentCreated = {
    /**
     * Unique document identifier
     */
    document_id?: null | string;
    /**
     * How document was generated
     */
    generation_method?: null | string;
    /**
     * Whether the template used is pinned
     */
    is_pinned_template?: boolean | null;
    /**
     * Whether this is a regeneration of an existing document
     */
    is_regeneration?: boolean | null;
    /**
     * Associated session ID
     */
    session_id?: null | string;
    /**
     * Template ID used (if applicable)
     */
    template_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks integration user searched event
 */
export type IntegrationUserSearched = {
    /**
     * First Name
     */
    first_name?: null | string;
    /**
     * Guid
     */
    guid?: null | string;
    /**
     * Integration Type
     */
    integration_type?: null | string;
    /**
     * Last Name
     */
    last_name?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an outbound message fails to deliver
 */
export type OutboundMessageFailed = {
    /**
     * Unique conversation identifier
     */
    conversation_id: string;
    /**
     * Error code from the messaging provider
     */
    error_code: string;
    /**
     * Human-readable error description
     */
    error_description?: null | string;
    /**
     * Category of the delivery error
     */
    error_type: string;
    /**
     * Unique message identifier
     */
    message_id: string;
    [property: string]: any;
}

/**
 * Tracks when a share link is copied to clipboard
 */
export type ShareLinkCopied = {
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Type of share
     */
    share_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an inbound call is routed to a handler
 */
export type InboundCallRouted = {
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Objective assigned to handle the call
     */
    objective_id?: null | string;
    /**
     * Target the call was routed to
     */
    route_target: string;
    [property: string]: any;
}

/**
 * Tracks when user signs out
 */
export type UserSignedOut = {
    /**
     * Screen section where sign out occurred
     */
    screen_section?: null | string;
    [property: string]: any;
}

/**
 * Tracks force update viewed event
 */
export type ForceUpdateViewed = {
    /**
     * Current Version
     */
    current_version?: null | string;
    /**
     * Deadline At
     */
    deadline_at?: null | string;
    /**
     * Min Supported Version
     */
    min_supported_version?: null | string;
    /**
     * Reason
     */
    reason?: null | string;
    /**
     * Update Type
     */
    update_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when patient documents are unlinked from a session's context set
 */
export type PatientDocumentUnlinked = {
    /**
     * Identifiers of the documents unlinked in this save action
     */
    document_ids?: string[];
    /**
     * Feature-flag exposure cohort at the moment of the event. Known values: treated, control,
     * unknown.
     */
    feature_flag_variant?: null | string;
    /**
     * Patient identifier
     */
    patient_id?: null | string;
    /**
     * Identifier of the session the documents were removed from
     */
    session_id?: null | string;
    /**
     * UI surface that produced the event. Known values: in_session.
     */
    surface?: null | string;
    /**
     * Number of documents removed in this save action. Equals len(document_ids); duplicated for
     * cheap aggregation.
     */
    unlinked_count?: number | null;
}

/**
 * Tracks when a template is deleted
 */
export type TemplateDeleted = {
    /**
     * Type of delete action
     */
    delete_action?: null | string;
    /**
     * Deletion timestamp
     */
    deleted_at?: null | string;
    /**
     * Organisation identifier
     */
    organization_id?: null | string;
    /**
     * Shared team identifiers
     */
    shared_team_ids?: string[] | null;
    /**
     * Team identifier
     */
    team_id?: null | string;
    /**
     * Template identifier
     */
    template_id?: null | string;
    /**
     * Template name
     */
    template_name?: null | string;
    /**
     * Template visibility setting
     */
    template_visibility?: null | string;
    [property: string]: any;
}

/**
 * Tracks consult note generation events including success and failure outcomes
 */
export type ConsultNoteGeneration = {
    /**
     * Shortened-demo generation mode when applicable
     */
    demo_mode?: DemoMode | null;
    /**
     * Duration of note generation in seconds
     */
    duration_seconds?: number | null;
    /**
     * Reason for generation failure
     */
    failure_reason?: null | string;
    /**
     * Linked-session count actually supplied to generation
     */
    linked_count?: number | null;
    /**
     * Note identifier
     */
    note_id?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Provenance of the linked-session context supplied to generation
     */
    source?: ConsultNoteGenerationSource | null;
    /**
     * Generation status (success or failed)
     */
    status?: null | string;
    [property: string]: any;
}

export type DemoMode = "shortened";

export type ConsultNoteGenerationSource = "auto" | "manual_individual" | "manual_bulk" | "backend_patient_attach" | "mixed" | "none";

/**
 * Tracks when an evidence toast notification is shown
 */
export type EvidenceToastShown = {
    /**
     * Chat identifier that completed
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * User region
     */
    region?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a toast notification is displayed
 */
export type ToastViewed = {
    /**
     * Client platform
     */
    client_platform?: null | string;
    /**
     * Toast message
     */
    message?: null | string;
    /**
     * Type of toast notification
     */
    toast_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks community viewed event
 */
export type CommunityViewed = {
    /**
     * Template Id
     */
    template_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an error is viewed by the user
 */
export type ErrorViewed = {
    /**
     * Desktop IPC only - which recorder raised the error. Closed set; the producer maps any
     * other value to unrecognised
     */
    capture_origin?: CaptureOrigin | null;
    /**
     * Machine-readable error code (desktop IPC - the Rust error_code, or absent / unrecognised)
     */
    error_code?: null | string;
    /**
     * Error message displayed to the user
     */
    error_message?: null | string;
    /**
     * Type of error displayed
     */
    error_type?: null | string;
    /**
     * Desktop IPC only - character length of the native error message; the text itself is never
     * sent
     */
    message_length?: number | null;
    /**
     * Screen where the error was viewed
     */
    screen_name?: null | string;
    [property: string]: any;
}

export type CaptureOrigin = "online" | "offline_recording" | "absent" | "unrecognised";

/**
 * Tracks integration completed event
 */
export type IntegrationCompleted = {
    /**
     * Completion Action
     */
    completion_action?: null | string;
    /**
     * Integration Type
     */
    integration_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an existing chat is viewed
 */
export type ChatViewed = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Location where chat was viewed
     */
    location?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Source of the chat event (evidence, ask_heidi)
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * Tracks when the Evidence share modal is opened
 */
export type EvidenceShareModalOpened = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a note is synced/pushed to an EHR
 */
export type NoteSynced = {
    /**
     * True when the clinician saw the unlinked-sections push warning earlier in this browser
     * session, whichever way they resolved it. Same definition as after_push_warning on
     * ehr_section_selected. Omitted otherwise.
     */
    after_push_warning?: boolean | null;
    /**
     * Additional sync details (JSON-encoded)
     */
    details?: null | string;
    /**
     * Type of integration
     */
    integration_type?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Sync action performed
     */
    sync_action?: null | string;
    /**
     * Sectional pushes from the markdown-editor cohort (Athena, AdvancedMD) only; absent on
     * other pushes. Sections with content that had no valid EHR section link when the push ran,
     * so the backend dropped them. 0 on a clean push, so the warned cohort has a baseline to
     * compare against.
     */
    unlinked_section_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks library document added to collection event
 */
export type LibraryDocumentAddedToCollection = {
    /**
     * Add Context
     */
    add_context?: null | string;
    /**
     * Document Count
     */
    document_count?: number | null;
    [property: string]: any;
}

/**
 * Tracks when a new chat is created
 */
export type ChatCreated = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Location where chat was created
     */
    location?: null | string;
    /**
     * Patient profile identifier
     */
    patient_profile_id?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Source of the chat event (evidence, ask_heidi)
     */
    source?: null | string;
    [property: string]: any;
}

/**
 * A nudge card was dismissed
 */
export type NudgeCardDismissed = {
    /**
     * Version of the protocol catalogue that produced this card
     */
    catalogue_version: string;
    /**
     * Classifier confidence for the match (0-1)
     */
    confidence: number;
    /**
     * Join key back to the PHI-safe detection context persisted on NudgeCardState. Null when no
     * detection pass produced the card
     */
    detection_id?: null | string;
    /**
     * Optional reason the card was dismissed. Only set on dismissals
     */
    dismiss_reason?: null | string;
    /**
     * Number of matched signals (length of matched_signals)
     */
    matched_signal_count?: number | null;
    /**
     * Named signals (chips) the classifier matched. PHI-free labels only
     */
    matched_signals?: string[];
    /**
     * Identifier of the model used by the classifier
     */
    model_id?: null | string;
    /**
     * Version of the classifier prompt, for slicing false-positive rates by classifier change
     */
    prompt_version?: null | string;
    /**
     * Identifier of the protocol that was dismissed
     */
    protocol_id: string;
    /**
     * Heidi session the nudge card belongs to
     */
    session_id: string;
    /**
     * How the card came to exist. Known values: auto (classifier-surfaced), manual (clinician
     * added from the protocol library)
     */
    source?: DownloadTrigger;
    /**
     * Hash of the transcript chunk that drove the detection; a join key only, never the raw text
     */
    transcript_chunk_hash?: null | string;
}

/**
 * Tracks evidence verb clicked event
 */
export type EvidenceVerbClicked = {
    /**
     * Evidence Location
     */
    evidence_location?: null | string;
    /**
     * Is Evidence Pro
     */
    is_evidence_pro?: boolean | null;
    /**
     * Is Expanded
     */
    is_expanded?: boolean | null;
    /**
     * Is Managed
     */
    is_managed?: boolean | null;
    /**
     * Is Team Member
     */
    is_team_member?: boolean | null;
    /**
     * Region
     */
    region?: null | string;
    /**
     * Verb
     */
    verb?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a CPD activity is queued for processing
 */
export type CpdActivityStarted = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Number of citations
     */
    citation_count?: number | null;
    /**
     * Message identifier
     */
    message_id?: null | string;
    /**
     * Queue length
     */
    queue_length?: number | null;
    /**
     * Whether the queue was trimmed
     */
    queue_was_trimmed?: boolean | null;
    /**
     * Count of unique domains
     */
    unique_domain_count?: number | null;
    /**
     * List of unique domains
     */
    unique_domains?: string[] | null;
    [property: string]: any;
}

/**
 * Tracks when a hardware recording starts
 */
export type HardwareRecordingStart = {
    /**
     * Origin of the recording start action
     */
    origin?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Local session identifier
     */
    session_id_local?: null | string;
    [property: string]: any;
}

/**
 * Tracks document copied event
 */
export type DocumentCopied = {
    /**
     * Document Id
     */
    document_id?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks cpd draft resumed event
 */
export type CpdDraftResumed = {
    /**
     * Draft Id
     */
    draft_id?: null | string;
    /**
     * Removed Count
     */
    removed_count?: number | null;
    /**
     * Time Since Edit Seconds
     */
    time_since_edit_seconds?: number | null;
    [property: string]: any;
}

/**
 * Tracks patient extraction modal opened event
 */
export type PatientExtractionModalOpened = {
    /**
     * Patient Id
     */
    patient_id?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Timestamp
     */
    timestamp?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a compliance check is performed (NHS template or Ask Heidi)
 */
export type ComplianceCheck = {
    /**
     * The AI command text that was rejected (for NHSAskHeidi source)
     */
    ai_command_text?: null | string;
    /**
     * Character count of ai_command_text, sent in place of the text
     */
    ai_command_text_length?: number | null;
    /**
     * The noncompliant_input field from the compliance check response
     */
    compliance_output?: null | string;
    /**
     * Character count of compliance_output, sent in place of the text
     */
    compliance_output_length?: number | null;
    /**
     * The content/context provided with the request (for NHSAskHeidi source)
     */
    content?: null | string;
    /**
     * Character count of content, sent in place of the text
     */
    content_length?: number | null;
    /**
     * The session ID where the request was made (for NHSAskHeidi source)
     */
    session_id?: null | string;
    /**
     * Source of the compliance check
     */
    source?: null | string;
    /**
     * Status of the compliance check
     */
    status?: null | string;
    /**
     * The template content that was rejected (for NHSTemplate source)
     */
    template_content?: null | string;
    /**
     * Character count of template_content, sent in place of the text
     */
    template_content_length?: number | null;
    /**
     * ID of the template that was checked (for NHSTemplate source)
     */
    template_id?: null | string;
    /**
     * Source of the template (for NHSTemplate source)
     */
    template_source?: null | string;
    [property: string]: any;
}

/**
 * Fired when the patient onboarding flow completes, is skipped, or was not required
 */
export type TranslatorOnboardingCompleted = {
    /**
     * Time spent in the onboarding flow in milliseconds. Null for not_required outcomes.
     */
    duration_ms?: number | null;
    /**
     * How the onboarding flow ended. completed = user went through onboarding and finished all
     * steps. skipped = onboarding started (clinician chose "New patient", component mounted)
     * but user skipped/dismissed steps before finishing. not_required = clinician chose
     * "Existing patient" — onboarding was never shown (fired at session creation time).
     */
    outcome: TranslatorOnboardingCompletedOutcome;
    /**
     * Translator session identifier
     */
    session_id: string;
}

/**
 * How the onboarding flow ended. completed = user went through onboarding and finished all
 * steps. skipped = onboarding started (clinician chose "New patient", component mounted)
 * but user skipped/dismissed steps before finishing. not_required = clinician chose
 * "Existing patient" — onboarding was never shown (fired at session creation time).
 */
export type TranslatorOnboardingCompletedOutcome = "completed" | "skipped" | "not_required";

/**
 * Tracks when a user starts a paid subscription
 */
export type SubscriptionStarted = {
    /**
     * Billing interval (month or year)
     */
    billing_interval?: null | string;
    /**
     * Stripe coupon ID applied to the invoice, if any
     */
    coupon_id?: null | string;
    /**
     * Product the subscription is for (e.g. scribe, evidence)
     */
    feature?: null | string;
    /**
     * The subscription plan name (e.g. pro, pro_plus, clinician_pro)
     */
    plan?: null | string;
    /**
     * Stripe promotion code ID redeemed for the coupon, if any
     */
    promotion_code?: null | string;
    /**
     * Number of seats on the billing account
     */
    seat_count?: number | null;
    /**
     * Billing seat type matching BillingSeatType (ASSISTANT, STANDARD, or MAX)
     */
    seat_type?: PreviousSeatTypeEnum | null;
    /**
     * Billing account / team id (opaque)
     */
    team_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when integration is successfully connected
 */
export type IntegrationLinked = {
    /**
     * EHR provider that was linked
     */
    ehr_provider?: null | string;
    [property: string]: any;
}

/**
 * Tracks the clinician's decision on a pre-push warning
 */
export type PushWarningResolved = {
    /**
     * EHR system name the note is being pushed to
     */
    ehr_provider?: null | string;
    /**
     * Unlinked sections whose heading is not in the template at all
     */
    note_only_count?: number | null;
    /**
     * What the clinician chose (pushed_anyway | cancelled)
     */
    outcome?: PushWarningResolvedOutcome | null;
    /**
     * Note sections with content, linked or not
     */
    section_count?: number | null;
    /**
     * Opaque clinical-session identifier for the note being pushed
     */
    session_id?: null | string;
    /**
     * Unlinked sections the template maps but the note's own mapping copy does not
     */
    stale_note_copy_count?: number | null;
    /**
     * Unlinked sections whose heading is also unmapped in the template
     */
    template_gap_count?: number | null;
    /**
     * Template the note was generated from
     */
    template_id?: null | string;
    /**
     * Sections with content that have no valid EHR section link and will be dropped
     */
    unlinked_section_count?: number | null;
    /**
     * Opaque id joining this decision to its push_warning_viewed
     */
    warning_id: string;
    /**
     * Which pre-push warning was resolved (unlinked_sections)
     */
    warning_type?: WarningType | null;
    [property: string]: any;
}

export type PushWarningResolvedOutcome = "pushed_anyway" | "cancelled";

/**
 * Tracks when a user aborts an evidence chat response
 */
export type EvidenceChatAborted = {
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Time until user aborted in milliseconds
     */
    duration_ms?: number | null;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Backend message ID (msg_*) of the assistant response being aborted. Joins the abort (a
     * friction signal) to the specific model that produced the in-flight response.
     */
    message_id?: null | string;
    /**
     * Query length in characters
     */
    query_length?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks session search usage baseline event
 */
export type SessionSearchUsageBaseline = {
    /**
     * Query Length
     */
    query_length?: number | null;
    /**
     * Result Count
     */
    result_count?: number | null;
    /**
     * Total Sessions Loaded
     */
    total_sessions_loaded?: number | null;
    [property: string]: any;
}

/**
 * Tracks when a panel is opened
 */
export type PanelOpened = {
    /**
     * Number of active source filters
     */
    active_filters_count?: number | null;
    /**
     * Chat identifier
     */
    chat_id?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Feature context
     */
    feature?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Mode context
     */
    mode?: null | string;
    /**
     * Type of panel being opened or closed
     */
    panel_type?: null | string;
    /**
     * Number of references
     */
    references_count?: number | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * What triggered the action (manual, auto)
     */
    trigger?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a document verification is started
 */
export type VerificationStarted = {
    /**
     * Document identifier
     */
    document_id?: null | string;
    /**
     * Session identifier
     */
    session_id?: null | string;
    /**
     * Type of verification being performed
     */
    verification_type?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a resolved conversation is reopened
 */
export type ConversationReopened = {
    /**
     * Unique conversation identifier
     */
    conversation_id: string;
    /**
     * Identifier of the user or system that reopened the conversation
     */
    reopened_by: string;
    [property: string]: any;
}

/**
 * Tracks when a shared Evidence chat is opened by a recipient
 */
export type EvidenceSharedChatOpened = {
    /**
     * Whether the user opening the shared chat is authenticated
     */
    is_authenticated?: boolean | null;
    /**
     * Share token from the URL
     */
    share_token?: null | string;
    [property: string]: any;
}

/**
 * Fired when the App Store review sentiment gate is presented to the user, spending one
 * lifetime prompt slot
 */
export type AppStoreReviewPromptRequested = {
    /**
     * Number of automatic review prompts shown to this user to date
     */
    automatic_prompt_count?: number | null;
    /**
     * Platform identifier (android, ios, web)
     */
    client_platform?: null | string;
    /**
     * How the prompt was presented. Closed set — the two arms of the sentiment-gate A/B
     * experiment (AppStoreReviewPromptPresentation).
     */
    prompt_presentation?: PromptPresentation | null;
    /**
     * Feature that triggered the prompt. Namespaced rather than `source` because the top-level
     * `source` key is reserved for Amplitude EU routing. Closed set —
     * AppStoreReviewPromptSource on iOS.
     */
    review_source?: ReviewSource | null;
}

export type PromptPresentation = "sentiment_gate" | "direct_storekit";

/**
 * Tracks document generated event
 */
export type DocumentGenerated = {
    /**
     * Document Id
     */
    document_id?: null | string;
    /**
     * Generation Method
     */
    generation_method?: null | string;
    /**
     * Is Pinned Template
     */
    is_pinned_template?: boolean | null;
    /**
     * Is Regeneration
     */
    is_regeneration?: boolean | null;
    /**
     * Session Id
     */
    session_id?: null | string;
    /**
     * Template Id
     */
    template_id?: null | string;
    [property: string]: any;
}

/**
 * Tracks when a phone number is added to the do-not-contact list
 */
export type DoNotContactAdded = {
    /**
     * SHA-256 hash of the phone number
     */
    phone_hash: string;
    /**
     * Source of the do-not-contact request
     */
    source: string;
    [property: string]: any;
}

/**
 * Tracks when a team member requests access to Evidence Pro
 */
export type EvidenceRequestAccessClicked = {
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Team identifier of the user
     */
    team_id?: null | string;
    [property: string]: any;
}

/**
 * Fired when a user interacts with a translated message (edit, playback, remove)
 */
export type TranslatorMessageInteraction = {
    /**
     * The type of interaction performed on the message
     */
    action: TranslatorMessageInteractionAction;
    /**
     * Which fields were edited; only populated for message_edited action
     */
    edited_fields?: EditedField[] | null;
    /**
     * Translator message identifier
     */
    message_id: string;
    /**
     * Translator session identifier
     */
    session_id: string;
    /**
     * Source language code for the original message, e.g. en
     */
    source_language: string;
    /**
     * Who spoke the message
     */
    speaker: Speaker;
    /**
     * Identifier of the speech-to-text model used
     */
    stt_model?: null | string;
    /**
     * Target language code for the translated message, e.g. vi
     */
    target_language: string;
    /**
     * Identifier of the translation model used
     */
    translation_model?: null | string;
    /**
     * Identifier of the text-to-speech model used
     */
    tts_model?: null | string;
}

/**
 * The type of interaction performed on the message
 */
export type TranslatorMessageInteractionAction = "message_edited" | "audio_playback_clicked" | "message_removed";

/**
 * The name of a field that was edited
 */
export type EditedField = "original_text" | "translated_text";

/**
 * User feedback or copy action on a Work message
 */
export type WorkMessageActioned = {
    /**
     * Message interaction
     */
    action: WorkMessageActionedAction;
    /**
     * Durable agent-harness run id
     */
    agent_run_id?: null | string;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * Copied text length in characters
     */
    copy_length?: number | null;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, or research_terms
     */
    entry_point?: null | string;
    /**
     * positive or negative
     */
    feedback?: Sentiment | null;
    /**
     * Whether feedback included a comment
     */
    has_comment?: boolean | null;
    /**
     * assistant or user
     */
    message_role?: MessageRole | null;
    /**
     * Project id when applicable
     */
    project_id?: null | string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
}

/**
 * Message interaction
 */
export type WorkMessageActionedAction = "feedback_submitted" | "copied";

export type MessageRole = "assistant" | "user";

/**
 * Artifact canvas or panel action (created, open, edit, export, apply, close, tab switch,
 * verification)
 */
export type WorkArtifactActioned = {
    /**
     * opened, closed, edited, exported, applied, tab_switched, verification_started,
     * verification_completed, or created
     */
    action: WorkArtifactActionedAction;
    /**
     * Durable agent-harness run id
     */
    agent_run_id?: null | string;
    /**
     * Artifact kind when known
     */
    artifact_type?: null | string;
    /**
     * Desktop, Web, iOSApp, AndroidApp, etc.
     */
    client_platform?: null | string;
    /**
     * Account SKU string (e.g. pro, pro_plus, clinician_pro). Not an enum; SKUs change.
     */
    current_plan?: null | string;
    /**
     * This user's seat type (assistant, standard, or max)
     */
    current_seat_type?: TSeatType | null;
    /**
     * How the user arrived such as nav, deep_link, voice, workflow, or research_terms
     */
    entry_point?: null | string;
    /**
     * artifact
     */
    object_type?: null | string;
    /**
     * succeeded or failed
     */
    outcome?: TaskDelegatedOutcome | null;
    /**
     * Project id when applicable
     */
    project_id?: null | string;
    /**
     * in_session, out_of_session, or work_home
     */
    project_location?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * UI surface such as work_home, project_chat, in_session, workflow, voice, or sidebar
     */
    surface?: null | string;
    /**
     * Selected mode such as auto, work, or evidence
     */
    work_mode?: null | string;
}

/**
 * opened, closed, edited, exported, applied, tab_switched, verification_started,
 * verification_completed, or created
 */
export type WorkArtifactActionedAction = "opened" | "closed" | "edited" | "exported" | "applied" | "tab_switched" | "verification_started" | "verification_completed" | "created";

/**
 * Tracks when a user navigates between tabs in Haiku
 */
export type HaikuTabChanged = {
    /**
     * The tab the user navigated from
     */
    from_tab?: null | string;
    /**
     * Integration context
     */
    integration_type?: null | string;
    /**
     * Platform for mobile integrations
     */
    platform?: null | string;
    /**
     * The tab the user navigated to
     */
    to_tab?: null | string;
    [property: string]: any;
}

/**
 * Tracks the start of local audio capture for an offline desktop recording
 */
export type OfflineRecordingStarted = {
    /**
     * Residency region code used for the capture
     */
    capture_region: string;
    /**
     * Desktop-generated local session identifier
     */
    local_session_id: string;
    /**
     * Which branch of the offline microphone precedence opened the capture device (DSK-747).
     * start_selection = the selection current when the clinician pressed record, including an
     * explicit "System Default" pick; create_column = the microphone persisted on the session
     * row at create time, used when no start-time selection existed; os_default = neither
     * source had a value and the OS default input was used. Deliberately not the device name,
     * which embeds personal names.
     */
    microphone_source?: MicrophoneSource | null;
    /**
     * Product platform; desktop for this event
     */
    platform: OfflineRecordingCreatedPlatform;
    /**
     * Recording source
     */
    source: OfflineSyncDeferredSource;
}

export type MicrophoneSource = "start_selection" | "create_column" | "os_default";

/**
 * Tracks when an inbound message is received
 */
export type InboundMessageReceived = {
    /**
     * Unique conversation identifier
     */
    conversation_id: string;
    /**
     * SHA-256 hash of the sender phone number
     */
    from_phone_hash: string;
    /**
     * Whether the message includes an attachment
     */
    has_attachment: boolean;
    /**
     * Whether this message started a new conversation
     */
    is_new_conversation?: boolean | null;
    /**
     * Length of the message body in characters
     */
    message_length_chars: number;
    [property: string]: any;
}

/**
 * Tracks when a user dismisses the experience feedback nudge
 */
export type EvidenceExperienceFeedbackDismissed = {
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * Number of completed queries when dismissed
     */
    query_count?: number | null;
    /**
     * User region
     */
    region?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an outbound call is initiated
 */
export type CallInitiated = {
    /**
     * Retry attempt number
     */
    attempt_number?: number | null;
    /**
     * Unique call identifier
     */
    call_id: string;
    /**
     * Call direction
     */
    direction: Direction;
    /**
     * Objective being executed
     */
    objective_id: string;
    /**
     * Scheduled call identifier if from a schedule
     */
    scheduled_call_id?: null | string;
    /**
     * SHA-256 hash of the target phone number
     */
    to_phone_hash: string;
    [property: string]: any;
}

/**
 * Tracks when an authentication link is manually copied
 */
export type AuthManualLinkCopied = {
    /**
     * Authentication flow context
     */
    auth_flow?: null | string;
    [property: string]: any;
}

/**
 * Tracks when an NHS user submits contact sales for evidence
 */
export type EvidenceNhsContactSalesSubmitted = {
    /**
     * User country (e.g. United Kingdom)
     */
    country?: null | string;
    /**
     * User email address
     */
    email?: null | string;
    /**
     * Evidence location context (logged_out, in_session, out_of_session)
     */
    evidence_location?: null | string;
    /**
     * User first name
     */
    first_name?: null | string;
    /**
     * Whether user has Evidence Pro subscription
     */
    is_evidence_pro?: boolean | null;
    /**
     * Whether user belongs to a managed team
     */
    is_managed?: boolean | null;
    /**
     * Whether the user is a team admin
     */
    is_team_admin?: boolean | null;
    /**
     * Whether user is a team member
     */
    is_team_member?: boolean | null;
    /**
     * User last name
     */
    last_name?: null | string;
    /**
     * User region
     */
    region?: null | string;
    /**
     * Team identifier
     */
    team_id?: null | string;
    /**
     * Team name
     */
    team_name?: null | string;
    /**
     * User role in the team
     */
    team_role?: null | string;
    /**
     * Number of members in the team
     */
    team_size?: number | null;
    [property: string]: any;
}

/**
 * Tracks when a user starts the Comms setup flow
 */
export type CommsSetupStarted = {
    /**
     * Type of setup flow
     */
    setup_type: string;
    /**
     * Total number of steps in the setup flow
     */
    total_steps: number;
    [property: string]: any;
}

/**
 * Tracks context file deleted event
 */
export type ContextFileDeleted = {
    /**
     * Deletion Source
     */
    deletion_source?: null | string;
    /**
     * File Id
     */
    file_id?: null | string;
    /**
     * File Type
     */
    file_type?: null | string;
    /**
     * Session Id
     */
    session_id?: null | string;
    [property: string]: any;
}

/**
 * Creates a Snowplow Event Specification entity.
 */
export function createEventSpecification(eventSpecification: Omit<EventSpecification, 'data_product_domain'> & Partial<Pick<EventSpecification, 'data_product_domain'>>){
    return {
        schema:
            'iglu:com.snowplowanalytics.snowplow/event_specification/jsonschema/1-0-4',
        data: eventSpecification,
    }
}

/**
 * Automatically attached context for event specifications
 */
interface EventSpecification {
    id: string;
    version: number;
    name: string;
    data_product_id: string;
    data_product_name: string;
    data_product_domain?: string;
}

type ContextsOrTimestamp<T = any> = Omit<CommonEventProperties<T>, 'context'> & { context?: SelfDescribingJson<T>[] | null | undefined }
/**
 * Track a Snowplow event for EvidenceClicked.
 * Tracks evidence clicked event
 */
export function trackEvidenceClicked<T extends {} = any>(evidenceClicked: EvidenceClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceClicked entity.
 */
export function createEvidenceClicked(evidenceClicked: EvidenceClicked){
    return {
        schema: 'iglu:com.heidi/evidence_clicked/jsonschema/1-0-1',
        data: evidenceClicked
    }
}
/**
 * Track a Snowplow event for EvidenceSourceControlToggled.
 * Tracks when the source control popover is toggled
 */
export function trackEvidenceSourceControlToggled<T extends {} = any>(evidenceSourceControlToggled: EvidenceSourceControlToggled & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceSourceControlToggled;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_source_control_toggled/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceSourceControlToggled entity.
 */
export function createEvidenceSourceControlToggled(evidenceSourceControlToggled: EvidenceSourceControlToggled){
    return {
        schema: 'iglu:com.heidi/evidence_source_control_toggled/jsonschema/2-0-2',
        data: evidenceSourceControlToggled
    }
}
/**
 * Track a Snowplow event for IntegrationSubmitted.
 * Tracks integration submitted event
 */
export function trackIntegrationSubmitted<T extends {} = any>(integrationSubmitted: IntegrationSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_submitted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationSubmitted entity.
 */
export function createIntegrationSubmitted(integrationSubmitted: IntegrationSubmitted){
    return {
        schema: 'iglu:com.heidi/integration_submitted/jsonschema/1-0-1',
        data: integrationSubmitted
    }
}
/**
 * Track a Snowplow event for DocumentPrinted.
 * Tracks document printed event
 */
export function trackDocumentPrinted<T extends {} = any>(documentPrinted: DocumentPrinted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = documentPrinted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/document_printed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DocumentPrinted entity.
 */
export function createDocumentPrinted(documentPrinted: DocumentPrinted){
    return {
        schema: 'iglu:com.heidi/document_printed/jsonschema/1-0-1',
        data: documentPrinted
    }
}
/**
 * Track a Snowplow event for MessageFailed.
 * Tracks when a message fails with an error
 */
export function trackMessageFailed<T extends {} = any>(messageFailed: MessageFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = messageFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/message_failed/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow MessageFailed entity.
 */
export function createMessageFailed(messageFailed: MessageFailed){
    return {
        schema: 'iglu:com.heidi/message_failed/jsonschema/2-0-3',
        data: messageFailed
    }
}
/**
 * Track a Snowplow event for IntegrationDataRetrieved.
 * Tracks when integration data is successfully retrieved
 */
export function trackIntegrationDataRetrieved<T extends {} = any>(integrationDataRetrieved: IntegrationDataRetrieved & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationDataRetrieved;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_data_retrieved/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationDataRetrieved entity.
 */
export function createIntegrationDataRetrieved(integrationDataRetrieved: IntegrationDataRetrieved){
    return {
        schema: 'iglu:com.heidi/integration_data_retrieved/jsonschema/1-0-1',
        data: integrationDataRetrieved
    }
}
/**
 * Track a Snowplow event for ObjectiveExecutionCompleted.
 * Tracks when an AI objective execution completes successfully
 */
export function trackObjectiveExecutionCompleted<T extends {} = any>(objectiveExecutionCompleted: ObjectiveExecutionCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = objectiveExecutionCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/objective_execution_completed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ObjectiveExecutionCompleted entity.
 */
export function createObjectiveExecutionCompleted(objectiveExecutionCompleted: ObjectiveExecutionCompleted){
    return {
        schema: 'iglu:com.heidi/objective_execution_completed/jsonschema/1-0-0',
        data: objectiveExecutionCompleted
    }
}
/**
 * Track a Snowplow event for DesktopUpdateFailed.
 * Fired when the desktop app auto-updater fails during the check or install step. Covers the region-aware updater (APP-6113, is_regional&#x3D;true) and the legacy global updater (is_regional&#x3D;false).
 */
export function trackDesktopUpdateFailed<T extends {} = any>(desktopUpdateFailed: DesktopUpdateFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = desktopUpdateFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/desktop_update_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DesktopUpdateFailed entity.
 */
export function createDesktopUpdateFailed(desktopUpdateFailed: DesktopUpdateFailed){
    return {
        schema: 'iglu:com.heidi/desktop_update_failed/jsonschema/1-0-0',
        data: desktopUpdateFailed
    }
}
/**
 * Track a Snowplow event for ShareButtonClicked.
 * Tracks when a share button is clicked
 */
export function trackShareButtonClicked<T extends {} = any>(shareButtonClicked: ShareButtonClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareButtonClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_button_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareButtonClicked entity.
 */
export function createShareButtonClicked(shareButtonClicked: ShareButtonClicked){
    return {
        schema: 'iglu:com.heidi/share_button_clicked/jsonschema/1-0-1',
        data: shareButtonClicked
    }
}
/**
 * Track a Snowplow event for ForceUpdateDeferred.
 * Tracks force update deferred event
 */
export function trackForceUpdateDeferred<T extends {} = any>(forceUpdateDeferred: ForceUpdateDeferred & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = forceUpdateDeferred;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/force_update_deferred/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ForceUpdateDeferred entity.
 */
export function createForceUpdateDeferred(forceUpdateDeferred: ForceUpdateDeferred){
    return {
        schema: 'iglu:com.heidi/force_update_deferred/jsonschema/1-0-1',
        data: forceUpdateDeferred
    }
}
/**
 * Track a Snowplow event for ProcessLifecycle.
 * Tracks state transitions for asynchronous and background processes
 */
export function trackProcessLifecycle<T extends {} = any>(processLifecycle: ProcessLifecycle & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = processLifecycle;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/process_lifecycle/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ProcessLifecycle entity.
 */
export function createProcessLifecycle(processLifecycle: ProcessLifecycle){
    return {
        schema: 'iglu:com.heidi/process_lifecycle/jsonschema/1-0-0',
        data: processLifecycle
    }
}
/**
 * Track a Snowplow event for OnboardingStep.
 * Fires for any step in an onboarding or setup flow, including sub-step task interactions inside a step (note styling, evidence, sandbox documents)
 */
export function trackOnboardingStep<T extends {} = any>(onboardingStep: OnboardingStep & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = onboardingStep;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/onboarding_step/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OnboardingStep entity.
 */
export function createOnboardingStep(onboardingStep: OnboardingStep){
    return {
        schema: 'iglu:com.heidi/onboarding_step/jsonschema/1-0-0',
        data: onboardingStep
    }
}
/**
 * Track a Snowplow event for TranscriptionStopped.
 * Tracks when transcription is stopped or paused
 */
export function trackTranscriptionStopped<T extends {} = any>(transcriptionStopped: TranscriptionStopped & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = transcriptionStopped;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/transcription_stopped/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TranscriptionStopped entity.
 */
export function createTranscriptionStopped(transcriptionStopped: TranscriptionStopped){
    return {
        schema: 'iglu:com.heidi/transcription_stopped/jsonschema/2-0-1',
        data: transcriptionStopped
    }
}
/**
 * Track a Snowplow event for ShareLinkGenerated.
 * Tracks when a new share link is generated
 */
export function trackShareLinkGenerated<T extends {} = any>(shareLinkGenerated: ShareLinkGenerated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareLinkGenerated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_link_generated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareLinkGenerated entity.
 */
export function createShareLinkGenerated(shareLinkGenerated: ShareLinkGenerated){
    return {
        schema: 'iglu:com.heidi/share_link_generated/jsonschema/1-0-1',
        data: shareLinkGenerated
    }
}
/**
 * Track a Snowplow event for WorkUpgradeActioned.
 * Tracks when a user views, clicks, or dismisses a Work / Agents paywall
 */
export function trackWorkUpgradeActioned<T extends {} = any>(workUpgradeActioned: WorkUpgradeActioned & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workUpgradeActioned;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_upgrade_actioned/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkUpgradeActioned entity.
 */
export function createWorkUpgradeActioned(workUpgradeActioned: WorkUpgradeActioned){
    return {
        schema: 'iglu:com.heidi/work_upgrade_actioned/jsonschema/1-0-0',
        data: workUpgradeActioned
    }
}
/**
 * Track a Snowplow event for NoteSelected.
 * Tracks when note is selected
 */
export function trackNoteSelected<T extends {} = any>(noteSelected: NoteSelected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteSelected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_selected/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteSelected entity.
 */
export function createNoteSelected(noteSelected: NoteSelected){
    return {
        schema: 'iglu:com.heidi/note_selected/jsonschema/2-0-3',
        data: noteSelected
    }
}
/**
 * Track a Snowplow event for PatientSummaryEmailSent.
 * Tracks a successful patient-summary email dispatch in the send-to-patient flow
 */
export function trackPatientSummaryEmailSent<T extends {} = any>(patientSummaryEmailSent: PatientSummaryEmailSent & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientSummaryEmailSent;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_summary_email_sent/jsonschema/1-0-4',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientSummaryEmailSent entity.
 */
export function createPatientSummaryEmailSent(patientSummaryEmailSent: PatientSummaryEmailSent){
    return {
        schema: 'iglu:com.heidi/patient_summary_email_sent/jsonschema/1-0-4',
        data: patientSummaryEmailSent
    }
}
/**
 * Track a Snowplow event for EvidenceReferenceScrollClicked.
 * Tracks when a user clicks a reference to scroll to its inline citation
 */
export function trackEvidenceReferenceScrollClicked<T extends {} = any>(evidenceReferenceScrollClicked: EvidenceReferenceScrollClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceReferenceScrollClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_reference_scroll_clicked/jsonschema/2-0-4',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceReferenceScrollClicked entity.
 */
export function createEvidenceReferenceScrollClicked(evidenceReferenceScrollClicked: EvidenceReferenceScrollClicked){
    return {
        schema: 'iglu:com.heidi/evidence_reference_scroll_clicked/jsonschema/2-0-4',
        data: evidenceReferenceScrollClicked
    }
}
/**
 * Track a Snowplow event for SnippetsUploaded.
 * Tracks snippets uploaded event
 */
export function trackSnippetsUploaded<T extends {} = any>(snippetsUploaded: SnippetsUploaded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = snippetsUploaded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/snippets_uploaded/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SnippetsUploaded entity.
 */
export function createSnippetsUploaded(snippetsUploaded: SnippetsUploaded){
    return {
        schema: 'iglu:com.heidi/snippets_uploaded/jsonschema/1-0-1',
        data: snippetsUploaded
    }
}
/**
 * Track a Snowplow event for ContextPreviewed.
 * Tracks context previewed event
 */
export function trackContextPreviewed<T extends {} = any>(contextPreviewed: ContextPreviewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = contextPreviewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/context_previewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ContextPreviewed entity.
 */
export function createContextPreviewed(contextPreviewed: ContextPreviewed){
    return {
        schema: 'iglu:com.heidi/context_previewed/jsonschema/1-0-1',
        data: contextPreviewed
    }
}
/**
 * Track a Snowplow event for ExportUpdated.
 * Tracks export updated event
 */
export function trackExportUpdated<T extends {} = any>(exportUpdated: ExportUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = exportUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/export_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ExportUpdated entity.
 */
export function createExportUpdated(exportUpdated: ExportUpdated){
    return {
        schema: 'iglu:com.heidi/export_updated/jsonschema/1-0-1',
        data: exportUpdated
    }
}
/**
 * Track a Snowplow event for TranslatorBulkUploadFailed.
 * Fired when a bulk audio upload fails during processing
 */
export function trackTranslatorBulkUploadFailed<T extends {} = any>(translatorBulkUploadFailed: TranslatorBulkUploadFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = translatorBulkUploadFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/translator_bulk_upload_failed/jsonschema/1-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TranslatorBulkUploadFailed entity.
 */
export function createTranslatorBulkUploadFailed(translatorBulkUploadFailed: TranslatorBulkUploadFailed){
    return {
        schema: 'iglu:com.heidi/translator_bulk_upload_failed/jsonschema/1-1-0',
        data: translatorBulkUploadFailed
    }
}
/**
 * Track a Snowplow event for DocumentShared.
 * Tracks document shared event
 */
export function trackDocumentShared<T extends {} = any>(documentShared: DocumentShared & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = documentShared;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/document_shared/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DocumentShared entity.
 */
export function createDocumentShared(documentShared: DocumentShared){
    return {
        schema: 'iglu:com.heidi/document_shared/jsonschema/1-0-1',
        data: documentShared
    }
}
/**
 * Track a Snowplow event for EvidenceSummarySuggestionShown.
 * Tracks when the post-note-generation Evidence summary suggestion banner is shown
 */
export function trackEvidenceSummarySuggestionShown<T extends {} = any>(evidenceSummarySuggestionShown: EvidenceSummarySuggestionShown & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceSummarySuggestionShown;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_summary_suggestion_shown/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceSummarySuggestionShown entity.
 */
export function createEvidenceSummarySuggestionShown(evidenceSummarySuggestionShown: EvidenceSummarySuggestionShown){
    return {
        schema: 'iglu:com.heidi/evidence_summary_suggestion_shown/jsonschema/1-0-0',
        data: evidenceSummarySuggestionShown
    }
}
/**
 * Track a Snowplow event for TranslatorSessionCreated.
 * Fired when a new translator session is created
 */
export function trackTranslatorSessionCreated<T extends {} = any>(translatorSessionCreated: TranslatorSessionCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = translatorSessionCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/translator_session_created/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TranslatorSessionCreated entity.
 */
export function createTranslatorSessionCreated(translatorSessionCreated: TranslatorSessionCreated){
    return {
        schema: 'iglu:com.heidi/translator_session_created/jsonschema/2-0-0',
        data: translatorSessionCreated
    }
}
/**
 * Track a Snowplow event for ReplacementsUploaded.
 * Tracks replacements uploaded event
 */
export function trackReplacementsUploaded<T extends {} = any>(replacementsUploaded: ReplacementsUploaded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = replacementsUploaded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/replacements_uploaded/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ReplacementsUploaded entity.
 */
export function createReplacementsUploaded(replacementsUploaded: ReplacementsUploaded){
    return {
        schema: 'iglu:com.heidi/replacements_uploaded/jsonschema/1-0-1',
        data: replacementsUploaded
    }
}
/**
 * Track a Snowplow event for AudioWarningAccepted.
 * Tracks when the user accepts the silent-capture warning&#x27;s one-click fix
 */
export function trackAudioWarningAccepted<T extends {} = any>(audioWarningAccepted: AudioWarningAccepted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = audioWarningAccepted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/audio_warning_accepted/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AudioWarningAccepted entity.
 */
export function createAudioWarningAccepted(audioWarningAccepted: AudioWarningAccepted){
    return {
        schema: 'iglu:com.heidi/audio_warning_accepted/jsonschema/1-0-0',
        data: audioWarningAccepted
    }
}
/**
 * Track a Snowplow event for LetterRecipientSearched.
 * Tracks a recipient search performed while addressing a letter
 */
export function trackLetterRecipientSearched<T extends {} = any>(letterRecipientSearched: LetterRecipientSearched & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = letterRecipientSearched;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/letter_recipient_searched/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LetterRecipientSearched entity.
 */
export function createLetterRecipientSearched(letterRecipientSearched: LetterRecipientSearched){
    return {
        schema: 'iglu:com.heidi/letter_recipient_searched/jsonschema/1-0-0',
        data: letterRecipientSearched
    }
}
/**
 * Track a Snowplow event for EvidenceSuggestionClicked.
 * Tracks evidence suggestion clicked event
 */
export function trackEvidenceSuggestionClicked<T extends {} = any>(evidenceSuggestionClicked: EvidenceSuggestionClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceSuggestionClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_suggestion_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceSuggestionClicked entity.
 */
export function createEvidenceSuggestionClicked(evidenceSuggestionClicked: EvidenceSuggestionClicked){
    return {
        schema: 'iglu:com.heidi/evidence_suggestion_clicked/jsonschema/1-0-1',
        data: evidenceSuggestionClicked
    }
}
/**
 * Track a Snowplow event for SessionUpdated.
 * Tracks session updated event
 */
export function trackSessionUpdated<T extends {} = any>(sessionUpdated: SessionUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/session_updated/jsonschema/1-0-5',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionUpdated entity.
 */
export function createSessionUpdated(sessionUpdated: SessionUpdated){
    return {
        schema: 'iglu:com.heidi/session_updated/jsonschema/1-0-5',
        data: sessionUpdated
    }
}
/**
 * Track a Snowplow event for PatientFieldEdited.
 * Tracks when an inline field save succeeds on the patient profile
 */
export function trackPatientFieldEdited<T extends {} = any>(patientFieldEdited: PatientFieldEdited & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientFieldEdited;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_field_edited/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientFieldEdited entity.
 */
export function createPatientFieldEdited(patientFieldEdited: PatientFieldEdited){
    return {
        schema: 'iglu:com.heidi/patient_field_edited/jsonschema/1-0-0',
        data: patientFieldEdited
    }
}
/**
 * Track a Snowplow event for NoteCopied.
 * Tracks when note or Evidence chat content is copied to the clipboard. PHI-free — carries no copied body. surface/chat_id/message_id attribute Evidence copies to the producing response so copy adoption can be compared with note_insert_applied.
 */
export function trackNoteCopied<T extends {} = any>(noteCopied: NoteCopied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteCopied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_copied/jsonschema/2-0-4',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteCopied entity.
 */
export function createNoteCopied(noteCopied: NoteCopied){
    return {
        schema: 'iglu:com.heidi/note_copied/jsonschema/2-0-4',
        data: noteCopied
    }
}
/**
 * Track a Snowplow event for FeedbackUpdated.
 * Tracks feedback updated event
 */
export function trackFeedbackUpdated<T extends {} = any>(feedbackUpdated: FeedbackUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = feedbackUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/feedback_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FeedbackUpdated entity.
 */
export function createFeedbackUpdated(feedbackUpdated: FeedbackUpdated){
    return {
        schema: 'iglu:com.heidi/feedback_updated/jsonschema/1-0-1',
        data: feedbackUpdated
    }
}
/**
 * Track a Snowplow event for SubscriptionCreated.
 * Tracks when a subscription is created
 */
export function trackSubscriptionCreated<T extends {} = any>(subscriptionCreated: SubscriptionCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = subscriptionCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/subscription_created/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SubscriptionCreated entity.
 */
export function createSubscriptionCreated(subscriptionCreated: SubscriptionCreated){
    return {
        schema: 'iglu:com.heidi/subscription_created/jsonschema/2-0-0',
        data: subscriptionCreated
    }
}
/**
 * Track a Snowplow event for AccountUpdated.
 * Tracks when user account details are updated
 */
export function trackAccountUpdated<T extends {} = any>(accountUpdated: AccountUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = accountUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/account_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AccountUpdated entity.
 */
export function createAccountUpdated(accountUpdated: AccountUpdated){
    return {
        schema: 'iglu:com.heidi/account_updated/jsonschema/1-0-1',
        data: accountUpdated
    }
}
/**
 * Track a Snowplow event for TemplateQualityGate.
 * Tracks presentations and outcomes of the UK/EU template quality attestation gate
 */
export function trackTemplateQualityGate<T extends {} = any>(templateQualityGate: TemplateQualityGate & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateQualityGate;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_quality_gate/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateQualityGate entity.
 */
export function createTemplateQualityGate(templateQualityGate: TemplateQualityGate){
    return {
        schema: 'iglu:com.heidi/template_quality_gate/jsonschema/1-0-0',
        data: templateQualityGate
    }
}
/**
 * Track a Snowplow event for LetterRecipientRemoved.
 * Tracks when a recipient is removed from a letter
 */
export function trackLetterRecipientRemoved<T extends {} = any>(letterRecipientRemoved: LetterRecipientRemoved & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = letterRecipientRemoved;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/letter_recipient_removed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LetterRecipientRemoved entity.
 */
export function createLetterRecipientRemoved(letterRecipientRemoved: LetterRecipientRemoved){
    return {
        schema: 'iglu:com.heidi/letter_recipient_removed/jsonschema/1-0-0',
        data: letterRecipientRemoved
    }
}
/**
 * Track a Snowplow event for TeamMemberAccepted.
 * Tracks team member accepted event
 */
export function trackTeamMemberAccepted<T extends {} = any>(teamMemberAccepted: TeamMemberAccepted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = teamMemberAccepted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/team_member_accepted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TeamMemberAccepted entity.
 */
export function createTeamMemberAccepted(teamMemberAccepted: TeamMemberAccepted){
    return {
        schema: 'iglu:com.heidi/team_member_accepted/jsonschema/1-0-1',
        data: teamMemberAccepted
    }
}
/**
 * Track a Snowplow event for CommunityAccepted.
 * Tracks when a community template request is approved
 */
export function trackCommunityAccepted<T extends {} = any>(communityAccepted: CommunityAccepted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = communityAccepted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/community_accepted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CommunityAccepted entity.
 */
export function createCommunityAccepted(communityAccepted: CommunityAccepted){
    return {
        schema: 'iglu:com.heidi/community_accepted/jsonschema/1-0-1',
        data: communityAccepted
    }
}
/**
 * Track a Snowplow event for PrechartViewed.
 * Tracks when the pre-chart tab becomes active in a session
 */
export function trackPrechartViewed<T extends {} = any>(prechartViewed: PrechartViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = prechartViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/prechart_viewed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PrechartViewed entity.
 */
export function createPrechartViewed(prechartViewed: PrechartViewed){
    return {
        schema: 'iglu:com.heidi/prechart_viewed/jsonschema/1-0-0',
        data: prechartViewed
    }
}
/**
 * Track a Snowplow event for UserStarted.
 * Tracks user started event
 */
export function trackUserStarted<T extends {} = any>(userStarted: UserStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = userStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/user_started/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UserStarted entity.
 */
export function createUserStarted(userStarted: UserStarted){
    return {
        schema: 'iglu:com.heidi/user_started/jsonschema/1-0-1',
        data: userStarted
    }
}
/**
 * Track a Snowplow event for AppearanceModeChanged.
 * Tracks when a user changes their Light / Dark / System appearance preference
 */
export function trackAppearanceModeChanged<T extends {} = any>(appearanceModeChanged: AppearanceModeChanged & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = appearanceModeChanged;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/appearance_mode_changed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AppearanceModeChanged entity.
 */
export function createAppearanceModeChanged(appearanceModeChanged: AppearanceModeChanged){
    return {
        schema: 'iglu:com.heidi/appearance_mode_changed/jsonschema/1-0-0',
        data: appearanceModeChanged
    }
}
/**
 * Track a Snowplow event for SessionTabViewed.
 * Fired when a session tab stops being active, reporting how long it was on screen.
 */
export function trackSessionTabViewed<T extends {} = any>(sessionTabViewed: SessionTabViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionTabViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/session_tab_viewed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionTabViewed entity.
 */
export function createSessionTabViewed(sessionTabViewed: SessionTabViewed){
    return {
        schema: 'iglu:com.heidi/session_tab_viewed/jsonschema/1-0-0',
        data: sessionTabViewed
    }
}
/**
 * Track a Snowplow event for TranslatorMessageCreated.
 * Fired when a new translated message is created in a session
 */
export function trackTranslatorMessageCreated<T extends {} = any>(translatorMessageCreated: TranslatorMessageCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = translatorMessageCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/translator_message_created/jsonschema/1-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TranslatorMessageCreated entity.
 */
export function createTranslatorMessageCreated(translatorMessageCreated: TranslatorMessageCreated){
    return {
        schema: 'iglu:com.heidi/translator_message_created/jsonschema/1-1-0',
        data: translatorMessageCreated
    }
}
/**
 * Track a Snowplow event for EvidenceOnboardingDismissed.
 * Tracks evidence onboarding dismissed event
 */
export function trackEvidenceOnboardingDismissed<T extends {} = any>(evidenceOnboardingDismissed: EvidenceOnboardingDismissed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceOnboardingDismissed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_onboarding_dismissed/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceOnboardingDismissed entity.
 */
export function createEvidenceOnboardingDismissed(evidenceOnboardingDismissed: EvidenceOnboardingDismissed){
    return {
        schema: 'iglu:com.heidi/evidence_onboarding_dismissed/jsonschema/2-0-1',
        data: evidenceOnboardingDismissed
    }
}
/**
 * Track a Snowplow event for InboxFilterApplied.
 * Tracks when a user applies a filter in the inbox
 */
export function trackInboxFilterApplied<T extends {} = any>(inboxFilterApplied: InboxFilterApplied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = inboxFilterApplied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/inbox_filter_applied/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow InboxFilterApplied entity.
 */
export function createInboxFilterApplied(inboxFilterApplied: InboxFilterApplied){
    return {
        schema: 'iglu:com.heidi/inbox_filter_applied/jsonschema/1-0-0',
        data: inboxFilterApplied
    }
}
/**
 * Track a Snowplow event for EvidenceOnboardingSkipped.
 * Tracks evidence onboarding skipped event
 */
export function trackEvidenceOnboardingSkipped<T extends {} = any>(evidenceOnboardingSkipped: EvidenceOnboardingSkipped & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceOnboardingSkipped;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_onboarding_skipped/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceOnboardingSkipped entity.
 */
export function createEvidenceOnboardingSkipped(evidenceOnboardingSkipped: EvidenceOnboardingSkipped){
    return {
        schema: 'iglu:com.heidi/evidence_onboarding_skipped/jsonschema/2-0-1',
        data: evidenceOnboardingSkipped
    }
}
/**
 * Track a Snowplow event for OfflineSyncDeferred.
 * Tracks a queue drain deferred because a required prerequisite is unavailable
 */
export function trackOfflineSyncDeferred<T extends {} = any>(offlineSyncDeferred: OfflineSyncDeferred & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = offlineSyncDeferred;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/offline_sync_deferred/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OfflineSyncDeferred entity.
 */
export function createOfflineSyncDeferred(offlineSyncDeferred: OfflineSyncDeferred){
    return {
        schema: 'iglu:com.heidi/offline_sync_deferred/jsonschema/1-0-0',
        data: offlineSyncDeferred
    }
}
/**
 * Track a Snowplow event for ObjectiveHandoffTriggered.
 * Tracks when an AI objective hands off to a human agent
 */
export function trackObjectiveHandoffTriggered<T extends {} = any>(objectiveHandoffTriggered: ObjectiveHandoffTriggered & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = objectiveHandoffTriggered;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/objective_handoff_triggered/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ObjectiveHandoffTriggered entity.
 */
export function createObjectiveHandoffTriggered(objectiveHandoffTriggered: ObjectiveHandoffTriggered){
    return {
        schema: 'iglu:com.heidi/objective_handoff_triggered/jsonschema/1-0-0',
        data: objectiveHandoffTriggered
    }
}
/**
 * Track a Snowplow event for PatientRecapPopoverOpened.
 * Tracks when the patient recap hover popover opens on the in-session linked-patient chip
 */
export function trackPatientRecapPopoverOpened<T extends {} = any>(patientRecapPopoverOpened: PatientRecapPopoverOpened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientRecapPopoverOpened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_recap_popover_opened/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientRecapPopoverOpened entity.
 */
export function createPatientRecapPopoverOpened(patientRecapPopoverOpened: PatientRecapPopoverOpened){
    return {
        schema: 'iglu:com.heidi/patient_recap_popover_opened/jsonschema/1-0-0',
        data: patientRecapPopoverOpened
    }
}
/**
 * Track a Snowplow event for DocumentDeleted.
 * Tracks when document is deleted
 */
export function trackDocumentDeleted<T extends {} = any>(documentDeleted: DocumentDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = documentDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/document_deleted/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DocumentDeleted entity.
 */
export function createDocumentDeleted(documentDeleted: DocumentDeleted){
    return {
        schema: 'iglu:com.heidi/document_deleted/jsonschema/1-0-3',
        data: documentDeleted
    }
}
/**
 * Track a Snowplow event for ScheduleUploadStarted.
 * Fired when a schedule file passes client-side validation and the upload begins, before the processing request is sent
 */
export function trackScheduleUploadStarted<T extends {} = any>(scheduleUploadStarted: ScheduleUploadStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = scheduleUploadStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/schedule_upload_started/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScheduleUploadStarted entity.
 */
export function createScheduleUploadStarted(scheduleUploadStarted: ScheduleUploadStarted){
    return {
        schema: 'iglu:com.heidi/schedule_upload_started/jsonschema/1-0-0',
        data: scheduleUploadStarted
    }
}
/**
 * Track a Snowplow event for TemplateSelected.
 * Tracks when a template is selected
 */
export function trackTemplateSelected<T extends {} = any>(templateSelected: TemplateSelected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateSelected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_selected/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateSelected entity.
 */
export function createTemplateSelected(templateSelected: TemplateSelected){
    return {
        schema: 'iglu:com.heidi/template_selected/jsonschema/1-0-2',
        data: templateSelected
    }
}
/**
 * Track a Snowplow event for OnboardingCompleted.
 * Tracks when onboarding is completed
 */
export function trackOnboardingCompleted<T extends {} = any>(onboardingCompleted: OnboardingCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = onboardingCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/onboarding_completed/jsonschema/1-1-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OnboardingCompleted entity.
 */
export function createOnboardingCompleted(onboardingCompleted: OnboardingCompleted){
    return {
        schema: 'iglu:com.heidi/onboarding_completed/jsonschema/1-1-1',
        data: onboardingCompleted
    }
}
/**
 * Track a Snowplow event for OnboardingStarted.
 * Tracks when onboarding is initiated
 */
export function trackOnboardingStarted<T extends {} = any>(onboardingStarted: OnboardingStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = onboardingStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/onboarding_started/jsonschema/2-1-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OnboardingStarted entity.
 */
export function createOnboardingStarted(onboardingStarted: OnboardingStarted){
    return {
        schema: 'iglu:com.heidi/onboarding_started/jsonschema/2-1-1',
        data: onboardingStarted
    }
}
/**
 * Track a Snowplow event for FollowUpQuestionClicked.
 * Tracks follow up question clicked event
 */
export function trackFollowUpQuestionClicked<T extends {} = any>(followUpQuestionClicked: FollowUpQuestionClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = followUpQuestionClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/follow_up_question_clicked/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FollowUpQuestionClicked entity.
 */
export function createFollowUpQuestionClicked(followUpQuestionClicked: FollowUpQuestionClicked){
    return {
        schema: 'iglu:com.heidi/follow_up_question_clicked/jsonschema/1-0-2',
        data: followUpQuestionClicked
    }
}
/**
 * Track a Snowplow event for ObjectiveExecutionStarted.
 * Tracks when an AI objective execution starts
 */
export function trackObjectiveExecutionStarted<T extends {} = any>(objectiveExecutionStarted: ObjectiveExecutionStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = objectiveExecutionStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/objective_execution_started/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ObjectiveExecutionStarted entity.
 */
export function createObjectiveExecutionStarted(objectiveExecutionStarted: ObjectiveExecutionStarted){
    return {
        schema: 'iglu:com.heidi/objective_execution_started/jsonschema/1-0-0',
        data: objectiveExecutionStarted
    }
}
/**
 * Track a Snowplow event for ChronicleDeviceDisconnected.
 * Tracks chronicle device disconnected event
 */
export function trackChronicleDeviceDisconnected<T extends {} = any>(chronicleDeviceDisconnected: ChronicleDeviceDisconnected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chronicleDeviceDisconnected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/chronicle_device_disconnected/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChronicleDeviceDisconnected entity.
 */
export function createChronicleDeviceDisconnected(chronicleDeviceDisconnected: ChronicleDeviceDisconnected){
    return {
        schema: 'iglu:com.heidi/chronicle_device_disconnected/jsonschema/1-0-3',
        data: chronicleDeviceDisconnected
    }
}
/**
 * Track a Snowplow event for AudioFailed.
 * Tracks audio failed event
 */
export function trackAudioFailed<T extends {} = any>(audioFailed: AudioFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = audioFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/audio_failed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AudioFailed entity.
 */
export function createAudioFailed(audioFailed: AudioFailed){
    return {
        schema: 'iglu:com.heidi/audio_failed/jsonschema/1-0-1',
        data: audioFailed
    }
}
/**
 * Track a Snowplow event for OfflineRecordingDeleted.
 * Tracks deletion of a local offline recording before or after sync
 */
export function trackOfflineRecordingDeleted<T extends {} = any>(offlineRecordingDeleted: OfflineRecordingDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = offlineRecordingDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/offline_recording_deleted/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OfflineRecordingDeleted entity.
 */
export function createOfflineRecordingDeleted(offlineRecordingDeleted: OfflineRecordingDeleted){
    return {
        schema: 'iglu:com.heidi/offline_recording_deleted/jsonschema/1-0-0',
        data: offlineRecordingDeleted
    }
}
/**
 * Track a Snowplow event for EvidenceCopied.
 * Tracks when Evidence chat content is successfully copied to the clipboard. PHI-free — carries no copied body. surface/chat_id/message_id attribute the copy to the producing response so copy adoption can be compared with note_insert_applied (add/replace/create-document).
 */
export function trackEvidenceCopied<T extends {} = any>(evidenceCopied: EvidenceCopied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceCopied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_copied/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceCopied entity.
 */
export function createEvidenceCopied(evidenceCopied: EvidenceCopied){
    return {
        schema: 'iglu:com.heidi/evidence_copied/jsonschema/1-0-0',
        data: evidenceCopied
    }
}
/**
 * Track a Snowplow event for EhrLaunchCreated.
 * Tracks EHR launch token generation
 */
export function trackEhrLaunchCreated<T extends {} = any>(ehrLaunchCreated: EhrLaunchCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = ehrLaunchCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/ehr_launch_created/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EhrLaunchCreated entity.
 */
export function createEhrLaunchCreated(ehrLaunchCreated: EhrLaunchCreated){
    return {
        schema: 'iglu:com.heidi/ehr_launch_created/jsonschema/1-0-3',
        data: ehrLaunchCreated
    }
}
/**
 * Track a Snowplow event for EhrSectionSelected.
 * Tracks EHR section selection
 */
export function trackEhrSectionSelected<T extends {} = any>(ehrSectionSelected: EhrSectionSelected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = ehrSectionSelected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/ehr_section_selected/jsonschema/1-0-4',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EhrSectionSelected entity.
 */
export function createEhrSectionSelected(ehrSectionSelected: EhrSectionSelected){
    return {
        schema: 'iglu:com.heidi/ehr_section_selected/jsonschema/1-0-4',
        data: ehrSectionSelected
    }
}
/**
 * Track a Snowplow event for SessionDeleted.
 * Tracks when session is deleted
 */
export function trackSessionDeleted<T extends {} = any>(sessionDeleted: SessionDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/session_deleted/jsonschema/3-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionDeleted entity.
 */
export function createSessionDeleted(sessionDeleted: SessionDeleted){
    return {
        schema: 'iglu:com.heidi/session_deleted/jsonschema/3-0-2',
        data: sessionDeleted
    }
}
/**
 * Track a Snowplow event for FindReplaceScopeUpdated.
 * Tracks when user changes find/replace scope
 */
export function trackFindReplaceScopeUpdated<T extends {} = any>(findReplaceScopeUpdated: FindReplaceScopeUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = findReplaceScopeUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/find_replace_scope_updated/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FindReplaceScopeUpdated entity.
 */
export function createFindReplaceScopeUpdated(findReplaceScopeUpdated: FindReplaceScopeUpdated){
    return {
        schema: 'iglu:com.heidi/find_replace_scope_updated/jsonschema/1-0-3',
        data: findReplaceScopeUpdated
    }
}
/**
 * Track a Snowplow event for EvidenceShortcutSearchUsed.
 * Tracks when Ask Heidi shortcut search is used. PHI-free — never carries search_query.
 */
export function trackEvidenceShortcutSearchUsed<T extends {} = any>(evidenceShortcutSearchUsed: EvidenceShortcutSearchUsed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceShortcutSearchUsed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_shortcut_search_used/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceShortcutSearchUsed entity.
 */
export function createEvidenceShortcutSearchUsed(evidenceShortcutSearchUsed: EvidenceShortcutSearchUsed){
    return {
        schema: 'iglu:com.heidi/evidence_shortcut_search_used/jsonschema/1-0-0',
        data: evidenceShortcutSearchUsed
    }
}
/**
 * Track a Snowplow event for RecordingConsentDeclined.
 * Tracks when recording consent is declined by a participant
 */
export function trackRecordingConsentDeclined<T extends {} = any>(recordingConsentDeclined: RecordingConsentDeclined & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = recordingConsentDeclined;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/recording_consent_declined/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow RecordingConsentDeclined entity.
 */
export function createRecordingConsentDeclined(recordingConsentDeclined: RecordingConsentDeclined){
    return {
        schema: 'iglu:com.heidi/recording_consent_declined/jsonschema/1-0-0',
        data: recordingConsentDeclined
    }
}
/**
 * Track a Snowplow event for PatientSummaryEmailSendAttempted.
 * Tracks a confirmed Send click in the send-to-patient email flow, before the dispatch result
 */
export function trackPatientSummaryEmailSendAttempted<T extends {} = any>(patientSummaryEmailSendAttempted: PatientSummaryEmailSendAttempted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientSummaryEmailSendAttempted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_summary_email_send_attempted/jsonschema/1-0-4',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientSummaryEmailSendAttempted entity.
 */
export function createPatientSummaryEmailSendAttempted(patientSummaryEmailSendAttempted: PatientSummaryEmailSendAttempted){
    return {
        schema: 'iglu:com.heidi/patient_summary_email_send_attempted/jsonschema/1-0-4',
        data: patientSummaryEmailSendAttempted
    }
}
/**
 * Track a Snowplow event for NoteAutoRetryDecided.
 * The note-generation auto-retry layer decided what to do after one attempt: retry once quietly, or stop and surface the outcome. One event per attempt.
 */
export function trackNoteAutoRetryDecided<T extends {} = any>(noteAutoRetryDecided: NoteAutoRetryDecided & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteAutoRetryDecided;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_auto_retry_decided/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteAutoRetryDecided entity.
 */
export function createNoteAutoRetryDecided(noteAutoRetryDecided: NoteAutoRetryDecided){
    return {
        schema: 'iglu:com.heidi/note_auto_retry_decided/jsonschema/1-0-0',
        data: noteAutoRetryDecided
    }
}
/**
 * Track a Snowplow event for CallAnswered.
 * Tracks when an outbound call is answered by the recipient
 */
export function trackCallAnswered<T extends {} = any>(callAnswered: CallAnswered & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callAnswered;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_answered/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallAnswered entity.
 */
export function createCallAnswered(callAnswered: CallAnswered){
    return {
        schema: 'iglu:com.heidi/call_answered/jsonschema/1-0-0',
        data: callAnswered
    }
}
/**
 * Track a Snowplow event for ScheduleUploadPromoClicked.
 * Tracks schedule upload promo clicked event
 */
export function trackScheduleUploadPromoClicked<T extends {} = any>(scheduleUploadPromoClicked: ScheduleUploadPromoClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = scheduleUploadPromoClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/schedule_upload_promo_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScheduleUploadPromoClicked entity.
 */
export function createScheduleUploadPromoClicked(scheduleUploadPromoClicked: ScheduleUploadPromoClicked){
    return {
        schema: 'iglu:com.heidi/schedule_upload_promo_clicked/jsonschema/1-0-1',
        data: scheduleUploadPromoClicked
    }
}
/**
 * Track a Snowplow event for EhrPatientMatched.
 * Tracks when a patient is matched to an EHR record
 */
export function trackEhrPatientMatched<T extends {} = any>(ehrPatientMatched: EhrPatientMatched & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = ehrPatientMatched;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/ehr_patient_matched/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EhrPatientMatched entity.
 */
export function createEhrPatientMatched(ehrPatientMatched: EhrPatientMatched){
    return {
        schema: 'iglu:com.heidi/ehr_patient_matched/jsonschema/1-0-0',
        data: ehrPatientMatched
    }
}
/**
 * Track a Snowplow event for ContextFileUploaded.
 * Tracks context file uploaded event
 */
export function trackContextFileUploaded<T extends {} = any>(contextFileUploaded: ContextFileUploaded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = contextFileUploaded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/context_file_uploaded/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ContextFileUploaded entity.
 */
export function createContextFileUploaded(contextFileUploaded: ContextFileUploaded){
    return {
        schema: 'iglu:com.heidi/context_file_uploaded/jsonschema/1-0-1',
        data: contextFileUploaded
    }
}
/**
 * Track a Snowplow event for UserSignedUp.
 * Tracks user signed up event
 */
export function trackUserSignedUp<T extends {} = any>(userSignedUp: UserSignedUp & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = userSignedUp;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/user_signed_up/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UserSignedUp entity.
 */
export function createUserSignedUp(userSignedUp: UserSignedUp){
    return {
        schema: 'iglu:com.heidi/user_signed_up/jsonschema/1-0-2',
        data: userSignedUp
    }
}
/**
 * Track a Snowplow event for MessageAttachmentUploadFailed.
 * Tracks when a message attachment upload fails
 */
export function trackMessageAttachmentUploadFailed<T extends {} = any>(messageAttachmentUploadFailed: MessageAttachmentUploadFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = messageAttachmentUploadFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/message_attachment_upload_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow MessageAttachmentUploadFailed entity.
 */
export function createMessageAttachmentUploadFailed(messageAttachmentUploadFailed: MessageAttachmentUploadFailed){
    return {
        schema: 'iglu:com.heidi/message_attachment_upload_failed/jsonschema/1-0-0',
        data: messageAttachmentUploadFailed
    }
}
/**
 * Track a Snowplow event for EvidenceSourcesOpened.
 * Tracks when the Ask Heidi Sources picker is opened
 */
export function trackEvidenceSourcesOpened<T extends {} = any>(evidenceSourcesOpened: EvidenceSourcesOpened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceSourcesOpened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_sources_opened/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceSourcesOpened entity.
 */
export function createEvidenceSourcesOpened(evidenceSourcesOpened: EvidenceSourcesOpened){
    return {
        schema: 'iglu:com.heidi/evidence_sources_opened/jsonschema/1-0-0',
        data: evidenceSourcesOpened
    }
}
/**
 * Track a Snowplow event for PatientProfileOpened.
 * Tracks when a patient drawer or full-page profile mounts for a patient
 */
export function trackPatientProfileOpened<T extends {} = any>(patientProfileOpened: PatientProfileOpened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientProfileOpened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_profile_opened/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientProfileOpened entity.
 */
export function createPatientProfileOpened(patientProfileOpened: PatientProfileOpened){
    return {
        schema: 'iglu:com.heidi/patient_profile_opened/jsonschema/1-0-0',
        data: patientProfileOpened
    }
}
/**
 * Track a Snowplow event for CpdDraftDeleted.
 * Tracks cpd draft deleted event
 */
export function trackCpdDraftDeleted<T extends {} = any>(cpdDraftDeleted: CpdDraftDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = cpdDraftDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/cpd_draft_deleted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CpdDraftDeleted entity.
 */
export function createCpdDraftDeleted(cpdDraftDeleted: CpdDraftDeleted){
    return {
        schema: 'iglu:com.heidi/cpd_draft_deleted/jsonschema/1-0-1',
        data: cpdDraftDeleted
    }
}
/**
 * Track a Snowplow event for IntegrationStarted.
 * Tracks integration started event
 */
export function trackIntegrationStarted<T extends {} = any>(integrationStarted: IntegrationStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_started/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationStarted entity.
 */
export function createIntegrationStarted(integrationStarted: IntegrationStarted){
    return {
        schema: 'iglu:com.heidi/integration_started/jsonschema/1-0-2',
        data: integrationStarted
    }
}
/**
 * Track a Snowplow event for EhrAppointmentSyncFailed.
 * Tracks when an appointment sync with the EHR fails
 */
export function trackEhrAppointmentSyncFailed<T extends {} = any>(ehrAppointmentSyncFailed: EhrAppointmentSyncFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = ehrAppointmentSyncFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/ehr_appointment_sync_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EhrAppointmentSyncFailed entity.
 */
export function createEhrAppointmentSyncFailed(ehrAppointmentSyncFailed: EhrAppointmentSyncFailed){
    return {
        schema: 'iglu:com.heidi/ehr_appointment_sync_failed/jsonschema/1-0-0',
        data: ehrAppointmentSyncFailed
    }
}
/**
 * Track a Snowplow event for ApplicationInstall.
 * Fired once on a desktop app&#x27;s first launch after a fresh install.
 */
export function trackApplicationInstall<T extends {} = any>(applicationInstall: ApplicationInstall & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = applicationInstall;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/application_install/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ApplicationInstall entity.
 */
export function createApplicationInstall(applicationInstall: ApplicationInstall){
    return {
        schema: 'iglu:com.heidi/application_install/jsonschema/1-0-0',
        data: applicationInstall
    }
}
/**
 * Track a Snowplow event for LetterDeleted.
 * Tracks deletion of a letter
 */
export function trackLetterDeleted<T extends {} = any>(letterDeleted: LetterDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = letterDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/letter_deleted/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LetterDeleted entity.
 */
export function createLetterDeleted(letterDeleted: LetterDeleted){
    return {
        schema: 'iglu:com.heidi/letter_deleted/jsonschema/1-0-0',
        data: letterDeleted
    }
}
/**
 * Track a Snowplow event for ModalViewed.
 * Tracks when modal is viewed
 */
export function trackModalViewed<T extends {} = any>(modalViewed: ModalViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = modalViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/modal_viewed/jsonschema/2-0-5',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ModalViewed entity.
 */
export function createModalViewed(modalViewed: ModalViewed){
    return {
        schema: 'iglu:com.heidi/modal_viewed/jsonschema/2-0-5',
        data: modalViewed
    }
}
/**
 * Track a Snowplow event for TaskViewed.
 * Tracks when a task is explicitly opened — task detail panel or in-session card expand
 */
export function trackTaskViewed<T extends {} = any>(taskViewed: TaskViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = taskViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/task_viewed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TaskViewed entity.
 */
export function createTaskViewed(taskViewed: TaskViewed){
    return {
        schema: 'iglu:com.heidi/task_viewed/jsonschema/1-0-0',
        data: taskViewed
    }
}
/**
 * Track a Snowplow event for SessionDetailResumeStarted.
 * Session Detail Resume Started — a session-detail Resume began capturing again; recording_device says whether the Heidi Remote or the app captures
 */
export function trackSessionDetailResumeStarted<T extends {} = any>(sessionDetailResumeStarted: SessionDetailResumeStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionDetailResumeStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/session_detail_resume_started/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionDetailResumeStarted entity.
 */
export function createSessionDetailResumeStarted(sessionDetailResumeStarted: SessionDetailResumeStarted){
    return {
        schema: 'iglu:com.heidi/session_detail_resume_started/jsonschema/1-0-0',
        data: sessionDetailResumeStarted
    }
}
/**
 * Track a Snowplow event for FormFillingDownloaded.
 * Tracks form filling downloaded event
 */
export function trackFormFillingDownloaded<T extends {} = any>(formFillingDownloaded: FormFillingDownloaded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = formFillingDownloaded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/form_filling_downloaded/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FormFillingDownloaded entity.
 */
export function createFormFillingDownloaded(formFillingDownloaded: FormFillingDownloaded){
    return {
        schema: 'iglu:com.heidi/form_filling_downloaded/jsonschema/1-0-2',
        data: formFillingDownloaded
    }
}
/**
 * Track a Snowplow event for ObjectiveSelected.
 * Tracks when a user selects an objective
 */
export function trackObjectiveSelected<T extends {} = any>(objectiveSelected: ObjectiveSelected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = objectiveSelected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/objective_selected/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ObjectiveSelected entity.
 */
export function createObjectiveSelected(objectiveSelected: ObjectiveSelected){
    return {
        schema: 'iglu:com.heidi/objective_selected/jsonschema/1-0-0',
        data: objectiveSelected
    }
}
/**
 * Track a Snowplow event for MessageSubmitted.
 * Tracks when a user submits a message
 */
export function trackMessageSubmitted<T extends {} = any>(messageSubmitted: MessageSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = messageSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/message_submitted/jsonschema/3-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow MessageSubmitted entity.
 */
export function createMessageSubmitted(messageSubmitted: MessageSubmitted){
    return {
        schema: 'iglu:com.heidi/message_submitted/jsonschema/3-0-0',
        data: messageSubmitted
    }
}
/**
 * Track a Snowplow event for BookDemoCliniciansSelected.
 * Tracks book demo clinicians selected event
 */
export function trackBookDemoCliniciansSelected<T extends {} = any>(bookDemoCliniciansSelected: BookDemoCliniciansSelected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = bookDemoCliniciansSelected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/book_demo_clinicians_selected/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow BookDemoCliniciansSelected entity.
 */
export function createBookDemoCliniciansSelected(bookDemoCliniciansSelected: BookDemoCliniciansSelected){
    return {
        schema: 'iglu:com.heidi/book_demo_clinicians_selected/jsonschema/1-0-1',
        data: bookDemoCliniciansSelected
    }
}
/**
 * Track a Snowplow event for OnboardingUpdated.
 * Tracks onboarding updated event
 */
export function trackOnboardingUpdated<T extends {} = any>(onboardingUpdated: OnboardingUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = onboardingUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/onboarding_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OnboardingUpdated entity.
 */
export function createOnboardingUpdated(onboardingUpdated: OnboardingUpdated){
    return {
        schema: 'iglu:com.heidi/onboarding_updated/jsonschema/1-0-1',
        data: onboardingUpdated
    }
}
/**
 * Track a Snowplow event for ExtractionModalOpened.
 * Tracks when the data extraction modal is opened
 */
export function trackExtractionModalOpened<T extends {} = any>(extractionModalOpened: ExtractionModalOpened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = extractionModalOpened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/extraction_modal_opened/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ExtractionModalOpened entity.
 */
export function createExtractionModalOpened(extractionModalOpened: ExtractionModalOpened){
    return {
        schema: 'iglu:com.heidi/extraction_modal_opened/jsonschema/1-0-1',
        data: extractionModalOpened
    }
}
/**
 * Track a Snowplow event for EvidenceLiveSuggestionsToggled.
 * Tracks when live suggestions are toggled on or off
 */
export function trackEvidenceLiveSuggestionsToggled<T extends {} = any>(evidenceLiveSuggestionsToggled: EvidenceLiveSuggestionsToggled & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceLiveSuggestionsToggled;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_live_suggestions_toggled/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceLiveSuggestionsToggled entity.
 */
export function createEvidenceLiveSuggestionsToggled(evidenceLiveSuggestionsToggled: EvidenceLiveSuggestionsToggled){
    return {
        schema: 'iglu:com.heidi/evidence_live_suggestions_toggled/jsonschema/2-0-2',
        data: evidenceLiveSuggestionsToggled
    }
}
/**
 * Track a Snowplow event for ChronicleFirmwareUpgradeStarted.
 * Tracks when a Chronicle device firmware upgrade is started
 */
export function trackChronicleFirmwareUpgradeStarted<T extends {} = any>(chronicleFirmwareUpgradeStarted: ChronicleFirmwareUpgradeStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chronicleFirmwareUpgradeStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/chronicle_firmware_upgrade_started/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChronicleFirmwareUpgradeStarted entity.
 */
export function createChronicleFirmwareUpgradeStarted(chronicleFirmwareUpgradeStarted: ChronicleFirmwareUpgradeStarted){
    return {
        schema: 'iglu:com.heidi/chronicle_firmware_upgrade_started/jsonschema/1-0-1',
        data: chronicleFirmwareUpgradeStarted
    }
}
/**
 * Track a Snowplow event for TranslatorPreferencesUpdated.
 * Fired when a user updates a translator preference setting
 */
export function trackTranslatorPreferencesUpdated<T extends {} = any>(translatorPreferencesUpdated: TranslatorPreferencesUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = translatorPreferencesUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/translator_preferences_updated/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TranslatorPreferencesUpdated entity.
 */
export function createTranslatorPreferencesUpdated(translatorPreferencesUpdated: TranslatorPreferencesUpdated){
    return {
        schema: 'iglu:com.heidi/translator_preferences_updated/jsonschema/1-0-0',
        data: translatorPreferencesUpdated
    }
}
/**
 * Track a Snowplow event for ConversationAutoResolved.
 * Tracks when a conversation is automatically resolved
 */
export function trackConversationAutoResolved<T extends {} = any>(conversationAutoResolved: ConversationAutoResolved & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = conversationAutoResolved;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/conversation_auto_resolved/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ConversationAutoResolved entity.
 */
export function createConversationAutoResolved(conversationAutoResolved: ConversationAutoResolved){
    return {
        schema: 'iglu:com.heidi/conversation_auto_resolved/jsonschema/1-0-0',
        data: conversationAutoResolved
    }
}
/**
 * Track a Snowplow event for ForceUpdateInitiated.
 * Tracks force update initiated event
 */
export function trackForceUpdateInitiated<T extends {} = any>(forceUpdateInitiated: ForceUpdateInitiated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = forceUpdateInitiated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/force_update_initiated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ForceUpdateInitiated entity.
 */
export function createForceUpdateInitiated(forceUpdateInitiated: ForceUpdateInitiated){
    return {
        schema: 'iglu:com.heidi/force_update_initiated/jsonschema/1-0-1',
        data: forceUpdateInitiated
    }
}
/**
 * Track a Snowplow event for IntegrationWidgetAuthStepCompleted.
 * Tracks integration widget auth step completed event
 */
export function trackIntegrationWidgetAuthStepCompleted<T extends {} = any>(integrationWidgetAuthStepCompleted: IntegrationWidgetAuthStepCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationWidgetAuthStepCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_widget_auth_step_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationWidgetAuthStepCompleted entity.
 */
export function createIntegrationWidgetAuthStepCompleted(integrationWidgetAuthStepCompleted: IntegrationWidgetAuthStepCompleted){
    return {
        schema: 'iglu:com.heidi/integration_widget_auth_step_completed/jsonschema/1-0-1',
        data: integrationWidgetAuthStepCompleted
    }
}
/**
 * Track a Snowplow event for UserCreatedViaInternalAdmin.
 * Tracks when a user account is created via the internal admin tool
 */
export function trackUserCreatedViaInternalAdmin<T extends {} = any>(userCreatedViaInternalAdmin: UserCreatedViaInternalAdmin & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = userCreatedViaInternalAdmin;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/user_created_via_internal_admin/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UserCreatedViaInternalAdmin entity.
 */
export function createUserCreatedViaInternalAdmin(userCreatedViaInternalAdmin: UserCreatedViaInternalAdmin){
    return {
        schema: 'iglu:com.heidi/user_created_via_internal_admin/jsonschema/2-0-0',
        data: userCreatedViaInternalAdmin
    }
}
/**
 * Track a Snowplow event for IntegrationDataFailed.
 * Tracks when integration data retrieval fails
 */
export function trackIntegrationDataFailed<T extends {} = any>(integrationDataFailed: IntegrationDataFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationDataFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_data_failed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationDataFailed entity.
 */
export function createIntegrationDataFailed(integrationDataFailed: IntegrationDataFailed){
    return {
        schema: 'iglu:com.heidi/integration_data_failed/jsonschema/1-0-1',
        data: integrationDataFailed
    }
}
/**
 * Track a Snowplow event for EhrLaunchPatientCompleted.
 * Tracks patient details retrieval
 */
export function trackEhrLaunchPatientCompleted<T extends {} = any>(ehrLaunchPatientCompleted: EhrLaunchPatientCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = ehrLaunchPatientCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/ehr_launch_patient_completed/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EhrLaunchPatientCompleted entity.
 */
export function createEhrLaunchPatientCompleted(ehrLaunchPatientCompleted: EhrLaunchPatientCompleted){
    return {
        schema: 'iglu:com.heidi/ehr_launch_patient_completed/jsonschema/1-0-3',
        data: ehrLaunchPatientCompleted
    }
}
/**
 * Track a Snowplow event for PatientFieldExtractionSurfaced.
 * Tracks when extracted patient fields are first shown to the user on the profile
 */
export function trackPatientFieldExtractionSurfaced<T extends {} = any>(patientFieldExtractionSurfaced: PatientFieldExtractionSurfaced & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientFieldExtractionSurfaced;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_field_extraction_surfaced/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientFieldExtractionSurfaced entity.
 */
export function createPatientFieldExtractionSurfaced(patientFieldExtractionSurfaced: PatientFieldExtractionSurfaced){
    return {
        schema: 'iglu:com.heidi/patient_field_extraction_surfaced/jsonschema/1-0-0',
        data: patientFieldExtractionSurfaced
    }
}
/**
 * Track a Snowplow event for LetterAttachmentUploaded.
 * Tracks a successful letter attachment upload
 */
export function trackLetterAttachmentUploaded<T extends {} = any>(letterAttachmentUploaded: LetterAttachmentUploaded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = letterAttachmentUploaded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/letter_attachment_uploaded/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LetterAttachmentUploaded entity.
 */
export function createLetterAttachmentUploaded(letterAttachmentUploaded: LetterAttachmentUploaded){
    return {
        schema: 'iglu:com.heidi/letter_attachment_uploaded/jsonschema/1-0-0',
        data: letterAttachmentUploaded
    }
}
/**
 * Track a Snowplow event for NudgeCardAdded.
 * A nudge card was manually added by the clinician from the protocol library (i.e. the classifier did not auto-surface it)
 */
export function trackNudgeCardAdded<T extends {} = any>(nudgeCardAdded: NudgeCardAdded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = nudgeCardAdded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/nudge_card_added/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NudgeCardAdded entity.
 */
export function createNudgeCardAdded(nudgeCardAdded: NudgeCardAdded){
    return {
        schema: 'iglu:com.heidi/nudge_card_added/jsonschema/1-0-0',
        data: nudgeCardAdded
    }
}
/**
 * Track a Snowplow event for ResponseCreated.
 * Tracks when AI response is regenerated
 */
export function trackResponseCreated<T extends {} = any>(responseCreated: ResponseCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = responseCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/response_created/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ResponseCreated entity.
 */
export function createResponseCreated(responseCreated: ResponseCreated){
    return {
        schema: 'iglu:com.heidi/response_created/jsonschema/1-0-3',
        data: responseCreated
    }
}
/**
 * Track a Snowplow event for WorkToolCompleted.
 * Agent tool finished (success or failure)
 */
export function trackWorkToolCompleted<T extends {} = any>(workToolCompleted: WorkToolCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workToolCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_tool_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkToolCompleted entity.
 */
export function createWorkToolCompleted(workToolCompleted: WorkToolCompleted){
    return {
        schema: 'iglu:com.heidi/work_tool_completed/jsonschema/1-0-1',
        data: workToolCompleted
    }
}
/**
 * Track a Snowplow event for NativeAppOffline.
 * Tracks a native client entering a confirmed offline or backend-unreachable state
 */
export function trackNativeAppOffline<T extends {} = any>(nativeAppOffline: NativeAppOffline & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = nativeAppOffline;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/native_app_offline/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NativeAppOffline entity.
 */
export function createNativeAppOffline(nativeAppOffline: NativeAppOffline){
    return {
        schema: 'iglu:com.heidi/native_app_offline/jsonschema/1-0-0',
        data: nativeAppOffline
    }
}
/**
 * Track a Snowplow event for LibraryCollectionCreated.
 * Tracks library collection created event
 */
export function trackLibraryCollectionCreated<T extends {} = any>(libraryCollectionCreated: LibraryCollectionCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = libraryCollectionCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/library_collection_created/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LibraryCollectionCreated entity.
 */
export function createLibraryCollectionCreated(libraryCollectionCreated: LibraryCollectionCreated){
    return {
        schema: 'iglu:com.heidi/library_collection_created/jsonschema/1-0-1',
        data: libraryCollectionCreated
    }
}
/**
 * Track a Snowplow event for TaskListViewed.
 * Tracks when the global task list is opened, capturing active filters and sort state
 */
export function trackTaskListViewed<T extends {} = any>(taskListViewed: TaskListViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = taskListViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/task_list_viewed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TaskListViewed entity.
 */
export function createTaskListViewed(taskListViewed: TaskListViewed){
    return {
        schema: 'iglu:com.heidi/task_list_viewed/jsonschema/1-0-0',
        data: taskListViewed
    }
}
/**
 * Track a Snowplow event for AuthFallbackUsed.
 * Tracks when an authentication fallback method is used
 */
export function trackAuthFallbackUsed<T extends {} = any>(authFallbackUsed: AuthFallbackUsed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = authFallbackUsed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/auth_fallback_used/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AuthFallbackUsed entity.
 */
export function createAuthFallbackUsed(authFallbackUsed: AuthFallbackUsed){
    return {
        schema: 'iglu:com.heidi/auth_fallback_used/jsonschema/1-0-1',
        data: authFallbackUsed
    }
}
/**
 * Track a Snowplow event for LetterRecipientAdded.
 * Tracks when a recipient is added to a letter
 */
export function trackLetterRecipientAdded<T extends {} = any>(letterRecipientAdded: LetterRecipientAdded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = letterRecipientAdded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/letter_recipient_added/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LetterRecipientAdded entity.
 */
export function createLetterRecipientAdded(letterRecipientAdded: LetterRecipientAdded){
    return {
        schema: 'iglu:com.heidi/letter_recipient_added/jsonschema/1-0-0',
        data: letterRecipientAdded
    }
}
/**
 * Track a Snowplow event for ContactSalesFormOpened.
 * Tracks contact sales form opened event
 */
export function trackContactSalesFormOpened<T extends {} = any>(contactSalesFormOpened: ContactSalesFormOpened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = contactSalesFormOpened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/contact_sales_form_opened/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ContactSalesFormOpened entity.
 */
export function createContactSalesFormOpened(contactSalesFormOpened: ContactSalesFormOpened){
    return {
        schema: 'iglu:com.heidi/contact_sales_form_opened/jsonschema/1-0-1',
        data: contactSalesFormOpened
    }
}
/**
 * Track a Snowplow event for ScreenViewed.
 * Tracks when screen or page is viewed
 */
export function trackScreenViewed<T extends {} = any>(screenViewed: ScreenViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = screenViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/screen_viewed/jsonschema/2-0-7',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScreenViewed entity.
 */
export function createScreenViewed(screenViewed: ScreenViewed){
    return {
        schema: 'iglu:com.heidi/screen_viewed/jsonschema/2-0-7',
        data: screenViewed
    }
}
/**
 * Track a Snowplow event for AppStoreReviewSentiment.
 * Fired when the user answers the App Store review sentiment gate
 */
export function trackAppStoreReviewSentiment<T extends {} = any>(appStoreReviewSentiment: AppStoreReviewSentiment & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = appStoreReviewSentiment;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/app_store_review_sentiment/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AppStoreReviewSentiment entity.
 */
export function createAppStoreReviewSentiment(appStoreReviewSentiment: AppStoreReviewSentiment){
    return {
        schema: 'iglu:com.heidi/app_store_review_sentiment/jsonschema/1-0-0',
        data: appStoreReviewSentiment
    }
}
/**
 * Track a Snowplow event for WorkSurfaceActioned.
 * User viewed or interacted with a Work entry surface
 */
export function trackWorkSurfaceActioned<T extends {} = any>(workSurfaceActioned: WorkSurfaceActioned & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workSurfaceActioned;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_surface_actioned/jsonschema/1-1-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkSurfaceActioned entity.
 */
export function createWorkSurfaceActioned(workSurfaceActioned: WorkSurfaceActioned){
    return {
        schema: 'iglu:com.heidi/work_surface_actioned/jsonschema/1-1-2',
        data: workSurfaceActioned
    }
}
/**
 * Track a Snowplow event for WorkErrorEncountered.
 * Work error surfaced (client or server)
 */
export function trackWorkErrorEncountered<T extends {} = any>(workErrorEncountered: WorkErrorEncountered & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workErrorEncountered;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_error_encountered/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkErrorEncountered entity.
 */
export function createWorkErrorEncountered(workErrorEncountered: WorkErrorEncountered){
    return {
        schema: 'iglu:com.heidi/work_error_encountered/jsonschema/1-0-0',
        data: workErrorEncountered
    }
}
/**
 * Track a Snowplow event for PatientSearched.
 * Tracks patient searched event
 */
export function trackPatientSearched<T extends {} = any>(patientSearched: PatientSearched & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientSearched;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_searched/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientSearched entity.
 */
export function createPatientSearched(patientSearched: PatientSearched){
    return {
        schema: 'iglu:com.heidi/patient_searched/jsonschema/2-0-0',
        data: patientSearched
    }
}
/**
 * Track a Snowplow event for TaskAssignmentUpdated.
 * Tracks when a task assignee changes, capturing previous and new assignee plus the action type
 */
export function trackTaskAssignmentUpdated<T extends {} = any>(taskAssignmentUpdated: TaskAssignmentUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = taskAssignmentUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/task_assignment_updated/jsonschema/1-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TaskAssignmentUpdated entity.
 */
export function createTaskAssignmentUpdated(taskAssignmentUpdated: TaskAssignmentUpdated){
    return {
        schema: 'iglu:com.heidi/task_assignment_updated/jsonschema/1-1-0',
        data: taskAssignmentUpdated
    }
}
/**
 * Track a Snowplow event for InboundCallReceived.
 * Tracks when an inbound call is received by the platform
 */
export function trackInboundCallReceived<T extends {} = any>(inboundCallReceived: InboundCallReceived & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = inboundCallReceived;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/inbound_call_received/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow InboundCallReceived entity.
 */
export function createInboundCallReceived(inboundCallReceived: InboundCallReceived){
    return {
        schema: 'iglu:com.heidi/inbound_call_received/jsonschema/1-0-0',
        data: inboundCallReceived
    }
}
/**
 * Track a Snowplow event for TemplateSearched.
 * Tracks template searched event
 */
export function trackTemplateSearched<T extends {} = any>(templateSearched: TemplateSearched & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateSearched;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_searched/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateSearched entity.
 */
export function createTemplateSearched(templateSearched: TemplateSearched){
    return {
        schema: 'iglu:com.heidi/template_searched/jsonschema/1-0-3',
        data: templateSearched
    }
}
/**
 * Track a Snowplow event for DoNotContactBlockedSend.
 * Tracks when an outbound message or call is blocked by a do-not-contact rule
 */
export function trackDoNotContactBlockedSend<T extends {} = any>(doNotContactBlockedSend: DoNotContactBlockedSend & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = doNotContactBlockedSend;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/do_not_contact_blocked_send/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DoNotContactBlockedSend entity.
 */
export function createDoNotContactBlockedSend(doNotContactBlockedSend: DoNotContactBlockedSend){
    return {
        schema: 'iglu:com.heidi/do_not_contact_blocked_send/jsonschema/1-0-0',
        data: doNotContactBlockedSend
    }
}
/**
 * Track a Snowplow event for InboxSessionStarted.
 * Tracks when a user starts an inbox session
 */
export function trackInboxSessionStarted<T extends {} = any>(inboxSessionStarted: InboxSessionStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = inboxSessionStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/inbox_session_started/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow InboxSessionStarted entity.
 */
export function createInboxSessionStarted(inboxSessionStarted: InboxSessionStarted){
    return {
        schema: 'iglu:com.heidi/inbox_session_started/jsonschema/1-0-0',
        data: inboxSessionStarted
    }
}
/**
 * Track a Snowplow event for EvidenceSignupWallViewed.
 * Tracks evidence signup wall viewed event
 */
export function trackEvidenceSignupWallViewed<T extends {} = any>(evidenceSignupWallViewed: EvidenceSignupWallViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceSignupWallViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_signup_wall_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceSignupWallViewed entity.
 */
export function createEvidenceSignupWallViewed(evidenceSignupWallViewed: EvidenceSignupWallViewed){
    return {
        schema: 'iglu:com.heidi/evidence_signup_wall_viewed/jsonschema/1-0-1',
        data: evidenceSignupWallViewed
    }
}
/**
 * Track a Snowplow event for BookDemoNextClicked.
 * Tracks book demo next clicked event
 */
export function trackBookDemoNextClicked<T extends {} = any>(bookDemoNextClicked: BookDemoNextClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = bookDemoNextClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/book_demo_next_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow BookDemoNextClicked entity.
 */
export function createBookDemoNextClicked(bookDemoNextClicked: BookDemoNextClicked){
    return {
        schema: 'iglu:com.heidi/book_demo_next_clicked/jsonschema/1-0-1',
        data: bookDemoNextClicked
    }
}
/**
 * Track a Snowplow event for NoteGenerationCompleted.
 * Consult note generation completed for a session
 */
export function trackNoteGenerationCompleted<T extends {} = any>(noteGenerationCompleted: NoteGenerationCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteGenerationCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_generation_completed/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteGenerationCompleted entity.
 */
export function createNoteGenerationCompleted(noteGenerationCompleted: NoteGenerationCompleted){
    return {
        schema: 'iglu:com.heidi/note_generation_completed/jsonschema/2-0-2',
        data: noteGenerationCompleted
    }
}
/**
 * Track a Snowplow event for EvidenceContactSalesClicked.
 * Tracks when a team admin clicks contact sales
 */
export function trackEvidenceContactSalesClicked<T extends {} = any>(evidenceContactSalesClicked: EvidenceContactSalesClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceContactSalesClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_contact_sales_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceContactSalesClicked entity.
 */
export function createEvidenceContactSalesClicked(evidenceContactSalesClicked: EvidenceContactSalesClicked){
    return {
        schema: 'iglu:com.heidi/evidence_contact_sales_clicked/jsonschema/1-0-1',
        data: evidenceContactSalesClicked
    }
}
/**
 * Track a Snowplow event for TranslatorUserCreated.
 * Fired when a new translator user account is created
 */
export function trackTranslatorUserCreated<T extends {} = any>(translatorUserCreated: TranslatorUserCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = translatorUserCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/translator_user_created/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TranslatorUserCreated entity.
 */
export function createTranslatorUserCreated(translatorUserCreated: TranslatorUserCreated){
    return {
        schema: 'iglu:com.heidi/translator_user_created/jsonschema/1-0-0',
        data: translatorUserCreated
    }
}
/**
 * Track a Snowplow event for InboxSessionEnded.
 * Tracks when a user ends an inbox session
 */
export function trackInboxSessionEnded<T extends {} = any>(inboxSessionEnded: InboxSessionEnded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = inboxSessionEnded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/inbox_session_ended/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow InboxSessionEnded entity.
 */
export function createInboxSessionEnded(inboxSessionEnded: InboxSessionEnded){
    return {
        schema: 'iglu:com.heidi/inbox_session_ended/jsonschema/1-0-0',
        data: inboxSessionEnded
    }
}
/**
 * Track a Snowplow event for DiarisedTranscriptViewed.
 * Tracks the first settled diarised transcript payload for a clinical session
 */
export function trackDiarisedTranscriptViewed<T extends {} = any>(diarisedTranscriptViewed: DiarisedTranscriptViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = diarisedTranscriptViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/diarised_transcript_viewed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DiarisedTranscriptViewed entity.
 */
export function createDiarisedTranscriptViewed(diarisedTranscriptViewed: DiarisedTranscriptViewed){
    return {
        schema: 'iglu:com.heidi/diarised_transcript_viewed/jsonschema/1-0-0',
        data: diarisedTranscriptViewed
    }
}
/**
 * Track a Snowplow event for HardwareRecordingStop.
 * Tracks when a hardware recording stops
 */
export function trackHardwareRecordingStop<T extends {} = any>(hardwareRecordingStop: HardwareRecordingStop & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = hardwareRecordingStop;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/hardware_recording_stop/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow HardwareRecordingStop entity.
 */
export function createHardwareRecordingStop(hardwareRecordingStop: HardwareRecordingStop){
    return {
        schema: 'iglu:com.heidi/hardware_recording_stop/jsonschema/1-0-1',
        data: hardwareRecordingStop
    }
}
/**
 * Track a Snowplow event for ShareMemberSearched.
 * Tracks when a member search is performed in the share modal
 */
export function trackShareMemberSearched<T extends {} = any>(shareMemberSearched: ShareMemberSearched & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareMemberSearched;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_member_searched/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareMemberSearched entity.
 */
export function createShareMemberSearched(shareMemberSearched: ShareMemberSearched){
    return {
        schema: 'iglu:com.heidi/share_member_searched/jsonschema/1-0-1',
        data: shareMemberSearched
    }
}
/**
 * Track a Snowplow event for LetterSigned.
 * Tracks successful application of a signature to a letter
 */
export function trackLetterSigned<T extends {} = any>(letterSigned: LetterSigned & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = letterSigned;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/letter_signed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LetterSigned entity.
 */
export function createLetterSigned(letterSigned: LetterSigned){
    return {
        schema: 'iglu:com.heidi/letter_signed/jsonschema/1-0-0',
        data: letterSigned
    }
}
/**
 * Track a Snowplow event for SectionCreated.
 * Tracks when a section is created in the note, template, or document editor
 */
export function trackSectionCreated<T extends {} = any>(sectionCreated: SectionCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sectionCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/section_created/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SectionCreated entity.
 */
export function createSectionCreated(sectionCreated: SectionCreated){
    return {
        schema: 'iglu:com.heidi/section_created/jsonschema/1-0-2',
        data: sectionCreated
    }
}
/**
 * Track a Snowplow event for DeviceEvent.
 * Device Event — consolidated hardware device interactions across recording devices and peripherals
 */
export function trackDeviceEvent<T extends {} = any>(deviceEvent: DeviceEvent & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = deviceEvent;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/device_event/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DeviceEvent entity.
 */
export function createDeviceEvent(deviceEvent: DeviceEvent){
    return {
        schema: 'iglu:com.heidi/device_event/jsonschema/1-0-0',
        data: deviceEvent
    }
}
/**
 * Track a Snowplow event for EvidenceSpecialtyChanged.
 * Tracks when the evidence specialty is changed
 */
export function trackEvidenceSpecialtyChanged<T extends {} = any>(evidenceSpecialtyChanged: EvidenceSpecialtyChanged & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceSpecialtyChanged;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_specialty_changed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceSpecialtyChanged entity.
 */
export function createEvidenceSpecialtyChanged(evidenceSpecialtyChanged: EvidenceSpecialtyChanged){
    return {
        schema: 'iglu:com.heidi/evidence_specialty_changed/jsonschema/1-0-1',
        data: evidenceSpecialtyChanged
    }
}
/**
 * Track a Snowplow event for UserSignInFailed.
 * Tracks when a desktop Auth0 sign-in fails (e.g. the deep-link token-exchange could not reach Auth0 on an enterprise network). Emitted by the desktop client; mirrors user_signed_in plus sanitized failure context (APP-6076).
 */
export function trackUserSignInFailed<T extends {} = any>(userSignInFailed: UserSignInFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = userSignInFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/user_sign_in_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UserSignInFailed entity.
 */
export function createUserSignInFailed(userSignInFailed: UserSignInFailed){
    return {
        schema: 'iglu:com.heidi/user_sign_in_failed/jsonschema/1-0-0',
        data: userSignInFailed
    }
}
/**
 * Track a Snowplow event for ObjectiveValidationFailed.
 * Tracks when an AI objective output fails validation
 */
export function trackObjectiveValidationFailed<T extends {} = any>(objectiveValidationFailed: ObjectiveValidationFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = objectiveValidationFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/objective_validation_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ObjectiveValidationFailed entity.
 */
export function createObjectiveValidationFailed(objectiveValidationFailed: ObjectiveValidationFailed){
    return {
        schema: 'iglu:com.heidi/objective_validation_failed/jsonschema/1-0-0',
        data: objectiveValidationFailed
    }
}
/**
 * Track a Snowplow event for PersonalizationCompleted.
 * Tracks when a personalisation insight is successfully generated
 */
export function trackPersonalizationCompleted<T extends {} = any>(personalizationCompleted: PersonalizationCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = personalizationCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/personalization_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PersonalizationCompleted entity.
 */
export function createPersonalizationCompleted(personalizationCompleted: PersonalizationCompleted){
    return {
        schema: 'iglu:com.heidi/personalization_completed/jsonschema/1-0-1',
        data: personalizationCompleted
    }
}
/**
 * Track a Snowplow event for SessionsSortChanged.
 * Tracks when the user changes the session list sort
 */
export function trackSessionsSortChanged<T extends {} = any>(sessionsSortChanged: SessionsSortChanged & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionsSortChanged;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/sessions_sort_changed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionsSortChanged entity.
 */
export function createSessionsSortChanged(sessionsSortChanged: SessionsSortChanged){
    return {
        schema: 'iglu:com.heidi/sessions_sort_changed/jsonschema/1-0-0',
        data: sessionsSortChanged
    }
}
/**
 * Track a Snowplow event for EvidenceExperienceFeedbackSnoozed.
 * Tracks when a user snoozes the experience feedback nudge
 */
export function trackEvidenceExperienceFeedbackSnoozed<T extends {} = any>(evidenceExperienceFeedbackSnoozed: EvidenceExperienceFeedbackSnoozed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceExperienceFeedbackSnoozed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_experience_feedback_snoozed/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceExperienceFeedbackSnoozed entity.
 */
export function createEvidenceExperienceFeedbackSnoozed(evidenceExperienceFeedbackSnoozed: EvidenceExperienceFeedbackSnoozed){
    return {
        schema: 'iglu:com.heidi/evidence_experience_feedback_snoozed/jsonschema/2-0-2',
        data: evidenceExperienceFeedbackSnoozed
    }
}
/**
 * Track a Snowplow event for PersonalizationToggled.
 * Tracks when personalization settings are updated
 */
export function trackPersonalizationToggled<T extends {} = any>(personalizationToggled: PersonalizationToggled & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = personalizationToggled;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/personalization_toggled/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PersonalizationToggled entity.
 */
export function createPersonalizationToggled(personalizationToggled: PersonalizationToggled){
    return {
        schema: 'iglu:com.heidi/personalization_toggled/jsonschema/2-0-3',
        data: personalizationToggled
    }
}
/**
 * Track a Snowplow event for IntegrationFailed.
 * Tracks integration failed event
 */
export function trackIntegrationFailed<T extends {} = any>(integrationFailed: IntegrationFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_failed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationFailed entity.
 */
export function createIntegrationFailed(integrationFailed: IntegrationFailed){
    return {
        schema: 'iglu:com.heidi/integration_failed/jsonschema/1-0-1',
        data: integrationFailed
    }
}
/**
 * Track a Snowplow event for ReferralCreated.
 * Tracks referral created event
 */
export function trackReferralCreated<T extends {} = any>(referralCreated: ReferralCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = referralCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/referral_created/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ReferralCreated entity.
 */
export function createReferralCreated(referralCreated: ReferralCreated){
    return {
        schema: 'iglu:com.heidi/referral_created/jsonschema/1-0-1',
        data: referralCreated
    }
}
/**
 * Track a Snowplow event for CpdPageViewed.
 * Tracks cpd page viewed event
 */
export function trackCpdPageViewed<T extends {} = any>(cpdPageViewed: CpdPageViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = cpdPageViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/cpd_page_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CpdPageViewed entity.
 */
export function createCpdPageViewed(cpdPageViewed: CpdPageViewed){
    return {
        schema: 'iglu:com.heidi/cpd_page_viewed/jsonschema/1-0-1',
        data: cpdPageViewed
    }
}
/**
 * Track a Snowplow event for WorkExperienceFeedbackActioned.
 * Work UMUX-Lite experience feedback nudge lifecycle action
 */
export function trackWorkExperienceFeedbackActioned<T extends {} = any>(workExperienceFeedbackActioned: WorkExperienceFeedbackActioned & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workExperienceFeedbackActioned;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_experience_feedback_actioned/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkExperienceFeedbackActioned entity.
 */
export function createWorkExperienceFeedbackActioned(workExperienceFeedbackActioned: WorkExperienceFeedbackActioned){
    return {
        schema: 'iglu:com.heidi/work_experience_feedback_actioned/jsonschema/1-0-2',
        data: workExperienceFeedbackActioned
    }
}
/**
 * Track a Snowplow event for CallCompleted.
 * Tracks when a call ends normally
 */
export function trackCallCompleted<T extends {} = any>(callCompleted: CallCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_completed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallCompleted entity.
 */
export function createCallCompleted(callCompleted: CallCompleted){
    return {
        schema: 'iglu:com.heidi/call_completed/jsonschema/1-0-0',
        data: callCompleted
    }
}
/**
 * Track a Snowplow event for ShareExternalConfirmed.
 * Tracks when an external share is confirmed
 */
export function trackShareExternalConfirmed<T extends {} = any>(shareExternalConfirmed: ShareExternalConfirmed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareExternalConfirmed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_external_confirmed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareExternalConfirmed entity.
 */
export function createShareExternalConfirmed(shareExternalConfirmed: ShareExternalConfirmed){
    return {
        schema: 'iglu:com.heidi/share_external_confirmed/jsonschema/1-0-1',
        data: shareExternalConfirmed
    }
}
/**
 * Track a Snowplow event for InboxItemViewed.
 * Tracks when a user views an inbox conversation item
 */
export function trackInboxItemViewed<T extends {} = any>(inboxItemViewed: InboxItemViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = inboxItemViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/inbox_item_viewed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow InboxItemViewed entity.
 */
export function createInboxItemViewed(inboxItemViewed: InboxItemViewed){
    return {
        schema: 'iglu:com.heidi/inbox_item_viewed/jsonschema/1-0-0',
        data: inboxItemViewed
    }
}
/**
 * Track a Snowplow event for UserClicked.
 * Tracks user clicked event
 */
export function trackUserClicked<T extends {} = any>(userClicked: UserClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = userClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/user_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UserClicked entity.
 */
export function createUserClicked(userClicked: UserClicked){
    return {
        schema: 'iglu:com.heidi/user_clicked/jsonschema/1-0-1',
        data: userClicked
    }
}
/**
 * Track a Snowplow event for CommunitySearched.
 * Tracks community searched event
 */
export function trackCommunitySearched<T extends {} = any>(communitySearched: CommunitySearched & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = communitySearched;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/community_searched/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CommunitySearched entity.
 */
export function createCommunitySearched(communitySearched: CommunitySearched){
    return {
        schema: 'iglu:com.heidi/community_searched/jsonschema/1-0-1',
        data: communitySearched
    }
}
/**
 * Track a Snowplow event for CalculatorExecuted.
 * Tracks successful clinical calculator executions
 */
export function trackCalculatorExecuted<T extends {} = any>(calculatorExecuted: CalculatorExecuted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = calculatorExecuted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/calculator_executed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CalculatorExecuted entity.
 */
export function createCalculatorExecuted(calculatorExecuted: CalculatorExecuted){
    return {
        schema: 'iglu:com.heidi/calculator_executed/jsonschema/1-0-1',
        data: calculatorExecuted
    }
}
/**
 * Track a Snowplow event for NotePushed.
 * Tracks when note is pushed to EHR/integration
 */
export function trackNotePushed<T extends {} = any>(notePushed: NotePushed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = notePushed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_pushed/jsonschema/3-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NotePushed entity.
 */
export function createNotePushed(notePushed: NotePushed){
    return {
        schema: 'iglu:com.heidi/note_pushed/jsonschema/3-0-0',
        data: notePushed
    }
}
/**
 * Track a Snowplow event for PaidActionTracked.
 * Tracks when a user performs an action that requires a paid plan
 */
export function trackPaidActionTracked<T extends {} = any>(paidActionTracked: PaidActionTracked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = paidActionTracked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/paid_action_tracked/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PaidActionTracked entity.
 */
export function createPaidActionTracked(paidActionTracked: PaidActionTracked){
    return {
        schema: 'iglu:com.heidi/paid_action_tracked/jsonschema/1-0-0',
        data: paidActionTracked
    }
}
/**
 * Track a Snowplow event for PatientExtracted.
 * Tracks when patient data is successfully extracted from a session
 */
export function trackPatientExtracted<T extends {} = any>(patientExtracted: PatientExtracted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientExtracted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_extracted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientExtracted entity.
 */
export function createPatientExtracted(patientExtracted: PatientExtracted){
    return {
        schema: 'iglu:com.heidi/patient_extracted/jsonschema/1-0-1',
        data: patientExtracted
    }
}
/**
 * Track a Snowplow event for UIInteraction.
 * Tracks semantic interactions with UI elements that do not map to a more specific domain event
 */
export function trackUIInteraction<T extends {} = any>(uIInteraction: UIInteraction & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = uIInteraction;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/ui_interaction/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UIInteraction entity.
 */
export function createUIInteraction(uIInteraction: UIInteraction){
    return {
        schema: 'iglu:com.heidi/ui_interaction/jsonschema/1-0-1',
        data: uIInteraction
    }
}
/**
 * Track a Snowplow event for PersonalizationDeleted.
 * Tracks when a personalisation insight is deleted
 */
export function trackPersonalizationDeleted<T extends {} = any>(personalizationDeleted: PersonalizationDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = personalizationDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/personalization_deleted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PersonalizationDeleted entity.
 */
export function createPersonalizationDeleted(personalizationDeleted: PersonalizationDeleted){
    return {
        schema: 'iglu:com.heidi/personalization_deleted/jsonschema/1-0-1',
        data: personalizationDeleted
    }
}
/**
 * Track a Snowplow event for IntegrationRequested.
 * Tracks when a user requests an integration
 */
export function trackIntegrationRequested<T extends {} = any>(integrationRequested: IntegrationRequested & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationRequested;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_requested/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationRequested entity.
 */
export function createIntegrationRequested(integrationRequested: IntegrationRequested){
    return {
        schema: 'iglu:com.heidi/integration_requested/jsonschema/1-0-0',
        data: integrationRequested
    }
}
/**
 * Track a Snowplow event for PatientProfileTabViewed.
 * Tracks when a tab is activated inside an open patient profile
 */
export function trackPatientProfileTabViewed<T extends {} = any>(patientProfileTabViewed: PatientProfileTabViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientProfileTabViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_profile_tab_viewed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientProfileTabViewed entity.
 */
export function createPatientProfileTabViewed(patientProfileTabViewed: PatientProfileTabViewed){
    return {
        schema: 'iglu:com.heidi/patient_profile_tab_viewed/jsonschema/1-0-0',
        data: patientProfileTabViewed
    }
}
/**
 * Track a Snowplow event for FieldSelected.
 * Tracks form field interactions
 */
export function trackFieldSelected<T extends {} = any>(fieldSelected: FieldSelected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = fieldSelected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/field_selected/jsonschema/1-1-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FieldSelected entity.
 */
export function createFieldSelected(fieldSelected: FieldSelected){
    return {
        schema: 'iglu:com.heidi/field_selected/jsonschema/1-1-1',
        data: fieldSelected
    }
}
/**
 * Track a Snowplow event for ChronicleDeviceSearchStarted.
 * Tracks chronicle device search started event
 */
export function trackChronicleDeviceSearchStarted<T extends {} = any>(chronicleDeviceSearchStarted: ChronicleDeviceSearchStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chronicleDeviceSearchStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/chronicle_device_search_started/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChronicleDeviceSearchStarted entity.
 */
export function createChronicleDeviceSearchStarted(chronicleDeviceSearchStarted: ChronicleDeviceSearchStarted){
    return {
        schema: 'iglu:com.heidi/chronicle_device_search_started/jsonschema/1-0-1',
        data: chronicleDeviceSearchStarted
    }
}
/**
 * Track a Snowplow event for LetterAttachmentRejected.
 * Tracks a letter attachment rejected before or during upload
 */
export function trackLetterAttachmentRejected<T extends {} = any>(letterAttachmentRejected: LetterAttachmentRejected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = letterAttachmentRejected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/letter_attachment_rejected/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LetterAttachmentRejected entity.
 */
export function createLetterAttachmentRejected(letterAttachmentRejected: LetterAttachmentRejected){
    return {
        schema: 'iglu:com.heidi/letter_attachment_rejected/jsonschema/1-0-0',
        data: letterAttachmentRejected
    }
}
/**
 * Track a Snowplow event for OnboardingStepSkipped.
 * Tracks when a user skips an optional onboarding step
 */
export function trackOnboardingStepSkipped<T extends {} = any>(onboardingStepSkipped: OnboardingStepSkipped & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = onboardingStepSkipped;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/onboarding_step_skipped/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OnboardingStepSkipped entity.
 */
export function createOnboardingStepSkipped(onboardingStepSkipped: OnboardingStepSkipped){
    return {
        schema: 'iglu:com.heidi/onboarding_step_skipped/jsonschema/1-0-1',
        data: onboardingStepSkipped
    }
}
/**
 * Track a Snowplow event for TrialUpdated.
 * Tracks when trial settings are updated
 */
export function trackTrialUpdated<T extends {} = any>(trialUpdated: TrialUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = trialUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/trial_updated/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TrialUpdated entity.
 */
export function createTrialUpdated(trialUpdated: TrialUpdated){
    return {
        schema: 'iglu:com.heidi/trial_updated/jsonschema/2-0-0',
        data: trialUpdated
    }
}
/**
 * Track a Snowplow event for EvidenceLanguageChanged.
 * Tracks when the evidence language is changed
 */
export function trackEvidenceLanguageChanged<T extends {} = any>(evidenceLanguageChanged: EvidenceLanguageChanged & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceLanguageChanged;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_language_changed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceLanguageChanged entity.
 */
export function createEvidenceLanguageChanged(evidenceLanguageChanged: EvidenceLanguageChanged){
    return {
        schema: 'iglu:com.heidi/evidence_language_changed/jsonschema/1-0-1',
        data: evidenceLanguageChanged
    }
}
/**
 * Track a Snowplow event for IntegrationPatientSelected.
 * Tracks integration patient selected event
 */
export function trackIntegrationPatientSelected<T extends {} = any>(integrationPatientSelected: IntegrationPatientSelected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationPatientSelected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_patient_selected/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationPatientSelected entity.
 */
export function createIntegrationPatientSelected(integrationPatientSelected: IntegrationPatientSelected){
    return {
        schema: 'iglu:com.heidi/integration_patient_selected/jsonschema/1-0-1',
        data: integrationPatientSelected
    }
}
/**
 * Track a Snowplow event for DesktopUpdateSkipped.
 * Fired when the region-aware desktop updater intentionally skips an update without attempting it (fail-closed) — e.g. the updater region could not be resolved, so UK/EU users silently receive no update (APP-6113). This is a deliberate skip, NOT a failure; tracked separately from desktop_update_failed to keep failure-rate metrics clean.
 */
export function trackDesktopUpdateSkipped<T extends {} = any>(desktopUpdateSkipped: DesktopUpdateSkipped & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = desktopUpdateSkipped;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/desktop_update_skipped/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DesktopUpdateSkipped entity.
 */
export function createDesktopUpdateSkipped(desktopUpdateSkipped: DesktopUpdateSkipped){
    return {
        schema: 'iglu:com.heidi/desktop_update_skipped/jsonschema/1-0-0',
        data: desktopUpdateSkipped
    }
}
/**
 * Track a Snowplow event for CallSummaryEmailed.
 * Tracks when a call summary is emailed to recipients
 */
export function trackCallSummaryEmailed<T extends {} = any>(callSummaryEmailed: CallSummaryEmailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callSummaryEmailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_summary_emailed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallSummaryEmailed entity.
 */
export function createCallSummaryEmailed(callSummaryEmailed: CallSummaryEmailed){
    return {
        schema: 'iglu:com.heidi/call_summary_emailed/jsonschema/1-0-0',
        data: callSummaryEmailed
    }
}
/**
 * Track a Snowplow event for CoachMarkViewed.
 * Tracks coach mark viewed event
 */
export function trackCoachMarkViewed<T extends {} = any>(coachMarkViewed: CoachMarkViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = coachMarkViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/coach_mark_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CoachMarkViewed entity.
 */
export function createCoachMarkViewed(coachMarkViewed: CoachMarkViewed){
    return {
        schema: 'iglu:com.heidi/coach_mark_viewed/jsonschema/1-0-1',
        data: coachMarkViewed
    }
}
/**
 * Track a Snowplow event for DictationCompleted.
 * Tracks when dictation completes
 */
export function trackDictationCompleted<T extends {} = any>(dictationCompleted: DictationCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = dictationCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/dictation_completed/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DictationCompleted entity.
 */
export function createDictationCompleted(dictationCompleted: DictationCompleted){
    return {
        schema: 'iglu:com.heidi/dictation_completed/jsonschema/1-0-3',
        data: dictationCompleted
    }
}
/**
 * Track a Snowplow event for ScribeStyleUpdated.
 * Tracks when scribe style is updated
 */
export function trackScribeStyleUpdated<T extends {} = any>(scribeStyleUpdated: ScribeStyleUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = scribeStyleUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/scribe_style_updated/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScribeStyleUpdated entity.
 */
export function createScribeStyleUpdated(scribeStyleUpdated: ScribeStyleUpdated){
    return {
        schema: 'iglu:com.heidi/scribe_style_updated/jsonschema/2-0-3',
        data: scribeStyleUpdated
    }
}
/**
 * Track a Snowplow event for CodeClicked.
 * Tracks when user clicks on a clinical code
 */
export function trackCodeClicked<T extends {} = any>(codeClicked: CodeClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = codeClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/code_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CodeClicked entity.
 */
export function createCodeClicked(codeClicked: CodeClicked){
    return {
        schema: 'iglu:com.heidi/code_clicked/jsonschema/1-0-1',
        data: codeClicked
    }
}
/**
 * Track a Snowplow event for ToggleUpdated.
 * Tracks when a toggle setting is updated
 */
export function trackToggleUpdated<T extends {} = any>(toggleUpdated: ToggleUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = toggleUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/toggle_updated/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ToggleUpdated entity.
 */
export function createToggleUpdated(toggleUpdated: ToggleUpdated){
    return {
        schema: 'iglu:com.heidi/toggle_updated/jsonschema/2-0-2',
        data: toggleUpdated
    }
}
/**
 * Track a Snowplow event for ContextUnlinked.
 * Tracks when context is unlinked
 */
export function trackContextUnlinked<T extends {} = any>(contextUnlinked: ContextUnlinked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = contextUnlinked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/context_unlinked/jsonschema/3-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ContextUnlinked entity.
 */
export function createContextUnlinked(contextUnlinked: ContextUnlinked){
    return {
        schema: 'iglu:com.heidi/context_unlinked/jsonschema/3-0-2',
        data: contextUnlinked
    }
}
/**
 * Track a Snowplow event for ShareExternalEmailAdded.
 * Tracks when an external email address is added to a share
 */
export function trackShareExternalEmailAdded<T extends {} = any>(shareExternalEmailAdded: ShareExternalEmailAdded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareExternalEmailAdded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_external_email_added/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareExternalEmailAdded entity.
 */
export function createShareExternalEmailAdded(shareExternalEmailAdded: ShareExternalEmailAdded){
    return {
        schema: 'iglu:com.heidi/share_external_email_added/jsonschema/1-0-1',
        data: shareExternalEmailAdded
    }
}
/**
 * Track a Snowplow event for TeamMemberUpdated.
 * Tracks when a team member&#x27;s role or settings are updated
 */
export function trackTeamMemberUpdated<T extends {} = any>(teamMemberUpdated: TeamMemberUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = teamMemberUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/team_member_updated/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TeamMemberUpdated entity.
 */
export function createTeamMemberUpdated(teamMemberUpdated: TeamMemberUpdated){
    return {
        schema: 'iglu:com.heidi/team_member_updated/jsonschema/1-0-0',
        data: teamMemberUpdated
    }
}
/**
 * Track a Snowplow event for ChronicleFirmwareUpgradeFailed.
 * Tracks when a Chronicle device firmware upgrade fails
 */
export function trackChronicleFirmwareUpgradeFailed<T extends {} = any>(chronicleFirmwareUpgradeFailed: ChronicleFirmwareUpgradeFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chronicleFirmwareUpgradeFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/chronicle_firmware_upgrade_failed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChronicleFirmwareUpgradeFailed entity.
 */
export function createChronicleFirmwareUpgradeFailed(chronicleFirmwareUpgradeFailed: ChronicleFirmwareUpgradeFailed){
    return {
        schema: 'iglu:com.heidi/chronicle_firmware_upgrade_failed/jsonschema/1-0-1',
        data: chronicleFirmwareUpgradeFailed
    }
}
/**
 * Track a Snowplow event for OrganisationCreated.
 * Tracks when a new organisation is created
 */
export function trackOrganisationCreated<T extends {} = any>(organisationCreated: OrganisationCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = organisationCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/organisation_created/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OrganisationCreated entity.
 */
export function createOrganisationCreated(organisationCreated: OrganisationCreated){
    return {
        schema: 'iglu:com.heidi/organisation_created/jsonschema/1-0-0',
        data: organisationCreated
    }
}
/**
 * Track a Snowplow event for LetterSendAttempted.
 * Tracks a user-confirmed attempt to send a letter
 */
export function trackLetterSendAttempted<T extends {} = any>(letterSendAttempted: LetterSendAttempted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = letterSendAttempted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/letter_send_attempted/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LetterSendAttempted entity.
 */
export function createLetterSendAttempted(letterSendAttempted: LetterSendAttempted){
    return {
        schema: 'iglu:com.heidi/letter_send_attempted/jsonschema/1-0-0',
        data: letterSendAttempted
    }
}
/**
 * Track a Snowplow event for EvidenceResponseFeedbackSubmitted.
 * Tracks when a user submits feedback on an evidence response
 */
export function trackEvidenceResponseFeedbackSubmitted<T extends {} = any>(evidenceResponseFeedbackSubmitted: EvidenceResponseFeedbackSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceResponseFeedbackSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_response_feedback_submitted/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceResponseFeedbackSubmitted entity.
 */
export function createEvidenceResponseFeedbackSubmitted(evidenceResponseFeedbackSubmitted: EvidenceResponseFeedbackSubmitted){
    return {
        schema: 'iglu:com.heidi/evidence_response_feedback_submitted/jsonschema/2-0-2',
        data: evidenceResponseFeedbackSubmitted
    }
}
/**
 * Track a Snowplow event for IntegrationCreated.
 * Tracks when integration event/record is created
 */
export function trackIntegrationCreated<T extends {} = any>(integrationCreated: IntegrationCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_created/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationCreated entity.
 */
export function createIntegrationCreated(integrationCreated: IntegrationCreated){
    return {
        schema: 'iglu:com.heidi/integration_created/jsonschema/2-0-3',
        data: integrationCreated
    }
}
/**
 * Track a Snowplow event for ExperimentStarted.
 * Tracks experiment exposure
 */
export function trackExperimentStarted<T extends {} = any>(experimentStarted: ExperimentStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = experimentStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/experiment_started/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ExperimentStarted entity.
 */
export function createExperimentStarted(experimentStarted: ExperimentStarted){
    return {
        schema: 'iglu:com.heidi/experiment_started/jsonschema/2-0-3',
        data: experimentStarted
    }
}
/**
 * Track a Snowplow event for TemplateQuotaFailed.
 * Tracks when template creation fails due to quota limit
 */
export function trackTemplateQuotaFailed<T extends {} = any>(templateQuotaFailed: TemplateQuotaFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateQuotaFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_quota_failed/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateQuotaFailed entity.
 */
export function createTemplateQuotaFailed(templateQuotaFailed: TemplateQuotaFailed){
    return {
        schema: 'iglu:com.heidi/template_quota_failed/jsonschema/1-0-2',
        data: templateQuotaFailed
    }
}
/**
 * Track a Snowplow event for ClinicalCodeCopied.
 * Tracks clinical code copied event
 */
export function trackClinicalCodeCopied<T extends {} = any>(clinicalCodeCopied: ClinicalCodeCopied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = clinicalCodeCopied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/clinical_code_copied/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ClinicalCodeCopied entity.
 */
export function createClinicalCodeCopied(clinicalCodeCopied: ClinicalCodeCopied){
    return {
        schema: 'iglu:com.heidi/clinical_code_copied/jsonschema/1-0-1',
        data: clinicalCodeCopied
    }
}
/**
 * Track a Snowplow event for UniversalDictationCsatDismissed.
 * Tracks when the universal dictation CSAT prompt is dismissed
 */
export function trackUniversalDictationCsatDismissed<T extends {} = any>(universalDictationCsatDismissed: UniversalDictationCsatDismissed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = universalDictationCsatDismissed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/universal_dictation_csat_dismissed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UniversalDictationCsatDismissed entity.
 */
export function createUniversalDictationCsatDismissed(universalDictationCsatDismissed: UniversalDictationCsatDismissed){
    return {
        schema: 'iglu:com.heidi/universal_dictation_csat_dismissed/jsonschema/1-0-1',
        data: universalDictationCsatDismissed
    }
}
/**
 * Track a Snowplow event for AutoLinkSessionsEligibilityEvaluated.
 * Tracks one bounded auto-link sessions eligibility evaluation at patient attach
 */
export function trackAutoLinkSessionsEligibilityEvaluated<T extends {} = any>(autoLinkSessionsEligibilityEvaluated: AutoLinkSessionsEligibilityEvaluated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = autoLinkSessionsEligibilityEvaluated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/auto_link_sessions_eligibility_evaluated/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AutoLinkSessionsEligibilityEvaluated entity.
 */
export function createAutoLinkSessionsEligibilityEvaluated(autoLinkSessionsEligibilityEvaluated: AutoLinkSessionsEligibilityEvaluated){
    return {
        schema: 'iglu:com.heidi/auto_link_sessions_eligibility_evaluated/jsonschema/1-0-0',
        data: autoLinkSessionsEligibilityEvaluated
    }
}
/**
 * Track a Snowplow event for AuthManualOpenClicked.
 * Tracks when the manual open button is clicked during authentication
 */
export function trackAuthManualOpenClicked<T extends {} = any>(authManualOpenClicked: AuthManualOpenClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = authManualOpenClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/auth_manual_open_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AuthManualOpenClicked entity.
 */
export function createAuthManualOpenClicked(authManualOpenClicked: AuthManualOpenClicked){
    return {
        schema: 'iglu:com.heidi/auth_manual_open_clicked/jsonschema/1-0-1',
        data: authManualOpenClicked
    }
}
/**
 * Track a Snowplow event for DemoStarted.
 * Tracks demo started event
 */
export function trackDemoStarted<T extends {} = any>(demoStarted: DemoStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = demoStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/demo_started/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DemoStarted entity.
 */
export function createDemoStarted(demoStarted: DemoStarted){
    return {
        schema: 'iglu:com.heidi/demo_started/jsonschema/2-0-1',
        data: demoStarted
    }
}
/**
 * Track a Snowplow event for WorkRunStarted.
 * Agent run or turn started (including computer-use or workflow kickoff)
 */
export function trackWorkRunStarted<T extends {} = any>(workRunStarted: WorkRunStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workRunStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_run_started/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkRunStarted entity.
 */
export function createWorkRunStarted(workRunStarted: WorkRunStarted){
    return {
        schema: 'iglu:com.heidi/work_run_started/jsonschema/1-0-2',
        data: workRunStarted
    }
}
/**
 * Track a Snowplow event for SessionStopped.
 * Tracks when a session is stopped
 */
export function trackSessionStopped<T extends {} = any>(sessionStopped: SessionStopped & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionStopped;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/session_stopped/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionStopped entity.
 */
export function createSessionStopped(sessionStopped: SessionStopped){
    return {
        schema: 'iglu:com.heidi/session_stopped/jsonschema/1-0-1',
        data: sessionStopped
    }
}
/**
 * Track a Snowplow event for LibraryDocumentUploaded.
 * Tracks library document uploaded event
 */
export function trackLibraryDocumentUploaded<T extends {} = any>(libraryDocumentUploaded: LibraryDocumentUploaded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = libraryDocumentUploaded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/library_document_uploaded/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LibraryDocumentUploaded entity.
 */
export function createLibraryDocumentUploaded(libraryDocumentUploaded: LibraryDocumentUploaded){
    return {
        schema: 'iglu:com.heidi/library_document_uploaded/jsonschema/1-0-1',
        data: libraryDocumentUploaded
    }
}
/**
 * Track a Snowplow event for FormFillFailed.
 * Tracks when a form fill upload fails
 */
export function trackFormFillFailed<T extends {} = any>(formFillFailed: FormFillFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = formFillFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/form_fill_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FormFillFailed entity.
 */
export function createFormFillFailed(formFillFailed: FormFillFailed){
    return {
        schema: 'iglu:com.heidi/form_fill_failed/jsonschema/1-0-0',
        data: formFillFailed
    }
}
/**
 * Track a Snowplow event for EvidenceSummarySuggestionGenerated.
 * Tracks when the user accepts a post-note Evidence summary suggestion and a query is generated
 */
export function trackEvidenceSummarySuggestionGenerated<T extends {} = any>(evidenceSummarySuggestionGenerated: EvidenceSummarySuggestionGenerated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceSummarySuggestionGenerated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_summary_suggestion_generated/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceSummarySuggestionGenerated entity.
 */
export function createEvidenceSummarySuggestionGenerated(evidenceSummarySuggestionGenerated: EvidenceSummarySuggestionGenerated){
    return {
        schema: 'iglu:com.heidi/evidence_summary_suggestion_generated/jsonschema/1-0-0',
        data: evidenceSummarySuggestionGenerated
    }
}
/**
 * Track a Snowplow event for AccountCreated.
 * Tracks account created event
 */
export function trackAccountCreated<T extends {} = any>(accountCreated: AccountCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = accountCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/account_created/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AccountCreated entity.
 */
export function createAccountCreated(accountCreated: AccountCreated){
    return {
        schema: 'iglu:com.heidi/account_created/jsonschema/2-0-1',
        data: accountCreated
    }
}
/**
 * Track a Snowplow event for EvidenceExperienceFeedbackSubmitted.
 * Tracks when a user submits experience feedback
 */
export function trackEvidenceExperienceFeedbackSubmitted<T extends {} = any>(evidenceExperienceFeedbackSubmitted: EvidenceExperienceFeedbackSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceExperienceFeedbackSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_experience_feedback_submitted/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceExperienceFeedbackSubmitted entity.
 */
export function createEvidenceExperienceFeedbackSubmitted(evidenceExperienceFeedbackSubmitted: EvidenceExperienceFeedbackSubmitted){
    return {
        schema: 'iglu:com.heidi/evidence_experience_feedback_submitted/jsonschema/2-0-2',
        data: evidenceExperienceFeedbackSubmitted
    }
}
/**
 * Track a Snowplow event for MessageAborted.
 * Tracks when a user aborts a message response
 */
export function trackMessageAborted<T extends {} = any>(messageAborted: MessageAborted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = messageAborted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/message_aborted/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow MessageAborted entity.
 */
export function createMessageAborted(messageAborted: MessageAborted){
    return {
        schema: 'iglu:com.heidi/message_aborted/jsonschema/2-0-3',
        data: messageAborted
    }
}
/**
 * Track a Snowplow event for ChatContextAttachPatientClicked.
 * Tracks chat context attach patient clicked event
 */
export function trackChatContextAttachPatientClicked<T extends {} = any>(chatContextAttachPatientClicked: ChatContextAttachPatientClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chatContextAttachPatientClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/chat_context_attach_patient_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChatContextAttachPatientClicked entity.
 */
export function createChatContextAttachPatientClicked(chatContextAttachPatientClicked: ChatContextAttachPatientClicked){
    return {
        schema: 'iglu:com.heidi/chat_context_attach_patient_clicked/jsonschema/1-0-1',
        data: chatContextAttachPatientClicked
    }
}
/**
 * Track a Snowplow event for ToolCallCompleted.
 * Tracks when an evidence tool call completes
 */
export function trackToolCallCompleted<T extends {} = any>(toolCallCompleted: ToolCallCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = toolCallCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/tool_call_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ToolCallCompleted entity.
 */
export function createToolCallCompleted(toolCallCompleted: ToolCallCompleted){
    return {
        schema: 'iglu:com.heidi/tool_call_completed/jsonschema/1-0-1',
        data: toolCallCompleted
    }
}
/**
 * Track a Snowplow event for SessionsScopeChanged.
 * Tracks when the user switches the session list scope
 */
export function trackSessionsScopeChanged<T extends {} = any>(sessionsScopeChanged: SessionsScopeChanged & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionsScopeChanged;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/sessions_scope_changed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionsScopeChanged entity.
 */
export function createSessionsScopeChanged(sessionsScopeChanged: SessionsScopeChanged){
    return {
        schema: 'iglu:com.heidi/sessions_scope_changed/jsonschema/1-0-0',
        data: sessionsScopeChanged
    }
}
/**
 * Track a Snowplow event for DarkModeSpotlightShown.
 * Tracks when the introductory dark-mode spotlight auto-opens for an eligible user
 */
export function trackDarkModeSpotlightShown<T extends {} = any>(darkModeSpotlightShown: DarkModeSpotlightShown & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = darkModeSpotlightShown;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/dark_mode_spotlight_shown/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DarkModeSpotlightShown entity.
 */
export function createDarkModeSpotlightShown(darkModeSpotlightShown: DarkModeSpotlightShown){
    return {
        schema: 'iglu:com.heidi/dark_mode_spotlight_shown/jsonschema/1-0-0',
        data: darkModeSpotlightShown
    }
}
/**
 * Track a Snowplow event for CallNoAnswer.
 * Tracks when an outbound call is not answered
 */
export function trackCallNoAnswer<T extends {} = any>(callNoAnswer: CallNoAnswer & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callNoAnswer;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_no_answer/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallNoAnswer entity.
 */
export function createCallNoAnswer(callNoAnswer: CallNoAnswer){
    return {
        schema: 'iglu:com.heidi/call_no_answer/jsonschema/1-0-0',
        data: callNoAnswer
    }
}
/**
 * Track a Snowplow event for TranslatorTranslationRated.
 * Fired when a user rates a translated message with thumbs up or thumbs down
 */
export function trackTranslatorTranslationRated<T extends {} = any>(translatorTranslationRated: TranslatorTranslationRated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = translatorTranslationRated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/translator_translation_rated/jsonschema/3-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TranslatorTranslationRated entity.
 */
export function createTranslatorTranslationRated(translatorTranslationRated: TranslatorTranslationRated){
    return {
        schema: 'iglu:com.heidi/translator_translation_rated/jsonschema/3-0-1',
        data: translatorTranslationRated
    }
}
/**
 * Track a Snowplow event for ScheduleCompleted.
 * Tracks schedule completed event
 */
export function trackScheduleCompleted<T extends {} = any>(scheduleCompleted: ScheduleCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = scheduleCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/schedule_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScheduleCompleted entity.
 */
export function createScheduleCompleted(scheduleCompleted: ScheduleCompleted){
    return {
        schema: 'iglu:com.heidi/schedule_completed/jsonschema/1-0-1',
        data: scheduleCompleted
    }
}
/**
 * Track a Snowplow event for NudgeProtocolsToggled.
 * ChenMed PIN protocols were turned on or off for a session (gate modal answer or header/card toggle)
 */
export function trackNudgeProtocolsToggled<T extends {} = any>(nudgeProtocolsToggled: NudgeProtocolsToggled & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = nudgeProtocolsToggled;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/nudge_protocols_toggled/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NudgeProtocolsToggled entity.
 */
export function createNudgeProtocolsToggled(nudgeProtocolsToggled: NudgeProtocolsToggled){
    return {
        schema: 'iglu:com.heidi/nudge_protocols_toggled/jsonschema/1-0-0',
        data: nudgeProtocolsToggled
    }
}
/**
 * Track a Snowplow event for SignInSuccessful.
 * Tracks when a user successfully signs in
 */
export function trackSignInSuccessful<T extends {} = any>(signInSuccessful: SignInSuccessful & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = signInSuccessful;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/sign_in_successful/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SignInSuccessful entity.
 */
export function createSignInSuccessful(signInSuccessful: SignInSuccessful){
    return {
        schema: 'iglu:com.heidi/sign_in_successful/jsonschema/2-0-0',
        data: signInSuccessful
    }
}
/**
 * Track a Snowplow event for SofLaunchError.
 * Tracks sof launch error event
 */
export function trackSofLaunchError<T extends {} = any>(sofLaunchError: SofLaunchError & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sofLaunchError;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/sof_launch_error/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SofLaunchError entity.
 */
export function createSofLaunchError(sofLaunchError: SofLaunchError){
    return {
        schema: 'iglu:com.heidi/sof_launch_error/jsonschema/1-0-1',
        data: sofLaunchError
    }
}
/**
 * Track a Snowplow event for DocumentSynced.
 * Tracks document synced event
 */
export function trackDocumentSynced<T extends {} = any>(documentSynced: DocumentSynced & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = documentSynced;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/document_synced/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DocumentSynced entity.
 */
export function createDocumentSynced(documentSynced: DocumentSynced){
    return {
        schema: 'iglu:com.heidi/document_synced/jsonschema/1-0-1',
        data: documentSynced
    }
}
/**
 * Track a Snowplow event for TranslatorBulkUploadCompleted.
 * Fired when a bulk audio upload is successfully processed
 */
export function trackTranslatorBulkUploadCompleted<T extends {} = any>(translatorBulkUploadCompleted: TranslatorBulkUploadCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = translatorBulkUploadCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/translator_bulk_upload_completed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TranslatorBulkUploadCompleted entity.
 */
export function createTranslatorBulkUploadCompleted(translatorBulkUploadCompleted: TranslatorBulkUploadCompleted){
    return {
        schema: 'iglu:com.heidi/translator_bulk_upload_completed/jsonschema/1-0-0',
        data: translatorBulkUploadCompleted
    }
}
/**
 * Track a Snowplow event for EvidenceOnboardingViewed.
 * Tracks evidence onboarding viewed event
 */
export function trackEvidenceOnboardingViewed<T extends {} = any>(evidenceOnboardingViewed: EvidenceOnboardingViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceOnboardingViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_onboarding_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceOnboardingViewed entity.
 */
export function createEvidenceOnboardingViewed(evidenceOnboardingViewed: EvidenceOnboardingViewed){
    return {
        schema: 'iglu:com.heidi/evidence_onboarding_viewed/jsonschema/1-0-1',
        data: evidenceOnboardingViewed
    }
}
/**
 * Track a Snowplow event for PatientCreated.
 * Tracks when new patient is created
 */
export function trackPatientCreated<T extends {} = any>(patientCreated: PatientCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_created/jsonschema/2-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientCreated entity.
 */
export function createPatientCreated(patientCreated: PatientCreated){
    return {
        schema: 'iglu:com.heidi/patient_created/jsonschema/2-1-0',
        data: patientCreated
    }
}
/**
 * Track a Snowplow event for NoteUpdated.
 * Tracks when note is modified
 */
export function trackNoteUpdated<T extends {} = any>(noteUpdated: NoteUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_updated/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteUpdated entity.
 */
export function createNoteUpdated(noteUpdated: NoteUpdated){
    return {
        schema: 'iglu:com.heidi/note_updated/jsonschema/2-0-3',
        data: noteUpdated
    }
}
/**
 * Track a Snowplow event for NoteEditorRendered.
 * Tracks which note editor variant was rendered for a session
 */
export function trackNoteEditorRendered<T extends {} = any>(noteEditorRendered: NoteEditorRendered & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteEditorRendered;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_editor_rendered/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteEditorRendered entity.
 */
export function createNoteEditorRendered(noteEditorRendered: NoteEditorRendered){
    return {
        schema: 'iglu:com.heidi/note_editor_rendered/jsonschema/1-0-1',
        data: noteEditorRendered
    }
}
/**
 * Track a Snowplow event for CallFailed.
 * Tracks when a call fails due to a technical error
 */
export function trackCallFailed<T extends {} = any>(callFailed: CallFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallFailed entity.
 */
export function createCallFailed(callFailed: CallFailed){
    return {
        schema: 'iglu:com.heidi/call_failed/jsonschema/1-0-0',
        data: callFailed
    }
}
/**
 * Track a Snowplow event for NotificationUpdated.
 * Tracks notification updated event
 */
export function trackNotificationUpdated<T extends {} = any>(notificationUpdated: NotificationUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = notificationUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/notification_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NotificationUpdated entity.
 */
export function createNotificationUpdated(notificationUpdated: NotificationUpdated){
    return {
        schema: 'iglu:com.heidi/notification_updated/jsonschema/1-0-1',
        data: notificationUpdated
    }
}
/**
 * Track a Snowplow event for FirstSessionCompleted.
 * Tracks when a user completes their first session
 */
export function trackFirstSessionCompleted<T extends {} = any>(firstSessionCompleted: FirstSessionCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = firstSessionCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/first_session_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FirstSessionCompleted entity.
 */
export function createFirstSessionCompleted(firstSessionCompleted: FirstSessionCompleted){
    return {
        schema: 'iglu:com.heidi/first_session_completed/jsonschema/1-0-1',
        data: firstSessionCompleted
    }
}
/**
 * Track a Snowplow event for EvidenceOnboardingSaved.
 * Tracks evidence onboarding saved event
 */
export function trackEvidenceOnboardingSaved<T extends {} = any>(evidenceOnboardingSaved: EvidenceOnboardingSaved & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceOnboardingSaved;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_onboarding_saved/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceOnboardingSaved entity.
 */
export function createEvidenceOnboardingSaved(evidenceOnboardingSaved: EvidenceOnboardingSaved){
    return {
        schema: 'iglu:com.heidi/evidence_onboarding_saved/jsonschema/1-0-1',
        data: evidenceOnboardingSaved
    }
}
/**
 * Track a Snowplow event for VerificationFailed.
 * Tracks when entity verification fails for a session
 */
export function trackVerificationFailed<T extends {} = any>(verificationFailed: VerificationFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = verificationFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/verification_failed/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow VerificationFailed entity.
 */
export function createVerificationFailed(verificationFailed: VerificationFailed){
    return {
        schema: 'iglu:com.heidi/verification_failed/jsonschema/2-0-0',
        data: verificationFailed
    }
}
/**
 * Track a Snowplow event for ChatDeleted.
 * Tracks when a chat is deleted
 */
export function trackChatDeleted<T extends {} = any>(chatDeleted: ChatDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chatDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/chat_deleted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChatDeleted entity.
 */
export function createChatDeleted(chatDeleted: ChatDeleted){
    return {
        schema: 'iglu:com.heidi/chat_deleted/jsonschema/1-0-1',
        data: chatDeleted
    }
}
/**
 * Track a Snowplow event for TaskCompleted.
 * Tracks task completed event
 */
export function trackTaskCompleted<T extends {} = any>(taskCompleted: TaskCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = taskCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/task_completed/jsonschema/1-1-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TaskCompleted entity.
 */
export function createTaskCompleted(taskCompleted: TaskCompleted){
    return {
        schema: 'iglu:com.heidi/task_completed/jsonschema/1-1-1',
        data: taskCompleted
    }
}
/**
 * Track a Snowplow event for SubscriptionUpdated.
 * Tracks when a subscription is updated (upgrade, downgrade, renewal, plan change, seat change)
 */
export function trackSubscriptionUpdated<T extends {} = any>(subscriptionUpdated: SubscriptionUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = subscriptionUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/subscription_updated/jsonschema/1-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SubscriptionUpdated entity.
 */
export function createSubscriptionUpdated(subscriptionUpdated: SubscriptionUpdated){
    return {
        schema: 'iglu:com.heidi/subscription_updated/jsonschema/1-1-0',
        data: subscriptionUpdated
    }
}
/**
 * Track a Snowplow event for PaymentStarted.
 * Tracks payment initiation
 */
export function trackPaymentStarted<T extends {} = any>(paymentStarted: PaymentStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = paymentStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/payment_started/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PaymentStarted entity.
 */
export function createPaymentStarted(paymentStarted: PaymentStarted){
    return {
        schema: 'iglu:com.heidi/payment_started/jsonschema/2-0-3',
        data: paymentStarted
    }
}
/**
 * Track a Snowplow event for ContextFileUploadStarted.
 * Tracks context file upload started event
 */
export function trackContextFileUploadStarted<T extends {} = any>(contextFileUploadStarted: ContextFileUploadStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = contextFileUploadStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/context_file_upload_started/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ContextFileUploadStarted entity.
 */
export function createContextFileUploadStarted(contextFileUploadStarted: ContextFileUploadStarted){
    return {
        schema: 'iglu:com.heidi/context_file_upload_started/jsonschema/1-0-1',
        data: contextFileUploadStarted
    }
}
/**
 * Track a Snowplow event for SessionCreated.
 * Tracks when session is created
 */
export function trackSessionCreated<T extends {} = any>(sessionCreated: SessionCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/session_created/jsonschema/2-0-4',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionCreated entity.
 */
export function createSessionCreated(sessionCreated: SessionCreated){
    return {
        schema: 'iglu:com.heidi/session_created/jsonschema/2-0-4',
        data: sessionCreated
    }
}
/**
 * Track a Snowplow event for PatientUnlinked.
 * Tracks when sessions are unlinked from patient
 */
export function trackPatientUnlinked<T extends {} = any>(patientUnlinked: PatientUnlinked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientUnlinked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_unlinked/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientUnlinked entity.
 */
export function createPatientUnlinked(patientUnlinked: PatientUnlinked){
    return {
        schema: 'iglu:com.heidi/patient_unlinked/jsonschema/2-0-3',
        data: patientUnlinked
    }
}
/**
 * Track a Snowplow event for SpeakerAttributionUpdated.
 * Tracks one clinician correction of diarised speaker attribution, success or failure
 */
export function trackSpeakerAttributionUpdated<T extends {} = any>(speakerAttributionUpdated: SpeakerAttributionUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = speakerAttributionUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/speaker_attribution_updated/jsonschema/1-3-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SpeakerAttributionUpdated entity.
 */
export function createSpeakerAttributionUpdated(speakerAttributionUpdated: SpeakerAttributionUpdated){
    return {
        schema: 'iglu:com.heidi/speaker_attribution_updated/jsonschema/1-3-0',
        data: speakerAttributionUpdated
    }
}
/**
 * Track a Snowplow event for SourceRecommendationShown.
 * Tracks when the personalised source recommendation banner is shown to an evidence-activated user. Top of the source-recommendation funnel (shown -&gt; dismissed | applied | customised). &#x60;matched_*&#x60; fields reflect the BE-selected recommendation, not the user&#x27;s current profile. 2-0-0: renamed &#x60;location&#x60; -&gt; &#x60;banner_location&#x60; (MODEL bump). The 1-0-0 property name caused snowtype to emit a top-level TypeScript &#x60;Location&#x60; alias that shadowed the DOM &#x60;Location&#x60; global; the rename scopes the alias to the source-recommendation banner and makes snowtype emit &#x60;BannerLocation&#x60; instead. No other shape changes vs 1-0-0.
 */
export function trackSourceRecommendationShown<T extends {} = any>(sourceRecommendationShown: SourceRecommendationShown & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sourceRecommendationShown;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/source_recommendation_shown/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SourceRecommendationShown entity.
 */
export function createSourceRecommendationShown(sourceRecommendationShown: SourceRecommendationShown){
    return {
        schema: 'iglu:com.heidi/source_recommendation_shown/jsonschema/2-0-0',
        data: sourceRecommendationShown
    }
}
/**
 * Track a Snowplow event for PrechartSentToContext.
 * Tracks when a pre-chart is sent to the session&#x27;s context
 */
export function trackPrechartSentToContext<T extends {} = any>(prechartSentToContext: PrechartSentToContext & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = prechartSentToContext;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/prechart_sent_to_context/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PrechartSentToContext entity.
 */
export function createPrechartSentToContext(prechartSentToContext: PrechartSentToContext){
    return {
        schema: 'iglu:com.heidi/prechart_sent_to_context/jsonschema/1-0-0',
        data: prechartSentToContext
    }
}
/**
 * Track a Snowplow event for MessageStopped.
 * Tracks message stopped event
 */
export function trackMessageStopped<T extends {} = any>(messageStopped: MessageStopped & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = messageStopped;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/message_stopped/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow MessageStopped entity.
 */
export function createMessageStopped(messageStopped: MessageStopped){
    return {
        schema: 'iglu:com.heidi/message_stopped/jsonschema/2-0-0',
        data: messageStopped
    }
}
/**
 * Track a Snowplow event for ScheduleSynced.
 * Tracks when a schedule is synced
 */
export function trackScheduleSynced<T extends {} = any>(scheduleSynced: ScheduleSynced & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = scheduleSynced;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/schedule_synced/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScheduleSynced entity.
 */
export function createScheduleSynced(scheduleSynced: ScheduleSynced){
    return {
        schema: 'iglu:com.heidi/schedule_synced/jsonschema/1-0-1',
        data: scheduleSynced
    }
}
/**
 * Track a Snowplow event for SnippetUpdated.
 * Tracks snippet updated event
 */
export function trackSnippetUpdated<T extends {} = any>(snippetUpdated: SnippetUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = snippetUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/snippet_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SnippetUpdated entity.
 */
export function createSnippetUpdated(snippetUpdated: SnippetUpdated){
    return {
        schema: 'iglu:com.heidi/snippet_updated/jsonschema/1-0-1',
        data: snippetUpdated
    }
}
/**
 * Track a Snowplow event for NoteGenerationStarted.
 * Consult note generation started for a session
 */
export function trackNoteGenerationStarted<T extends {} = any>(noteGenerationStarted: NoteGenerationStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteGenerationStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_generation_started/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteGenerationStarted entity.
 */
export function createNoteGenerationStarted(noteGenerationStarted: NoteGenerationStarted){
    return {
        schema: 'iglu:com.heidi/note_generation_started/jsonschema/2-0-2',
        data: noteGenerationStarted
    }
}
/**
 * Track a Snowplow event for BookDemoCountrySelected.
 * Tracks book demo country selected event
 */
export function trackBookDemoCountrySelected<T extends {} = any>(bookDemoCountrySelected: BookDemoCountrySelected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = bookDemoCountrySelected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/book_demo_country_selected/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow BookDemoCountrySelected entity.
 */
export function createBookDemoCountrySelected(bookDemoCountrySelected: BookDemoCountrySelected){
    return {
        schema: 'iglu:com.heidi/book_demo_country_selected/jsonschema/1-0-1',
        data: bookDemoCountrySelected
    }
}
/**
 * Track a Snowplow event for TranscriptionCompleted.
 * Tracks when transcription process completes
 */
export function trackTranscriptionCompleted<T extends {} = any>(transcriptionCompleted: TranscriptionCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = transcriptionCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/transcription_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TranscriptionCompleted entity.
 */
export function createTranscriptionCompleted(transcriptionCompleted: TranscriptionCompleted){
    return {
        schema: 'iglu:com.heidi/transcription_completed/jsonschema/1-0-1',
        data: transcriptionCompleted
    }
}
/**
 * Track a Snowplow event for OfflineSyncFailed.
 * Tracks a retryable or terminal failure in an offline recording sync
 */
export function trackOfflineSyncFailed<T extends {} = any>(offlineSyncFailed: OfflineSyncFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = offlineSyncFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/offline_sync_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OfflineSyncFailed entity.
 */
export function createOfflineSyncFailed(offlineSyncFailed: OfflineSyncFailed){
    return {
        schema: 'iglu:com.heidi/offline_sync_failed/jsonschema/1-0-0',
        data: offlineSyncFailed
    }
}
/**
 * Track a Snowplow event for SurveyUpdated.
 * Tracks survey updated event
 */
export function trackSurveyUpdated<T extends {} = any>(surveyUpdated: SurveyUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = surveyUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/survey_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SurveyUpdated entity.
 */
export function createSurveyUpdated(surveyUpdated: SurveyUpdated){
    return {
        schema: 'iglu:com.heidi/survey_updated/jsonschema/1-0-1',
        data: surveyUpdated
    }
}
/**
 * Track a Snowplow event for CursorDictationPostProcessingFailed.
 * Tracks when cursor dictation post-processing fails
 */
export function trackCursorDictationPostProcessingFailed<T extends {} = any>(cursorDictationPostProcessingFailed: CursorDictationPostProcessingFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = cursorDictationPostProcessingFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/cursor_dictation_post_processing_failed/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CursorDictationPostProcessingFailed entity.
 */
export function createCursorDictationPostProcessingFailed(cursorDictationPostProcessingFailed: CursorDictationPostProcessingFailed){
    return {
        schema: 'iglu:com.heidi/cursor_dictation_post_processing_failed/jsonschema/1-0-2',
        data: cursorDictationPostProcessingFailed
    }
}
/**
 * Track a Snowplow event for LibraryLinkClicked.
 * Tracks library link clicked event
 */
export function trackLibraryLinkClicked<T extends {} = any>(libraryLinkClicked: LibraryLinkClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = libraryLinkClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/library_link_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LibraryLinkClicked entity.
 */
export function createLibraryLinkClicked(libraryLinkClicked: LibraryLinkClicked){
    return {
        schema: 'iglu:com.heidi/library_link_clicked/jsonschema/1-0-1',
        data: libraryLinkClicked
    }
}
/**
 * Track a Snowplow event for TrialUpgradeClicked.
 * Tracks when user clicks to upgrade from trial
 */
export function trackTrialUpgradeClicked<T extends {} = any>(trialUpgradeClicked: TrialUpgradeClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = trialUpgradeClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/trial_upgrade_clicked/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TrialUpgradeClicked entity.
 */
export function createTrialUpgradeClicked(trialUpgradeClicked: TrialUpgradeClicked){
    return {
        schema: 'iglu:com.heidi/trial_upgrade_clicked/jsonschema/1-0-3',
        data: trialUpgradeClicked
    }
}
/**
 * Track a Snowplow event for EvidenceChatCompleted.
 * Tracks when an evidence chat response completes
 */
export function trackEvidenceChatCompleted<T extends {} = any>(evidenceChatCompleted: EvidenceChatCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceChatCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_chat_completed/jsonschema/2-0-5',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceChatCompleted entity.
 */
export function createEvidenceChatCompleted(evidenceChatCompleted: EvidenceChatCompleted){
    return {
        schema: 'iglu:com.heidi/evidence_chat_completed/jsonschema/2-0-5',
        data: evidenceChatCompleted
    }
}
/**
 * Track a Snowplow event for AuthenticationCompleted.
 * Tracks when user successfully completes authentication/sign-in
 */
export function trackAuthenticationCompleted<T extends {} = any>(authenticationCompleted: AuthenticationCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = authenticationCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/authentication_completed/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AuthenticationCompleted entity.
 */
export function createAuthenticationCompleted(authenticationCompleted: AuthenticationCompleted){
    return {
        schema: 'iglu:com.heidi/authentication_completed/jsonschema/2-0-3',
        data: authenticationCompleted
    }
}
/**
 * Track a Snowplow event for ScheduleUploadPromoViewed.
 * Tracks schedule upload promo viewed event
 */
export function trackScheduleUploadPromoViewed<T extends {} = any>(scheduleUploadPromoViewed: ScheduleUploadPromoViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = scheduleUploadPromoViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/schedule_upload_promo_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScheduleUploadPromoViewed entity.
 */
export function createScheduleUploadPromoViewed(scheduleUploadPromoViewed: ScheduleUploadPromoViewed){
    return {
        schema: 'iglu:com.heidi/schedule_upload_promo_viewed/jsonschema/1-0-1',
        data: scheduleUploadPromoViewed
    }
}
/**
 * Track a Snowplow event for TemplateViewed.
 * Tracks when a template is viewed
 */
export function trackTemplateViewed<T extends {} = any>(templateViewed: TemplateViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_viewed/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateViewed entity.
 */
export function createTemplateViewed(templateViewed: TemplateViewed){
    return {
        schema: 'iglu:com.heidi/template_viewed/jsonschema/2-0-1',
        data: templateViewed
    }
}
/**
 * Track a Snowplow event for WorkMessageSubmitted.
 * User submitted a Work message or turn (composer, home, in-session)
 */
export function trackWorkMessageSubmitted<T extends {} = any>(workMessageSubmitted: WorkMessageSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workMessageSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_message_submitted/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkMessageSubmitted entity.
 */
export function createWorkMessageSubmitted(workMessageSubmitted: WorkMessageSubmitted){
    return {
        schema: 'iglu:com.heidi/work_message_submitted/jsonschema/1-0-2',
        data: workMessageSubmitted
    }
}
/**
 * Track a Snowplow event for TemplateCreationRecordCreated.
 * Tracks template creation record created event
 */
export function trackTemplateCreationRecordCreated<T extends {} = any>(templateCreationRecordCreated: TemplateCreationRecordCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateCreationRecordCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_creation_record_created/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateCreationRecordCreated entity.
 */
export function createTemplateCreationRecordCreated(templateCreationRecordCreated: TemplateCreationRecordCreated){
    return {
        schema: 'iglu:com.heidi/template_creation_record_created/jsonschema/1-0-1',
        data: templateCreationRecordCreated
    }
}
/**
 * Track a Snowplow event for ScheduledCallSkipped.
 * Tracks when a scheduled call is skipped
 */
export function trackScheduledCallSkipped<T extends {} = any>(scheduledCallSkipped: ScheduledCallSkipped & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = scheduledCallSkipped;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/scheduled_call_skipped/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScheduledCallSkipped entity.
 */
export function createScheduledCallSkipped(scheduledCallSkipped: ScheduledCallSkipped){
    return {
        schema: 'iglu:com.heidi/scheduled_call_skipped/jsonschema/1-0-0',
        data: scheduledCallSkipped
    }
}
/**
 * Track a Snowplow event for LibraryUploadNudgeClicked.
 * Tracks library upload nudge clicked event
 */
export function trackLibraryUploadNudgeClicked<T extends {} = any>(libraryUploadNudgeClicked: LibraryUploadNudgeClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = libraryUploadNudgeClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/library_upload_nudge_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LibraryUploadNudgeClicked entity.
 */
export function createLibraryUploadNudgeClicked(libraryUploadNudgeClicked: LibraryUploadNudgeClicked){
    return {
        schema: 'iglu:com.heidi/library_upload_nudge_clicked/jsonschema/1-0-1',
        data: libraryUploadNudgeClicked
    }
}
/**
 * Track a Snowplow event for TaskStatusChanged.
 * Tracks when a task status transitions, capturing from and to status values
 */
export function trackTaskStatusChanged<T extends {} = any>(taskStatusChanged: TaskStatusChanged & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = taskStatusChanged;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/task_status_changed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TaskStatusChanged entity.
 */
export function createTaskStatusChanged(taskStatusChanged: TaskStatusChanged){
    return {
        schema: 'iglu:com.heidi/task_status_changed/jsonschema/1-0-0',
        data: taskStatusChanged
    }
}
/**
 * Track a Snowplow event for CommunityTemplateViewed.
 * Tracks when community template is viewed
 */
export function trackCommunityTemplateViewed<T extends {} = any>(communityTemplateViewed: CommunityTemplateViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = communityTemplateViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/community_template_viewed/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CommunityTemplateViewed entity.
 */
export function createCommunityTemplateViewed(communityTemplateViewed: CommunityTemplateViewed){
    return {
        schema: 'iglu:com.heidi/community_template_viewed/jsonschema/2-0-1',
        data: communityTemplateViewed
    }
}
/**
 * Track a Snowplow event for OrganisationUpdated.
 * Tracks when organisation settings or membership are updated
 */
export function trackOrganisationUpdated<T extends {} = any>(organisationUpdated: OrganisationUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = organisationUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/organisation_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OrganisationUpdated entity.
 */
export function createOrganisationUpdated(organisationUpdated: OrganisationUpdated){
    return {
        schema: 'iglu:com.heidi/organisation_updated/jsonschema/1-0-1',
        data: organisationUpdated
    }
}
/**
 * Track a Snowplow event for PrechartInstructionsUpdated.
 * Tracks when a clinician&#x27;s pre-chart instructions are saved
 */
export function trackPrechartInstructionsUpdated<T extends {} = any>(prechartInstructionsUpdated: PrechartInstructionsUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = prechartInstructionsUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/prechart_instructions_updated/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PrechartInstructionsUpdated entity.
 */
export function createPrechartInstructionsUpdated(prechartInstructionsUpdated: PrechartInstructionsUpdated){
    return {
        schema: 'iglu:com.heidi/prechart_instructions_updated/jsonschema/1-0-0',
        data: prechartInstructionsUpdated
    }
}
/**
 * Track a Snowplow event for EvidenceUserActive.
 * Tracks when an Evidence user is active (5+ queries in a week)
 */
export function trackEvidenceUserActive<T extends {} = any>(evidenceUserActive: EvidenceUserActive & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceUserActive;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_user_active/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceUserActive entity.
 */
export function createEvidenceUserActive(evidenceUserActive: EvidenceUserActive){
    return {
        schema: 'iglu:com.heidi/evidence_user_active/jsonschema/2-0-0',
        data: evidenceUserActive
    }
}
/**
 * Track a Snowplow event for NudgeItemChecked.
 * A nudge checklist item was checked
 */
export function trackNudgeItemChecked<T extends {} = any>(nudgeItemChecked: NudgeItemChecked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = nudgeItemChecked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/nudge_item_checked/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NudgeItemChecked entity.
 */
export function createNudgeItemChecked(nudgeItemChecked: NudgeItemChecked){
    return {
        schema: 'iglu:com.heidi/nudge_item_checked/jsonschema/1-0-0',
        data: nudgeItemChecked
    }
}
/**
 * Track a Snowplow event for WorkProjectCreated.
 * Server created a Work project (and backing agent run)
 */
export function trackWorkProjectCreated<T extends {} = any>(workProjectCreated: WorkProjectCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workProjectCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_project_created/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkProjectCreated entity.
 */
export function createWorkProjectCreated(workProjectCreated: WorkProjectCreated){
    return {
        schema: 'iglu:com.heidi/work_project_created/jsonschema/1-0-1',
        data: workProjectCreated
    }
}
/**
 * Track a Snowplow event for SnippetDeleted.
 * Tracks snippet deleted event
 */
export function trackSnippetDeleted<T extends {} = any>(snippetDeleted: SnippetDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = snippetDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/snippet_deleted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SnippetDeleted entity.
 */
export function createSnippetDeleted(snippetDeleted: SnippetDeleted){
    return {
        schema: 'iglu:com.heidi/snippet_deleted/jsonschema/1-0-1',
        data: snippetDeleted
    }
}
/**
 * Track a Snowplow event for EhrAppointmentSynced.
 * Tracks when appointments are synced with the EHR
 */
export function trackEhrAppointmentSynced<T extends {} = any>(ehrAppointmentSynced: EhrAppointmentSynced & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = ehrAppointmentSynced;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/ehr_appointment_synced/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EhrAppointmentSynced entity.
 */
export function createEhrAppointmentSynced(ehrAppointmentSynced: EhrAppointmentSynced){
    return {
        schema: 'iglu:com.heidi/ehr_appointment_synced/jsonschema/1-0-0',
        data: ehrAppointmentSynced
    }
}
/**
 * Track a Snowplow event for SessionCopied.
 * Tracks when session is copied
 */
export function trackSessionCopied<T extends {} = any>(sessionCopied: SessionCopied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionCopied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/session_copied/jsonschema/3-0-7',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionCopied entity.
 */
export function createSessionCopied(sessionCopied: SessionCopied){
    return {
        schema: 'iglu:com.heidi/session_copied/jsonschema/3-0-7',
        data: sessionCopied
    }
}
/**
 * Track a Snowplow event for TaskCreated.
 * Tracks task created event
 */
export function trackTaskCreated<T extends {} = any>(taskCreated: TaskCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = taskCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/task_created/jsonschema/1-1-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TaskCreated entity.
 */
export function createTaskCreated(taskCreated: TaskCreated){
    return {
        schema: 'iglu:com.heidi/task_created/jsonschema/1-1-1',
        data: taskCreated
    }
}
/**
 * Track a Snowplow event for NoteEditorRenderFailed.
 * Tracks when the note editor fails to render (hydrate/serialize) content. PHI-free — carries no note body and no raw error string.
 */
export function trackNoteEditorRenderFailed<T extends {} = any>(noteEditorRenderFailed: NoteEditorRenderFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteEditorRenderFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_editor_render_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteEditorRenderFailed entity.
 */
export function createNoteEditorRenderFailed(noteEditorRenderFailed: NoteEditorRenderFailed){
    return {
        schema: 'iglu:com.heidi/note_editor_render_failed/jsonschema/1-0-0',
        data: noteEditorRenderFailed
    }
}
/**
 * Track a Snowplow event for ShareNotificationClicked.
 * Tracks when a share notification is clicked
 */
export function trackShareNotificationClicked<T extends {} = any>(shareNotificationClicked: ShareNotificationClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareNotificationClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_notification_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareNotificationClicked entity.
 */
export function createShareNotificationClicked(shareNotificationClicked: ShareNotificationClicked){
    return {
        schema: 'iglu:com.heidi/share_notification_clicked/jsonschema/1-0-1',
        data: shareNotificationClicked
    }
}
/**
 * Track a Snowplow event for CpdSourceUpdated.
 * Tracks when a CPD source is updated
 */
export function trackCpdSourceUpdated<T extends {} = any>(cpdSourceUpdated: CpdSourceUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = cpdSourceUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/cpd_source_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CpdSourceUpdated entity.
 */
export function createCpdSourceUpdated(cpdSourceUpdated: CpdSourceUpdated){
    return {
        schema: 'iglu:com.heidi/cpd_source_updated/jsonschema/1-0-1',
        data: cpdSourceUpdated
    }
}
/**
 * Track a Snowplow event for EhrLaunchStarted.
 * Tracks EHR launch initiation
 */
export function trackEhrLaunchStarted<T extends {} = any>(ehrLaunchStarted: EhrLaunchStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = ehrLaunchStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/ehr_launch_started/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EhrLaunchStarted entity.
 */
export function createEhrLaunchStarted(ehrLaunchStarted: EhrLaunchStarted){
    return {
        schema: 'iglu:com.heidi/ehr_launch_started/jsonschema/1-0-3',
        data: ehrLaunchStarted
    }
}
/**
 * Track a Snowplow event for PatientUpdated.
 * Tracks when patient profile is updated
 */
export function trackPatientUpdated<T extends {} = any>(patientUpdated: PatientUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_updated/jsonschema/3-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientUpdated entity.
 */
export function createPatientUpdated(patientUpdated: PatientUpdated){
    return {
        schema: 'iglu:com.heidi/patient_updated/jsonschema/3-0-0',
        data: patientUpdated
    }
}
/**
 * Track a Snowplow event for DocumentUpdated.
 * Tracks when document is updated/edited
 */
export function trackDocumentUpdated<T extends {} = any>(documentUpdated: DocumentUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = documentUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/document_updated/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DocumentUpdated entity.
 */
export function createDocumentUpdated(documentUpdated: DocumentUpdated){
    return {
        schema: 'iglu:com.heidi/document_updated/jsonschema/2-0-2',
        data: documentUpdated
    }
}
/**
 * Track a Snowplow event for TeamCreated.
 * Tracks team created event
 */
export function trackTeamCreated<T extends {} = any>(teamCreated: TeamCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = teamCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/team_created/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TeamCreated entity.
 */
export function createTeamCreated(teamCreated: TeamCreated){
    return {
        schema: 'iglu:com.heidi/team_created/jsonschema/1-0-2',
        data: teamCreated
    }
}
/**
 * Track a Snowplow event for MessageReceived.
 * Tracks when a message response is received
 */
export function trackMessageReceived<T extends {} = any>(messageReceived: MessageReceived & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = messageReceived;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/message_received/jsonschema/2-0-5',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow MessageReceived entity.
 */
export function createMessageReceived(messageReceived: MessageReceived){
    return {
        schema: 'iglu:com.heidi/message_received/jsonschema/2-0-5',
        data: messageReceived
    }
}
/**
 * Track a Snowplow event for ContextFailed.
 * Tracks context failed event
 */
export function trackContextFailed<T extends {} = any>(contextFailed: ContextFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = contextFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/context_failed/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ContextFailed entity.
 */
export function createContextFailed(contextFailed: ContextFailed){
    return {
        schema: 'iglu:com.heidi/context_failed/jsonschema/1-0-3',
        data: contextFailed
    }
}
/**
 * Track a Snowplow event for AccountDeleted.
 * Tracks account deleted event
 */
export function trackAccountDeleted<T extends {} = any>(accountDeleted: AccountDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = accountDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/account_deleted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AccountDeleted entity.
 */
export function createAccountDeleted(accountDeleted: AccountDeleted){
    return {
        schema: 'iglu:com.heidi/account_deleted/jsonschema/1-0-1',
        data: accountDeleted
    }
}
/**
 * Track a Snowplow event for MessageAttachmentUploaded.
 * Tracks when a message attachment is uploaded
 */
export function trackMessageAttachmentUploaded<T extends {} = any>(messageAttachmentUploaded: MessageAttachmentUploaded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = messageAttachmentUploaded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/message_attachment_uploaded/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow MessageAttachmentUploaded entity.
 */
export function createMessageAttachmentUploaded(messageAttachmentUploaded: MessageAttachmentUploaded){
    return {
        schema: 'iglu:com.heidi/message_attachment_uploaded/jsonschema/1-0-0',
        data: messageAttachmentUploaded
    }
}
/**
 * Track a Snowplow event for ChronicleFirmwareUpgradeCompleted.
 * Tracks when a Chronicle device firmware upgrade completes successfully
 */
export function trackChronicleFirmwareUpgradeCompleted<T extends {} = any>(chronicleFirmwareUpgradeCompleted: ChronicleFirmwareUpgradeCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chronicleFirmwareUpgradeCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/chronicle_firmware_upgrade_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChronicleFirmwareUpgradeCompleted entity.
 */
export function createChronicleFirmwareUpgradeCompleted(chronicleFirmwareUpgradeCompleted: ChronicleFirmwareUpgradeCompleted){
    return {
        schema: 'iglu:com.heidi/chronicle_firmware_upgrade_completed/jsonschema/1-0-1',
        data: chronicleFirmwareUpgradeCompleted
    }
}
/**
 * Track a Snowplow event for PatientFailed.
 * Tracks when patient data extraction fails or returns empty
 */
export function trackPatientFailed<T extends {} = any>(patientFailed: PatientFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_failed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientFailed entity.
 */
export function createPatientFailed(patientFailed: PatientFailed){
    return {
        schema: 'iglu:com.heidi/patient_failed/jsonschema/1-0-1',
        data: patientFailed
    }
}
/**
 * Track a Snowplow event for PatientListViewed.
 * Tracks when the patient list page mounts
 */
export function trackPatientListViewed<T extends {} = any>(patientListViewed: PatientListViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientListViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_list_viewed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientListViewed entity.
 */
export function createPatientListViewed(patientListViewed: PatientListViewed){
    return {
        schema: 'iglu:com.heidi/patient_list_viewed/jsonschema/1-0-0',
        data: patientListViewed
    }
}
/**
 * Track a Snowplow event for PatientSummaryLinkOpened.
 * Tracks a patient opening a shared summary link (public download route serves the PDF)
 */
export function trackPatientSummaryLinkOpened<T extends {} = any>(patientSummaryLinkOpened: PatientSummaryLinkOpened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientSummaryLinkOpened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_summary_link_opened/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientSummaryLinkOpened entity.
 */
export function createPatientSummaryLinkOpened(patientSummaryLinkOpened: PatientSummaryLinkOpened){
    return {
        schema: 'iglu:com.heidi/patient_summary_link_opened/jsonschema/1-0-1',
        data: patientSummaryLinkOpened
    }
}
/**
 * Track a Snowplow event for ObjectiveViewed.
 * Tracks when a user views an objective detail page
 */
export function trackObjectiveViewed<T extends {} = any>(objectiveViewed: ObjectiveViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = objectiveViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/objective_viewed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ObjectiveViewed entity.
 */
export function createObjectiveViewed(objectiveViewed: ObjectiveViewed){
    return {
        schema: 'iglu:com.heidi/objective_viewed/jsonschema/1-0-0',
        data: objectiveViewed
    }
}
/**
 * Track a Snowplow event for VerificationCompleted.
 * Tracks when entity verification completes for a session
 */
export function trackVerificationCompleted<T extends {} = any>(verificationCompleted: VerificationCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = verificationCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/verification_completed/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow VerificationCompleted entity.
 */
export function createVerificationCompleted(verificationCompleted: VerificationCompleted){
    return {
        schema: 'iglu:com.heidi/verification_completed/jsonschema/2-0-1',
        data: verificationCompleted
    }
}
/**
 * Track a Snowplow event for AuthenticationClicked.
 * Tracks authentication clicked event
 */
export function trackAuthenticationClicked<T extends {} = any>(authenticationClicked: AuthenticationClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = authenticationClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/authentication_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AuthenticationClicked entity.
 */
export function createAuthenticationClicked(authenticationClicked: AuthenticationClicked){
    return {
        schema: 'iglu:com.heidi/authentication_clicked/jsonschema/1-0-1',
        data: authenticationClicked
    }
}
/**
 * Track a Snowplow event for NoteStarted.
 * Tracks note started event
 */
export function trackNoteStarted<T extends {} = any>(noteStarted: NoteStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_started/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteStarted entity.
 */
export function createNoteStarted(noteStarted: NoteStarted){
    return {
        schema: 'iglu:com.heidi/note_started/jsonschema/1-0-1',
        data: noteStarted
    }
}
/**
 * Track a Snowplow event for ReferralViewed.
 * Tracks referral viewed event
 */
export function trackReferralViewed<T extends {} = any>(referralViewed: ReferralViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = referralViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/referral_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ReferralViewed entity.
 */
export function createReferralViewed(referralViewed: ReferralViewed){
    return {
        schema: 'iglu:com.heidi/referral_viewed/jsonschema/1-0-1',
        data: referralViewed
    }
}
/**
 * Track a Snowplow event for UniversalDictationPostProcessingCompleted.
 * Tracks when universal dictation post-processing completes successfully
 */
export function trackUniversalDictationPostProcessingCompleted<T extends {} = any>(universalDictationPostProcessingCompleted: UniversalDictationPostProcessingCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = universalDictationPostProcessingCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/universal_dictation_post_processing_completed/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UniversalDictationPostProcessingCompleted entity.
 */
export function createUniversalDictationPostProcessingCompleted(universalDictationPostProcessingCompleted: UniversalDictationPostProcessingCompleted){
    return {
        schema: 'iglu:com.heidi/universal_dictation_post_processing_completed/jsonschema/1-0-2',
        data: universalDictationPostProcessingCompleted
    }
}
/**
 * Track a Snowplow event for TemplateUpdated.
 * Tracks when a template is modified
 */
export function trackTemplateUpdated<T extends {} = any>(templateUpdated: TemplateUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_updated/jsonschema/2-0-4',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateUpdated entity.
 */
export function createTemplateUpdated(templateUpdated: TemplateUpdated){
    return {
        schema: 'iglu:com.heidi/template_updated/jsonschema/2-0-4',
        data: templateUpdated
    }
}
/**
 * Track a Snowplow event for EvidencePromptsBarShown.
 * Tracks when the in-session Evidence prompts bar is shown to the user
 */
export function trackEvidencePromptsBarShown<T extends {} = any>(evidencePromptsBarShown: EvidencePromptsBarShown & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidencePromptsBarShown;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_prompts_bar_shown/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidencePromptsBarShown entity.
 */
export function createEvidencePromptsBarShown(evidencePromptsBarShown: EvidencePromptsBarShown){
    return {
        schema: 'iglu:com.heidi/evidence_prompts_bar_shown/jsonschema/1-0-0',
        data: evidencePromptsBarShown
    }
}
/**
 * Track a Snowplow event for CodeCopied.
 * Tracks when user copies a clinical code
 */
export function trackCodeCopied<T extends {} = any>(codeCopied: CodeCopied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = codeCopied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/code_copied/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CodeCopied entity.
 */
export function createCodeCopied(codeCopied: CodeCopied){
    return {
        schema: 'iglu:com.heidi/code_copied/jsonschema/1-0-3',
        data: codeCopied
    }
}
/**
 * Track a Snowplow event for ButtonClicked.
 * Tracks button clicks throughout the application
 */
export function trackButtonClicked<T extends {} = any>(buttonClicked: ButtonClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = buttonClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/button_clicked/jsonschema/3-0-12',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ButtonClicked entity.
 */
export function createButtonClicked(buttonClicked: ButtonClicked){
    return {
        schema: 'iglu:com.heidi/button_clicked/jsonschema/3-0-12',
        data: buttonClicked
    }
}
/**
 * Track a Snowplow event for EntityUpdated.
 * Tracks a successfully persisted modification to a business entity
 */
export function trackEntityUpdated<T extends {} = any>(entityUpdated: EntityUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = entityUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/entity_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EntityUpdated entity.
 */
export function createEntityUpdated(entityUpdated: EntityUpdated){
    return {
        schema: 'iglu:com.heidi/entity_updated/jsonschema/1-0-1',
        data: entityUpdated
    }
}
/**
 * Track a Snowplow event for CommsDashboardTabViewed.
 * Tracks when a user views a tab on the Comms dashboard
 */
export function trackCommsDashboardTabViewed<T extends {} = any>(commsDashboardTabViewed: CommsDashboardTabViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = commsDashboardTabViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/comms_dashboard_tab_viewed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CommsDashboardTabViewed entity.
 */
export function createCommsDashboardTabViewed(commsDashboardTabViewed: CommsDashboardTabViewed){
    return {
        schema: 'iglu:com.heidi/comms_dashboard_tab_viewed/jsonschema/1-0-0',
        data: commsDashboardTabViewed
    }
}
/**
 * Track a Snowplow event for ShareAccessRevoked.
 * Tracks when sharing access is revoked for a session
 */
export function trackShareAccessRevoked<T extends {} = any>(shareAccessRevoked: ShareAccessRevoked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareAccessRevoked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_access_revoked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareAccessRevoked entity.
 */
export function createShareAccessRevoked(shareAccessRevoked: ShareAccessRevoked){
    return {
        schema: 'iglu:com.heidi/share_access_revoked/jsonschema/1-0-1',
        data: shareAccessRevoked
    }
}
/**
 * Track a Snowplow event for WorkApprovalActioned.
 * HITL, RPA, or CUA approval prompt shown or resolved
 */
export function trackWorkApprovalActioned<T extends {} = any>(workApprovalActioned: WorkApprovalActioned & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workApprovalActioned;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_approval_actioned/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkApprovalActioned entity.
 */
export function createWorkApprovalActioned(workApprovalActioned: WorkApprovalActioned){
    return {
        schema: 'iglu:com.heidi/work_approval_actioned/jsonschema/1-0-0',
        data: workApprovalActioned
    }
}
/**
 * Track a Snowplow event for SignUpStarted.
 * Tracks when sign up is initiated
 */
export function trackSignUpStarted<T extends {} = any>(signUpStarted: SignUpStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = signUpStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/sign_up_started/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SignUpStarted entity.
 */
export function createSignUpStarted(signUpStarted: SignUpStarted){
    return {
        schema: 'iglu:com.heidi/sign_up_started/jsonschema/1-0-1',
        data: signUpStarted
    }
}
/**
 * Track a Snowplow event for SnippetCreated.
 * Tracks snippet created event
 */
export function trackSnippetCreated<T extends {} = any>(snippetCreated: SnippetCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = snippetCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/snippet_created/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SnippetCreated entity.
 */
export function createSnippetCreated(snippetCreated: SnippetCreated){
    return {
        schema: 'iglu:com.heidi/snippet_created/jsonschema/1-0-3',
        data: snippetCreated
    }
}
/**
 * Track a Snowplow event for TemplateCreated.
 * Tracks when a new template is created
 */
export function trackTemplateCreated<T extends {} = any>(templateCreated: TemplateCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_created/jsonschema/3-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateCreated entity.
 */
export function createTemplateCreated(templateCreated: TemplateCreated){
    return {
        schema: 'iglu:com.heidi/template_created/jsonschema/3-0-2',
        data: templateCreated
    }
}
/**
 * Track a Snowplow event for DocumentViewed.
 * Tracks document viewed event
 */
export function trackDocumentViewed<T extends {} = any>(documentViewed: DocumentViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = documentViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/document_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DocumentViewed entity.
 */
export function createDocumentViewed(documentViewed: DocumentViewed){
    return {
        schema: 'iglu:com.heidi/document_viewed/jsonschema/1-0-1',
        data: documentViewed
    }
}
/**
 * Track a Snowplow event for LibraryDocumentScopeChanged.
 * Tracks library document scope changed event
 */
export function trackLibraryDocumentScopeChanged<T extends {} = any>(libraryDocumentScopeChanged: LibraryDocumentScopeChanged & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = libraryDocumentScopeChanged;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/library_document_scope_changed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LibraryDocumentScopeChanged entity.
 */
export function createLibraryDocumentScopeChanged(libraryDocumentScopeChanged: LibraryDocumentScopeChanged){
    return {
        schema: 'iglu:com.heidi/library_document_scope_changed/jsonschema/1-0-1',
        data: libraryDocumentScopeChanged
    }
}
/**
 * Track a Snowplow event for EvidenceJurisdictionChanged.
 * Tracks when the evidence jurisdiction is changed
 */
export function trackEvidenceJurisdictionChanged<T extends {} = any>(evidenceJurisdictionChanged: EvidenceJurisdictionChanged & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceJurisdictionChanged;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_jurisdiction_changed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceJurisdictionChanged entity.
 */
export function createEvidenceJurisdictionChanged(evidenceJurisdictionChanged: EvidenceJurisdictionChanged){
    return {
        schema: 'iglu:com.heidi/evidence_jurisdiction_changed/jsonschema/1-0-1',
        data: evidenceJurisdictionChanged
    }
}
/**
 * Track a Snowplow event for WorkJobCompleted.
 * Aggregate Work job finished for one agent run, including turns, tools, approvals, references, duration, and credits. Schema 1-0-2 establishes one row per completed agent_run_id; versions through 1-0-1 were emitted per billed tool call, so consumers spanning the cutover must distinguish schema versions. Schema 2-0-0 widens credits_charged from integer to number so fractional charges are preserved.
 */
export function trackWorkJobCompleted<T extends {} = any>(workJobCompleted: WorkJobCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workJobCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_job_completed/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkJobCompleted entity.
 */
export function createWorkJobCompleted(workJobCompleted: WorkJobCompleted){
    return {
        schema: 'iglu:com.heidi/work_job_completed/jsonschema/2-0-0',
        data: workJobCompleted
    }
}
/**
 * Track a Snowplow event for CallRecordingFailed.
 * Tracks when a call recording fails to process
 */
export function trackCallRecordingFailed<T extends {} = any>(callRecordingFailed: CallRecordingFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callRecordingFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_recording_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallRecordingFailed entity.
 */
export function createCallRecordingFailed(callRecordingFailed: CallRecordingFailed){
    return {
        schema: 'iglu:com.heidi/call_recording_failed/jsonschema/1-0-0',
        data: callRecordingFailed
    }
}
/**
 * Track a Snowplow event for PatientViewed.
 * Tracks when patient profile is viewed
 */
export function trackPatientViewed<T extends {} = any>(patientViewed: PatientViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_viewed/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientViewed entity.
 */
export function createPatientViewed(patientViewed: PatientViewed){
    return {
        schema: 'iglu:com.heidi/patient_viewed/jsonschema/2-0-3',
        data: patientViewed
    }
}
/**
 * Track a Snowplow event for CursorDictationPostProcessingCompleted.
 * Tracks when cursor dictation post-processing completes successfully
 */
export function trackCursorDictationPostProcessingCompleted<T extends {} = any>(cursorDictationPostProcessingCompleted: CursorDictationPostProcessingCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = cursorDictationPostProcessingCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/cursor_dictation_post_processing_completed/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CursorDictationPostProcessingCompleted entity.
 */
export function createCursorDictationPostProcessingCompleted(cursorDictationPostProcessingCompleted: CursorDictationPostProcessingCompleted){
    return {
        schema: 'iglu:com.heidi/cursor_dictation_post_processing_completed/jsonschema/1-0-2',
        data: cursorDictationPostProcessingCompleted
    }
}
/**
 * Track a Snowplow event for CodeProcessed.
 * Tracks when clinical codes are processed
 */
export function trackCodeProcessed<T extends {} = any>(codeProcessed: CodeProcessed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = codeProcessed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/code_processed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CodeProcessed entity.
 */
export function createCodeProcessed(codeProcessed: CodeProcessed){
    return {
        schema: 'iglu:com.heidi/code_processed/jsonschema/1-0-1',
        data: codeProcessed
    }
}
/**
 * Track a Snowplow event for ExportSettingToggled.
 * Tracks export setting toggled event
 */
export function trackExportSettingToggled<T extends {} = any>(exportSettingToggled: ExportSettingToggled & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = exportSettingToggled;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/export_setting_toggled/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ExportSettingToggled entity.
 */
export function createExportSettingToggled(exportSettingToggled: ExportSettingToggled){
    return {
        schema: 'iglu:com.heidi/export_setting_toggled/jsonschema/1-0-1',
        data: exportSettingToggled
    }
}
/**
 * Track a Snowplow event for WorkTermsActioned.
 * Research Preview terms viewed, accepted, or declined
 */
export function trackWorkTermsActioned<T extends {} = any>(workTermsActioned: WorkTermsActioned & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workTermsActioned;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_terms_actioned/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkTermsActioned entity.
 */
export function createWorkTermsActioned(workTermsActioned: WorkTermsActioned){
    return {
        schema: 'iglu:com.heidi/work_terms_actioned/jsonschema/1-0-0',
        data: workTermsActioned
    }
}
/**
 * Track a Snowplow event for SignUpSuccessful.
 * Tracks when a user successfully signs up
 */
export function trackSignUpSuccessful<T extends {} = any>(signUpSuccessful: SignUpSuccessful & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = signUpSuccessful;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/sign_up_successful/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SignUpSuccessful entity.
 */
export function createSignUpSuccessful(signUpSuccessful: SignUpSuccessful){
    return {
        schema: 'iglu:com.heidi/sign_up_successful/jsonschema/2-0-1',
        data: signUpSuccessful
    }
}
/**
 * Track a Snowplow event for TemplateEditorUpdated.
 * Tracks template editor updated event
 */
export function trackTemplateEditorUpdated<T extends {} = any>(templateEditorUpdated: TemplateEditorUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateEditorUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_editor_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateEditorUpdated entity.
 */
export function createTemplateEditorUpdated(templateEditorUpdated: TemplateEditorUpdated){
    return {
        schema: 'iglu:com.heidi/template_editor_updated/jsonschema/1-0-1',
        data: templateEditorUpdated
    }
}
/**
 * Track a Snowplow event for TeamEvidenceAccessUpdated.
 * Tracks when a team admin updates Evidence access controls. Emitted on master toggle, access-mode switch, role selection persist, and per-member enable/disable. Count fields are snapshots at persist time for the active access mode only (role_count when by_role, member_enabled_count when by_member).
 */
export function trackTeamEvidenceAccessUpdated<T extends {} = any>(teamEvidenceAccessUpdated: TeamEvidenceAccessUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = teamEvidenceAccessUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/team_evidence_access_updated/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TeamEvidenceAccessUpdated entity.
 */
export function createTeamEvidenceAccessUpdated(teamEvidenceAccessUpdated: TeamEvidenceAccessUpdated){
    return {
        schema: 'iglu:com.heidi/team_evidence_access_updated/jsonschema/1-0-0',
        data: teamEvidenceAccessUpdated
    }
}
/**
 * Track a Snowplow event for RoadmapViewed.
 * Tracks when roadmap is viewed
 */
export function trackRoadmapViewed<T extends {} = any>(roadmapViewed: RoadmapViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = roadmapViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/roadmap_viewed/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow RoadmapViewed entity.
 */
export function createRoadmapViewed(roadmapViewed: RoadmapViewed){
    return {
        schema: 'iglu:com.heidi/roadmap_viewed/jsonschema/2-0-1',
        data: roadmapViewed
    }
}
/**
 * Track a Snowplow event for PanelViewed.
 * Tracks when panel is viewed or opened
 */
export function trackPanelViewed<T extends {} = any>(panelViewed: PanelViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = panelViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/panel_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PanelViewed entity.
 */
export function createPanelViewed(panelViewed: PanelViewed){
    return {
        schema: 'iglu:com.heidi/panel_viewed/jsonschema/1-0-1',
        data: panelViewed
    }
}
/**
 * Track a Snowplow event for FindReplaceSubmitted.
 * Tracks when find/replace is executed
 */
export function trackFindReplaceSubmitted<T extends {} = any>(findReplaceSubmitted: FindReplaceSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = findReplaceSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/find_replace_submitted/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FindReplaceSubmitted entity.
 */
export function createFindReplaceSubmitted(findReplaceSubmitted: FindReplaceSubmitted){
    return {
        schema: 'iglu:com.heidi/find_replace_submitted/jsonschema/2-0-1',
        data: findReplaceSubmitted
    }
}
/**
 * Track a Snowplow event for CpdActivityCompleted.
 * Tracks when a CPD activity finishes processing
 */
export function trackCpdActivityCompleted<T extends {} = any>(cpdActivityCompleted: CpdActivityCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = cpdActivityCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/cpd_activity_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CpdActivityCompleted entity.
 */
export function createCpdActivityCompleted(cpdActivityCompleted: CpdActivityCompleted){
    return {
        schema: 'iglu:com.heidi/cpd_activity_completed/jsonschema/1-0-1',
        data: cpdActivityCompleted
    }
}
/**
 * Track a Snowplow event for AutoLinkSessionsPreferenceUpdateCompleted.
 * Tracks the authoritative outcome of an auto-link sessions preference update
 */
export function trackAutoLinkSessionsPreferenceUpdateCompleted<T extends {} = any>(autoLinkSessionsPreferenceUpdateCompleted: AutoLinkSessionsPreferenceUpdateCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = autoLinkSessionsPreferenceUpdateCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/auto_link_sessions_preference_update_completed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AutoLinkSessionsPreferenceUpdateCompleted entity.
 */
export function createAutoLinkSessionsPreferenceUpdateCompleted(autoLinkSessionsPreferenceUpdateCompleted: AutoLinkSessionsPreferenceUpdateCompleted){
    return {
        schema: 'iglu:com.heidi/auto_link_sessions_preference_update_completed/jsonschema/1-0-0',
        data: autoLinkSessionsPreferenceUpdateCompleted
    }
}
/**
 * Track a Snowplow event for TrialDowngradeClicked.
 * Tracks when user clicks to downgrade from trial
 */
export function trackTrialDowngradeClicked<T extends {} = any>(trialDowngradeClicked: TrialDowngradeClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = trialDowngradeClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/trial_downgrade_clicked/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TrialDowngradeClicked entity.
 */
export function createTrialDowngradeClicked(trialDowngradeClicked: TrialDowngradeClicked){
    return {
        schema: 'iglu:com.heidi/trial_downgrade_clicked/jsonschema/1-0-3',
        data: trialDowngradeClicked
    }
}
/**
 * Track a Snowplow event for NudgeDetectionEvaluated.
 * One in-session nudge detection run was evaluated: every candidate the code-mediated engine considered plus the gate decision for each, so surfaced-vs-suppressed rates are computable. PHI-free: ids, scores, decisions, and counts only; never transcript or quote text.
 */
export function trackNudgeDetectionEvaluated<T extends {} = any>(nudgeDetectionEvaluated: NudgeDetectionEvaluated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = nudgeDetectionEvaluated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/nudge_detection_evaluated/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NudgeDetectionEvaluated entity.
 */
export function createNudgeDetectionEvaluated(nudgeDetectionEvaluated: NudgeDetectionEvaluated){
    return {
        schema: 'iglu:com.heidi/nudge_detection_evaluated/jsonschema/1-0-0',
        data: nudgeDetectionEvaluated
    }
}
/**
 * Track a Snowplow event for DocumentEmailed.
 * Fired when a consult note or document is emailed to one or more recipients.
 */
export function trackDocumentEmailed<T extends {} = any>(documentEmailed: DocumentEmailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = documentEmailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/document_emailed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DocumentEmailed entity.
 */
export function createDocumentEmailed(documentEmailed: DocumentEmailed){
    return {
        schema: 'iglu:com.heidi/document_emailed/jsonschema/1-0-0',
        data: documentEmailed
    }
}
/**
 * Track a Snowplow event for EvidenceExperienceFeedbackViewed.
 * Tracks when the experience feedback nudge is shown
 */
export function trackEvidenceExperienceFeedbackViewed<T extends {} = any>(evidenceExperienceFeedbackViewed: EvidenceExperienceFeedbackViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceExperienceFeedbackViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_experience_feedback_viewed/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceExperienceFeedbackViewed entity.
 */
export function createEvidenceExperienceFeedbackViewed(evidenceExperienceFeedbackViewed: EvidenceExperienceFeedbackViewed){
    return {
        schema: 'iglu:com.heidi/evidence_experience_feedback_viewed/jsonschema/2-0-2',
        data: evidenceExperienceFeedbackViewed
    }
}
/**
 * Track a Snowplow event for EvidenceChatSubmitted.
 * Tracks when an evidence chat query is submitted
 */
export function trackEvidenceChatSubmitted<T extends {} = any>(evidenceChatSubmitted: EvidenceChatSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceChatSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_chat_submitted/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceChatSubmitted entity.
 */
export function createEvidenceChatSubmitted(evidenceChatSubmitted: EvidenceChatSubmitted){
    return {
        schema: 'iglu:com.heidi/evidence_chat_submitted/jsonschema/2-0-3',
        data: evidenceChatSubmitted
    }
}
/**
 * Track a Snowplow event for EvidenceExperienceFeedbackRatingSubmitted.
 * Tracks when a user submits an experience feedback star rating
 */
export function trackEvidenceExperienceFeedbackRatingSubmitted<T extends {} = any>(evidenceExperienceFeedbackRatingSubmitted: EvidenceExperienceFeedbackRatingSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceExperienceFeedbackRatingSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_experience_feedback_rating_submitted/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceExperienceFeedbackRatingSubmitted entity.
 */
export function createEvidenceExperienceFeedbackRatingSubmitted(evidenceExperienceFeedbackRatingSubmitted: EvidenceExperienceFeedbackRatingSubmitted){
    return {
        schema: 'iglu:com.heidi/evidence_experience_feedback_rating_submitted/jsonschema/2-0-2',
        data: evidenceExperienceFeedbackRatingSubmitted
    }
}
/**
 * Track a Snowplow event for PatientDocumentLinked.
 * Tracks when patient documents are linked to a session as context
 */
export function trackPatientDocumentLinked<T extends {} = any>(patientDocumentLinked: PatientDocumentLinked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientDocumentLinked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_document_linked/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientDocumentLinked entity.
 */
export function createPatientDocumentLinked(patientDocumentLinked: PatientDocumentLinked){
    return {
        schema: 'iglu:com.heidi/patient_document_linked/jsonschema/1-0-0',
        data: patientDocumentLinked
    }
}
/**
 * Track a Snowplow event for ProductTourViewed.
 * Tracks product tour viewed event
 */
export function trackProductTourViewed<T extends {} = any>(productTourViewed: ProductTourViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = productTourViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/product_tour_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ProductTourViewed entity.
 */
export function createProductTourViewed(productTourViewed: ProductTourViewed){
    return {
        schema: 'iglu:com.heidi/product_tour_viewed/jsonschema/1-0-1',
        data: productTourViewed
    }
}
/**
 * Track a Snowplow event for SessionsListPaginated.
 * Tracks each successful infinite-scroll fetch of more sessions in the list
 */
export function trackSessionsListPaginated<T extends {} = any>(sessionsListPaginated: SessionsListPaginated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionsListPaginated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/sessions_list_paginated/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionsListPaginated entity.
 */
export function createSessionsListPaginated(sessionsListPaginated: SessionsListPaginated){
    return {
        schema: 'iglu:com.heidi/sessions_list_paginated/jsonschema/1-0-0',
        data: sessionsListPaginated
    }
}
/**
 * Track a Snowplow event for AudioUploaded.
 * Tracks audio uploaded event
 */
export function trackAudioUploaded<T extends {} = any>(audioUploaded: AudioUploaded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = audioUploaded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/audio_uploaded/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AudioUploaded entity.
 */
export function createAudioUploaded(audioUploaded: AudioUploaded){
    return {
        schema: 'iglu:com.heidi/audio_uploaded/jsonschema/1-0-1',
        data: audioUploaded
    }
}
/**
 * Track a Snowplow event for TeamUpdated.
 * Tracks when team settings or information is updated
 */
export function trackTeamUpdated<T extends {} = any>(teamUpdated: TeamUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = teamUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/team_updated/jsonschema/2-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TeamUpdated entity.
 */
export function createTeamUpdated(teamUpdated: TeamUpdated){
    return {
        schema: 'iglu:com.heidi/team_updated/jsonschema/2-1-0',
        data: teamUpdated
    }
}
/**
 * Track a Snowplow event for HardwareDeviceLinked.
 * Tracks when a hardware device is linked
 */
export function trackHardwareDeviceLinked<T extends {} = any>(hardwareDeviceLinked: HardwareDeviceLinked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = hardwareDeviceLinked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/hardware_device_linked/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow HardwareDeviceLinked entity.
 */
export function createHardwareDeviceLinked(hardwareDeviceLinked: HardwareDeviceLinked){
    return {
        schema: 'iglu:com.heidi/hardware_device_linked/jsonschema/1-0-0',
        data: hardwareDeviceLinked
    }
}
/**
 * Track a Snowplow event for TemplateCompleted.
 * Tracks template completed event
 */
export function trackTemplateCompleted<T extends {} = any>(templateCompleted: TemplateCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateCompleted entity.
 */
export function createTemplateCompleted(templateCompleted: TemplateCompleted){
    return {
        schema: 'iglu:com.heidi/template_completed/jsonschema/1-0-1',
        data: templateCompleted
    }
}
/**
 * Track a Snowplow event for CallVoicemailLeft.
 * Tracks when a voicemail message is left on an outbound call
 */
export function trackCallVoicemailLeft<T extends {} = any>(callVoicemailLeft: CallVoicemailLeft & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callVoicemailLeft;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_voicemail_left/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallVoicemailLeft entity.
 */
export function createCallVoicemailLeft(callVoicemailLeft: CallVoicemailLeft){
    return {
        schema: 'iglu:com.heidi/call_voicemail_left/jsonschema/1-0-0',
        data: callVoicemailLeft
    }
}
/**
 * Track a Snowplow event for PersonalizationUpdated.
 * Tracks personalization updated event
 */
export function trackPersonalizationUpdated<T extends {} = any>(personalizationUpdated: PersonalizationUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = personalizationUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/personalization_updated/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PersonalizationUpdated entity.
 */
export function createPersonalizationUpdated(personalizationUpdated: PersonalizationUpdated){
    return {
        schema: 'iglu:com.heidi/personalization_updated/jsonschema/1-0-2',
        data: personalizationUpdated
    }
}
/**
 * Track a Snowplow event for ContextUpdated.
 * Tracks context updated event
 */
export function trackContextUpdated<T extends {} = any>(contextUpdated: ContextUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = contextUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/context_updated/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ContextUpdated entity.
 */
export function createContextUpdated(contextUpdated: ContextUpdated){
    return {
        schema: 'iglu:com.heidi/context_updated/jsonschema/1-0-3',
        data: contextUpdated
    }
}
/**
 * Track a Snowplow event for AudioStopped.
 * Tracks audio stopped event
 */
export function trackAudioStopped<T extends {} = any>(audioStopped: AudioStopped & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = audioStopped;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/audio_stopped/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AudioStopped entity.
 */
export function createAudioStopped(audioStopped: AudioStopped){
    return {
        schema: 'iglu:com.heidi/audio_stopped/jsonschema/1-0-1',
        data: audioStopped
    }
}
/**
 * Track a Snowplow event for FirstEvidenceCompleted.
 * Tracks when a user completes their first Evidence query within their first week of account creation
 */
export function trackFirstEvidenceCompleted<T extends {} = any>(firstEvidenceCompleted: FirstEvidenceCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = firstEvidenceCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/first_evidence_completed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FirstEvidenceCompleted entity.
 */
export function createFirstEvidenceCompleted(firstEvidenceCompleted: FirstEvidenceCompleted){
    return {
        schema: 'iglu:com.heidi/first_evidence_completed/jsonschema/1-0-0',
        data: firstEvidenceCompleted
    }
}
/**
 * Track a Snowplow event for OfflineRecordingCreated.
 * Tracks creation of a local desktop session shell for offline recording
 */
export function trackOfflineRecordingCreated<T extends {} = any>(offlineRecordingCreated: OfflineRecordingCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = offlineRecordingCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/offline_recording_created/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OfflineRecordingCreated entity.
 */
export function createOfflineRecordingCreated(offlineRecordingCreated: OfflineRecordingCreated){
    return {
        schema: 'iglu:com.heidi/offline_recording_created/jsonschema/1-0-0',
        data: offlineRecordingCreated
    }
}
/**
 * Track a Snowplow event for EvidenceActivated.
 * Tracks when Evidence is activated for a user
 */
export function trackEvidenceActivated<T extends {} = any>(evidenceActivated: EvidenceActivated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceActivated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_activated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceActivated entity.
 */
export function createEvidenceActivated(evidenceActivated: EvidenceActivated){
    return {
        schema: 'iglu:com.heidi/evidence_activated/jsonschema/1-0-1',
        data: evidenceActivated
    }
}
/**
 * Track a Snowplow event for CoachMarkActionTaken.
 * Tracks coach mark action taken event
 */
export function trackCoachMarkActionTaken<T extends {} = any>(coachMarkActionTaken: CoachMarkActionTaken & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = coachMarkActionTaken;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/coach_mark_action_taken/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CoachMarkActionTaken entity.
 */
export function createCoachMarkActionTaken(coachMarkActionTaken: CoachMarkActionTaken){
    return {
        schema: 'iglu:com.heidi/coach_mark_action_taken/jsonschema/1-0-1',
        data: coachMarkActionTaken
    }
}
/**
 * Track a Snowplow event for AdminUserCreated.
 * Tracks when a user is created or added to a team via internal admin tools
 */
export function trackAdminUserCreated<T extends {} = any>(adminUserCreated: AdminUserCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = adminUserCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/admin_user_created/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AdminUserCreated entity.
 */
export function createAdminUserCreated(adminUserCreated: AdminUserCreated){
    return {
        schema: 'iglu:com.heidi/admin_user_created/jsonschema/1-0-1',
        data: adminUserCreated
    }
}
/**
 * Track a Snowplow event for NoteViewed.
 * Tracks note viewed event
 */
export function trackNoteViewed<T extends {} = any>(noteViewed: NoteViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteViewed entity.
 */
export function createNoteViewed(noteViewed: NoteViewed){
    return {
        schema: 'iglu:com.heidi/note_viewed/jsonschema/1-0-1',
        data: noteViewed
    }
}
/**
 * Track a Snowplow event for EvidencePromptsBarDismissed.
 * Tracks when the user dismisses the in-session Evidence prompts bar
 */
export function trackEvidencePromptsBarDismissed<T extends {} = any>(evidencePromptsBarDismissed: EvidencePromptsBarDismissed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidencePromptsBarDismissed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_prompts_bar_dismissed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidencePromptsBarDismissed entity.
 */
export function createEvidencePromptsBarDismissed(evidencePromptsBarDismissed: EvidencePromptsBarDismissed){
    return {
        schema: 'iglu:com.heidi/evidence_prompts_bar_dismissed/jsonschema/1-0-0',
        data: evidencePromptsBarDismissed
    }
}
/**
 * Track a Snowplow event for CallSummaryFailed.
 * Tracks when a call summary fails to generate
 */
export function trackCallSummaryFailed<T extends {} = any>(callSummaryFailed: CallSummaryFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callSummaryFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_summary_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallSummaryFailed entity.
 */
export function createCallSummaryFailed(callSummaryFailed: CallSummaryFailed){
    return {
        schema: 'iglu:com.heidi/call_summary_failed/jsonschema/1-0-0',
        data: callSummaryFailed
    }
}
/**
 * Track a Snowplow event for OutboundMessageQueued.
 * Tracks when an outbound message is queued for delivery
 */
export function trackOutboundMessageQueued<T extends {} = any>(outboundMessageQueued: OutboundMessageQueued & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = outboundMessageQueued;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/outbound_message_queued/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OutboundMessageQueued entity.
 */
export function createOutboundMessageQueued(outboundMessageQueued: OutboundMessageQueued){
    return {
        schema: 'iglu:com.heidi/outbound_message_queued/jsonschema/1-0-0',
        data: outboundMessageQueued
    }
}
/**
 * Track a Snowplow event for ObjectiveExecutionFailed.
 * Tracks when an AI objective execution fails
 */
export function trackObjectiveExecutionFailed<T extends {} = any>(objectiveExecutionFailed: ObjectiveExecutionFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = objectiveExecutionFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/objective_execution_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ObjectiveExecutionFailed entity.
 */
export function createObjectiveExecutionFailed(objectiveExecutionFailed: ObjectiveExecutionFailed){
    return {
        schema: 'iglu:com.heidi/objective_execution_failed/jsonschema/1-0-0',
        data: objectiveExecutionFailed
    }
}
/**
 * Track a Snowplow event for UserSignedIn.
 * Tracks when a user signs in
 */
export function trackUserSignedIn<T extends {} = any>(userSignedIn: UserSignedIn & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = userSignedIn;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/user_signed_in/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UserSignedIn entity.
 */
export function createUserSignedIn(userSignedIn: UserSignedIn){
    return {
        schema: 'iglu:com.heidi/user_signed_in/jsonschema/2-0-1',
        data: userSignedIn
    }
}
/**
 * Track a Snowplow event for PatientExtractionReviewed.
 * Tracks patient extraction reviewed event
 */
export function trackPatientExtractionReviewed<T extends {} = any>(patientExtractionReviewed: PatientExtractionReviewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientExtractionReviewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_extraction_reviewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientExtractionReviewed entity.
 */
export function createPatientExtractionReviewed(patientExtractionReviewed: PatientExtractionReviewed){
    return {
        schema: 'iglu:com.heidi/patient_extraction_reviewed/jsonschema/1-0-1',
        data: patientExtractionReviewed
    }
}
/**
 * Track a Snowplow event for PaywallViewed.
 * Tracks when paywall is displayed
 */
export function trackPaywallViewed<T extends {} = any>(paywallViewed: PaywallViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = paywallViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/paywall_viewed/jsonschema/3-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PaywallViewed entity.
 */
export function createPaywallViewed(paywallViewed: PaywallViewed){
    return {
        schema: 'iglu:com.heidi/paywall_viewed/jsonschema/3-0-3',
        data: paywallViewed
    }
}
/**
 * Track a Snowplow event for NudgeItemUnchecked.
 * A nudge checklist item was unchecked
 */
export function trackNudgeItemUnchecked<T extends {} = any>(nudgeItemUnchecked: NudgeItemUnchecked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = nudgeItemUnchecked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/nudge_item_unchecked/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NudgeItemUnchecked entity.
 */
export function createNudgeItemUnchecked(nudgeItemUnchecked: NudgeItemUnchecked){
    return {
        schema: 'iglu:com.heidi/nudge_item_unchecked/jsonschema/1-0-0',
        data: nudgeItemUnchecked
    }
}
/**
 * Track a Snowplow event for EvidenceSourceClicked.
 * Tracks evidence source clicked event
 */
export function trackEvidenceSourceClicked<T extends {} = any>(evidenceSourceClicked: EvidenceSourceClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceSourceClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_source_clicked/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceSourceClicked entity.
 */
export function createEvidenceSourceClicked(evidenceSourceClicked: EvidenceSourceClicked){
    return {
        schema: 'iglu:com.heidi/evidence_source_clicked/jsonschema/1-0-2',
        data: evidenceSourceClicked
    }
}
/**
 * Track a Snowplow event for ShareSaved.
 * Tracks when share settings are saved
 */
export function trackShareSaved<T extends {} = any>(shareSaved: ShareSaved & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareSaved;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_saved/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareSaved entity.
 */
export function createShareSaved(shareSaved: ShareSaved){
    return {
        schema: 'iglu:com.heidi/share_saved/jsonschema/1-0-1',
        data: shareSaved
    }
}
/**
 * Track a Snowplow event for DeeplinkTriggered.
 * Tracks deeplink triggered event
 */
export function trackDeeplinkTriggered<T extends {} = any>(deeplinkTriggered: DeeplinkTriggered & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = deeplinkTriggered;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/deeplink_triggered/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DeeplinkTriggered entity.
 */
export function createDeeplinkTriggered(deeplinkTriggered: DeeplinkTriggered){
    return {
        schema: 'iglu:com.heidi/deeplink_triggered/jsonschema/1-0-1',
        data: deeplinkTriggered
    }
}
/**
 * Track a Snowplow event for DemoCompleted.
 * Tracks demo completed event
 */
export function trackDemoCompleted<T extends {} = any>(demoCompleted: DemoCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = demoCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/demo_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DemoCompleted entity.
 */
export function createDemoCompleted(demoCompleted: DemoCompleted){
    return {
        schema: 'iglu:com.heidi/demo_completed/jsonschema/1-0-1',
        data: demoCompleted
    }
}
/**
 * Track a Snowplow event for PatientLinked.
 * Tracks when sessions are linked to patient
 */
export function trackPatientLinked<T extends {} = any>(patientLinked: PatientLinked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientLinked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_linked/jsonschema/2-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientLinked entity.
 */
export function createPatientLinked(patientLinked: PatientLinked){
    return {
        schema: 'iglu:com.heidi/patient_linked/jsonschema/2-1-0',
        data: patientLinked
    }
}
/**
 * Track a Snowplow event for AuthTokenUpdated.
 * Tracks when an authentication token is set or updated
 */
export function trackAuthTokenUpdated<T extends {} = any>(authTokenUpdated: AuthTokenUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = authTokenUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/auth_token_updated/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AuthTokenUpdated entity.
 */
export function createAuthTokenUpdated(authTokenUpdated: AuthTokenUpdated){
    return {
        schema: 'iglu:com.heidi/auth_token_updated/jsonschema/1-0-3',
        data: authTokenUpdated
    }
}
/**
 * Track a Snowplow event for NudgeSurfaced.
 * A nudge card was auto-surfaced by the classifier
 */
export function trackNudgeSurfaced<T extends {} = any>(nudgeSurfaced: NudgeSurfaced & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = nudgeSurfaced;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/nudge_surfaced/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NudgeSurfaced entity.
 */
export function createNudgeSurfaced(nudgeSurfaced: NudgeSurfaced){
    return {
        schema: 'iglu:com.heidi/nudge_surfaced/jsonschema/1-0-0',
        data: nudgeSurfaced
    }
}
/**
 * Track a Snowplow event for ExportContentSaved.
 * Tracks export content saved event
 */
export function trackExportContentSaved<T extends {} = any>(exportContentSaved: ExportContentSaved & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = exportContentSaved;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/export_content_saved/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ExportContentSaved entity.
 */
export function createExportContentSaved(exportContentSaved: ExportContentSaved){
    return {
        schema: 'iglu:com.heidi/export_content_saved/jsonschema/1-0-1',
        data: exportContentSaved
    }
}
/**
 * Track a Snowplow event for TrialActivated.
 * Tracks when a user activates their trial by completing their first session after signup, marking when the trial countdown begins
 */
export function trackTrialActivated<T extends {} = any>(trialActivated: TrialActivated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = trialActivated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/trial_activated/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TrialActivated entity.
 */
export function createTrialActivated(trialActivated: TrialActivated){
    return {
        schema: 'iglu:com.heidi/trial_activated/jsonschema/2-0-2',
        data: trialActivated
    }
}
/**
 * Track a Snowplow event for FirstDictationCompleted.
 * Tracks when a user completes their first dictation within their first week of account creation
 */
export function trackFirstDictationCompleted<T extends {} = any>(firstDictationCompleted: FirstDictationCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = firstDictationCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/first_dictation_completed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FirstDictationCompleted entity.
 */
export function createFirstDictationCompleted(firstDictationCompleted: FirstDictationCompleted){
    return {
        schema: 'iglu:com.heidi/first_dictation_completed/jsonschema/1-0-0',
        data: firstDictationCompleted
    }
}
/**
 * Track a Snowplow event for CallTransferred.
 * Tracks when a call is transferred to another party
 */
export function trackCallTransferred<T extends {} = any>(callTransferred: CallTransferred & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callTransferred;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_transferred/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallTransferred entity.
 */
export function createCallTransferred(callTransferred: CallTransferred){
    return {
        schema: 'iglu:com.heidi/call_transferred/jsonschema/1-0-0',
        data: callTransferred
    }
}
/**
 * Track a Snowplow event for CheckoutCompleted.
 * Tracks when a checkout is completed (Stripe checkout.session.completed)
 */
export function trackCheckoutCompleted<T extends {} = any>(checkoutCompleted: CheckoutCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = checkoutCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/checkout_completed/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CheckoutCompleted entity.
 */
export function createCheckoutCompleted(checkoutCompleted: CheckoutCompleted){
    return {
        schema: 'iglu:com.heidi/checkout_completed/jsonschema/2-0-1',
        data: checkoutCompleted
    }
}
/**
 * Track a Snowplow event for ObjectiveRemoved.
 * Tracks when a user removes an objective
 */
export function trackObjectiveRemoved<T extends {} = any>(objectiveRemoved: ObjectiveRemoved & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = objectiveRemoved;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/objective_removed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ObjectiveRemoved entity.
 */
export function createObjectiveRemoved(objectiveRemoved: ObjectiveRemoved){
    return {
        schema: 'iglu:com.heidi/objective_removed/jsonschema/1-0-0',
        data: objectiveRemoved
    }
}
/**
 * Track a Snowplow event for CommunityCreated.
 * Tracks community created event
 */
export function trackCommunityCreated<T extends {} = any>(communityCreated: CommunityCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = communityCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/community_created/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CommunityCreated entity.
 */
export function createCommunityCreated(communityCreated: CommunityCreated){
    return {
        schema: 'iglu:com.heidi/community_created/jsonschema/1-0-1',
        data: communityCreated
    }
}
/**
 * Track a Snowplow event for EvidenceChatError.
 * Tracks when an evidence chat encounters an error
 */
export function trackEvidenceChatError<T extends {} = any>(evidenceChatError: EvidenceChatError & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceChatError;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_chat_error/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceChatError entity.
 */
export function createEvidenceChatError(evidenceChatError: EvidenceChatError){
    return {
        schema: 'iglu:com.heidi/evidence_chat_error/jsonschema/2-0-2',
        data: evidenceChatError
    }
}
/**
 * Track a Snowplow event for SessionMarkedAsContextOnly.
 * Tracks when a session is marked as context only
 */
export function trackSessionMarkedAsContextOnly<T extends {} = any>(sessionMarkedAsContextOnly: SessionMarkedAsContextOnly & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionMarkedAsContextOnly;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/session_marked_as_context_only/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionMarkedAsContextOnly entity.
 */
export function createSessionMarkedAsContextOnly(sessionMarkedAsContextOnly: SessionMarkedAsContextOnly){
    return {
        schema: 'iglu:com.heidi/session_marked_as_context_only/jsonschema/2-0-0',
        data: sessionMarkedAsContextOnly
    }
}
/**
 * Track a Snowplow event for FormTemplateFieldsUpdated.
 * Tracks when form template fields are updated
 */
export function trackFormTemplateFieldsUpdated<T extends {} = any>(formTemplateFieldsUpdated: FormTemplateFieldsUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = formTemplateFieldsUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/form_template_fields_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FormTemplateFieldsUpdated entity.
 */
export function createFormTemplateFieldsUpdated(formTemplateFieldsUpdated: FormTemplateFieldsUpdated){
    return {
        schema: 'iglu:com.heidi/form_template_fields_updated/jsonschema/1-0-1',
        data: formTemplateFieldsUpdated
    }
}
/**
 * Track a Snowplow event for IntegrationSessionCreated.
 * Tracks integration session created event
 */
export function trackIntegrationSessionCreated<T extends {} = any>(integrationSessionCreated: IntegrationSessionCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationSessionCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_session_created/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationSessionCreated entity.
 */
export function createIntegrationSessionCreated(integrationSessionCreated: IntegrationSessionCreated){
    return {
        schema: 'iglu:com.heidi/integration_session_created/jsonschema/1-0-1',
        data: integrationSessionCreated
    }
}
/**
 * Track a Snowplow event for SurveyViewed.
 * Tracks survey viewed event
 */
export function trackSurveyViewed<T extends {} = any>(surveyViewed: SurveyViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = surveyViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/survey_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SurveyViewed entity.
 */
export function createSurveyViewed(surveyViewed: SurveyViewed){
    return {
        schema: 'iglu:com.heidi/survey_viewed/jsonschema/1-0-1',
        data: surveyViewed
    }
}
/**
 * Track a Snowplow event for CommsSetupCompleted.
 * Tracks when a user completes the Comms setup flow
 */
export function trackCommsSetupCompleted<T extends {} = any>(commsSetupCompleted: CommsSetupCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = commsSetupCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/comms_setup_completed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CommsSetupCompleted entity.
 */
export function createCommsSetupCompleted(commsSetupCompleted: CommsSetupCompleted){
    return {
        schema: 'iglu:com.heidi/comms_setup_completed/jsonschema/1-0-0',
        data: commsSetupCompleted
    }
}
/**
 * Track a Snowplow event for OfflineSyncCompleted.
 * Tracks successful completion of an offline recording sync
 */
export function trackOfflineSyncCompleted<T extends {} = any>(offlineSyncCompleted: OfflineSyncCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = offlineSyncCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/offline_sync_completed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OfflineSyncCompleted entity.
 */
export function createOfflineSyncCompleted(offlineSyncCompleted: OfflineSyncCompleted){
    return {
        schema: 'iglu:com.heidi/offline_sync_completed/jsonschema/1-0-0',
        data: offlineSyncCompleted
    }
}
/**
 * Track a Snowplow event for ContextUploaded.
 * Tracks context uploaded event
 */
export function trackContextUploaded<T extends {} = any>(contextUploaded: ContextUploaded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = contextUploaded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/context_uploaded/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ContextUploaded entity.
 */
export function createContextUploaded(contextUploaded: ContextUploaded){
    return {
        schema: 'iglu:com.heidi/context_uploaded/jsonschema/1-0-1',
        data: contextUploaded
    }
}
/**
 * Track a Snowplow event for ScheduleUploadProcessingFailed.
 * Tracks schedule upload processing failed event
 */
export function trackScheduleUploadProcessingFailed<T extends {} = any>(scheduleUploadProcessingFailed: ScheduleUploadProcessingFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = scheduleUploadProcessingFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/schedule_upload_processing_failed/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScheduleUploadProcessingFailed entity.
 */
export function createScheduleUploadProcessingFailed(scheduleUploadProcessingFailed: ScheduleUploadProcessingFailed){
    return {
        schema: 'iglu:com.heidi/schedule_upload_processing_failed/jsonschema/1-0-2',
        data: scheduleUploadProcessingFailed
    }
}
/**
 * Track a Snowplow event for TaskDeleted.
 * Tracks task deleted event
 */
export function trackTaskDeleted<T extends {} = any>(taskDeleted: TaskDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = taskDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/task_deleted/jsonschema/1-1-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TaskDeleted entity.
 */
export function createTaskDeleted(taskDeleted: TaskDeleted){
    return {
        schema: 'iglu:com.heidi/task_deleted/jsonschema/1-1-1',
        data: taskDeleted
    }
}
/**
 * Track a Snowplow event for SourceRecommendationApplied.
 * Tracks when a user applies the personalised source recommendation, either directly or via the customise wizard. Conversion leg of the source-recommendation funnel (shown -&gt; applied). &#x60;matched_*&#x60; fields reflect the BE-selected recommendation; &#x60;removed_*&#x60; / &#x60;added_*&#x60; arrays carry the diff between the offered list and what the user actually saved (only populated on the wizard path). 2-0-0: renamed &#x60;location&#x60; -&gt; &#x60;banner_location&#x60; (MODEL bump). The 1-0-0 property name caused snowtype to emit a top-level TypeScript &#x60;Location&#x60; alias that shadowed the DOM &#x60;Location&#x60; global; the rename scopes the alias to the source-recommendation banner and makes snowtype emit &#x60;BannerLocation&#x60; instead. No other shape changes vs 1-0-0.
 */
export function trackSourceRecommendationApplied<T extends {} = any>(sourceRecommendationApplied: SourceRecommendationApplied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sourceRecommendationApplied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/source_recommendation_applied/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SourceRecommendationApplied entity.
 */
export function createSourceRecommendationApplied(sourceRecommendationApplied: SourceRecommendationApplied){
    return {
        schema: 'iglu:com.heidi/source_recommendation_applied/jsonschema/2-0-0',
        data: sourceRecommendationApplied
    }
}
/**
 * Track a Snowplow event for ObjectivePublished.
 * Tracks when an AI objective is published
 */
export function trackObjectivePublished<T extends {} = any>(objectivePublished: ObjectivePublished & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = objectivePublished;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/objective_published/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ObjectivePublished entity.
 */
export function createObjectivePublished(objectivePublished: ObjectivePublished){
    return {
        schema: 'iglu:com.heidi/objective_published/jsonschema/1-0-0',
        data: objectivePublished
    }
}
/**
 * Track a Snowplow event for ProductSignUpSuccessful.
 * Tracks when a sign up is attributed to a specific product feature
 */
export function trackProductSignUpSuccessful<T extends {} = any>(productSignUpSuccessful: ProductSignUpSuccessful & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = productSignUpSuccessful;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/product_sign_up_successful/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ProductSignUpSuccessful entity.
 */
export function createProductSignUpSuccessful(productSignUpSuccessful: ProductSignUpSuccessful){
    return {
        schema: 'iglu:com.heidi/product_sign_up_successful/jsonschema/1-0-2',
        data: productSignUpSuccessful
    }
}
/**
 * Track a Snowplow event for ProductTourCompleted.
 * Tracks when product tour is completed
 */
export function trackProductTourCompleted<T extends {} = any>(productTourCompleted: ProductTourCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = productTourCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/product_tour_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ProductTourCompleted entity.
 */
export function createProductTourCompleted(productTourCompleted: ProductTourCompleted){
    return {
        schema: 'iglu:com.heidi/product_tour_completed/jsonschema/1-0-1',
        data: productTourCompleted
    }
}
/**
 * Track a Snowplow event for RecordingConsentCaptured.
 * Tracks when recording consent is captured from a participant
 */
export function trackRecordingConsentCaptured<T extends {} = any>(recordingConsentCaptured: RecordingConsentCaptured & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = recordingConsentCaptured;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/recording_consent_captured/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow RecordingConsentCaptured entity.
 */
export function createRecordingConsentCaptured(recordingConsentCaptured: RecordingConsentCaptured){
    return {
        schema: 'iglu:com.heidi/recording_consent_captured/jsonschema/1-0-0',
        data: recordingConsentCaptured
    }
}
/**
 * Track a Snowplow event for CpdExported.
 * Tracks when user exports CPD (Continuing Professional Development) data
 */
export function trackCpdExported<T extends {} = any>(cpdExported: CpdExported & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = cpdExported;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/cpd_exported/jsonschema/1-0-4',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CpdExported entity.
 */
export function createCpdExported(cpdExported: CpdExported){
    return {
        schema: 'iglu:com.heidi/cpd_exported/jsonschema/1-0-4',
        data: cpdExported
    }
}
/**
 * Track a Snowplow event for TrialExtendClicked.
 * Tracks when user clicks to extend trial
 */
export function trackTrialExtendClicked<T extends {} = any>(trialExtendClicked: TrialExtendClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = trialExtendClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/trial_extend_clicked/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TrialExtendClicked entity.
 */
export function createTrialExtendClicked(trialExtendClicked: TrialExtendClicked){
    return {
        schema: 'iglu:com.heidi/trial_extend_clicked/jsonschema/1-0-3',
        data: trialExtendClicked
    }
}
/**
 * Track a Snowplow event for SectionDeleted.
 * Tracks when a section is deleted in the note, template, or document editor
 */
export function trackSectionDeleted<T extends {} = any>(sectionDeleted: SectionDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sectionDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/section_deleted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SectionDeleted entity.
 */
export function createSectionDeleted(sectionDeleted: SectionDeleted){
    return {
        schema: 'iglu:com.heidi/section_deleted/jsonschema/1-0-1',
        data: sectionDeleted
    }
}
/**
 * Track a Snowplow event for EvidenceShortcutRun.
 * Tracks when an Ask Heidi shortcut is run
 */
export function trackEvidenceShortcutRun<T extends {} = any>(evidenceShortcutRun: EvidenceShortcutRun & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceShortcutRun;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_shortcut_run/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceShortcutRun entity.
 */
export function createEvidenceShortcutRun(evidenceShortcutRun: EvidenceShortcutRun){
    return {
        schema: 'iglu:com.heidi/evidence_shortcut_run/jsonschema/1-0-0',
        data: evidenceShortcutRun
    }
}
/**
 * Track a Snowplow event for ChatContextAddFileClicked.
 * Tracks chat context add file clicked event
 */
export function trackChatContextAddFileClicked<T extends {} = any>(chatContextAddFileClicked: ChatContextAddFileClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chatContextAddFileClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/chat_context_add_file_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChatContextAddFileClicked entity.
 */
export function createChatContextAddFileClicked(chatContextAddFileClicked: ChatContextAddFileClicked){
    return {
        schema: 'iglu:com.heidi/chat_context_add_file_clicked/jsonschema/1-0-1',
        data: chatContextAddFileClicked
    }
}
/**
 * Track a Snowplow event for PatientContextToggleOff.
 * Tracks patient context toggle off event
 */
export function trackPatientContextToggleOff<T extends {} = any>(patientContextToggleOff: PatientContextToggleOff & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientContextToggleOff;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_context_toggle_off/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientContextToggleOff entity.
 */
export function createPatientContextToggleOff(patientContextToggleOff: PatientContextToggleOff){
    return {
        schema: 'iglu:com.heidi/patient_context_toggle_off/jsonschema/1-0-1',
        data: patientContextToggleOff
    }
}
/**
 * Track a Snowplow event for CodeSearched.
 * Tracks when user searches for clinical codes
 */
export function trackCodeSearched<T extends {} = any>(codeSearched: CodeSearched & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = codeSearched;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/code_searched/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CodeSearched entity.
 */
export function createCodeSearched(codeSearched: CodeSearched){
    return {
        schema: 'iglu:com.heidi/code_searched/jsonschema/1-0-1',
        data: codeSearched
    }
}
/**
 * Track a Snowplow event for SettingsUpdated.
 * Tracks settings updated event. For update_action copy_setting_toggled (one of the four copy-formatting toggles flipped), the event records user intent at click time, not confirmed backend persistence.
 */
export function trackSettingsUpdated<T extends {} = any>(settingsUpdated: SettingsUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = settingsUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/settings_updated/jsonschema/1-0-5',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SettingsUpdated entity.
 */
export function createSettingsUpdated(settingsUpdated: SettingsUpdated){
    return {
        schema: 'iglu:com.heidi/settings_updated/jsonschema/1-0-5',
        data: settingsUpdated
    }
}
/**
 * Track a Snowplow event for OrganisationSurveySubmitted.
 * Tracks organisation survey submitted event
 */
export function trackOrganisationSurveySubmitted<T extends {} = any>(organisationSurveySubmitted: OrganisationSurveySubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = organisationSurveySubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/organisation_survey_submitted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OrganisationSurveySubmitted entity.
 */
export function createOrganisationSurveySubmitted(organisationSurveySubmitted: OrganisationSurveySubmitted){
    return {
        schema: 'iglu:com.heidi/organisation_survey_submitted/jsonschema/1-0-1',
        data: organisationSurveySubmitted
    }
}
/**
 * Track a Snowplow event for PersonalizationFailed.
 * Tracks when personalisation insight extraction fails
 */
export function trackPersonalizationFailed<T extends {} = any>(personalizationFailed: PersonalizationFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = personalizationFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/personalization_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PersonalizationFailed entity.
 */
export function createPersonalizationFailed(personalizationFailed: PersonalizationFailed){
    return {
        schema: 'iglu:com.heidi/personalization_failed/jsonschema/1-0-0',
        data: personalizationFailed
    }
}
/**
 * Track a Snowplow event for IntegrationUnlinked.
 * Tracks integration unlinked event
 */
export function trackIntegrationUnlinked<T extends {} = any>(integrationUnlinked: IntegrationUnlinked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationUnlinked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_unlinked/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationUnlinked entity.
 */
export function createIntegrationUnlinked(integrationUnlinked: IntegrationUnlinked){
    return {
        schema: 'iglu:com.heidi/integration_unlinked/jsonschema/1-0-2',
        data: integrationUnlinked
    }
}
/**
 * Track a Snowplow event for LetterCreated.
 * Tracks successful creation of a letter, from a session document or directly with empty content
 */
export function trackLetterCreated<T extends {} = any>(letterCreated: LetterCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = letterCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/letter_created/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LetterCreated entity.
 */
export function createLetterCreated(letterCreated: LetterCreated){
    return {
        schema: 'iglu:com.heidi/letter_created/jsonschema/2-0-0',
        data: letterCreated
    }
}
/**
 * Track a Snowplow event for PatientDocumentUploaded.
 * Tracks when a patient document upload completes FE-side
 */
export function trackPatientDocumentUploaded<T extends {} = any>(patientDocumentUploaded: PatientDocumentUploaded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientDocumentUploaded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_document_uploaded/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientDocumentUploaded entity.
 */
export function createPatientDocumentUploaded(patientDocumentUploaded: PatientDocumentUploaded){
    return {
        schema: 'iglu:com.heidi/patient_document_uploaded/jsonschema/1-0-0',
        data: patientDocumentUploaded
    }
}
/**
 * Track a Snowplow event for CommunityTemplateSearched.
 * Tracks when user searches community templates
 */
export function trackCommunityTemplateSearched<T extends {} = any>(communityTemplateSearched: CommunityTemplateSearched & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = communityTemplateSearched;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/community_template_searched/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CommunityTemplateSearched entity.
 */
export function createCommunityTemplateSearched(communityTemplateSearched: CommunityTemplateSearched){
    return {
        schema: 'iglu:com.heidi/community_template_searched/jsonschema/2-0-1',
        data: communityTemplateSearched
    }
}
/**
 * Track a Snowplow event for FeatureToggled.
 * Tracks when a feature is toggled
 */
export function trackFeatureToggled<T extends {} = any>(featureToggled: FeatureToggled & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = featureToggled;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/feature_toggled/jsonschema/2-0-4',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FeatureToggled entity.
 */
export function createFeatureToggled(featureToggled: FeatureToggled){
    return {
        schema: 'iglu:com.heidi/feature_toggled/jsonschema/2-0-4',
        data: featureToggled
    }
}
/**
 * Track a Snowplow event for EvidenceReferencesSectionToggled.
 * Tracks when a user expands or collapses the references section
 */
export function trackEvidenceReferencesSectionToggled<T extends {} = any>(evidenceReferencesSectionToggled: EvidenceReferencesSectionToggled & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceReferencesSectionToggled;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_references_section_toggled/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceReferencesSectionToggled entity.
 */
export function createEvidenceReferencesSectionToggled(evidenceReferencesSectionToggled: EvidenceReferencesSectionToggled){
    return {
        schema: 'iglu:com.heidi/evidence_references_section_toggled/jsonschema/2-0-3',
        data: evidenceReferencesSectionToggled
    }
}
/**
 * Track a Snowplow event for EvidenceCategoryClicked.
 * Tracks evidence category clicked event
 */
export function trackEvidenceCategoryClicked<T extends {} = any>(evidenceCategoryClicked: EvidenceCategoryClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceCategoryClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_category_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceCategoryClicked entity.
 */
export function createEvidenceCategoryClicked(evidenceCategoryClicked: EvidenceCategoryClicked){
    return {
        schema: 'iglu:com.heidi/evidence_category_clicked/jsonschema/1-0-1',
        data: evidenceCategoryClicked
    }
}
/**
 * Track a Snowplow event for EvidenceSummarySuggestionDismissed.
 * Tracks when the user dismisses the post-note Evidence summary suggestion banner
 */
export function trackEvidenceSummarySuggestionDismissed<T extends {} = any>(evidenceSummarySuggestionDismissed: EvidenceSummarySuggestionDismissed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceSummarySuggestionDismissed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_summary_suggestion_dismissed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceSummarySuggestionDismissed entity.
 */
export function createEvidenceSummarySuggestionDismissed(evidenceSummarySuggestionDismissed: EvidenceSummarySuggestionDismissed){
    return {
        schema: 'iglu:com.heidi/evidence_summary_suggestion_dismissed/jsonschema/1-0-0',
        data: evidenceSummarySuggestionDismissed
    }
}
/**
 * Track a Snowplow event for TeamMemberDeleted.
 * Tracks team member deleted event
 */
export function trackTeamMemberDeleted<T extends {} = any>(teamMemberDeleted: TeamMemberDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = teamMemberDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/team_member_deleted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TeamMemberDeleted entity.
 */
export function createTeamMemberDeleted(teamMemberDeleted: TeamMemberDeleted){
    return {
        schema: 'iglu:com.heidi/team_member_deleted/jsonschema/1-0-1',
        data: teamMemberDeleted
    }
}
/**
 * Track a Snowplow event for LetterSendCompleted.
 * Tracks the persisted delivery result of a letter send attempt
 */
export function trackLetterSendCompleted<T extends {} = any>(letterSendCompleted: LetterSendCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = letterSendCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/letter_send_completed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LetterSendCompleted entity.
 */
export function createLetterSendCompleted(letterSendCompleted: LetterSendCompleted){
    return {
        schema: 'iglu:com.heidi/letter_send_completed/jsonschema/1-0-0',
        data: letterSendCompleted
    }
}
/**
 * Track a Snowplow event for PatientFieldExtractionResolved.
 * Tracks when the user accepts, edits, or rejects a surfaced extracted field
 */
export function trackPatientFieldExtractionResolved<T extends {} = any>(patientFieldExtractionResolved: PatientFieldExtractionResolved & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientFieldExtractionResolved;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_field_extraction_resolved/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientFieldExtractionResolved entity.
 */
export function createPatientFieldExtractionResolved(patientFieldExtractionResolved: PatientFieldExtractionResolved){
    return {
        schema: 'iglu:com.heidi/patient_field_extraction_resolved/jsonschema/1-0-0',
        data: patientFieldExtractionResolved
    }
}
/**
 * Track a Snowplow event for OutputCreated.
 * Tracks when output is created
 */
export function trackOutputCreated<T extends {} = any>(outputCreated: OutputCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = outputCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/output_created/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OutputCreated entity.
 */
export function createOutputCreated(outputCreated: OutputCreated){
    return {
        schema: 'iglu:com.heidi/output_created/jsonschema/1-0-3',
        data: outputCreated
    }
}
/**
 * Track a Snowplow event for NotePushFailed.
 * Tracks when a note push to EHR fails
 */
export function trackNotePushFailed<T extends {} = any>(notePushFailed: NotePushFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = notePushFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_push_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NotePushFailed entity.
 */
export function createNotePushFailed(notePushFailed: NotePushFailed){
    return {
        schema: 'iglu:com.heidi/note_push_failed/jsonschema/1-0-0',
        data: notePushFailed
    }
}
/**
 * Track a Snowplow event for EhrLaunchCompleted.
 * Tracks EHR launch completion
 */
export function trackEhrLaunchCompleted<T extends {} = any>(ehrLaunchCompleted: EhrLaunchCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = ehrLaunchCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/ehr_launch_completed/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EhrLaunchCompleted entity.
 */
export function createEhrLaunchCompleted(ehrLaunchCompleted: EhrLaunchCompleted){
    return {
        schema: 'iglu:com.heidi/ehr_launch_completed/jsonschema/1-0-3',
        data: ehrLaunchCompleted
    }
}
/**
 * Track a Snowplow event for ScheduledCallBulkUploadProcessed.
 * Tracks when a bulk scheduled call upload is processed
 */
export function trackScheduledCallBulkUploadProcessed<T extends {} = any>(scheduledCallBulkUploadProcessed: ScheduledCallBulkUploadProcessed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = scheduledCallBulkUploadProcessed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/scheduled_call_bulk_upload_processed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScheduledCallBulkUploadProcessed entity.
 */
export function createScheduledCallBulkUploadProcessed(scheduledCallBulkUploadProcessed: ScheduledCallBulkUploadProcessed){
    return {
        schema: 'iglu:com.heidi/scheduled_call_bulk_upload_processed/jsonschema/1-0-0',
        data: scheduledCallBulkUploadProcessed
    }
}
/**
 * Track a Snowplow event for ScheduleUploadSessionsCreated.
 * Tracks schedule upload sessions created event
 */
export function trackScheduleUploadSessionsCreated<T extends {} = any>(scheduleUploadSessionsCreated: ScheduleUploadSessionsCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = scheduleUploadSessionsCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/schedule_upload_sessions_created/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScheduleUploadSessionsCreated entity.
 */
export function createScheduleUploadSessionsCreated(scheduleUploadSessionsCreated: ScheduleUploadSessionsCreated){
    return {
        schema: 'iglu:com.heidi/schedule_upload_sessions_created/jsonschema/1-0-1',
        data: scheduleUploadSessionsCreated
    }
}
/**
 * Track a Snowplow event for PrechartGenerationCompleted.
 * Tracks when pre-chart generation reaches a terminal state (success, empty, or failed)
 */
export function trackPrechartGenerationCompleted<T extends {} = any>(prechartGenerationCompleted: PrechartGenerationCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = prechartGenerationCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/prechart_generation_completed/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PrechartGenerationCompleted entity.
 */
export function createPrechartGenerationCompleted(prechartGenerationCompleted: PrechartGenerationCompleted){
    return {
        schema: 'iglu:com.heidi/prechart_generation_completed/jsonschema/1-0-2',
        data: prechartGenerationCompleted
    }
}
/**
 * Track a Snowplow event for CallRinging.
 * Tracks when an outbound call is ringing at the recipient
 */
export function trackCallRinging<T extends {} = any>(callRinging: CallRinging & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callRinging;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_ringing/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallRinging entity.
 */
export function createCallRinging(callRinging: CallRinging){
    return {
        schema: 'iglu:com.heidi/call_ringing/jsonschema/1-0-0',
        data: callRinging
    }
}
/**
 * Track a Snowplow event for FlagsmithFlagEvaluated.
 * Tracks a Flagsmith feature flag evaluation for a user identity, forwarded via webhook
 */
export function trackFlagsmithFlagEvaluated<T extends {} = any>(flagsmithFlagEvaluated: FlagsmithFlagEvaluated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = flagsmithFlagEvaluated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/flagsmith_flag_evaluated/jsonschema/1-0-4',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FlagsmithFlagEvaluated entity.
 */
export function createFlagsmithFlagEvaluated(flagsmithFlagEvaluated: FlagsmithFlagEvaluated){
    return {
        schema: 'iglu:com.heidi/flagsmith_flag_evaluated/jsonschema/1-0-4',
        data: flagsmithFlagEvaluated
    }
}
/**
 * Track a Snowplow event for PatientDocumentProcessed.
 * Tracks when the BE content-extraction pipeline reaches a terminal state for a patient document
 */
export function trackPatientDocumentProcessed<T extends {} = any>(patientDocumentProcessed: PatientDocumentProcessed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientDocumentProcessed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_document_processed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientDocumentProcessed entity.
 */
export function createPatientDocumentProcessed(patientDocumentProcessed: PatientDocumentProcessed){
    return {
        schema: 'iglu:com.heidi/patient_document_processed/jsonschema/1-0-0',
        data: patientDocumentProcessed
    }
}
/**
 * Track a Snowplow event for SubscriptionRenewed.
 * Tracks when a subscription is renewed
 */
export function trackSubscriptionRenewed<T extends {} = any>(subscriptionRenewed: SubscriptionRenewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = subscriptionRenewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/subscription_renewed/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SubscriptionRenewed entity.
 */
export function createSubscriptionRenewed(subscriptionRenewed: SubscriptionRenewed){
    return {
        schema: 'iglu:com.heidi/subscription_renewed/jsonschema/2-0-0',
        data: subscriptionRenewed
    }
}
/**
 * Track a Snowplow event for IntegrationWidgetAuthStepErrored.
 * Tracks integration widget auth step errored event
 */
export function trackIntegrationWidgetAuthStepErrored<T extends {} = any>(integrationWidgetAuthStepErrored: IntegrationWidgetAuthStepErrored & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationWidgetAuthStepErrored;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_widget_auth_step_errored/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationWidgetAuthStepErrored entity.
 */
export function createIntegrationWidgetAuthStepErrored(integrationWidgetAuthStepErrored: IntegrationWidgetAuthStepErrored){
    return {
        schema: 'iglu:com.heidi/integration_widget_auth_step_errored/jsonschema/1-0-1',
        data: integrationWidgetAuthStepErrored
    }
}
/**
 * Track a Snowplow event for TemplateOpened.
 * Tracks template opened event
 */
export function trackTemplateOpened<T extends {} = any>(templateOpened: TemplateOpened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateOpened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_opened/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateOpened entity.
 */
export function createTemplateOpened(templateOpened: TemplateOpened){
    return {
        schema: 'iglu:com.heidi/template_opened/jsonschema/1-0-1',
        data: templateOpened
    }
}
/**
 * Track a Snowplow event for FeatureModeToggled.
 * Tracks when a feature&#x27;s mode is toggled
 */
export function trackFeatureModeToggled<T extends {} = any>(featureModeToggled: FeatureModeToggled & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = featureModeToggled;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/feature_mode_toggled/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FeatureModeToggled entity.
 */
export function createFeatureModeToggled(featureModeToggled: FeatureModeToggled){
    return {
        schema: 'iglu:com.heidi/feature_mode_toggled/jsonschema/1-0-3',
        data: featureModeToggled
    }
}
/**
 * Track a Snowplow event for CitationClicked.
 * Tracks when user clicks on a citation
 */
export function trackCitationClicked<T extends {} = any>(citationClicked: CitationClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = citationClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/citation_clicked/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CitationClicked entity.
 */
export function createCitationClicked(citationClicked: CitationClicked){
    return {
        schema: 'iglu:com.heidi/citation_clicked/jsonschema/1-0-3',
        data: citationClicked
    }
}
/**
 * Track a Snowplow event for NoteReverted.
 * Tracks note reverted event
 */
export function trackNoteReverted<T extends {} = any>(noteReverted: NoteReverted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteReverted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_reverted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteReverted entity.
 */
export function createNoteReverted(noteReverted: NoteReverted){
    return {
        schema: 'iglu:com.heidi/note_reverted/jsonschema/1-0-1',
        data: noteReverted
    }
}
/**
 * Track a Snowplow event for EvidenceShareSocialClicked.
 * Tracks when a social sharing button is clicked for Evidence
 */
export function trackEvidenceShareSocialClicked<T extends {} = any>(evidenceShareSocialClicked: EvidenceShareSocialClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceShareSocialClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_share_social_clicked/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceShareSocialClicked entity.
 */
export function createEvidenceShareSocialClicked(evidenceShareSocialClicked: EvidenceShareSocialClicked){
    return {
        schema: 'iglu:com.heidi/evidence_share_social_clicked/jsonschema/2-0-0',
        data: evidenceShareSocialClicked
    }
}
/**
 * Track a Snowplow event for ExtractionReviewed.
 * Tracks when extracted data is reviewed by the user
 */
export function trackExtractionReviewed<T extends {} = any>(extractionReviewed: ExtractionReviewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = extractionReviewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/extraction_reviewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ExtractionReviewed entity.
 */
export function createExtractionReviewed(extractionReviewed: ExtractionReviewed){
    return {
        schema: 'iglu:com.heidi/extraction_reviewed/jsonschema/1-0-1',
        data: extractionReviewed
    }
}
/**
 * Track a Snowplow event for PatientSessionExpanded.
 * Tracks when a session accordion row expands inside an open patient profile
 */
export function trackPatientSessionExpanded<T extends {} = any>(patientSessionExpanded: PatientSessionExpanded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientSessionExpanded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_session_expanded/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientSessionExpanded entity.
 */
export function createPatientSessionExpanded(patientSessionExpanded: PatientSessionExpanded){
    return {
        schema: 'iglu:com.heidi/patient_session_expanded/jsonschema/1-0-0',
        data: patientSessionExpanded
    }
}
/**
 * Track a Snowplow event for FormTemplateFieldsExtracted.
 * Tracks when fields are extracted from a form template
 */
export function trackFormTemplateFieldsExtracted<T extends {} = any>(formTemplateFieldsExtracted: FormTemplateFieldsExtracted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = formTemplateFieldsExtracted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/form_template_fields_extracted/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FormTemplateFieldsExtracted entity.
 */
export function createFormTemplateFieldsExtracted(formTemplateFieldsExtracted: FormTemplateFieldsExtracted){
    return {
        schema: 'iglu:com.heidi/form_template_fields_extracted/jsonschema/2-0-0',
        data: formTemplateFieldsExtracted
    }
}
/**
 * Track a Snowplow event for PatientSelected.
 * Tracks when patient is selected
 */
export function trackPatientSelected<T extends {} = any>(patientSelected: PatientSelected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientSelected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_selected/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientSelected entity.
 */
export function createPatientSelected(patientSelected: PatientSelected){
    return {
        schema: 'iglu:com.heidi/patient_selected/jsonschema/2-0-3',
        data: patientSelected
    }
}
/**
 * Track a Snowplow event for TranslatorEvaluationErrorLogged.
 * Fired once per stage-level evaluation error logged against a Translator message during evaluation
 */
export function trackTranslatorEvaluationErrorLogged<T extends {} = any>(translatorEvaluationErrorLogged: TranslatorEvaluationErrorLogged & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = translatorEvaluationErrorLogged;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/translator_evaluation_error_logged/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TranslatorEvaluationErrorLogged entity.
 */
export function createTranslatorEvaluationErrorLogged(translatorEvaluationErrorLogged: TranslatorEvaluationErrorLogged){
    return {
        schema: 'iglu:com.heidi/translator_evaluation_error_logged/jsonschema/1-0-0',
        data: translatorEvaluationErrorLogged
    }
}
/**
 * Track a Snowplow event for IntegrationUpdated.
 * Tracks integration updated event
 */
export function trackIntegrationUpdated<T extends {} = any>(integrationUpdated: IntegrationUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationUpdated entity.
 */
export function createIntegrationUpdated(integrationUpdated: IntegrationUpdated){
    return {
        schema: 'iglu:com.heidi/integration_updated/jsonschema/1-0-1',
        data: integrationUpdated
    }
}
/**
 * Track a Snowplow event for NoteSyncFailed.
 * Tracks note sync failed event
 */
export function trackNoteSyncFailed<T extends {} = any>(noteSyncFailed: NoteSyncFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteSyncFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_sync_failed/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteSyncFailed entity.
 */
export function createNoteSyncFailed(noteSyncFailed: NoteSyncFailed){
    return {
        schema: 'iglu:com.heidi/note_sync_failed/jsonschema/1-0-2',
        data: noteSyncFailed
    }
}
/**
 * Track a Snowplow event for NudgeCardReopened.
 * A dismissed nudge card was reopened
 */
export function trackNudgeCardReopened<T extends {} = any>(nudgeCardReopened: NudgeCardReopened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = nudgeCardReopened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/nudge_card_reopened/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NudgeCardReopened entity.
 */
export function createNudgeCardReopened(nudgeCardReopened: NudgeCardReopened){
    return {
        schema: 'iglu:com.heidi/nudge_card_reopened/jsonschema/1-0-0',
        data: nudgeCardReopened
    }
}
/**
 * Track a Snowplow event for CallVoicemailDetected.
 * Tracks when voicemail is detected on an outbound call
 */
export function trackCallVoicemailDetected<T extends {} = any>(callVoicemailDetected: CallVoicemailDetected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callVoicemailDetected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_voicemail_detected/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallVoicemailDetected entity.
 */
export function createCallVoicemailDetected(callVoicemailDetected: CallVoicemailDetected){
    return {
        schema: 'iglu:com.heidi/call_voicemail_detected/jsonschema/1-0-0',
        data: callVoicemailDetected
    }
}
/**
 * Track a Snowplow event for SubscriptionCancelled.
 * Tracks when a user cancels their subscription
 */
export function trackSubscriptionCancelled<T extends {} = any>(subscriptionCancelled: SubscriptionCancelled & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = subscriptionCancelled;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/subscription_cancelled/jsonschema/1-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SubscriptionCancelled entity.
 */
export function createSubscriptionCancelled(subscriptionCancelled: SubscriptionCancelled){
    return {
        schema: 'iglu:com.heidi/subscription_cancelled/jsonschema/1-1-0',
        data: subscriptionCancelled
    }
}
/**
 * Track a Snowplow event for ObjectiveConfigured.
 * Tracks when a user configures an objective
 */
export function trackObjectiveConfigured<T extends {} = any>(objectiveConfigured: ObjectiveConfigured & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = objectiveConfigured;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/objective_configured/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ObjectiveConfigured entity.
 */
export function createObjectiveConfigured(objectiveConfigured: ObjectiveConfigured){
    return {
        schema: 'iglu:com.heidi/objective_configured/jsonschema/1-0-0',
        data: objectiveConfigured
    }
}
/**
 * Track a Snowplow event for SessionLinkShareOtpVerified.
 * Tracks when OTP verification succeeds for a session link share
 */
export function trackSessionLinkShareOtpVerified<T extends {} = any>(sessionLinkShareOtpVerified: SessionLinkShareOtpVerified & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionLinkShareOtpVerified;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/session_link_share_otp_verified/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionLinkShareOtpVerified entity.
 */
export function createSessionLinkShareOtpVerified(sessionLinkShareOtpVerified: SessionLinkShareOtpVerified){
    return {
        schema: 'iglu:com.heidi/session_link_share_otp_verified/jsonschema/1-0-1',
        data: sessionLinkShareOtpVerified
    }
}
/**
 * Track a Snowplow event for HelpViewed.
 * Tracks when help is accessed or viewed
 */
export function trackHelpViewed<T extends {} = any>(helpViewed: HelpViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = helpViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/help_viewed/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow HelpViewed entity.
 */
export function createHelpViewed(helpViewed: HelpViewed){
    return {
        schema: 'iglu:com.heidi/help_viewed/jsonschema/2-0-1',
        data: helpViewed
    }
}
/**
 * Track a Snowplow event for TrialStarted.
 * Tracks when a trial begins
 */
export function trackTrialStarted<T extends {} = any>(trialStarted: TrialStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = trialStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/trial_started/jsonschema/4-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TrialStarted entity.
 */
export function createTrialStarted(trialStarted: TrialStarted){
    return {
        schema: 'iglu:com.heidi/trial_started/jsonschema/4-0-0',
        data: trialStarted
    }
}
/**
 * Track a Snowplow event for EvidenceReferenceFeedbackSubmitted.
 * Tracks when a user submits feedback on a reference
 */
export function trackEvidenceReferenceFeedbackSubmitted<T extends {} = any>(evidenceReferenceFeedbackSubmitted: EvidenceReferenceFeedbackSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceReferenceFeedbackSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_reference_feedback_submitted/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceReferenceFeedbackSubmitted entity.
 */
export function createEvidenceReferenceFeedbackSubmitted(evidenceReferenceFeedbackSubmitted: EvidenceReferenceFeedbackSubmitted){
    return {
        schema: 'iglu:com.heidi/evidence_reference_feedback_submitted/jsonschema/2-0-2',
        data: evidenceReferenceFeedbackSubmitted
    }
}
/**
 * Track a Snowplow event for OfflineSyncStarted.
 * Tracks the start of a drain for one or more queued offline recordings
 */
export function trackOfflineSyncStarted<T extends {} = any>(offlineSyncStarted: OfflineSyncStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = offlineSyncStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/offline_sync_started/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OfflineSyncStarted entity.
 */
export function createOfflineSyncStarted(offlineSyncStarted: OfflineSyncStarted){
    return {
        schema: 'iglu:com.heidi/offline_sync_started/jsonschema/1-0-0',
        data: offlineSyncStarted
    }
}
/**
 * Track a Snowplow event for TaskUpdated.
 * Tracks task updated event
 */
export function trackTaskUpdated<T extends {} = any>(taskUpdated: TaskUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = taskUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/task_updated/jsonschema/1-1-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TaskUpdated entity.
 */
export function createTaskUpdated(taskUpdated: TaskUpdated){
    return {
        schema: 'iglu:com.heidi/task_updated/jsonschema/1-1-1',
        data: taskUpdated
    }
}
/**
 * Track a Snowplow event for NoteSelectionSkipped.
 * Tracks when note selection is skipped
 */
export function trackNoteSelectionSkipped<T extends {} = any>(noteSelectionSkipped: NoteSelectionSkipped & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteSelectionSkipped;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_selection_skipped/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteSelectionSkipped entity.
 */
export function createNoteSelectionSkipped(noteSelectionSkipped: NoteSelectionSkipped){
    return {
        schema: 'iglu:com.heidi/note_selection_skipped/jsonschema/2-0-3',
        data: noteSelectionSkipped
    }
}
/**
 * Track a Snowplow event for InboundCallAbandoned.
 * Tracks when an inbound call is abandoned by the caller
 */
export function trackInboundCallAbandoned<T extends {} = any>(inboundCallAbandoned: InboundCallAbandoned & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = inboundCallAbandoned;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/inbound_call_abandoned/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow InboundCallAbandoned entity.
 */
export function createInboundCallAbandoned(inboundCallAbandoned: InboundCallAbandoned){
    return {
        schema: 'iglu:com.heidi/inbound_call_abandoned/jsonschema/1-0-0',
        data: inboundCallAbandoned
    }
}
/**
 * Track a Snowplow event for TaskDelegated.
 * Tracks when a clinician attempts to hand a clinical task to Heidi as a Work project
 */
export function trackTaskDelegated<T extends {} = any>(taskDelegated: TaskDelegated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = taskDelegated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/task_delegated/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TaskDelegated entity.
 */
export function createTaskDelegated(taskDelegated: TaskDelegated){
    return {
        schema: 'iglu:com.heidi/task_delegated/jsonschema/1-0-0',
        data: taskDelegated
    }
}
/**
 * Track a Snowplow event for ScheduleViewed.
 * Tracks schedule viewed event
 */
export function trackScheduleViewed<T extends {} = any>(scheduleViewed: ScheduleViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = scheduleViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/schedule_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScheduleViewed entity.
 */
export function createScheduleViewed(scheduleViewed: ScheduleViewed){
    return {
        schema: 'iglu:com.heidi/schedule_viewed/jsonschema/1-0-1',
        data: scheduleViewed
    }
}
/**
 * Track a Snowplow event for CommsSetupStepAbandoned.
 * Tracks when a user abandons the Comms setup flow
 */
export function trackCommsSetupStepAbandoned<T extends {} = any>(commsSetupStepAbandoned: CommsSetupStepAbandoned & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = commsSetupStepAbandoned;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/comms_setup_step_abandoned/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CommsSetupStepAbandoned entity.
 */
export function createCommsSetupStepAbandoned(commsSetupStepAbandoned: CommsSetupStepAbandoned){
    return {
        schema: 'iglu:com.heidi/comms_setup_step_abandoned/jsonschema/1-0-0',
        data: commsSetupStepAbandoned
    }
}
/**
 * Track a Snowplow event for InboxItemUpdated.
 * Tracks when a user updates an inbox conversation item
 */
export function trackInboxItemUpdated<T extends {} = any>(inboxItemUpdated: InboxItemUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = inboxItemUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/inbox_item_updated/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow InboxItemUpdated entity.
 */
export function createInboxItemUpdated(inboxItemUpdated: InboxItemUpdated){
    return {
        schema: 'iglu:com.heidi/inbox_item_updated/jsonschema/1-0-0',
        data: inboxItemUpdated
    }
}
/**
 * Track a Snowplow event for ChatMessageSentWithContext.
 * Tracks chat message sent with context event
 */
export function trackChatMessageSentWithContext<T extends {} = any>(chatMessageSentWithContext: ChatMessageSentWithContext & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chatMessageSentWithContext;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/chat_message_sent_with_context/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChatMessageSentWithContext entity.
 */
export function createChatMessageSentWithContext(chatMessageSentWithContext: ChatMessageSentWithContext){
    return {
        schema: 'iglu:com.heidi/chat_message_sent_with_context/jsonschema/1-0-1',
        data: chatMessageSentWithContext
    }
}
/**
 * Track a Snowplow event for SessionTabSwitched.
 * Fired when the active tab changes within a session.
 */
export function trackSessionTabSwitched<T extends {} = any>(sessionTabSwitched: SessionTabSwitched & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionTabSwitched;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/session_tab_switched/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionTabSwitched entity.
 */
export function createSessionTabSwitched(sessionTabSwitched: SessionTabSwitched){
    return {
        schema: 'iglu:com.heidi/session_tab_switched/jsonschema/1-0-0',
        data: sessionTabSwitched
    }
}
/**
 * Track a Snowplow event for NoteContentPasted.
 * Tracks content pasted into the note editor, classified by clipboard flavor. PHI-free — carries lengths and flavor only, never pasted content.
 */
export function trackNoteContentPasted<T extends {} = any>(noteContentPasted: NoteContentPasted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteContentPasted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_content_pasted/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteContentPasted entity.
 */
export function createNoteContentPasted(noteContentPasted: NoteContentPasted){
    return {
        schema: 'iglu:com.heidi/note_content_pasted/jsonschema/1-0-0',
        data: noteContentPasted
    }
}
/**
 * Track a Snowplow event for TabSharingNotAvailableModalShown.
 * Tracks tab sharing not available modal shown event
 */
export function trackTabSharingNotAvailableModalShown<T extends {} = any>(tabSharingNotAvailableModalShown: TabSharingNotAvailableModalShown & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = tabSharingNotAvailableModalShown;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/tab_sharing_not_available_modal_shown/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TabSharingNotAvailableModalShown entity.
 */
export function createTabSharingNotAvailableModalShown(tabSharingNotAvailableModalShown: TabSharingNotAvailableModalShown){
    return {
        schema: 'iglu:com.heidi/tab_sharing_not_available_modal_shown/jsonschema/2-0-1',
        data: tabSharingNotAvailableModalShown
    }
}
/**
 * Track a Snowplow event for SourceRecommendationCustomised.
 * Tracks when a user opens the customise wizard from the personalised source recommendation banner. Intermediate step in the funnel (shown -&gt; customised -&gt; applied with &#x60;is_customised: true&#x60;). &#x60;matched_*&#x60; fields reflect the BE-selected recommendation, not the user&#x27;s current profile. 2-0-0: renamed &#x60;location&#x60; -&gt; &#x60;banner_location&#x60; (MODEL bump). The 1-0-0 property name caused snowtype to emit a top-level TypeScript &#x60;Location&#x60; alias that shadowed the DOM &#x60;Location&#x60; global; the rename scopes the alias to the source-recommendation banner and makes snowtype emit &#x60;BannerLocation&#x60; instead. No other shape changes vs 1-0-0.
 */
export function trackSourceRecommendationCustomised<T extends {} = any>(sourceRecommendationCustomised: SourceRecommendationCustomised & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sourceRecommendationCustomised;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/source_recommendation_customised/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SourceRecommendationCustomised entity.
 */
export function createSourceRecommendationCustomised(sourceRecommendationCustomised: SourceRecommendationCustomised){
    return {
        schema: 'iglu:com.heidi/source_recommendation_customised/jsonschema/2-0-0',
        data: sourceRecommendationCustomised
    }
}
/**
 * Track a Snowplow event for ShareNotificationReceived.
 * Tracks when a share notification is received
 */
export function trackShareNotificationReceived<T extends {} = any>(shareNotificationReceived: ShareNotificationReceived & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareNotificationReceived;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_notification_received/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareNotificationReceived entity.
 */
export function createShareNotificationReceived(shareNotificationReceived: ShareNotificationReceived){
    return {
        schema: 'iglu:com.heidi/share_notification_received/jsonschema/1-0-1',
        data: shareNotificationReceived
    }
}
/**
 * Track a Snowplow event for ConversationCreated.
 * Tracks when a new conversation is created
 */
export function trackConversationCreated<T extends {} = any>(conversationCreated: ConversationCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = conversationCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/conversation_created/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ConversationCreated entity.
 */
export function createConversationCreated(conversationCreated: ConversationCreated){
    return {
        schema: 'iglu:com.heidi/conversation_created/jsonschema/1-0-0',
        data: conversationCreated
    }
}
/**
 * Track a Snowplow event for TokenGenerated.
 * Tracks when an API or integration token is generated
 */
export function trackTokenGenerated<T extends {} = any>(tokenGenerated: TokenGenerated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = tokenGenerated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/token_generated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TokenGenerated entity.
 */
export function createTokenGenerated(tokenGenerated: TokenGenerated){
    return {
        schema: 'iglu:com.heidi/token_generated/jsonschema/1-0-1',
        data: tokenGenerated
    }
}
/**
 * Track a Snowplow event for EhrStarted.
 * Tracks ehr started event
 */
export function trackEhrStarted<T extends {} = any>(ehrStarted: EhrStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = ehrStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/ehr_started/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EhrStarted entity.
 */
export function createEhrStarted(ehrStarted: EhrStarted){
    return {
        schema: 'iglu:com.heidi/ehr_started/jsonschema/1-0-1',
        data: ehrStarted
    }
}
/**
 * Track a Snowplow event for IntegrationSetupStarted.
 * Tracks when integration setup is initiated
 */
export function trackIntegrationSetupStarted<T extends {} = any>(integrationSetupStarted: IntegrationSetupStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationSetupStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_setup_started/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationSetupStarted entity.
 */
export function createIntegrationSetupStarted(integrationSetupStarted: IntegrationSetupStarted){
    return {
        schema: 'iglu:com.heidi/integration_setup_started/jsonschema/1-0-1',
        data: integrationSetupStarted
    }
}
/**
 * Track a Snowplow event for ReferralLinkCreated.
 * Tracks when referral link is generated
 */
export function trackReferralLinkCreated<T extends {} = any>(referralLinkCreated: ReferralLinkCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = referralLinkCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/referral_link_created/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ReferralLinkCreated entity.
 */
export function createReferralLinkCreated(referralLinkCreated: ReferralLinkCreated){
    return {
        schema: 'iglu:com.heidi/referral_link_created/jsonschema/2-0-1',
        data: referralLinkCreated
    }
}
/**
 * Track a Snowplow event for UniversalDictationPostProcessingFailed.
 * Tracks when universal dictation post-processing fails
 */
export function trackUniversalDictationPostProcessingFailed<T extends {} = any>(universalDictationPostProcessingFailed: UniversalDictationPostProcessingFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = universalDictationPostProcessingFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/universal_dictation_post_processing_failed/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UniversalDictationPostProcessingFailed entity.
 */
export function createUniversalDictationPostProcessingFailed(universalDictationPostProcessingFailed: UniversalDictationPostProcessingFailed){
    return {
        schema: 'iglu:com.heidi/universal_dictation_post_processing_failed/jsonschema/1-0-2',
        data: universalDictationPostProcessingFailed
    }
}
/**
 * Track a Snowplow event for ScheduledCallEnqueued.
 * Tracks when a scheduled call is added to the queue
 */
export function trackScheduledCallEnqueued<T extends {} = any>(scheduledCallEnqueued: ScheduledCallEnqueued & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = scheduledCallEnqueued;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/scheduled_call_enqueued/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScheduledCallEnqueued entity.
 */
export function createScheduledCallEnqueued(scheduledCallEnqueued: ScheduledCallEnqueued){
    return {
        schema: 'iglu:com.heidi/scheduled_call_enqueued/jsonschema/1-0-0',
        data: scheduledCallEnqueued
    }
}
/**
 * Track a Snowplow event for SessionLinkShareOtpFailed.
 * Tracks when OTP verification fails for a session link share
 */
export function trackSessionLinkShareOtpFailed<T extends {} = any>(sessionLinkShareOtpFailed: SessionLinkShareOtpFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionLinkShareOtpFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/session_link_share_otp_failed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionLinkShareOtpFailed entity.
 */
export function createSessionLinkShareOtpFailed(sessionLinkShareOtpFailed: SessionLinkShareOtpFailed){
    return {
        schema: 'iglu:com.heidi/session_link_share_otp_failed/jsonschema/1-0-1',
        data: sessionLinkShareOtpFailed
    }
}
/**
 * Track a Snowplow event for EvidenceShortcutSaveToggled.
 * Tracks when an Ask Heidi shortcut is saved or unsaved
 */
export function trackEvidenceShortcutSaveToggled<T extends {} = any>(evidenceShortcutSaveToggled: EvidenceShortcutSaveToggled & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceShortcutSaveToggled;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_shortcut_save_toggled/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceShortcutSaveToggled entity.
 */
export function createEvidenceShortcutSaveToggled(evidenceShortcutSaveToggled: EvidenceShortcutSaveToggled){
    return {
        schema: 'iglu:com.heidi/evidence_shortcut_save_toggled/jsonschema/1-0-0',
        data: evidenceShortcutSaveToggled
    }
}
/**
 * Track a Snowplow event for CommsSetupStepCompleted.
 * Tracks when a user completes a step in the Comms setup flow
 */
export function trackCommsSetupStepCompleted<T extends {} = any>(commsSetupStepCompleted: CommsSetupStepCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = commsSetupStepCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/comms_setup_step_completed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CommsSetupStepCompleted entity.
 */
export function createCommsSetupStepCompleted(commsSetupStepCompleted: CommsSetupStepCompleted){
    return {
        schema: 'iglu:com.heidi/comms_setup_step_completed/jsonschema/1-0-0',
        data: commsSetupStepCompleted
    }
}
/**
 * Track a Snowplow event for SubscriptionEvent.
 * Fires for billing, subscription, and payment lifecycle actions. GRO-3835 introduces cancel-retention funnel steps via subscription_action.
 */
export function trackSubscriptionEvent<T extends {} = any>(subscriptionEvent: SubscriptionEvent & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = subscriptionEvent;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/subscription_event/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SubscriptionEvent entity.
 */
export function createSubscriptionEvent(subscriptionEvent: SubscriptionEvent){
    return {
        schema: 'iglu:com.heidi/subscription_event/jsonschema/2-0-1',
        data: subscriptionEvent
    }
}
/**
 * Track a Snowplow event for LegacyInlineDictationCompleted.
 * Tracks legacy inline dictation completed event
 */
export function trackLegacyInlineDictationCompleted<T extends {} = any>(legacyInlineDictationCompleted: LegacyInlineDictationCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = legacyInlineDictationCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/legacy_inline_dictation_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LegacyInlineDictationCompleted entity.
 */
export function createLegacyInlineDictationCompleted(legacyInlineDictationCompleted: LegacyInlineDictationCompleted){
    return {
        schema: 'iglu:com.heidi/legacy_inline_dictation_completed/jsonschema/1-0-1',
        data: legacyInlineDictationCompleted
    }
}
/**
 * Track a Snowplow event for ChronicleDeviceConnected.
 * Tracks chronicle device connected event
 */
export function trackChronicleDeviceConnected<T extends {} = any>(chronicleDeviceConnected: ChronicleDeviceConnected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chronicleDeviceConnected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/chronicle_device_connected/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChronicleDeviceConnected entity.
 */
export function createChronicleDeviceConnected(chronicleDeviceConnected: ChronicleDeviceConnected){
    return {
        schema: 'iglu:com.heidi/chronicle_device_connected/jsonschema/1-0-1',
        data: chronicleDeviceConnected
    }
}
/**
 * Track a Snowplow event for DocumentExported.
 * Tracks document exported event
 */
export function trackDocumentExported<T extends {} = any>(documentExported: DocumentExported & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = documentExported;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/document_exported/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DocumentExported entity.
 */
export function createDocumentExported(documentExported: DocumentExported){
    return {
        schema: 'iglu:com.heidi/document_exported/jsonschema/1-0-2',
        data: documentExported
    }
}
/**
 * Track a Snowplow event for NoteInsertApplied.
 * Tracks when a chat/Ask-Heidi response is inserted into a note, document, or new tab. Adoption signal for chat responses. PHI-free — carries no note body. chat_id/message_id join the adoption back to the specific model that produced the inserted response (per-message model attribution).
 */
export function trackNoteInsertApplied<T extends {} = any>(noteInsertApplied: NoteInsertApplied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteInsertApplied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_insert_applied/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteInsertApplied entity.
 */
export function createNoteInsertApplied(noteInsertApplied: NoteInsertApplied){
    return {
        schema: 'iglu:com.heidi/note_insert_applied/jsonschema/1-0-0',
        data: noteInsertApplied
    }
}
/**
 * Track a Snowplow event for ShareEditRequested.
 * Tracks when editing of a share is requested
 */
export function trackShareEditRequested<T extends {} = any>(shareEditRequested: ShareEditRequested & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareEditRequested;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_edit_requested/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareEditRequested entity.
 */
export function createShareEditRequested(shareEditRequested: ShareEditRequested){
    return {
        schema: 'iglu:com.heidi/share_edit_requested/jsonschema/1-0-1',
        data: shareEditRequested
    }
}
/**
 * Track a Snowplow event for EvidenceCopyConfirmation.
 * Tracks the Evidence copy-to-clipboard PHI/PII confirmation popover lifecycle (shown/confirmed/dismissed) — the SWR-88 med-device audit trail. PHI-free. chat_id/message_id attribute the copy (an adoption signal) to the specific model that produced the response.
 */
export function trackEvidenceCopyConfirmation<T extends {} = any>(evidenceCopyConfirmation: EvidenceCopyConfirmation & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceCopyConfirmation;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_copy_confirmation/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceCopyConfirmation entity.
 */
export function createEvidenceCopyConfirmation(evidenceCopyConfirmation: EvidenceCopyConfirmation){
    return {
        schema: 'iglu:com.heidi/evidence_copy_confirmation/jsonschema/1-0-0',
        data: evidenceCopyConfirmation
    }
}
/**
 * Track a Snowplow event for TabSharingDoShowMessageAgain.
 * Tracks tab sharing do show message again event
 */
export function trackTabSharingDoShowMessageAgain<T extends {} = any>(tabSharingDoShowMessageAgain: TabSharingDoShowMessageAgain & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = tabSharingDoShowMessageAgain;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/tab_sharing_do_show_message_again/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TabSharingDoShowMessageAgain entity.
 */
export function createTabSharingDoShowMessageAgain(tabSharingDoShowMessageAgain: TabSharingDoShowMessageAgain){
    return {
        schema: 'iglu:com.heidi/tab_sharing_do_show_message_again/jsonschema/2-0-1',
        data: tabSharingDoShowMessageAgain
    }
}
/**
 * Track a Snowplow event for ScheduleUploadCompleted.
 * Tracks schedule upload completed event
 */
export function trackScheduleUploadCompleted<T extends {} = any>(scheduleUploadCompleted: ScheduleUploadCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = scheduleUploadCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/schedule_upload_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ScheduleUploadCompleted entity.
 */
export function createScheduleUploadCompleted(scheduleUploadCompleted: ScheduleUploadCompleted){
    return {
        schema: 'iglu:com.heidi/schedule_upload_completed/jsonschema/1-0-1',
        data: scheduleUploadCompleted
    }
}
/**
 * Track a Snowplow event for ContextLinked.
 * Tracks when context is linked to session or chat
 */
export function trackContextLinked<T extends {} = any>(contextLinked: ContextLinked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = contextLinked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/context_linked/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ContextLinked entity.
 */
export function createContextLinked(contextLinked: ContextLinked){
    return {
        schema: 'iglu:com.heidi/context_linked/jsonschema/2-0-3',
        data: contextLinked
    }
}
/**
 * Track a Snowplow event for FormFillCompleted.
 * Tracks when a form fill upload completes successfully
 */
export function trackFormFillCompleted<T extends {} = any>(formFillCompleted: FormFillCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = formFillCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/form_fill_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FormFillCompleted entity.
 */
export function createFormFillCompleted(formFillCompleted: FormFillCompleted){
    return {
        schema: 'iglu:com.heidi/form_fill_completed/jsonschema/1-0-1',
        data: formFillCompleted
    }
}
/**
 * Track a Snowplow event for EvidenceShareLinkRevoked.
 * Tracks when an Evidence chat&#x27;s share link is revoked by its owner
 */
export function trackEvidenceShareLinkRevoked<T extends {} = any>(evidenceShareLinkRevoked: EvidenceShareLinkRevoked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceShareLinkRevoked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_share_link_revoked/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceShareLinkRevoked entity.
 */
export function createEvidenceShareLinkRevoked(evidenceShareLinkRevoked: EvidenceShareLinkRevoked){
    return {
        schema: 'iglu:com.heidi/evidence_share_link_revoked/jsonschema/1-0-0',
        data: evidenceShareLinkRevoked
    }
}
/**
 * Track a Snowplow event for NoteGenerationFailed.
 * Consult note generation failed for a session
 */
export function trackNoteGenerationFailed<T extends {} = any>(noteGenerationFailed: NoteGenerationFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteGenerationFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_generation_failed/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteGenerationFailed entity.
 */
export function createNoteGenerationFailed(noteGenerationFailed: NoteGenerationFailed){
    return {
        schema: 'iglu:com.heidi/note_generation_failed/jsonschema/2-0-2',
        data: noteGenerationFailed
    }
}
/**
 * Track a Snowplow event for OnboardingErrorViewed.
 * Tracks when an error is viewed during onboarding
 */
export function trackOnboardingErrorViewed<T extends {} = any>(onboardingErrorViewed: OnboardingErrorViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = onboardingErrorViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/onboarding_error_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OnboardingErrorViewed entity.
 */
export function createOnboardingErrorViewed(onboardingErrorViewed: OnboardingErrorViewed){
    return {
        schema: 'iglu:com.heidi/onboarding_error_viewed/jsonschema/1-0-1',
        data: onboardingErrorViewed
    }
}
/**
 * Track a Snowplow event for LinkedSessionsMutationCompleted.
 * Tracks the authoritative outcome of a linked-session mutation
 */
export function trackLinkedSessionsMutationCompleted<T extends {} = any>(linkedSessionsMutationCompleted: LinkedSessionsMutationCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = linkedSessionsMutationCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/linked_sessions_mutation_completed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LinkedSessionsMutationCompleted entity.
 */
export function createLinkedSessionsMutationCompleted(linkedSessionsMutationCompleted: LinkedSessionsMutationCompleted){
    return {
        schema: 'iglu:com.heidi/linked_sessions_mutation_completed/jsonschema/1-0-0',
        data: linkedSessionsMutationCompleted
    }
}
/**
 * Track a Snowplow event for NoteCreated.
 * Tracks when note is created
 */
export function trackNoteCreated<T extends {} = any>(noteCreated: NoteCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_created/jsonschema/2-0-6',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteCreated entity.
 */
export function createNoteCreated(noteCreated: NoteCreated){
    return {
        schema: 'iglu:com.heidi/note_created/jsonschema/2-0-6',
        data: noteCreated
    }
}
/**
 * Track a Snowplow event for SessionsFilterApplied.
 * Tracks sessions filter applied event
 */
export function trackSessionsFilterApplied<T extends {} = any>(sessionsFilterApplied: SessionsFilterApplied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionsFilterApplied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/sessions_filter_applied/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionsFilterApplied entity.
 */
export function createSessionsFilterApplied(sessionsFilterApplied: SessionsFilterApplied){
    return {
        schema: 'iglu:com.heidi/sessions_filter_applied/jsonschema/1-0-1',
        data: sessionsFilterApplied
    }
}
/**
 * Track a Snowplow event for CallRecordingProcessed.
 * Tracks when a call recording is successfully processed
 */
export function trackCallRecordingProcessed<T extends {} = any>(callRecordingProcessed: CallRecordingProcessed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callRecordingProcessed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_recording_processed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallRecordingProcessed entity.
 */
export function createCallRecordingProcessed(callRecordingProcessed: CallRecordingProcessed){
    return {
        schema: 'iglu:com.heidi/call_recording_processed/jsonschema/1-0-0',
        data: callRecordingProcessed
    }
}
/**
 * Track a Snowplow event for TranscriptionStarted.
 * Tracks when transcription begins
 */
export function trackTranscriptionStarted<T extends {} = any>(transcriptionStarted: TranscriptionStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = transcriptionStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/transcription_started/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TranscriptionStarted entity.
 */
export function createTranscriptionStarted(transcriptionStarted: TranscriptionStarted){
    return {
        schema: 'iglu:com.heidi/transcription_started/jsonschema/2-0-2',
        data: transcriptionStarted
    }
}
/**
 * Track a Snowplow event for EvidenceShareLinkCopied.
 * Tracks when an Evidence share link is copied to clipboard
 */
export function trackEvidenceShareLinkCopied<T extends {} = any>(evidenceShareLinkCopied: EvidenceShareLinkCopied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceShareLinkCopied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_share_link_copied/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceShareLinkCopied entity.
 */
export function createEvidenceShareLinkCopied(evidenceShareLinkCopied: EvidenceShareLinkCopied){
    return {
        schema: 'iglu:com.heidi/evidence_share_link_copied/jsonschema/1-0-2',
        data: evidenceShareLinkCopied
    }
}
/**
 * Track a Snowplow event for OnboardingStepCompleted.
 * Tracks when onboarding step is completed
 */
export function trackOnboardingStepCompleted<T extends {} = any>(onboardingStepCompleted: OnboardingStepCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = onboardingStepCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/onboarding_step_completed/jsonschema/2-1-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OnboardingStepCompleted entity.
 */
export function createOnboardingStepCompleted(onboardingStepCompleted: OnboardingStepCompleted){
    return {
        schema: 'iglu:com.heidi/onboarding_step_completed/jsonschema/2-1-1',
        data: onboardingStepCompleted
    }
}
/**
 * Track a Snowplow event for NoteSubmitted.
 * Tracks note submitted event
 */
export function trackNoteSubmitted<T extends {} = any>(noteSubmitted: NoteSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_submitted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteSubmitted entity.
 */
export function createNoteSubmitted(noteSubmitted: NoteSubmitted){
    return {
        schema: 'iglu:com.heidi/note_submitted/jsonschema/1-0-1',
        data: noteSubmitted
    }
}
/**
 * Track a Snowplow event for SofLaunch.
 * Tracks sof launch event
 */
export function trackSofLaunch<T extends {} = any>(sofLaunch: SofLaunch & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sofLaunch;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/sof_launch/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SofLaunch entity.
 */
export function createSofLaunch(sofLaunch: SofLaunch){
    return {
        schema: 'iglu:com.heidi/sof_launch/jsonschema/1-0-1',
        data: sofLaunch
    }
}
/**
 * Track a Snowplow event for Heidi2_Activated.
 * Fired when a workspace successfully opts into Heidi 2.0 and the complimentary usage period starts
 */
export function trackHeidi2_Activated<T extends {} = any>(heidi2_Activated: Heidi2_Activated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = heidi2_Activated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/heidi_2_activated/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow Heidi2_Activated entity.
 */
export function createHeidi2_Activated(heidi2_Activated: Heidi2_Activated){
    return {
        schema: 'iglu:com.heidi/heidi_2_activated/jsonschema/1-0-0',
        data: heidi2_Activated
    }
}
/**
 * Track a Snowplow event for PanelClosed.
 * Tracks when a panel is closed
 */
export function trackPanelClosed<T extends {} = any>(panelClosed: PanelClosed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = panelClosed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/panel_closed/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PanelClosed entity.
 */
export function createPanelClosed(panelClosed: PanelClosed){
    return {
        schema: 'iglu:com.heidi/panel_closed/jsonschema/2-0-2',
        data: panelClosed
    }
}
/**
 * Track a Snowplow event for TaskNotificationClicked.
 * Tracks when a user clicks a task notification, capturing the notification type
 */
export function trackTaskNotificationClicked<T extends {} = any>(taskNotificationClicked: TaskNotificationClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = taskNotificationClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/task_notification_clicked/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TaskNotificationClicked entity.
 */
export function createTaskNotificationClicked(taskNotificationClicked: TaskNotificationClicked){
    return {
        schema: 'iglu:com.heidi/task_notification_clicked/jsonschema/1-0-0',
        data: taskNotificationClicked
    }
}
/**
 * Track a Snowplow event for UserUpdated.
 * Tracks user updated event
 */
export function trackUserUpdated<T extends {} = any>(userUpdated: UserUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = userUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/user_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UserUpdated entity.
 */
export function createUserUpdated(userUpdated: UserUpdated){
    return {
        schema: 'iglu:com.heidi/user_updated/jsonschema/1-0-1',
        data: userUpdated
    }
}
/**
 * Track a Snowplow event for FeedbackSubmitted.
 * Tracks when user submits feedback
 */
export function trackFeedbackSubmitted<T extends {} = any>(feedbackSubmitted: FeedbackSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = feedbackSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/feedback_submitted/jsonschema/2-0-4',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow FeedbackSubmitted entity.
 */
export function createFeedbackSubmitted(feedbackSubmitted: FeedbackSubmitted){
    return {
        schema: 'iglu:com.heidi/feedback_submitted/jsonschema/2-0-4',
        data: feedbackSubmitted
    }
}
/**
 * Track a Snowplow event for DarkModeSpotlightDismissed.
 * Tracks when the user dismisses the dark-mode spotlight
 */
export function trackDarkModeSpotlightDismissed<T extends {} = any>(darkModeSpotlightDismissed: DarkModeSpotlightDismissed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = darkModeSpotlightDismissed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/dark_mode_spotlight_dismissed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DarkModeSpotlightDismissed entity.
 */
export function createDarkModeSpotlightDismissed(darkModeSpotlightDismissed: DarkModeSpotlightDismissed){
    return {
        schema: 'iglu:com.heidi/dark_mode_spotlight_dismissed/jsonschema/1-0-0',
        data: darkModeSpotlightDismissed
    }
}
/**
 * Track a Snowplow event for ClinicalCodeClickable.
 * Tracks clinical code clickable event
 */
export function trackClinicalCodeClickable<T extends {} = any>(clinicalCodeClickable: ClinicalCodeClickable & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = clinicalCodeClickable;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/clinical_code_clickable/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ClinicalCodeClickable entity.
 */
export function createClinicalCodeClickable(clinicalCodeClickable: ClinicalCodeClickable){
    return {
        schema: 'iglu:com.heidi/clinical_code_clickable/jsonschema/1-0-1',
        data: clinicalCodeClickable
    }
}
/**
 * Track a Snowplow event for UniversalDictationCsatSubmitted.
 * Tracks when a universal dictation CSAT rating is submitted
 */
export function trackUniversalDictationCsatSubmitted<T extends {} = any>(universalDictationCsatSubmitted: UniversalDictationCsatSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = universalDictationCsatSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/universal_dictation_csat_submitted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UniversalDictationCsatSubmitted entity.
 */
export function createUniversalDictationCsatSubmitted(universalDictationCsatSubmitted: UniversalDictationCsatSubmitted){
    return {
        schema: 'iglu:com.heidi/universal_dictation_csat_submitted/jsonschema/1-0-1',
        data: universalDictationCsatSubmitted
    }
}
/**
 * Track a Snowplow event for ShareAccessChanged.
 * Tracks when sharing access level is changed for a session
 */
export function trackShareAccessChanged<T extends {} = any>(shareAccessChanged: ShareAccessChanged & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareAccessChanged;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_access_changed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareAccessChanged entity.
 */
export function createShareAccessChanged(shareAccessChanged: ShareAccessChanged){
    return {
        schema: 'iglu:com.heidi/share_access_changed/jsonschema/1-0-1',
        data: shareAccessChanged
    }
}
/**
 * Track a Snowplow event for AudioWarningViewed.
 * Tracks when the in-recording silent-capture warning is shown
 */
export function trackAudioWarningViewed<T extends {} = any>(audioWarningViewed: AudioWarningViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = audioWarningViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/audio_warning_viewed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AudioWarningViewed entity.
 */
export function createAudioWarningViewed(audioWarningViewed: AudioWarningViewed){
    return {
        schema: 'iglu:com.heidi/audio_warning_viewed/jsonschema/1-0-0',
        data: audioWarningViewed
    }
}
/**
 * Track a Snowplow event for IntegrationWidgetAuthStepMisc.
 * Tracks integration widget auth step misc event
 */
export function trackIntegrationWidgetAuthStepMisc<T extends {} = any>(integrationWidgetAuthStepMisc: IntegrationWidgetAuthStepMisc & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationWidgetAuthStepMisc;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_widget_auth_step_misc/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationWidgetAuthStepMisc entity.
 */
export function createIntegrationWidgetAuthStepMisc(integrationWidgetAuthStepMisc: IntegrationWidgetAuthStepMisc){
    return {
        schema: 'iglu:com.heidi/integration_widget_auth_step_misc/jsonschema/1-0-1',
        data: integrationWidgetAuthStepMisc
    }
}
/**
 * Track a Snowplow event for VerificationSuggestedFixApplied.
 * Tracks when a suggested fix from verification is applied
 */
export function trackVerificationSuggestedFixApplied<T extends {} = any>(verificationSuggestedFixApplied: VerificationSuggestedFixApplied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = verificationSuggestedFixApplied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/verification_suggested_fix_applied/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow VerificationSuggestedFixApplied entity.
 */
export function createVerificationSuggestedFixApplied(verificationSuggestedFixApplied: VerificationSuggestedFixApplied){
    return {
        schema: 'iglu:com.heidi/verification_suggested_fix_applied/jsonschema/1-0-1',
        data: verificationSuggestedFixApplied
    }
}
/**
 * Track a Snowplow event for TemplateConverted.
 * Tracks Use sections conversion of an existing consult template between flat and sectioned content
 */
export function trackTemplateConverted<T extends {} = any>(templateConverted: TemplateConverted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateConverted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_converted/jsonschema/1-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateConverted entity.
 */
export function createTemplateConverted(templateConverted: TemplateConverted){
    return {
        schema: 'iglu:com.heidi/template_converted/jsonschema/1-1-0',
        data: templateConverted
    }
}
/**
 * Track a Snowplow event for CheckoutStarted.
 * Tracks when user initiates checkout process
 */
export function trackCheckoutStarted<T extends {} = any>(checkoutStarted: CheckoutStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = checkoutStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/checkout_started/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CheckoutStarted entity.
 */
export function createCheckoutStarted(checkoutStarted: CheckoutStarted){
    return {
        schema: 'iglu:com.heidi/checkout_started/jsonschema/2-0-1',
        data: checkoutStarted
    }
}
/**
 * Track a Snowplow event for OutboundMessageDelivered.
 * Tracks when an outbound message is delivered
 */
export function trackOutboundMessageDelivered<T extends {} = any>(outboundMessageDelivered: OutboundMessageDelivered & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = outboundMessageDelivered;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/outbound_message_delivered/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OutboundMessageDelivered entity.
 */
export function createOutboundMessageDelivered(outboundMessageDelivered: OutboundMessageDelivered){
    return {
        schema: 'iglu:com.heidi/outbound_message_delivered/jsonschema/1-0-0',
        data: outboundMessageDelivered
    }
}
/**
 * Track a Snowplow event for CpdDraftSubmitted.
 * Tracks when a CPD draft is submitted
 */
export function trackCpdDraftSubmitted<T extends {} = any>(cpdDraftSubmitted: CpdDraftSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = cpdDraftSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/cpd_draft_submitted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CpdDraftSubmitted entity.
 */
export function createCpdDraftSubmitted(cpdDraftSubmitted: CpdDraftSubmitted){
    return {
        schema: 'iglu:com.heidi/cpd_draft_submitted/jsonschema/1-0-1',
        data: cpdDraftSubmitted
    }
}
/**
 * Track a Snowplow event for IntegrationDataCompleted.
 * Tracks integration data completed event
 */
export function trackIntegrationDataCompleted<T extends {} = any>(integrationDataCompleted: IntegrationDataCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationDataCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_data_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationDataCompleted entity.
 */
export function createIntegrationDataCompleted(integrationDataCompleted: IntegrationDataCompleted){
    return {
        schema: 'iglu:com.heidi/integration_data_completed/jsonschema/1-0-1',
        data: integrationDataCompleted
    }
}
/**
 * Track a Snowplow event for PushWarningViewed.
 * Tracks when a pre-push warning is shown before a note is pushed to the EHR
 */
export function trackPushWarningViewed<T extends {} = any>(pushWarningViewed: PushWarningViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = pushWarningViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/push_warning_viewed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PushWarningViewed entity.
 */
export function createPushWarningViewed(pushWarningViewed: PushWarningViewed){
    return {
        schema: 'iglu:com.heidi/push_warning_viewed/jsonschema/1-0-0',
        data: pushWarningViewed
    }
}
/**
 * Track a Snowplow event for SourceRecommendationDismissed.
 * Tracks when a user dismisses the personalised source recommendation banner. Negative-outcome leg of the source-recommendation funnel (shown -&gt; dismissed | applied | customised). &#x60;matched_*&#x60; fields reflect the BE-selected recommendation, not the user&#x27;s current profile. 2-0-0: renamed &#x60;location&#x60; -&gt; &#x60;banner_location&#x60; (MODEL bump). The 1-0-0 property name caused snowtype to emit a top-level TypeScript &#x60;Location&#x60; alias that shadowed the DOM &#x60;Location&#x60; global; the rename scopes the alias to the source-recommendation banner and makes snowtype emit &#x60;BannerLocation&#x60; instead. No other shape changes vs 1-0-0.
 */
export function trackSourceRecommendationDismissed<T extends {} = any>(sourceRecommendationDismissed: SourceRecommendationDismissed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sourceRecommendationDismissed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/source_recommendation_dismissed/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SourceRecommendationDismissed entity.
 */
export function createSourceRecommendationDismissed(sourceRecommendationDismissed: SourceRecommendationDismissed){
    return {
        schema: 'iglu:com.heidi/source_recommendation_dismissed/jsonschema/2-0-0',
        data: sourceRecommendationDismissed
    }
}
/**
 * Track a Snowplow event for OfflineRecordingQueued.
 * Tracks an offline recording entering the pending-sync queue after capture
 */
export function trackOfflineRecordingQueued<T extends {} = any>(offlineRecordingQueued: OfflineRecordingQueued & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = offlineRecordingQueued;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/offline_recording_queued/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OfflineRecordingQueued entity.
 */
export function createOfflineRecordingQueued(offlineRecordingQueued: OfflineRecordingQueued){
    return {
        schema: 'iglu:com.heidi/offline_recording_queued/jsonschema/1-0-0',
        data: offlineRecordingQueued
    }
}
/**
 * Track a Snowplow event for HardwareDeviceRemoved.
 * Tracks when a hardware device is removed
 */
export function trackHardwareDeviceRemoved<T extends {} = any>(hardwareDeviceRemoved: HardwareDeviceRemoved & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = hardwareDeviceRemoved;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/hardware_device_removed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow HardwareDeviceRemoved entity.
 */
export function createHardwareDeviceRemoved(hardwareDeviceRemoved: HardwareDeviceRemoved){
    return {
        schema: 'iglu:com.heidi/hardware_device_removed/jsonschema/1-0-0',
        data: hardwareDeviceRemoved
    }
}
/**
 * Track a Snowplow event for ReferralLinkCopied.
 * Tracks when referral link is copied
 */
export function trackReferralLinkCopied<T extends {} = any>(referralLinkCopied: ReferralLinkCopied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = referralLinkCopied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/referral_link_copied/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ReferralLinkCopied entity.
 */
export function createReferralLinkCopied(referralLinkCopied: ReferralLinkCopied){
    return {
        schema: 'iglu:com.heidi/referral_link_copied/jsonschema/2-0-1',
        data: referralLinkCopied
    }
}
/**
 * Track a Snowplow event for EvidenceCheckOtherSourcesClicked.
 * Tracks when a user opens the &quot;Check other sources&quot; dropdown on an Evidence chat response to re-run the same query against a different set of sources. chat_id/message_id attribute the click to the specific response (and producing model) the user is unsatisfied with.
 */
export function trackEvidenceCheckOtherSourcesClicked<T extends {} = any>(evidenceCheckOtherSourcesClicked: EvidenceCheckOtherSourcesClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceCheckOtherSourcesClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_check_other_sources_clicked/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceCheckOtherSourcesClicked entity.
 */
export function createEvidenceCheckOtherSourcesClicked(evidenceCheckOtherSourcesClicked: EvidenceCheckOtherSourcesClicked){
    return {
        schema: 'iglu:com.heidi/evidence_check_other_sources_clicked/jsonschema/1-0-0',
        data: evidenceCheckOtherSourcesClicked
    }
}
/**
 * Track a Snowplow event for ChatContextAddSessionClicked.
 * Tracks chat context add session clicked event
 */
export function trackChatContextAddSessionClicked<T extends {} = any>(chatContextAddSessionClicked: ChatContextAddSessionClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chatContextAddSessionClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/chat_context_add_session_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChatContextAddSessionClicked entity.
 */
export function createChatContextAddSessionClicked(chatContextAddSessionClicked: ChatContextAddSessionClicked){
    return {
        schema: 'iglu:com.heidi/chat_context_add_session_clicked/jsonschema/1-0-1',
        data: chatContextAddSessionClicked
    }
}
/**
 * Track a Snowplow event for NoteDisplayed.
 * Tracks note displayed event
 */
export function trackNoteDisplayed<T extends {} = any>(noteDisplayed: NoteDisplayed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteDisplayed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_displayed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteDisplayed entity.
 */
export function createNoteDisplayed(noteDisplayed: NoteDisplayed){
    return {
        schema: 'iglu:com.heidi/note_displayed/jsonschema/1-0-1',
        data: noteDisplayed
    }
}
/**
 * Track a Snowplow event for SnippetUploaded.
 * Tracks snippet uploaded event
 */
export function trackSnippetUploaded<T extends {} = any>(snippetUploaded: SnippetUploaded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = snippetUploaded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/snippet_uploaded/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SnippetUploaded entity.
 */
export function createSnippetUploaded(snippetUploaded: SnippetUploaded){
    return {
        schema: 'iglu:com.heidi/snippet_uploaded/jsonschema/1-0-1',
        data: snippetUploaded
    }
}
/**
 * Track a Snowplow event for TeamMemberCreated.
 * Tracks team member created event
 */
export function trackTeamMemberCreated<T extends {} = any>(teamMemberCreated: TeamMemberCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = teamMemberCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/team_member_created/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TeamMemberCreated entity.
 */
export function createTeamMemberCreated(teamMemberCreated: TeamMemberCreated){
    return {
        schema: 'iglu:com.heidi/team_member_created/jsonschema/1-0-1',
        data: teamMemberCreated
    }
}
/**
 * Track a Snowplow event for NoteEditStarted.
 * Fired when the user begins editing a note
 */
export function trackNoteEditStarted<T extends {} = any>(noteEditStarted: NoteEditStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteEditStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_edit_started/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteEditStarted entity.
 */
export function createNoteEditStarted(noteEditStarted: NoteEditStarted){
    return {
        schema: 'iglu:com.heidi/note_edit_started/jsonschema/1-0-1',
        data: noteEditStarted
    }
}
/**
 * Track a Snowplow event for WorkVoiceHandoffCompleted.
 * Voice to Work handoff completed
 */
export function trackWorkVoiceHandoffCompleted<T extends {} = any>(workVoiceHandoffCompleted: WorkVoiceHandoffCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workVoiceHandoffCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_voice_handoff_completed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkVoiceHandoffCompleted entity.
 */
export function createWorkVoiceHandoffCompleted(workVoiceHandoffCompleted: WorkVoiceHandoffCompleted){
    return {
        schema: 'iglu:com.heidi/work_voice_handoff_completed/jsonschema/1-0-0',
        data: workVoiceHandoffCompleted
    }
}
/**
 * Track a Snowplow event for ReferralShared.
 * Tracks referral shared event
 */
export function trackReferralShared<T extends {} = any>(referralShared: ReferralShared & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = referralShared;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/referral_shared/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ReferralShared entity.
 */
export function createReferralShared(referralShared: ReferralShared){
    return {
        schema: 'iglu:com.heidi/referral_shared/jsonschema/1-0-1',
        data: referralShared
    }
}
/**
 * Track a Snowplow event for WorkRunCompleted.
 * Agent run or turn finished (success, failure, or user cancel), including tools, query length, approval state, and agentic use
 */
export function trackWorkRunCompleted<T extends {} = any>(workRunCompleted: WorkRunCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workRunCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_run_completed/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkRunCompleted entity.
 */
export function createWorkRunCompleted(workRunCompleted: WorkRunCompleted){
    return {
        schema: 'iglu:com.heidi/work_run_completed/jsonschema/1-0-2',
        data: workRunCompleted
    }
}
/**
 * Track a Snowplow event for TemplateFailed.
 * Tracks template failed event
 */
export function trackTemplateFailed<T extends {} = any>(templateFailed: TemplateFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_failed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateFailed entity.
 */
export function createTemplateFailed(templateFailed: TemplateFailed){
    return {
        schema: 'iglu:com.heidi/template_failed/jsonschema/1-0-1',
        data: templateFailed
    }
}
/**
 * Track a Snowplow event for UniversalDictationCompleted.
 * Tracks universal dictation completed event
 */
export function trackUniversalDictationCompleted<T extends {} = any>(universalDictationCompleted: UniversalDictationCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = universalDictationCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/universal_dictation_completed/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UniversalDictationCompleted entity.
 */
export function createUniversalDictationCompleted(universalDictationCompleted: UniversalDictationCompleted){
    return {
        schema: 'iglu:com.heidi/universal_dictation_completed/jsonschema/1-0-2',
        data: universalDictationCompleted
    }
}
/**
 * Track a Snowplow event for TeamHeidiIiAccessUpdated.
 * Tracks when a team admin updates Heidi 2 access. Emitted after a successful team switch, per-member checkbox, or select-all toggle. change_type splits the team switch from member toggles, which can share the same is_enabled and member_enabled_count. is_enabled is access-on after the change, not the inverted heidi_ii_access_revoked entitlement write. member_enabled_count is an aggregate snapshot.
 */
export function trackTeamHeidiIiAccessUpdated<T extends {} = any>(teamHeidiIiAccessUpdated: TeamHeidiIiAccessUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = teamHeidiIiAccessUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/team_heidi_ii_access_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TeamHeidiIiAccessUpdated entity.
 */
export function createTeamHeidiIiAccessUpdated(teamHeidiIiAccessUpdated: TeamHeidiIiAccessUpdated){
    return {
        schema: 'iglu:com.heidi/team_heidi_ii_access_updated/jsonschema/1-0-1',
        data: teamHeidiIiAccessUpdated
    }
}
/**
 * Track a Snowplow event for CallSummaryGenerated.
 * Tracks when a call summary is successfully generated
 */
export function trackCallSummaryGenerated<T extends {} = any>(callSummaryGenerated: CallSummaryGenerated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callSummaryGenerated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_summary_generated/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallSummaryGenerated entity.
 */
export function createCallSummaryGenerated(callSummaryGenerated: CallSummaryGenerated){
    return {
        schema: 'iglu:com.heidi/call_summary_generated/jsonschema/1-0-0',
        data: callSummaryGenerated
    }
}
/**
 * Track a Snowplow event for CallSummaryEmailFailed.
 * Tracks when a call summary email fails to send
 */
export function trackCallSummaryEmailFailed<T extends {} = any>(callSummaryEmailFailed: CallSummaryEmailFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callSummaryEmailFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_summary_email_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallSummaryEmailFailed entity.
 */
export function createCallSummaryEmailFailed(callSummaryEmailFailed: CallSummaryEmailFailed){
    return {
        schema: 'iglu:com.heidi/call_summary_email_failed/jsonschema/1-0-0',
        data: callSummaryEmailFailed
    }
}
/**
 * Track a Snowplow event for LetterAttachmentRemoved.
 * Tracks successful removal of a letter attachment
 */
export function trackLetterAttachmentRemoved<T extends {} = any>(letterAttachmentRemoved: LetterAttachmentRemoved & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = letterAttachmentRemoved;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/letter_attachment_removed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LetterAttachmentRemoved entity.
 */
export function createLetterAttachmentRemoved(letterAttachmentRemoved: LetterAttachmentRemoved){
    return {
        schema: 'iglu:com.heidi/letter_attachment_removed/jsonschema/1-0-0',
        data: letterAttachmentRemoved
    }
}
/**
 * Track a Snowplow event for ContextFileUploadFailed.
 * Tracks context file upload failed event
 */
export function trackContextFileUploadFailed<T extends {} = any>(contextFileUploadFailed: ContextFileUploadFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = contextFileUploadFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/context_file_upload_failed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ContextFileUploadFailed entity.
 */
export function createContextFileUploadFailed(contextFileUploadFailed: ContextFileUploadFailed){
    return {
        schema: 'iglu:com.heidi/context_file_upload_failed/jsonschema/1-0-1',
        data: contextFileUploadFailed
    }
}
/**
 * Track a Snowplow event for ReferralCompleted.
 * Tracks referral completed event
 */
export function trackReferralCompleted<T extends {} = any>(referralCompleted: ReferralCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = referralCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/referral_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ReferralCompleted entity.
 */
export function createReferralCompleted(referralCompleted: ReferralCompleted){
    return {
        schema: 'iglu:com.heidi/referral_completed/jsonschema/1-0-1',
        data: referralCompleted
    }
}
/**
 * Track a Snowplow event for EvidenceShortcutsOpened.
 * Tracks when the Ask Heidi shortcuts menu is opened
 */
export function trackEvidenceShortcutsOpened<T extends {} = any>(evidenceShortcutsOpened: EvidenceShortcutsOpened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceShortcutsOpened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_shortcuts_opened/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceShortcutsOpened entity.
 */
export function createEvidenceShortcutsOpened(evidenceShortcutsOpened: EvidenceShortcutsOpened){
    return {
        schema: 'iglu:com.heidi/evidence_shortcuts_opened/jsonschema/1-0-0',
        data: evidenceShortcutsOpened
    }
}
/**
 * Track a Snowplow event for TemplateGeneration.
 * Tracks template generation event
 */
export function trackTemplateGeneration<T extends {} = any>(templateGeneration: TemplateGeneration & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateGeneration;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_generation/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateGeneration entity.
 */
export function createTemplateGeneration(templateGeneration: TemplateGeneration){
    return {
        schema: 'iglu:com.heidi/template_generation/jsonschema/1-0-2',
        data: templateGeneration
    }
}
/**
 * Track a Snowplow event for VoiceStyleUpdated.
 * Tracks voice style updated event
 */
export function trackVoiceStyleUpdated<T extends {} = any>(voiceStyleUpdated: VoiceStyleUpdated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = voiceStyleUpdated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/voice_style_updated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow VoiceStyleUpdated entity.
 */
export function createVoiceStyleUpdated(voiceStyleUpdated: VoiceStyleUpdated){
    return {
        schema: 'iglu:com.heidi/voice_style_updated/jsonschema/1-0-1',
        data: voiceStyleUpdated
    }
}
/**
 * Track a Snowplow event for SurveySubmitted.
 * Tracks when survey is submitted
 */
export function trackSurveySubmitted<T extends {} = any>(surveySubmitted: SurveySubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = surveySubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/survey_submitted/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SurveySubmitted entity.
 */
export function createSurveySubmitted(surveySubmitted: SurveySubmitted){
    return {
        schema: 'iglu:com.heidi/survey_submitted/jsonschema/2-0-1',
        data: surveySubmitted
    }
}
/**
 * Track a Snowplow event for ShareModalOpened.
 * Tracks when the share modal is opened
 */
export function trackShareModalOpened<T extends {} = any>(shareModalOpened: ShareModalOpened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareModalOpened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_modal_opened/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareModalOpened entity.
 */
export function createShareModalOpened(shareModalOpened: ShareModalOpened){
    return {
        schema: 'iglu:com.heidi/share_modal_opened/jsonschema/1-0-1',
        data: shareModalOpened
    }
}
/**
 * Track a Snowplow event for EvidencePromptsBarExpanded.
 * Tracks when the user expands the in-session Evidence prompts bar
 */
export function trackEvidencePromptsBarExpanded<T extends {} = any>(evidencePromptsBarExpanded: EvidencePromptsBarExpanded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidencePromptsBarExpanded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_prompts_bar_expanded/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidencePromptsBarExpanded entity.
 */
export function createEvidencePromptsBarExpanded(evidencePromptsBarExpanded: EvidencePromptsBarExpanded){
    return {
        schema: 'iglu:com.heidi/evidence_prompts_bar_expanded/jsonschema/1-0-0',
        data: evidencePromptsBarExpanded
    }
}
/**
 * Track a Snowplow event for WorkFollowUpActioned.
 * Tracks when a user sees, clicks, or dismisses a follow-up suggestion under a Work reply
 */
export function trackWorkFollowUpActioned<T extends {} = any>(workFollowUpActioned: WorkFollowUpActioned & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workFollowUpActioned;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_follow_up_actioned/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkFollowUpActioned entity.
 */
export function createWorkFollowUpActioned(workFollowUpActioned: WorkFollowUpActioned){
    return {
        schema: 'iglu:com.heidi/work_follow_up_actioned/jsonschema/1-0-0',
        data: workFollowUpActioned
    }
}
/**
 * Track a Snowplow event for SessionViewed.
 * Tracks when session is viewed
 */
export function trackSessionViewed<T extends {} = any>(sessionViewed: SessionViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/session_viewed/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionViewed entity.
 */
export function createSessionViewed(sessionViewed: SessionViewed){
    return {
        schema: 'iglu:com.heidi/session_viewed/jsonschema/2-0-3',
        data: sessionViewed
    }
}
/**
 * Track a Snowplow event for ContactSalesFormSubmitted.
 * Tracks contact sales form submitted event
 */
export function trackContactSalesFormSubmitted<T extends {} = any>(contactSalesFormSubmitted: ContactSalesFormSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = contactSalesFormSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/contact_sales_form_submitted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ContactSalesFormSubmitted entity.
 */
export function createContactSalesFormSubmitted(contactSalesFormSubmitted: ContactSalesFormSubmitted){
    return {
        schema: 'iglu:com.heidi/contact_sales_form_submitted/jsonschema/1-0-1',
        data: contactSalesFormSubmitted
    }
}
/**
 * Track a Snowplow event for PatientSummaryCtaShown.
 * Tracks the send-to-patient CTA becoming visible (impression), on whichever surface exposes it — see location
 */
export function trackPatientSummaryCtaShown<T extends {} = any>(patientSummaryCtaShown: PatientSummaryCtaShown & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientSummaryCtaShown;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_summary_cta_shown/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientSummaryCtaShown entity.
 */
export function createPatientSummaryCtaShown(patientSummaryCtaShown: PatientSummaryCtaShown){
    return {
        schema: 'iglu:com.heidi/patient_summary_cta_shown/jsonschema/1-0-1',
        data: patientSummaryCtaShown
    }
}
/**
 * Track a Snowplow event for WorkWorkflowActioned.
 * Workflow or routine lifecycle action (install, edit, run, share, schedule)
 */
export function trackWorkWorkflowActioned<T extends {} = any>(workWorkflowActioned: WorkWorkflowActioned & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workWorkflowActioned;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_workflow_actioned/jsonschema/1-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkWorkflowActioned entity.
 */
export function createWorkWorkflowActioned(workWorkflowActioned: WorkWorkflowActioned){
    return {
        schema: 'iglu:com.heidi/work_workflow_actioned/jsonschema/1-1-0',
        data: workWorkflowActioned
    }
}
/**
 * Track a Snowplow event for CommunityRejected.
 * Tracks when a community template request is rejected
 */
export function trackCommunityRejected<T extends {} = any>(communityRejected: CommunityRejected & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = communityRejected;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/community_rejected/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CommunityRejected entity.
 */
export function createCommunityRejected(communityRejected: CommunityRejected){
    return {
        schema: 'iglu:com.heidi/community_rejected/jsonschema/1-0-0',
        data: communityRejected
    }
}
/**
 * Track a Snowplow event for PaymentCompleted.
 * Tracks successful payment completion
 */
export function trackPaymentCompleted<T extends {} = any>(paymentCompleted: PaymentCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = paymentCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/payment_completed/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PaymentCompleted entity.
 */
export function createPaymentCompleted(paymentCompleted: PaymentCompleted){
    return {
        schema: 'iglu:com.heidi/payment_completed/jsonschema/2-0-3',
        data: paymentCompleted
    }
}
/**
 * Track a Snowplow event for TrialCompleted.
 * Tracks when a trial period ends
 */
export function trackTrialCompleted<T extends {} = any>(trialCompleted: TrialCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = trialCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/trial_completed/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TrialCompleted entity.
 */
export function createTrialCompleted(trialCompleted: TrialCompleted){
    return {
        schema: 'iglu:com.heidi/trial_completed/jsonschema/2-0-0',
        data: trialCompleted
    }
}
/**
 * Track a Snowplow event for ConversationAssigned.
 * Tracks when a conversation is assigned
 */
export function trackConversationAssigned<T extends {} = any>(conversationAssigned: ConversationAssigned & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = conversationAssigned;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/conversation_assigned/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ConversationAssigned entity.
 */
export function createConversationAssigned(conversationAssigned: ConversationAssigned){
    return {
        schema: 'iglu:com.heidi/conversation_assigned/jsonschema/1-0-0',
        data: conversationAssigned
    }
}
/**
 * Track a Snowplow event for VerificationFeedbackSubmitted.
 * Tracks when feedback is submitted for a verification result
 */
export function trackVerificationFeedbackSubmitted<T extends {} = any>(verificationFeedbackSubmitted: VerificationFeedbackSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = verificationFeedbackSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/verification_feedback_submitted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow VerificationFeedbackSubmitted entity.
 */
export function createVerificationFeedbackSubmitted(verificationFeedbackSubmitted: VerificationFeedbackSubmitted){
    return {
        schema: 'iglu:com.heidi/verification_feedback_submitted/jsonschema/1-0-1',
        data: verificationFeedbackSubmitted
    }
}
/**
 * Track a Snowplow event for PatientContextToggleOn.
 * Tracks patient context toggle on event
 */
export function trackPatientContextToggleOn<T extends {} = any>(patientContextToggleOn: PatientContextToggleOn & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientContextToggleOn;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_context_toggle_on/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientContextToggleOn entity.
 */
export function createPatientContextToggleOn(patientContextToggleOn: PatientContextToggleOn){
    return {
        schema: 'iglu:com.heidi/patient_context_toggle_on/jsonschema/1-0-1',
        data: patientContextToggleOn
    }
}
/**
 * Track a Snowplow event for CommsEntryPointClicked.
 * Tracks when a user clicks an entry point to the Comms feature
 */
export function trackCommsEntryPointClicked<T extends {} = any>(commsEntryPointClicked: CommsEntryPointClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = commsEntryPointClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/comms_entry_point_clicked/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CommsEntryPointClicked entity.
 */
export function createCommsEntryPointClicked(commsEntryPointClicked: CommsEntryPointClicked){
    return {
        schema: 'iglu:com.heidi/comms_entry_point_clicked/jsonschema/1-0-0',
        data: commsEntryPointClicked
    }
}
/**
 * Track a Snowplow event for EvidenceChatCtaClicked.
 * Tracks when a user clicks an inline &lt;heidi-button&gt; CTA in an Evidence chat response. chat_id/message_id attribute the CTA click (an engagement signal) to the specific model that produced the response.
 */
export function trackEvidenceChatCtaClicked<T extends {} = any>(evidenceChatCtaClicked: EvidenceChatCtaClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceChatCtaClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_chat_cta_clicked/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceChatCtaClicked entity.
 */
export function createEvidenceChatCtaClicked(evidenceChatCtaClicked: EvidenceChatCtaClicked){
    return {
        schema: 'iglu:com.heidi/evidence_chat_cta_clicked/jsonschema/1-0-0',
        data: evidenceChatCtaClicked
    }
}
/**
 * Track a Snowplow event for CpdDraftCreated.
 * Tracks cpd draft created event
 */
export function trackCpdDraftCreated<T extends {} = any>(cpdDraftCreated: CpdDraftCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = cpdDraftCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/cpd_draft_created/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CpdDraftCreated entity.
 */
export function createCpdDraftCreated(cpdDraftCreated: CpdDraftCreated){
    return {
        schema: 'iglu:com.heidi/cpd_draft_created/jsonschema/1-0-1',
        data: cpdDraftCreated
    }
}
/**
 * Track a Snowplow event for ContextDeleted.
 * Tracks context deleted event
 */
export function trackContextDeleted<T extends {} = any>(contextDeleted: ContextDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = contextDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/context_deleted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ContextDeleted entity.
 */
export function createContextDeleted(contextDeleted: ContextDeleted){
    return {
        schema: 'iglu:com.heidi/context_deleted/jsonschema/1-0-1',
        data: contextDeleted
    }
}
/**
 * Track a Snowplow event for ResponseCopied.
 * Tracks when AI response is copied
 */
export function trackResponseCopied<T extends {} = any>(responseCopied: ResponseCopied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = responseCopied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/response_copied/jsonschema/1-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ResponseCopied entity.
 */
export function createResponseCopied(responseCopied: ResponseCopied){
    return {
        schema: 'iglu:com.heidi/response_copied/jsonschema/1-0-3',
        data: responseCopied
    }
}
/**
 * Track a Snowplow event for ShareMemberAdded.
 * Tracks when a member is added to a share
 */
export function trackShareMemberAdded<T extends {} = any>(shareMemberAdded: ShareMemberAdded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareMemberAdded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_member_added/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareMemberAdded entity.
 */
export function createShareMemberAdded(shareMemberAdded: ShareMemberAdded){
    return {
        schema: 'iglu:com.heidi/share_member_added/jsonschema/1-0-1',
        data: shareMemberAdded
    }
}
/**
 * Track a Snowplow event for ObjectiveGuardrailTriggered.
 * Tracks when an AI objective triggers a safety guardrail
 */
export function trackObjectiveGuardrailTriggered<T extends {} = any>(objectiveGuardrailTriggered: ObjectiveGuardrailTriggered & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = objectiveGuardrailTriggered;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/objective_guardrail_triggered/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ObjectiveGuardrailTriggered entity.
 */
export function createObjectiveGuardrailTriggered(objectiveGuardrailTriggered: ObjectiveGuardrailTriggered){
    return {
        schema: 'iglu:com.heidi/objective_guardrail_triggered/jsonschema/1-0-0',
        data: objectiveGuardrailTriggered
    }
}
/**
 * Track a Snowplow event for BookDemoFormSubmitted.
 * Tracks book demo form submitted event
 */
export function trackBookDemoFormSubmitted<T extends {} = any>(bookDemoFormSubmitted: BookDemoFormSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = bookDemoFormSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/book_demo_form_submitted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow BookDemoFormSubmitted entity.
 */
export function createBookDemoFormSubmitted(bookDemoFormSubmitted: BookDemoFormSubmitted){
    return {
        schema: 'iglu:com.heidi/book_demo_form_submitted/jsonschema/1-0-1',
        data: bookDemoFormSubmitted
    }
}
/**
 * Track a Snowplow event for EvidenceSignUpWallViewed.
 * Tracks when the Evidence sign-up wall is viewed
 */
export function trackEvidenceSignUpWallViewed<T extends {} = any>(evidenceSignUpWallViewed: EvidenceSignUpWallViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceSignUpWallViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_sign_up_wall_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceSignUpWallViewed entity.
 */
export function createEvidenceSignUpWallViewed(evidenceSignUpWallViewed: EvidenceSignUpWallViewed){
    return {
        schema: 'iglu:com.heidi/evidence_sign_up_wall_viewed/jsonschema/1-0-1',
        data: evidenceSignUpWallViewed
    }
}
/**
 * Track a Snowplow event for DocumentCreated.
 * Tracks when a document is created through various methods in the application
 */
export function trackDocumentCreated<T extends {} = any>(documentCreated: DocumentCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = documentCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/document_created/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DocumentCreated entity.
 */
export function createDocumentCreated(documentCreated: DocumentCreated){
    return {
        schema: 'iglu:com.heidi/document_created/jsonschema/2-0-3',
        data: documentCreated
    }
}
/**
 * Track a Snowplow event for IntegrationUserSearched.
 * Tracks integration user searched event
 */
export function trackIntegrationUserSearched<T extends {} = any>(integrationUserSearched: IntegrationUserSearched & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationUserSearched;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_user_searched/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationUserSearched entity.
 */
export function createIntegrationUserSearched(integrationUserSearched: IntegrationUserSearched){
    return {
        schema: 'iglu:com.heidi/integration_user_searched/jsonschema/1-0-1',
        data: integrationUserSearched
    }
}
/**
 * Track a Snowplow event for OutboundMessageFailed.
 * Tracks when an outbound message fails to deliver
 */
export function trackOutboundMessageFailed<T extends {} = any>(outboundMessageFailed: OutboundMessageFailed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = outboundMessageFailed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/outbound_message_failed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OutboundMessageFailed entity.
 */
export function createOutboundMessageFailed(outboundMessageFailed: OutboundMessageFailed){
    return {
        schema: 'iglu:com.heidi/outbound_message_failed/jsonschema/1-0-0',
        data: outboundMessageFailed
    }
}
/**
 * Track a Snowplow event for ShareLinkCopied.
 * Tracks when a share link is copied to clipboard
 */
export function trackShareLinkCopied<T extends {} = any>(shareLinkCopied: ShareLinkCopied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = shareLinkCopied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/share_link_copied/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ShareLinkCopied entity.
 */
export function createShareLinkCopied(shareLinkCopied: ShareLinkCopied){
    return {
        schema: 'iglu:com.heidi/share_link_copied/jsonschema/1-0-1',
        data: shareLinkCopied
    }
}
/**
 * Track a Snowplow event for InboundCallRouted.
 * Tracks when an inbound call is routed to a handler
 */
export function trackInboundCallRouted<T extends {} = any>(inboundCallRouted: InboundCallRouted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = inboundCallRouted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/inbound_call_routed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow InboundCallRouted entity.
 */
export function createInboundCallRouted(inboundCallRouted: InboundCallRouted){
    return {
        schema: 'iglu:com.heidi/inbound_call_routed/jsonschema/1-0-0',
        data: inboundCallRouted
    }
}
/**
 * Track a Snowplow event for UserSignedOut.
 * Tracks when user signs out
 */
export function trackUserSignedOut<T extends {} = any>(userSignedOut: UserSignedOut & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = userSignedOut;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/user_signed_out/jsonschema/2-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow UserSignedOut entity.
 */
export function createUserSignedOut(userSignedOut: UserSignedOut){
    return {
        schema: 'iglu:com.heidi/user_signed_out/jsonschema/2-0-1',
        data: userSignedOut
    }
}
/**
 * Track a Snowplow event for ForceUpdateViewed.
 * Tracks force update viewed event
 */
export function trackForceUpdateViewed<T extends {} = any>(forceUpdateViewed: ForceUpdateViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = forceUpdateViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/force_update_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ForceUpdateViewed entity.
 */
export function createForceUpdateViewed(forceUpdateViewed: ForceUpdateViewed){
    return {
        schema: 'iglu:com.heidi/force_update_viewed/jsonschema/1-0-1',
        data: forceUpdateViewed
    }
}
/**
 * Track a Snowplow event for PatientDocumentUnlinked.
 * Tracks when patient documents are unlinked from a session&#x27;s context set
 */
export function trackPatientDocumentUnlinked<T extends {} = any>(patientDocumentUnlinked: PatientDocumentUnlinked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientDocumentUnlinked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_document_unlinked/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientDocumentUnlinked entity.
 */
export function createPatientDocumentUnlinked(patientDocumentUnlinked: PatientDocumentUnlinked){
    return {
        schema: 'iglu:com.heidi/patient_document_unlinked/jsonschema/1-0-0',
        data: patientDocumentUnlinked
    }
}
/**
 * Track a Snowplow event for TemplateDeleted.
 * Tracks when a template is deleted
 */
export function trackTemplateDeleted<T extends {} = any>(templateDeleted: TemplateDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = templateDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/template_deleted/jsonschema/2-0-4',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TemplateDeleted entity.
 */
export function createTemplateDeleted(templateDeleted: TemplateDeleted){
    return {
        schema: 'iglu:com.heidi/template_deleted/jsonschema/2-0-4',
        data: templateDeleted
    }
}
/**
 * Track a Snowplow event for ConsultNoteGeneration.
 * Tracks consult note generation events including success and failure outcomes
 */
export function trackConsultNoteGeneration<T extends {} = any>(consultNoteGeneration: ConsultNoteGeneration & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = consultNoteGeneration;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/consult_note_generation/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ConsultNoteGeneration entity.
 */
export function createConsultNoteGeneration(consultNoteGeneration: ConsultNoteGeneration){
    return {
        schema: 'iglu:com.heidi/consult_note_generation/jsonschema/1-0-1',
        data: consultNoteGeneration
    }
}
/**
 * Track a Snowplow event for EvidenceToastShown.
 * Tracks when an evidence toast notification is shown
 */
export function trackEvidenceToastShown<T extends {} = any>(evidenceToastShown: EvidenceToastShown & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceToastShown;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_toast_shown/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceToastShown entity.
 */
export function createEvidenceToastShown(evidenceToastShown: EvidenceToastShown){
    return {
        schema: 'iglu:com.heidi/evidence_toast_shown/jsonschema/1-0-1',
        data: evidenceToastShown
    }
}
/**
 * Track a Snowplow event for ToastViewed.
 * Tracks when a toast notification is displayed
 */
export function trackToastViewed<T extends {} = any>(toastViewed: ToastViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = toastViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/toast_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ToastViewed entity.
 */
export function createToastViewed(toastViewed: ToastViewed){
    return {
        schema: 'iglu:com.heidi/toast_viewed/jsonschema/1-0-1',
        data: toastViewed
    }
}
/**
 * Track a Snowplow event for CommunityViewed.
 * Tracks community viewed event
 */
export function trackCommunityViewed<T extends {} = any>(communityViewed: CommunityViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = communityViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/community_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CommunityViewed entity.
 */
export function createCommunityViewed(communityViewed: CommunityViewed){
    return {
        schema: 'iglu:com.heidi/community_viewed/jsonschema/1-0-1',
        data: communityViewed
    }
}
/**
 * Track a Snowplow event for ErrorViewed.
 * Tracks when an error is viewed by the user
 */
export function trackErrorViewed<T extends {} = any>(errorViewed: ErrorViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = errorViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/error_viewed/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ErrorViewed entity.
 */
export function createErrorViewed(errorViewed: ErrorViewed){
    return {
        schema: 'iglu:com.heidi/error_viewed/jsonschema/1-0-2',
        data: errorViewed
    }
}
/**
 * Track a Snowplow event for IntegrationCompleted.
 * Tracks integration completed event
 */
export function trackIntegrationCompleted<T extends {} = any>(integrationCompleted: IntegrationCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_completed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationCompleted entity.
 */
export function createIntegrationCompleted(integrationCompleted: IntegrationCompleted){
    return {
        schema: 'iglu:com.heidi/integration_completed/jsonschema/1-0-1',
        data: integrationCompleted
    }
}
/**
 * Track a Snowplow event for ChatViewed.
 * Tracks when an existing chat is viewed
 */
export function trackChatViewed<T extends {} = any>(chatViewed: ChatViewed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chatViewed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/chat_viewed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChatViewed entity.
 */
export function createChatViewed(chatViewed: ChatViewed){
    return {
        schema: 'iglu:com.heidi/chat_viewed/jsonschema/1-0-1',
        data: chatViewed
    }
}
/**
 * Track a Snowplow event for EvidenceShareModalOpened.
 * Tracks when the Evidence share modal is opened
 */
export function trackEvidenceShareModalOpened<T extends {} = any>(evidenceShareModalOpened: EvidenceShareModalOpened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceShareModalOpened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_share_modal_opened/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceShareModalOpened entity.
 */
export function createEvidenceShareModalOpened(evidenceShareModalOpened: EvidenceShareModalOpened){
    return {
        schema: 'iglu:com.heidi/evidence_share_modal_opened/jsonschema/1-0-2',
        data: evidenceShareModalOpened
    }
}
/**
 * Track a Snowplow event for NoteSynced.
 * Tracks when a note is synced/pushed to an EHR
 */
export function trackNoteSynced<T extends {} = any>(noteSynced: NoteSynced & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = noteSynced;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/note_synced/jsonschema/1-0-6',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NoteSynced entity.
 */
export function createNoteSynced(noteSynced: NoteSynced){
    return {
        schema: 'iglu:com.heidi/note_synced/jsonschema/1-0-6',
        data: noteSynced
    }
}
/**
 * Track a Snowplow event for LibraryDocumentAddedToCollection.
 * Tracks library document added to collection event
 */
export function trackLibraryDocumentAddedToCollection<T extends {} = any>(libraryDocumentAddedToCollection: LibraryDocumentAddedToCollection & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = libraryDocumentAddedToCollection;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/library_document_added_to_collection/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow LibraryDocumentAddedToCollection entity.
 */
export function createLibraryDocumentAddedToCollection(libraryDocumentAddedToCollection: LibraryDocumentAddedToCollection){
    return {
        schema: 'iglu:com.heidi/library_document_added_to_collection/jsonschema/1-0-1',
        data: libraryDocumentAddedToCollection
    }
}
/**
 * Track a Snowplow event for ChatCreated.
 * Tracks when a new chat is created
 */
export function trackChatCreated<T extends {} = any>(chatCreated: ChatCreated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = chatCreated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/chat_created/jsonschema/1-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ChatCreated entity.
 */
export function createChatCreated(chatCreated: ChatCreated){
    return {
        schema: 'iglu:com.heidi/chat_created/jsonschema/1-0-2',
        data: chatCreated
    }
}
/**
 * Track a Snowplow event for NudgeCardDismissed.
 * A nudge card was dismissed
 */
export function trackNudgeCardDismissed<T extends {} = any>(nudgeCardDismissed: NudgeCardDismissed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = nudgeCardDismissed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/nudge_card_dismissed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow NudgeCardDismissed entity.
 */
export function createNudgeCardDismissed(nudgeCardDismissed: NudgeCardDismissed){
    return {
        schema: 'iglu:com.heidi/nudge_card_dismissed/jsonschema/1-0-0',
        data: nudgeCardDismissed
    }
}
/**
 * Track a Snowplow event for EvidenceVerbClicked.
 * Tracks evidence verb clicked event
 */
export function trackEvidenceVerbClicked<T extends {} = any>(evidenceVerbClicked: EvidenceVerbClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceVerbClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_verb_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceVerbClicked entity.
 */
export function createEvidenceVerbClicked(evidenceVerbClicked: EvidenceVerbClicked){
    return {
        schema: 'iglu:com.heidi/evidence_verb_clicked/jsonschema/1-0-1',
        data: evidenceVerbClicked
    }
}
/**
 * Track a Snowplow event for CpdActivityStarted.
 * Tracks when a CPD activity is queued for processing
 */
export function trackCpdActivityStarted<T extends {} = any>(cpdActivityStarted: CpdActivityStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = cpdActivityStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/cpd_activity_started/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CpdActivityStarted entity.
 */
export function createCpdActivityStarted(cpdActivityStarted: CpdActivityStarted){
    return {
        schema: 'iglu:com.heidi/cpd_activity_started/jsonschema/1-0-1',
        data: cpdActivityStarted
    }
}
/**
 * Track a Snowplow event for HardwareRecordingStart.
 * Tracks when a hardware recording starts
 */
export function trackHardwareRecordingStart<T extends {} = any>(hardwareRecordingStart: HardwareRecordingStart & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = hardwareRecordingStart;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/hardware_recording_start/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow HardwareRecordingStart entity.
 */
export function createHardwareRecordingStart(hardwareRecordingStart: HardwareRecordingStart){
    return {
        schema: 'iglu:com.heidi/hardware_recording_start/jsonschema/1-0-1',
        data: hardwareRecordingStart
    }
}
/**
 * Track a Snowplow event for DocumentCopied.
 * Tracks document copied event
 */
export function trackDocumentCopied<T extends {} = any>(documentCopied: DocumentCopied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = documentCopied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/document_copied/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DocumentCopied entity.
 */
export function createDocumentCopied(documentCopied: DocumentCopied){
    return {
        schema: 'iglu:com.heidi/document_copied/jsonschema/1-0-1',
        data: documentCopied
    }
}
/**
 * Track a Snowplow event for CpdDraftResumed.
 * Tracks cpd draft resumed event
 */
export function trackCpdDraftResumed<T extends {} = any>(cpdDraftResumed: CpdDraftResumed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = cpdDraftResumed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/cpd_draft_resumed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CpdDraftResumed entity.
 */
export function createCpdDraftResumed(cpdDraftResumed: CpdDraftResumed){
    return {
        schema: 'iglu:com.heidi/cpd_draft_resumed/jsonschema/1-0-1',
        data: cpdDraftResumed
    }
}
/**
 * Track a Snowplow event for PatientExtractionModalOpened.
 * Tracks patient extraction modal opened event
 */
export function trackPatientExtractionModalOpened<T extends {} = any>(patientExtractionModalOpened: PatientExtractionModalOpened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = patientExtractionModalOpened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/patient_extraction_modal_opened/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PatientExtractionModalOpened entity.
 */
export function createPatientExtractionModalOpened(patientExtractionModalOpened: PatientExtractionModalOpened){
    return {
        schema: 'iglu:com.heidi/patient_extraction_modal_opened/jsonschema/1-0-1',
        data: patientExtractionModalOpened
    }
}
/**
 * Track a Snowplow event for ComplianceCheck.
 * Tracks when a compliance check is performed (NHS template or Ask Heidi)
 */
export function trackComplianceCheck<T extends {} = any>(complianceCheck: ComplianceCheck & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = complianceCheck;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/compliance_check/jsonschema/1-0-4',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ComplianceCheck entity.
 */
export function createComplianceCheck(complianceCheck: ComplianceCheck){
    return {
        schema: 'iglu:com.heidi/compliance_check/jsonschema/1-0-4',
        data: complianceCheck
    }
}
/**
 * Track a Snowplow event for TranslatorOnboardingCompleted.
 * Fired when the patient onboarding flow completes, is skipped, or was not required
 */
export function trackTranslatorOnboardingCompleted<T extends {} = any>(translatorOnboardingCompleted: TranslatorOnboardingCompleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = translatorOnboardingCompleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/translator_onboarding_completed/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TranslatorOnboardingCompleted entity.
 */
export function createTranslatorOnboardingCompleted(translatorOnboardingCompleted: TranslatorOnboardingCompleted){
    return {
        schema: 'iglu:com.heidi/translator_onboarding_completed/jsonschema/1-0-0',
        data: translatorOnboardingCompleted
    }
}
/**
 * Track a Snowplow event for SubscriptionStarted.
 * Tracks when a user starts a paid subscription
 */
export function trackSubscriptionStarted<T extends {} = any>(subscriptionStarted: SubscriptionStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = subscriptionStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/subscription_started/jsonschema/1-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SubscriptionStarted entity.
 */
export function createSubscriptionStarted(subscriptionStarted: SubscriptionStarted){
    return {
        schema: 'iglu:com.heidi/subscription_started/jsonschema/1-1-0',
        data: subscriptionStarted
    }
}
/**
 * Track a Snowplow event for IntegrationLinked.
 * Tracks when integration is successfully connected
 */
export function trackIntegrationLinked<T extends {} = any>(integrationLinked: IntegrationLinked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = integrationLinked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/integration_linked/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow IntegrationLinked entity.
 */
export function createIntegrationLinked(integrationLinked: IntegrationLinked){
    return {
        schema: 'iglu:com.heidi/integration_linked/jsonschema/2-0-0',
        data: integrationLinked
    }
}
/**
 * Track a Snowplow event for PushWarningResolved.
 * Tracks the clinician&#x27;s decision on a pre-push warning
 */
export function trackPushWarningResolved<T extends {} = any>(pushWarningResolved: PushWarningResolved & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = pushWarningResolved;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/push_warning_resolved/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PushWarningResolved entity.
 */
export function createPushWarningResolved(pushWarningResolved: PushWarningResolved){
    return {
        schema: 'iglu:com.heidi/push_warning_resolved/jsonschema/1-0-0',
        data: pushWarningResolved
    }
}
/**
 * Track a Snowplow event for EvidenceChatAborted.
 * Tracks when a user aborts an evidence chat response
 */
export function trackEvidenceChatAborted<T extends {} = any>(evidenceChatAborted: EvidenceChatAborted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceChatAborted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_chat_aborted/jsonschema/2-0-3',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceChatAborted entity.
 */
export function createEvidenceChatAborted(evidenceChatAborted: EvidenceChatAborted){
    return {
        schema: 'iglu:com.heidi/evidence_chat_aborted/jsonschema/2-0-3',
        data: evidenceChatAborted
    }
}
/**
 * Track a Snowplow event for SessionSearchUsageBaseline.
 * Tracks session search usage baseline event
 */
export function trackSessionSearchUsageBaseline<T extends {} = any>(sessionSearchUsageBaseline: SessionSearchUsageBaseline & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = sessionSearchUsageBaseline;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/session_search_usage_baseline/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow SessionSearchUsageBaseline entity.
 */
export function createSessionSearchUsageBaseline(sessionSearchUsageBaseline: SessionSearchUsageBaseline){
    return {
        schema: 'iglu:com.heidi/session_search_usage_baseline/jsonschema/1-0-1',
        data: sessionSearchUsageBaseline
    }
}
/**
 * Track a Snowplow event for PanelOpened.
 * Tracks when a panel is opened
 */
export function trackPanelOpened<T extends {} = any>(panelOpened: PanelOpened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = panelOpened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/panel_opened/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow PanelOpened entity.
 */
export function createPanelOpened(panelOpened: PanelOpened){
    return {
        schema: 'iglu:com.heidi/panel_opened/jsonschema/2-0-2',
        data: panelOpened
    }
}
/**
 * Track a Snowplow event for VerificationStarted.
 * Tracks when a document verification is started
 */
export function trackVerificationStarted<T extends {} = any>(verificationStarted: VerificationStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = verificationStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/verification_started/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow VerificationStarted entity.
 */
export function createVerificationStarted(verificationStarted: VerificationStarted){
    return {
        schema: 'iglu:com.heidi/verification_started/jsonschema/1-0-1',
        data: verificationStarted
    }
}
/**
 * Track a Snowplow event for ConversationReopened.
 * Tracks when a resolved conversation is reopened
 */
export function trackConversationReopened<T extends {} = any>(conversationReopened: ConversationReopened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = conversationReopened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/conversation_reopened/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ConversationReopened entity.
 */
export function createConversationReopened(conversationReopened: ConversationReopened){
    return {
        schema: 'iglu:com.heidi/conversation_reopened/jsonschema/1-0-0',
        data: conversationReopened
    }
}
/**
 * Track a Snowplow event for EvidenceSharedChatOpened.
 * Tracks when a shared Evidence chat is opened by a recipient
 */
export function trackEvidenceSharedChatOpened<T extends {} = any>(evidenceSharedChatOpened: EvidenceSharedChatOpened & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceSharedChatOpened;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_shared_chat_opened/jsonschema/2-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceSharedChatOpened entity.
 */
export function createEvidenceSharedChatOpened(evidenceSharedChatOpened: EvidenceSharedChatOpened){
    return {
        schema: 'iglu:com.heidi/evidence_shared_chat_opened/jsonschema/2-0-0',
        data: evidenceSharedChatOpened
    }
}
/**
 * Track a Snowplow event for AppStoreReviewPromptRequested.
 * Fired when the App Store review sentiment gate is presented to the user, spending one lifetime prompt slot
 */
export function trackAppStoreReviewPromptRequested<T extends {} = any>(appStoreReviewPromptRequested: AppStoreReviewPromptRequested & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = appStoreReviewPromptRequested;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/app_store_review_prompt_requested/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AppStoreReviewPromptRequested entity.
 */
export function createAppStoreReviewPromptRequested(appStoreReviewPromptRequested: AppStoreReviewPromptRequested){
    return {
        schema: 'iglu:com.heidi/app_store_review_prompt_requested/jsonschema/1-0-0',
        data: appStoreReviewPromptRequested
    }
}
/**
 * Track a Snowplow event for DocumentGenerated.
 * Tracks document generated event
 */
export function trackDocumentGenerated<T extends {} = any>(documentGenerated: DocumentGenerated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = documentGenerated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/document_generated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DocumentGenerated entity.
 */
export function createDocumentGenerated(documentGenerated: DocumentGenerated){
    return {
        schema: 'iglu:com.heidi/document_generated/jsonschema/1-0-1',
        data: documentGenerated
    }
}
/**
 * Track a Snowplow event for DoNotContactAdded.
 * Tracks when a phone number is added to the do-not-contact list
 */
export function trackDoNotContactAdded<T extends {} = any>(doNotContactAdded: DoNotContactAdded & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = doNotContactAdded;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/do_not_contact_added/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow DoNotContactAdded entity.
 */
export function createDoNotContactAdded(doNotContactAdded: DoNotContactAdded){
    return {
        schema: 'iglu:com.heidi/do_not_contact_added/jsonschema/1-0-0',
        data: doNotContactAdded
    }
}
/**
 * Track a Snowplow event for EvidenceRequestAccessClicked.
 * Tracks when a team member requests access to Evidence Pro
 */
export function trackEvidenceRequestAccessClicked<T extends {} = any>(evidenceRequestAccessClicked: EvidenceRequestAccessClicked & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceRequestAccessClicked;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_request_access_clicked/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceRequestAccessClicked entity.
 */
export function createEvidenceRequestAccessClicked(evidenceRequestAccessClicked: EvidenceRequestAccessClicked){
    return {
        schema: 'iglu:com.heidi/evidence_request_access_clicked/jsonschema/1-0-1',
        data: evidenceRequestAccessClicked
    }
}
/**
 * Track a Snowplow event for TranslatorMessageInteraction.
 * Fired when a user interacts with a translated message (edit, playback, remove)
 */
export function trackTranslatorMessageInteraction<T extends {} = any>(translatorMessageInteraction: TranslatorMessageInteraction & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = translatorMessageInteraction;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/translator_message_interaction/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow TranslatorMessageInteraction entity.
 */
export function createTranslatorMessageInteraction(translatorMessageInteraction: TranslatorMessageInteraction){
    return {
        schema: 'iglu:com.heidi/translator_message_interaction/jsonschema/1-0-0',
        data: translatorMessageInteraction
    }
}
/**
 * Track a Snowplow event for WorkMessageActioned.
 * User feedback or copy action on a Work message
 */
export function trackWorkMessageActioned<T extends {} = any>(workMessageActioned: WorkMessageActioned & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workMessageActioned;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_message_actioned/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkMessageActioned entity.
 */
export function createWorkMessageActioned(workMessageActioned: WorkMessageActioned){
    return {
        schema: 'iglu:com.heidi/work_message_actioned/jsonschema/1-0-0',
        data: workMessageActioned
    }
}
/**
 * Track a Snowplow event for WorkArtifactActioned.
 * Artifact canvas or panel action (created, open, edit, export, apply, close, tab switch, verification)
 */
export function trackWorkArtifactActioned<T extends {} = any>(workArtifactActioned: WorkArtifactActioned & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = workArtifactActioned;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/work_artifact_actioned/jsonschema/1-1-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow WorkArtifactActioned entity.
 */
export function createWorkArtifactActioned(workArtifactActioned: WorkArtifactActioned){
    return {
        schema: 'iglu:com.heidi/work_artifact_actioned/jsonschema/1-1-0',
        data: workArtifactActioned
    }
}
/**
 * Track a Snowplow event for HaikuTabChanged.
 * Tracks when a user navigates between tabs in Haiku
 */
export function trackHaikuTabChanged<T extends {} = any>(haikuTabChanged: HaikuTabChanged & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = haikuTabChanged;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/haiku_tab_changed/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow HaikuTabChanged entity.
 */
export function createHaikuTabChanged(haikuTabChanged: HaikuTabChanged){
    return {
        schema: 'iglu:com.heidi/haiku_tab_changed/jsonschema/1-0-1',
        data: haikuTabChanged
    }
}
/**
 * Track a Snowplow event for OfflineRecordingStarted.
 * Tracks the start of local audio capture for an offline desktop recording
 */
export function trackOfflineRecordingStarted<T extends {} = any>(offlineRecordingStarted: OfflineRecordingStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = offlineRecordingStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/offline_recording_started/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow OfflineRecordingStarted entity.
 */
export function createOfflineRecordingStarted(offlineRecordingStarted: OfflineRecordingStarted){
    return {
        schema: 'iglu:com.heidi/offline_recording_started/jsonschema/1-0-1',
        data: offlineRecordingStarted
    }
}
/**
 * Track a Snowplow event for InboundMessageReceived.
 * Tracks when an inbound message is received
 */
export function trackInboundMessageReceived<T extends {} = any>(inboundMessageReceived: InboundMessageReceived & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = inboundMessageReceived;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/inbound_message_received/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow InboundMessageReceived entity.
 */
export function createInboundMessageReceived(inboundMessageReceived: InboundMessageReceived){
    return {
        schema: 'iglu:com.heidi/inbound_message_received/jsonschema/1-0-0',
        data: inboundMessageReceived
    }
}
/**
 * Track a Snowplow event for EvidenceExperienceFeedbackDismissed.
 * Tracks when a user dismisses the experience feedback nudge
 */
export function trackEvidenceExperienceFeedbackDismissed<T extends {} = any>(evidenceExperienceFeedbackDismissed: EvidenceExperienceFeedbackDismissed & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceExperienceFeedbackDismissed;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_experience_feedback_dismissed/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceExperienceFeedbackDismissed entity.
 */
export function createEvidenceExperienceFeedbackDismissed(evidenceExperienceFeedbackDismissed: EvidenceExperienceFeedbackDismissed){
    return {
        schema: 'iglu:com.heidi/evidence_experience_feedback_dismissed/jsonschema/2-0-2',
        data: evidenceExperienceFeedbackDismissed
    }
}
/**
 * Track a Snowplow event for CallInitiated.
 * Tracks when an outbound call is initiated
 */
export function trackCallInitiated<T extends {} = any>(callInitiated: CallInitiated & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = callInitiated;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/call_initiated/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CallInitiated entity.
 */
export function createCallInitiated(callInitiated: CallInitiated){
    return {
        schema: 'iglu:com.heidi/call_initiated/jsonschema/1-0-1',
        data: callInitiated
    }
}
/**
 * Track a Snowplow event for AuthManualLinkCopied.
 * Tracks when an authentication link is manually copied
 */
export function trackAuthManualLinkCopied<T extends {} = any>(authManualLinkCopied: AuthManualLinkCopied & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = authManualLinkCopied;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/auth_manual_link_copied/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow AuthManualLinkCopied entity.
 */
export function createAuthManualLinkCopied(authManualLinkCopied: AuthManualLinkCopied){
    return {
        schema: 'iglu:com.heidi/auth_manual_link_copied/jsonschema/1-0-1',
        data: authManualLinkCopied
    }
}
/**
 * Track a Snowplow event for EvidenceNhsContactSalesSubmitted.
 * Tracks when an NHS user submits contact sales for evidence
 */
export function trackEvidenceNhsContactSalesSubmitted<T extends {} = any>(evidenceNhsContactSalesSubmitted: EvidenceNhsContactSalesSubmitted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = evidenceNhsContactSalesSubmitted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/evidence_nhs_contact_sales_submitted/jsonschema/2-0-2',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow EvidenceNhsContactSalesSubmitted entity.
 */
export function createEvidenceNhsContactSalesSubmitted(evidenceNhsContactSalesSubmitted: EvidenceNhsContactSalesSubmitted){
    return {
        schema: 'iglu:com.heidi/evidence_nhs_contact_sales_submitted/jsonschema/2-0-2',
        data: evidenceNhsContactSalesSubmitted
    }
}
/**
 * Track a Snowplow event for CommsSetupStarted.
 * Tracks when a user starts the Comms setup flow
 */
export function trackCommsSetupStarted<T extends {} = any>(commsSetupStarted: CommsSetupStarted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = commsSetupStarted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/comms_setup_started/jsonschema/1-0-0',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow CommsSetupStarted entity.
 */
export function createCommsSetupStarted(commsSetupStarted: CommsSetupStarted){
    return {
        schema: 'iglu:com.heidi/comms_setup_started/jsonschema/1-0-0',
        data: commsSetupStarted
    }
}
/**
 * Track a Snowplow event for ContextFileDeleted.
 * Tracks context file deleted event
 */
export function trackContextFileDeleted<T extends {} = any>(contextFileDeleted: ContextFileDeleted & ContextsOrTimestamp<T>, trackers?: string[]){
    const { context, timestamp, ...data } = contextFileDeleted;
    const event: SelfDescribingJson = {
        schema: 'iglu:com.heidi/context_file_deleted/jsonschema/1-0-1',
        data
    };

    trackSelfDescribingEvent({
        event,
        context,
        timestamp,
    }, trackers);
}

/**
 * Creates a Snowplow ContextFileDeleted entity.
 */
export function createContextFileDeleted(contextFileDeleted: ContextFileDeleted){
    return {
        schema: 'iglu:com.heidi/context_file_deleted/jsonschema/1-0-1',
        data: contextFileDeleted
    }
}

/**
 * Tracks a EvidenceClicked event specification.
 * ID: 0005d99d-a58a-497e-b432-614a8395db26
 */
export function trackEvidenceClickedSpec(evidenceClicked: EvidenceClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0005d99d-a58a-497e-b432-614a8395db26',
        version: 1,
        name: 'evidence_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceClicked.context)
        ? [...evidenceClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceClicked = {
        ...evidenceClicked,
        context,
    };

    trackEvidenceClicked<Record<string, unknown> | EventSpecification>(modifiedEvidenceClicked, trackers);
}
/**
 * Tracks a EvidenceSourceControlToggled event specification.
 * ID: 00d6fa64-484a-4dc1-a98e-6f73ee2cd460
 */
export function trackEvidenceSourceControlToggledSpec(evidenceSourceControlToggled: EvidenceSourceControlToggled & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '00d6fa64-484a-4dc1-a98e-6f73ee2cd460',
        version: 3,
        name: 'evidence_source_control_toggled',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceSourceControlToggled.context)
        ? [...evidenceSourceControlToggled.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceSourceControlToggled = {
        ...evidenceSourceControlToggled,
        context,
    };

    trackEvidenceSourceControlToggled<Record<string, unknown> | EventSpecification>(modifiedEvidenceSourceControlToggled, trackers);
}
/**
 * Tracks a IntegrationSubmitted event specification.
 * ID: 011e7db4-b838-4947-96d9-efb9e34d51ea
 */
export function trackIntegrationSubmittedSpec(integrationSubmitted: IntegrationSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '011e7db4-b838-4947-96d9-efb9e34d51ea',
        version: 1,
        name: 'integration_submitted',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationSubmitted.context)
        ? [...integrationSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationSubmitted = {
        ...integrationSubmitted,
        context,
    };

    trackIntegrationSubmitted<Record<string, unknown> | EventSpecification>(modifiedIntegrationSubmitted, trackers);
}
/**
 * Tracks a DocumentPrinted event specification.
 * ID: 012c90df-d350-499e-b1ae-3b16d50e7d2c
 */
export function trackDocumentPrintedSpec(documentPrinted: DocumentPrinted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '012c90df-d350-499e-b1ae-3b16d50e7d2c',
        version: 1,
        name: 'document_printed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(documentPrinted.context)
        ? [...documentPrinted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDocumentPrinted = {
        ...documentPrinted,
        context,
    };

    trackDocumentPrinted<Record<string, unknown> | EventSpecification>(modifiedDocumentPrinted, trackers);
}
/**
 * Tracks a MessageFailed event specification.
 * ID: 01827faa-9f50-4585-877c-83d616cb7ed1
 */
export function trackMessageFailedSpec(messageFailed: MessageFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '01827faa-9f50-4585-877c-83d616cb7ed1',
        version: 4,
        name: 'message_failed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(messageFailed.context)
        ? [...messageFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedMessageFailed = {
        ...messageFailed,
        context,
    };

    trackMessageFailed<Record<string, unknown> | EventSpecification>(modifiedMessageFailed, trackers);
}
/**
 * Tracks a IntegrationDataRetrieved event specification.
 * ID: 01f07a34-6a52-4a69-91c2-47970aad0d73
 */
export function trackIntegrationDataRetrievedSpec(integrationDataRetrieved: IntegrationDataRetrieved & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '01f07a34-6a52-4a69-91c2-47970aad0d73',
        version: 1,
        name: 'integration_data_retrieved',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationDataRetrieved.context)
        ? [...integrationDataRetrieved.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationDataRetrieved = {
        ...integrationDataRetrieved,
        context,
    };

    trackIntegrationDataRetrieved<Record<string, unknown> | EventSpecification>(modifiedIntegrationDataRetrieved, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a ObjectiveExecutionCompleted event specification.
 * ID: 021f7c9e-e9cd-474d-8995-7122ffe92681
 */
export function trackObjectiveExecutionCompletedSpec(objectiveExecutionCompleted: ObjectiveExecutionCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '021f7c9e-e9cd-474d-8995-7122ffe92681',
        version: 0,
        name: 'objective_execution_completed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(objectiveExecutionCompleted.context)
        ? [...objectiveExecutionCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedObjectiveExecutionCompleted = {
        ...objectiveExecutionCompleted,
        context,
    };

    trackObjectiveExecutionCompleted<Record<string, unknown> | EventSpecification>(modifiedObjectiveExecutionCompleted, trackers);
}
/**
 * Tracks a DesktopUpdateFailed event specification.
 * ID: 025958bd-91b8-4a21-a60f-21a951e5d8a3
 */
export function trackDesktopUpdateFailedSpec(desktopUpdateFailed: DesktopUpdateFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '025958bd-91b8-4a21-a60f-21a951e5d8a3',
        version: 0,
        name: 'desktop_update_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(desktopUpdateFailed.context)
        ? [...desktopUpdateFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDesktopUpdateFailed = {
        ...desktopUpdateFailed,
        context,
    };

    trackDesktopUpdateFailed<Record<string, unknown> | EventSpecification>(modifiedDesktopUpdateFailed, trackers);
}
/**
 * Tracks a ShareButtonClicked event specification.
 * ID: 02942c25-1221-4adc-a190-8121eac03053
 */
export function trackShareButtonClickedSpec(shareButtonClicked: ShareButtonClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '02942c25-1221-4adc-a190-8121eac03053',
        version: 1,
        name: 'share_button_clicked',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareButtonClicked.context)
        ? [...shareButtonClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareButtonClicked = {
        ...shareButtonClicked,
        context,
    };

    trackShareButtonClicked<Record<string, unknown> | EventSpecification>(modifiedShareButtonClicked, trackers);
}
/**
 * Tracks a ForceUpdateDeferred event specification.
 * ID: 02c7e0f7-ca94-49d9-93a9-a52fe371440a
 */
export function trackForceUpdateDeferredSpec(forceUpdateDeferred: ForceUpdateDeferred & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '02c7e0f7-ca94-49d9-93a9-a52fe371440a',
        version: 1,
        name: 'force_update_deferred',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(forceUpdateDeferred.context)
        ? [...forceUpdateDeferred.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedForceUpdateDeferred = {
        ...forceUpdateDeferred,
        context,
    };

    trackForceUpdateDeferred<Record<string, unknown> | EventSpecification>(modifiedForceUpdateDeferred, trackers);
}
/**
 * Tracks a ProcessLifecycle event specification.
 * ID: 03011158-1e90-47f4-a844-2f3b39206094
 */
export function trackProcessLifecycleSpec(processLifecycle: ProcessLifecycle & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '03011158-1e90-47f4-a844-2f3b39206094',
        version: 0,
        name: 'process_lifecycle',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(processLifecycle.context)
        ? [...processLifecycle.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedProcessLifecycle = {
        ...processLifecycle,
        context,
    };

    trackProcessLifecycle<Record<string, unknown> | EventSpecification>(modifiedProcessLifecycle, trackers);
}
/**
 * Tracks a OnboardingStep event specification.
 * ID: 0309fea2-a0c8-4853-85bb-07d80eff543c
 */
export function trackOnboardingStepSpec(onboardingStep: OnboardingStep & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0309fea2-a0c8-4853-85bb-07d80eff543c',
        version: 0,
        name: 'onboarding_step',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(onboardingStep.context)
        ? [...onboardingStep.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOnboardingStep = {
        ...onboardingStep,
        context,
    };

    trackOnboardingStep<Record<string, unknown> | EventSpecification>(modifiedOnboardingStep, trackers);
}
/**
 * Tracks a TranscriptionStopped event specification.
 * ID: 03a5661d-693d-469b-a2b0-9349e07b5173
 */
export function trackTranscriptionStoppedSpec(transcriptionStopped: TranscriptionStopped & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '03a5661d-693d-469b-a2b0-9349e07b5173',
        version: 2,
        name: 'transcription_stopped',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(transcriptionStopped.context)
        ? [...transcriptionStopped.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTranscriptionStopped = {
        ...transcriptionStopped,
        context,
    };

    trackTranscriptionStopped<Record<string, unknown> | EventSpecification>(modifiedTranscriptionStopped, trackers);
}
/**
 * Tracks a ShareLinkGenerated event specification.
 * ID: 03b95e63-ed12-448c-90b8-3d9d394506a0
 */
export function trackShareLinkGeneratedSpec(shareLinkGenerated: ShareLinkGenerated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '03b95e63-ed12-448c-90b8-3d9d394506a0',
        version: 1,
        name: 'share_link_generated',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareLinkGenerated.context)
        ? [...shareLinkGenerated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareLinkGenerated = {
        ...shareLinkGenerated,
        context,
    };

    trackShareLinkGenerated<Record<string, unknown> | EventSpecification>(modifiedShareLinkGenerated, trackers);
}
/**
 * Tracks a WorkUpgradeActioned event specification.
 * ID: 049e3dda-9700-5a79-a6e8-2d16ec05e570
 */
export function trackWorkUpgradeActionedSpec(workUpgradeActioned: WorkUpgradeActioned & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '049e3dda-9700-5a79-a6e8-2d16ec05e570',
        version: 0,
        name: 'work_upgrade_actioned',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workUpgradeActioned.context)
        ? [...workUpgradeActioned.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkUpgradeActioned = {
        ...workUpgradeActioned,
        context,
    };

    trackWorkUpgradeActioned<Record<string, unknown> | EventSpecification>(modifiedWorkUpgradeActioned, trackers);
}
/**
 * Tracks a NoteSelected event specification.
 * ID: 04e48e8f-e6d6-4a63-b7bd-ab21c2f06771
 */
export function trackNoteSelectedSpec(noteSelected: NoteSelected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '04e48e8f-e6d6-4a63-b7bd-ab21c2f06771',
        version: 4,
        name: 'note_selected',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteSelected.context)
        ? [...noteSelected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteSelected = {
        ...noteSelected,
        context,
    };

    trackNoteSelected<Record<string, unknown> | EventSpecification>(modifiedNoteSelected, trackers);
}
/**
 * Tracks a PatientSummaryEmailSent event specification.
 * ID: 05091ccd-9782-43f0-80a7-dc9071a1936e
 */
export function trackPatientSummaryEmailSentSpec(patientSummaryEmailSent: PatientSummaryEmailSent & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '05091ccd-9782-43f0-80a7-dc9071a1936e',
        version: 4,
        name: 'patient_summary_email_sent',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(patientSummaryEmailSent.context)
        ? [...patientSummaryEmailSent.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientSummaryEmailSent = {
        ...patientSummaryEmailSent,
        context,
    };

    trackPatientSummaryEmailSent<Record<string, unknown> | EventSpecification>(modifiedPatientSummaryEmailSent, trackers);
}
/**
 * Tracks a EvidenceReferenceScrollClicked event specification.
 * ID: 05f444d2-a34a-48d3-87c5-0ef52a003e70
 */
export function trackEvidenceReferenceScrollClickedSpec(evidenceReferenceScrollClicked: EvidenceReferenceScrollClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '05f444d2-a34a-48d3-87c5-0ef52a003e70',
        version: 5,
        name: 'evidence_reference_scroll_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceReferenceScrollClicked.context)
        ? [...evidenceReferenceScrollClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceReferenceScrollClicked = {
        ...evidenceReferenceScrollClicked,
        context,
    };

    trackEvidenceReferenceScrollClicked<Record<string, unknown> | EventSpecification>(modifiedEvidenceReferenceScrollClicked, trackers);
}
/**
 * Tracks a SnippetsUploaded event specification.
 * ID: 06a0d34a-20b1-421d-9029-e1546f49549b
 */
export function trackSnippetsUploadedSpec(snippetsUploaded: SnippetsUploaded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '06a0d34a-20b1-421d-9029-e1546f49549b',
        version: 1,
        name: 'snippets_uploaded',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(snippetsUploaded.context)
        ? [...snippetsUploaded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSnippetsUploaded = {
        ...snippetsUploaded,
        context,
    };

    trackSnippetsUploaded<Record<string, unknown> | EventSpecification>(modifiedSnippetsUploaded, trackers);
}
/**
 * Tracks a ContextPreviewed event specification.
 * ID: 06f90e9a-5c7d-444d-ba62-3f917280e3ad
 */
export function trackContextPreviewedSpec(contextPreviewed: ContextPreviewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '06f90e9a-5c7d-444d-ba62-3f917280e3ad',
        version: 1,
        name: 'context_previewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(contextPreviewed.context)
        ? [...contextPreviewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedContextPreviewed = {
        ...contextPreviewed,
        context,
    };

    trackContextPreviewed<Record<string, unknown> | EventSpecification>(modifiedContextPreviewed, trackers);
}
/**
 * Tracks a ExportUpdated event specification.
 * ID: 081cd71b-b759-4a40-adc5-596eae5c1f58
 */
export function trackExportUpdatedSpec(exportUpdated: ExportUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '081cd71b-b759-4a40-adc5-596eae5c1f58',
        version: 1,
        name: 'export_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(exportUpdated.context)
        ? [...exportUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedExportUpdated = {
        ...exportUpdated,
        context,
    };

    trackExportUpdated<Record<string, unknown> | EventSpecification>(modifiedExportUpdated, trackers);
}
/**
 * Tracks a TranslatorBulkUploadFailed event specification.
 * ID: 0834e080-5b54-4008-8c12-231f9c42b7d7
 */
export function trackTranslatorBulkUploadFailedSpec(translatorBulkUploadFailed: TranslatorBulkUploadFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0834e080-5b54-4008-8c12-231f9c42b7d7',
        version: 1,
        name: 'translator_bulk_upload_failed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(translatorBulkUploadFailed.context)
        ? [...translatorBulkUploadFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTranslatorBulkUploadFailed = {
        ...translatorBulkUploadFailed,
        context,
    };

    trackTranslatorBulkUploadFailed<Record<string, unknown> | EventSpecification>(modifiedTranslatorBulkUploadFailed, trackers);
}
/**
 * Tracks a DocumentShared event specification.
 * ID: 0854a3e1-d6b5-4b25-9816-e00058fd815d
 */
export function trackDocumentSharedSpec(documentShared: DocumentShared & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0854a3e1-d6b5-4b25-9816-e00058fd815d',
        version: 1,
        name: 'document_shared',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(documentShared.context)
        ? [...documentShared.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDocumentShared = {
        ...documentShared,
        context,
    };

    trackDocumentShared<Record<string, unknown> | EventSpecification>(modifiedDocumentShared, trackers);
}
/**
 * Tracks a EvidenceSummarySuggestionShown event specification.
 * ID: 0a3473c6-90ca-49ec-ac12-55af627b3ab4
 */
export function trackEvidenceSummarySuggestionShownSpec(evidenceSummarySuggestionShown: EvidenceSummarySuggestionShown & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0a3473c6-90ca-49ec-ac12-55af627b3ab4',
        version: 0,
        name: 'evidence_summary_suggestion_shown',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceSummarySuggestionShown.context)
        ? [...evidenceSummarySuggestionShown.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceSummarySuggestionShown = {
        ...evidenceSummarySuggestionShown,
        context,
    };

    trackEvidenceSummarySuggestionShown<Record<string, unknown> | EventSpecification>(modifiedEvidenceSummarySuggestionShown, trackers);
}
/**
 * Tracks a TranslatorSessionCreated event specification.
 * ID: 0a78189b-fb1c-4074-9324-f9d2880b5d5d
 */
export function trackTranslatorSessionCreatedSpec(translatorSessionCreated: TranslatorSessionCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0a78189b-fb1c-4074-9324-f9d2880b5d5d',
        version: 0,
        name: 'translator_session_created',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(translatorSessionCreated.context)
        ? [...translatorSessionCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTranslatorSessionCreated = {
        ...translatorSessionCreated,
        context,
    };

    trackTranslatorSessionCreated<Record<string, unknown> | EventSpecification>(modifiedTranslatorSessionCreated, trackers);
}
/**
 * Tracks a ReplacementsUploaded event specification.
 * ID: 0af26184-c7f2-41d8-8189-867919d32a88
 */
export function trackReplacementsUploadedSpec(replacementsUploaded: ReplacementsUploaded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0af26184-c7f2-41d8-8189-867919d32a88',
        version: 1,
        name: 'replacements_uploaded',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(replacementsUploaded.context)
        ? [...replacementsUploaded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedReplacementsUploaded = {
        ...replacementsUploaded,
        context,
    };

    trackReplacementsUploaded<Record<string, unknown> | EventSpecification>(modifiedReplacementsUploaded, trackers);
}
/**
 * Tracks a AudioWarningAccepted event specification.
 * ID: 0b049e5a-9617-44d2-858b-1f60729b6026
 */
export function trackAudioWarningAcceptedSpec(audioWarningAccepted: AudioWarningAccepted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0b049e5a-9617-44d2-858b-1f60729b6026',
        version: 0,
        name: 'audio_warning_accepted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(audioWarningAccepted.context)
        ? [...audioWarningAccepted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAudioWarningAccepted = {
        ...audioWarningAccepted,
        context,
    };

    trackAudioWarningAccepted<Record<string, unknown> | EventSpecification>(modifiedAudioWarningAccepted, trackers);
}
/**
 * Tracks a LetterRecipientSearched event specification.
 * ID: 0b4ba93c-f08f-4146-aedf-e4cef445f82b
 */
export function trackLetterRecipientSearchedSpec(letterRecipientSearched: LetterRecipientSearched & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0b4ba93c-f08f-4146-aedf-e4cef445f82b',
        version: 0,
        name: 'letter_recipient_searched',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(letterRecipientSearched.context)
        ? [...letterRecipientSearched.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLetterRecipientSearched = {
        ...letterRecipientSearched,
        context,
    };

    trackLetterRecipientSearched<Record<string, unknown> | EventSpecification>(modifiedLetterRecipientSearched, trackers);
}
/**
 * Tracks a EvidenceSuggestionClicked event specification.
 * ID: 0c041af5-b440-40cb-a50a-2248199921a1
 */
export function trackEvidenceSuggestionClickedSpec(evidenceSuggestionClicked: EvidenceSuggestionClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0c041af5-b440-40cb-a50a-2248199921a1',
        version: 1,
        name: 'evidence_suggestion_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceSuggestionClicked.context)
        ? [...evidenceSuggestionClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceSuggestionClicked = {
        ...evidenceSuggestionClicked,
        context,
    };

    trackEvidenceSuggestionClicked<Record<string, unknown> | EventSpecification>(modifiedEvidenceSuggestionClicked, trackers);
}
/**
 * Tracks a SessionUpdated event specification.
 * ID: 0c67bf59-d683-4d7d-a56b-087a7bea7348
 */
export function trackSessionUpdatedSpec(sessionUpdated: SessionUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0c67bf59-d683-4d7d-a56b-087a7bea7348',
        version: 5,
        name: 'session_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionUpdated.context)
        ? [...sessionUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionUpdated = {
        ...sessionUpdated,
        context,
    };

    trackSessionUpdated<Record<string, unknown> | EventSpecification>(modifiedSessionUpdated, trackers);
}
/**
 * Tracks a PatientFieldEdited event specification.
 * ID: 0e7ffb19-0ef9-4ed2-8cb9-cee4c8bf8366
 */
export function trackPatientFieldEditedSpec(patientFieldEdited: PatientFieldEdited & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0e7ffb19-0ef9-4ed2-8cb9-cee4c8bf8366',
        version: 0,
        name: 'patient_field_edited',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(patientFieldEdited.context)
        ? [...patientFieldEdited.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientFieldEdited = {
        ...patientFieldEdited,
        context,
    };

    trackPatientFieldEdited<Record<string, unknown> | EventSpecification>(modifiedPatientFieldEdited, trackers);
}
/**
 * Tracks a NoteCopied event specification.
 * ID: 0f09ba26-dca8-4a14-896b-d3f48b9aef13
 */
export function trackNoteCopiedSpec(noteCopied: NoteCopied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0f09ba26-dca8-4a14-896b-d3f48b9aef13',
        version: 5,
        name: 'note_copied',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteCopied.context)
        ? [...noteCopied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteCopied = {
        ...noteCopied,
        context,
    };

    trackNoteCopied<Record<string, unknown> | EventSpecification>(modifiedNoteCopied, trackers);
}
/**
 * Tracks a FeedbackUpdated event specification.
 * ID: 0f0e8d41-6387-4e4d-bb62-932a0b976205
 */
export function trackFeedbackUpdatedSpec(feedbackUpdated: FeedbackUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0f0e8d41-6387-4e4d-bb62-932a0b976205',
        version: 1,
        name: 'feedback_updated',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(feedbackUpdated.context)
        ? [...feedbackUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFeedbackUpdated = {
        ...feedbackUpdated,
        context,
    };

    trackFeedbackUpdated<Record<string, unknown> | EventSpecification>(modifiedFeedbackUpdated, trackers);
}
/**
 * Tracks a SubscriptionCreated event specification.
 * ID: 0f227d91-12d0-4d8c-84f7-695a336975ce
 */
export function trackSubscriptionCreatedSpec(subscriptionCreated: SubscriptionCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0f227d91-12d0-4d8c-84f7-695a336975ce',
        version: 2,
        name: 'subscription_created',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(subscriptionCreated.context)
        ? [...subscriptionCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSubscriptionCreated = {
        ...subscriptionCreated,
        context,
    };

    trackSubscriptionCreated<Record<string, unknown> | EventSpecification>(modifiedSubscriptionCreated, trackers);
}
/**
 * Tracks a AccountUpdated event specification.
 * ID: 0f28d28d-8526-41be-8c3b-ed7bceaee317
 */
export function trackAccountUpdatedSpec(accountUpdated: AccountUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0f28d28d-8526-41be-8c3b-ed7bceaee317',
        version: 1,
        name: 'account_updated',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(accountUpdated.context)
        ? [...accountUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAccountUpdated = {
        ...accountUpdated,
        context,
    };

    trackAccountUpdated<Record<string, unknown> | EventSpecification>(modifiedAccountUpdated, trackers);
}
/**
 * Tracks a TemplateQualityGate event specification.
 * ID: 0f2c5ada-d6cd-4504-b151-987458802bbf
 */
export function trackTemplateQualityGateSpec(templateQualityGate: TemplateQualityGate & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0f2c5ada-d6cd-4504-b151-987458802bbf',
        version: 0,
        name: 'template_quality_gate',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateQualityGate.context)
        ? [...templateQualityGate.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateQualityGate = {
        ...templateQualityGate,
        context,
    };

    trackTemplateQualityGate<Record<string, unknown> | EventSpecification>(modifiedTemplateQualityGate, trackers);
}
/**
 * Tracks a LetterRecipientRemoved event specification.
 * ID: 0f66ed22-e667-4f87-b7b6-0dcd482c0f59
 */
export function trackLetterRecipientRemovedSpec(letterRecipientRemoved: LetterRecipientRemoved & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0f66ed22-e667-4f87-b7b6-0dcd482c0f59',
        version: 0,
        name: 'letter_recipient_removed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(letterRecipientRemoved.context)
        ? [...letterRecipientRemoved.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLetterRecipientRemoved = {
        ...letterRecipientRemoved,
        context,
    };

    trackLetterRecipientRemoved<Record<string, unknown> | EventSpecification>(modifiedLetterRecipientRemoved, trackers);
}
/**
 * Tracks a TeamMemberAccepted event specification.
 * ID: 0fb0e458-583a-4579-b325-df1449e198e4
 */
export function trackTeamMemberAcceptedSpec(teamMemberAccepted: TeamMemberAccepted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '0fb0e458-583a-4579-b325-df1449e198e4',
        version: 1,
        name: 'team_member_accepted',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(teamMemberAccepted.context)
        ? [...teamMemberAccepted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTeamMemberAccepted = {
        ...teamMemberAccepted,
        context,
    };

    trackTeamMemberAccepted<Record<string, unknown> | EventSpecification>(modifiedTeamMemberAccepted, trackers);
}
/**
 * Tracks a CommunityAccepted event specification.
 * ID: 102510c1-d153-4e46-bbe7-20c69af21837
 */
export function trackCommunityAcceptedSpec(communityAccepted: CommunityAccepted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '102510c1-d153-4e46-bbe7-20c69af21837',
        version: 1,
        name: 'community_accepted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(communityAccepted.context)
        ? [...communityAccepted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCommunityAccepted = {
        ...communityAccepted,
        context,
    };

    trackCommunityAccepted<Record<string, unknown> | EventSpecification>(modifiedCommunityAccepted, trackers);
}
/**
 * Tracks a PrechartViewed event specification.
 * ID: 10961f7c-5410-44e7-9b1e-3d4e7a64ee1c
 */
export function trackPrechartViewedSpec(prechartViewed: PrechartViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '10961f7c-5410-44e7-9b1e-3d4e7a64ee1c',
        version: 0,
        name: 'prechart_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(prechartViewed.context)
        ? [...prechartViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPrechartViewed = {
        ...prechartViewed,
        context,
    };

    trackPrechartViewed<Record<string, unknown> | EventSpecification>(modifiedPrechartViewed, trackers);
}
/**
 * Tracks a UserStarted event specification.
 * ID: 121b001d-b414-4b9c-bd1e-422b447d109a
 */
export function trackUserStartedSpec(userStarted: UserStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '121b001d-b414-4b9c-bd1e-422b447d109a',
        version: 1,
        name: 'user_started',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(userStarted.context)
        ? [...userStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUserStarted = {
        ...userStarted,
        context,
    };

    trackUserStarted<Record<string, unknown> | EventSpecification>(modifiedUserStarted, trackers);
}
/**
 * Tracks a AppearanceModeChanged event specification.
 * ID: 12967245-8cc5-4cf7-b670-072ef3eadebc
 */
export function trackAppearanceModeChangedSpec(appearanceModeChanged: AppearanceModeChanged & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '12967245-8cc5-4cf7-b670-072ef3eadebc',
        version: 0,
        name: 'appearance_mode_changed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(appearanceModeChanged.context)
        ? [...appearanceModeChanged.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAppearanceModeChanged = {
        ...appearanceModeChanged,
        context,
    };

    trackAppearanceModeChanged<Record<string, unknown> | EventSpecification>(modifiedAppearanceModeChanged, trackers);
}
/**
 * Tracks a SessionTabViewed event specification.
 * ID: 12e77a57-47aa-418f-8314-3581f57911d3
 */
export function trackSessionTabViewedSpec(sessionTabViewed: SessionTabViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '12e77a57-47aa-418f-8314-3581f57911d3',
        version: 0,
        name: 'session_tab_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionTabViewed.context)
        ? [...sessionTabViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionTabViewed = {
        ...sessionTabViewed,
        context,
    };

    trackSessionTabViewed<Record<string, unknown> | EventSpecification>(modifiedSessionTabViewed, trackers);
}
/**
 * Tracks a TranslatorMessageCreated event specification.
 * ID: 139a0629-f232-41c6-9c1f-4c3c73399291
 */
export function trackTranslatorMessageCreatedSpec(translatorMessageCreated: TranslatorMessageCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '139a0629-f232-41c6-9c1f-4c3c73399291',
        version: 1,
        name: 'translator_message_created',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(translatorMessageCreated.context)
        ? [...translatorMessageCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTranslatorMessageCreated = {
        ...translatorMessageCreated,
        context,
    };

    trackTranslatorMessageCreated<Record<string, unknown> | EventSpecification>(modifiedTranslatorMessageCreated, trackers);
}
/**
 * Tracks a EvidenceOnboardingDismissed event specification.
 * ID: 13af5204-7ef6-40a2-afdd-c14d198f2f92
 */
export function trackEvidenceOnboardingDismissedSpec(evidenceOnboardingDismissed: EvidenceOnboardingDismissed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '13af5204-7ef6-40a2-afdd-c14d198f2f92',
        version: 3,
        name: 'evidence_onboarding_dismissed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceOnboardingDismissed.context)
        ? [...evidenceOnboardingDismissed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceOnboardingDismissed = {
        ...evidenceOnboardingDismissed,
        context,
    };

    trackEvidenceOnboardingDismissed<Record<string, unknown> | EventSpecification>(modifiedEvidenceOnboardingDismissed, trackers);
}
/**
 * Tracks a InboxFilterApplied event specification.
 * ID: 13bc80e7-8862-4d00-9df2-8ebdec70e1f7
 */
export function trackInboxFilterAppliedSpec(inboxFilterApplied: InboxFilterApplied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '13bc80e7-8862-4d00-9df2-8ebdec70e1f7',
        version: 0,
        name: 'inbox_filter_applied',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(inboxFilterApplied.context)
        ? [...inboxFilterApplied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedInboxFilterApplied = {
        ...inboxFilterApplied,
        context,
    };

    trackInboxFilterApplied<Record<string, unknown> | EventSpecification>(modifiedInboxFilterApplied, trackers);
}
/**
 * Tracks a EvidenceOnboardingSkipped event specification.
 * ID: 13bfc519-dd3b-45f6-a3ae-1b7dea415134
 */
export function trackEvidenceOnboardingSkippedSpec(evidenceOnboardingSkipped: EvidenceOnboardingSkipped & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '13bfc519-dd3b-45f6-a3ae-1b7dea415134',
        version: 3,
        name: 'evidence_onboarding_skipped',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceOnboardingSkipped.context)
        ? [...evidenceOnboardingSkipped.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceOnboardingSkipped = {
        ...evidenceOnboardingSkipped,
        context,
    };

    trackEvidenceOnboardingSkipped<Record<string, unknown> | EventSpecification>(modifiedEvidenceOnboardingSkipped, trackers);
}
/**
 * Tracks a OfflineSyncDeferred event specification.
 * ID: 13e9c9f2-4f6e-432e-a567-c34e69ee52f5
 */
export function trackOfflineSyncDeferredSpec(offlineSyncDeferred: OfflineSyncDeferred & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '13e9c9f2-4f6e-432e-a567-c34e69ee52f5',
        version: 0,
        name: 'offline_sync_deferred',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(offlineSyncDeferred.context)
        ? [...offlineSyncDeferred.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOfflineSyncDeferred = {
        ...offlineSyncDeferred,
        context,
    };

    trackOfflineSyncDeferred<Record<string, unknown> | EventSpecification>(modifiedOfflineSyncDeferred, trackers);
}
/**
 * Tracks a ObjectiveHandoffTriggered event specification.
 * ID: 1483d110-e31e-49b1-9c25-d749b19790c0
 */
export function trackObjectiveHandoffTriggeredSpec(objectiveHandoffTriggered: ObjectiveHandoffTriggered & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1483d110-e31e-49b1-9c25-d749b19790c0',
        version: 0,
        name: 'objective_handoff_triggered',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(objectiveHandoffTriggered.context)
        ? [...objectiveHandoffTriggered.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedObjectiveHandoffTriggered = {
        ...objectiveHandoffTriggered,
        context,
    };

    trackObjectiveHandoffTriggered<Record<string, unknown> | EventSpecification>(modifiedObjectiveHandoffTriggered, trackers);
}
/**
 * Tracks a PatientRecapPopoverOpened event specification.
 * ID: 15771d8f-3d06-43e3-93fe-c98641de2d74
 */
export function trackPatientRecapPopoverOpenedSpec(patientRecapPopoverOpened: PatientRecapPopoverOpened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '15771d8f-3d06-43e3-93fe-c98641de2d74',
        version: 0,
        name: 'patient_recap_popover_opened',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(patientRecapPopoverOpened.context)
        ? [...patientRecapPopoverOpened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientRecapPopoverOpened = {
        ...patientRecapPopoverOpened,
        context,
    };

    trackPatientRecapPopoverOpened<Record<string, unknown> | EventSpecification>(modifiedPatientRecapPopoverOpened, trackers);
}
/**
 * Tracks a DocumentDeleted event specification.
 * ID: 157b38d6-5c1b-49da-9ebb-8b30f6ee4ae6
 */
export function trackDocumentDeletedSpec(documentDeleted: DocumentDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '157b38d6-5c1b-49da-9ebb-8b30f6ee4ae6',
        version: 3,
        name: 'document_deleted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(documentDeleted.context)
        ? [...documentDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDocumentDeleted = {
        ...documentDeleted,
        context,
    };

    trackDocumentDeleted<Record<string, unknown> | EventSpecification>(modifiedDocumentDeleted, trackers);
}
/**
 * Tracks a ScheduleUploadStarted event specification.
 * ID: 15a08b5d-5955-4b2e-8c0b-7e27dd0400f7
 */
export function trackScheduleUploadStartedSpec(scheduleUploadStarted: ScheduleUploadStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '15a08b5d-5955-4b2e-8c0b-7e27dd0400f7',
        version: 0,
        name: 'schedule_upload_started',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(scheduleUploadStarted.context)
        ? [...scheduleUploadStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScheduleUploadStarted = {
        ...scheduleUploadStarted,
        context,
    };

    trackScheduleUploadStarted<Record<string, unknown> | EventSpecification>(modifiedScheduleUploadStarted, trackers);
}
/**
 * Tracks a TemplateSelected event specification.
 * ID: 1826f6b5-8020-4a48-987f-e3d575b5e1af
 */
export function trackTemplateSelectedSpec(templateSelected: TemplateSelected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1826f6b5-8020-4a48-987f-e3d575b5e1af',
        version: 2,
        name: 'template_selected',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateSelected.context)
        ? [...templateSelected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateSelected = {
        ...templateSelected,
        context,
    };

    trackTemplateSelected<Record<string, unknown> | EventSpecification>(modifiedTemplateSelected, trackers);
}
/**
 * Tracks a OnboardingCompleted event specification.
 * ID: 19c3e2fb-9b9a-454c-bb86-037dfc21d7e0
 */
export function trackOnboardingCompletedSpec(onboardingCompleted: OnboardingCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '19c3e2fb-9b9a-454c-bb86-037dfc21d7e0',
        version: 3,
        name: 'onboarding_completed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(onboardingCompleted.context)
        ? [...onboardingCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOnboardingCompleted = {
        ...onboardingCompleted,
        context,
    };

    trackOnboardingCompleted<Record<string, unknown> | EventSpecification>(modifiedOnboardingCompleted, trackers);
}
/**
 * Tracks a OnboardingStarted event specification.
 * ID: 19ecba68-47aa-44b9-a103-ea8f18e11bd8
 */
export function trackOnboardingStartedSpec(onboardingStarted: OnboardingStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '19ecba68-47aa-44b9-a103-ea8f18e11bd8',
        version: 5,
        name: 'onboarding_started',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(onboardingStarted.context)
        ? [...onboardingStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOnboardingStarted = {
        ...onboardingStarted,
        context,
    };

    trackOnboardingStarted<Record<string, unknown> | EventSpecification>(modifiedOnboardingStarted, trackers);
}
/**
 * Tracks a FollowUpQuestionClicked event specification.
 * ID: 1a015511-d31d-438d-9289-9c4dd307095b
 */
export function trackFollowUpQuestionClickedSpec(followUpQuestionClicked: FollowUpQuestionClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1a015511-d31d-438d-9289-9c4dd307095b',
        version: 2,
        name: 'follow_up_question_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(followUpQuestionClicked.context)
        ? [...followUpQuestionClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFollowUpQuestionClicked = {
        ...followUpQuestionClicked,
        context,
    };

    trackFollowUpQuestionClicked<Record<string, unknown> | EventSpecification>(modifiedFollowUpQuestionClicked, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a ObjectiveExecutionStarted event specification.
 * ID: 1bbce6c7-5c8a-4b06-8734-7456e5de41f2
 */
export function trackObjectiveExecutionStartedSpec(objectiveExecutionStarted: ObjectiveExecutionStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1bbce6c7-5c8a-4b06-8734-7456e5de41f2',
        version: 0,
        name: 'objective_execution_started',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(objectiveExecutionStarted.context)
        ? [...objectiveExecutionStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedObjectiveExecutionStarted = {
        ...objectiveExecutionStarted,
        context,
    };

    trackObjectiveExecutionStarted<Record<string, unknown> | EventSpecification>(modifiedObjectiveExecutionStarted, trackers);
}
/**
 * Tracks a ChronicleDeviceDisconnected event specification.
 * ID: 1d663481-1926-4e49-bf39-fbd7468ac5fd
 */
export function trackChronicleDeviceDisconnectedSpec(chronicleDeviceDisconnected: ChronicleDeviceDisconnected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1d663481-1926-4e49-bf39-fbd7468ac5fd',
        version: 2,
        name: 'chronicle_device_disconnected',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(chronicleDeviceDisconnected.context)
        ? [...chronicleDeviceDisconnected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChronicleDeviceDisconnected = {
        ...chronicleDeviceDisconnected,
        context,
    };

    trackChronicleDeviceDisconnected<Record<string, unknown> | EventSpecification>(modifiedChronicleDeviceDisconnected, trackers);
}
/**
 * Tracks a AudioFailed event specification.
 * ID: 1d83c27f-4894-4bca-96eb-838c0981eb72
 */
export function trackAudioFailedSpec(audioFailed: AudioFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1d83c27f-4894-4bca-96eb-838c0981eb72',
        version: 1,
        name: 'audio_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(audioFailed.context)
        ? [...audioFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAudioFailed = {
        ...audioFailed,
        context,
    };

    trackAudioFailed<Record<string, unknown> | EventSpecification>(modifiedAudioFailed, trackers);
}
/**
 * Tracks a OfflineRecordingDeleted event specification.
 * ID: 1dba41ee-2fe9-4806-b15f-1029cac8bea5
 */
export function trackOfflineRecordingDeletedSpec(offlineRecordingDeleted: OfflineRecordingDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1dba41ee-2fe9-4806-b15f-1029cac8bea5',
        version: 0,
        name: 'offline_recording_deleted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(offlineRecordingDeleted.context)
        ? [...offlineRecordingDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOfflineRecordingDeleted = {
        ...offlineRecordingDeleted,
        context,
    };

    trackOfflineRecordingDeleted<Record<string, unknown> | EventSpecification>(modifiedOfflineRecordingDeleted, trackers);
}
/**
 * Tracks a EvidenceCopied event specification.
 * ID: 1dbe9ea2-ed13-4585-8c0a-64afdc8d5471
 */
export function trackEvidenceCopiedSpec(evidenceCopied: EvidenceCopied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1dbe9ea2-ed13-4585-8c0a-64afdc8d5471',
        version: 0,
        name: 'evidence_copied',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceCopied.context)
        ? [...evidenceCopied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceCopied = {
        ...evidenceCopied,
        context,
    };

    trackEvidenceCopied<Record<string, unknown> | EventSpecification>(modifiedEvidenceCopied, trackers);
}
/**
 * Tracks a EhrLaunchCreated event specification.
 * ID: 1e21e89b-dfa4-4a38-87d4-593ba0ca02a6
 */
export function trackEhrLaunchCreatedSpec(ehrLaunchCreated: EhrLaunchCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1e21e89b-dfa4-4a38-87d4-593ba0ca02a6',
        version: 0,
        name: 'ehr_launch_created',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(ehrLaunchCreated.context)
        ? [...ehrLaunchCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEhrLaunchCreated = {
        ...ehrLaunchCreated,
        context,
    };

    trackEhrLaunchCreated<Record<string, unknown> | EventSpecification>(modifiedEhrLaunchCreated, trackers);
}
/**
 * Tracks a EhrSectionSelected event specification.
 * ID: 1e22765f-e214-44f2-a906-b1471258ea26
 */
export function trackEhrSectionSelectedSpec(ehrSectionSelected: EhrSectionSelected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1e22765f-e214-44f2-a906-b1471258ea26',
        version: 1,
        name: 'ehr_section_selected',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(ehrSectionSelected.context)
        ? [...ehrSectionSelected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEhrSectionSelected = {
        ...ehrSectionSelected,
        context,
    };

    trackEhrSectionSelected<Record<string, unknown> | EventSpecification>(modifiedEhrSectionSelected, trackers);
}
/**
 * Tracks a SessionDeleted event specification.
 * ID: 1e2beae2-1b97-476a-80cd-7d27dac55e38
 */
export function trackSessionDeletedSpec(sessionDeleted: SessionDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1e2beae2-1b97-476a-80cd-7d27dac55e38',
        version: 4,
        name: 'session_deleted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionDeleted.context)
        ? [...sessionDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionDeleted = {
        ...sessionDeleted,
        context,
    };

    trackSessionDeleted<Record<string, unknown> | EventSpecification>(modifiedSessionDeleted, trackers);
}
/**
 * Tracks a FindReplaceScopeUpdated event specification.
 * ID: 1e567470-16dd-4a06-8374-2161473df5cf
 */
export function trackFindReplaceScopeUpdatedSpec(findReplaceScopeUpdated: FindReplaceScopeUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1e567470-16dd-4a06-8374-2161473df5cf',
        version: 3,
        name: 'find_replace_scope_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(findReplaceScopeUpdated.context)
        ? [...findReplaceScopeUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFindReplaceScopeUpdated = {
        ...findReplaceScopeUpdated,
        context,
    };

    trackFindReplaceScopeUpdated<Record<string, unknown> | EventSpecification>(modifiedFindReplaceScopeUpdated, trackers);
}
/**
 * Tracks a EvidenceShortcutSearchUsed event specification.
 * ID: 1ef83150-56a9-4a8b-9df0-20a4db1ea12c
 */
export function trackEvidenceShortcutSearchUsedSpec(evidenceShortcutSearchUsed: EvidenceShortcutSearchUsed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1ef83150-56a9-4a8b-9df0-20a4db1ea12c',
        version: 0,
        name: 'evidence_shortcut_search_used',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceShortcutSearchUsed.context)
        ? [...evidenceShortcutSearchUsed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceShortcutSearchUsed = {
        ...evidenceShortcutSearchUsed,
        context,
    };

    trackEvidenceShortcutSearchUsed<Record<string, unknown> | EventSpecification>(modifiedEvidenceShortcutSearchUsed, trackers);
}
/**
 * Tracks a RecordingConsentDeclined event specification.
 * ID: 1f18eb5b-c9f0-458b-8eff-c15cd028d8b7
 */
export function trackRecordingConsentDeclinedSpec(recordingConsentDeclined: RecordingConsentDeclined & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1f18eb5b-c9f0-458b-8eff-c15cd028d8b7',
        version: 0,
        name: 'recording_consent_declined',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(recordingConsentDeclined.context)
        ? [...recordingConsentDeclined.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedRecordingConsentDeclined = {
        ...recordingConsentDeclined,
        context,
    };

    trackRecordingConsentDeclined<Record<string, unknown> | EventSpecification>(modifiedRecordingConsentDeclined, trackers);
}
/**
 * Tracks a PatientSummaryEmailSendAttempted event specification.
 * ID: 1f903bb0-3f1a-44db-8ea1-1758c0cdc1b5
 */
export function trackPatientSummaryEmailSendAttemptedSpec(patientSummaryEmailSendAttempted: PatientSummaryEmailSendAttempted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1f903bb0-3f1a-44db-8ea1-1758c0cdc1b5',
        version: 4,
        name: 'patient_summary_email_send_attempted',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(patientSummaryEmailSendAttempted.context)
        ? [...patientSummaryEmailSendAttempted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientSummaryEmailSendAttempted = {
        ...patientSummaryEmailSendAttempted,
        context,
    };

    trackPatientSummaryEmailSendAttempted<Record<string, unknown> | EventSpecification>(modifiedPatientSummaryEmailSendAttempted, trackers);
}
/**
 * Tracks a NoteAutoRetryDecided event specification.
 * ID: 1ff49e2d-fa8b-4c8b-867c-aa5429e99a4c
 */
export function trackNoteAutoRetryDecidedSpec(noteAutoRetryDecided: NoteAutoRetryDecided & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '1ff49e2d-fa8b-4c8b-867c-aa5429e99a4c',
        version: 0,
        name: 'note_auto_retry_decided',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteAutoRetryDecided.context)
        ? [...noteAutoRetryDecided.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteAutoRetryDecided = {
        ...noteAutoRetryDecided,
        context,
    };

    trackNoteAutoRetryDecided<Record<string, unknown> | EventSpecification>(modifiedNoteAutoRetryDecided, trackers);
}
/**
 * Tracks a CallAnswered event specification.
 * ID: 219e95cc-5eff-4d48-9590-915f58dbdfd2
 */
export function trackCallAnsweredSpec(callAnswered: CallAnswered & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '219e95cc-5eff-4d48-9590-915f58dbdfd2',
        version: 0,
        name: 'call_answered',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callAnswered.context)
        ? [...callAnswered.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallAnswered = {
        ...callAnswered,
        context,
    };

    trackCallAnswered<Record<string, unknown> | EventSpecification>(modifiedCallAnswered, trackers);
}
/**
 * Tracks a ScheduleUploadPromoClicked event specification.
 * ID: 22c9e4fc-399b-4b4b-a1ab-fca0009ef621
 */
export function trackScheduleUploadPromoClickedSpec(scheduleUploadPromoClicked: ScheduleUploadPromoClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '22c9e4fc-399b-4b4b-a1ab-fca0009ef621',
        version: 1,
        name: 'schedule_upload_promo_clicked',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(scheduleUploadPromoClicked.context)
        ? [...scheduleUploadPromoClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScheduleUploadPromoClicked = {
        ...scheduleUploadPromoClicked,
        context,
    };

    trackScheduleUploadPromoClicked<Record<string, unknown> | EventSpecification>(modifiedScheduleUploadPromoClicked, trackers);
}
/**
 * Tracks a EhrPatientMatched event specification.
 * ID: 22f286ac-f834-4cb6-bd4f-d3fdc6a59bc0
 */
export function trackEhrPatientMatchedSpec(ehrPatientMatched: EhrPatientMatched & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '22f286ac-f834-4cb6-bd4f-d3fdc6a59bc0',
        version: 0,
        name: 'ehr_patient_matched',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(ehrPatientMatched.context)
        ? [...ehrPatientMatched.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEhrPatientMatched = {
        ...ehrPatientMatched,
        context,
    };

    trackEhrPatientMatched<Record<string, unknown> | EventSpecification>(modifiedEhrPatientMatched, trackers);
}
/**
 * Tracks a ContextFileUploaded event specification.
 * ID: 233f4e67-8530-4184-b10f-e3f77b2b0c3a
 */
export function trackContextFileUploadedSpec(contextFileUploaded: ContextFileUploaded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '233f4e67-8530-4184-b10f-e3f77b2b0c3a',
        version: 1,
        name: 'context_file_uploaded',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(contextFileUploaded.context)
        ? [...contextFileUploaded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedContextFileUploaded = {
        ...contextFileUploaded,
        context,
    };

    trackContextFileUploaded<Record<string, unknown> | EventSpecification>(modifiedContextFileUploaded, trackers);
}
/**
 * Tracks a UserSignedUp event specification.
 * ID: 23ebbcb3-bb13-4f2e-bcde-8bc049127145
 */
export function trackUserSignedUpSpec(userSignedUp: UserSignedUp & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '23ebbcb3-bb13-4f2e-bcde-8bc049127145',
        version: 2,
        name: 'user_signed_up',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(userSignedUp.context)
        ? [...userSignedUp.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUserSignedUp = {
        ...userSignedUp,
        context,
    };

    trackUserSignedUp<Record<string, unknown> | EventSpecification>(modifiedUserSignedUp, trackers);
}
/**
 * Tracks a MessageAttachmentUploadFailed event specification.
 * ID: 248f3e2a-c7bf-49c1-a53d-812ab83b49b4
 */
export function trackMessageAttachmentUploadFailedSpec(messageAttachmentUploadFailed: MessageAttachmentUploadFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '248f3e2a-c7bf-49c1-a53d-812ab83b49b4',
        version: 0,
        name: 'message_attachment_upload_failed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(messageAttachmentUploadFailed.context)
        ? [...messageAttachmentUploadFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedMessageAttachmentUploadFailed = {
        ...messageAttachmentUploadFailed,
        context,
    };

    trackMessageAttachmentUploadFailed<Record<string, unknown> | EventSpecification>(modifiedMessageAttachmentUploadFailed, trackers);
}
/**
 * Tracks a EvidenceSourcesOpened event specification.
 * ID: 24d60f1a-c698-4318-8b67-3aa85df34ef7
 */
export function trackEvidenceSourcesOpenedSpec(evidenceSourcesOpened: EvidenceSourcesOpened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '24d60f1a-c698-4318-8b67-3aa85df34ef7',
        version: 0,
        name: 'evidence_sources_opened',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceSourcesOpened.context)
        ? [...evidenceSourcesOpened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceSourcesOpened = {
        ...evidenceSourcesOpened,
        context,
    };

    trackEvidenceSourcesOpened<Record<string, unknown> | EventSpecification>(modifiedEvidenceSourcesOpened, trackers);
}
/**
 * Tracks a PatientProfileOpened event specification.
 * ID: 2634b9d5-2980-42a7-a0af-f3840c325cd5
 */
export function trackPatientProfileOpenedSpec(patientProfileOpened: PatientProfileOpened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2634b9d5-2980-42a7-a0af-f3840c325cd5',
        version: 0,
        name: 'patient_profile_opened',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(patientProfileOpened.context)
        ? [...patientProfileOpened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientProfileOpened = {
        ...patientProfileOpened,
        context,
    };

    trackPatientProfileOpened<Record<string, unknown> | EventSpecification>(modifiedPatientProfileOpened, trackers);
}
/**
 * Tracks a CpdDraftDeleted event specification.
 * ID: 282c63a1-b9aa-4ed9-95d6-a19fadfca160
 */
export function trackCpdDraftDeletedSpec(cpdDraftDeleted: CpdDraftDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '282c63a1-b9aa-4ed9-95d6-a19fadfca160',
        version: 1,
        name: 'cpd_draft_deleted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(cpdDraftDeleted.context)
        ? [...cpdDraftDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCpdDraftDeleted = {
        ...cpdDraftDeleted,
        context,
    };

    trackCpdDraftDeleted<Record<string, unknown> | EventSpecification>(modifiedCpdDraftDeleted, trackers);
}
/**
 * Tracks a IntegrationStarted event specification.
 * ID: 2847dfee-a2c5-4add-bd9d-cea79510f1f3
 */
export function trackIntegrationStartedSpec(integrationStarted: IntegrationStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2847dfee-a2c5-4add-bd9d-cea79510f1f3',
        version: 2,
        name: 'integration_started',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationStarted.context)
        ? [...integrationStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationStarted = {
        ...integrationStarted,
        context,
    };

    trackIntegrationStarted<Record<string, unknown> | EventSpecification>(modifiedIntegrationStarted, trackers);
}
/**
 * Tracks a EhrAppointmentSyncFailed event specification.
 * ID: 286d1311-24cf-4c71-ad17-ed371a872273
 */
export function trackEhrAppointmentSyncFailedSpec(ehrAppointmentSyncFailed: EhrAppointmentSyncFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '286d1311-24cf-4c71-ad17-ed371a872273',
        version: 0,
        name: 'ehr_appointment_sync_failed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(ehrAppointmentSyncFailed.context)
        ? [...ehrAppointmentSyncFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEhrAppointmentSyncFailed = {
        ...ehrAppointmentSyncFailed,
        context,
    };

    trackEhrAppointmentSyncFailed<Record<string, unknown> | EventSpecification>(modifiedEhrAppointmentSyncFailed, trackers);
}
/**
 * Tracks a ApplicationInstall event specification.
 * ID: 28ad7c67-1b95-44f0-a1f6-b858fac28ce3
 */
export function trackApplicationInstallSpec(applicationInstall: ApplicationInstall & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '28ad7c67-1b95-44f0-a1f6-b858fac28ce3',
        version: 0,
        name: 'application_install',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(applicationInstall.context)
        ? [...applicationInstall.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedApplicationInstall = {
        ...applicationInstall,
        context,
    };

    trackApplicationInstall<Record<string, unknown> | EventSpecification>(modifiedApplicationInstall, trackers);
}
/**
 * Tracks a LetterDeleted event specification.
 * ID: 28f4b310-efab-4588-a381-b98f4a67aeaa
 */
export function trackLetterDeletedSpec(letterDeleted: LetterDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '28f4b310-efab-4588-a381-b98f4a67aeaa',
        version: 0,
        name: 'letter_deleted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(letterDeleted.context)
        ? [...letterDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLetterDeleted = {
        ...letterDeleted,
        context,
    };

    trackLetterDeleted<Record<string, unknown> | EventSpecification>(modifiedLetterDeleted, trackers);
}
/**
 * Tracks a ModalViewed event specification.
 * ID: 29095935-5303-4f6d-863a-e7adc013c404
 */
export function trackModalViewedSpec(modalViewed: ModalViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '29095935-5303-4f6d-863a-e7adc013c404',
        version: 6,
        name: 'modal_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(modalViewed.context)
        ? [...modalViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedModalViewed = {
        ...modalViewed,
        context,
    };

    trackModalViewed<Record<string, unknown> | EventSpecification>(modifiedModalViewed, trackers);
}
/**
 * Tracks a TaskViewed event specification.
 * ID: 2926ed88-40bf-438b-bcb4-4c7c73e57657
 */
export function trackTaskViewedSpec(taskViewed: TaskViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2926ed88-40bf-438b-bcb4-4c7c73e57657',
        version: 0,
        name: 'task_viewed',
        data_product_id: '521bdabc-e43c-414b-a3d8-af7f5bdfb182',
        data_product_name: 'multiplayer'
    });

    const context = Array.isArray(taskViewed.context)
        ? [...taskViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTaskViewed = {
        ...taskViewed,
        context,
    };

    trackTaskViewed<Record<string, unknown> | EventSpecification>(modifiedTaskViewed, trackers);
}
/**
 * Tracks a SessionDetailResumeStarted event specification.
 * ID: 295175d2-8d99-46d5-b421-7d0ccfcd4237
 */
export function trackSessionDetailResumeStartedSpec(sessionDetailResumeStarted: SessionDetailResumeStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '295175d2-8d99-46d5-b421-7d0ccfcd4237',
        version: 0,
        name: 'session_detail_resume_started',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionDetailResumeStarted.context)
        ? [...sessionDetailResumeStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionDetailResumeStarted = {
        ...sessionDetailResumeStarted,
        context,
    };

    trackSessionDetailResumeStarted<Record<string, unknown> | EventSpecification>(modifiedSessionDetailResumeStarted, trackers);
}
/**
 * Tracks a FormFillingDownloaded event specification.
 * ID: 296d462f-ed1a-4663-a400-5cc45ddef6c2
 */
export function trackFormFillingDownloadedSpec(formFillingDownloaded: FormFillingDownloaded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '296d462f-ed1a-4663-a400-5cc45ddef6c2',
        version: 2,
        name: 'form_filling_downloaded',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(formFillingDownloaded.context)
        ? [...formFillingDownloaded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFormFillingDownloaded = {
        ...formFillingDownloaded,
        context,
    };

    trackFormFillingDownloaded<Record<string, unknown> | EventSpecification>(modifiedFormFillingDownloaded, trackers);
}
/**
 * Tracks a ObjectiveSelected event specification.
 * ID: 2a958001-f739-4d35-908d-1891b84f9642
 */
export function trackObjectiveSelectedSpec(objectiveSelected: ObjectiveSelected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2a958001-f739-4d35-908d-1891b84f9642',
        version: 0,
        name: 'objective_selected',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(objectiveSelected.context)
        ? [...objectiveSelected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedObjectiveSelected = {
        ...objectiveSelected,
        context,
    };

    trackObjectiveSelected<Record<string, unknown> | EventSpecification>(modifiedObjectiveSelected, trackers);
}
/**
 * Tracks a MessageSubmitted event specification.
 * ID: 2af28880-b6e9-4c3c-a90f-68877ff99b09
 */
export function trackMessageSubmittedSpec(messageSubmitted: MessageSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2af28880-b6e9-4c3c-a90f-68877ff99b09',
        version: 4,
        name: 'message_submitted',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(messageSubmitted.context)
        ? [...messageSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedMessageSubmitted = {
        ...messageSubmitted,
        context,
    };

    trackMessageSubmitted<Record<string, unknown> | EventSpecification>(modifiedMessageSubmitted, trackers);
}
/**
 * Tracks a BookDemoCliniciansSelected event specification.
 * ID: 2b2c2580-ca00-4131-97da-b42eacd1eb71
 */
export function trackBookDemoCliniciansSelectedSpec(bookDemoCliniciansSelected: BookDemoCliniciansSelected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2b2c2580-ca00-4131-97da-b42eacd1eb71',
        version: 1,
        name: 'book_demo_clinicians_selected',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(bookDemoCliniciansSelected.context)
        ? [...bookDemoCliniciansSelected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedBookDemoCliniciansSelected = {
        ...bookDemoCliniciansSelected,
        context,
    };

    trackBookDemoCliniciansSelected<Record<string, unknown> | EventSpecification>(modifiedBookDemoCliniciansSelected, trackers);
}
/**
 * Tracks a OnboardingUpdated event specification.
 * ID: 2b3ca9af-49e2-4ad6-a00f-bfad277d0b8c
 */
export function trackOnboardingUpdatedSpec(onboardingUpdated: OnboardingUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2b3ca9af-49e2-4ad6-a00f-bfad277d0b8c',
        version: 1,
        name: 'onboarding_updated',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(onboardingUpdated.context)
        ? [...onboardingUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOnboardingUpdated = {
        ...onboardingUpdated,
        context,
    };

    trackOnboardingUpdated<Record<string, unknown> | EventSpecification>(modifiedOnboardingUpdated, trackers);
}
/**
 * Tracks a ExtractionModalOpened event specification.
 * ID: 2b867d4c-788c-44e6-ab6c-97a49c8c9829
 */
export function trackExtractionModalOpenedSpec(extractionModalOpened: ExtractionModalOpened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2b867d4c-788c-44e6-ab6c-97a49c8c9829',
        version: 1,
        name: 'extraction_modal_opened',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(extractionModalOpened.context)
        ? [...extractionModalOpened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedExtractionModalOpened = {
        ...extractionModalOpened,
        context,
    };

    trackExtractionModalOpened<Record<string, unknown> | EventSpecification>(modifiedExtractionModalOpened, trackers);
}
/**
 * Tracks a EvidenceLiveSuggestionsToggled event specification.
 * ID: 2bbd554b-1f72-4da0-beef-2f3393500162
 */
export function trackEvidenceLiveSuggestionsToggledSpec(evidenceLiveSuggestionsToggled: EvidenceLiveSuggestionsToggled & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2bbd554b-1f72-4da0-beef-2f3393500162',
        version: 3,
        name: 'evidence_live_suggestions_toggled',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceLiveSuggestionsToggled.context)
        ? [...evidenceLiveSuggestionsToggled.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceLiveSuggestionsToggled = {
        ...evidenceLiveSuggestionsToggled,
        context,
    };

    trackEvidenceLiveSuggestionsToggled<Record<string, unknown> | EventSpecification>(modifiedEvidenceLiveSuggestionsToggled, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a ChronicleFirmwareUpgradeStarted event specification.
 * ID: 2c073cfb-59c4-435e-b51b-a76dc06c8e55
 */
export function trackChronicleFirmwareUpgradeStartedSpec(chronicleFirmwareUpgradeStarted: ChronicleFirmwareUpgradeStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2c073cfb-59c4-435e-b51b-a76dc06c8e55',
        version: 1,
        name: 'chronicle_firmware_upgrade_started',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(chronicleFirmwareUpgradeStarted.context)
        ? [...chronicleFirmwareUpgradeStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChronicleFirmwareUpgradeStarted = {
        ...chronicleFirmwareUpgradeStarted,
        context,
    };

    trackChronicleFirmwareUpgradeStarted<Record<string, unknown> | EventSpecification>(modifiedChronicleFirmwareUpgradeStarted, trackers);
}
/**
 * Tracks a TranslatorPreferencesUpdated event specification.
 * ID: 2c5bb4d5-c86d-4d0f-8e01-c4d192e9448c
 */
export function trackTranslatorPreferencesUpdatedSpec(translatorPreferencesUpdated: TranslatorPreferencesUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2c5bb4d5-c86d-4d0f-8e01-c4d192e9448c',
        version: 0,
        name: 'translator_preferences_updated',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(translatorPreferencesUpdated.context)
        ? [...translatorPreferencesUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTranslatorPreferencesUpdated = {
        ...translatorPreferencesUpdated,
        context,
    };

    trackTranslatorPreferencesUpdated<Record<string, unknown> | EventSpecification>(modifiedTranslatorPreferencesUpdated, trackers);
}
/**
 * Tracks a ConversationAutoResolved event specification.
 * ID: 2c674a89-fd48-48c8-8f06-557b1b8895d3
 */
export function trackConversationAutoResolvedSpec(conversationAutoResolved: ConversationAutoResolved & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2c674a89-fd48-48c8-8f06-557b1b8895d3',
        version: 0,
        name: 'conversation_auto_resolved',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(conversationAutoResolved.context)
        ? [...conversationAutoResolved.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedConversationAutoResolved = {
        ...conversationAutoResolved,
        context,
    };

    trackConversationAutoResolved<Record<string, unknown> | EventSpecification>(modifiedConversationAutoResolved, trackers);
}
/**
 * Tracks a ForceUpdateInitiated event specification.
 * ID: 2c783f37-5de5-406c-bdfb-412d6dc37dc1
 */
export function trackForceUpdateInitiatedSpec(forceUpdateInitiated: ForceUpdateInitiated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2c783f37-5de5-406c-bdfb-412d6dc37dc1',
        version: 1,
        name: 'force_update_initiated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(forceUpdateInitiated.context)
        ? [...forceUpdateInitiated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedForceUpdateInitiated = {
        ...forceUpdateInitiated,
        context,
    };

    trackForceUpdateInitiated<Record<string, unknown> | EventSpecification>(modifiedForceUpdateInitiated, trackers);
}
/**
 * Tracks a IntegrationWidgetAuthStepCompleted event specification.
 * ID: 2d4ea641-31a2-406a-b1df-c31efb986fa5
 */
export function trackIntegrationWidgetAuthStepCompletedSpec(integrationWidgetAuthStepCompleted: IntegrationWidgetAuthStepCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2d4ea641-31a2-406a-b1df-c31efb986fa5',
        version: 1,
        name: 'integration_widget_auth_step_completed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationWidgetAuthStepCompleted.context)
        ? [...integrationWidgetAuthStepCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationWidgetAuthStepCompleted = {
        ...integrationWidgetAuthStepCompleted,
        context,
    };

    trackIntegrationWidgetAuthStepCompleted<Record<string, unknown> | EventSpecification>(modifiedIntegrationWidgetAuthStepCompleted, trackers);
}
/**
 * Tracks a UserCreatedViaInternalAdmin event specification.
 * ID: 2d5836c7-17f2-43f1-a625-5f1a1ea5e63a
 */
export function trackUserCreatedViaInternalAdminSpec(userCreatedViaInternalAdmin: UserCreatedViaInternalAdmin & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2d5836c7-17f2-43f1-a625-5f1a1ea5e63a',
        version: 1,
        name: 'user_created_via_internal_admin',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(userCreatedViaInternalAdmin.context)
        ? [...userCreatedViaInternalAdmin.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUserCreatedViaInternalAdmin = {
        ...userCreatedViaInternalAdmin,
        context,
    };

    trackUserCreatedViaInternalAdmin<Record<string, unknown> | EventSpecification>(modifiedUserCreatedViaInternalAdmin, trackers);
}
/**
 * Tracks a IntegrationDataFailed event specification.
 * ID: 2d813ae5-d95d-4d1c-b2cc-a1ca385cb32f
 */
export function trackIntegrationDataFailedSpec(integrationDataFailed: IntegrationDataFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2d813ae5-d95d-4d1c-b2cc-a1ca385cb32f',
        version: 1,
        name: 'integration_data_failed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationDataFailed.context)
        ? [...integrationDataFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationDataFailed = {
        ...integrationDataFailed,
        context,
    };

    trackIntegrationDataFailed<Record<string, unknown> | EventSpecification>(modifiedIntegrationDataFailed, trackers);
}
/**
 * Tracks a EhrLaunchPatientCompleted event specification.
 * ID: 2eb28669-62d2-4305-9e99-2f61afa89ad6
 */
export function trackEhrLaunchPatientCompletedSpec(ehrLaunchPatientCompleted: EhrLaunchPatientCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2eb28669-62d2-4305-9e99-2f61afa89ad6',
        version: 0,
        name: 'ehr_launch_patient_completed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(ehrLaunchPatientCompleted.context)
        ? [...ehrLaunchPatientCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEhrLaunchPatientCompleted = {
        ...ehrLaunchPatientCompleted,
        context,
    };

    trackEhrLaunchPatientCompleted<Record<string, unknown> | EventSpecification>(modifiedEhrLaunchPatientCompleted, trackers);
}
/**
 * Tracks a PatientFieldExtractionSurfaced event specification.
 * ID: 2f7783cb-0eba-40b4-bab3-0263f8e28157
 */
export function trackPatientFieldExtractionSurfacedSpec(patientFieldExtractionSurfaced: PatientFieldExtractionSurfaced & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2f7783cb-0eba-40b4-bab3-0263f8e28157',
        version: 0,
        name: 'patient_field_extraction_surfaced',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(patientFieldExtractionSurfaced.context)
        ? [...patientFieldExtractionSurfaced.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientFieldExtractionSurfaced = {
        ...patientFieldExtractionSurfaced,
        context,
    };

    trackPatientFieldExtractionSurfaced<Record<string, unknown> | EventSpecification>(modifiedPatientFieldExtractionSurfaced, trackers);
}
/**
 * Tracks a LetterAttachmentUploaded event specification.
 * ID: 2fb549aa-1c14-4545-8ec2-518ce4de8b5a
 */
export function trackLetterAttachmentUploadedSpec(letterAttachmentUploaded: LetterAttachmentUploaded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2fb549aa-1c14-4545-8ec2-518ce4de8b5a',
        version: 0,
        name: 'letter_attachment_uploaded',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(letterAttachmentUploaded.context)
        ? [...letterAttachmentUploaded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLetterAttachmentUploaded = {
        ...letterAttachmentUploaded,
        context,
    };

    trackLetterAttachmentUploaded<Record<string, unknown> | EventSpecification>(modifiedLetterAttachmentUploaded, trackers);
}
/**
 * Tracks a NudgeCardAdded event specification.
 * ID: 2fb8082a-3573-40ac-a8e9-0c35dd859d4e
 */
export function trackNudgeCardAddedSpec(nudgeCardAdded: NudgeCardAdded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '2fb8082a-3573-40ac-a8e9-0c35dd859d4e',
        version: 0,
        name: 'nudge_card_added',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(nudgeCardAdded.context)
        ? [...nudgeCardAdded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNudgeCardAdded = {
        ...nudgeCardAdded,
        context,
    };

    trackNudgeCardAdded<Record<string, unknown> | EventSpecification>(modifiedNudgeCardAdded, trackers);
}
/**
 * Tracks a ResponseCreated event specification.
 * ID: 300065f2-f963-4356-8016-604d84681b68
 */
export function trackResponseCreatedSpec(responseCreated: ResponseCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '300065f2-f963-4356-8016-604d84681b68',
        version: 3,
        name: 'response_created',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(responseCreated.context)
        ? [...responseCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedResponseCreated = {
        ...responseCreated,
        context,
    };

    trackResponseCreated<Record<string, unknown> | EventSpecification>(modifiedResponseCreated, trackers);
}
/**
 * Tracks a WorkToolCompleted event specification.
 * ID: 3046bf05-ee18-5d93-bfcf-de015932d892
 */
export function trackWorkToolCompletedSpec(workToolCompleted: WorkToolCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3046bf05-ee18-5d93-bfcf-de015932d892',
        version: 1,
        name: 'work_tool_completed',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workToolCompleted.context)
        ? [...workToolCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkToolCompleted = {
        ...workToolCompleted,
        context,
    };

    trackWorkToolCompleted<Record<string, unknown> | EventSpecification>(modifiedWorkToolCompleted, trackers);
}
/**
 * Tracks a NativeAppOffline event specification.
 * ID: 3093a0f0-567c-4ae8-9f88-2a9b5dedf694
 */
export function trackNativeAppOfflineSpec(nativeAppOffline: NativeAppOffline & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3093a0f0-567c-4ae8-9f88-2a9b5dedf694',
        version: 0,
        name: 'native_app_offline',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(nativeAppOffline.context)
        ? [...nativeAppOffline.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNativeAppOffline = {
        ...nativeAppOffline,
        context,
    };

    trackNativeAppOffline<Record<string, unknown> | EventSpecification>(modifiedNativeAppOffline, trackers);
}
/**
 * Tracks a LibraryCollectionCreated event specification.
 * ID: 30df63a9-fbbe-4607-9efa-75b82ec4a84d
 */
export function trackLibraryCollectionCreatedSpec(libraryCollectionCreated: LibraryCollectionCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '30df63a9-fbbe-4607-9efa-75b82ec4a84d',
        version: 1,
        name: 'library_collection_created',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(libraryCollectionCreated.context)
        ? [...libraryCollectionCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLibraryCollectionCreated = {
        ...libraryCollectionCreated,
        context,
    };

    trackLibraryCollectionCreated<Record<string, unknown> | EventSpecification>(modifiedLibraryCollectionCreated, trackers);
}
/**
 * Tracks a TaskListViewed event specification.
 * ID: 30e42a16-a3b1-4c63-b193-89de7d8b2afa
 */
export function trackTaskListViewedSpec(taskListViewed: TaskListViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '30e42a16-a3b1-4c63-b193-89de7d8b2afa',
        version: 0,
        name: 'task_list_viewed',
        data_product_id: '521bdabc-e43c-414b-a3d8-af7f5bdfb182',
        data_product_name: 'multiplayer'
    });

    const context = Array.isArray(taskListViewed.context)
        ? [...taskListViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTaskListViewed = {
        ...taskListViewed,
        context,
    };

    trackTaskListViewed<Record<string, unknown> | EventSpecification>(modifiedTaskListViewed, trackers);
}
/**
 * Tracks a AuthFallbackUsed event specification.
 * ID: 31fde5b4-8c0d-4c3d-806e-2f32aff83a68
 */
export function trackAuthFallbackUsedSpec(authFallbackUsed: AuthFallbackUsed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '31fde5b4-8c0d-4c3d-806e-2f32aff83a68',
        version: 1,
        name: 'auth_fallback_used',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(authFallbackUsed.context)
        ? [...authFallbackUsed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAuthFallbackUsed = {
        ...authFallbackUsed,
        context,
    };

    trackAuthFallbackUsed<Record<string, unknown> | EventSpecification>(modifiedAuthFallbackUsed, trackers);
}
/**
 * Tracks a LetterRecipientAdded event specification.
 * ID: 3244386a-31bf-4097-8cb7-a304ff1b11e2
 */
export function trackLetterRecipientAddedSpec(letterRecipientAdded: LetterRecipientAdded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3244386a-31bf-4097-8cb7-a304ff1b11e2',
        version: 0,
        name: 'letter_recipient_added',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(letterRecipientAdded.context)
        ? [...letterRecipientAdded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLetterRecipientAdded = {
        ...letterRecipientAdded,
        context,
    };

    trackLetterRecipientAdded<Record<string, unknown> | EventSpecification>(modifiedLetterRecipientAdded, trackers);
}
/**
 * Tracks a ContactSalesFormOpened event specification.
 * ID: 32708bbc-06b9-4328-ab22-b4a77175d7b6
 */
export function trackContactSalesFormOpenedSpec(contactSalesFormOpened: ContactSalesFormOpened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '32708bbc-06b9-4328-ab22-b4a77175d7b6',
        version: 1,
        name: 'contact_sales_form_opened',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(contactSalesFormOpened.context)
        ? [...contactSalesFormOpened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedContactSalesFormOpened = {
        ...contactSalesFormOpened,
        context,
    };

    trackContactSalesFormOpened<Record<string, unknown> | EventSpecification>(modifiedContactSalesFormOpened, trackers);
}
/**
 * Tracks a ScreenViewed event specification.
 * ID: 340992f6-c50f-4df6-9ecc-18bcdf842aa7
 */
export function trackScreenViewedSpec(screenViewed: ScreenViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '340992f6-c50f-4df6-9ecc-18bcdf842aa7',
        version: 8,
        name: 'screen_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(screenViewed.context)
        ? [...screenViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScreenViewed = {
        ...screenViewed,
        context,
    };

    trackScreenViewed<Record<string, unknown> | EventSpecification>(modifiedScreenViewed, trackers);
}
/**
 * Tracks a AppStoreReviewSentiment event specification.
 * ID: 34573638-2ab4-4318-92de-9cba6ea8c9e3
 */
export function trackAppStoreReviewSentimentSpec(appStoreReviewSentiment: AppStoreReviewSentiment & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '34573638-2ab4-4318-92de-9cba6ea8c9e3',
        version: 0,
        name: 'app_store_review_sentiment',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(appStoreReviewSentiment.context)
        ? [...appStoreReviewSentiment.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAppStoreReviewSentiment = {
        ...appStoreReviewSentiment,
        context,
    };

    trackAppStoreReviewSentiment<Record<string, unknown> | EventSpecification>(modifiedAppStoreReviewSentiment, trackers);
}
/**
 * Tracks a WorkSurfaceActioned event specification.
 * ID: 3490eb84-c868-5cdf-8291-b7fca561bc1a
 */
export function trackWorkSurfaceActionedSpec(workSurfaceActioned: WorkSurfaceActioned & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3490eb84-c868-5cdf-8291-b7fca561bc1a',
        version: 3,
        name: 'work_surface_actioned',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workSurfaceActioned.context)
        ? [...workSurfaceActioned.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkSurfaceActioned = {
        ...workSurfaceActioned,
        context,
    };

    trackWorkSurfaceActioned<Record<string, unknown> | EventSpecification>(modifiedWorkSurfaceActioned, trackers);
}
/**
 * Tracks a WorkErrorEncountered event specification.
 * ID: 35008ae5-7b8b-5df6-878b-bd1db4b02e4b
 */
export function trackWorkErrorEncounteredSpec(workErrorEncountered: WorkErrorEncountered & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '35008ae5-7b8b-5df6-878b-bd1db4b02e4b',
        version: 0,
        name: 'work_error_encountered',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workErrorEncountered.context)
        ? [...workErrorEncountered.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkErrorEncountered = {
        ...workErrorEncountered,
        context,
    };

    trackWorkErrorEncountered<Record<string, unknown> | EventSpecification>(modifiedWorkErrorEncountered, trackers);
}
/**
 * Tracks a PatientSearched event specification.
 * ID: 36ce6494-b389-4def-9a9c-20ea9e8d3fcb
 */
export function trackPatientSearchedSpec(patientSearched: PatientSearched & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '36ce6494-b389-4def-9a9c-20ea9e8d3fcb',
        version: 2,
        name: 'patient_searched',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(patientSearched.context)
        ? [...patientSearched.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientSearched = {
        ...patientSearched,
        context,
    };

    trackPatientSearched<Record<string, unknown> | EventSpecification>(modifiedPatientSearched, trackers);
}
/**
 * Tracks a TaskAssignmentUpdated event specification.
 * ID: 374ec83c-4438-4fca-a66e-d594220066c4
 */
export function trackTaskAssignmentUpdatedSpec(taskAssignmentUpdated: TaskAssignmentUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '374ec83c-4438-4fca-a66e-d594220066c4',
        version: 1,
        name: 'task_assignment_updated',
        data_product_id: '521bdabc-e43c-414b-a3d8-af7f5bdfb182',
        data_product_name: 'multiplayer'
    });

    const context = Array.isArray(taskAssignmentUpdated.context)
        ? [...taskAssignmentUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTaskAssignmentUpdated = {
        ...taskAssignmentUpdated,
        context,
    };

    trackTaskAssignmentUpdated<Record<string, unknown> | EventSpecification>(modifiedTaskAssignmentUpdated, trackers);
}
/**
 * Tracks a InboundCallReceived event specification.
 * ID: 375d58f4-dfb2-4085-968c-bf45bc6dda18
 */
export function trackInboundCallReceivedSpec(inboundCallReceived: InboundCallReceived & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '375d58f4-dfb2-4085-968c-bf45bc6dda18',
        version: 0,
        name: 'inbound_call_received',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(inboundCallReceived.context)
        ? [...inboundCallReceived.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedInboundCallReceived = {
        ...inboundCallReceived,
        context,
    };

    trackInboundCallReceived<Record<string, unknown> | EventSpecification>(modifiedInboundCallReceived, trackers);
}
/**
 * Tracks a TemplateSearched event specification.
 * ID: 379ea1fe-ac30-495f-bfd3-ffb3513b89e8
 */
export function trackTemplateSearchedSpec(templateSearched: TemplateSearched & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '379ea1fe-ac30-495f-bfd3-ffb3513b89e8',
        version: 3,
        name: 'template_searched',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateSearched.context)
        ? [...templateSearched.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateSearched = {
        ...templateSearched,
        context,
    };

    trackTemplateSearched<Record<string, unknown> | EventSpecification>(modifiedTemplateSearched, trackers);
}
/**
 * Tracks a DoNotContactBlockedSend event specification.
 * ID: 3879c66e-68d9-42ed-9007-7dde536e58ce
 */
export function trackDoNotContactBlockedSendSpec(doNotContactBlockedSend: DoNotContactBlockedSend & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3879c66e-68d9-42ed-9007-7dde536e58ce',
        version: 0,
        name: 'do_not_contact_blocked_send',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(doNotContactBlockedSend.context)
        ? [...doNotContactBlockedSend.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDoNotContactBlockedSend = {
        ...doNotContactBlockedSend,
        context,
    };

    trackDoNotContactBlockedSend<Record<string, unknown> | EventSpecification>(modifiedDoNotContactBlockedSend, trackers);
}
/**
 * Tracks a InboxSessionStarted event specification.
 * ID: 387c350a-6651-421c-905a-da1a994ad103
 */
export function trackInboxSessionStartedSpec(inboxSessionStarted: InboxSessionStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '387c350a-6651-421c-905a-da1a994ad103',
        version: 0,
        name: 'inbox_session_started',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(inboxSessionStarted.context)
        ? [...inboxSessionStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedInboxSessionStarted = {
        ...inboxSessionStarted,
        context,
    };

    trackInboxSessionStarted<Record<string, unknown> | EventSpecification>(modifiedInboxSessionStarted, trackers);
}
/**
 * Tracks a EvidenceSignupWallViewed event specification.
 * ID: 38f35acc-bedc-4b8a-a8e6-55cf099f3ce3
 */
export function trackEvidenceSignupWallViewedSpec(evidenceSignupWallViewed: EvidenceSignupWallViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '38f35acc-bedc-4b8a-a8e6-55cf099f3ce3',
        version: 1,
        name: 'evidence_signup_wall_viewed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceSignupWallViewed.context)
        ? [...evidenceSignupWallViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceSignupWallViewed = {
        ...evidenceSignupWallViewed,
        context,
    };

    trackEvidenceSignupWallViewed<Record<string, unknown> | EventSpecification>(modifiedEvidenceSignupWallViewed, trackers);
}
/**
 * Tracks a BookDemoNextClicked event specification.
 * ID: 39b1cd18-c996-4d82-b650-19c73957b010
 */
export function trackBookDemoNextClickedSpec(bookDemoNextClicked: BookDemoNextClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '39b1cd18-c996-4d82-b650-19c73957b010',
        version: 1,
        name: 'book_demo_next_clicked',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(bookDemoNextClicked.context)
        ? [...bookDemoNextClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedBookDemoNextClicked = {
        ...bookDemoNextClicked,
        context,
    };

    trackBookDemoNextClicked<Record<string, unknown> | EventSpecification>(modifiedBookDemoNextClicked, trackers);
}
/**
 * Tracks a NoteGenerationCompleted event specification.
 * ID: 39b87b82-17c2-40c6-84ac-0f7ab19176be
 */
export function trackNoteGenerationCompletedSpec(noteGenerationCompleted: NoteGenerationCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '39b87b82-17c2-40c6-84ac-0f7ab19176be',
        version: 1,
        name: 'note_generation_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteGenerationCompleted.context)
        ? [...noteGenerationCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteGenerationCompleted = {
        ...noteGenerationCompleted,
        context,
    };

    trackNoteGenerationCompleted<Record<string, unknown> | EventSpecification>(modifiedNoteGenerationCompleted, trackers);
}
/**
 * Tracks a EvidenceContactSalesClicked event specification.
 * ID: 3a57efd5-57c3-4043-af7b-0806bb02bddc
 */
export function trackEvidenceContactSalesClickedSpec(evidenceContactSalesClicked: EvidenceContactSalesClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3a57efd5-57c3-4043-af7b-0806bb02bddc',
        version: 1,
        name: 'evidence_contact_sales_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceContactSalesClicked.context)
        ? [...evidenceContactSalesClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceContactSalesClicked = {
        ...evidenceContactSalesClicked,
        context,
    };

    trackEvidenceContactSalesClicked<Record<string, unknown> | EventSpecification>(modifiedEvidenceContactSalesClicked, trackers);
}
/**
 * Tracks a TranslatorUserCreated event specification.
 * ID: 3b918e15-c507-4cd3-9227-aa8db51ddd47
 */
export function trackTranslatorUserCreatedSpec(translatorUserCreated: TranslatorUserCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3b918e15-c507-4cd3-9227-aa8db51ddd47',
        version: 0,
        name: 'translator_user_created',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(translatorUserCreated.context)
        ? [...translatorUserCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTranslatorUserCreated = {
        ...translatorUserCreated,
        context,
    };

    trackTranslatorUserCreated<Record<string, unknown> | EventSpecification>(modifiedTranslatorUserCreated, trackers);
}
/**
 * Tracks a InboxSessionEnded event specification.
 * ID: 3bbe4d52-4ff7-4116-bc6b-47ed45e4085e
 */
export function trackInboxSessionEndedSpec(inboxSessionEnded: InboxSessionEnded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3bbe4d52-4ff7-4116-bc6b-47ed45e4085e',
        version: 0,
        name: 'inbox_session_ended',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(inboxSessionEnded.context)
        ? [...inboxSessionEnded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedInboxSessionEnded = {
        ...inboxSessionEnded,
        context,
    };

    trackInboxSessionEnded<Record<string, unknown> | EventSpecification>(modifiedInboxSessionEnded, trackers);
}
/**
 * Tracks a DiarisedTranscriptViewed event specification.
 * ID: 3c01b032-256c-427e-a038-86bd9cf312f1
 */
export function trackDiarisedTranscriptViewedSpec(diarisedTranscriptViewed: DiarisedTranscriptViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3c01b032-256c-427e-a038-86bd9cf312f1',
        version: 0,
        name: 'diarised_transcript_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(diarisedTranscriptViewed.context)
        ? [...diarisedTranscriptViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDiarisedTranscriptViewed = {
        ...diarisedTranscriptViewed,
        context,
    };

    trackDiarisedTranscriptViewed<Record<string, unknown> | EventSpecification>(modifiedDiarisedTranscriptViewed, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a HardwareRecordingStop event specification.
 * ID: 3c7d5a18-e9f2-4b63-a841-d2f8e6c09b35
 */
export function trackHardwareRecordingStopSpec(hardwareRecordingStop: HardwareRecordingStop & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3c7d5a18-e9f2-4b63-a841-d2f8e6c09b35',
        version: 1,
        name: 'hardware_recording_stop',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(hardwareRecordingStop.context)
        ? [...hardwareRecordingStop.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedHardwareRecordingStop = {
        ...hardwareRecordingStop,
        context,
    };

    trackHardwareRecordingStop<Record<string, unknown> | EventSpecification>(modifiedHardwareRecordingStop, trackers);
}
/**
 * Tracks a ShareMemberSearched event specification.
 * ID: 3ce98140-46b3-4206-8c95-80b1f37b1743
 */
export function trackShareMemberSearchedSpec(shareMemberSearched: ShareMemberSearched & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3ce98140-46b3-4206-8c95-80b1f37b1743',
        version: 1,
        name: 'share_member_searched',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareMemberSearched.context)
        ? [...shareMemberSearched.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareMemberSearched = {
        ...shareMemberSearched,
        context,
    };

    trackShareMemberSearched<Record<string, unknown> | EventSpecification>(modifiedShareMemberSearched, trackers);
}
/**
 * Tracks a LetterSigned event specification.
 * ID: 3dfe0a98-e02e-4efc-b37a-eaaf9c7710dc
 */
export function trackLetterSignedSpec(letterSigned: LetterSigned & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3dfe0a98-e02e-4efc-b37a-eaaf9c7710dc',
        version: 0,
        name: 'letter_signed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(letterSigned.context)
        ? [...letterSigned.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLetterSigned = {
        ...letterSigned,
        context,
    };

    trackLetterSigned<Record<string, unknown> | EventSpecification>(modifiedLetterSigned, trackers);
}
/**
 * Tracks a SectionCreated event specification.
 * ID: 3e0f1c05-5874-4683-928a-1524e530f78e
 */
export function trackSectionCreatedSpec(sectionCreated: SectionCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3e0f1c05-5874-4683-928a-1524e530f78e',
        version: 2,
        name: 'section_created',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sectionCreated.context)
        ? [...sectionCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSectionCreated = {
        ...sectionCreated,
        context,
    };

    trackSectionCreated<Record<string, unknown> | EventSpecification>(modifiedSectionCreated, trackers);
}
/**
 * Tracks a DeviceEvent event specification.
 * ID: 3e2b507f-cf1d-4bbf-9570-684653b1f45a
 */
export function trackDeviceEventSpec(deviceEvent: DeviceEvent & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3e2b507f-cf1d-4bbf-9570-684653b1f45a',
        version: 0,
        name: 'device_event',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(deviceEvent.context)
        ? [...deviceEvent.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDeviceEvent = {
        ...deviceEvent,
        context,
    };

    trackDeviceEvent<Record<string, unknown> | EventSpecification>(modifiedDeviceEvent, trackers);
}
/**
 * Tracks a EvidenceSpecialtyChanged event specification.
 * ID: 3e324169-4157-46b6-836c-d01fdda37ee2
 */
export function trackEvidenceSpecialtyChangedSpec(evidenceSpecialtyChanged: EvidenceSpecialtyChanged & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3e324169-4157-46b6-836c-d01fdda37ee2',
        version: 1,
        name: 'evidence_specialty_changed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceSpecialtyChanged.context)
        ? [...evidenceSpecialtyChanged.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceSpecialtyChanged = {
        ...evidenceSpecialtyChanged,
        context,
    };

    trackEvidenceSpecialtyChanged<Record<string, unknown> | EventSpecification>(modifiedEvidenceSpecialtyChanged, trackers);
}
/**
 * Tracks a UserSignInFailed event specification.
 * ID: 3e3fd2bd-d5e3-4efe-b496-42b1e423192a
 */
export function trackUserSignInFailedSpec(userSignInFailed: UserSignInFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3e3fd2bd-d5e3-4efe-b496-42b1e423192a',
        version: 0,
        name: 'user_sign_in_failed',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(userSignInFailed.context)
        ? [...userSignInFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUserSignInFailed = {
        ...userSignInFailed,
        context,
    };

    trackUserSignInFailed<Record<string, unknown> | EventSpecification>(modifiedUserSignInFailed, trackers);
}
/**
 * Tracks a ObjectiveValidationFailed event specification.
 * ID: 3e68e1b3-5417-4bb5-abde-7c8a07c86415
 */
export function trackObjectiveValidationFailedSpec(objectiveValidationFailed: ObjectiveValidationFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3e68e1b3-5417-4bb5-abde-7c8a07c86415',
        version: 0,
        name: 'objective_validation_failed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(objectiveValidationFailed.context)
        ? [...objectiveValidationFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedObjectiveValidationFailed = {
        ...objectiveValidationFailed,
        context,
    };

    trackObjectiveValidationFailed<Record<string, unknown> | EventSpecification>(modifiedObjectiveValidationFailed, trackers);
}
/**
 * Tracks a PersonalizationCompleted event specification.
 * ID: 3f316028-2a9e-46d4-b79b-6348feb9d5b1
 */
export function trackPersonalizationCompletedSpec(personalizationCompleted: PersonalizationCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3f316028-2a9e-46d4-b79b-6348feb9d5b1',
        version: 1,
        name: 'personalization_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(personalizationCompleted.context)
        ? [...personalizationCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPersonalizationCompleted = {
        ...personalizationCompleted,
        context,
    };

    trackPersonalizationCompleted<Record<string, unknown> | EventSpecification>(modifiedPersonalizationCompleted, trackers);
}
/**
 * Tracks a SessionsSortChanged event specification.
 * ID: 3f450c1d-ba6f-410e-86a8-939c8c092446
 */
export function trackSessionsSortChangedSpec(sessionsSortChanged: SessionsSortChanged & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3f450c1d-ba6f-410e-86a8-939c8c092446',
        version: 0,
        name: 'sessions_sort_changed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionsSortChanged.context)
        ? [...sessionsSortChanged.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionsSortChanged = {
        ...sessionsSortChanged,
        context,
    };

    trackSessionsSortChanged<Record<string, unknown> | EventSpecification>(modifiedSessionsSortChanged, trackers);
}
/**
 * Tracks a EvidenceExperienceFeedbackSnoozed event specification.
 * ID: 3fc305fa-6f1d-4638-9a94-8e6be6565380
 */
export function trackEvidenceExperienceFeedbackSnoozedSpec(evidenceExperienceFeedbackSnoozed: EvidenceExperienceFeedbackSnoozed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3fc305fa-6f1d-4638-9a94-8e6be6565380',
        version: 3,
        name: 'evidence_experience_feedback_snoozed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceExperienceFeedbackSnoozed.context)
        ? [...evidenceExperienceFeedbackSnoozed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceExperienceFeedbackSnoozed = {
        ...evidenceExperienceFeedbackSnoozed,
        context,
    };

    trackEvidenceExperienceFeedbackSnoozed<Record<string, unknown> | EventSpecification>(modifiedEvidenceExperienceFeedbackSnoozed, trackers);
}
/**
 * Tracks a PersonalizationToggled event specification.
 * ID: 3fe2cc2c-52ef-497c-ad09-5c97b469ae80
 */
export function trackPersonalizationToggledSpec(personalizationToggled: PersonalizationToggled & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '3fe2cc2c-52ef-497c-ad09-5c97b469ae80',
        version: 4,
        name: 'personalization_toggled',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(personalizationToggled.context)
        ? [...personalizationToggled.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPersonalizationToggled = {
        ...personalizationToggled,
        context,
    };

    trackPersonalizationToggled<Record<string, unknown> | EventSpecification>(modifiedPersonalizationToggled, trackers);
}
/**
 * Tracks a IntegrationFailed event specification.
 * ID: 402a4202-aec1-45e7-a536-884d128d1691
 */
export function trackIntegrationFailedSpec(integrationFailed: IntegrationFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '402a4202-aec1-45e7-a536-884d128d1691',
        version: 1,
        name: 'integration_failed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationFailed.context)
        ? [...integrationFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationFailed = {
        ...integrationFailed,
        context,
    };

    trackIntegrationFailed<Record<string, unknown> | EventSpecification>(modifiedIntegrationFailed, trackers);
}
/**
 * Tracks a ReferralCreated event specification.
 * ID: 403a11d0-e4a1-489f-bd91-4a3659290a22
 */
export function trackReferralCreatedSpec(referralCreated: ReferralCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '403a11d0-e4a1-489f-bd91-4a3659290a22',
        version: 1,
        name: 'referral_created',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(referralCreated.context)
        ? [...referralCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedReferralCreated = {
        ...referralCreated,
        context,
    };

    trackReferralCreated<Record<string, unknown> | EventSpecification>(modifiedReferralCreated, trackers);
}
/**
 * Tracks a CpdPageViewed event specification.
 * ID: 40bebd5f-67c6-4dfc-995f-0589ae13d5a2
 */
export function trackCpdPageViewedSpec(cpdPageViewed: CpdPageViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '40bebd5f-67c6-4dfc-995f-0589ae13d5a2',
        version: 1,
        name: 'cpd_page_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(cpdPageViewed.context)
        ? [...cpdPageViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCpdPageViewed = {
        ...cpdPageViewed,
        context,
    };

    trackCpdPageViewed<Record<string, unknown> | EventSpecification>(modifiedCpdPageViewed, trackers);
}
/**
 * Tracks a WorkExperienceFeedbackActioned event specification.
 * ID: 40e68f36-846d-4435-af1d-07b8d59dd11b
 */
export function trackWorkExperienceFeedbackActionedSpec(workExperienceFeedbackActioned: WorkExperienceFeedbackActioned & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '40e68f36-846d-4435-af1d-07b8d59dd11b',
        version: 2,
        name: 'work_experience_feedback_actioned',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workExperienceFeedbackActioned.context)
        ? [...workExperienceFeedbackActioned.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkExperienceFeedbackActioned = {
        ...workExperienceFeedbackActioned,
        context,
    };

    trackWorkExperienceFeedbackActioned<Record<string, unknown> | EventSpecification>(modifiedWorkExperienceFeedbackActioned, trackers);
}
/**
 * Tracks a CallCompleted event specification.
 * ID: 410d6104-07df-40d3-845f-7f003e792ce6
 */
export function trackCallCompletedSpec(callCompleted: CallCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '410d6104-07df-40d3-845f-7f003e792ce6',
        version: 0,
        name: 'call_completed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callCompleted.context)
        ? [...callCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallCompleted = {
        ...callCompleted,
        context,
    };

    trackCallCompleted<Record<string, unknown> | EventSpecification>(modifiedCallCompleted, trackers);
}
/**
 * Tracks a ShareExternalConfirmed event specification.
 * ID: 419f59c8-c0d0-4a16-96d4-fe60636c8910
 */
export function trackShareExternalConfirmedSpec(shareExternalConfirmed: ShareExternalConfirmed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '419f59c8-c0d0-4a16-96d4-fe60636c8910',
        version: 1,
        name: 'share_external_confirmed',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareExternalConfirmed.context)
        ? [...shareExternalConfirmed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareExternalConfirmed = {
        ...shareExternalConfirmed,
        context,
    };

    trackShareExternalConfirmed<Record<string, unknown> | EventSpecification>(modifiedShareExternalConfirmed, trackers);
}
/**
 * Tracks a InboxItemViewed event specification.
 * ID: 42161ef3-8891-4dd7-b221-5826b2d9cf8f
 */
export function trackInboxItemViewedSpec(inboxItemViewed: InboxItemViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '42161ef3-8891-4dd7-b221-5826b2d9cf8f',
        version: 0,
        name: 'inbox_item_viewed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(inboxItemViewed.context)
        ? [...inboxItemViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedInboxItemViewed = {
        ...inboxItemViewed,
        context,
    };

    trackInboxItemViewed<Record<string, unknown> | EventSpecification>(modifiedInboxItemViewed, trackers);
}
/**
 * Tracks a UserClicked event specification.
 * ID: 421c8ab8-215f-43ff-b939-c7753ec88a30
 */
export function trackUserClickedSpec(userClicked: UserClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '421c8ab8-215f-43ff-b939-c7753ec88a30',
        version: 1,
        name: 'user_clicked',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(userClicked.context)
        ? [...userClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUserClicked = {
        ...userClicked,
        context,
    };

    trackUserClicked<Record<string, unknown> | EventSpecification>(modifiedUserClicked, trackers);
}
/**
 * Tracks a CommunitySearched event specification.
 * ID: 4236d246-aabe-4ee0-8d6b-0dd9f67f5c8e
 */
export function trackCommunitySearchedSpec(communitySearched: CommunitySearched & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4236d246-aabe-4ee0-8d6b-0dd9f67f5c8e',
        version: 1,
        name: 'community_searched',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(communitySearched.context)
        ? [...communitySearched.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCommunitySearched = {
        ...communitySearched,
        context,
    };

    trackCommunitySearched<Record<string, unknown> | EventSpecification>(modifiedCommunitySearched, trackers);
}
/**
 * Tracks a CalculatorExecuted event specification.
 * ID: 429f66ae-cf26-4f5a-ac9c-a877a3ba393b
 */
export function trackCalculatorExecutedSpec(calculatorExecuted: CalculatorExecuted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '429f66ae-cf26-4f5a-ac9c-a877a3ba393b',
        version: 1,
        name: 'calculator_executed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(calculatorExecuted.context)
        ? [...calculatorExecuted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCalculatorExecuted = {
        ...calculatorExecuted,
        context,
    };

    trackCalculatorExecuted<Record<string, unknown> | EventSpecification>(modifiedCalculatorExecuted, trackers);
}
/**
 * Tracks a NotePushed event specification.
 * ID: 42d1b65c-ae02-4ed4-a998-a23be1a2dede
 */
export function trackNotePushedSpec(notePushed: NotePushed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '42d1b65c-ae02-4ed4-a998-a23be1a2dede',
        version: 4,
        name: 'note_pushed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(notePushed.context)
        ? [...notePushed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNotePushed = {
        ...notePushed,
        context,
    };

    trackNotePushed<Record<string, unknown> | EventSpecification>(modifiedNotePushed, trackers);
}
/**
 * Tracks a PaidActionTracked event specification.
 * ID: 43358947-3686-432f-9608-ad4467568477
 */
export function trackPaidActionTrackedSpec(paidActionTracked: PaidActionTracked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '43358947-3686-432f-9608-ad4467568477',
        version: 0,
        name: 'paid_action_tracked',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(paidActionTracked.context)
        ? [...paidActionTracked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPaidActionTracked = {
        ...paidActionTracked,
        context,
    };

    trackPaidActionTracked<Record<string, unknown> | EventSpecification>(modifiedPaidActionTracked, trackers);
}
/**
 * Tracks a PatientExtracted event specification.
 * ID: 43458dd4-a618-4907-8c79-fdbc0c86df65
 */
export function trackPatientExtractedSpec(patientExtracted: PatientExtracted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '43458dd4-a618-4907-8c79-fdbc0c86df65',
        version: 1,
        name: 'patient_extracted',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(patientExtracted.context)
        ? [...patientExtracted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientExtracted = {
        ...patientExtracted,
        context,
    };

    trackPatientExtracted<Record<string, unknown> | EventSpecification>(modifiedPatientExtracted, trackers);
}
/**
 * Tracks a UiInteraction event specification.
 * ID: 43de88a4-1bf0-4b89-9621-fb352659c0a4
 */
export function trackUiInteractionSpec(uiInteraction: UIInteraction & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '43de88a4-1bf0-4b89-9621-fb352659c0a4',
        version: 1,
        name: 'ui_interaction',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(uiInteraction.context)
        ? [...uiInteraction.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUiInteraction = {
        ...uiInteraction,
        context,
    };

    trackUIInteraction<Record<string, unknown> | EventSpecification>(modifiedUiInteraction, trackers);
}
/**
 * Tracks a PersonalizationDeleted event specification.
 * ID: 4406c22f-6ac8-4695-98f8-a40034a2d344
 */
export function trackPersonalizationDeletedSpec(personalizationDeleted: PersonalizationDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4406c22f-6ac8-4695-98f8-a40034a2d344',
        version: 1,
        name: 'personalization_deleted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(personalizationDeleted.context)
        ? [...personalizationDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPersonalizationDeleted = {
        ...personalizationDeleted,
        context,
    };

    trackPersonalizationDeleted<Record<string, unknown> | EventSpecification>(modifiedPersonalizationDeleted, trackers);
}
/**
 * Tracks a IntegrationRequested event specification.
 * ID: 44302dd3-49b0-44c2-af8e-c0969f6347b0
 */
export function trackIntegrationRequestedSpec(integrationRequested: IntegrationRequested & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '44302dd3-49b0-44c2-af8e-c0969f6347b0',
        version: 0,
        name: 'integration_requested',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationRequested.context)
        ? [...integrationRequested.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationRequested = {
        ...integrationRequested,
        context,
    };

    trackIntegrationRequested<Record<string, unknown> | EventSpecification>(modifiedIntegrationRequested, trackers);
}
/**
 * Tracks a PatientProfileTabViewed event specification.
 * ID: 44ac44f3-453d-49a7-b33b-3244bfd73619
 */
export function trackPatientProfileTabViewedSpec(patientProfileTabViewed: PatientProfileTabViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '44ac44f3-453d-49a7-b33b-3244bfd73619',
        version: 0,
        name: 'patient_profile_tab_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(patientProfileTabViewed.context)
        ? [...patientProfileTabViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientProfileTabViewed = {
        ...patientProfileTabViewed,
        context,
    };

    trackPatientProfileTabViewed<Record<string, unknown> | EventSpecification>(modifiedPatientProfileTabViewed, trackers);
}
/**
 * Tracks a FieldSelected event specification.
 * ID: 4523d20b-08ef-473f-b234-48d622f34e33
 */
export function trackFieldSelectedSpec(fieldSelected: FieldSelected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4523d20b-08ef-473f-b234-48d622f34e33',
        version: 5,
        name: 'field_selected',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(fieldSelected.context)
        ? [...fieldSelected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFieldSelected = {
        ...fieldSelected,
        context,
    };

    trackFieldSelected<Record<string, unknown> | EventSpecification>(modifiedFieldSelected, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a ChronicleDeviceSearchStarted event specification.
 * ID: 456a0502-3d06-44e2-9f0e-862d30b69fab
 */
export function trackChronicleDeviceSearchStartedSpec(chronicleDeviceSearchStarted: ChronicleDeviceSearchStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '456a0502-3d06-44e2-9f0e-862d30b69fab',
        version: 1,
        name: 'chronicle_device_search_started',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(chronicleDeviceSearchStarted.context)
        ? [...chronicleDeviceSearchStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChronicleDeviceSearchStarted = {
        ...chronicleDeviceSearchStarted,
        context,
    };

    trackChronicleDeviceSearchStarted<Record<string, unknown> | EventSpecification>(modifiedChronicleDeviceSearchStarted, trackers);
}
/**
 * Tracks a LetterAttachmentRejected event specification.
 * ID: 459491ef-a6d2-4f5f-854d-8cb2f59c9327
 */
export function trackLetterAttachmentRejectedSpec(letterAttachmentRejected: LetterAttachmentRejected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '459491ef-a6d2-4f5f-854d-8cb2f59c9327',
        version: 0,
        name: 'letter_attachment_rejected',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(letterAttachmentRejected.context)
        ? [...letterAttachmentRejected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLetterAttachmentRejected = {
        ...letterAttachmentRejected,
        context,
    };

    trackLetterAttachmentRejected<Record<string, unknown> | EventSpecification>(modifiedLetterAttachmentRejected, trackers);
}
/**
 * Tracks a OnboardingStepSkipped event specification.
 * ID: 463afd79-45ec-48dd-a69c-67310c324047
 */
export function trackOnboardingStepSkippedSpec(onboardingStepSkipped: OnboardingStepSkipped & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '463afd79-45ec-48dd-a69c-67310c324047',
        version: 1,
        name: 'onboarding_step_skipped',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(onboardingStepSkipped.context)
        ? [...onboardingStepSkipped.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOnboardingStepSkipped = {
        ...onboardingStepSkipped,
        context,
    };

    trackOnboardingStepSkipped<Record<string, unknown> | EventSpecification>(modifiedOnboardingStepSkipped, trackers);
}
/**
 * Tracks a TrialUpdated event specification.
 * ID: 4731d4f2-df7f-4c03-8f38-1b8a1fba2721
 */
export function trackTrialUpdatedSpec(trialUpdated: TrialUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4731d4f2-df7f-4c03-8f38-1b8a1fba2721',
        version: 0,
        name: 'trial_updated',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(trialUpdated.context)
        ? [...trialUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTrialUpdated = {
        ...trialUpdated,
        context,
    };

    trackTrialUpdated<Record<string, unknown> | EventSpecification>(modifiedTrialUpdated, trackers);
}
/**
 * Tracks a EvidenceLanguageChanged event specification.
 * ID: 4740428c-c31f-4421-9360-a0f0a8437c92
 */
export function trackEvidenceLanguageChangedSpec(evidenceLanguageChanged: EvidenceLanguageChanged & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4740428c-c31f-4421-9360-a0f0a8437c92',
        version: 1,
        name: 'evidence_language_changed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceLanguageChanged.context)
        ? [...evidenceLanguageChanged.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceLanguageChanged = {
        ...evidenceLanguageChanged,
        context,
    };

    trackEvidenceLanguageChanged<Record<string, unknown> | EventSpecification>(modifiedEvidenceLanguageChanged, trackers);
}
/**
 * Tracks a IntegrationPatientSelected event specification.
 * ID: 47e66180-5be5-4ebf-8ea0-b0e1fba49101
 */
export function trackIntegrationPatientSelectedSpec(integrationPatientSelected: IntegrationPatientSelected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '47e66180-5be5-4ebf-8ea0-b0e1fba49101',
        version: 1,
        name: 'integration_patient_selected',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationPatientSelected.context)
        ? [...integrationPatientSelected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationPatientSelected = {
        ...integrationPatientSelected,
        context,
    };

    trackIntegrationPatientSelected<Record<string, unknown> | EventSpecification>(modifiedIntegrationPatientSelected, trackers);
}
/**
 * Tracks a DesktopUpdateSkipped event specification.
 * ID: 4930aed2-9470-49d3-8a6c-cab55d206368
 */
export function trackDesktopUpdateSkippedSpec(desktopUpdateSkipped: DesktopUpdateSkipped & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4930aed2-9470-49d3-8a6c-cab55d206368',
        version: 0,
        name: 'desktop_update_skipped',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(desktopUpdateSkipped.context)
        ? [...desktopUpdateSkipped.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDesktopUpdateSkipped = {
        ...desktopUpdateSkipped,
        context,
    };

    trackDesktopUpdateSkipped<Record<string, unknown> | EventSpecification>(modifiedDesktopUpdateSkipped, trackers);
}
/**
 * Tracks a CallSummaryEmailed event specification.
 * ID: 49d208a8-1adb-47f4-8399-6bc08b193f3f
 */
export function trackCallSummaryEmailedSpec(callSummaryEmailed: CallSummaryEmailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '49d208a8-1adb-47f4-8399-6bc08b193f3f',
        version: 0,
        name: 'call_summary_emailed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callSummaryEmailed.context)
        ? [...callSummaryEmailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallSummaryEmailed = {
        ...callSummaryEmailed,
        context,
    };

    trackCallSummaryEmailed<Record<string, unknown> | EventSpecification>(modifiedCallSummaryEmailed, trackers);
}
/**
 * Tracks a CoachMarkViewed event specification.
 * ID: 49e6f581-8eaa-45a1-9abf-97f5ab43c9f1
 */
export function trackCoachMarkViewedSpec(coachMarkViewed: CoachMarkViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '49e6f581-8eaa-45a1-9abf-97f5ab43c9f1',
        version: 1,
        name: 'coach_mark_viewed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(coachMarkViewed.context)
        ? [...coachMarkViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCoachMarkViewed = {
        ...coachMarkViewed,
        context,
    };

    trackCoachMarkViewed<Record<string, unknown> | EventSpecification>(modifiedCoachMarkViewed, trackers);
}
/**
 * Tracks a DictationCompleted event specification.
 * ID: 4aae598c-945f-4e9a-aed6-6c5b62d85173
 */
export function trackDictationCompletedSpec(dictationCompleted: DictationCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4aae598c-945f-4e9a-aed6-6c5b62d85173',
        version: 3,
        name: 'dictation_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(dictationCompleted.context)
        ? [...dictationCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDictationCompleted = {
        ...dictationCompleted,
        context,
    };

    trackDictationCompleted<Record<string, unknown> | EventSpecification>(modifiedDictationCompleted, trackers);
}
/**
 * Tracks a ScribeStyleUpdated event specification.
 * ID: 4b9af8ba-439c-47e0-9f23-ea4df8fbbb36
 */
export function trackScribeStyleUpdatedSpec(scribeStyleUpdated: ScribeStyleUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4b9af8ba-439c-47e0-9f23-ea4df8fbbb36',
        version: 4,
        name: 'scribe_style_updated',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(scribeStyleUpdated.context)
        ? [...scribeStyleUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScribeStyleUpdated = {
        ...scribeStyleUpdated,
        context,
    };

    trackScribeStyleUpdated<Record<string, unknown> | EventSpecification>(modifiedScribeStyleUpdated, trackers);
}
/**
 * Tracks a CodeClicked event specification.
 * ID: 4b9c3ff4-1ac8-4d08-b899-cacbe6c8c906
 */
export function trackCodeClickedSpec(codeClicked: CodeClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4b9c3ff4-1ac8-4d08-b899-cacbe6c8c906',
        version: 1,
        name: 'code_clicked',
        data_product_id: '97404acb-340e-4ca4-bf39-5e42ff587704',
        data_product_name: 'revenue_cycle'
    });

    const context = Array.isArray(codeClicked.context)
        ? [...codeClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCodeClicked = {
        ...codeClicked,
        context,
    };

    trackCodeClicked<Record<string, unknown> | EventSpecification>(modifiedCodeClicked, trackers);
}
/**
 * Tracks a ToggleUpdated event specification.
 * ID: 4bba7be7-9635-47b3-9504-ac5316f67fa4
 */
export function trackToggleUpdatedSpec(toggleUpdated: ToggleUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4bba7be7-9635-47b3-9504-ac5316f67fa4',
        version: 3,
        name: 'toggle_updated',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(toggleUpdated.context)
        ? [...toggleUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedToggleUpdated = {
        ...toggleUpdated,
        context,
    };

    trackToggleUpdated<Record<string, unknown> | EventSpecification>(modifiedToggleUpdated, trackers);
}
/**
 * Tracks a ContextUnlinked event specification.
 * ID: 4be4168e-cb8c-484c-a2ce-7ee5727734c6
 */
export function trackContextUnlinkedSpec(contextUnlinked: ContextUnlinked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4be4168e-cb8c-484c-a2ce-7ee5727734c6',
        version: 4,
        name: 'context_unlinked',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(contextUnlinked.context)
        ? [...contextUnlinked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedContextUnlinked = {
        ...contextUnlinked,
        context,
    };

    trackContextUnlinked<Record<string, unknown> | EventSpecification>(modifiedContextUnlinked, trackers);
}
/**
 * Tracks a ShareExternalEmailAdded event specification.
 * ID: 4c18b260-f30d-48fb-b574-028238a26539
 */
export function trackShareExternalEmailAddedSpec(shareExternalEmailAdded: ShareExternalEmailAdded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4c18b260-f30d-48fb-b574-028238a26539',
        version: 1,
        name: 'share_external_email_added',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareExternalEmailAdded.context)
        ? [...shareExternalEmailAdded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareExternalEmailAdded = {
        ...shareExternalEmailAdded,
        context,
    };

    trackShareExternalEmailAdded<Record<string, unknown> | EventSpecification>(modifiedShareExternalEmailAdded, trackers);
}
/**
 * Tracks a TeamMemberUpdated event specification.
 * ID: 4c2f1228-e167-43cb-8632-7dcb99ab025b
 */
export function trackTeamMemberUpdatedSpec(teamMemberUpdated: TeamMemberUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4c2f1228-e167-43cb-8632-7dcb99ab025b',
        version: 0,
        name: 'team_member_updated',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(teamMemberUpdated.context)
        ? [...teamMemberUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTeamMemberUpdated = {
        ...teamMemberUpdated,
        context,
    };

    trackTeamMemberUpdated<Record<string, unknown> | EventSpecification>(modifiedTeamMemberUpdated, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a ChronicleFirmwareUpgradeFailed event specification.
 * ID: 4ce00a75-fad1-4a37-bff9-f41ed3fe27c7
 */
export function trackChronicleFirmwareUpgradeFailedSpec(chronicleFirmwareUpgradeFailed: ChronicleFirmwareUpgradeFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4ce00a75-fad1-4a37-bff9-f41ed3fe27c7',
        version: 1,
        name: 'chronicle_firmware_upgrade_failed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(chronicleFirmwareUpgradeFailed.context)
        ? [...chronicleFirmwareUpgradeFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChronicleFirmwareUpgradeFailed = {
        ...chronicleFirmwareUpgradeFailed,
        context,
    };

    trackChronicleFirmwareUpgradeFailed<Record<string, unknown> | EventSpecification>(modifiedChronicleFirmwareUpgradeFailed, trackers);
}
/**
 * Tracks a OrganisationCreated event specification.
 * ID: 4d120f8e-bb43-470d-8572-b50c86301253
 */
export function trackOrganisationCreatedSpec(organisationCreated: OrganisationCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4d120f8e-bb43-470d-8572-b50c86301253',
        version: 0,
        name: 'organisation_created',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(organisationCreated.context)
        ? [...organisationCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOrganisationCreated = {
        ...organisationCreated,
        context,
    };

    trackOrganisationCreated<Record<string, unknown> | EventSpecification>(modifiedOrganisationCreated, trackers);
}
/**
 * Tracks a LetterSendAttempted event specification.
 * ID: 4e00e9d2-7733-4c3e-a351-90c0a5738756
 */
export function trackLetterSendAttemptedSpec(letterSendAttempted: LetterSendAttempted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4e00e9d2-7733-4c3e-a351-90c0a5738756',
        version: 0,
        name: 'letter_send_attempted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(letterSendAttempted.context)
        ? [...letterSendAttempted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLetterSendAttempted = {
        ...letterSendAttempted,
        context,
    };

    trackLetterSendAttempted<Record<string, unknown> | EventSpecification>(modifiedLetterSendAttempted, trackers);
}
/**
 * Tracks a EvidenceResponseFeedbackSubmitted event specification.
 * ID: 4e9e83c1-72a6-4f37-b118-8b33bf34e662
 */
export function trackEvidenceResponseFeedbackSubmittedSpec(evidenceResponseFeedbackSubmitted: EvidenceResponseFeedbackSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4e9e83c1-72a6-4f37-b118-8b33bf34e662',
        version: 3,
        name: 'evidence_response_feedback_submitted',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceResponseFeedbackSubmitted.context)
        ? [...evidenceResponseFeedbackSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceResponseFeedbackSubmitted = {
        ...evidenceResponseFeedbackSubmitted,
        context,
    };

    trackEvidenceResponseFeedbackSubmitted<Record<string, unknown> | EventSpecification>(modifiedEvidenceResponseFeedbackSubmitted, trackers);
}
/**
 * Tracks a IntegrationCreated event specification.
 * ID: 4ecf65ca-d34f-4051-b777-4379b204dd8d
 */
export function trackIntegrationCreatedSpec(integrationCreated: IntegrationCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4ecf65ca-d34f-4051-b777-4379b204dd8d',
        version: 4,
        name: 'integration_created',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationCreated.context)
        ? [...integrationCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationCreated = {
        ...integrationCreated,
        context,
    };

    trackIntegrationCreated<Record<string, unknown> | EventSpecification>(modifiedIntegrationCreated, trackers);
}
/**
 * Tracks a ExperimentStarted event specification.
 * ID: 4f2af982-8165-4fab-afdb-fa0bea7f7f84
 */
export function trackExperimentStartedSpec(experimentStarted: ExperimentStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4f2af982-8165-4fab-afdb-fa0bea7f7f84',
        version: 4,
        name: 'experiment_started',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(experimentStarted.context)
        ? [...experimentStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedExperimentStarted = {
        ...experimentStarted,
        context,
    };

    trackExperimentStarted<Record<string, unknown> | EventSpecification>(modifiedExperimentStarted, trackers);
}
/**
 * Tracks a TemplateQuotaFailed event specification.
 * ID: 4f3f4e75-80a1-47a9-998d-12d17b5b44ce
 */
export function trackTemplateQuotaFailedSpec(templateQuotaFailed: TemplateQuotaFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4f3f4e75-80a1-47a9-998d-12d17b5b44ce',
        version: 2,
        name: 'template_quota_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateQuotaFailed.context)
        ? [...templateQuotaFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateQuotaFailed = {
        ...templateQuotaFailed,
        context,
    };

    trackTemplateQuotaFailed<Record<string, unknown> | EventSpecification>(modifiedTemplateQuotaFailed, trackers);
}
/**
 * Tracks a ClinicalCodeCopied event specification.
 * ID: 4fdf90ca-6cc6-4488-94cf-2b3f87dd435e
 */
export function trackClinicalCodeCopiedSpec(clinicalCodeCopied: ClinicalCodeCopied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '4fdf90ca-6cc6-4488-94cf-2b3f87dd435e',
        version: 1,
        name: 'clinical_code_copied',
        data_product_id: '97404acb-340e-4ca4-bf39-5e42ff587704',
        data_product_name: 'revenue_cycle'
    });

    const context = Array.isArray(clinicalCodeCopied.context)
        ? [...clinicalCodeCopied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedClinicalCodeCopied = {
        ...clinicalCodeCopied,
        context,
    };

    trackClinicalCodeCopied<Record<string, unknown> | EventSpecification>(modifiedClinicalCodeCopied, trackers);
}
/**
 * Tracks a UniversalDictationCsatDismissed event specification.
 * ID: 50352b92-eff5-4f16-a368-e55f0072ce82
 */
export function trackUniversalDictationCsatDismissedSpec(universalDictationCsatDismissed: UniversalDictationCsatDismissed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '50352b92-eff5-4f16-a368-e55f0072ce82',
        version: 1,
        name: 'universal_dictation_csat_dismissed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(universalDictationCsatDismissed.context)
        ? [...universalDictationCsatDismissed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUniversalDictationCsatDismissed = {
        ...universalDictationCsatDismissed,
        context,
    };

    trackUniversalDictationCsatDismissed<Record<string, unknown> | EventSpecification>(modifiedUniversalDictationCsatDismissed, trackers);
}
/**
 * Tracks a AutoLinkSessionsEligibilityEvaluated event specification.
 * ID: 50b06ffd-6506-4796-ae13-f30f5a3fca8e
 */
export function trackAutoLinkSessionsEligibilityEvaluatedSpec(autoLinkSessionsEligibilityEvaluated: AutoLinkSessionsEligibilityEvaluated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '50b06ffd-6506-4796-ae13-f30f5a3fca8e',
        version: 0,
        name: 'auto_link_sessions_eligibility_evaluated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(autoLinkSessionsEligibilityEvaluated.context)
        ? [...autoLinkSessionsEligibilityEvaluated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAutoLinkSessionsEligibilityEvaluated = {
        ...autoLinkSessionsEligibilityEvaluated,
        context,
    };

    trackAutoLinkSessionsEligibilityEvaluated<Record<string, unknown> | EventSpecification>(modifiedAutoLinkSessionsEligibilityEvaluated, trackers);
}
/**
 * Tracks a AuthManualOpenClicked event specification.
 * ID: 50cd75ed-6f07-49b9-8fa6-9ae9e078ae61
 */
export function trackAuthManualOpenClickedSpec(authManualOpenClicked: AuthManualOpenClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '50cd75ed-6f07-49b9-8fa6-9ae9e078ae61',
        version: 1,
        name: 'auth_manual_open_clicked',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(authManualOpenClicked.context)
        ? [...authManualOpenClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAuthManualOpenClicked = {
        ...authManualOpenClicked,
        context,
    };

    trackAuthManualOpenClicked<Record<string, unknown> | EventSpecification>(modifiedAuthManualOpenClicked, trackers);
}
/**
 * Tracks a DemoStarted event specification.
 * ID: 51102b95-f6f1-4e64-ba06-4ac5a7a220b2
 */
export function trackDemoStartedSpec(demoStarted: DemoStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '51102b95-f6f1-4e64-ba06-4ac5a7a220b2',
        version: 3,
        name: 'demo_started',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(demoStarted.context)
        ? [...demoStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDemoStarted = {
        ...demoStarted,
        context,
    };

    trackDemoStarted<Record<string, unknown> | EventSpecification>(modifiedDemoStarted, trackers);
}
/**
 * Tracks a WorkRunStarted event specification.
 * ID: 514b07cf-0473-5486-a6bc-2f8e9ece431b
 */
export function trackWorkRunStartedSpec(workRunStarted: WorkRunStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '514b07cf-0473-5486-a6bc-2f8e9ece431b',
        version: 2,
        name: 'work_run_started',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workRunStarted.context)
        ? [...workRunStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkRunStarted = {
        ...workRunStarted,
        context,
    };

    trackWorkRunStarted<Record<string, unknown> | EventSpecification>(modifiedWorkRunStarted, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a SessionStopped event specification.
 * ID: 52198e85-c295-4829-b348-4f6d538b073f
 */
export function trackSessionStoppedSpec(sessionStopped: SessionStopped & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '52198e85-c295-4829-b348-4f6d538b073f',
        version: 1,
        name: 'session_stopped',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionStopped.context)
        ? [...sessionStopped.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionStopped = {
        ...sessionStopped,
        context,
    };

    trackSessionStopped<Record<string, unknown> | EventSpecification>(modifiedSessionStopped, trackers);
}
/**
 * Tracks a LibraryDocumentUploaded event specification.
 * ID: 524a6450-a0e2-4022-9076-691f2bcb294f
 */
export function trackLibraryDocumentUploadedSpec(libraryDocumentUploaded: LibraryDocumentUploaded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '524a6450-a0e2-4022-9076-691f2bcb294f',
        version: 1,
        name: 'library_document_uploaded',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(libraryDocumentUploaded.context)
        ? [...libraryDocumentUploaded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLibraryDocumentUploaded = {
        ...libraryDocumentUploaded,
        context,
    };

    trackLibraryDocumentUploaded<Record<string, unknown> | EventSpecification>(modifiedLibraryDocumentUploaded, trackers);
}
/**
 * Tracks a FormFillFailed event specification.
 * ID: 5250db4a-08c3-4913-a41f-86883e68bdac
 */
export function trackFormFillFailedSpec(formFillFailed: FormFillFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '5250db4a-08c3-4913-a41f-86883e68bdac',
        version: 0,
        name: 'form_fill_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(formFillFailed.context)
        ? [...formFillFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFormFillFailed = {
        ...formFillFailed,
        context,
    };

    trackFormFillFailed<Record<string, unknown> | EventSpecification>(modifiedFormFillFailed, trackers);
}
/**
 * Tracks a EvidenceSummarySuggestionGenerated event specification.
 * ID: 526a739e-93dd-4845-a291-c0bc3553b115
 */
export function trackEvidenceSummarySuggestionGeneratedSpec(evidenceSummarySuggestionGenerated: EvidenceSummarySuggestionGenerated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '526a739e-93dd-4845-a291-c0bc3553b115',
        version: 0,
        name: 'evidence_summary_suggestion_generated',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceSummarySuggestionGenerated.context)
        ? [...evidenceSummarySuggestionGenerated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceSummarySuggestionGenerated = {
        ...evidenceSummarySuggestionGenerated,
        context,
    };

    trackEvidenceSummarySuggestionGenerated<Record<string, unknown> | EventSpecification>(modifiedEvidenceSummarySuggestionGenerated, trackers);
}
/**
 * Tracks a AccountCreated event specification.
 * ID: 52f97db1-43ef-4ca6-b0a4-1b25bb88b5cd
 */
export function trackAccountCreatedSpec(accountCreated: AccountCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '52f97db1-43ef-4ca6-b0a4-1b25bb88b5cd',
        version: 3,
        name: 'account_created',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(accountCreated.context)
        ? [...accountCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAccountCreated = {
        ...accountCreated,
        context,
    };

    trackAccountCreated<Record<string, unknown> | EventSpecification>(modifiedAccountCreated, trackers);
}
/**
 * Tracks a EvidenceExperienceFeedbackSubmitted event specification.
 * ID: 539ef014-736a-431e-b361-1d944769e5aa
 */
export function trackEvidenceExperienceFeedbackSubmittedSpec(evidenceExperienceFeedbackSubmitted: EvidenceExperienceFeedbackSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '539ef014-736a-431e-b361-1d944769e5aa',
        version: 3,
        name: 'evidence_experience_feedback_submitted',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceExperienceFeedbackSubmitted.context)
        ? [...evidenceExperienceFeedbackSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceExperienceFeedbackSubmitted = {
        ...evidenceExperienceFeedbackSubmitted,
        context,
    };

    trackEvidenceExperienceFeedbackSubmitted<Record<string, unknown> | EventSpecification>(modifiedEvidenceExperienceFeedbackSubmitted, trackers);
}
/**
 * Tracks a MessageAborted event specification.
 * ID: 53a97e99-3a74-4ab3-9b90-c0820fd8a694
 */
export function trackMessageAbortedSpec(messageAborted: MessageAborted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '53a97e99-3a74-4ab3-9b90-c0820fd8a694',
        version: 4,
        name: 'message_aborted',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(messageAborted.context)
        ? [...messageAborted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedMessageAborted = {
        ...messageAborted,
        context,
    };

    trackMessageAborted<Record<string, unknown> | EventSpecification>(modifiedMessageAborted, trackers);
}
/**
 * Tracks a ChatContextAttachPatientClicked event specification.
 * ID: 54a657c2-cd10-4499-9bda-5aaf266660a0
 */
export function trackChatContextAttachPatientClickedSpec(chatContextAttachPatientClicked: ChatContextAttachPatientClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '54a657c2-cd10-4499-9bda-5aaf266660a0',
        version: 1,
        name: 'chat_context_attach_patient_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(chatContextAttachPatientClicked.context)
        ? [...chatContextAttachPatientClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChatContextAttachPatientClicked = {
        ...chatContextAttachPatientClicked,
        context,
    };

    trackChatContextAttachPatientClicked<Record<string, unknown> | EventSpecification>(modifiedChatContextAttachPatientClicked, trackers);
}
/**
 * Tracks a ToolCallCompleted event specification.
 * ID: 5510874f-a8cf-4fd3-989a-696121dc9e3a
 */
export function trackToolCallCompletedSpec(toolCallCompleted: ToolCallCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '5510874f-a8cf-4fd3-989a-696121dc9e3a',
        version: 1,
        name: 'tool_call_completed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(toolCallCompleted.context)
        ? [...toolCallCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedToolCallCompleted = {
        ...toolCallCompleted,
        context,
    };

    trackToolCallCompleted<Record<string, unknown> | EventSpecification>(modifiedToolCallCompleted, trackers);
}
/**
 * Tracks a SessionsScopeChanged event specification.
 * ID: 552c4746-37a5-48b0-9be9-025237955028
 */
export function trackSessionsScopeChangedSpec(sessionsScopeChanged: SessionsScopeChanged & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '552c4746-37a5-48b0-9be9-025237955028',
        version: 0,
        name: 'sessions_scope_changed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionsScopeChanged.context)
        ? [...sessionsScopeChanged.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionsScopeChanged = {
        ...sessionsScopeChanged,
        context,
    };

    trackSessionsScopeChanged<Record<string, unknown> | EventSpecification>(modifiedSessionsScopeChanged, trackers);
}
/**
 * Tracks a DarkModeSpotlightShown event specification.
 * ID: 55b1edbe-2647-4e64-b8dd-60440da702de
 */
export function trackDarkModeSpotlightShownSpec(darkModeSpotlightShown: DarkModeSpotlightShown & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '55b1edbe-2647-4e64-b8dd-60440da702de',
        version: 0,
        name: 'dark_mode_spotlight_shown',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(darkModeSpotlightShown.context)
        ? [...darkModeSpotlightShown.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDarkModeSpotlightShown = {
        ...darkModeSpotlightShown,
        context,
    };

    trackDarkModeSpotlightShown<Record<string, unknown> | EventSpecification>(modifiedDarkModeSpotlightShown, trackers);
}
/**
 * Tracks a CallNoAnswer event specification.
 * ID: 55e652de-474a-4293-ae1e-edaee8ea74d7
 */
export function trackCallNoAnswerSpec(callNoAnswer: CallNoAnswer & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '55e652de-474a-4293-ae1e-edaee8ea74d7',
        version: 0,
        name: 'call_no_answer',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callNoAnswer.context)
        ? [...callNoAnswer.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallNoAnswer = {
        ...callNoAnswer,
        context,
    };

    trackCallNoAnswer<Record<string, unknown> | EventSpecification>(modifiedCallNoAnswer, trackers);
}
/**
 * Tracks a TranslatorTranslationRated event specification.
 * ID: 55e98ec4-e219-4ce9-87df-fd017d777e92
 */
export function trackTranslatorTranslationRatedSpec(translatorTranslationRated: TranslatorTranslationRated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '55e98ec4-e219-4ce9-87df-fd017d777e92',
        version: 3,
        name: 'translator_translation_rated',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(translatorTranslationRated.context)
        ? [...translatorTranslationRated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTranslatorTranslationRated = {
        ...translatorTranslationRated,
        context,
    };

    trackTranslatorTranslationRated<Record<string, unknown> | EventSpecification>(modifiedTranslatorTranslationRated, trackers);
}
/**
 * Tracks a ScheduleCompleted event specification.
 * ID: 56529044-76df-477f-9839-7b61a2a5f0b9
 */
export function trackScheduleCompletedSpec(scheduleCompleted: ScheduleCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '56529044-76df-477f-9839-7b61a2a5f0b9',
        version: 1,
        name: 'schedule_completed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(scheduleCompleted.context)
        ? [...scheduleCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScheduleCompleted = {
        ...scheduleCompleted,
        context,
    };

    trackScheduleCompleted<Record<string, unknown> | EventSpecification>(modifiedScheduleCompleted, trackers);
}
/**
 * Tracks a NudgeProtocolsToggled event specification.
 * ID: 56569989-aca8-4e11-8aa3-b196963e1f88
 */
export function trackNudgeProtocolsToggledSpec(nudgeProtocolsToggled: NudgeProtocolsToggled & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '56569989-aca8-4e11-8aa3-b196963e1f88',
        version: 0,
        name: 'nudge_protocols_toggled',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(nudgeProtocolsToggled.context)
        ? [...nudgeProtocolsToggled.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNudgeProtocolsToggled = {
        ...nudgeProtocolsToggled,
        context,
    };

    trackNudgeProtocolsToggled<Record<string, unknown> | EventSpecification>(modifiedNudgeProtocolsToggled, trackers);
}
/**
 * Tracks a SignInSuccessful event specification.
 * ID: 56ff0565-0cc3-4b9c-9ae9-223ca70e5a63
 */
export function trackSignInSuccessfulSpec(signInSuccessful: SignInSuccessful & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '56ff0565-0cc3-4b9c-9ae9-223ca70e5a63',
        version: 1,
        name: 'sign_in_successful',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(signInSuccessful.context)
        ? [...signInSuccessful.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSignInSuccessful = {
        ...signInSuccessful,
        context,
    };

    trackSignInSuccessful<Record<string, unknown> | EventSpecification>(modifiedSignInSuccessful, trackers);
}
/**
 * Tracks a SofLaunchError event specification.
 * ID: 572608a1-1ebc-4dd9-8985-44b94e56eefd
 */
export function trackSofLaunchErrorSpec(sofLaunchError: SofLaunchError & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '572608a1-1ebc-4dd9-8985-44b94e56eefd',
        version: 1,
        name: 'sof_launch_error',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(sofLaunchError.context)
        ? [...sofLaunchError.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSofLaunchError = {
        ...sofLaunchError,
        context,
    };

    trackSofLaunchError<Record<string, unknown> | EventSpecification>(modifiedSofLaunchError, trackers);
}
/**
 * Tracks a DocumentSynced event specification.
 * ID: 57454905-5c4d-4364-a4b5-2937d3009b60
 */
export function trackDocumentSyncedSpec(documentSynced: DocumentSynced & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '57454905-5c4d-4364-a4b5-2937d3009b60',
        version: 1,
        name: 'document_synced',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(documentSynced.context)
        ? [...documentSynced.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDocumentSynced = {
        ...documentSynced,
        context,
    };

    trackDocumentSynced<Record<string, unknown> | EventSpecification>(modifiedDocumentSynced, trackers);
}
/**
 * Tracks a TranslatorBulkUploadCompleted event specification.
 * ID: 57e6b26b-4b16-48e4-b0f6-8a1151ce7622
 */
export function trackTranslatorBulkUploadCompletedSpec(translatorBulkUploadCompleted: TranslatorBulkUploadCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '57e6b26b-4b16-48e4-b0f6-8a1151ce7622',
        version: 0,
        name: 'translator_bulk_upload_completed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(translatorBulkUploadCompleted.context)
        ? [...translatorBulkUploadCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTranslatorBulkUploadCompleted = {
        ...translatorBulkUploadCompleted,
        context,
    };

    trackTranslatorBulkUploadCompleted<Record<string, unknown> | EventSpecification>(modifiedTranslatorBulkUploadCompleted, trackers);
}
/**
 * Tracks a EvidenceOnboardingViewed event specification.
 * ID: 57f57142-5b74-4cd2-93eb-6eadedd54233
 */
export function trackEvidenceOnboardingViewedSpec(evidenceOnboardingViewed: EvidenceOnboardingViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '57f57142-5b74-4cd2-93eb-6eadedd54233',
        version: 1,
        name: 'evidence_onboarding_viewed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceOnboardingViewed.context)
        ? [...evidenceOnboardingViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceOnboardingViewed = {
        ...evidenceOnboardingViewed,
        context,
    };

    trackEvidenceOnboardingViewed<Record<string, unknown> | EventSpecification>(modifiedEvidenceOnboardingViewed, trackers);
}
/**
 * Tracks a PatientCreated event specification.
 * ID: 58296e4f-ad2c-4766-b5f8-1e73ec9be321
 */
export function trackPatientCreatedSpec(patientCreated: PatientCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '58296e4f-ad2c-4766-b5f8-1e73ec9be321',
        version: 5,
        name: 'patient_created',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(patientCreated.context)
        ? [...patientCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientCreated = {
        ...patientCreated,
        context,
    };

    trackPatientCreated<Record<string, unknown> | EventSpecification>(modifiedPatientCreated, trackers);
}
/**
 * Tracks a NoteUpdated event specification.
 * ID: 583d5a13-7c79-4fcb-89f9-dcda2dd919f1
 */
export function trackNoteUpdatedSpec(noteUpdated: NoteUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '583d5a13-7c79-4fcb-89f9-dcda2dd919f1',
        version: 4,
        name: 'note_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteUpdated.context)
        ? [...noteUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteUpdated = {
        ...noteUpdated,
        context,
    };

    trackNoteUpdated<Record<string, unknown> | EventSpecification>(modifiedNoteUpdated, trackers);
}
/**
 * Tracks a NoteEditorRendered event specification.
 * ID: 584990e2-28f6-41fc-817e-f4f06e9cdfe9
 */
export function trackNoteEditorRenderedSpec(noteEditorRendered: NoteEditorRendered & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '584990e2-28f6-41fc-817e-f4f06e9cdfe9',
        version: 1,
        name: 'note_editor_rendered',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteEditorRendered.context)
        ? [...noteEditorRendered.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteEditorRendered = {
        ...noteEditorRendered,
        context,
    };

    trackNoteEditorRendered<Record<string, unknown> | EventSpecification>(modifiedNoteEditorRendered, trackers);
}
/**
 * Tracks a CallFailed event specification.
 * ID: 586199bc-248c-446f-8cb8-3d6036b8e0c0
 */
export function trackCallFailedSpec(callFailed: CallFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '586199bc-248c-446f-8cb8-3d6036b8e0c0',
        version: 0,
        name: 'call_failed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callFailed.context)
        ? [...callFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallFailed = {
        ...callFailed,
        context,
    };

    trackCallFailed<Record<string, unknown> | EventSpecification>(modifiedCallFailed, trackers);
}
/**
 * Tracks a NotificationUpdated event specification.
 * ID: 589b3a58-e053-4a98-b825-9ff085136ca3
 */
export function trackNotificationUpdatedSpec(notificationUpdated: NotificationUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '589b3a58-e053-4a98-b825-9ff085136ca3',
        version: 1,
        name: 'notification_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(notificationUpdated.context)
        ? [...notificationUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNotificationUpdated = {
        ...notificationUpdated,
        context,
    };

    trackNotificationUpdated<Record<string, unknown> | EventSpecification>(modifiedNotificationUpdated, trackers);
}
/**
 * Tracks a FirstSessionCompleted event specification.
 * ID: 58c23f49-9a52-4ca1-ac82-788e832bca53
 */
export function trackFirstSessionCompletedSpec(firstSessionCompleted: FirstSessionCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '58c23f49-9a52-4ca1-ac82-788e832bca53',
        version: 1,
        name: 'first_session_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(firstSessionCompleted.context)
        ? [...firstSessionCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFirstSessionCompleted = {
        ...firstSessionCompleted,
        context,
    };

    trackFirstSessionCompleted<Record<string, unknown> | EventSpecification>(modifiedFirstSessionCompleted, trackers);
}
/**
 * Tracks a EvidenceOnboardingSaved event specification.
 * ID: 58cd32b9-54b7-4ff2-96e1-3c9f2514b2c2
 */
export function trackEvidenceOnboardingSavedSpec(evidenceOnboardingSaved: EvidenceOnboardingSaved & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '58cd32b9-54b7-4ff2-96e1-3c9f2514b2c2',
        version: 1,
        name: 'evidence_onboarding_saved',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceOnboardingSaved.context)
        ? [...evidenceOnboardingSaved.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceOnboardingSaved = {
        ...evidenceOnboardingSaved,
        context,
    };

    trackEvidenceOnboardingSaved<Record<string, unknown> | EventSpecification>(modifiedEvidenceOnboardingSaved, trackers);
}
/**
 * Tracks a VerificationFailed event specification.
 * ID: 59587bc3-027a-4573-9884-dc9d4d091703
 */
export function trackVerificationFailedSpec(verificationFailed: VerificationFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '59587bc3-027a-4573-9884-dc9d4d091703',
        version: 1,
        name: 'verification_failed',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(verificationFailed.context)
        ? [...verificationFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedVerificationFailed = {
        ...verificationFailed,
        context,
    };

    trackVerificationFailed<Record<string, unknown> | EventSpecification>(modifiedVerificationFailed, trackers);
}
/**
 * Tracks a ChatDeleted event specification.
 * ID: 597a8ff7-6269-424c-b5f6-ffbcb793b3af
 */
export function trackChatDeletedSpec(chatDeleted: ChatDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '597a8ff7-6269-424c-b5f6-ffbcb793b3af',
        version: 1,
        name: 'chat_deleted',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(chatDeleted.context)
        ? [...chatDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChatDeleted = {
        ...chatDeleted,
        context,
    };

    trackChatDeleted<Record<string, unknown> | EventSpecification>(modifiedChatDeleted, trackers);
}
/**
 * Tracks a TaskCompleted event specification.
 * ID: 599031f2-bcf6-418d-bb1d-092a971f5489
 */
export function trackTaskCompletedSpec(taskCompleted: TaskCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '599031f2-bcf6-418d-bb1d-092a971f5489',
        version: 3,
        name: 'task_completed',
        data_product_id: '521bdabc-e43c-414b-a3d8-af7f5bdfb182',
        data_product_name: 'multiplayer'
    });

    const context = Array.isArray(taskCompleted.context)
        ? [...taskCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTaskCompleted = {
        ...taskCompleted,
        context,
    };

    trackTaskCompleted<Record<string, unknown> | EventSpecification>(modifiedTaskCompleted, trackers);
}
/**
 * Tracks a SubscriptionUpdated event specification.
 * ID: 59b15c12-e839-44b6-a0d1-ec376171a80a
 */
export function trackSubscriptionUpdatedSpec(subscriptionUpdated: SubscriptionUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '59b15c12-e839-44b6-a0d1-ec376171a80a',
        version: 3,
        name: 'subscription_updated',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(subscriptionUpdated.context)
        ? [...subscriptionUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSubscriptionUpdated = {
        ...subscriptionUpdated,
        context,
    };

    trackSubscriptionUpdated<Record<string, unknown> | EventSpecification>(modifiedSubscriptionUpdated, trackers);
}
/**
 * Tracks a PaymentStarted event specification.
 * ID: 5a06cfab-c8dc-4536-882f-f26a2ae37c4b
 */
export function trackPaymentStartedSpec(paymentStarted: PaymentStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '5a06cfab-c8dc-4536-882f-f26a2ae37c4b',
        version: 4,
        name: 'payment_started',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(paymentStarted.context)
        ? [...paymentStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPaymentStarted = {
        ...paymentStarted,
        context,
    };

    trackPaymentStarted<Record<string, unknown> | EventSpecification>(modifiedPaymentStarted, trackers);
}
/**
 * Tracks a ContextFileUploadStarted event specification.
 * ID: 5aa6b874-4532-4744-b572-43ba47dbb6d6
 */
export function trackContextFileUploadStartedSpec(contextFileUploadStarted: ContextFileUploadStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '5aa6b874-4532-4744-b572-43ba47dbb6d6',
        version: 1,
        name: 'context_file_upload_started',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(contextFileUploadStarted.context)
        ? [...contextFileUploadStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedContextFileUploadStarted = {
        ...contextFileUploadStarted,
        context,
    };

    trackContextFileUploadStarted<Record<string, unknown> | EventSpecification>(modifiedContextFileUploadStarted, trackers);
}
/**
 * Tracks a SessionCreated event specification.
 * ID: 5afad75a-f499-4b71-951e-520461bd5db5
 */
export function trackSessionCreatedSpec(sessionCreated: SessionCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '5afad75a-f499-4b71-951e-520461bd5db5',
        version: 5,
        name: 'session_created',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionCreated.context)
        ? [...sessionCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionCreated = {
        ...sessionCreated,
        context,
    };

    trackSessionCreated<Record<string, unknown> | EventSpecification>(modifiedSessionCreated, trackers);
}
/**
 * Tracks a PatientUnlinked event specification.
 * ID: 5ba3c9d2-1bdd-4263-9ff0-1dde12c806c9
 */
export function trackPatientUnlinkedSpec(patientUnlinked: PatientUnlinked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '5ba3c9d2-1bdd-4263-9ff0-1dde12c806c9',
        version: 4,
        name: 'patient_unlinked',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(patientUnlinked.context)
        ? [...patientUnlinked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientUnlinked = {
        ...patientUnlinked,
        context,
    };

    trackPatientUnlinked<Record<string, unknown> | EventSpecification>(modifiedPatientUnlinked, trackers);
}
/**
 * Tracks a SpeakerAttributionUpdated event specification.
 * ID: 5bf859b7-ff0b-4f5b-84a7-b44714ff86c1
 */
export function trackSpeakerAttributionUpdatedSpec(speakerAttributionUpdated: SpeakerAttributionUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '5bf859b7-ff0b-4f5b-84a7-b44714ff86c1',
        version: 3,
        name: 'speaker_attribution_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(speakerAttributionUpdated.context)
        ? [...speakerAttributionUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSpeakerAttributionUpdated = {
        ...speakerAttributionUpdated,
        context,
    };

    trackSpeakerAttributionUpdated<Record<string, unknown> | EventSpecification>(modifiedSpeakerAttributionUpdated, trackers);
}
/**
 * Tracks a SourceRecommendationShown event specification.
 * ID: 5c0aa138-e9e0-4275-b5cd-17a7e72bec9d
 */
export function trackSourceRecommendationShownSpec(sourceRecommendationShown: SourceRecommendationShown & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '5c0aa138-e9e0-4275-b5cd-17a7e72bec9d',
        version: 1,
        name: 'source_recommendation_shown',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(sourceRecommendationShown.context)
        ? [...sourceRecommendationShown.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSourceRecommendationShown = {
        ...sourceRecommendationShown,
        context,
    };

    trackSourceRecommendationShown<Record<string, unknown> | EventSpecification>(modifiedSourceRecommendationShown, trackers);
}
/**
 * Tracks a PrechartSentToContext event specification.
 * ID: 5c54f491-2ca1-4175-80cc-34643893ff46
 */
export function trackPrechartSentToContextSpec(prechartSentToContext: PrechartSentToContext & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '5c54f491-2ca1-4175-80cc-34643893ff46',
        version: 0,
        name: 'prechart_sent_to_context',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(prechartSentToContext.context)
        ? [...prechartSentToContext.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPrechartSentToContext = {
        ...prechartSentToContext,
        context,
    };

    trackPrechartSentToContext<Record<string, unknown> | EventSpecification>(modifiedPrechartSentToContext, trackers);
}
/**
 * Tracks a MessageStopped event specification.
 * ID: 5dd51299-100e-495f-b4f2-4801ae33e471
 */
export function trackMessageStoppedSpec(messageStopped: MessageStopped & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '5dd51299-100e-495f-b4f2-4801ae33e471',
        version: 2,
        name: 'message_stopped',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(messageStopped.context)
        ? [...messageStopped.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedMessageStopped = {
        ...messageStopped,
        context,
    };

    trackMessageStopped<Record<string, unknown> | EventSpecification>(modifiedMessageStopped, trackers);
}
/**
 * Tracks a ScheduleSynced event specification.
 * ID: 5e42f556-9f67-42de-bd38-dff50122dd39
 */
export function trackScheduleSyncedSpec(scheduleSynced: ScheduleSynced & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '5e42f556-9f67-42de-bd38-dff50122dd39',
        version: 1,
        name: 'schedule_synced',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(scheduleSynced.context)
        ? [...scheduleSynced.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScheduleSynced = {
        ...scheduleSynced,
        context,
    };

    trackScheduleSynced<Record<string, unknown> | EventSpecification>(modifiedScheduleSynced, trackers);
}
/**
 * Tracks a SnippetUpdated event specification.
 * ID: 5e6dcc38-9a64-42a0-8c5d-cedff397b53e
 */
export function trackSnippetUpdatedSpec(snippetUpdated: SnippetUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '5e6dcc38-9a64-42a0-8c5d-cedff397b53e',
        version: 1,
        name: 'snippet_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(snippetUpdated.context)
        ? [...snippetUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSnippetUpdated = {
        ...snippetUpdated,
        context,
    };

    trackSnippetUpdated<Record<string, unknown> | EventSpecification>(modifiedSnippetUpdated, trackers);
}
/**
 * Tracks a NoteGenerationStarted event specification.
 * ID: 5f08c1ec-4e94-4691-b6d3-61efd5c69015
 */
export function trackNoteGenerationStartedSpec(noteGenerationStarted: NoteGenerationStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '5f08c1ec-4e94-4691-b6d3-61efd5c69015',
        version: 1,
        name: 'note_generation_started',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteGenerationStarted.context)
        ? [...noteGenerationStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteGenerationStarted = {
        ...noteGenerationStarted,
        context,
    };

    trackNoteGenerationStarted<Record<string, unknown> | EventSpecification>(modifiedNoteGenerationStarted, trackers);
}
/**
 * Tracks a BookDemoCountrySelected event specification.
 * ID: 601aaba9-42e4-483c-b327-362231064cda
 */
export function trackBookDemoCountrySelectedSpec(bookDemoCountrySelected: BookDemoCountrySelected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '601aaba9-42e4-483c-b327-362231064cda',
        version: 1,
        name: 'book_demo_country_selected',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(bookDemoCountrySelected.context)
        ? [...bookDemoCountrySelected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedBookDemoCountrySelected = {
        ...bookDemoCountrySelected,
        context,
    };

    trackBookDemoCountrySelected<Record<string, unknown> | EventSpecification>(modifiedBookDemoCountrySelected, trackers);
}
/**
 * Tracks a TranscriptionCompleted event specification.
 * ID: 60238042-75ed-45fb-bbe4-ac7265ffda7d
 */
export function trackTranscriptionCompletedSpec(transcriptionCompleted: TranscriptionCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '60238042-75ed-45fb-bbe4-ac7265ffda7d',
        version: 1,
        name: 'transcription_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(transcriptionCompleted.context)
        ? [...transcriptionCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTranscriptionCompleted = {
        ...transcriptionCompleted,
        context,
    };

    trackTranscriptionCompleted<Record<string, unknown> | EventSpecification>(modifiedTranscriptionCompleted, trackers);
}
/**
 * Tracks a OfflineSyncFailed event specification.
 * ID: 6085ab6c-551d-4a25-834e-3c8aafdd87fc
 */
export function trackOfflineSyncFailedSpec(offlineSyncFailed: OfflineSyncFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6085ab6c-551d-4a25-834e-3c8aafdd87fc',
        version: 0,
        name: 'offline_sync_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(offlineSyncFailed.context)
        ? [...offlineSyncFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOfflineSyncFailed = {
        ...offlineSyncFailed,
        context,
    };

    trackOfflineSyncFailed<Record<string, unknown> | EventSpecification>(modifiedOfflineSyncFailed, trackers);
}
/**
 * Tracks a SurveyUpdated event specification.
 * ID: 614a38bb-8061-4371-878b-ce7651687fef
 */
export function trackSurveyUpdatedSpec(surveyUpdated: SurveyUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '614a38bb-8061-4371-878b-ce7651687fef',
        version: 1,
        name: 'survey_updated',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(surveyUpdated.context)
        ? [...surveyUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSurveyUpdated = {
        ...surveyUpdated,
        context,
    };

    trackSurveyUpdated<Record<string, unknown> | EventSpecification>(modifiedSurveyUpdated, trackers);
}
/**
 * Tracks a CursorDictationPostProcessingFailed event specification.
 * ID: 617460c3-eabc-4265-a12a-c47685d97a31
 */
export function trackCursorDictationPostProcessingFailedSpec(cursorDictationPostProcessingFailed: CursorDictationPostProcessingFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '617460c3-eabc-4265-a12a-c47685d97a31',
        version: 2,
        name: 'cursor_dictation_post_processing_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(cursorDictationPostProcessingFailed.context)
        ? [...cursorDictationPostProcessingFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCursorDictationPostProcessingFailed = {
        ...cursorDictationPostProcessingFailed,
        context,
    };

    trackCursorDictationPostProcessingFailed<Record<string, unknown> | EventSpecification>(modifiedCursorDictationPostProcessingFailed, trackers);
}
/**
 * Tracks a LibraryLinkClicked event specification.
 * ID: 61b52948-ceb4-4625-a5b5-93c33e142eca
 */
export function trackLibraryLinkClickedSpec(libraryLinkClicked: LibraryLinkClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '61b52948-ceb4-4625-a5b5-93c33e142eca',
        version: 1,
        name: 'library_link_clicked',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(libraryLinkClicked.context)
        ? [...libraryLinkClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLibraryLinkClicked = {
        ...libraryLinkClicked,
        context,
    };

    trackLibraryLinkClicked<Record<string, unknown> | EventSpecification>(modifiedLibraryLinkClicked, trackers);
}
/**
 * Tracks a TrialUpgradeClicked event specification.
 * ID: 631f8776-cf1f-4547-bac6-2121871eba31
 */
export function trackTrialUpgradeClickedSpec(trialUpgradeClicked: TrialUpgradeClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '631f8776-cf1f-4547-bac6-2121871eba31',
        version: 3,
        name: 'trial_upgrade_clicked',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(trialUpgradeClicked.context)
        ? [...trialUpgradeClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTrialUpgradeClicked = {
        ...trialUpgradeClicked,
        context,
    };

    trackTrialUpgradeClicked<Record<string, unknown> | EventSpecification>(modifiedTrialUpgradeClicked, trackers);
}
/**
 * Tracks a EvidenceChatCompleted event specification.
 * ID: 6393cea5-a3ad-4273-bdcd-a64c223c4ac9
 */
export function trackEvidenceChatCompletedSpec(evidenceChatCompleted: EvidenceChatCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6393cea5-a3ad-4273-bdcd-a64c223c4ac9',
        version: 6,
        name: 'evidence_chat_completed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceChatCompleted.context)
        ? [...evidenceChatCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceChatCompleted = {
        ...evidenceChatCompleted,
        context,
    };

    trackEvidenceChatCompleted<Record<string, unknown> | EventSpecification>(modifiedEvidenceChatCompleted, trackers);
}
/**
 * Tracks a AuthenticationCompleted event specification.
 * ID: 63abed00-f7b1-4f56-9e17-a42abee2755c
 */
export function trackAuthenticationCompletedSpec(authenticationCompleted: AuthenticationCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '63abed00-f7b1-4f56-9e17-a42abee2755c',
        version: 4,
        name: 'authentication_completed',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(authenticationCompleted.context)
        ? [...authenticationCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAuthenticationCompleted = {
        ...authenticationCompleted,
        context,
    };

    trackAuthenticationCompleted<Record<string, unknown> | EventSpecification>(modifiedAuthenticationCompleted, trackers);
}
/**
 * Tracks a ScheduleUploadPromoViewed event specification.
 * ID: 641ba696-22c2-4793-bcc3-67de5cc27ab3
 */
export function trackScheduleUploadPromoViewedSpec(scheduleUploadPromoViewed: ScheduleUploadPromoViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '641ba696-22c2-4793-bcc3-67de5cc27ab3',
        version: 1,
        name: 'schedule_upload_promo_viewed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(scheduleUploadPromoViewed.context)
        ? [...scheduleUploadPromoViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScheduleUploadPromoViewed = {
        ...scheduleUploadPromoViewed,
        context,
    };

    trackScheduleUploadPromoViewed<Record<string, unknown> | EventSpecification>(modifiedScheduleUploadPromoViewed, trackers);
}
/**
 * Tracks a TemplateViewed event specification.
 * ID: 6420d2d8-729f-4be6-ac2f-75324f4429dd
 */
export function trackTemplateViewedSpec(templateViewed: TemplateViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6420d2d8-729f-4be6-ac2f-75324f4429dd',
        version: 2,
        name: 'template_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateViewed.context)
        ? [...templateViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateViewed = {
        ...templateViewed,
        context,
    };

    trackTemplateViewed<Record<string, unknown> | EventSpecification>(modifiedTemplateViewed, trackers);
}
/**
 * Tracks a WorkMessageSubmitted event specification.
 * ID: 648627b5-244a-5bc1-9d9f-712e218be751
 */
export function trackWorkMessageSubmittedSpec(workMessageSubmitted: WorkMessageSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '648627b5-244a-5bc1-9d9f-712e218be751',
        version: 2,
        name: 'work_message_submitted',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workMessageSubmitted.context)
        ? [...workMessageSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkMessageSubmitted = {
        ...workMessageSubmitted,
        context,
    };

    trackWorkMessageSubmitted<Record<string, unknown> | EventSpecification>(modifiedWorkMessageSubmitted, trackers);
}
/**
 * Tracks a TemplateCreationRecordCreated event specification.
 * ID: 655b1947-100c-4f25-857f-aa89ddb9fb35
 */
export function trackTemplateCreationRecordCreatedSpec(templateCreationRecordCreated: TemplateCreationRecordCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '655b1947-100c-4f25-857f-aa89ddb9fb35',
        version: 1,
        name: 'template_creation_record_created',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateCreationRecordCreated.context)
        ? [...templateCreationRecordCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateCreationRecordCreated = {
        ...templateCreationRecordCreated,
        context,
    };

    trackTemplateCreationRecordCreated<Record<string, unknown> | EventSpecification>(modifiedTemplateCreationRecordCreated, trackers);
}
/**
 * Tracks a ScheduledCallSkipped event specification.
 * ID: 6657f5d4-19c6-4748-b6e9-27eec326f6e2
 */
export function trackScheduledCallSkippedSpec(scheduledCallSkipped: ScheduledCallSkipped & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6657f5d4-19c6-4748-b6e9-27eec326f6e2',
        version: 0,
        name: 'scheduled_call_skipped',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(scheduledCallSkipped.context)
        ? [...scheduledCallSkipped.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScheduledCallSkipped = {
        ...scheduledCallSkipped,
        context,
    };

    trackScheduledCallSkipped<Record<string, unknown> | EventSpecification>(modifiedScheduledCallSkipped, trackers);
}
/**
 * Tracks a LibraryUploadNudgeClicked event specification.
 * ID: 6729f911-9d53-424d-8f46-2167a2ec2a53
 */
export function trackLibraryUploadNudgeClickedSpec(libraryUploadNudgeClicked: LibraryUploadNudgeClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6729f911-9d53-424d-8f46-2167a2ec2a53',
        version: 1,
        name: 'library_upload_nudge_clicked',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(libraryUploadNudgeClicked.context)
        ? [...libraryUploadNudgeClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLibraryUploadNudgeClicked = {
        ...libraryUploadNudgeClicked,
        context,
    };

    trackLibraryUploadNudgeClicked<Record<string, unknown> | EventSpecification>(modifiedLibraryUploadNudgeClicked, trackers);
}
/**
 * Tracks a TaskStatusChanged event specification.
 * ID: 67552f86-f870-49f5-a4b2-bdaf6641a358
 */
export function trackTaskStatusChangedSpec(taskStatusChanged: TaskStatusChanged & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '67552f86-f870-49f5-a4b2-bdaf6641a358',
        version: 0,
        name: 'task_status_changed',
        data_product_id: '521bdabc-e43c-414b-a3d8-af7f5bdfb182',
        data_product_name: 'multiplayer'
    });

    const context = Array.isArray(taskStatusChanged.context)
        ? [...taskStatusChanged.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTaskStatusChanged = {
        ...taskStatusChanged,
        context,
    };

    trackTaskStatusChanged<Record<string, unknown> | EventSpecification>(modifiedTaskStatusChanged, trackers);
}
/**
 * Tracks a CommunityTemplateViewed event specification.
 * ID: 67760f4c-9894-488e-87fa-4949a8ca15cd
 */
export function trackCommunityTemplateViewedSpec(communityTemplateViewed: CommunityTemplateViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '67760f4c-9894-488e-87fa-4949a8ca15cd',
        version: 2,
        name: 'community_template_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(communityTemplateViewed.context)
        ? [...communityTemplateViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCommunityTemplateViewed = {
        ...communityTemplateViewed,
        context,
    };

    trackCommunityTemplateViewed<Record<string, unknown> | EventSpecification>(modifiedCommunityTemplateViewed, trackers);
}
/**
 * Tracks a OrganisationUpdated event specification.
 * ID: 67a61602-3680-4059-b719-6d06e9e10311
 */
export function trackOrganisationUpdatedSpec(organisationUpdated: OrganisationUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '67a61602-3680-4059-b719-6d06e9e10311',
        version: 1,
        name: 'organisation_updated',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(organisationUpdated.context)
        ? [...organisationUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOrganisationUpdated = {
        ...organisationUpdated,
        context,
    };

    trackOrganisationUpdated<Record<string, unknown> | EventSpecification>(modifiedOrganisationUpdated, trackers);
}
/**
 * Tracks a PrechartInstructionsUpdated event specification.
 * ID: 67c509b2-2462-4cbc-92a8-ee45b53dd39c
 */
export function trackPrechartInstructionsUpdatedSpec(prechartInstructionsUpdated: PrechartInstructionsUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '67c509b2-2462-4cbc-92a8-ee45b53dd39c',
        version: 0,
        name: 'prechart_instructions_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(prechartInstructionsUpdated.context)
        ? [...prechartInstructionsUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPrechartInstructionsUpdated = {
        ...prechartInstructionsUpdated,
        context,
    };

    trackPrechartInstructionsUpdated<Record<string, unknown> | EventSpecification>(modifiedPrechartInstructionsUpdated, trackers);
}
/**
 * Tracks a EvidenceUserActive event specification.
 * ID: 67d124a3-c1b3-4506-a4dd-5fa066af5a67
 */
export function trackEvidenceUserActiveSpec(evidenceUserActive: EvidenceUserActive & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '67d124a3-c1b3-4506-a4dd-5fa066af5a67',
        version: 1,
        name: 'evidence_user_active',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceUserActive.context)
        ? [...evidenceUserActive.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceUserActive = {
        ...evidenceUserActive,
        context,
    };

    trackEvidenceUserActive<Record<string, unknown> | EventSpecification>(modifiedEvidenceUserActive, trackers);
}
/**
 * Tracks a NudgeItemChecked event specification.
 * ID: 6833e21e-685f-42c9-848d-86f9b4b04018
 */
export function trackNudgeItemCheckedSpec(nudgeItemChecked: NudgeItemChecked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6833e21e-685f-42c9-848d-86f9b4b04018',
        version: 0,
        name: 'nudge_item_checked',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(nudgeItemChecked.context)
        ? [...nudgeItemChecked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNudgeItemChecked = {
        ...nudgeItemChecked,
        context,
    };

    trackNudgeItemChecked<Record<string, unknown> | EventSpecification>(modifiedNudgeItemChecked, trackers);
}
/**
 * Tracks a WorkProjectCreated event specification.
 * ID: 689e8c35-b232-5bd6-8ca0-4c2f9b873fec
 */
export function trackWorkProjectCreatedSpec(workProjectCreated: WorkProjectCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '689e8c35-b232-5bd6-8ca0-4c2f9b873fec',
        version: 1,
        name: 'work_project_created',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workProjectCreated.context)
        ? [...workProjectCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkProjectCreated = {
        ...workProjectCreated,
        context,
    };

    trackWorkProjectCreated<Record<string, unknown> | EventSpecification>(modifiedWorkProjectCreated, trackers);
}
/**
 * Tracks a SnippetDeleted event specification.
 * ID: 68ebc97e-023f-4a17-a02e-02df27631ab0
 */
export function trackSnippetDeletedSpec(snippetDeleted: SnippetDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '68ebc97e-023f-4a17-a02e-02df27631ab0',
        version: 1,
        name: 'snippet_deleted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(snippetDeleted.context)
        ? [...snippetDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSnippetDeleted = {
        ...snippetDeleted,
        context,
    };

    trackSnippetDeleted<Record<string, unknown> | EventSpecification>(modifiedSnippetDeleted, trackers);
}
/**
 * Tracks a EhrAppointmentSynced event specification.
 * ID: 6a584f75-5d68-4578-bf6c-a5553d1b1d05
 */
export function trackEhrAppointmentSyncedSpec(ehrAppointmentSynced: EhrAppointmentSynced & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6a584f75-5d68-4578-bf6c-a5553d1b1d05',
        version: 0,
        name: 'ehr_appointment_synced',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(ehrAppointmentSynced.context)
        ? [...ehrAppointmentSynced.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEhrAppointmentSynced = {
        ...ehrAppointmentSynced,
        context,
    };

    trackEhrAppointmentSynced<Record<string, unknown> | EventSpecification>(modifiedEhrAppointmentSynced, trackers);
}
/**
 * Tracks a SessionCopied event specification.
 * ID: 6b2453bd-0dc0-4cb3-a116-2f4a28b91e11
 */
export function trackSessionCopiedSpec(sessionCopied: SessionCopied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6b2453bd-0dc0-4cb3-a116-2f4a28b91e11',
        version: 9,
        name: 'session_copied',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionCopied.context)
        ? [...sessionCopied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionCopied = {
        ...sessionCopied,
        context,
    };

    trackSessionCopied<Record<string, unknown> | EventSpecification>(modifiedSessionCopied, trackers);
}
/**
 * Tracks a TaskCreated event specification.
 * ID: 6b6b91bc-15b3-4003-aa00-1e95b6aec241
 */
export function trackTaskCreatedSpec(taskCreated: TaskCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6b6b91bc-15b3-4003-aa00-1e95b6aec241',
        version: 3,
        name: 'task_created',
        data_product_id: '521bdabc-e43c-414b-a3d8-af7f5bdfb182',
        data_product_name: 'multiplayer'
    });

    const context = Array.isArray(taskCreated.context)
        ? [...taskCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTaskCreated = {
        ...taskCreated,
        context,
    };

    trackTaskCreated<Record<string, unknown> | EventSpecification>(modifiedTaskCreated, trackers);
}
/**
 * Tracks a NoteEditorRenderFailed event specification.
 * ID: 6be01f3b-b977-4b51-8259-abf3a7edee47
 */
export function trackNoteEditorRenderFailedSpec(noteEditorRenderFailed: NoteEditorRenderFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6be01f3b-b977-4b51-8259-abf3a7edee47',
        version: 0,
        name: 'note_editor_render_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteEditorRenderFailed.context)
        ? [...noteEditorRenderFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteEditorRenderFailed = {
        ...noteEditorRenderFailed,
        context,
    };

    trackNoteEditorRenderFailed<Record<string, unknown> | EventSpecification>(modifiedNoteEditorRenderFailed, trackers);
}
/**
 * Tracks a ShareNotificationClicked event specification.
 * ID: 6c653d25-7e75-403f-aa84-31eb8eb579cb
 */
export function trackShareNotificationClickedSpec(shareNotificationClicked: ShareNotificationClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6c653d25-7e75-403f-aa84-31eb8eb579cb',
        version: 1,
        name: 'share_notification_clicked',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareNotificationClicked.context)
        ? [...shareNotificationClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareNotificationClicked = {
        ...shareNotificationClicked,
        context,
    };

    trackShareNotificationClicked<Record<string, unknown> | EventSpecification>(modifiedShareNotificationClicked, trackers);
}
/**
 * Tracks a CpdSourceUpdated event specification.
 * ID: 6cfcbce9-8c49-4b04-86cd-6dc43b233e8a
 */
export function trackCpdSourceUpdatedSpec(cpdSourceUpdated: CpdSourceUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6cfcbce9-8c49-4b04-86cd-6dc43b233e8a',
        version: 1,
        name: 'cpd_source_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(cpdSourceUpdated.context)
        ? [...cpdSourceUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCpdSourceUpdated = {
        ...cpdSourceUpdated,
        context,
    };

    trackCpdSourceUpdated<Record<string, unknown> | EventSpecification>(modifiedCpdSourceUpdated, trackers);
}
/**
 * Tracks a EhrLaunchStarted event specification.
 * ID: 6ddd8760-bb16-4ccb-85a2-8d45c29da371
 */
export function trackEhrLaunchStartedSpec(ehrLaunchStarted: EhrLaunchStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6ddd8760-bb16-4ccb-85a2-8d45c29da371',
        version: 0,
        name: 'ehr_launch_started',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(ehrLaunchStarted.context)
        ? [...ehrLaunchStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEhrLaunchStarted = {
        ...ehrLaunchStarted,
        context,
    };

    trackEhrLaunchStarted<Record<string, unknown> | EventSpecification>(modifiedEhrLaunchStarted, trackers);
}
/**
 * Tracks a PatientUpdated event specification.
 * ID: 6e0562b7-536b-423b-84b0-8b77478837ee
 */
export function trackPatientUpdatedSpec(patientUpdated: PatientUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6e0562b7-536b-423b-84b0-8b77478837ee',
        version: 5,
        name: 'patient_updated',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(patientUpdated.context)
        ? [...patientUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientUpdated = {
        ...patientUpdated,
        context,
    };

    trackPatientUpdated<Record<string, unknown> | EventSpecification>(modifiedPatientUpdated, trackers);
}
/**
 * Tracks a DocumentUpdated event specification.
 * ID: 6e3d9fed-1d36-4bcc-a923-8fced31d697e
 */
export function trackDocumentUpdatedSpec(documentUpdated: DocumentUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6e3d9fed-1d36-4bcc-a923-8fced31d697e',
        version: 3,
        name: 'document_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(documentUpdated.context)
        ? [...documentUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDocumentUpdated = {
        ...documentUpdated,
        context,
    };

    trackDocumentUpdated<Record<string, unknown> | EventSpecification>(modifiedDocumentUpdated, trackers);
}
/**
 * Tracks a TeamCreated event specification.
 * ID: 6e57b5ef-263d-42eb-94d8-37b8d256cb52
 */
export function trackTeamCreatedSpec(teamCreated: TeamCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6e57b5ef-263d-42eb-94d8-37b8d256cb52',
        version: 2,
        name: 'team_created',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(teamCreated.context)
        ? [...teamCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTeamCreated = {
        ...teamCreated,
        context,
    };

    trackTeamCreated<Record<string, unknown> | EventSpecification>(modifiedTeamCreated, trackers);
}
/**
 * Tracks a MessageReceived event specification.
 * ID: 6e86e508-a4ad-420a-9b9e-c0891d86d1fe
 */
export function trackMessageReceivedSpec(messageReceived: MessageReceived & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '6e86e508-a4ad-420a-9b9e-c0891d86d1fe',
        version: 6,
        name: 'message_received',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(messageReceived.context)
        ? [...messageReceived.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedMessageReceived = {
        ...messageReceived,
        context,
    };

    trackMessageReceived<Record<string, unknown> | EventSpecification>(modifiedMessageReceived, trackers);
}
/**
 * Tracks a ContextFailed event specification.
 * ID: 70041bed-03e4-49b7-bde9-36b106f81f4b
 */
export function trackContextFailedSpec(contextFailed: ContextFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '70041bed-03e4-49b7-bde9-36b106f81f4b',
        version: 3,
        name: 'context_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(contextFailed.context)
        ? [...contextFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedContextFailed = {
        ...contextFailed,
        context,
    };

    trackContextFailed<Record<string, unknown> | EventSpecification>(modifiedContextFailed, trackers);
}
/**
 * Tracks a AccountDeleted event specification.
 * ID: 70432872-90a3-40d2-a236-e27b98836a2a
 */
export function trackAccountDeletedSpec(accountDeleted: AccountDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '70432872-90a3-40d2-a236-e27b98836a2a',
        version: 1,
        name: 'account_deleted',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(accountDeleted.context)
        ? [...accountDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAccountDeleted = {
        ...accountDeleted,
        context,
    };

    trackAccountDeleted<Record<string, unknown> | EventSpecification>(modifiedAccountDeleted, trackers);
}
/**
 * Tracks a MessageAttachmentUploaded event specification.
 * ID: 706b7290-fc99-409e-9b45-ae978d5aff46
 */
export function trackMessageAttachmentUploadedSpec(messageAttachmentUploaded: MessageAttachmentUploaded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '706b7290-fc99-409e-9b45-ae978d5aff46',
        version: 0,
        name: 'message_attachment_uploaded',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(messageAttachmentUploaded.context)
        ? [...messageAttachmentUploaded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedMessageAttachmentUploaded = {
        ...messageAttachmentUploaded,
        context,
    };

    trackMessageAttachmentUploaded<Record<string, unknown> | EventSpecification>(modifiedMessageAttachmentUploaded, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a ChronicleFirmwareUpgradeCompleted event specification.
 * ID: 70c58db7-8fda-4dc7-97be-f597dfd66234
 */
export function trackChronicleFirmwareUpgradeCompletedSpec(chronicleFirmwareUpgradeCompleted: ChronicleFirmwareUpgradeCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '70c58db7-8fda-4dc7-97be-f597dfd66234',
        version: 1,
        name: 'chronicle_firmware_upgrade_completed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(chronicleFirmwareUpgradeCompleted.context)
        ? [...chronicleFirmwareUpgradeCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChronicleFirmwareUpgradeCompleted = {
        ...chronicleFirmwareUpgradeCompleted,
        context,
    };

    trackChronicleFirmwareUpgradeCompleted<Record<string, unknown> | EventSpecification>(modifiedChronicleFirmwareUpgradeCompleted, trackers);
}
/**
 * Tracks a PatientFailed event specification.
 * ID: 711e9467-9fa5-4246-8289-2a6f70e2608c
 */
export function trackPatientFailedSpec(patientFailed: PatientFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '711e9467-9fa5-4246-8289-2a6f70e2608c',
        version: 1,
        name: 'patient_failed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(patientFailed.context)
        ? [...patientFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientFailed = {
        ...patientFailed,
        context,
    };

    trackPatientFailed<Record<string, unknown> | EventSpecification>(modifiedPatientFailed, trackers);
}
/**
 * Tracks a PatientListViewed event specification.
 * ID: 7144187a-5be9-4e5d-a15f-7c41a69ea677
 */
export function trackPatientListViewedSpec(patientListViewed: PatientListViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7144187a-5be9-4e5d-a15f-7c41a69ea677',
        version: 0,
        name: 'patient_list_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(patientListViewed.context)
        ? [...patientListViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientListViewed = {
        ...patientListViewed,
        context,
    };

    trackPatientListViewed<Record<string, unknown> | EventSpecification>(modifiedPatientListViewed, trackers);
}
/**
 * Tracks a PatientSummaryLinkOpened event specification.
 * ID: 724c77cf-5cc8-47a7-8aaf-e6df09aff47e
 */
export function trackPatientSummaryLinkOpenedSpec(patientSummaryLinkOpened: PatientSummaryLinkOpened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '724c77cf-5cc8-47a7-8aaf-e6df09aff47e',
        version: 1,
        name: 'patient_summary_link_opened',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(patientSummaryLinkOpened.context)
        ? [...patientSummaryLinkOpened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientSummaryLinkOpened = {
        ...patientSummaryLinkOpened,
        context,
    };

    trackPatientSummaryLinkOpened<Record<string, unknown> | EventSpecification>(modifiedPatientSummaryLinkOpened, trackers);
}
/**
 * Tracks a ObjectiveViewed event specification.
 * ID: 72b87ac4-dc34-4418-aa05-ea68e3fbbcb6
 */
export function trackObjectiveViewedSpec(objectiveViewed: ObjectiveViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '72b87ac4-dc34-4418-aa05-ea68e3fbbcb6',
        version: 0,
        name: 'objective_viewed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(objectiveViewed.context)
        ? [...objectiveViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedObjectiveViewed = {
        ...objectiveViewed,
        context,
    };

    trackObjectiveViewed<Record<string, unknown> | EventSpecification>(modifiedObjectiveViewed, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a VerificationCompleted event specification.
 * ID: 73e67dd5-c920-47da-b84b-25e6cb7ae1f1
 */
export function trackVerificationCompletedSpec(verificationCompleted: VerificationCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '73e67dd5-c920-47da-b84b-25e6cb7ae1f1',
        version: 2,
        name: 'verification_completed',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(verificationCompleted.context)
        ? [...verificationCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedVerificationCompleted = {
        ...verificationCompleted,
        context,
    };

    trackVerificationCompleted<Record<string, unknown> | EventSpecification>(modifiedVerificationCompleted, trackers);
}
/**
 * Tracks a AuthenticationClicked event specification.
 * ID: 74088cea-9cae-41f7-aa23-8e24598e5930
 */
export function trackAuthenticationClickedSpec(authenticationClicked: AuthenticationClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '74088cea-9cae-41f7-aa23-8e24598e5930',
        version: 1,
        name: 'authentication_clicked',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(authenticationClicked.context)
        ? [...authenticationClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAuthenticationClicked = {
        ...authenticationClicked,
        context,
    };

    trackAuthenticationClicked<Record<string, unknown> | EventSpecification>(modifiedAuthenticationClicked, trackers);
}
/**
 * Tracks a NoteStarted event specification.
 * ID: 742018f8-e9e7-407c-87f5-90741d35df59
 */
export function trackNoteStartedSpec(noteStarted: NoteStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '742018f8-e9e7-407c-87f5-90741d35df59',
        version: 1,
        name: 'note_started',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteStarted.context)
        ? [...noteStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteStarted = {
        ...noteStarted,
        context,
    };

    trackNoteStarted<Record<string, unknown> | EventSpecification>(modifiedNoteStarted, trackers);
}
/**
 * Tracks a ReferralViewed event specification.
 * ID: 75bf55f4-f1a3-4ad5-9199-52b2751cd367
 */
export function trackReferralViewedSpec(referralViewed: ReferralViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '75bf55f4-f1a3-4ad5-9199-52b2751cd367',
        version: 1,
        name: 'referral_viewed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(referralViewed.context)
        ? [...referralViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedReferralViewed = {
        ...referralViewed,
        context,
    };

    trackReferralViewed<Record<string, unknown> | EventSpecification>(modifiedReferralViewed, trackers);
}
/**
 * Tracks a UniversalDictationPostProcessingCompleted event specification.
 * ID: 76047234-d2fc-46a4-9e82-285d78891fb4
 */
export function trackUniversalDictationPostProcessingCompletedSpec(universalDictationPostProcessingCompleted: UniversalDictationPostProcessingCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '76047234-d2fc-46a4-9e82-285d78891fb4',
        version: 2,
        name: 'universal_dictation_post_processing_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(universalDictationPostProcessingCompleted.context)
        ? [...universalDictationPostProcessingCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUniversalDictationPostProcessingCompleted = {
        ...universalDictationPostProcessingCompleted,
        context,
    };

    trackUniversalDictationPostProcessingCompleted<Record<string, unknown> | EventSpecification>(modifiedUniversalDictationPostProcessingCompleted, trackers);
}
/**
 * Tracks a TemplateUpdated event specification.
 * ID: 761eb06c-01d9-4ef7-a7dc-60e87d4f9145
 */
export function trackTemplateUpdatedSpec(templateUpdated: TemplateUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '761eb06c-01d9-4ef7-a7dc-60e87d4f9145',
        version: 5,
        name: 'template_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateUpdated.context)
        ? [...templateUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateUpdated = {
        ...templateUpdated,
        context,
    };

    trackTemplateUpdated<Record<string, unknown> | EventSpecification>(modifiedTemplateUpdated, trackers);
}
/**
 * Tracks a EvidencePromptsBarShown event specification.
 * ID: 7674f9a4-1a77-4e34-9b1c-09321d46e296
 */
export function trackEvidencePromptsBarShownSpec(evidencePromptsBarShown: EvidencePromptsBarShown & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7674f9a4-1a77-4e34-9b1c-09321d46e296',
        version: 0,
        name: 'evidence_prompts_bar_shown',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidencePromptsBarShown.context)
        ? [...evidencePromptsBarShown.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidencePromptsBarShown = {
        ...evidencePromptsBarShown,
        context,
    };

    trackEvidencePromptsBarShown<Record<string, unknown> | EventSpecification>(modifiedEvidencePromptsBarShown, trackers);
}
/**
 * Tracks a CodeCopied event specification.
 * ID: 76a2a447-c014-4ea7-9cb3-0a2e74a18de1
 */
export function trackCodeCopiedSpec(codeCopied: CodeCopied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '76a2a447-c014-4ea7-9cb3-0a2e74a18de1',
        version: 3,
        name: 'code_copied',
        data_product_id: '97404acb-340e-4ca4-bf39-5e42ff587704',
        data_product_name: 'revenue_cycle'
    });

    const context = Array.isArray(codeCopied.context)
        ? [...codeCopied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCodeCopied = {
        ...codeCopied,
        context,
    };

    trackCodeCopied<Record<string, unknown> | EventSpecification>(modifiedCodeCopied, trackers);
}
/**
 * Tracks a ButtonClicked event specification.
 * ID: 76a3277a-8bf3-43d6-b635-f70dca21a9af
 */
export function trackButtonClickedSpec(buttonClicked: ButtonClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '76a3277a-8bf3-43d6-b635-f70dca21a9af',
        version: 14,
        name: 'button_clicked',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(buttonClicked.context)
        ? [...buttonClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedButtonClicked = {
        ...buttonClicked,
        context,
    };

    trackButtonClicked<Record<string, unknown> | EventSpecification>(modifiedButtonClicked, trackers);
}
/**
 * Tracks a EntityUpdated event specification.
 * ID: 771a6f72-cddf-4cd0-9dcf-2b577b9de115
 */
export function trackEntityUpdatedSpec(entityUpdated: EntityUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '771a6f72-cddf-4cd0-9dcf-2b577b9de115',
        version: 0,
        name: 'entity_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(entityUpdated.context)
        ? [...entityUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEntityUpdated = {
        ...entityUpdated,
        context,
    };

    trackEntityUpdated<Record<string, unknown> | EventSpecification>(modifiedEntityUpdated, trackers);
}
/**
 * Tracks a CommsDashboardTabViewed event specification.
 * ID: 771f21ae-ffd7-42b4-a967-51b0c4b78546
 */
export function trackCommsDashboardTabViewedSpec(commsDashboardTabViewed: CommsDashboardTabViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '771f21ae-ffd7-42b4-a967-51b0c4b78546',
        version: 0,
        name: 'comms_dashboard_tab_viewed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(commsDashboardTabViewed.context)
        ? [...commsDashboardTabViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCommsDashboardTabViewed = {
        ...commsDashboardTabViewed,
        context,
    };

    trackCommsDashboardTabViewed<Record<string, unknown> | EventSpecification>(modifiedCommsDashboardTabViewed, trackers);
}
/**
 * Tracks a ShareAccessRevoked event specification.
 * ID: 772db2b0-653a-4f60-bc0c-19e23fe4e073
 */
export function trackShareAccessRevokedSpec(shareAccessRevoked: ShareAccessRevoked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '772db2b0-653a-4f60-bc0c-19e23fe4e073',
        version: 1,
        name: 'share_access_revoked',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareAccessRevoked.context)
        ? [...shareAccessRevoked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareAccessRevoked = {
        ...shareAccessRevoked,
        context,
    };

    trackShareAccessRevoked<Record<string, unknown> | EventSpecification>(modifiedShareAccessRevoked, trackers);
}
/**
 * Tracks a WorkApprovalActioned event specification.
 * ID: 7793805b-5921-571b-aa57-e79587c32dd9
 */
export function trackWorkApprovalActionedSpec(workApprovalActioned: WorkApprovalActioned & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7793805b-5921-571b-aa57-e79587c32dd9',
        version: 0,
        name: 'work_approval_actioned',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workApprovalActioned.context)
        ? [...workApprovalActioned.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkApprovalActioned = {
        ...workApprovalActioned,
        context,
    };

    trackWorkApprovalActioned<Record<string, unknown> | EventSpecification>(modifiedWorkApprovalActioned, trackers);
}
/**
 * Tracks a SignUpStarted event specification.
 * ID: 77b0fbe5-78e5-47df-bd14-3bde85304e82
 */
export function trackSignUpStartedSpec(signUpStarted: SignUpStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '77b0fbe5-78e5-47df-bd14-3bde85304e82',
        version: 1,
        name: 'sign_up_started',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(signUpStarted.context)
        ? [...signUpStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSignUpStarted = {
        ...signUpStarted,
        context,
    };

    trackSignUpStarted<Record<string, unknown> | EventSpecification>(modifiedSignUpStarted, trackers);
}
/**
 * Tracks a SnippetCreated event specification.
 * ID: 77d1470b-f2a1-4488-9d48-6f81c45a0ecf
 */
export function trackSnippetCreatedSpec(snippetCreated: SnippetCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '77d1470b-f2a1-4488-9d48-6f81c45a0ecf',
        version: 3,
        name: 'snippet_created',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(snippetCreated.context)
        ? [...snippetCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSnippetCreated = {
        ...snippetCreated,
        context,
    };

    trackSnippetCreated<Record<string, unknown> | EventSpecification>(modifiedSnippetCreated, trackers);
}
/**
 * Tracks a TemplateCreated event specification.
 * ID: 78035f21-85bb-4c4b-be19-5f1c0a931b25
 */
export function trackTemplateCreatedSpec(templateCreated: TemplateCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '78035f21-85bb-4c4b-be19-5f1c0a931b25',
        version: 8,
        name: 'template_created',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateCreated.context)
        ? [...templateCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateCreated = {
        ...templateCreated,
        context,
    };

    trackTemplateCreated<Record<string, unknown> | EventSpecification>(modifiedTemplateCreated, trackers);
}
/**
 * Tracks a DocumentViewed event specification.
 * ID: 78817bb4-d6d4-4bf0-afe1-50643ee3c8f4
 */
export function trackDocumentViewedSpec(documentViewed: DocumentViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '78817bb4-d6d4-4bf0-afe1-50643ee3c8f4',
        version: 1,
        name: 'document_viewed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(documentViewed.context)
        ? [...documentViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDocumentViewed = {
        ...documentViewed,
        context,
    };

    trackDocumentViewed<Record<string, unknown> | EventSpecification>(modifiedDocumentViewed, trackers);
}
/**
 * Tracks a LibraryDocumentScopeChanged event specification.
 * ID: 7929c80d-b6f4-4680-876d-c681d4625a21
 */
export function trackLibraryDocumentScopeChangedSpec(libraryDocumentScopeChanged: LibraryDocumentScopeChanged & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7929c80d-b6f4-4680-876d-c681d4625a21',
        version: 1,
        name: 'library_document_scope_changed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(libraryDocumentScopeChanged.context)
        ? [...libraryDocumentScopeChanged.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLibraryDocumentScopeChanged = {
        ...libraryDocumentScopeChanged,
        context,
    };

    trackLibraryDocumentScopeChanged<Record<string, unknown> | EventSpecification>(modifiedLibraryDocumentScopeChanged, trackers);
}
/**
 * Tracks a EvidenceJurisdictionChanged event specification.
 * ID: 793ba1e1-cd00-4b33-9688-2c2c036b5ab8
 */
export function trackEvidenceJurisdictionChangedSpec(evidenceJurisdictionChanged: EvidenceJurisdictionChanged & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '793ba1e1-cd00-4b33-9688-2c2c036b5ab8',
        version: 1,
        name: 'evidence_jurisdiction_changed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceJurisdictionChanged.context)
        ? [...evidenceJurisdictionChanged.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceJurisdictionChanged = {
        ...evidenceJurisdictionChanged,
        context,
    };

    trackEvidenceJurisdictionChanged<Record<string, unknown> | EventSpecification>(modifiedEvidenceJurisdictionChanged, trackers);
}
/**
 * Tracks a WorkJobCompleted event specification.
 * ID: 7986467c-81f8-5347-8d37-5b5bfda7c485
 */
export function trackWorkJobCompletedSpec(workJobCompleted: WorkJobCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7986467c-81f8-5347-8d37-5b5bfda7c485',
        version: 3,
        name: 'work_job_completed',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workJobCompleted.context)
        ? [...workJobCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkJobCompleted = {
        ...workJobCompleted,
        context,
    };

    trackWorkJobCompleted<Record<string, unknown> | EventSpecification>(modifiedWorkJobCompleted, trackers);
}
/**
 * Tracks a CallRecordingFailed event specification.
 * ID: 79ee05fe-92ba-4745-a573-45a6bb4f1ff4
 */
export function trackCallRecordingFailedSpec(callRecordingFailed: CallRecordingFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '79ee05fe-92ba-4745-a573-45a6bb4f1ff4',
        version: 0,
        name: 'call_recording_failed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callRecordingFailed.context)
        ? [...callRecordingFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallRecordingFailed = {
        ...callRecordingFailed,
        context,
    };

    trackCallRecordingFailed<Record<string, unknown> | EventSpecification>(modifiedCallRecordingFailed, trackers);
}
/**
 * Tracks a PatientViewed event specification.
 * ID: 7b208bb9-e62b-4021-81bf-b8c85832c599
 */
export function trackPatientViewedSpec(patientViewed: PatientViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7b208bb9-e62b-4021-81bf-b8c85832c599',
        version: 4,
        name: 'patient_viewed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(patientViewed.context)
        ? [...patientViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientViewed = {
        ...patientViewed,
        context,
    };

    trackPatientViewed<Record<string, unknown> | EventSpecification>(modifiedPatientViewed, trackers);
}
/**
 * Tracks a CursorDictationPostProcessingCompleted event specification.
 * ID: 7b697533-fe53-40fb-b9d0-ba599e094964
 */
export function trackCursorDictationPostProcessingCompletedSpec(cursorDictationPostProcessingCompleted: CursorDictationPostProcessingCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7b697533-fe53-40fb-b9d0-ba599e094964',
        version: 2,
        name: 'cursor_dictation_post_processing_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(cursorDictationPostProcessingCompleted.context)
        ? [...cursorDictationPostProcessingCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCursorDictationPostProcessingCompleted = {
        ...cursorDictationPostProcessingCompleted,
        context,
    };

    trackCursorDictationPostProcessingCompleted<Record<string, unknown> | EventSpecification>(modifiedCursorDictationPostProcessingCompleted, trackers);
}
/**
 * Tracks a CodeProcessed event specification.
 * ID: 7bcd1946-6e74-4c30-a650-fc4834246d23
 */
export function trackCodeProcessedSpec(codeProcessed: CodeProcessed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7bcd1946-6e74-4c30-a650-fc4834246d23',
        version: 1,
        name: 'code_processed',
        data_product_id: '97404acb-340e-4ca4-bf39-5e42ff587704',
        data_product_name: 'revenue_cycle'
    });

    const context = Array.isArray(codeProcessed.context)
        ? [...codeProcessed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCodeProcessed = {
        ...codeProcessed,
        context,
    };

    trackCodeProcessed<Record<string, unknown> | EventSpecification>(modifiedCodeProcessed, trackers);
}
/**
 * Tracks a ExportSettingToggled event specification.
 * ID: 7ca90994-eb46-454b-adbb-00e56762e4e1
 */
export function trackExportSettingToggledSpec(exportSettingToggled: ExportSettingToggled & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7ca90994-eb46-454b-adbb-00e56762e4e1',
        version: 1,
        name: 'export_setting_toggled',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(exportSettingToggled.context)
        ? [...exportSettingToggled.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedExportSettingToggled = {
        ...exportSettingToggled,
        context,
    };

    trackExportSettingToggled<Record<string, unknown> | EventSpecification>(modifiedExportSettingToggled, trackers);
}
/**
 * Tracks a WorkTermsActioned event specification.
 * ID: 7d83c9a3-cad9-5c0e-b86f-2377e58dc24f
 */
export function trackWorkTermsActionedSpec(workTermsActioned: WorkTermsActioned & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7d83c9a3-cad9-5c0e-b86f-2377e58dc24f',
        version: 0,
        name: 'work_terms_actioned',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workTermsActioned.context)
        ? [...workTermsActioned.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkTermsActioned = {
        ...workTermsActioned,
        context,
    };

    trackWorkTermsActioned<Record<string, unknown> | EventSpecification>(modifiedWorkTermsActioned, trackers);
}
/**
 * Tracks a SignUpSuccessful event specification.
 * ID: 7eb9ae6c-af17-4b01-ae60-de93825cf5ce
 */
export function trackSignUpSuccessfulSpec(signUpSuccessful: SignUpSuccessful & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7eb9ae6c-af17-4b01-ae60-de93825cf5ce',
        version: 2,
        name: 'sign_up_successful',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(signUpSuccessful.context)
        ? [...signUpSuccessful.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSignUpSuccessful = {
        ...signUpSuccessful,
        context,
    };

    trackSignUpSuccessful<Record<string, unknown> | EventSpecification>(modifiedSignUpSuccessful, trackers);
}
/**
 * Tracks a TemplateEditorUpdated event specification.
 * ID: 7f140f4c-0111-43d0-bbd6-c5ba088c525d
 */
export function trackTemplateEditorUpdatedSpec(templateEditorUpdated: TemplateEditorUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7f140f4c-0111-43d0-bbd6-c5ba088c525d',
        version: 1,
        name: 'template_editor_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateEditorUpdated.context)
        ? [...templateEditorUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateEditorUpdated = {
        ...templateEditorUpdated,
        context,
    };

    trackTemplateEditorUpdated<Record<string, unknown> | EventSpecification>(modifiedTemplateEditorUpdated, trackers);
}
/**
 * Tracks a TeamEvidenceAccessUpdated event specification.
 * ID: 7f3a9c2e-4b1d-4e8a-9f6c-2d8e5a1b0c73
 */
export function trackTeamEvidenceAccessUpdatedSpec(teamEvidenceAccessUpdated: TeamEvidenceAccessUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7f3a9c2e-4b1d-4e8a-9f6c-2d8e5a1b0c73',
        version: 0,
        name: 'team_evidence_access_updated',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(teamEvidenceAccessUpdated.context)
        ? [...teamEvidenceAccessUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTeamEvidenceAccessUpdated = {
        ...teamEvidenceAccessUpdated,
        context,
    };

    trackTeamEvidenceAccessUpdated<Record<string, unknown> | EventSpecification>(modifiedTeamEvidenceAccessUpdated, trackers);
}
/**
 * Tracks a RoadmapViewed event specification.
 * ID: 7feef018-f45a-45c1-a2c6-45898edbfd71
 */
export function trackRoadmapViewedSpec(roadmapViewed: RoadmapViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '7feef018-f45a-45c1-a2c6-45898edbfd71',
        version: 3,
        name: 'roadmap_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(roadmapViewed.context)
        ? [...roadmapViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedRoadmapViewed = {
        ...roadmapViewed,
        context,
    };

    trackRoadmapViewed<Record<string, unknown> | EventSpecification>(modifiedRoadmapViewed, trackers);
}
/**
 * Tracks a PanelViewed event specification.
 * ID: 8001b81c-61ae-4a33-9d88-764d1ad0c720
 */
export function trackPanelViewedSpec(panelViewed: PanelViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8001b81c-61ae-4a33-9d88-764d1ad0c720',
        version: 0,
        name: 'panel_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(panelViewed.context)
        ? [...panelViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPanelViewed = {
        ...panelViewed,
        context,
    };

    trackPanelViewed<Record<string, unknown> | EventSpecification>(modifiedPanelViewed, trackers);
}
/**
 * Tracks a FindReplaceSubmitted event specification.
 * ID: 809277a4-f5f5-45a0-a5dc-c7d9b30dba4a
 */
export function trackFindReplaceSubmittedSpec(findReplaceSubmitted: FindReplaceSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '809277a4-f5f5-45a0-a5dc-c7d9b30dba4a',
        version: 3,
        name: 'find_replace_submitted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(findReplaceSubmitted.context)
        ? [...findReplaceSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFindReplaceSubmitted = {
        ...findReplaceSubmitted,
        context,
    };

    trackFindReplaceSubmitted<Record<string, unknown> | EventSpecification>(modifiedFindReplaceSubmitted, trackers);
}
/**
 * Tracks a CpdActivityCompleted event specification.
 * ID: 80e1e1b3-8a16-4e62-9d13-2180609d03f9
 */
export function trackCpdActivityCompletedSpec(cpdActivityCompleted: CpdActivityCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '80e1e1b3-8a16-4e62-9d13-2180609d03f9',
        version: 1,
        name: 'cpd_activity_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(cpdActivityCompleted.context)
        ? [...cpdActivityCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCpdActivityCompleted = {
        ...cpdActivityCompleted,
        context,
    };

    trackCpdActivityCompleted<Record<string, unknown> | EventSpecification>(modifiedCpdActivityCompleted, trackers);
}
/**
 * Tracks a AutoLinkSessionsPreferenceUpdateCompleted event specification.
 * ID: 82a3c6ec-593d-4a9a-a980-846c6b35e926
 */
export function trackAutoLinkSessionsPreferenceUpdateCompletedSpec(autoLinkSessionsPreferenceUpdateCompleted: AutoLinkSessionsPreferenceUpdateCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '82a3c6ec-593d-4a9a-a980-846c6b35e926',
        version: 0,
        name: 'auto_link_sessions_preference_update_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(autoLinkSessionsPreferenceUpdateCompleted.context)
        ? [...autoLinkSessionsPreferenceUpdateCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAutoLinkSessionsPreferenceUpdateCompleted = {
        ...autoLinkSessionsPreferenceUpdateCompleted,
        context,
    };

    trackAutoLinkSessionsPreferenceUpdateCompleted<Record<string, unknown> | EventSpecification>(modifiedAutoLinkSessionsPreferenceUpdateCompleted, trackers);
}
/**
 * Tracks a TrialDowngradeClicked event specification.
 * ID: 83753856-e836-4bad-a181-944a6fa0ab17
 */
export function trackTrialDowngradeClickedSpec(trialDowngradeClicked: TrialDowngradeClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '83753856-e836-4bad-a181-944a6fa0ab17',
        version: 3,
        name: 'trial_downgrade_clicked',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(trialDowngradeClicked.context)
        ? [...trialDowngradeClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTrialDowngradeClicked = {
        ...trialDowngradeClicked,
        context,
    };

    trackTrialDowngradeClicked<Record<string, unknown> | EventSpecification>(modifiedTrialDowngradeClicked, trackers);
}
/**
 * Tracks a NudgeDetectionEvaluated event specification.
 * ID: 8493309b-9366-46be-9da6-dacc0475e5ec
 */
export function trackNudgeDetectionEvaluatedSpec(nudgeDetectionEvaluated: NudgeDetectionEvaluated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8493309b-9366-46be-9da6-dacc0475e5ec',
        version: 0,
        name: 'nudge_detection_evaluated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(nudgeDetectionEvaluated.context)
        ? [...nudgeDetectionEvaluated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNudgeDetectionEvaluated = {
        ...nudgeDetectionEvaluated,
        context,
    };

    trackNudgeDetectionEvaluated<Record<string, unknown> | EventSpecification>(modifiedNudgeDetectionEvaluated, trackers);
}
/**
 * Tracks a DocumentEmailed event specification.
 * ID: 84eac2b3-3bb8-4b18-aa9a-9eff8401ebae
 */
export function trackDocumentEmailedSpec(documentEmailed: DocumentEmailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '84eac2b3-3bb8-4b18-aa9a-9eff8401ebae',
        version: 0,
        name: 'document_emailed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(documentEmailed.context)
        ? [...documentEmailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDocumentEmailed = {
        ...documentEmailed,
        context,
    };

    trackDocumentEmailed<Record<string, unknown> | EventSpecification>(modifiedDocumentEmailed, trackers);
}
/**
 * Tracks a EvidenceExperienceFeedbackViewed event specification.
 * ID: 852af8e1-8cf2-498d-92fc-08c2aa74ffdc
 */
export function trackEvidenceExperienceFeedbackViewedSpec(evidenceExperienceFeedbackViewed: EvidenceExperienceFeedbackViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '852af8e1-8cf2-498d-92fc-08c2aa74ffdc',
        version: 3,
        name: 'evidence_experience_feedback_viewed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceExperienceFeedbackViewed.context)
        ? [...evidenceExperienceFeedbackViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceExperienceFeedbackViewed = {
        ...evidenceExperienceFeedbackViewed,
        context,
    };

    trackEvidenceExperienceFeedbackViewed<Record<string, unknown> | EventSpecification>(modifiedEvidenceExperienceFeedbackViewed, trackers);
}
/**
 * Tracks a EvidenceChatSubmitted event specification.
 * ID: 86a645ec-0770-4f5b-854a-4e553375e5e2
 */
export function trackEvidenceChatSubmittedSpec(evidenceChatSubmitted: EvidenceChatSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '86a645ec-0770-4f5b-854a-4e553375e5e2',
        version: 4,
        name: 'evidence_chat_submitted',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceChatSubmitted.context)
        ? [...evidenceChatSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceChatSubmitted = {
        ...evidenceChatSubmitted,
        context,
    };

    trackEvidenceChatSubmitted<Record<string, unknown> | EventSpecification>(modifiedEvidenceChatSubmitted, trackers);
}
/**
 * Tracks a EvidenceExperienceFeedbackRatingSubmitted event specification.
 * ID: 86da4422-8e83-4c4f-a871-2ce7f4715793
 */
export function trackEvidenceExperienceFeedbackRatingSubmittedSpec(evidenceExperienceFeedbackRatingSubmitted: EvidenceExperienceFeedbackRatingSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '86da4422-8e83-4c4f-a871-2ce7f4715793',
        version: 3,
        name: 'evidence_experience_feedback_rating_submitted',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceExperienceFeedbackRatingSubmitted.context)
        ? [...evidenceExperienceFeedbackRatingSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceExperienceFeedbackRatingSubmitted = {
        ...evidenceExperienceFeedbackRatingSubmitted,
        context,
    };

    trackEvidenceExperienceFeedbackRatingSubmitted<Record<string, unknown> | EventSpecification>(modifiedEvidenceExperienceFeedbackRatingSubmitted, trackers);
}
/**
 * Tracks a PatientDocumentLinked event specification.
 * ID: 86e6f4e4-5bb8-4d50-a6cb-e85f7766ed13
 */
export function trackPatientDocumentLinkedSpec(patientDocumentLinked: PatientDocumentLinked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '86e6f4e4-5bb8-4d50-a6cb-e85f7766ed13',
        version: 0,
        name: 'patient_document_linked',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(patientDocumentLinked.context)
        ? [...patientDocumentLinked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientDocumentLinked = {
        ...patientDocumentLinked,
        context,
    };

    trackPatientDocumentLinked<Record<string, unknown> | EventSpecification>(modifiedPatientDocumentLinked, trackers);
}
/**
 * Tracks a ProductTourViewed event specification.
 * ID: 87f1fdd7-1576-489b-a335-cfe95a1e458c
 */
export function trackProductTourViewedSpec(productTourViewed: ProductTourViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '87f1fdd7-1576-489b-a335-cfe95a1e458c',
        version: 1,
        name: 'product_tour_viewed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(productTourViewed.context)
        ? [...productTourViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedProductTourViewed = {
        ...productTourViewed,
        context,
    };

    trackProductTourViewed<Record<string, unknown> | EventSpecification>(modifiedProductTourViewed, trackers);
}
/**
 * Tracks a SessionsListPaginated event specification.
 * ID: 87f8d027-d7c6-4722-850f-8c85a53e6c38
 */
export function trackSessionsListPaginatedSpec(sessionsListPaginated: SessionsListPaginated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '87f8d027-d7c6-4722-850f-8c85a53e6c38',
        version: 0,
        name: 'sessions_list_paginated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionsListPaginated.context)
        ? [...sessionsListPaginated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionsListPaginated = {
        ...sessionsListPaginated,
        context,
    };

    trackSessionsListPaginated<Record<string, unknown> | EventSpecification>(modifiedSessionsListPaginated, trackers);
}
/**
 * Tracks a AudioUploaded event specification.
 * ID: 88e4ad74-3bb9-4790-9290-a8eaca44bebc
 */
export function trackAudioUploadedSpec(audioUploaded: AudioUploaded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '88e4ad74-3bb9-4790-9290-a8eaca44bebc',
        version: 1,
        name: 'audio_uploaded',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(audioUploaded.context)
        ? [...audioUploaded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAudioUploaded = {
        ...audioUploaded,
        context,
    };

    trackAudioUploaded<Record<string, unknown> | EventSpecification>(modifiedAudioUploaded, trackers);
}
/**
 * Tracks a TeamUpdated event specification.
 * ID: 89a2a36d-117a-4b23-b549-0d2f57af45f7
 */
export function trackTeamUpdatedSpec(teamUpdated: TeamUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '89a2a36d-117a-4b23-b549-0d2f57af45f7',
        version: 5,
        name: 'team_updated',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(teamUpdated.context)
        ? [...teamUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTeamUpdated = {
        ...teamUpdated,
        context,
    };

    trackTeamUpdated<Record<string, unknown> | EventSpecification>(modifiedTeamUpdated, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a HardwareDeviceLinked event specification.
 * ID: 8a3c1f47-b2d9-4e6a-9f18-c5a7d3e20b64
 */
export function trackHardwareDeviceLinkedSpec(hardwareDeviceLinked: HardwareDeviceLinked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8a3c1f47-b2d9-4e6a-9f18-c5a7d3e20b64',
        version: 0,
        name: 'hardware_device_linked',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(hardwareDeviceLinked.context)
        ? [...hardwareDeviceLinked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedHardwareDeviceLinked = {
        ...hardwareDeviceLinked,
        context,
    };

    trackHardwareDeviceLinked<Record<string, unknown> | EventSpecification>(modifiedHardwareDeviceLinked, trackers);
}
/**
 * Tracks a TemplateCompleted event specification.
 * ID: 8c0309d8-3c17-459e-a307-23023a6f4cc4
 */
export function trackTemplateCompletedSpec(templateCompleted: TemplateCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8c0309d8-3c17-459e-a307-23023a6f4cc4',
        version: 1,
        name: 'template_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateCompleted.context)
        ? [...templateCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateCompleted = {
        ...templateCompleted,
        context,
    };

    trackTemplateCompleted<Record<string, unknown> | EventSpecification>(modifiedTemplateCompleted, trackers);
}
/**
 * Tracks a CallVoicemailLeft event specification.
 * ID: 8c2cb3a4-4aa8-46e9-a6f8-b8002f936bac
 */
export function trackCallVoicemailLeftSpec(callVoicemailLeft: CallVoicemailLeft & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8c2cb3a4-4aa8-46e9-a6f8-b8002f936bac',
        version: 0,
        name: 'call_voicemail_left',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callVoicemailLeft.context)
        ? [...callVoicemailLeft.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallVoicemailLeft = {
        ...callVoicemailLeft,
        context,
    };

    trackCallVoicemailLeft<Record<string, unknown> | EventSpecification>(modifiedCallVoicemailLeft, trackers);
}
/**
 * Tracks a PersonalizationUpdated event specification.
 * ID: 8cabedd4-a0dd-4d40-a07c-6d979e84e78e
 */
export function trackPersonalizationUpdatedSpec(personalizationUpdated: PersonalizationUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8cabedd4-a0dd-4d40-a07c-6d979e84e78e',
        version: 2,
        name: 'personalization_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(personalizationUpdated.context)
        ? [...personalizationUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPersonalizationUpdated = {
        ...personalizationUpdated,
        context,
    };

    trackPersonalizationUpdated<Record<string, unknown> | EventSpecification>(modifiedPersonalizationUpdated, trackers);
}
/**
 * Tracks a ContextUpdated event specification.
 * ID: 8cc12bb5-52e2-4c95-b3a2-51ee31e48e8f
 */
export function trackContextUpdatedSpec(contextUpdated: ContextUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8cc12bb5-52e2-4c95-b3a2-51ee31e48e8f',
        version: 3,
        name: 'context_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(contextUpdated.context)
        ? [...contextUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedContextUpdated = {
        ...contextUpdated,
        context,
    };

    trackContextUpdated<Record<string, unknown> | EventSpecification>(modifiedContextUpdated, trackers);
}
/**
 * Tracks a AudioStopped event specification.
 * ID: 8d28e655-8108-4306-83c8-e05d54fe0871
 */
export function trackAudioStoppedSpec(audioStopped: AudioStopped & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8d28e655-8108-4306-83c8-e05d54fe0871',
        version: 1,
        name: 'audio_stopped',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(audioStopped.context)
        ? [...audioStopped.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAudioStopped = {
        ...audioStopped,
        context,
    };

    trackAudioStopped<Record<string, unknown> | EventSpecification>(modifiedAudioStopped, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a FirstEvidenceCompleted event specification.
 * ID: 8dfc3c69-a53e-4056-b7b8-b0e7f4cae427
 */
export function trackFirstEvidenceCompletedSpec(firstEvidenceCompleted: FirstEvidenceCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8dfc3c69-a53e-4056-b7b8-b0e7f4cae427',
        version: 0,
        name: 'first_evidence_completed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(firstEvidenceCompleted.context)
        ? [...firstEvidenceCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFirstEvidenceCompleted = {
        ...firstEvidenceCompleted,
        context,
    };

    trackFirstEvidenceCompleted<Record<string, unknown> | EventSpecification>(modifiedFirstEvidenceCompleted, trackers);
}
/**
 * Tracks a OfflineRecordingCreated event specification.
 * ID: 8e34e8ef-f0c5-4ce8-9c16-aa5547863dc1
 */
export function trackOfflineRecordingCreatedSpec(offlineRecordingCreated: OfflineRecordingCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8e34e8ef-f0c5-4ce8-9c16-aa5547863dc1',
        version: 0,
        name: 'offline_recording_created',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(offlineRecordingCreated.context)
        ? [...offlineRecordingCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOfflineRecordingCreated = {
        ...offlineRecordingCreated,
        context,
    };

    trackOfflineRecordingCreated<Record<string, unknown> | EventSpecification>(modifiedOfflineRecordingCreated, trackers);
}
/**
 * Tracks a EvidenceActivated event specification.
 * ID: 8eb267e3-2472-434d-8770-f0f41db9e9c5
 */
export function trackEvidenceActivatedSpec(evidenceActivated: EvidenceActivated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8eb267e3-2472-434d-8770-f0f41db9e9c5',
        version: 1,
        name: 'evidence_activated',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceActivated.context)
        ? [...evidenceActivated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceActivated = {
        ...evidenceActivated,
        context,
    };

    trackEvidenceActivated<Record<string, unknown> | EventSpecification>(modifiedEvidenceActivated, trackers);
}
/**
 * Tracks a CoachMarkActionTaken event specification.
 * ID: 8ed1ac58-b828-422b-bbc0-94748578d013
 */
export function trackCoachMarkActionTakenSpec(coachMarkActionTaken: CoachMarkActionTaken & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8ed1ac58-b828-422b-bbc0-94748578d013',
        version: 1,
        name: 'coach_mark_action_taken',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(coachMarkActionTaken.context)
        ? [...coachMarkActionTaken.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCoachMarkActionTaken = {
        ...coachMarkActionTaken,
        context,
    };

    trackCoachMarkActionTaken<Record<string, unknown> | EventSpecification>(modifiedCoachMarkActionTaken, trackers);
}
/**
 * Tracks a AdminUserCreated event specification.
 * ID: 8f3b2a1e-6c4d-49e7-b5a8-1d7f9e2c3b64
 */
export function trackAdminUserCreatedSpec(adminUserCreated: AdminUserCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8f3b2a1e-6c4d-49e7-b5a8-1d7f9e2c3b64',
        version: 1,
        name: 'admin_user_created',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(adminUserCreated.context)
        ? [...adminUserCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAdminUserCreated = {
        ...adminUserCreated,
        context,
    };

    trackAdminUserCreated<Record<string, unknown> | EventSpecification>(modifiedAdminUserCreated, trackers);
}
/**
 * Tracks a NoteViewed event specification.
 * ID: 8f8cba17-429b-4141-9dff-a3867fffe397
 */
export function trackNoteViewedSpec(noteViewed: NoteViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8f8cba17-429b-4141-9dff-a3867fffe397',
        version: 1,
        name: 'note_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteViewed.context)
        ? [...noteViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteViewed = {
        ...noteViewed,
        context,
    };

    trackNoteViewed<Record<string, unknown> | EventSpecification>(modifiedNoteViewed, trackers);
}
/**
 * Tracks a EvidencePromptsBarDismissed event specification.
 * ID: 8fd79ac8-0dfc-4822-9ea9-3c5ca21f2914
 */
export function trackEvidencePromptsBarDismissedSpec(evidencePromptsBarDismissed: EvidencePromptsBarDismissed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '8fd79ac8-0dfc-4822-9ea9-3c5ca21f2914',
        version: 0,
        name: 'evidence_prompts_bar_dismissed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidencePromptsBarDismissed.context)
        ? [...evidencePromptsBarDismissed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidencePromptsBarDismissed = {
        ...evidencePromptsBarDismissed,
        context,
    };

    trackEvidencePromptsBarDismissed<Record<string, unknown> | EventSpecification>(modifiedEvidencePromptsBarDismissed, trackers);
}
/**
 * Tracks a CallSummaryFailed event specification.
 * ID: 90474143-bc12-4c25-a19f-068f8a6871c4
 */
export function trackCallSummaryFailedSpec(callSummaryFailed: CallSummaryFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '90474143-bc12-4c25-a19f-068f8a6871c4',
        version: 0,
        name: 'call_summary_failed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callSummaryFailed.context)
        ? [...callSummaryFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallSummaryFailed = {
        ...callSummaryFailed,
        context,
    };

    trackCallSummaryFailed<Record<string, unknown> | EventSpecification>(modifiedCallSummaryFailed, trackers);
}
/**
 * Tracks a OutboundMessageQueued event specification.
 * ID: 904e258d-eb61-4ab7-ae23-863b806f2f59
 */
export function trackOutboundMessageQueuedSpec(outboundMessageQueued: OutboundMessageQueued & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '904e258d-eb61-4ab7-ae23-863b806f2f59',
        version: 0,
        name: 'outbound_message_queued',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(outboundMessageQueued.context)
        ? [...outboundMessageQueued.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOutboundMessageQueued = {
        ...outboundMessageQueued,
        context,
    };

    trackOutboundMessageQueued<Record<string, unknown> | EventSpecification>(modifiedOutboundMessageQueued, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a ObjectiveExecutionFailed event specification.
 * ID: 9128ba5b-d3c9-4e3b-b69e-9ef709ba033c
 */
export function trackObjectiveExecutionFailedSpec(objectiveExecutionFailed: ObjectiveExecutionFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '9128ba5b-d3c9-4e3b-b69e-9ef709ba033c',
        version: 0,
        name: 'objective_execution_failed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(objectiveExecutionFailed.context)
        ? [...objectiveExecutionFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedObjectiveExecutionFailed = {
        ...objectiveExecutionFailed,
        context,
    };

    trackObjectiveExecutionFailed<Record<string, unknown> | EventSpecification>(modifiedObjectiveExecutionFailed, trackers);
}
/**
 * Tracks a UserSignedIn event specification.
 * ID: 91fb327e-5efd-4eb4-b39b-2e7f75a52fd3
 */
export function trackUserSignedInSpec(userSignedIn: UserSignedIn & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '91fb327e-5efd-4eb4-b39b-2e7f75a52fd3',
        version: 2,
        name: 'user_signed_in',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(userSignedIn.context)
        ? [...userSignedIn.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUserSignedIn = {
        ...userSignedIn,
        context,
    };

    trackUserSignedIn<Record<string, unknown> | EventSpecification>(modifiedUserSignedIn, trackers);
}
/**
 * Tracks a PatientExtractionReviewed event specification.
 * ID: 92aea68f-f70f-49b9-a187-64b68394b5d9
 */
export function trackPatientExtractionReviewedSpec(patientExtractionReviewed: PatientExtractionReviewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '92aea68f-f70f-49b9-a187-64b68394b5d9',
        version: 1,
        name: 'patient_extraction_reviewed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(patientExtractionReviewed.context)
        ? [...patientExtractionReviewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientExtractionReviewed = {
        ...patientExtractionReviewed,
        context,
    };

    trackPatientExtractionReviewed<Record<string, unknown> | EventSpecification>(modifiedPatientExtractionReviewed, trackers);
}
/**
 * Tracks a PaywallViewed event specification.
 * ID: 92cf6545-ed51-4091-9a8e-70beaa76e0fc
 */
export function trackPaywallViewedSpec(paywallViewed: PaywallViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '92cf6545-ed51-4091-9a8e-70beaa76e0fc',
        version: 5,
        name: 'paywall_viewed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(paywallViewed.context)
        ? [...paywallViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPaywallViewed = {
        ...paywallViewed,
        context,
    };

    trackPaywallViewed<Record<string, unknown> | EventSpecification>(modifiedPaywallViewed, trackers);
}
/**
 * Tracks a NudgeItemUnchecked event specification.
 * ID: 93162f2d-6aa5-4ad0-beab-6398b6ef9701
 */
export function trackNudgeItemUncheckedSpec(nudgeItemUnchecked: NudgeItemUnchecked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '93162f2d-6aa5-4ad0-beab-6398b6ef9701',
        version: 0,
        name: 'nudge_item_unchecked',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(nudgeItemUnchecked.context)
        ? [...nudgeItemUnchecked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNudgeItemUnchecked = {
        ...nudgeItemUnchecked,
        context,
    };

    trackNudgeItemUnchecked<Record<string, unknown> | EventSpecification>(modifiedNudgeItemUnchecked, trackers);
}
/**
 * Tracks a EvidenceSourceClicked event specification.
 * ID: 9350e5ec-04ed-4485-94cd-b1bd139d0fab
 */
export function trackEvidenceSourceClickedSpec(evidenceSourceClicked: EvidenceSourceClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '9350e5ec-04ed-4485-94cd-b1bd139d0fab',
        version: 2,
        name: 'evidence_source_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceSourceClicked.context)
        ? [...evidenceSourceClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceSourceClicked = {
        ...evidenceSourceClicked,
        context,
    };

    trackEvidenceSourceClicked<Record<string, unknown> | EventSpecification>(modifiedEvidenceSourceClicked, trackers);
}
/**
 * Tracks a ShareSaved event specification.
 * ID: 9403c4f3-b7e6-43f5-ac48-3ac14cb00025
 */
export function trackShareSavedSpec(shareSaved: ShareSaved & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '9403c4f3-b7e6-43f5-ac48-3ac14cb00025',
        version: 1,
        name: 'share_saved',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareSaved.context)
        ? [...shareSaved.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareSaved = {
        ...shareSaved,
        context,
    };

    trackShareSaved<Record<string, unknown> | EventSpecification>(modifiedShareSaved, trackers);
}
/**
 * Tracks a DeeplinkTriggered event specification.
 * ID: 95768672-fbf8-4e6b-983b-29759bffb449
 */
export function trackDeeplinkTriggeredSpec(deeplinkTriggered: DeeplinkTriggered & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '95768672-fbf8-4e6b-983b-29759bffb449',
        version: 1,
        name: 'deeplink_triggered',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(deeplinkTriggered.context)
        ? [...deeplinkTriggered.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDeeplinkTriggered = {
        ...deeplinkTriggered,
        context,
    };

    trackDeeplinkTriggered<Record<string, unknown> | EventSpecification>(modifiedDeeplinkTriggered, trackers);
}
/**
 * Tracks a DemoCompleted event specification.
 * ID: 95b0daf4-2dc3-4b5f-b511-7ddcf90655ce
 */
export function trackDemoCompletedSpec(demoCompleted: DemoCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '95b0daf4-2dc3-4b5f-b511-7ddcf90655ce',
        version: 1,
        name: 'demo_completed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(demoCompleted.context)
        ? [...demoCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDemoCompleted = {
        ...demoCompleted,
        context,
    };

    trackDemoCompleted<Record<string, unknown> | EventSpecification>(modifiedDemoCompleted, trackers);
}
/**
 * Tracks a PatientLinked event specification.
 * ID: 95ec8dd6-fa4d-4658-a43f-f8758781dc12
 */
export function trackPatientLinkedSpec(patientLinked: PatientLinked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '95ec8dd6-fa4d-4658-a43f-f8758781dc12',
        version: 5,
        name: 'patient_linked',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(patientLinked.context)
        ? [...patientLinked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientLinked = {
        ...patientLinked,
        context,
    };

    trackPatientLinked<Record<string, unknown> | EventSpecification>(modifiedPatientLinked, trackers);
}
/**
 * Tracks a AuthTokenUpdated event specification.
 * ID: 96f6fc70-56c6-415d-a5f8-55e8578ae80d
 */
export function trackAuthTokenUpdatedSpec(authTokenUpdated: AuthTokenUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '96f6fc70-56c6-415d-a5f8-55e8578ae80d',
        version: 3,
        name: 'auth_token_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(authTokenUpdated.context)
        ? [...authTokenUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAuthTokenUpdated = {
        ...authTokenUpdated,
        context,
    };

    trackAuthTokenUpdated<Record<string, unknown> | EventSpecification>(modifiedAuthTokenUpdated, trackers);
}
/**
 * Tracks a NudgeSurfaced event specification.
 * ID: 97f00469-4ca6-4103-9665-b2a993ba10bc
 */
export function trackNudgeSurfacedSpec(nudgeSurfaced: NudgeSurfaced & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '97f00469-4ca6-4103-9665-b2a993ba10bc',
        version: 0,
        name: 'nudge_surfaced',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(nudgeSurfaced.context)
        ? [...nudgeSurfaced.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNudgeSurfaced = {
        ...nudgeSurfaced,
        context,
    };

    trackNudgeSurfaced<Record<string, unknown> | EventSpecification>(modifiedNudgeSurfaced, trackers);
}
/**
 * Tracks a ExportContentSaved event specification.
 * ID: 97f8bf71-66b2-4c0d-8dc6-910bd28d721a
 */
export function trackExportContentSavedSpec(exportContentSaved: ExportContentSaved & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '97f8bf71-66b2-4c0d-8dc6-910bd28d721a',
        version: 1,
        name: 'export_content_saved',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(exportContentSaved.context)
        ? [...exportContentSaved.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedExportContentSaved = {
        ...exportContentSaved,
        context,
    };

    trackExportContentSaved<Record<string, unknown> | EventSpecification>(modifiedExportContentSaved, trackers);
}
/**
 * Tracks a TrialActivated event specification.
 * ID: 9893af90-2513-4c6b-b595-517f6a8b4f67
 */
export function trackTrialActivatedSpec(trialActivated: TrialActivated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '9893af90-2513-4c6b-b595-517f6a8b4f67',
        version: 3,
        name: 'trial_activated',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(trialActivated.context)
        ? [...trialActivated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTrialActivated = {
        ...trialActivated,
        context,
    };

    trackTrialActivated<Record<string, unknown> | EventSpecification>(modifiedTrialActivated, trackers);
}
/**
 * Tracks a FirstDictationCompleted event specification.
 * ID: 98a5c186-16e0-45e4-87b1-07e56794e3d0
 */
export function trackFirstDictationCompletedSpec(firstDictationCompleted: FirstDictationCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '98a5c186-16e0-45e4-87b1-07e56794e3d0',
        version: 0,
        name: 'first_dictation_completed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(firstDictationCompleted.context)
        ? [...firstDictationCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFirstDictationCompleted = {
        ...firstDictationCompleted,
        context,
    };

    trackFirstDictationCompleted<Record<string, unknown> | EventSpecification>(modifiedFirstDictationCompleted, trackers);
}
/**
 * Tracks a CallTransferred event specification.
 * ID: 993d5063-3e42-4356-bde7-d3202da9a66a
 */
export function trackCallTransferredSpec(callTransferred: CallTransferred & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '993d5063-3e42-4356-bde7-d3202da9a66a',
        version: 0,
        name: 'call_transferred',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callTransferred.context)
        ? [...callTransferred.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallTransferred = {
        ...callTransferred,
        context,
    };

    trackCallTransferred<Record<string, unknown> | EventSpecification>(modifiedCallTransferred, trackers);
}
/**
 * Tracks a CheckoutCompleted event specification.
 * ID: 99916656-6254-49b4-a98b-2527cc52747c
 */
export function trackCheckoutCompletedSpec(checkoutCompleted: CheckoutCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '99916656-6254-49b4-a98b-2527cc52747c',
        version: 2,
        name: 'checkout_completed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(checkoutCompleted.context)
        ? [...checkoutCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCheckoutCompleted = {
        ...checkoutCompleted,
        context,
    };

    trackCheckoutCompleted<Record<string, unknown> | EventSpecification>(modifiedCheckoutCompleted, trackers);
}
/**
 * Tracks a ObjectiveRemoved event specification.
 * ID: 99fff09b-a71d-47da-878f-a76e3b59d91e
 */
export function trackObjectiveRemovedSpec(objectiveRemoved: ObjectiveRemoved & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '99fff09b-a71d-47da-878f-a76e3b59d91e',
        version: 0,
        name: 'objective_removed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(objectiveRemoved.context)
        ? [...objectiveRemoved.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedObjectiveRemoved = {
        ...objectiveRemoved,
        context,
    };

    trackObjectiveRemoved<Record<string, unknown> | EventSpecification>(modifiedObjectiveRemoved, trackers);
}
/**
 * Tracks a CommunityCreated event specification.
 * ID: 9a365ab1-6428-44ea-bac4-718e0d61ec58
 */
export function trackCommunityCreatedSpec(communityCreated: CommunityCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '9a365ab1-6428-44ea-bac4-718e0d61ec58',
        version: 1,
        name: 'community_created',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(communityCreated.context)
        ? [...communityCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCommunityCreated = {
        ...communityCreated,
        context,
    };

    trackCommunityCreated<Record<string, unknown> | EventSpecification>(modifiedCommunityCreated, trackers);
}
/**
 * Tracks a EvidenceChatError event specification.
 * ID: 9b2a198f-faac-41fb-9f1c-0ea1373e2574
 */
export function trackEvidenceChatErrorSpec(evidenceChatError: EvidenceChatError & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '9b2a198f-faac-41fb-9f1c-0ea1373e2574',
        version: 3,
        name: 'evidence_chat_error',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceChatError.context)
        ? [...evidenceChatError.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceChatError = {
        ...evidenceChatError,
        context,
    };

    trackEvidenceChatError<Record<string, unknown> | EventSpecification>(modifiedEvidenceChatError, trackers);
}
/**
 * Tracks a SessionMarkedAsContextOnly event specification.
 * ID: 9b4e2d17-f683-4a51-b0c9-e71a3f85d260
 */
export function trackSessionMarkedAsContextOnlySpec(sessionMarkedAsContextOnly: SessionMarkedAsContextOnly & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '9b4e2d17-f683-4a51-b0c9-e71a3f85d260',
        version: 1,
        name: 'session_marked_as_context_only',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionMarkedAsContextOnly.context)
        ? [...sessionMarkedAsContextOnly.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionMarkedAsContextOnly = {
        ...sessionMarkedAsContextOnly,
        context,
    };

    trackSessionMarkedAsContextOnly<Record<string, unknown> | EventSpecification>(modifiedSessionMarkedAsContextOnly, trackers);
}
/**
 * Tracks a FormTemplateFieldsUpdated event specification.
 * ID: 9c634ef5-05fe-408e-8a61-cecf3c853788
 */
export function trackFormTemplateFieldsUpdatedSpec(formTemplateFieldsUpdated: FormTemplateFieldsUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '9c634ef5-05fe-408e-8a61-cecf3c853788',
        version: 1,
        name: 'form_template_fields_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(formTemplateFieldsUpdated.context)
        ? [...formTemplateFieldsUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFormTemplateFieldsUpdated = {
        ...formTemplateFieldsUpdated,
        context,
    };

    trackFormTemplateFieldsUpdated<Record<string, unknown> | EventSpecification>(modifiedFormTemplateFieldsUpdated, trackers);
}
/**
 * Tracks a IntegrationSessionCreated event specification.
 * ID: 9c73d497-989c-4f7d-bd30-5434c6372ef8
 */
export function trackIntegrationSessionCreatedSpec(integrationSessionCreated: IntegrationSessionCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '9c73d497-989c-4f7d-bd30-5434c6372ef8',
        version: 1,
        name: 'integration_session_created',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationSessionCreated.context)
        ? [...integrationSessionCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationSessionCreated = {
        ...integrationSessionCreated,
        context,
    };

    trackIntegrationSessionCreated<Record<string, unknown> | EventSpecification>(modifiedIntegrationSessionCreated, trackers);
}
/**
 * Tracks a SurveyViewed event specification.
 * ID: 9cb4b3ab-e103-400e-bc28-11935ddef7be
 */
export function trackSurveyViewedSpec(surveyViewed: SurveyViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '9cb4b3ab-e103-400e-bc28-11935ddef7be',
        version: 1,
        name: 'survey_viewed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(surveyViewed.context)
        ? [...surveyViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSurveyViewed = {
        ...surveyViewed,
        context,
    };

    trackSurveyViewed<Record<string, unknown> | EventSpecification>(modifiedSurveyViewed, trackers);
}
/**
 * Tracks a CommsSetupCompleted event specification.
 * ID: 9f16262a-d758-4d1c-96d0-e82cf72f8124
 */
export function trackCommsSetupCompletedSpec(commsSetupCompleted: CommsSetupCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '9f16262a-d758-4d1c-96d0-e82cf72f8124',
        version: 0,
        name: 'comms_setup_completed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(commsSetupCompleted.context)
        ? [...commsSetupCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCommsSetupCompleted = {
        ...commsSetupCompleted,
        context,
    };

    trackCommsSetupCompleted<Record<string, unknown> | EventSpecification>(modifiedCommsSetupCompleted, trackers);
}
/**
 * Tracks a OfflineSyncCompleted event specification.
 * ID: 9f7af66a-83b5-452f-8c3f-af383a30dd2d
 */
export function trackOfflineSyncCompletedSpec(offlineSyncCompleted: OfflineSyncCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '9f7af66a-83b5-452f-8c3f-af383a30dd2d',
        version: 0,
        name: 'offline_sync_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(offlineSyncCompleted.context)
        ? [...offlineSyncCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOfflineSyncCompleted = {
        ...offlineSyncCompleted,
        context,
    };

    trackOfflineSyncCompleted<Record<string, unknown> | EventSpecification>(modifiedOfflineSyncCompleted, trackers);
}
/**
 * Tracks a ContextUploaded event specification.
 * ID: 9f7e5da3-ffb1-4afb-a364-d73f5bea10d7
 */
export function trackContextUploadedSpec(contextUploaded: ContextUploaded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: '9f7e5da3-ffb1-4afb-a364-d73f5bea10d7',
        version: 1,
        name: 'context_uploaded',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(contextUploaded.context)
        ? [...contextUploaded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedContextUploaded = {
        ...contextUploaded,
        context,
    };

    trackContextUploaded<Record<string, unknown> | EventSpecification>(modifiedContextUploaded, trackers);
}
/**
 * Tracks a ScheduleUploadProcessingFailed event specification.
 * ID: a04b0089-57c2-4ff1-a55e-e49b392f3790
 */
export function trackScheduleUploadProcessingFailedSpec(scheduleUploadProcessingFailed: ScheduleUploadProcessingFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a04b0089-57c2-4ff1-a55e-e49b392f3790',
        version: 2,
        name: 'schedule_upload_processing_failed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(scheduleUploadProcessingFailed.context)
        ? [...scheduleUploadProcessingFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScheduleUploadProcessingFailed = {
        ...scheduleUploadProcessingFailed,
        context,
    };

    trackScheduleUploadProcessingFailed<Record<string, unknown> | EventSpecification>(modifiedScheduleUploadProcessingFailed, trackers);
}
/**
 * Tracks a TaskDeleted event specification.
 * ID: a0abacb4-44bf-44c2-9d11-c26d314ec1ad
 */
export function trackTaskDeletedSpec(taskDeleted: TaskDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a0abacb4-44bf-44c2-9d11-c26d314ec1ad',
        version: 3,
        name: 'task_deleted',
        data_product_id: '521bdabc-e43c-414b-a3d8-af7f5bdfb182',
        data_product_name: 'multiplayer'
    });

    const context = Array.isArray(taskDeleted.context)
        ? [...taskDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTaskDeleted = {
        ...taskDeleted,
        context,
    };

    trackTaskDeleted<Record<string, unknown> | EventSpecification>(modifiedTaskDeleted, trackers);
}
/**
 * Tracks a SourceRecommendationApplied event specification.
 * ID: a1643bf2-e9a6-42ac-a12d-97d40cf16560
 */
export function trackSourceRecommendationAppliedSpec(sourceRecommendationApplied: SourceRecommendationApplied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a1643bf2-e9a6-42ac-a12d-97d40cf16560',
        version: 1,
        name: 'source_recommendation_applied',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(sourceRecommendationApplied.context)
        ? [...sourceRecommendationApplied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSourceRecommendationApplied = {
        ...sourceRecommendationApplied,
        context,
    };

    trackSourceRecommendationApplied<Record<string, unknown> | EventSpecification>(modifiedSourceRecommendationApplied, trackers);
}
/**
 * Tracks a ObjectivePublished event specification.
 * ID: a17db903-f8ac-45f9-883a-606a33e6cbcd
 */
export function trackObjectivePublishedSpec(objectivePublished: ObjectivePublished & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a17db903-f8ac-45f9-883a-606a33e6cbcd',
        version: 0,
        name: 'objective_published',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(objectivePublished.context)
        ? [...objectivePublished.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedObjectivePublished = {
        ...objectivePublished,
        context,
    };

    trackObjectivePublished<Record<string, unknown> | EventSpecification>(modifiedObjectivePublished, trackers);
}
/**
 * Tracks a ProductSignUpSuccessful event specification.
 * ID: a1c4e8f2-3b7d-4a9e-8f15-6d2c9b5e4a71
 */
export function trackProductSignUpSuccessfulSpec(productSignUpSuccessful: ProductSignUpSuccessful & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a1c4e8f2-3b7d-4a9e-8f15-6d2c9b5e4a71',
        version: 2,
        name: 'product_sign_up_successful',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(productSignUpSuccessful.context)
        ? [...productSignUpSuccessful.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedProductSignUpSuccessful = {
        ...productSignUpSuccessful,
        context,
    };

    trackProductSignUpSuccessful<Record<string, unknown> | EventSpecification>(modifiedProductSignUpSuccessful, trackers);
}
/**
 * Tracks a ProductTourCompleted event specification.
 * ID: a1ead0d4-5ed0-4dbc-9944-7010055e6543
 */
export function trackProductTourCompletedSpec(productTourCompleted: ProductTourCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a1ead0d4-5ed0-4dbc-9944-7010055e6543',
        version: 1,
        name: 'product_tour_completed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(productTourCompleted.context)
        ? [...productTourCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedProductTourCompleted = {
        ...productTourCompleted,
        context,
    };

    trackProductTourCompleted<Record<string, unknown> | EventSpecification>(modifiedProductTourCompleted, trackers);
}
/**
 * Tracks a RecordingConsentCaptured event specification.
 * ID: a2dbd338-5e2d-4f4e-87f3-bc351163eb95
 */
export function trackRecordingConsentCapturedSpec(recordingConsentCaptured: RecordingConsentCaptured & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a2dbd338-5e2d-4f4e-87f3-bc351163eb95',
        version: 0,
        name: 'recording_consent_captured',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(recordingConsentCaptured.context)
        ? [...recordingConsentCaptured.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedRecordingConsentCaptured = {
        ...recordingConsentCaptured,
        context,
    };

    trackRecordingConsentCaptured<Record<string, unknown> | EventSpecification>(modifiedRecordingConsentCaptured, trackers);
}
/**
 * Tracks a CpdExported event specification.
 * ID: a2e807e4-17f3-4e23-a581-23f412f8a5bc
 */
export function trackCpdExportedSpec(cpdExported: CpdExported & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a2e807e4-17f3-4e23-a581-23f412f8a5bc',
        version: 4,
        name: 'cpd_exported',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(cpdExported.context)
        ? [...cpdExported.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCpdExported = {
        ...cpdExported,
        context,
    };

    trackCpdExported<Record<string, unknown> | EventSpecification>(modifiedCpdExported, trackers);
}
/**
 * Tracks a TrialExtendClicked event specification.
 * ID: a31840f9-77ea-47bd-a06f-bb4841749138
 */
export function trackTrialExtendClickedSpec(trialExtendClicked: TrialExtendClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a31840f9-77ea-47bd-a06f-bb4841749138',
        version: 3,
        name: 'trial_extend_clicked',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(trialExtendClicked.context)
        ? [...trialExtendClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTrialExtendClicked = {
        ...trialExtendClicked,
        context,
    };

    trackTrialExtendClicked<Record<string, unknown> | EventSpecification>(modifiedTrialExtendClicked, trackers);
}
/**
 * Tracks a SectionDeleted event specification.
 * ID: a32eaeab-147f-4250-8120-f79050d1d76b
 */
export function trackSectionDeletedSpec(sectionDeleted: SectionDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a32eaeab-147f-4250-8120-f79050d1d76b',
        version: 1,
        name: 'section_deleted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sectionDeleted.context)
        ? [...sectionDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSectionDeleted = {
        ...sectionDeleted,
        context,
    };

    trackSectionDeleted<Record<string, unknown> | EventSpecification>(modifiedSectionDeleted, trackers);
}
/**
 * Tracks a EvidenceShortcutRun event specification.
 * ID: a33580bd-3070-4816-a4d5-fb7c74d38c90
 */
export function trackEvidenceShortcutRunSpec(evidenceShortcutRun: EvidenceShortcutRun & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a33580bd-3070-4816-a4d5-fb7c74d38c90',
        version: 0,
        name: 'evidence_shortcut_run',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceShortcutRun.context)
        ? [...evidenceShortcutRun.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceShortcutRun = {
        ...evidenceShortcutRun,
        context,
    };

    trackEvidenceShortcutRun<Record<string, unknown> | EventSpecification>(modifiedEvidenceShortcutRun, trackers);
}
/**
 * Tracks a ChatContextAddFileClicked event specification.
 * ID: a3890853-37be-4ad8-9db3-bb2b4037a11d
 */
export function trackChatContextAddFileClickedSpec(chatContextAddFileClicked: ChatContextAddFileClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a3890853-37be-4ad8-9db3-bb2b4037a11d',
        version: 1,
        name: 'chat_context_add_file_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(chatContextAddFileClicked.context)
        ? [...chatContextAddFileClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChatContextAddFileClicked = {
        ...chatContextAddFileClicked,
        context,
    };

    trackChatContextAddFileClicked<Record<string, unknown> | EventSpecification>(modifiedChatContextAddFileClicked, trackers);
}
/**
 * Tracks a PatientContextToggleOff event specification.
 * ID: a39c5a50-352f-4725-a563-390c404f25e6
 */
export function trackPatientContextToggleOffSpec(patientContextToggleOff: PatientContextToggleOff & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a39c5a50-352f-4725-a563-390c404f25e6',
        version: 1,
        name: 'patient_context_toggle_off',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(patientContextToggleOff.context)
        ? [...patientContextToggleOff.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientContextToggleOff = {
        ...patientContextToggleOff,
        context,
    };

    trackPatientContextToggleOff<Record<string, unknown> | EventSpecification>(modifiedPatientContextToggleOff, trackers);
}
/**
 * Tracks a CodeSearched event specification.
 * ID: a403fe06-d98d-4121-92de-4be6a031b792
 */
export function trackCodeSearchedSpec(codeSearched: CodeSearched & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a403fe06-d98d-4121-92de-4be6a031b792',
        version: 1,
        name: 'code_searched',
        data_product_id: '97404acb-340e-4ca4-bf39-5e42ff587704',
        data_product_name: 'revenue_cycle'
    });

    const context = Array.isArray(codeSearched.context)
        ? [...codeSearched.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCodeSearched = {
        ...codeSearched,
        context,
    };

    trackCodeSearched<Record<string, unknown> | EventSpecification>(modifiedCodeSearched, trackers);
}
/**
 * Tracks a SettingsUpdated event specification.
 * ID: a42da9ef-4623-489c-8e56-9486ba35f624
 */
export function trackSettingsUpdatedSpec(settingsUpdated: SettingsUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a42da9ef-4623-489c-8e56-9486ba35f624',
        version: 5,
        name: 'settings_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(settingsUpdated.context)
        ? [...settingsUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSettingsUpdated = {
        ...settingsUpdated,
        context,
    };

    trackSettingsUpdated<Record<string, unknown> | EventSpecification>(modifiedSettingsUpdated, trackers);
}
/**
 * Tracks a OrganisationSurveySubmitted event specification.
 * ID: a64451f5-3fe2-43cb-ba2a-4ce3916b2059
 */
export function trackOrganisationSurveySubmittedSpec(organisationSurveySubmitted: OrganisationSurveySubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a64451f5-3fe2-43cb-ba2a-4ce3916b2059',
        version: 1,
        name: 'organisation_survey_submitted',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(organisationSurveySubmitted.context)
        ? [...organisationSurveySubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOrganisationSurveySubmitted = {
        ...organisationSurveySubmitted,
        context,
    };

    trackOrganisationSurveySubmitted<Record<string, unknown> | EventSpecification>(modifiedOrganisationSurveySubmitted, trackers);
}
/**
 * Tracks a PersonalizationFailed event specification.
 * ID: a6b345d4-67bf-4482-9778-4b45036062b7
 */
export function trackPersonalizationFailedSpec(personalizationFailed: PersonalizationFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a6b345d4-67bf-4482-9778-4b45036062b7',
        version: 0,
        name: 'personalization_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(personalizationFailed.context)
        ? [...personalizationFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPersonalizationFailed = {
        ...personalizationFailed,
        context,
    };

    trackPersonalizationFailed<Record<string, unknown> | EventSpecification>(modifiedPersonalizationFailed, trackers);
}
/**
 * Tracks a IntegrationUnlinked event specification.
 * ID: a6b5ac82-d460-48d9-8f2e-c437b7bfe8fe
 */
export function trackIntegrationUnlinkedSpec(integrationUnlinked: IntegrationUnlinked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a6b5ac82-d460-48d9-8f2e-c437b7bfe8fe',
        version: 2,
        name: 'integration_unlinked',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationUnlinked.context)
        ? [...integrationUnlinked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationUnlinked = {
        ...integrationUnlinked,
        context,
    };

    trackIntegrationUnlinked<Record<string, unknown> | EventSpecification>(modifiedIntegrationUnlinked, trackers);
}
/**
 * Tracks a LetterCreated event specification.
 * ID: a6cdb186-275b-4ed5-82b3-cd369c1dd693
 */
export function trackLetterCreatedSpec(letterCreated: LetterCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a6cdb186-275b-4ed5-82b3-cd369c1dd693',
        version: 1,
        name: 'letter_created',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(letterCreated.context)
        ? [...letterCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLetterCreated = {
        ...letterCreated,
        context,
    };

    trackLetterCreated<Record<string, unknown> | EventSpecification>(modifiedLetterCreated, trackers);
}
/**
 * Tracks a PatientDocumentUploaded event specification.
 * ID: a7226219-f21f-4af9-a5c5-8340331a8fa9
 */
export function trackPatientDocumentUploadedSpec(patientDocumentUploaded: PatientDocumentUploaded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a7226219-f21f-4af9-a5c5-8340331a8fa9',
        version: 0,
        name: 'patient_document_uploaded',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(patientDocumentUploaded.context)
        ? [...patientDocumentUploaded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientDocumentUploaded = {
        ...patientDocumentUploaded,
        context,
    };

    trackPatientDocumentUploaded<Record<string, unknown> | EventSpecification>(modifiedPatientDocumentUploaded, trackers);
}
/**
 * Tracks a CommunityTemplateSearched event specification.
 * ID: a733f8ad-1dca-4abd-b9b9-8ab375744498
 */
export function trackCommunityTemplateSearchedSpec(communityTemplateSearched: CommunityTemplateSearched & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a733f8ad-1dca-4abd-b9b9-8ab375744498',
        version: 2,
        name: 'community_template_searched',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(communityTemplateSearched.context)
        ? [...communityTemplateSearched.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCommunityTemplateSearched = {
        ...communityTemplateSearched,
        context,
    };

    trackCommunityTemplateSearched<Record<string, unknown> | EventSpecification>(modifiedCommunityTemplateSearched, trackers);
}
/**
 * Tracks a FeatureToggled event specification.
 * ID: a7aa758c-891f-4d87-9704-bc44a69f70be
 */
export function trackFeatureToggledSpec(featureToggled: FeatureToggled & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a7aa758c-891f-4d87-9704-bc44a69f70be',
        version: 5,
        name: 'feature_toggled',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(featureToggled.context)
        ? [...featureToggled.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFeatureToggled = {
        ...featureToggled,
        context,
    };

    trackFeatureToggled<Record<string, unknown> | EventSpecification>(modifiedFeatureToggled, trackers);
}
/**
 * Tracks a EvidenceReferencesSectionToggled event specification.
 * ID: a834958a-5efa-4338-a45a-5f3ab1a1d22b
 */
export function trackEvidenceReferencesSectionToggledSpec(evidenceReferencesSectionToggled: EvidenceReferencesSectionToggled & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a834958a-5efa-4338-a45a-5f3ab1a1d22b',
        version: 4,
        name: 'evidence_references_section_toggled',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceReferencesSectionToggled.context)
        ? [...evidenceReferencesSectionToggled.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceReferencesSectionToggled = {
        ...evidenceReferencesSectionToggled,
        context,
    };

    trackEvidenceReferencesSectionToggled<Record<string, unknown> | EventSpecification>(modifiedEvidenceReferencesSectionToggled, trackers);
}
/**
 * Tracks a EvidenceCategoryClicked event specification.
 * ID: a85419d4-e895-4297-827f-0057c5345f1b
 */
export function trackEvidenceCategoryClickedSpec(evidenceCategoryClicked: EvidenceCategoryClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a85419d4-e895-4297-827f-0057c5345f1b',
        version: 1,
        name: 'evidence_category_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceCategoryClicked.context)
        ? [...evidenceCategoryClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceCategoryClicked = {
        ...evidenceCategoryClicked,
        context,
    };

    trackEvidenceCategoryClicked<Record<string, unknown> | EventSpecification>(modifiedEvidenceCategoryClicked, trackers);
}
/**
 * Tracks a EvidenceSummarySuggestionDismissed event specification.
 * ID: a9178077-8399-4f97-8e96-cbe44fa75026
 */
export function trackEvidenceSummarySuggestionDismissedSpec(evidenceSummarySuggestionDismissed: EvidenceSummarySuggestionDismissed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a9178077-8399-4f97-8e96-cbe44fa75026',
        version: 0,
        name: 'evidence_summary_suggestion_dismissed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceSummarySuggestionDismissed.context)
        ? [...evidenceSummarySuggestionDismissed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceSummarySuggestionDismissed = {
        ...evidenceSummarySuggestionDismissed,
        context,
    };

    trackEvidenceSummarySuggestionDismissed<Record<string, unknown> | EventSpecification>(modifiedEvidenceSummarySuggestionDismissed, trackers);
}
/**
 * Tracks a TeamMemberDeleted event specification.
 * ID: a99e4902-9bb0-4242-85fd-fe73e4396c6e
 */
export function trackTeamMemberDeletedSpec(teamMemberDeleted: TeamMemberDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'a99e4902-9bb0-4242-85fd-fe73e4396c6e',
        version: 1,
        name: 'team_member_deleted',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(teamMemberDeleted.context)
        ? [...teamMemberDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTeamMemberDeleted = {
        ...teamMemberDeleted,
        context,
    };

    trackTeamMemberDeleted<Record<string, unknown> | EventSpecification>(modifiedTeamMemberDeleted, trackers);
}
/**
 * Tracks a LetterSendCompleted event specification.
 * ID: ab49b775-e8ac-4963-a8fc-02da5c91c1fc
 */
export function trackLetterSendCompletedSpec(letterSendCompleted: LetterSendCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ab49b775-e8ac-4963-a8fc-02da5c91c1fc',
        version: 0,
        name: 'letter_send_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(letterSendCompleted.context)
        ? [...letterSendCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLetterSendCompleted = {
        ...letterSendCompleted,
        context,
    };

    trackLetterSendCompleted<Record<string, unknown> | EventSpecification>(modifiedLetterSendCompleted, trackers);
}
/**
 * Tracks a PatientFieldExtractionResolved event specification.
 * ID: ad303e82-7c85-4a29-b709-769c87165245
 */
export function trackPatientFieldExtractionResolvedSpec(patientFieldExtractionResolved: PatientFieldExtractionResolved & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ad303e82-7c85-4a29-b709-769c87165245',
        version: 0,
        name: 'patient_field_extraction_resolved',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(patientFieldExtractionResolved.context)
        ? [...patientFieldExtractionResolved.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientFieldExtractionResolved = {
        ...patientFieldExtractionResolved,
        context,
    };

    trackPatientFieldExtractionResolved<Record<string, unknown> | EventSpecification>(modifiedPatientFieldExtractionResolved, trackers);
}
/**
 * Tracks a OutputCreated event specification.
 * ID: ad94c3c7-3a42-49ce-aae3-29a5d3aeb171
 */
export function trackOutputCreatedSpec(outputCreated: OutputCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ad94c3c7-3a42-49ce-aae3-29a5d3aeb171',
        version: 3,
        name: 'output_created',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(outputCreated.context)
        ? [...outputCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOutputCreated = {
        ...outputCreated,
        context,
    };

    trackOutputCreated<Record<string, unknown> | EventSpecification>(modifiedOutputCreated, trackers);
}
/**
 * Tracks a NotePushFailed event specification.
 * ID: ad9fb7fb-6028-49a2-9f53-7829d8944c2a
 */
export function trackNotePushFailedSpec(notePushFailed: NotePushFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ad9fb7fb-6028-49a2-9f53-7829d8944c2a',
        version: 3,
        name: 'note_push_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(notePushFailed.context)
        ? [...notePushFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNotePushFailed = {
        ...notePushFailed,
        context,
    };

    trackNotePushFailed<Record<string, unknown> | EventSpecification>(modifiedNotePushFailed, trackers);
}
/**
 * Tracks a EhrLaunchCompleted event specification.
 * ID: addbd6d0-7d7e-4daf-ace5-e33acc2fe7f0
 */
export function trackEhrLaunchCompletedSpec(ehrLaunchCompleted: EhrLaunchCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'addbd6d0-7d7e-4daf-ace5-e33acc2fe7f0',
        version: 0,
        name: 'ehr_launch_completed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(ehrLaunchCompleted.context)
        ? [...ehrLaunchCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEhrLaunchCompleted = {
        ...ehrLaunchCompleted,
        context,
    };

    trackEhrLaunchCompleted<Record<string, unknown> | EventSpecification>(modifiedEhrLaunchCompleted, trackers);
}
/**
 * Tracks a ScheduledCallBulkUploadProcessed event specification.
 * ID: af61c2d6-90a9-4ff6-b7f5-2bfaca9c78da
 */
export function trackScheduledCallBulkUploadProcessedSpec(scheduledCallBulkUploadProcessed: ScheduledCallBulkUploadProcessed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'af61c2d6-90a9-4ff6-b7f5-2bfaca9c78da',
        version: 0,
        name: 'scheduled_call_bulk_upload_processed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(scheduledCallBulkUploadProcessed.context)
        ? [...scheduledCallBulkUploadProcessed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScheduledCallBulkUploadProcessed = {
        ...scheduledCallBulkUploadProcessed,
        context,
    };

    trackScheduledCallBulkUploadProcessed<Record<string, unknown> | EventSpecification>(modifiedScheduledCallBulkUploadProcessed, trackers);
}
/**
 * Tracks a ScheduleUploadSessionsCreated event specification.
 * ID: af78a3c9-0cd7-411b-b33b-d8605c8a1417
 */
export function trackScheduleUploadSessionsCreatedSpec(scheduleUploadSessionsCreated: ScheduleUploadSessionsCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'af78a3c9-0cd7-411b-b33b-d8605c8a1417',
        version: 1,
        name: 'schedule_upload_sessions_created',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(scheduleUploadSessionsCreated.context)
        ? [...scheduleUploadSessionsCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScheduleUploadSessionsCreated = {
        ...scheduleUploadSessionsCreated,
        context,
    };

    trackScheduleUploadSessionsCreated<Record<string, unknown> | EventSpecification>(modifiedScheduleUploadSessionsCreated, trackers);
}
/**
 * Tracks a PrechartGenerationCompleted event specification.
 * ID: b055a17b-4b88-46f5-823e-ce7a2491cf1e
 */
export function trackPrechartGenerationCompletedSpec(prechartGenerationCompleted: PrechartGenerationCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b055a17b-4b88-46f5-823e-ce7a2491cf1e',
        version: 2,
        name: 'prechart_generation_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(prechartGenerationCompleted.context)
        ? [...prechartGenerationCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPrechartGenerationCompleted = {
        ...prechartGenerationCompleted,
        context,
    };

    trackPrechartGenerationCompleted<Record<string, unknown> | EventSpecification>(modifiedPrechartGenerationCompleted, trackers);
}
/**
 * Tracks a CallRinging event specification.
 * ID: b0c1f7c8-7f38-486e-840b-8c6b82bdc798
 */
export function trackCallRingingSpec(callRinging: CallRinging & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b0c1f7c8-7f38-486e-840b-8c6b82bdc798',
        version: 0,
        name: 'call_ringing',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callRinging.context)
        ? [...callRinging.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallRinging = {
        ...callRinging,
        context,
    };

    trackCallRinging<Record<string, unknown> | EventSpecification>(modifiedCallRinging, trackers);
}
/**
 * Tracks a FlagsmithFlagEvaluated event specification.
 * ID: b0ef9e3e-8bcb-46bb-98e1-644905afd262
 */
export function trackFlagsmithFlagEvaluatedSpec(flagsmithFlagEvaluated: FlagsmithFlagEvaluated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b0ef9e3e-8bcb-46bb-98e1-644905afd262',
        version: 0,
        name: 'flagsmith_flag_evaluated',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(flagsmithFlagEvaluated.context)
        ? [...flagsmithFlagEvaluated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFlagsmithFlagEvaluated = {
        ...flagsmithFlagEvaluated,
        context,
    };

    trackFlagsmithFlagEvaluated<Record<string, unknown> | EventSpecification>(modifiedFlagsmithFlagEvaluated, trackers);
}
/**
 * Tracks a PatientDocumentProcessed event specification.
 * ID: b1609294-e079-4744-8d0b-988f9079a0d5
 */
export function trackPatientDocumentProcessedSpec(patientDocumentProcessed: PatientDocumentProcessed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b1609294-e079-4744-8d0b-988f9079a0d5',
        version: 0,
        name: 'patient_document_processed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(patientDocumentProcessed.context)
        ? [...patientDocumentProcessed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientDocumentProcessed = {
        ...patientDocumentProcessed,
        context,
    };

    trackPatientDocumentProcessed<Record<string, unknown> | EventSpecification>(modifiedPatientDocumentProcessed, trackers);
}
/**
 * Tracks a SubscriptionRenewed event specification.
 * ID: b1765cdf-dfd3-45db-8887-79e004d3408f
 */
export function trackSubscriptionRenewedSpec(subscriptionRenewed: SubscriptionRenewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b1765cdf-dfd3-45db-8887-79e004d3408f',
        version: 1,
        name: 'subscription_renewed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(subscriptionRenewed.context)
        ? [...subscriptionRenewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSubscriptionRenewed = {
        ...subscriptionRenewed,
        context,
    };

    trackSubscriptionRenewed<Record<string, unknown> | EventSpecification>(modifiedSubscriptionRenewed, trackers);
}
/**
 * Tracks a IntegrationWidgetAuthStepErrored event specification.
 * ID: b1a93b52-12dc-4fea-b34a-32208d101fe7
 */
export function trackIntegrationWidgetAuthStepErroredSpec(integrationWidgetAuthStepErrored: IntegrationWidgetAuthStepErrored & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b1a93b52-12dc-4fea-b34a-32208d101fe7',
        version: 1,
        name: 'integration_widget_auth_step_errored',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationWidgetAuthStepErrored.context)
        ? [...integrationWidgetAuthStepErrored.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationWidgetAuthStepErrored = {
        ...integrationWidgetAuthStepErrored,
        context,
    };

    trackIntegrationWidgetAuthStepErrored<Record<string, unknown> | EventSpecification>(modifiedIntegrationWidgetAuthStepErrored, trackers);
}
/**
 * Tracks a TemplateOpened event specification.
 * ID: b1fb919c-a366-4ca3-945f-1205a31e2521
 */
export function trackTemplateOpenedSpec(templateOpened: TemplateOpened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b1fb919c-a366-4ca3-945f-1205a31e2521',
        version: 1,
        name: 'template_opened',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateOpened.context)
        ? [...templateOpened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateOpened = {
        ...templateOpened,
        context,
    };

    trackTemplateOpened<Record<string, unknown> | EventSpecification>(modifiedTemplateOpened, trackers);
}
/**
 * Tracks a FeatureModeToggled event specification.
 * ID: b2d449d9-a90f-4acf-8118-553f29010f7a
 */
export function trackFeatureModeToggledSpec(featureModeToggled: FeatureModeToggled & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b2d449d9-a90f-4acf-8118-553f29010f7a',
        version: 3,
        name: 'feature_mode_toggled',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(featureModeToggled.context)
        ? [...featureModeToggled.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFeatureModeToggled = {
        ...featureModeToggled,
        context,
    };

    trackFeatureModeToggled<Record<string, unknown> | EventSpecification>(modifiedFeatureModeToggled, trackers);
}
/**
 * Tracks a CitationClicked event specification.
 * ID: b2ef308e-de14-4665-9378-1afed6b002dd
 */
export function trackCitationClickedSpec(citationClicked: CitationClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b2ef308e-de14-4665-9378-1afed6b002dd',
        version: 3,
        name: 'citation_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(citationClicked.context)
        ? [...citationClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCitationClicked = {
        ...citationClicked,
        context,
    };

    trackCitationClicked<Record<string, unknown> | EventSpecification>(modifiedCitationClicked, trackers);
}
/**
 * Tracks a NoteReverted event specification.
 * ID: b3c13a83-b171-4186-a572-5e8ddd30b29f
 */
export function trackNoteRevertedSpec(noteReverted: NoteReverted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b3c13a83-b171-4186-a572-5e8ddd30b29f',
        version: 1,
        name: 'note_reverted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteReverted.context)
        ? [...noteReverted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteReverted = {
        ...noteReverted,
        context,
    };

    trackNoteReverted<Record<string, unknown> | EventSpecification>(modifiedNoteReverted, trackers);
}
/**
 * Tracks a EvidenceShareSocialClicked event specification.
 * ID: b3c15b28-101d-4da7-86fe-9cdfb4306042
 */
export function trackEvidenceShareSocialClickedSpec(evidenceShareSocialClicked: EvidenceShareSocialClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b3c15b28-101d-4da7-86fe-9cdfb4306042',
        version: 2,
        name: 'evidence_share_social_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceShareSocialClicked.context)
        ? [...evidenceShareSocialClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceShareSocialClicked = {
        ...evidenceShareSocialClicked,
        context,
    };

    trackEvidenceShareSocialClicked<Record<string, unknown> | EventSpecification>(modifiedEvidenceShareSocialClicked, trackers);
}
/**
 * Tracks a ExtractionReviewed event specification.
 * ID: b403b7d2-dce1-445c-ba3f-29043c5e7573
 */
export function trackExtractionReviewedSpec(extractionReviewed: ExtractionReviewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b403b7d2-dce1-445c-ba3f-29043c5e7573',
        version: 1,
        name: 'extraction_reviewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(extractionReviewed.context)
        ? [...extractionReviewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedExtractionReviewed = {
        ...extractionReviewed,
        context,
    };

    trackExtractionReviewed<Record<string, unknown> | EventSpecification>(modifiedExtractionReviewed, trackers);
}
/**
 * Tracks a PatientSessionExpanded event specification.
 * ID: b455e5ca-3ec3-42d2-b767-d9d2434d9bf1
 */
export function trackPatientSessionExpandedSpec(patientSessionExpanded: PatientSessionExpanded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b455e5ca-3ec3-42d2-b767-d9d2434d9bf1',
        version: 0,
        name: 'patient_session_expanded',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(patientSessionExpanded.context)
        ? [...patientSessionExpanded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientSessionExpanded = {
        ...patientSessionExpanded,
        context,
    };

    trackPatientSessionExpanded<Record<string, unknown> | EventSpecification>(modifiedPatientSessionExpanded, trackers);
}
/**
 * Tracks a FormTemplateFieldsExtracted event specification.
 * ID: b6658b72-7b0b-4e5b-84df-a5094d06f3c6
 */
export function trackFormTemplateFieldsExtractedSpec(formTemplateFieldsExtracted: FormTemplateFieldsExtracted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b6658b72-7b0b-4e5b-84df-a5094d06f3c6',
        version: 1,
        name: 'form_template_fields_extracted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(formTemplateFieldsExtracted.context)
        ? [...formTemplateFieldsExtracted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFormTemplateFieldsExtracted = {
        ...formTemplateFieldsExtracted,
        context,
    };

    trackFormTemplateFieldsExtracted<Record<string, unknown> | EventSpecification>(modifiedFormTemplateFieldsExtracted, trackers);
}
/**
 * Tracks a PatientSelected event specification.
 * ID: b66c8b7a-036a-4c79-8d57-09e6a828017a
 */
export function trackPatientSelectedSpec(patientSelected: PatientSelected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b66c8b7a-036a-4c79-8d57-09e6a828017a',
        version: 4,
        name: 'patient_selected',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(patientSelected.context)
        ? [...patientSelected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientSelected = {
        ...patientSelected,
        context,
    };

    trackPatientSelected<Record<string, unknown> | EventSpecification>(modifiedPatientSelected, trackers);
}
/**
 * Tracks a TranslatorEvaluationErrorLogged event specification.
 * ID: b711e468-ad48-4ab2-854d-314beeb72124
 */
export function trackTranslatorEvaluationErrorLoggedSpec(translatorEvaluationErrorLogged: TranslatorEvaluationErrorLogged & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b711e468-ad48-4ab2-854d-314beeb72124',
        version: 0,
        name: 'translator_evaluation_error_logged',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(translatorEvaluationErrorLogged.context)
        ? [...translatorEvaluationErrorLogged.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTranslatorEvaluationErrorLogged = {
        ...translatorEvaluationErrorLogged,
        context,
    };

    trackTranslatorEvaluationErrorLogged<Record<string, unknown> | EventSpecification>(modifiedTranslatorEvaluationErrorLogged, trackers);
}
/**
 * Tracks a IntegrationUpdated event specification.
 * ID: b7a3298a-3907-43db-924a-fc4fd86cedfa
 */
export function trackIntegrationUpdatedSpec(integrationUpdated: IntegrationUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b7a3298a-3907-43db-924a-fc4fd86cedfa',
        version: 1,
        name: 'integration_updated',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationUpdated.context)
        ? [...integrationUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationUpdated = {
        ...integrationUpdated,
        context,
    };

    trackIntegrationUpdated<Record<string, unknown> | EventSpecification>(modifiedIntegrationUpdated, trackers);
}
/**
 * Tracks a NoteSyncFailed event specification.
 * ID: b7b30df4-e58e-4405-8851-4b7b4d80c5ab
 */
export function trackNoteSyncFailedSpec(noteSyncFailed: NoteSyncFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b7b30df4-e58e-4405-8851-4b7b4d80c5ab',
        version: 2,
        name: 'note_sync_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteSyncFailed.context)
        ? [...noteSyncFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteSyncFailed = {
        ...noteSyncFailed,
        context,
    };

    trackNoteSyncFailed<Record<string, unknown> | EventSpecification>(modifiedNoteSyncFailed, trackers);
}
/**
 * Tracks a NudgeCardReopened event specification.
 * ID: b7d033ae-c7c4-48c2-9b0c-d562fae8d113
 */
export function trackNudgeCardReopenedSpec(nudgeCardReopened: NudgeCardReopened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b7d033ae-c7c4-48c2-9b0c-d562fae8d113',
        version: 0,
        name: 'nudge_card_reopened',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(nudgeCardReopened.context)
        ? [...nudgeCardReopened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNudgeCardReopened = {
        ...nudgeCardReopened,
        context,
    };

    trackNudgeCardReopened<Record<string, unknown> | EventSpecification>(modifiedNudgeCardReopened, trackers);
}
/**
 * Tracks a CallVoicemailDetected event specification.
 * ID: b7e238e3-1f18-489e-adc2-4b2e36635813
 */
export function trackCallVoicemailDetectedSpec(callVoicemailDetected: CallVoicemailDetected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b7e238e3-1f18-489e-adc2-4b2e36635813',
        version: 0,
        name: 'call_voicemail_detected',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callVoicemailDetected.context)
        ? [...callVoicemailDetected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallVoicemailDetected = {
        ...callVoicemailDetected,
        context,
    };

    trackCallVoicemailDetected<Record<string, unknown> | EventSpecification>(modifiedCallVoicemailDetected, trackers);
}
/**
 * Tracks a SubscriptionCancelled event specification.
 * ID: b7e3a1d4-5f92-4c8e-a6b1-2d9f8e4c7a03
 */
export function trackSubscriptionCancelledSpec(subscriptionCancelled: SubscriptionCancelled & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b7e3a1d4-5f92-4c8e-a6b1-2d9f8e4c7a03',
        version: 3,
        name: 'subscription_cancelled',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(subscriptionCancelled.context)
        ? [...subscriptionCancelled.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSubscriptionCancelled = {
        ...subscriptionCancelled,
        context,
    };

    trackSubscriptionCancelled<Record<string, unknown> | EventSpecification>(modifiedSubscriptionCancelled, trackers);
}
/**
 * Tracks a ObjectiveConfigured event specification.
 * ID: b9c8c170-1bc9-49e7-ac4f-3e568030f37c
 */
export function trackObjectiveConfiguredSpec(objectiveConfigured: ObjectiveConfigured & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'b9c8c170-1bc9-49e7-ac4f-3e568030f37c',
        version: 0,
        name: 'objective_configured',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(objectiveConfigured.context)
        ? [...objectiveConfigured.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedObjectiveConfigured = {
        ...objectiveConfigured,
        context,
    };

    trackObjectiveConfigured<Record<string, unknown> | EventSpecification>(modifiedObjectiveConfigured, trackers);
}
/**
 * Tracks a SessionLinkShareOtpVerified event specification.
 * ID: ba18a28d-e949-4ef2-9d82-cb4c731a4897
 */
export function trackSessionLinkShareOtpVerifiedSpec(sessionLinkShareOtpVerified: SessionLinkShareOtpVerified & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ba18a28d-e949-4ef2-9d82-cb4c731a4897',
        version: 1,
        name: 'session_link_share_otp_verified',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(sessionLinkShareOtpVerified.context)
        ? [...sessionLinkShareOtpVerified.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionLinkShareOtpVerified = {
        ...sessionLinkShareOtpVerified,
        context,
    };

    trackSessionLinkShareOtpVerified<Record<string, unknown> | EventSpecification>(modifiedSessionLinkShareOtpVerified, trackers);
}
/**
 * Tracks a HelpViewed event specification.
 * ID: ba660a92-55a1-41c9-b7b5-fa0255a18893
 */
export function trackHelpViewedSpec(helpViewed: HelpViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ba660a92-55a1-41c9-b7b5-fa0255a18893',
        version: 2,
        name: 'help_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(helpViewed.context)
        ? [...helpViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedHelpViewed = {
        ...helpViewed,
        context,
    };

    trackHelpViewed<Record<string, unknown> | EventSpecification>(modifiedHelpViewed, trackers);
}
/**
 * Tracks a TrialStarted event specification.
 * ID: bb47948d-9823-4a76-a908-27d0164fbc9b
 */
export function trackTrialStartedSpec(trialStarted: TrialStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bb47948d-9823-4a76-a908-27d0164fbc9b',
        version: 4,
        name: 'trial_started',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(trialStarted.context)
        ? [...trialStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTrialStarted = {
        ...trialStarted,
        context,
    };

    trackTrialStarted<Record<string, unknown> | EventSpecification>(modifiedTrialStarted, trackers);
}
/**
 * Tracks a EvidenceReferenceFeedbackSubmitted event specification.
 * ID: bb54e266-a7e1-4b9e-a1cf-afe4b74f6d56
 */
export function trackEvidenceReferenceFeedbackSubmittedSpec(evidenceReferenceFeedbackSubmitted: EvidenceReferenceFeedbackSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bb54e266-a7e1-4b9e-a1cf-afe4b74f6d56',
        version: 3,
        name: 'evidence_reference_feedback_submitted',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceReferenceFeedbackSubmitted.context)
        ? [...evidenceReferenceFeedbackSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceReferenceFeedbackSubmitted = {
        ...evidenceReferenceFeedbackSubmitted,
        context,
    };

    trackEvidenceReferenceFeedbackSubmitted<Record<string, unknown> | EventSpecification>(modifiedEvidenceReferenceFeedbackSubmitted, trackers);
}
/**
 * Tracks a OfflineSyncStarted event specification.
 * ID: bb8abd21-beb7-4e83-99eb-e776e8518108
 */
export function trackOfflineSyncStartedSpec(offlineSyncStarted: OfflineSyncStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bb8abd21-beb7-4e83-99eb-e776e8518108',
        version: 0,
        name: 'offline_sync_started',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(offlineSyncStarted.context)
        ? [...offlineSyncStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOfflineSyncStarted = {
        ...offlineSyncStarted,
        context,
    };

    trackOfflineSyncStarted<Record<string, unknown> | EventSpecification>(modifiedOfflineSyncStarted, trackers);
}
/**
 * Tracks a TaskUpdated event specification.
 * ID: bb99b46b-8892-4f22-acc3-5a90eb52cc3c
 */
export function trackTaskUpdatedSpec(taskUpdated: TaskUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bb99b46b-8892-4f22-acc3-5a90eb52cc3c',
        version: 3,
        name: 'task_updated',
        data_product_id: '521bdabc-e43c-414b-a3d8-af7f5bdfb182',
        data_product_name: 'multiplayer'
    });

    const context = Array.isArray(taskUpdated.context)
        ? [...taskUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTaskUpdated = {
        ...taskUpdated,
        context,
    };

    trackTaskUpdated<Record<string, unknown> | EventSpecification>(modifiedTaskUpdated, trackers);
}
/**
 * Tracks a NoteSelectionSkipped event specification.
 * ID: bba5ca20-93a7-4280-9dd7-20d8c4a92d30
 */
export function trackNoteSelectionSkippedSpec(noteSelectionSkipped: NoteSelectionSkipped & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bba5ca20-93a7-4280-9dd7-20d8c4a92d30',
        version: 4,
        name: 'note_selection_skipped',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteSelectionSkipped.context)
        ? [...noteSelectionSkipped.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteSelectionSkipped = {
        ...noteSelectionSkipped,
        context,
    };

    trackNoteSelectionSkipped<Record<string, unknown> | EventSpecification>(modifiedNoteSelectionSkipped, trackers);
}
/**
 * Tracks a InboundCallAbandoned event specification.
 * ID: bbd96b99-d7d8-40ff-8600-eb615aba7673
 */
export function trackInboundCallAbandonedSpec(inboundCallAbandoned: InboundCallAbandoned & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bbd96b99-d7d8-40ff-8600-eb615aba7673',
        version: 0,
        name: 'inbound_call_abandoned',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(inboundCallAbandoned.context)
        ? [...inboundCallAbandoned.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedInboundCallAbandoned = {
        ...inboundCallAbandoned,
        context,
    };

    trackInboundCallAbandoned<Record<string, unknown> | EventSpecification>(modifiedInboundCallAbandoned, trackers);
}
/**
 * Tracks a TaskDelegated event specification.
 * ID: bc586e01-2739-4955-ba19-be13dc52b874
 */
export function trackTaskDelegatedSpec(taskDelegated: TaskDelegated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bc586e01-2739-4955-ba19-be13dc52b874',
        version: 0,
        name: 'task_delegated',
        data_product_id: '521bdabc-e43c-414b-a3d8-af7f5bdfb182',
        data_product_name: 'multiplayer'
    });

    const context = Array.isArray(taskDelegated.context)
        ? [...taskDelegated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTaskDelegated = {
        ...taskDelegated,
        context,
    };

    trackTaskDelegated<Record<string, unknown> | EventSpecification>(modifiedTaskDelegated, trackers);
}
/**
 * Tracks a ScheduleViewed event specification.
 * ID: bc8f6710-8416-4108-ad38-7c5ab9772345
 */
export function trackScheduleViewedSpec(scheduleViewed: ScheduleViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bc8f6710-8416-4108-ad38-7c5ab9772345',
        version: 1,
        name: 'schedule_viewed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(scheduleViewed.context)
        ? [...scheduleViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScheduleViewed = {
        ...scheduleViewed,
        context,
    };

    trackScheduleViewed<Record<string, unknown> | EventSpecification>(modifiedScheduleViewed, trackers);
}
/**
 * Tracks a CommsSetupStepAbandoned event specification.
 * ID: bcbec3f0-05ec-47cb-a4d7-d0d9d566cc55
 */
export function trackCommsSetupStepAbandonedSpec(commsSetupStepAbandoned: CommsSetupStepAbandoned & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bcbec3f0-05ec-47cb-a4d7-d0d9d566cc55',
        version: 0,
        name: 'comms_setup_step_abandoned',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(commsSetupStepAbandoned.context)
        ? [...commsSetupStepAbandoned.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCommsSetupStepAbandoned = {
        ...commsSetupStepAbandoned,
        context,
    };

    trackCommsSetupStepAbandoned<Record<string, unknown> | EventSpecification>(modifiedCommsSetupStepAbandoned, trackers);
}
/**
 * Tracks a InboxItemUpdated event specification.
 * ID: bd24e462-8ee3-44f7-9710-31c0f73d4235
 */
export function trackInboxItemUpdatedSpec(inboxItemUpdated: InboxItemUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bd24e462-8ee3-44f7-9710-31c0f73d4235',
        version: 0,
        name: 'inbox_item_updated',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(inboxItemUpdated.context)
        ? [...inboxItemUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedInboxItemUpdated = {
        ...inboxItemUpdated,
        context,
    };

    trackInboxItemUpdated<Record<string, unknown> | EventSpecification>(modifiedInboxItemUpdated, trackers);
}
/**
 * Tracks a ChatMessageSentWithContext event specification.
 * ID: bd3667e6-c65e-40e4-8fd5-787093d535e1
 */
export function trackChatMessageSentWithContextSpec(chatMessageSentWithContext: ChatMessageSentWithContext & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bd3667e6-c65e-40e4-8fd5-787093d535e1',
        version: 1,
        name: 'chat_message_sent_with_context',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(chatMessageSentWithContext.context)
        ? [...chatMessageSentWithContext.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChatMessageSentWithContext = {
        ...chatMessageSentWithContext,
        context,
    };

    trackChatMessageSentWithContext<Record<string, unknown> | EventSpecification>(modifiedChatMessageSentWithContext, trackers);
}
/**
 * Tracks a SessionTabSwitched event specification.
 * ID: bd3ab7ce-c48c-488b-91b1-5822669ab773
 */
export function trackSessionTabSwitchedSpec(sessionTabSwitched: SessionTabSwitched & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bd3ab7ce-c48c-488b-91b1-5822669ab773',
        version: 0,
        name: 'session_tab_switched',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionTabSwitched.context)
        ? [...sessionTabSwitched.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionTabSwitched = {
        ...sessionTabSwitched,
        context,
    };

    trackSessionTabSwitched<Record<string, unknown> | EventSpecification>(modifiedSessionTabSwitched, trackers);
}
/**
 * Tracks a NoteContentPasted event specification.
 * ID: bd5b4376-25bb-4e40-bfa4-e86e6e9211c3
 */
export function trackNoteContentPastedSpec(noteContentPasted: NoteContentPasted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bd5b4376-25bb-4e40-bfa4-e86e6e9211c3',
        version: 0,
        name: 'note_content_pasted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteContentPasted.context)
        ? [...noteContentPasted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteContentPasted = {
        ...noteContentPasted,
        context,
    };

    trackNoteContentPasted<Record<string, unknown> | EventSpecification>(modifiedNoteContentPasted, trackers);
}
/**
 * Tracks a TabSharingNotAvailableModalShown event specification.
 * ID: be237b8d-3d0c-41b5-8257-b4bc4dd8ce00
 */
export function trackTabSharingNotAvailableModalShownSpec(tabSharingNotAvailableModalShown: TabSharingNotAvailableModalShown & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'be237b8d-3d0c-41b5-8257-b4bc4dd8ce00',
        version: 3,
        name: 'tab_sharing_not_available_modal_shown',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(tabSharingNotAvailableModalShown.context)
        ? [...tabSharingNotAvailableModalShown.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTabSharingNotAvailableModalShown = {
        ...tabSharingNotAvailableModalShown,
        context,
    };

    trackTabSharingNotAvailableModalShown<Record<string, unknown> | EventSpecification>(modifiedTabSharingNotAvailableModalShown, trackers);
}
/**
 * Tracks a SourceRecommendationCustomised event specification.
 * ID: bef8467b-3d04-4f03-a2d5-3458a864e1a6
 */
export function trackSourceRecommendationCustomisedSpec(sourceRecommendationCustomised: SourceRecommendationCustomised & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bef8467b-3d04-4f03-a2d5-3458a864e1a6',
        version: 1,
        name: 'source_recommendation_customised',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(sourceRecommendationCustomised.context)
        ? [...sourceRecommendationCustomised.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSourceRecommendationCustomised = {
        ...sourceRecommendationCustomised,
        context,
    };

    trackSourceRecommendationCustomised<Record<string, unknown> | EventSpecification>(modifiedSourceRecommendationCustomised, trackers);
}
/**
 * Tracks a ShareNotificationReceived event specification.
 * ID: bf2db3eb-5b26-45d3-b927-dbb19dd29c31
 */
export function trackShareNotificationReceivedSpec(shareNotificationReceived: ShareNotificationReceived & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bf2db3eb-5b26-45d3-b927-dbb19dd29c31',
        version: 1,
        name: 'share_notification_received',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareNotificationReceived.context)
        ? [...shareNotificationReceived.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareNotificationReceived = {
        ...shareNotificationReceived,
        context,
    };

    trackShareNotificationReceived<Record<string, unknown> | EventSpecification>(modifiedShareNotificationReceived, trackers);
}
/**
 * Tracks a ConversationCreated event specification.
 * ID: bfe2979a-c663-4501-9486-c4756af14573
 */
export function trackConversationCreatedSpec(conversationCreated: ConversationCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bfe2979a-c663-4501-9486-c4756af14573',
        version: 0,
        name: 'conversation_created',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(conversationCreated.context)
        ? [...conversationCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedConversationCreated = {
        ...conversationCreated,
        context,
    };

    trackConversationCreated<Record<string, unknown> | EventSpecification>(modifiedConversationCreated, trackers);
}
/**
 * Tracks a TokenGenerated event specification.
 * ID: bff720ed-016f-4bf6-984e-7cbc194f6098
 */
export function trackTokenGeneratedSpec(tokenGenerated: TokenGenerated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'bff720ed-016f-4bf6-984e-7cbc194f6098',
        version: 1,
        name: 'token_generated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(tokenGenerated.context)
        ? [...tokenGenerated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTokenGenerated = {
        ...tokenGenerated,
        context,
    };

    trackTokenGenerated<Record<string, unknown> | EventSpecification>(modifiedTokenGenerated, trackers);
}
/**
 * Tracks a EhrStarted event specification.
 * ID: c0fb4bae-f332-4bc8-a3f1-2776f1e462a9
 */
export function trackEhrStartedSpec(ehrStarted: EhrStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c0fb4bae-f332-4bc8-a3f1-2776f1e462a9',
        version: 1,
        name: 'ehr_started',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(ehrStarted.context)
        ? [...ehrStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEhrStarted = {
        ...ehrStarted,
        context,
    };

    trackEhrStarted<Record<string, unknown> | EventSpecification>(modifiedEhrStarted, trackers);
}
/**
 * Tracks a IntegrationSetupStarted event specification.
 * ID: c1041307-b91d-4fc4-add2-dbe9e622b94f
 */
export function trackIntegrationSetupStartedSpec(integrationSetupStarted: IntegrationSetupStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c1041307-b91d-4fc4-add2-dbe9e622b94f',
        version: 1,
        name: 'integration_setup_started',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationSetupStarted.context)
        ? [...integrationSetupStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationSetupStarted = {
        ...integrationSetupStarted,
        context,
    };

    trackIntegrationSetupStarted<Record<string, unknown> | EventSpecification>(modifiedIntegrationSetupStarted, trackers);
}
/**
 * Tracks a ReferralLinkCreated event specification.
 * ID: c24b5fba-6b5c-4432-92bc-8d9cad1fd48f
 */
export function trackReferralLinkCreatedSpec(referralLinkCreated: ReferralLinkCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c24b5fba-6b5c-4432-92bc-8d9cad1fd48f',
        version: 2,
        name: 'referral_link_created',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(referralLinkCreated.context)
        ? [...referralLinkCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedReferralLinkCreated = {
        ...referralLinkCreated,
        context,
    };

    trackReferralLinkCreated<Record<string, unknown> | EventSpecification>(modifiedReferralLinkCreated, trackers);
}
/**
 * Tracks a UniversalDictationPostProcessingFailed event specification.
 * ID: c2ba8a31-6b05-410e-992b-f58b3a157749
 */
export function trackUniversalDictationPostProcessingFailedSpec(universalDictationPostProcessingFailed: UniversalDictationPostProcessingFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c2ba8a31-6b05-410e-992b-f58b3a157749',
        version: 2,
        name: 'universal_dictation_post_processing_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(universalDictationPostProcessingFailed.context)
        ? [...universalDictationPostProcessingFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUniversalDictationPostProcessingFailed = {
        ...universalDictationPostProcessingFailed,
        context,
    };

    trackUniversalDictationPostProcessingFailed<Record<string, unknown> | EventSpecification>(modifiedUniversalDictationPostProcessingFailed, trackers);
}
/**
 * Tracks a ScheduledCallEnqueued event specification.
 * ID: c315aabc-726d-4eb4-af00-f57c5753d5f4
 */
export function trackScheduledCallEnqueuedSpec(scheduledCallEnqueued: ScheduledCallEnqueued & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c315aabc-726d-4eb4-af00-f57c5753d5f4',
        version: 0,
        name: 'scheduled_call_enqueued',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(scheduledCallEnqueued.context)
        ? [...scheduledCallEnqueued.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScheduledCallEnqueued = {
        ...scheduledCallEnqueued,
        context,
    };

    trackScheduledCallEnqueued<Record<string, unknown> | EventSpecification>(modifiedScheduledCallEnqueued, trackers);
}
/**
 * Tracks a SessionLinkShareOtpFailed event specification.
 * ID: c3a0e0cd-7eea-4a6a-a0a3-bd8aa685dc8f
 */
export function trackSessionLinkShareOtpFailedSpec(sessionLinkShareOtpFailed: SessionLinkShareOtpFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c3a0e0cd-7eea-4a6a-a0a3-bd8aa685dc8f',
        version: 1,
        name: 'session_link_share_otp_failed',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(sessionLinkShareOtpFailed.context)
        ? [...sessionLinkShareOtpFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionLinkShareOtpFailed = {
        ...sessionLinkShareOtpFailed,
        context,
    };

    trackSessionLinkShareOtpFailed<Record<string, unknown> | EventSpecification>(modifiedSessionLinkShareOtpFailed, trackers);
}
/**
 * Tracks a EvidenceShortcutSaveToggled event specification.
 * ID: c3f50069-8138-49c6-9996-eec27d049ed0
 */
export function trackEvidenceShortcutSaveToggledSpec(evidenceShortcutSaveToggled: EvidenceShortcutSaveToggled & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c3f50069-8138-49c6-9996-eec27d049ed0',
        version: 0,
        name: 'evidence_shortcut_save_toggled',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceShortcutSaveToggled.context)
        ? [...evidenceShortcutSaveToggled.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceShortcutSaveToggled = {
        ...evidenceShortcutSaveToggled,
        context,
    };

    trackEvidenceShortcutSaveToggled<Record<string, unknown> | EventSpecification>(modifiedEvidenceShortcutSaveToggled, trackers);
}
/**
 * Tracks a CommsSetupStepCompleted event specification.
 * ID: c4e807ee-f69d-4e00-9abf-2e2065b81954
 */
export function trackCommsSetupStepCompletedSpec(commsSetupStepCompleted: CommsSetupStepCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c4e807ee-f69d-4e00-9abf-2e2065b81954',
        version: 0,
        name: 'comms_setup_step_completed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(commsSetupStepCompleted.context)
        ? [...commsSetupStepCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCommsSetupStepCompleted = {
        ...commsSetupStepCompleted,
        context,
    };

    trackCommsSetupStepCompleted<Record<string, unknown> | EventSpecification>(modifiedCommsSetupStepCompleted, trackers);
}
/**
 * Tracks a SubscriptionEvent event specification.
 * ID: c4f8a2b1-6d3e-4a9f-8b7c-1e2d3f4a5b6c
 */
export function trackSubscriptionEventSpec(subscriptionEvent: SubscriptionEvent & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c4f8a2b1-6d3e-4a9f-8b7c-1e2d3f4a5b6c',
        version: 2,
        name: 'subscription_event',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(subscriptionEvent.context)
        ? [...subscriptionEvent.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSubscriptionEvent = {
        ...subscriptionEvent,
        context,
    };

    trackSubscriptionEvent<Record<string, unknown> | EventSpecification>(modifiedSubscriptionEvent, trackers);
}
/**
 * Tracks a LegacyInlineDictationCompleted event specification.
 * ID: c5ad6b46-b1b9-4e57-b6f7-3719ac89daf7
 */
export function trackLegacyInlineDictationCompletedSpec(legacyInlineDictationCompleted: LegacyInlineDictationCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c5ad6b46-b1b9-4e57-b6f7-3719ac89daf7',
        version: 1,
        name: 'legacy_inline_dictation_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(legacyInlineDictationCompleted.context)
        ? [...legacyInlineDictationCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLegacyInlineDictationCompleted = {
        ...legacyInlineDictationCompleted,
        context,
    };

    trackLegacyInlineDictationCompleted<Record<string, unknown> | EventSpecification>(modifiedLegacyInlineDictationCompleted, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a ChronicleDeviceConnected event specification.
 * ID: c653442e-f5c6-4164-bf31-eef192b77fad
 */
export function trackChronicleDeviceConnectedSpec(chronicleDeviceConnected: ChronicleDeviceConnected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c653442e-f5c6-4164-bf31-eef192b77fad',
        version: 1,
        name: 'chronicle_device_connected',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(chronicleDeviceConnected.context)
        ? [...chronicleDeviceConnected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChronicleDeviceConnected = {
        ...chronicleDeviceConnected,
        context,
    };

    trackChronicleDeviceConnected<Record<string, unknown> | EventSpecification>(modifiedChronicleDeviceConnected, trackers);
}
/**
 * Tracks a DocumentExported event specification.
 * ID: c772bd1e-8342-4fea-8d7d-763db1d6d364
 */
export function trackDocumentExportedSpec(documentExported: DocumentExported & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c772bd1e-8342-4fea-8d7d-763db1d6d364',
        version: 2,
        name: 'document_exported',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(documentExported.context)
        ? [...documentExported.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDocumentExported = {
        ...documentExported,
        context,
    };

    trackDocumentExported<Record<string, unknown> | EventSpecification>(modifiedDocumentExported, trackers);
}
/**
 * Tracks a NoteInsertApplied event specification.
 * ID: c7c840be-7457-48a4-86b4-b6d8f2a80303
 */
export function trackNoteInsertAppliedSpec(noteInsertApplied: NoteInsertApplied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c7c840be-7457-48a4-86b4-b6d8f2a80303',
        version: 0,
        name: 'note_insert_applied',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteInsertApplied.context)
        ? [...noteInsertApplied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteInsertApplied = {
        ...noteInsertApplied,
        context,
    };

    trackNoteInsertApplied<Record<string, unknown> | EventSpecification>(modifiedNoteInsertApplied, trackers);
}
/**
 * Tracks a ShareEditRequested event specification.
 * ID: c8cf7310-a498-4353-b554-4d4141e58355
 */
export function trackShareEditRequestedSpec(shareEditRequested: ShareEditRequested & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c8cf7310-a498-4353-b554-4d4141e58355',
        version: 1,
        name: 'share_edit_requested',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareEditRequested.context)
        ? [...shareEditRequested.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareEditRequested = {
        ...shareEditRequested,
        context,
    };

    trackShareEditRequested<Record<string, unknown> | EventSpecification>(modifiedShareEditRequested, trackers);
}
/**
 * Tracks a EvidenceCopyConfirmation event specification.
 * ID: c8f46388-6229-46df-90b9-fa7cb6f15540
 */
export function trackEvidenceCopyConfirmationSpec(evidenceCopyConfirmation: EvidenceCopyConfirmation & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c8f46388-6229-46df-90b9-fa7cb6f15540',
        version: 0,
        name: 'evidence_copy_confirmation',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceCopyConfirmation.context)
        ? [...evidenceCopyConfirmation.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceCopyConfirmation = {
        ...evidenceCopyConfirmation,
        context,
    };

    trackEvidenceCopyConfirmation<Record<string, unknown> | EventSpecification>(modifiedEvidenceCopyConfirmation, trackers);
}
/**
 * Tracks a TabSharingDoShowMessageAgain event specification.
 * ID: c933a256-b89c-4fb1-a087-6f0258078799
 */
export function trackTabSharingDoShowMessageAgainSpec(tabSharingDoShowMessageAgain: TabSharingDoShowMessageAgain & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c933a256-b89c-4fb1-a087-6f0258078799',
        version: 3,
        name: 'tab_sharing_do_show_message_again',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(tabSharingDoShowMessageAgain.context)
        ? [...tabSharingDoShowMessageAgain.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTabSharingDoShowMessageAgain = {
        ...tabSharingDoShowMessageAgain,
        context,
    };

    trackTabSharingDoShowMessageAgain<Record<string, unknown> | EventSpecification>(modifiedTabSharingDoShowMessageAgain, trackers);
}
/**
 * Tracks a ScheduleUploadCompleted event specification.
 * ID: c9b6a8bd-c81e-4064-8e1c-151d8a3ea05d
 */
export function trackScheduleUploadCompletedSpec(scheduleUploadCompleted: ScheduleUploadCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'c9b6a8bd-c81e-4064-8e1c-151d8a3ea05d',
        version: 1,
        name: 'schedule_upload_completed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(scheduleUploadCompleted.context)
        ? [...scheduleUploadCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedScheduleUploadCompleted = {
        ...scheduleUploadCompleted,
        context,
    };

    trackScheduleUploadCompleted<Record<string, unknown> | EventSpecification>(modifiedScheduleUploadCompleted, trackers);
}
/**
 * Tracks a ContextLinked event specification.
 * ID: ca831e2d-a134-4e27-a307-fbd858f92072
 */
export function trackContextLinkedSpec(contextLinked: ContextLinked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ca831e2d-a134-4e27-a307-fbd858f92072',
        version: 4,
        name: 'context_linked',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(contextLinked.context)
        ? [...contextLinked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedContextLinked = {
        ...contextLinked,
        context,
    };

    trackContextLinked<Record<string, unknown> | EventSpecification>(modifiedContextLinked, trackers);
}
/**
 * Tracks a FormFillCompleted event specification.
 * ID: cb50d196-9594-441e-ac9c-fe3b2accedeb
 */
export function trackFormFillCompletedSpec(formFillCompleted: FormFillCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cb50d196-9594-441e-ac9c-fe3b2accedeb',
        version: 1,
        name: 'form_fill_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(formFillCompleted.context)
        ? [...formFillCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFormFillCompleted = {
        ...formFillCompleted,
        context,
    };

    trackFormFillCompleted<Record<string, unknown> | EventSpecification>(modifiedFormFillCompleted, trackers);
}
/**
 * Tracks a EvidenceShareLinkRevoked event specification.
 * ID: cb81aa8f-0219-4b21-b911-5d8c34c5ce62
 */
export function trackEvidenceShareLinkRevokedSpec(evidenceShareLinkRevoked: EvidenceShareLinkRevoked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cb81aa8f-0219-4b21-b911-5d8c34c5ce62',
        version: 0,
        name: 'evidence_share_link_revoked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceShareLinkRevoked.context)
        ? [...evidenceShareLinkRevoked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceShareLinkRevoked = {
        ...evidenceShareLinkRevoked,
        context,
    };

    trackEvidenceShareLinkRevoked<Record<string, unknown> | EventSpecification>(modifiedEvidenceShareLinkRevoked, trackers);
}
/**
 * Tracks a NoteGenerationFailed event specification.
 * ID: cbe3fdff-1159-49d7-b9e3-93d8962009fc
 */
export function trackNoteGenerationFailedSpec(noteGenerationFailed: NoteGenerationFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cbe3fdff-1159-49d7-b9e3-93d8962009fc',
        version: 1,
        name: 'note_generation_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteGenerationFailed.context)
        ? [...noteGenerationFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteGenerationFailed = {
        ...noteGenerationFailed,
        context,
    };

    trackNoteGenerationFailed<Record<string, unknown> | EventSpecification>(modifiedNoteGenerationFailed, trackers);
}
/**
 * Tracks a OnboardingErrorViewed event specification.
 * ID: cc19007e-f3ee-4fb2-9fcd-6830413bf536
 */
export function trackOnboardingErrorViewedSpec(onboardingErrorViewed: OnboardingErrorViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cc19007e-f3ee-4fb2-9fcd-6830413bf536',
        version: 1,
        name: 'onboarding_error_viewed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(onboardingErrorViewed.context)
        ? [...onboardingErrorViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOnboardingErrorViewed = {
        ...onboardingErrorViewed,
        context,
    };

    trackOnboardingErrorViewed<Record<string, unknown> | EventSpecification>(modifiedOnboardingErrorViewed, trackers);
}
/**
 * Tracks a LinkedSessionsMutationCompleted event specification.
 * ID: cc2a6280-afe2-4ddd-988e-341b7f68845e
 */
export function trackLinkedSessionsMutationCompletedSpec(linkedSessionsMutationCompleted: LinkedSessionsMutationCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cc2a6280-afe2-4ddd-988e-341b7f68845e',
        version: 0,
        name: 'linked_sessions_mutation_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(linkedSessionsMutationCompleted.context)
        ? [...linkedSessionsMutationCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLinkedSessionsMutationCompleted = {
        ...linkedSessionsMutationCompleted,
        context,
    };

    trackLinkedSessionsMutationCompleted<Record<string, unknown> | EventSpecification>(modifiedLinkedSessionsMutationCompleted, trackers);
}
/**
 * Tracks a NoteCreated event specification.
 * ID: cc65e3c2-3fab-46f2-a833-52209add207e
 */
export function trackNoteCreatedSpec(noteCreated: NoteCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cc65e3c2-3fab-46f2-a833-52209add207e',
        version: 6,
        name: 'note_created',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteCreated.context)
        ? [...noteCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteCreated = {
        ...noteCreated,
        context,
    };

    trackNoteCreated<Record<string, unknown> | EventSpecification>(modifiedNoteCreated, trackers);
}
/**
 * Tracks a SessionsFilterApplied event specification.
 * ID: cd1ce43b-da7e-4038-aea9-ce5c5ca24977
 */
export function trackSessionsFilterAppliedSpec(sessionsFilterApplied: SessionsFilterApplied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cd1ce43b-da7e-4038-aea9-ce5c5ca24977',
        version: 1,
        name: 'sessions_filter_applied',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionsFilterApplied.context)
        ? [...sessionsFilterApplied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionsFilterApplied = {
        ...sessionsFilterApplied,
        context,
    };

    trackSessionsFilterApplied<Record<string, unknown> | EventSpecification>(modifiedSessionsFilterApplied, trackers);
}
/**
 * Tracks a CallRecordingProcessed event specification.
 * ID: cd301dec-ce2b-4de7-b0de-7ed92012daf5
 */
export function trackCallRecordingProcessedSpec(callRecordingProcessed: CallRecordingProcessed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cd301dec-ce2b-4de7-b0de-7ed92012daf5',
        version: 0,
        name: 'call_recording_processed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callRecordingProcessed.context)
        ? [...callRecordingProcessed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallRecordingProcessed = {
        ...callRecordingProcessed,
        context,
    };

    trackCallRecordingProcessed<Record<string, unknown> | EventSpecification>(modifiedCallRecordingProcessed, trackers);
}
/**
 * Tracks a TranscriptionStarted event specification.
 * ID: cd4faf7d-47b9-4414-b40e-756913b9f43d
 */
export function trackTranscriptionStartedSpec(transcriptionStarted: TranscriptionStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cd4faf7d-47b9-4414-b40e-756913b9f43d',
        version: 3,
        name: 'transcription_started',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(transcriptionStarted.context)
        ? [...transcriptionStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTranscriptionStarted = {
        ...transcriptionStarted,
        context,
    };

    trackTranscriptionStarted<Record<string, unknown> | EventSpecification>(modifiedTranscriptionStarted, trackers);
}
/**
 * Tracks a EvidenceShareLinkCopied event specification.
 * ID: cd5dd928-73c1-4df5-a894-53a0a2d3a5ea
 */
export function trackEvidenceShareLinkCopiedSpec(evidenceShareLinkCopied: EvidenceShareLinkCopied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cd5dd928-73c1-4df5-a894-53a0a2d3a5ea',
        version: 2,
        name: 'evidence_share_link_copied',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceShareLinkCopied.context)
        ? [...evidenceShareLinkCopied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceShareLinkCopied = {
        ...evidenceShareLinkCopied,
        context,
    };

    trackEvidenceShareLinkCopied<Record<string, unknown> | EventSpecification>(modifiedEvidenceShareLinkCopied, trackers);
}
/**
 * Tracks a OnboardingStepCompleted event specification.
 * ID: cd9af297-3509-4e7e-bbdd-107cc52996ca
 */
export function trackOnboardingStepCompletedSpec(onboardingStepCompleted: OnboardingStepCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cd9af297-3509-4e7e-bbdd-107cc52996ca',
        version: 7,
        name: 'onboarding_step_completed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(onboardingStepCompleted.context)
        ? [...onboardingStepCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOnboardingStepCompleted = {
        ...onboardingStepCompleted,
        context,
    };

    trackOnboardingStepCompleted<Record<string, unknown> | EventSpecification>(modifiedOnboardingStepCompleted, trackers);
}
/**
 * Tracks a NoteSubmitted event specification.
 * ID: ce1ceac4-0f9b-45dd-8046-f1c926fd4ed8
 */
export function trackNoteSubmittedSpec(noteSubmitted: NoteSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ce1ceac4-0f9b-45dd-8046-f1c926fd4ed8',
        version: 1,
        name: 'note_submitted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteSubmitted.context)
        ? [...noteSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteSubmitted = {
        ...noteSubmitted,
        context,
    };

    trackNoteSubmitted<Record<string, unknown> | EventSpecification>(modifiedNoteSubmitted, trackers);
}
/**
 * Tracks a SofLaunch event specification.
 * ID: cee45b1d-f508-46ef-bb7d-9e2c88900d53
 */
export function trackSofLaunchSpec(sofLaunch: SofLaunch & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cee45b1d-f508-46ef-bb7d-9e2c88900d53',
        version: 1,
        name: 'sof_launch',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(sofLaunch.context)
        ? [...sofLaunch.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSofLaunch = {
        ...sofLaunch,
        context,
    };

    trackSofLaunch<Record<string, unknown> | EventSpecification>(modifiedSofLaunch, trackers);
}
/**
 * Tracks a Heidi2_Activated event specification.
 * ID: cf63aef4-ded1-40cf-b614-7785b48209b3
 */
export function trackHeidi2_ActivatedSpec(heidi2_Activated: Heidi2_Activated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cf63aef4-ded1-40cf-b614-7785b48209b3',
        version: 0,
        name: 'heidi_2_activated',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(heidi2_Activated.context)
        ? [...heidi2_Activated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedHeidi2_Activated = {
        ...heidi2_Activated,
        context,
    };

    trackHeidi2_Activated<Record<string, unknown> | EventSpecification>(modifiedHeidi2_Activated, trackers);
}
/**
 * Tracks a PanelClosed event specification.
 * ID: cfdc6a93-45ef-4598-bbd9-76cb901f3004
 */
export function trackPanelClosedSpec(panelClosed: PanelClosed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'cfdc6a93-45ef-4598-bbd9-76cb901f3004',
        version: 3,
        name: 'panel_closed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(panelClosed.context)
        ? [...panelClosed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPanelClosed = {
        ...panelClosed,
        context,
    };

    trackPanelClosed<Record<string, unknown> | EventSpecification>(modifiedPanelClosed, trackers);
}
/**
 * Tracks a TaskNotificationClicked event specification.
 * ID: d0106eee-680d-4bca-8aed-5c5e5282c5e7
 */
export function trackTaskNotificationClickedSpec(taskNotificationClicked: TaskNotificationClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd0106eee-680d-4bca-8aed-5c5e5282c5e7',
        version: 0,
        name: 'task_notification_clicked',
        data_product_id: '521bdabc-e43c-414b-a3d8-af7f5bdfb182',
        data_product_name: 'multiplayer'
    });

    const context = Array.isArray(taskNotificationClicked.context)
        ? [...taskNotificationClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTaskNotificationClicked = {
        ...taskNotificationClicked,
        context,
    };

    trackTaskNotificationClicked<Record<string, unknown> | EventSpecification>(modifiedTaskNotificationClicked, trackers);
}
/**
 * Tracks a UserUpdated event specification.
 * ID: d03a7558-2b59-4eb8-b8a2-e0889ba5ccd7
 */
export function trackUserUpdatedSpec(userUpdated: UserUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd03a7558-2b59-4eb8-b8a2-e0889ba5ccd7',
        version: 1,
        name: 'user_updated',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(userUpdated.context)
        ? [...userUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUserUpdated = {
        ...userUpdated,
        context,
    };

    trackUserUpdated<Record<string, unknown> | EventSpecification>(modifiedUserUpdated, trackers);
}
/**
 * Tracks a FeedbackSubmitted event specification.
 * ID: d05c92ef-22f1-4014-9e86-303666098e25
 */
export function trackFeedbackSubmittedSpec(feedbackSubmitted: FeedbackSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd05c92ef-22f1-4014-9e86-303666098e25',
        version: 5,
        name: 'feedback_submitted',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(feedbackSubmitted.context)
        ? [...feedbackSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedFeedbackSubmitted = {
        ...feedbackSubmitted,
        context,
    };

    trackFeedbackSubmitted<Record<string, unknown> | EventSpecification>(modifiedFeedbackSubmitted, trackers);
}
/**
 * Tracks a DarkModeSpotlightDismissed event specification.
 * ID: d13d96bf-802b-44b0-977c-a068a1b8168d
 */
export function trackDarkModeSpotlightDismissedSpec(darkModeSpotlightDismissed: DarkModeSpotlightDismissed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd13d96bf-802b-44b0-977c-a068a1b8168d',
        version: 0,
        name: 'dark_mode_spotlight_dismissed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(darkModeSpotlightDismissed.context)
        ? [...darkModeSpotlightDismissed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDarkModeSpotlightDismissed = {
        ...darkModeSpotlightDismissed,
        context,
    };

    trackDarkModeSpotlightDismissed<Record<string, unknown> | EventSpecification>(modifiedDarkModeSpotlightDismissed, trackers);
}
/**
 * Tracks a ClinicalCodeClickable event specification.
 * ID: d2a769d9-1f46-476d-9a26-aa6488fd0203
 */
export function trackClinicalCodeClickableSpec(clinicalCodeClickable: ClinicalCodeClickable & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd2a769d9-1f46-476d-9a26-aa6488fd0203',
        version: 1,
        name: 'clinical_code_clickable',
        data_product_id: '97404acb-340e-4ca4-bf39-5e42ff587704',
        data_product_name: 'revenue_cycle'
    });

    const context = Array.isArray(clinicalCodeClickable.context)
        ? [...clinicalCodeClickable.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedClinicalCodeClickable = {
        ...clinicalCodeClickable,
        context,
    };

    trackClinicalCodeClickable<Record<string, unknown> | EventSpecification>(modifiedClinicalCodeClickable, trackers);
}
/**
 * Tracks a UniversalDictationCsatSubmitted event specification.
 * ID: d2c8a001-2d7f-454e-9889-d05a4df936fa
 */
export function trackUniversalDictationCsatSubmittedSpec(universalDictationCsatSubmitted: UniversalDictationCsatSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd2c8a001-2d7f-454e-9889-d05a4df936fa',
        version: 1,
        name: 'universal_dictation_csat_submitted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(universalDictationCsatSubmitted.context)
        ? [...universalDictationCsatSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUniversalDictationCsatSubmitted = {
        ...universalDictationCsatSubmitted,
        context,
    };

    trackUniversalDictationCsatSubmitted<Record<string, unknown> | EventSpecification>(modifiedUniversalDictationCsatSubmitted, trackers);
}
/**
 * Tracks a ShareAccessChanged event specification.
 * ID: d332f4bf-d6ce-4f4d-af2d-45011205127d
 */
export function trackShareAccessChangedSpec(shareAccessChanged: ShareAccessChanged & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd332f4bf-d6ce-4f4d-af2d-45011205127d',
        version: 1,
        name: 'share_access_changed',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareAccessChanged.context)
        ? [...shareAccessChanged.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareAccessChanged = {
        ...shareAccessChanged,
        context,
    };

    trackShareAccessChanged<Record<string, unknown> | EventSpecification>(modifiedShareAccessChanged, trackers);
}
/**
 * Tracks a AudioWarningViewed event specification.
 * ID: d352a435-8736-41eb-bafc-c5d49b7464d7
 */
export function trackAudioWarningViewedSpec(audioWarningViewed: AudioWarningViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd352a435-8736-41eb-bafc-c5d49b7464d7',
        version: 0,
        name: 'audio_warning_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(audioWarningViewed.context)
        ? [...audioWarningViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAudioWarningViewed = {
        ...audioWarningViewed,
        context,
    };

    trackAudioWarningViewed<Record<string, unknown> | EventSpecification>(modifiedAudioWarningViewed, trackers);
}
/**
 * Tracks a IntegrationWidgetAuthStepMisc event specification.
 * ID: d4055260-d743-4777-a3b4-7975d84cf9d9
 */
export function trackIntegrationWidgetAuthStepMiscSpec(integrationWidgetAuthStepMisc: IntegrationWidgetAuthStepMisc & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd4055260-d743-4777-a3b4-7975d84cf9d9',
        version: 1,
        name: 'integration_widget_auth_step_misc',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationWidgetAuthStepMisc.context)
        ? [...integrationWidgetAuthStepMisc.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationWidgetAuthStepMisc = {
        ...integrationWidgetAuthStepMisc,
        context,
    };

    trackIntegrationWidgetAuthStepMisc<Record<string, unknown> | EventSpecification>(modifiedIntegrationWidgetAuthStepMisc, trackers);
}
/**
 * Tracks a VerificationSuggestedFixApplied event specification.
 * ID: d4856211-8ff2-4d87-bd3b-86b3ad497ecc
 */
export function trackVerificationSuggestedFixAppliedSpec(verificationSuggestedFixApplied: VerificationSuggestedFixApplied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd4856211-8ff2-4d87-bd3b-86b3ad497ecc',
        version: 1,
        name: 'verification_suggested_fix_applied',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(verificationSuggestedFixApplied.context)
        ? [...verificationSuggestedFixApplied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedVerificationSuggestedFixApplied = {
        ...verificationSuggestedFixApplied,
        context,
    };

    trackVerificationSuggestedFixApplied<Record<string, unknown> | EventSpecification>(modifiedVerificationSuggestedFixApplied, trackers);
}
/**
 * Tracks a TemplateConverted event specification.
 * ID: d4cf0ba8-55c5-42a8-a833-ed8e51f8803a
 */
export function trackTemplateConvertedSpec(templateConverted: TemplateConverted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd4cf0ba8-55c5-42a8-a833-ed8e51f8803a',
        version: 1,
        name: 'template_converted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateConverted.context)
        ? [...templateConverted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateConverted = {
        ...templateConverted,
        context,
    };

    trackTemplateConverted<Record<string, unknown> | EventSpecification>(modifiedTemplateConverted, trackers);
}
/**
 * Tracks a CheckoutStarted event specification.
 * ID: d4ff087f-7a6f-441b-acad-3e7997aa8343
 */
export function trackCheckoutStartedSpec(checkoutStarted: CheckoutStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd4ff087f-7a6f-441b-acad-3e7997aa8343',
        version: 2,
        name: 'checkout_started',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(checkoutStarted.context)
        ? [...checkoutStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCheckoutStarted = {
        ...checkoutStarted,
        context,
    };

    trackCheckoutStarted<Record<string, unknown> | EventSpecification>(modifiedCheckoutStarted, trackers);
}
/**
 * Tracks a OutboundMessageDelivered event specification.
 * ID: d52e1287-ea77-474c-b15d-e9bfb2aa3fc0
 */
export function trackOutboundMessageDeliveredSpec(outboundMessageDelivered: OutboundMessageDelivered & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd52e1287-ea77-474c-b15d-e9bfb2aa3fc0',
        version: 0,
        name: 'outbound_message_delivered',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(outboundMessageDelivered.context)
        ? [...outboundMessageDelivered.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOutboundMessageDelivered = {
        ...outboundMessageDelivered,
        context,
    };

    trackOutboundMessageDelivered<Record<string, unknown> | EventSpecification>(modifiedOutboundMessageDelivered, trackers);
}
/**
 * Tracks a CpdDraftSubmitted event specification.
 * ID: d5d518f1-6abd-4a6a-953c-4e10cb6c7fa2
 */
export function trackCpdDraftSubmittedSpec(cpdDraftSubmitted: CpdDraftSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd5d518f1-6abd-4a6a-953c-4e10cb6c7fa2',
        version: 1,
        name: 'cpd_draft_submitted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(cpdDraftSubmitted.context)
        ? [...cpdDraftSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCpdDraftSubmitted = {
        ...cpdDraftSubmitted,
        context,
    };

    trackCpdDraftSubmitted<Record<string, unknown> | EventSpecification>(modifiedCpdDraftSubmitted, trackers);
}
/**
 * Tracks a IntegrationDataCompleted event specification.
 * ID: d5ece399-7dfa-4d96-8d88-4de3afa98db4
 */
export function trackIntegrationDataCompletedSpec(integrationDataCompleted: IntegrationDataCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd5ece399-7dfa-4d96-8d88-4de3afa98db4',
        version: 1,
        name: 'integration_data_completed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationDataCompleted.context)
        ? [...integrationDataCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationDataCompleted = {
        ...integrationDataCompleted,
        context,
    };

    trackIntegrationDataCompleted<Record<string, unknown> | EventSpecification>(modifiedIntegrationDataCompleted, trackers);
}
/**
 * Tracks a PushWarningViewed event specification.
 * ID: d633e6a2-d522-462b-a7aa-d27670e83b74
 */
export function trackPushWarningViewedSpec(pushWarningViewed: PushWarningViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd633e6a2-d522-462b-a7aa-d27670e83b74',
        version: 0,
        name: 'push_warning_viewed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(pushWarningViewed.context)
        ? [...pushWarningViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPushWarningViewed = {
        ...pushWarningViewed,
        context,
    };

    trackPushWarningViewed<Record<string, unknown> | EventSpecification>(modifiedPushWarningViewed, trackers);
}
/**
 * Tracks a SourceRecommendationDismissed event specification.
 * ID: d635d364-edf2-4461-b05a-11f0ed5bf832
 */
export function trackSourceRecommendationDismissedSpec(sourceRecommendationDismissed: SourceRecommendationDismissed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd635d364-edf2-4461-b05a-11f0ed5bf832',
        version: 1,
        name: 'source_recommendation_dismissed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(sourceRecommendationDismissed.context)
        ? [...sourceRecommendationDismissed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSourceRecommendationDismissed = {
        ...sourceRecommendationDismissed,
        context,
    };

    trackSourceRecommendationDismissed<Record<string, unknown> | EventSpecification>(modifiedSourceRecommendationDismissed, trackers);
}
/**
 * Tracks a OfflineRecordingQueued event specification.
 * ID: d685a778-9ebf-4fda-abba-19240a1241fa
 */
export function trackOfflineRecordingQueuedSpec(offlineRecordingQueued: OfflineRecordingQueued & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd685a778-9ebf-4fda-abba-19240a1241fa',
        version: 0,
        name: 'offline_recording_queued',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(offlineRecordingQueued.context)
        ? [...offlineRecordingQueued.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOfflineRecordingQueued = {
        ...offlineRecordingQueued,
        context,
    };

    trackOfflineRecordingQueued<Record<string, unknown> | EventSpecification>(modifiedOfflineRecordingQueued, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a HardwareDeviceRemoved event specification.
 * ID: d6e4a912-7c3b-4f85-b1a0-9e8d2f63c4a7
 */
export function trackHardwareDeviceRemovedSpec(hardwareDeviceRemoved: HardwareDeviceRemoved & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd6e4a912-7c3b-4f85-b1a0-9e8d2f63c4a7',
        version: 0,
        name: 'hardware_device_removed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(hardwareDeviceRemoved.context)
        ? [...hardwareDeviceRemoved.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedHardwareDeviceRemoved = {
        ...hardwareDeviceRemoved,
        context,
    };

    trackHardwareDeviceRemoved<Record<string, unknown> | EventSpecification>(modifiedHardwareDeviceRemoved, trackers);
}
/**
 * Tracks a ReferralLinkCopied event specification.
 * ID: d80bbebe-56eb-4cfd-99c6-780b4ceb31e5
 */
export function trackReferralLinkCopiedSpec(referralLinkCopied: ReferralLinkCopied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd80bbebe-56eb-4cfd-99c6-780b4ceb31e5',
        version: 2,
        name: 'referral_link_copied',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(referralLinkCopied.context)
        ? [...referralLinkCopied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedReferralLinkCopied = {
        ...referralLinkCopied,
        context,
    };

    trackReferralLinkCopied<Record<string, unknown> | EventSpecification>(modifiedReferralLinkCopied, trackers);
}
/**
 * Tracks a EvidenceCheckOtherSourcesClicked event specification.
 * ID: d81563e7-0ceb-4c10-90e5-40f505cc41e9
 */
export function trackEvidenceCheckOtherSourcesClickedSpec(evidenceCheckOtherSourcesClicked: EvidenceCheckOtherSourcesClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd81563e7-0ceb-4c10-90e5-40f505cc41e9',
        version: 0,
        name: 'evidence_check_other_sources_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceCheckOtherSourcesClicked.context)
        ? [...evidenceCheckOtherSourcesClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceCheckOtherSourcesClicked = {
        ...evidenceCheckOtherSourcesClicked,
        context,
    };

    trackEvidenceCheckOtherSourcesClicked<Record<string, unknown> | EventSpecification>(modifiedEvidenceCheckOtherSourcesClicked, trackers);
}
/**
 * Tracks a ChatContextAddSessionClicked event specification.
 * ID: d821181d-e595-45dd-99b5-3d3dc21aac4b
 */
export function trackChatContextAddSessionClickedSpec(chatContextAddSessionClicked: ChatContextAddSessionClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd821181d-e595-45dd-99b5-3d3dc21aac4b',
        version: 1,
        name: 'chat_context_add_session_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(chatContextAddSessionClicked.context)
        ? [...chatContextAddSessionClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChatContextAddSessionClicked = {
        ...chatContextAddSessionClicked,
        context,
    };

    trackChatContextAddSessionClicked<Record<string, unknown> | EventSpecification>(modifiedChatContextAddSessionClicked, trackers);
}
/**
 * Tracks a NoteDisplayed event specification.
 * ID: d8affcc9-27fb-41b2-b710-eb535dee02af
 */
export function trackNoteDisplayedSpec(noteDisplayed: NoteDisplayed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd8affcc9-27fb-41b2-b710-eb535dee02af',
        version: 1,
        name: 'note_displayed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteDisplayed.context)
        ? [...noteDisplayed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteDisplayed = {
        ...noteDisplayed,
        context,
    };

    trackNoteDisplayed<Record<string, unknown> | EventSpecification>(modifiedNoteDisplayed, trackers);
}
/**
 * Tracks a SnippetUploaded event specification.
 * ID: d8c8b5b4-c308-4fc8-ba4c-9a6201173ec2
 */
export function trackSnippetUploadedSpec(snippetUploaded: SnippetUploaded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd8c8b5b4-c308-4fc8-ba4c-9a6201173ec2',
        version: 1,
        name: 'snippet_uploaded',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(snippetUploaded.context)
        ? [...snippetUploaded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSnippetUploaded = {
        ...snippetUploaded,
        context,
    };

    trackSnippetUploaded<Record<string, unknown> | EventSpecification>(modifiedSnippetUploaded, trackers);
}
/**
 * Tracks a TeamMemberCreated event specification.
 * ID: d9a77ca4-9e10-4781-b8a2-5476010bb83f
 */
export function trackTeamMemberCreatedSpec(teamMemberCreated: TeamMemberCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'd9a77ca4-9e10-4781-b8a2-5476010bb83f',
        version: 1,
        name: 'team_member_created',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(teamMemberCreated.context)
        ? [...teamMemberCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTeamMemberCreated = {
        ...teamMemberCreated,
        context,
    };

    trackTeamMemberCreated<Record<string, unknown> | EventSpecification>(modifiedTeamMemberCreated, trackers);
}
/**
 * Tracks a NoteEditStarted event specification.
 * ID: da8989d4-b54e-467b-9576-7d4926e512b3
 */
export function trackNoteEditStartedSpec(noteEditStarted: NoteEditStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'da8989d4-b54e-467b-9576-7d4926e512b3',
        version: 0,
        name: 'note_edit_started',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(noteEditStarted.context)
        ? [...noteEditStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteEditStarted = {
        ...noteEditStarted,
        context,
    };

    trackNoteEditStarted<Record<string, unknown> | EventSpecification>(modifiedNoteEditStarted, trackers);
}
/**
 * Tracks a WorkVoiceHandoffCompleted event specification.
 * ID: da8abec6-1b00-5b6f-aef7-8744ff528649
 */
export function trackWorkVoiceHandoffCompletedSpec(workVoiceHandoffCompleted: WorkVoiceHandoffCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'da8abec6-1b00-5b6f-aef7-8744ff528649',
        version: 0,
        name: 'work_voice_handoff_completed',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workVoiceHandoffCompleted.context)
        ? [...workVoiceHandoffCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkVoiceHandoffCompleted = {
        ...workVoiceHandoffCompleted,
        context,
    };

    trackWorkVoiceHandoffCompleted<Record<string, unknown> | EventSpecification>(modifiedWorkVoiceHandoffCompleted, trackers);
}
/**
 * Tracks a ReferralShared event specification.
 * ID: dab5cae4-9f32-4a3e-ab1e-ce0a08f507f5
 */
export function trackReferralSharedSpec(referralShared: ReferralShared & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'dab5cae4-9f32-4a3e-ab1e-ce0a08f507f5',
        version: 1,
        name: 'referral_shared',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(referralShared.context)
        ? [...referralShared.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedReferralShared = {
        ...referralShared,
        context,
    };

    trackReferralShared<Record<string, unknown> | EventSpecification>(modifiedReferralShared, trackers);
}
/**
 * Tracks a WorkRunCompleted event specification.
 * ID: dabd0f06-916c-59e7-be00-13bdcd2bdadf
 */
export function trackWorkRunCompletedSpec(workRunCompleted: WorkRunCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'dabd0f06-916c-59e7-be00-13bdcd2bdadf',
        version: 2,
        name: 'work_run_completed',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workRunCompleted.context)
        ? [...workRunCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkRunCompleted = {
        ...workRunCompleted,
        context,
    };

    trackWorkRunCompleted<Record<string, unknown> | EventSpecification>(modifiedWorkRunCompleted, trackers);
}
/**
 * Tracks a TemplateFailed event specification.
 * ID: db17d4be-a197-40fa-a97d-9f7d72d019a7
 */
export function trackTemplateFailedSpec(templateFailed: TemplateFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'db17d4be-a197-40fa-a97d-9f7d72d019a7',
        version: 1,
        name: 'template_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateFailed.context)
        ? [...templateFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateFailed = {
        ...templateFailed,
        context,
    };

    trackTemplateFailed<Record<string, unknown> | EventSpecification>(modifiedTemplateFailed, trackers);
}
/**
 * Tracks a UniversalDictationCompleted event specification.
 * ID: dbcf9017-eda7-49b6-8846-e89d087b8271
 */
export function trackUniversalDictationCompletedSpec(universalDictationCompleted: UniversalDictationCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'dbcf9017-eda7-49b6-8846-e89d087b8271',
        version: 2,
        name: 'universal_dictation_completed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(universalDictationCompleted.context)
        ? [...universalDictationCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUniversalDictationCompleted = {
        ...universalDictationCompleted,
        context,
    };

    trackUniversalDictationCompleted<Record<string, unknown> | EventSpecification>(modifiedUniversalDictationCompleted, trackers);
}
/**
 * Tracks a TeamHeidiIiAccessUpdated event specification.
 * ID: dbd679e7-7523-492b-94f5-53d6540a0666
 */
export function trackTeamHeidiIiAccessUpdatedSpec(teamHeidiIiAccessUpdated: TeamHeidiIiAccessUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'dbd679e7-7523-492b-94f5-53d6540a0666',
        version: 1,
        name: 'team_heidi_ii_access_updated',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(teamHeidiIiAccessUpdated.context)
        ? [...teamHeidiIiAccessUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTeamHeidiIiAccessUpdated = {
        ...teamHeidiIiAccessUpdated,
        context,
    };

    trackTeamHeidiIiAccessUpdated<Record<string, unknown> | EventSpecification>(modifiedTeamHeidiIiAccessUpdated, trackers);
}
/**
 * Tracks a CallSummaryGenerated event specification.
 * ID: dbf6dc83-019e-42d2-9a5a-0c386b310752
 */
export function trackCallSummaryGeneratedSpec(callSummaryGenerated: CallSummaryGenerated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'dbf6dc83-019e-42d2-9a5a-0c386b310752',
        version: 0,
        name: 'call_summary_generated',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callSummaryGenerated.context)
        ? [...callSummaryGenerated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallSummaryGenerated = {
        ...callSummaryGenerated,
        context,
    };

    trackCallSummaryGenerated<Record<string, unknown> | EventSpecification>(modifiedCallSummaryGenerated, trackers);
}
/**
 * Tracks a CallSummaryEmailFailed event specification.
 * ID: dc428869-ce2c-412a-8db0-22f041519306
 */
export function trackCallSummaryEmailFailedSpec(callSummaryEmailFailed: CallSummaryEmailFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'dc428869-ce2c-412a-8db0-22f041519306',
        version: 0,
        name: 'call_summary_email_failed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callSummaryEmailFailed.context)
        ? [...callSummaryEmailFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallSummaryEmailFailed = {
        ...callSummaryEmailFailed,
        context,
    };

    trackCallSummaryEmailFailed<Record<string, unknown> | EventSpecification>(modifiedCallSummaryEmailFailed, trackers);
}
/**
 * Tracks a LetterAttachmentRemoved event specification.
 * ID: dc7d8a5e-1b64-4b56-a120-3cb000694f2f
 */
export function trackLetterAttachmentRemovedSpec(letterAttachmentRemoved: LetterAttachmentRemoved & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'dc7d8a5e-1b64-4b56-a120-3cb000694f2f',
        version: 0,
        name: 'letter_attachment_removed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(letterAttachmentRemoved.context)
        ? [...letterAttachmentRemoved.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLetterAttachmentRemoved = {
        ...letterAttachmentRemoved,
        context,
    };

    trackLetterAttachmentRemoved<Record<string, unknown> | EventSpecification>(modifiedLetterAttachmentRemoved, trackers);
}
/**
 * Tracks a ContextFileUploadFailed event specification.
 * ID: dcad0799-33ed-4e69-a655-846ecf1fc2cc
 */
export function trackContextFileUploadFailedSpec(contextFileUploadFailed: ContextFileUploadFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'dcad0799-33ed-4e69-a655-846ecf1fc2cc',
        version: 1,
        name: 'context_file_upload_failed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(contextFileUploadFailed.context)
        ? [...contextFileUploadFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedContextFileUploadFailed = {
        ...contextFileUploadFailed,
        context,
    };

    trackContextFileUploadFailed<Record<string, unknown> | EventSpecification>(modifiedContextFileUploadFailed, trackers);
}
/**
 * Tracks a ReferralCompleted event specification.
 * ID: dd099377-029a-406f-9206-b16e6dc1c614
 */
export function trackReferralCompletedSpec(referralCompleted: ReferralCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'dd099377-029a-406f-9206-b16e6dc1c614',
        version: 1,
        name: 'referral_completed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(referralCompleted.context)
        ? [...referralCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedReferralCompleted = {
        ...referralCompleted,
        context,
    };

    trackReferralCompleted<Record<string, unknown> | EventSpecification>(modifiedReferralCompleted, trackers);
}
/**
 * Tracks a EvidenceShortcutsOpened event specification.
 * ID: dd205bf2-302d-4455-ad7c-b283bf2b3440
 */
export function trackEvidenceShortcutsOpenedSpec(evidenceShortcutsOpened: EvidenceShortcutsOpened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'dd205bf2-302d-4455-ad7c-b283bf2b3440',
        version: 0,
        name: 'evidence_shortcuts_opened',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceShortcutsOpened.context)
        ? [...evidenceShortcutsOpened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceShortcutsOpened = {
        ...evidenceShortcutsOpened,
        context,
    };

    trackEvidenceShortcutsOpened<Record<string, unknown> | EventSpecification>(modifiedEvidenceShortcutsOpened, trackers);
}
/**
 * Tracks a TemplateGeneration event specification.
 * ID: ddc1bf64-5132-4c25-99bb-2d25e5a7f272
 */
export function trackTemplateGenerationSpec(templateGeneration: TemplateGeneration & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ddc1bf64-5132-4c25-99bb-2d25e5a7f272',
        version: 2,
        name: 'template_generation',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateGeneration.context)
        ? [...templateGeneration.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateGeneration = {
        ...templateGeneration,
        context,
    };

    trackTemplateGeneration<Record<string, unknown> | EventSpecification>(modifiedTemplateGeneration, trackers);
}
/**
 * Tracks a VoiceStyleUpdated event specification.
 * ID: ddffd83e-2356-42f9-aeef-f30ea63b8c0a
 */
export function trackVoiceStyleUpdatedSpec(voiceStyleUpdated: VoiceStyleUpdated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ddffd83e-2356-42f9-aeef-f30ea63b8c0a',
        version: 1,
        name: 'voice_style_updated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(voiceStyleUpdated.context)
        ? [...voiceStyleUpdated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedVoiceStyleUpdated = {
        ...voiceStyleUpdated,
        context,
    };

    trackVoiceStyleUpdated<Record<string, unknown> | EventSpecification>(modifiedVoiceStyleUpdated, trackers);
}
/**
 * Tracks a SurveySubmitted event specification.
 * ID: de9b346d-65c6-4282-982f-590d71a14c24
 */
export function trackSurveySubmittedSpec(surveySubmitted: SurveySubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'de9b346d-65c6-4282-982f-590d71a14c24',
        version: 2,
        name: 'survey_submitted',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(surveySubmitted.context)
        ? [...surveySubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSurveySubmitted = {
        ...surveySubmitted,
        context,
    };

    trackSurveySubmitted<Record<string, unknown> | EventSpecification>(modifiedSurveySubmitted, trackers);
}
/**
 * Tracks a ShareModalOpened event specification.
 * ID: ded8a3a0-f94c-443e-a608-87fa1b7da665
 */
export function trackShareModalOpenedSpec(shareModalOpened: ShareModalOpened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ded8a3a0-f94c-443e-a608-87fa1b7da665',
        version: 1,
        name: 'share_modal_opened',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareModalOpened.context)
        ? [...shareModalOpened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareModalOpened = {
        ...shareModalOpened,
        context,
    };

    trackShareModalOpened<Record<string, unknown> | EventSpecification>(modifiedShareModalOpened, trackers);
}
/**
 * Tracks a EvidencePromptsBarExpanded event specification.
 * ID: dfebba8d-5a9e-42cc-9500-58f7e700a893
 */
export function trackEvidencePromptsBarExpandedSpec(evidencePromptsBarExpanded: EvidencePromptsBarExpanded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'dfebba8d-5a9e-42cc-9500-58f7e700a893',
        version: 0,
        name: 'evidence_prompts_bar_expanded',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidencePromptsBarExpanded.context)
        ? [...evidencePromptsBarExpanded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidencePromptsBarExpanded = {
        ...evidencePromptsBarExpanded,
        context,
    };

    trackEvidencePromptsBarExpanded<Record<string, unknown> | EventSpecification>(modifiedEvidencePromptsBarExpanded, trackers);
}
/**
 * Tracks a WorkFollowUpActioned event specification.
 * ID: e0029678-60af-5aa4-93dc-d0835c378e43
 */
export function trackWorkFollowUpActionedSpec(workFollowUpActioned: WorkFollowUpActioned & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e0029678-60af-5aa4-93dc-d0835c378e43',
        version: 0,
        name: 'work_follow_up_actioned',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workFollowUpActioned.context)
        ? [...workFollowUpActioned.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkFollowUpActioned = {
        ...workFollowUpActioned,
        context,
    };

    trackWorkFollowUpActioned<Record<string, unknown> | EventSpecification>(modifiedWorkFollowUpActioned, trackers);
}
/**
 * Tracks a SessionViewed event specification.
 * ID: e0d91340-4dc2-46e0-8e3b-7da23dc009e5
 */
export function trackSessionViewedSpec(sessionViewed: SessionViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e0d91340-4dc2-46e0-8e3b-7da23dc009e5',
        version: 4,
        name: 'session_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionViewed.context)
        ? [...sessionViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionViewed = {
        ...sessionViewed,
        context,
    };

    trackSessionViewed<Record<string, unknown> | EventSpecification>(modifiedSessionViewed, trackers);
}
/**
 * Tracks a ContactSalesFormSubmitted event specification.
 * ID: e1a522d6-be1a-493f-90ea-80ec1ed7b847
 */
export function trackContactSalesFormSubmittedSpec(contactSalesFormSubmitted: ContactSalesFormSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e1a522d6-be1a-493f-90ea-80ec1ed7b847',
        version: 1,
        name: 'contact_sales_form_submitted',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(contactSalesFormSubmitted.context)
        ? [...contactSalesFormSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedContactSalesFormSubmitted = {
        ...contactSalesFormSubmitted,
        context,
    };

    trackContactSalesFormSubmitted<Record<string, unknown> | EventSpecification>(modifiedContactSalesFormSubmitted, trackers);
}
/**
 * Tracks a PatientSummaryCtaShown event specification.
 * ID: e1cda608-2737-4a1d-8df9-fc60acad7735
 */
export function trackPatientSummaryCtaShownSpec(patientSummaryCtaShown: PatientSummaryCtaShown & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e1cda608-2737-4a1d-8df9-fc60acad7735',
        version: 1,
        name: 'patient_summary_cta_shown',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(patientSummaryCtaShown.context)
        ? [...patientSummaryCtaShown.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientSummaryCtaShown = {
        ...patientSummaryCtaShown,
        context,
    };

    trackPatientSummaryCtaShown<Record<string, unknown> | EventSpecification>(modifiedPatientSummaryCtaShown, trackers);
}
/**
 * Tracks a WorkWorkflowActioned event specification.
 * ID: e1ceb2f0-5454-5d4e-9eaf-c951c3a15f51
 */
export function trackWorkWorkflowActionedSpec(workWorkflowActioned: WorkWorkflowActioned & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e1ceb2f0-5454-5d4e-9eaf-c951c3a15f51',
        version: 2,
        name: 'work_workflow_actioned',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workWorkflowActioned.context)
        ? [...workWorkflowActioned.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkWorkflowActioned = {
        ...workWorkflowActioned,
        context,
    };

    trackWorkWorkflowActioned<Record<string, unknown> | EventSpecification>(modifiedWorkWorkflowActioned, trackers);
}
/**
 * Tracks a CommunityRejected event specification.
 * ID: e27e7d56-3f30-42bd-84f3-e78665a79563
 */
export function trackCommunityRejectedSpec(communityRejected: CommunityRejected & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e27e7d56-3f30-42bd-84f3-e78665a79563',
        version: 0,
        name: 'community_rejected',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(communityRejected.context)
        ? [...communityRejected.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCommunityRejected = {
        ...communityRejected,
        context,
    };

    trackCommunityRejected<Record<string, unknown> | EventSpecification>(modifiedCommunityRejected, trackers);
}
/**
 * Tracks a PaymentCompleted event specification.
 * ID: e35f4b47-84ec-471d-8909-6c50a8b27c9f
 */
export function trackPaymentCompletedSpec(paymentCompleted: PaymentCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e35f4b47-84ec-471d-8909-6c50a8b27c9f',
        version: 4,
        name: 'payment_completed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(paymentCompleted.context)
        ? [...paymentCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPaymentCompleted = {
        ...paymentCompleted,
        context,
    };

    trackPaymentCompleted<Record<string, unknown> | EventSpecification>(modifiedPaymentCompleted, trackers);
}
/**
 * Tracks a TrialCompleted event specification.
 * ID: e422a057-30d0-4e84-8bc2-189d80db1da6
 */
export function trackTrialCompletedSpec(trialCompleted: TrialCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e422a057-30d0-4e84-8bc2-189d80db1da6',
        version: 2,
        name: 'trial_completed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(trialCompleted.context)
        ? [...trialCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTrialCompleted = {
        ...trialCompleted,
        context,
    };

    trackTrialCompleted<Record<string, unknown> | EventSpecification>(modifiedTrialCompleted, trackers);
}
/**
 * Tracks a ConversationAssigned event specification.
 * ID: e4b77ac6-34dd-4bad-9b27-1ed79c371874
 */
export function trackConversationAssignedSpec(conversationAssigned: ConversationAssigned & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e4b77ac6-34dd-4bad-9b27-1ed79c371874',
        version: 0,
        name: 'conversation_assigned',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(conversationAssigned.context)
        ? [...conversationAssigned.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedConversationAssigned = {
        ...conversationAssigned,
        context,
    };

    trackConversationAssigned<Record<string, unknown> | EventSpecification>(modifiedConversationAssigned, trackers);
}
/**
 * Tracks a VerificationFeedbackSubmitted event specification.
 * ID: e4c14e0d-9768-4767-bfc7-35990be720df
 */
export function trackVerificationFeedbackSubmittedSpec(verificationFeedbackSubmitted: VerificationFeedbackSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e4c14e0d-9768-4767-bfc7-35990be720df',
        version: 1,
        name: 'verification_feedback_submitted',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(verificationFeedbackSubmitted.context)
        ? [...verificationFeedbackSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedVerificationFeedbackSubmitted = {
        ...verificationFeedbackSubmitted,
        context,
    };

    trackVerificationFeedbackSubmitted<Record<string, unknown> | EventSpecification>(modifiedVerificationFeedbackSubmitted, trackers);
}
/**
 * Tracks a PatientContextToggleOn event specification.
 * ID: e4d8f2f2-1821-4480-a14b-22b981d62256
 */
export function trackPatientContextToggleOnSpec(patientContextToggleOn: PatientContextToggleOn & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e4d8f2f2-1821-4480-a14b-22b981d62256',
        version: 1,
        name: 'patient_context_toggle_on',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(patientContextToggleOn.context)
        ? [...patientContextToggleOn.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientContextToggleOn = {
        ...patientContextToggleOn,
        context,
    };

    trackPatientContextToggleOn<Record<string, unknown> | EventSpecification>(modifiedPatientContextToggleOn, trackers);
}
/**
 * Tracks a CommsEntryPointClicked event specification.
 * ID: e5117367-fb78-41bb-a510-562e8f97d285
 */
export function trackCommsEntryPointClickedSpec(commsEntryPointClicked: CommsEntryPointClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e5117367-fb78-41bb-a510-562e8f97d285',
        version: 0,
        name: 'comms_entry_point_clicked',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(commsEntryPointClicked.context)
        ? [...commsEntryPointClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCommsEntryPointClicked = {
        ...commsEntryPointClicked,
        context,
    };

    trackCommsEntryPointClicked<Record<string, unknown> | EventSpecification>(modifiedCommsEntryPointClicked, trackers);
}
/**
 * Tracks a EvidenceChatCtaClicked event specification.
 * ID: e51f1e8a-1931-4a2f-9226-a2940debb4ae
 */
export function trackEvidenceChatCtaClickedSpec(evidenceChatCtaClicked: EvidenceChatCtaClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e51f1e8a-1931-4a2f-9226-a2940debb4ae',
        version: 0,
        name: 'evidence_chat_cta_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceChatCtaClicked.context)
        ? [...evidenceChatCtaClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceChatCtaClicked = {
        ...evidenceChatCtaClicked,
        context,
    };

    trackEvidenceChatCtaClicked<Record<string, unknown> | EventSpecification>(modifiedEvidenceChatCtaClicked, trackers);
}
/**
 * Tracks a CpdDraftCreated event specification.
 * ID: e53f6493-725a-46ee-b1f6-7ebcd1262566
 */
export function trackCpdDraftCreatedSpec(cpdDraftCreated: CpdDraftCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e53f6493-725a-46ee-b1f6-7ebcd1262566',
        version: 1,
        name: 'cpd_draft_created',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(cpdDraftCreated.context)
        ? [...cpdDraftCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCpdDraftCreated = {
        ...cpdDraftCreated,
        context,
    };

    trackCpdDraftCreated<Record<string, unknown> | EventSpecification>(modifiedCpdDraftCreated, trackers);
}
/**
 * Tracks a ContextDeleted event specification.
 * ID: e6e99710-db8d-4ec3-a29f-239ea678fcfd
 */
export function trackContextDeletedSpec(contextDeleted: ContextDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e6e99710-db8d-4ec3-a29f-239ea678fcfd',
        version: 1,
        name: 'context_deleted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(contextDeleted.context)
        ? [...contextDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedContextDeleted = {
        ...contextDeleted,
        context,
    };

    trackContextDeleted<Record<string, unknown> | EventSpecification>(modifiedContextDeleted, trackers);
}
/**
 * Tracks a ResponseCopied event specification.
 * ID: e7aad124-1c21-43e1-88ec-f109fbaca5b8
 */
export function trackResponseCopiedSpec(responseCopied: ResponseCopied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e7aad124-1c21-43e1-88ec-f109fbaca5b8',
        version: 3,
        name: 'response_copied',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(responseCopied.context)
        ? [...responseCopied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedResponseCopied = {
        ...responseCopied,
        context,
    };

    trackResponseCopied<Record<string, unknown> | EventSpecification>(modifiedResponseCopied, trackers);
}
/**
 * Tracks a ShareMemberAdded event specification.
 * ID: e7d8c2e8-f03c-4244-875d-c1832230bba8
 */
export function trackShareMemberAddedSpec(shareMemberAdded: ShareMemberAdded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e7d8c2e8-f03c-4244-875d-c1832230bba8',
        version: 1,
        name: 'share_member_added',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareMemberAdded.context)
        ? [...shareMemberAdded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareMemberAdded = {
        ...shareMemberAdded,
        context,
    };

    trackShareMemberAdded<Record<string, unknown> | EventSpecification>(modifiedShareMemberAdded, trackers);
}
/**
 * Tracks a ObjectiveGuardrailTriggered event specification.
 * ID: e8ce50d7-b102-440c-9d2f-86e0524caf21
 */
export function trackObjectiveGuardrailTriggeredSpec(objectiveGuardrailTriggered: ObjectiveGuardrailTriggered & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e8ce50d7-b102-440c-9d2f-86e0524caf21',
        version: 0,
        name: 'objective_guardrail_triggered',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(objectiveGuardrailTriggered.context)
        ? [...objectiveGuardrailTriggered.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedObjectiveGuardrailTriggered = {
        ...objectiveGuardrailTriggered,
        context,
    };

    trackObjectiveGuardrailTriggered<Record<string, unknown> | EventSpecification>(modifiedObjectiveGuardrailTriggered, trackers);
}
/**
 * Tracks a BookDemoFormSubmitted event specification.
 * ID: e9a0a478-d90f-4cd6-a773-2de54d723f1a
 */
export function trackBookDemoFormSubmittedSpec(bookDemoFormSubmitted: BookDemoFormSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e9a0a478-d90f-4cd6-a773-2de54d723f1a',
        version: 1,
        name: 'book_demo_form_submitted',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(bookDemoFormSubmitted.context)
        ? [...bookDemoFormSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedBookDemoFormSubmitted = {
        ...bookDemoFormSubmitted,
        context,
    };

    trackBookDemoFormSubmitted<Record<string, unknown> | EventSpecification>(modifiedBookDemoFormSubmitted, trackers);
}
/**
 * Tracks a EvidenceSignUpWallViewed event specification.
 * ID: e9e8eddc-a895-47d0-887d-dd6abd585d5f
 */
export function trackEvidenceSignUpWallViewedSpec(evidenceSignUpWallViewed: EvidenceSignUpWallViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'e9e8eddc-a895-47d0-887d-dd6abd585d5f',
        version: 1,
        name: 'evidence_sign_up_wall_viewed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceSignUpWallViewed.context)
        ? [...evidenceSignUpWallViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceSignUpWallViewed = {
        ...evidenceSignUpWallViewed,
        context,
    };

    trackEvidenceSignUpWallViewed<Record<string, unknown> | EventSpecification>(modifiedEvidenceSignUpWallViewed, trackers);
}
/**
 * Tracks a DocumentCreated event specification.
 * ID: ea46236f-8d9a-4e33-ab5f-0d58cf517624
 */
export function trackDocumentCreatedSpec(documentCreated: DocumentCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ea46236f-8d9a-4e33-ab5f-0d58cf517624',
        version: 4,
        name: 'document_created',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(documentCreated.context)
        ? [...documentCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDocumentCreated = {
        ...documentCreated,
        context,
    };

    trackDocumentCreated<Record<string, unknown> | EventSpecification>(modifiedDocumentCreated, trackers);
}
/**
 * Tracks a IntegrationUserSearched event specification.
 * ID: ea9a4bbe-bd29-45c7-8a9a-9891ea077699
 */
export function trackIntegrationUserSearchedSpec(integrationUserSearched: IntegrationUserSearched & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ea9a4bbe-bd29-45c7-8a9a-9891ea077699',
        version: 1,
        name: 'integration_user_searched',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationUserSearched.context)
        ? [...integrationUserSearched.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationUserSearched = {
        ...integrationUserSearched,
        context,
    };

    trackIntegrationUserSearched<Record<string, unknown> | EventSpecification>(modifiedIntegrationUserSearched, trackers);
}
/**
 * Tracks a OutboundMessageFailed event specification.
 * ID: eb36ab09-ebe6-4f66-a7c0-17f7e4f98e2f
 */
export function trackOutboundMessageFailedSpec(outboundMessageFailed: OutboundMessageFailed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'eb36ab09-ebe6-4f66-a7c0-17f7e4f98e2f',
        version: 0,
        name: 'outbound_message_failed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(outboundMessageFailed.context)
        ? [...outboundMessageFailed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOutboundMessageFailed = {
        ...outboundMessageFailed,
        context,
    };

    trackOutboundMessageFailed<Record<string, unknown> | EventSpecification>(modifiedOutboundMessageFailed, trackers);
}
/**
 * Tracks a ShareLinkCopied event specification.
 * ID: eb47c8e4-e99a-40c9-acf7-1682bf550205
 */
export function trackShareLinkCopiedSpec(shareLinkCopied: ShareLinkCopied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'eb47c8e4-e99a-40c9-acf7-1682bf550205',
        version: 1,
        name: 'share_link_copied',
        data_product_id: '4895371f-86d2-41fd-a67d-2e852189cb62',
        data_product_name: 'collaboration_pod'
    });

    const context = Array.isArray(shareLinkCopied.context)
        ? [...shareLinkCopied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedShareLinkCopied = {
        ...shareLinkCopied,
        context,
    };

    trackShareLinkCopied<Record<string, unknown> | EventSpecification>(modifiedShareLinkCopied, trackers);
}
/**
 * Tracks a InboundCallRouted event specification.
 * ID: eb5a5ad2-af6b-445f-9472-8acd6d9db93d
 */
export function trackInboundCallRoutedSpec(inboundCallRouted: InboundCallRouted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'eb5a5ad2-af6b-445f-9472-8acd6d9db93d',
        version: 0,
        name: 'inbound_call_routed',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(inboundCallRouted.context)
        ? [...inboundCallRouted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedInboundCallRouted = {
        ...inboundCallRouted,
        context,
    };

    trackInboundCallRouted<Record<string, unknown> | EventSpecification>(modifiedInboundCallRouted, trackers);
}
/**
 * Tracks a UserSignedOut event specification.
 * ID: ebbe85d3-e4a3-4262-b8d7-b1a035ae57f6
 */
export function trackUserSignedOutSpec(userSignedOut: UserSignedOut & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ebbe85d3-e4a3-4262-b8d7-b1a035ae57f6',
        version: 2,
        name: 'user_signed_out',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(userSignedOut.context)
        ? [...userSignedOut.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedUserSignedOut = {
        ...userSignedOut,
        context,
    };

    trackUserSignedOut<Record<string, unknown> | EventSpecification>(modifiedUserSignedOut, trackers);
}
/**
 * Tracks a ForceUpdateViewed event specification.
 * ID: ec206b8c-b013-4ece-99da-5cc2583154e0
 */
export function trackForceUpdateViewedSpec(forceUpdateViewed: ForceUpdateViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ec206b8c-b013-4ece-99da-5cc2583154e0',
        version: 1,
        name: 'force_update_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(forceUpdateViewed.context)
        ? [...forceUpdateViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedForceUpdateViewed = {
        ...forceUpdateViewed,
        context,
    };

    trackForceUpdateViewed<Record<string, unknown> | EventSpecification>(modifiedForceUpdateViewed, trackers);
}
/**
 * Tracks a PatientDocumentUnlinked event specification.
 * ID: ec2da9d1-0aab-49b1-99b1-ed59f7ea2d60
 */
export function trackPatientDocumentUnlinkedSpec(patientDocumentUnlinked: PatientDocumentUnlinked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ec2da9d1-0aab-49b1-99b1-ed59f7ea2d60',
        version: 0,
        name: 'patient_document_unlinked',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(patientDocumentUnlinked.context)
        ? [...patientDocumentUnlinked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientDocumentUnlinked = {
        ...patientDocumentUnlinked,
        context,
    };

    trackPatientDocumentUnlinked<Record<string, unknown> | EventSpecification>(modifiedPatientDocumentUnlinked, trackers);
}
/**
 * Tracks a TemplateDeleted event specification.
 * ID: ec989dc1-1891-49c3-98f1-d114291b3171
 */
export function trackTemplateDeletedSpec(templateDeleted: TemplateDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ec989dc1-1891-49c3-98f1-d114291b3171',
        version: 5,
        name: 'template_deleted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(templateDeleted.context)
        ? [...templateDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTemplateDeleted = {
        ...templateDeleted,
        context,
    };

    trackTemplateDeleted<Record<string, unknown> | EventSpecification>(modifiedTemplateDeleted, trackers);
}
/**
 * Tracks a ConsultNoteGeneration event specification.
 * ID: ecc7ca39-088f-4aaf-bb29-322900236c1d
 */
export function trackConsultNoteGenerationSpec(consultNoteGeneration: ConsultNoteGeneration & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ecc7ca39-088f-4aaf-bb29-322900236c1d',
        version: 1,
        name: 'consult_note_generation',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(consultNoteGeneration.context)
        ? [...consultNoteGeneration.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedConsultNoteGeneration = {
        ...consultNoteGeneration,
        context,
    };

    trackConsultNoteGeneration<Record<string, unknown> | EventSpecification>(modifiedConsultNoteGeneration, trackers);
}
/**
 * Tracks a EvidenceToastShown event specification.
 * ID: ed47ceef-079f-4734-b2b6-5b010395e66c
 */
export function trackEvidenceToastShownSpec(evidenceToastShown: EvidenceToastShown & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ed47ceef-079f-4734-b2b6-5b010395e66c',
        version: 1,
        name: 'evidence_toast_shown',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceToastShown.context)
        ? [...evidenceToastShown.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceToastShown = {
        ...evidenceToastShown,
        context,
    };

    trackEvidenceToastShown<Record<string, unknown> | EventSpecification>(modifiedEvidenceToastShown, trackers);
}
/**
 * Tracks a ToastViewed event specification.
 * ID: edf45c9c-fd3a-494b-bac4-4726bee4f42a
 */
export function trackToastViewedSpec(toastViewed: ToastViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'edf45c9c-fd3a-494b-bac4-4726bee4f42a',
        version: 0,
        name: 'toast_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(toastViewed.context)
        ? [...toastViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedToastViewed = {
        ...toastViewed,
        context,
    };

    trackToastViewed<Record<string, unknown> | EventSpecification>(modifiedToastViewed, trackers);
}
/**
 * Tracks a CommunityViewed event specification.
 * ID: eeed9b5b-bec1-47cd-bb0c-61cc3fd574fd
 */
export function trackCommunityViewedSpec(communityViewed: CommunityViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'eeed9b5b-bec1-47cd-bb0c-61cc3fd574fd',
        version: 1,
        name: 'community_viewed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(communityViewed.context)
        ? [...communityViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCommunityViewed = {
        ...communityViewed,
        context,
    };

    trackCommunityViewed<Record<string, unknown> | EventSpecification>(modifiedCommunityViewed, trackers);
}
/**
 * Tracks a ErrorViewed event specification.
 * ID: ef0355e3-a4e5-4f4d-9a12-348e9d7b1732
 */
export function trackErrorViewedSpec(errorViewed: ErrorViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ef0355e3-a4e5-4f4d-9a12-348e9d7b1732',
        version: 2,
        name: 'error_viewed',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(errorViewed.context)
        ? [...errorViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedErrorViewed = {
        ...errorViewed,
        context,
    };

    trackErrorViewed<Record<string, unknown> | EventSpecification>(modifiedErrorViewed, trackers);
}
/**
 * Tracks a IntegrationCompleted event specification.
 * ID: ef1d3adc-3184-4e83-8008-d237753b2bd4
 */
export function trackIntegrationCompletedSpec(integrationCompleted: IntegrationCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ef1d3adc-3184-4e83-8008-d237753b2bd4',
        version: 1,
        name: 'integration_completed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationCompleted.context)
        ? [...integrationCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationCompleted = {
        ...integrationCompleted,
        context,
    };

    trackIntegrationCompleted<Record<string, unknown> | EventSpecification>(modifiedIntegrationCompleted, trackers);
}
/**
 * Tracks a ChatViewed event specification.
 * ID: ef38f036-0474-425c-ba5a-55d923b40aff
 */
export function trackChatViewedSpec(chatViewed: ChatViewed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ef38f036-0474-425c-ba5a-55d923b40aff',
        version: 1,
        name: 'chat_viewed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(chatViewed.context)
        ? [...chatViewed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChatViewed = {
        ...chatViewed,
        context,
    };

    trackChatViewed<Record<string, unknown> | EventSpecification>(modifiedChatViewed, trackers);
}
/**
 * Tracks a EvidenceShareModalOpened event specification.
 * ID: ef9900fb-e251-4466-b726-739657fa9a75
 */
export function trackEvidenceShareModalOpenedSpec(evidenceShareModalOpened: EvidenceShareModalOpened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ef9900fb-e251-4466-b726-739657fa9a75',
        version: 2,
        name: 'evidence_share_modal_opened',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceShareModalOpened.context)
        ? [...evidenceShareModalOpened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceShareModalOpened = {
        ...evidenceShareModalOpened,
        context,
    };

    trackEvidenceShareModalOpened<Record<string, unknown> | EventSpecification>(modifiedEvidenceShareModalOpened, trackers);
}
/**
 * Tracks a NoteSynced event specification.
 * ID: efb84862-4c07-4a3b-a7b8-59e22784a684
 */
export function trackNoteSyncedSpec(noteSynced: NoteSynced & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'efb84862-4c07-4a3b-a7b8-59e22784a684',
        version: 6,
        name: 'note_synced',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(noteSynced.context)
        ? [...noteSynced.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNoteSynced = {
        ...noteSynced,
        context,
    };

    trackNoteSynced<Record<string, unknown> | EventSpecification>(modifiedNoteSynced, trackers);
}
/**
 * Tracks a LibraryDocumentAddedToCollection event specification.
 * ID: f02bfacf-ec3d-4ff8-afd2-c36f33a1b3a8
 */
export function trackLibraryDocumentAddedToCollectionSpec(libraryDocumentAddedToCollection: LibraryDocumentAddedToCollection & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f02bfacf-ec3d-4ff8-afd2-c36f33a1b3a8',
        version: 1,
        name: 'library_document_added_to_collection',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(libraryDocumentAddedToCollection.context)
        ? [...libraryDocumentAddedToCollection.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedLibraryDocumentAddedToCollection = {
        ...libraryDocumentAddedToCollection,
        context,
    };

    trackLibraryDocumentAddedToCollection<Record<string, unknown> | EventSpecification>(modifiedLibraryDocumentAddedToCollection, trackers);
}
/**
 * Tracks a ChatCreated event specification.
 * ID: f038dd97-dea0-40ec-8929-f1c7abe0ac57
 */
export function trackChatCreatedSpec(chatCreated: ChatCreated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f038dd97-dea0-40ec-8929-f1c7abe0ac57',
        version: 2,
        name: 'chat_created',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(chatCreated.context)
        ? [...chatCreated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedChatCreated = {
        ...chatCreated,
        context,
    };

    trackChatCreated<Record<string, unknown> | EventSpecification>(modifiedChatCreated, trackers);
}
/**
 * Tracks a NudgeCardDismissed event specification.
 * ID: f09e110c-aa5f-4897-806d-a27ec40c8ed6
 */
export function trackNudgeCardDismissedSpec(nudgeCardDismissed: NudgeCardDismissed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f09e110c-aa5f-4897-806d-a27ec40c8ed6',
        version: 0,
        name: 'nudge_card_dismissed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(nudgeCardDismissed.context)
        ? [...nudgeCardDismissed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedNudgeCardDismissed = {
        ...nudgeCardDismissed,
        context,
    };

    trackNudgeCardDismissed<Record<string, unknown> | EventSpecification>(modifiedNudgeCardDismissed, trackers);
}
/**
 * Tracks a EvidenceVerbClicked event specification.
 * ID: f0c92b6e-1c83-48ea-a744-802c18b73c68
 */
export function trackEvidenceVerbClickedSpec(evidenceVerbClicked: EvidenceVerbClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f0c92b6e-1c83-48ea-a744-802c18b73c68',
        version: 1,
        name: 'evidence_verb_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceVerbClicked.context)
        ? [...evidenceVerbClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceVerbClicked = {
        ...evidenceVerbClicked,
        context,
    };

    trackEvidenceVerbClicked<Record<string, unknown> | EventSpecification>(modifiedEvidenceVerbClicked, trackers);
}
/**
 * Tracks a CpdActivityStarted event specification.
 * ID: f12d9a0d-d4f0-45d8-b833-801d708e9aca
 */
export function trackCpdActivityStartedSpec(cpdActivityStarted: CpdActivityStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f12d9a0d-d4f0-45d8-b833-801d708e9aca',
        version: 1,
        name: 'cpd_activity_started',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(cpdActivityStarted.context)
        ? [...cpdActivityStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCpdActivityStarted = {
        ...cpdActivityStarted,
        context,
    };

    trackCpdActivityStarted<Record<string, unknown> | EventSpecification>(modifiedCpdActivityStarted, trackers);
}
/**
 * @deprecated Outdated data structure detected.
 * Tracks a HardwareRecordingStart event specification.
 * ID: f1b28e63-4d97-4a0c-8e5f-a3c6d9b71e42
 */
export function trackHardwareRecordingStartSpec(hardwareRecordingStart: HardwareRecordingStart & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f1b28e63-4d97-4a0c-8e5f-a3c6d9b71e42',
        version: 1,
        name: 'hardware_recording_start',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(hardwareRecordingStart.context)
        ? [...hardwareRecordingStart.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedHardwareRecordingStart = {
        ...hardwareRecordingStart,
        context,
    };

    trackHardwareRecordingStart<Record<string, unknown> | EventSpecification>(modifiedHardwareRecordingStart, trackers);
}
/**
 * Tracks a DocumentCopied event specification.
 * ID: f29d4544-3179-41ee-942f-df62b7fbc6b2
 */
export function trackDocumentCopiedSpec(documentCopied: DocumentCopied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f29d4544-3179-41ee-942f-df62b7fbc6b2',
        version: 1,
        name: 'document_copied',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(documentCopied.context)
        ? [...documentCopied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDocumentCopied = {
        ...documentCopied,
        context,
    };

    trackDocumentCopied<Record<string, unknown> | EventSpecification>(modifiedDocumentCopied, trackers);
}
/**
 * Tracks a CpdDraftResumed event specification.
 * ID: f2e98c62-de68-4021-9413-0fffa727cbe7
 */
export function trackCpdDraftResumedSpec(cpdDraftResumed: CpdDraftResumed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f2e98c62-de68-4021-9413-0fffa727cbe7',
        version: 1,
        name: 'cpd_draft_resumed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(cpdDraftResumed.context)
        ? [...cpdDraftResumed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCpdDraftResumed = {
        ...cpdDraftResumed,
        context,
    };

    trackCpdDraftResumed<Record<string, unknown> | EventSpecification>(modifiedCpdDraftResumed, trackers);
}
/**
 * Tracks a PatientExtractionModalOpened event specification.
 * ID: f34bf0de-521b-4ccf-b71c-0ba842393fe8
 */
export function trackPatientExtractionModalOpenedSpec(patientExtractionModalOpened: PatientExtractionModalOpened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f34bf0de-521b-4ccf-b71c-0ba842393fe8',
        version: 1,
        name: 'patient_extraction_modal_opened',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(patientExtractionModalOpened.context)
        ? [...patientExtractionModalOpened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPatientExtractionModalOpened = {
        ...patientExtractionModalOpened,
        context,
    };

    trackPatientExtractionModalOpened<Record<string, unknown> | EventSpecification>(modifiedPatientExtractionModalOpened, trackers);
}
/**
 * Tracks a ComplianceCheck event specification.
 * ID: f3af95a5-6b5a-46a4-a44a-f5d9f7cc4982
 */
export function trackComplianceCheckSpec(complianceCheck: ComplianceCheck & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f3af95a5-6b5a-46a4-a44a-f5d9f7cc4982',
        version: 4,
        name: 'compliance_check',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(complianceCheck.context)
        ? [...complianceCheck.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedComplianceCheck = {
        ...complianceCheck,
        context,
    };

    trackComplianceCheck<Record<string, unknown> | EventSpecification>(modifiedComplianceCheck, trackers);
}
/**
 * Tracks a TranslatorOnboardingCompleted event specification.
 * ID: f49fd9b2-efa7-41c8-8292-171014d5361b
 */
export function trackTranslatorOnboardingCompletedSpec(translatorOnboardingCompleted: TranslatorOnboardingCompleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f49fd9b2-efa7-41c8-8292-171014d5361b',
        version: 0,
        name: 'translator_onboarding_completed',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(translatorOnboardingCompleted.context)
        ? [...translatorOnboardingCompleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTranslatorOnboardingCompleted = {
        ...translatorOnboardingCompleted,
        context,
    };

    trackTranslatorOnboardingCompleted<Record<string, unknown> | EventSpecification>(modifiedTranslatorOnboardingCompleted, trackers);
}
/**
 * Tracks a SubscriptionStarted event specification.
 * ID: f4d2fc83-7f34-4aaa-817a-8083ae68b525
 */
export function trackSubscriptionStartedSpec(subscriptionStarted: SubscriptionStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f4d2fc83-7f34-4aaa-817a-8083ae68b525',
        version: 3,
        name: 'subscription_started',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(subscriptionStarted.context)
        ? [...subscriptionStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSubscriptionStarted = {
        ...subscriptionStarted,
        context,
    };

    trackSubscriptionStarted<Record<string, unknown> | EventSpecification>(modifiedSubscriptionStarted, trackers);
}
/**
 * Tracks a IntegrationLinked event specification.
 * ID: f4df4ec0-3028-49b4-9d6c-7f364530349b
 */
export function trackIntegrationLinkedSpec(integrationLinked: IntegrationLinked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f4df4ec0-3028-49b4-9d6c-7f364530349b',
        version: 2,
        name: 'integration_linked',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(integrationLinked.context)
        ? [...integrationLinked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedIntegrationLinked = {
        ...integrationLinked,
        context,
    };

    trackIntegrationLinked<Record<string, unknown> | EventSpecification>(modifiedIntegrationLinked, trackers);
}
/**
 * Tracks a PushWarningResolved event specification.
 * ID: f671eff1-314d-4c41-bd5f-00897d7a0b84
 */
export function trackPushWarningResolvedSpec(pushWarningResolved: PushWarningResolved & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f671eff1-314d-4c41-bd5f-00897d7a0b84',
        version: 0,
        name: 'push_warning_resolved',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(pushWarningResolved.context)
        ? [...pushWarningResolved.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPushWarningResolved = {
        ...pushWarningResolved,
        context,
    };

    trackPushWarningResolved<Record<string, unknown> | EventSpecification>(modifiedPushWarningResolved, trackers);
}
/**
 * Tracks a EvidenceChatAborted event specification.
 * ID: f72ba2e4-f03a-4aec-9f9e-cbc662ed7f56
 */
export function trackEvidenceChatAbortedSpec(evidenceChatAborted: EvidenceChatAborted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f72ba2e4-f03a-4aec-9f9e-cbc662ed7f56',
        version: 4,
        name: 'evidence_chat_aborted',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceChatAborted.context)
        ? [...evidenceChatAborted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceChatAborted = {
        ...evidenceChatAborted,
        context,
    };

    trackEvidenceChatAborted<Record<string, unknown> | EventSpecification>(modifiedEvidenceChatAborted, trackers);
}
/**
 * Tracks a SessionSearchUsageBaseline event specification.
 * ID: f743e87f-86bc-4851-9ab0-d38428771d28
 */
export function trackSessionSearchUsageBaselineSpec(sessionSearchUsageBaseline: SessionSearchUsageBaseline & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f743e87f-86bc-4851-9ab0-d38428771d28',
        version: 1,
        name: 'session_search_usage_baseline',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(sessionSearchUsageBaseline.context)
        ? [...sessionSearchUsageBaseline.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedSessionSearchUsageBaseline = {
        ...sessionSearchUsageBaseline,
        context,
    };

    trackSessionSearchUsageBaseline<Record<string, unknown> | EventSpecification>(modifiedSessionSearchUsageBaseline, trackers);
}
/**
 * Tracks a PanelOpened event specification.
 * ID: f7ab16f6-e935-421a-8dd0-f4490c360291
 */
export function trackPanelOpenedSpec(panelOpened: PanelOpened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f7ab16f6-e935-421a-8dd0-f4490c360291',
        version: 3,
        name: 'panel_opened',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(panelOpened.context)
        ? [...panelOpened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedPanelOpened = {
        ...panelOpened,
        context,
    };

    trackPanelOpened<Record<string, unknown> | EventSpecification>(modifiedPanelOpened, trackers);
}
/**
 * Tracks a VerificationStarted event specification.
 * ID: f887ad51-fa0d-456f-90ca-608729a8bbf9
 */
export function trackVerificationStartedSpec(verificationStarted: VerificationStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f887ad51-fa0d-456f-90ca-608729a8bbf9',
        version: 1,
        name: 'verification_started',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(verificationStarted.context)
        ? [...verificationStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedVerificationStarted = {
        ...verificationStarted,
        context,
    };

    trackVerificationStarted<Record<string, unknown> | EventSpecification>(modifiedVerificationStarted, trackers);
}
/**
 * Tracks a ConversationReopened event specification.
 * ID: f8a5072f-76e1-40a3-af0d-4f9769239cfd
 */
export function trackConversationReopenedSpec(conversationReopened: ConversationReopened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'f8a5072f-76e1-40a3-af0d-4f9769239cfd',
        version: 0,
        name: 'conversation_reopened',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(conversationReopened.context)
        ? [...conversationReopened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedConversationReopened = {
        ...conversationReopened,
        context,
    };

    trackConversationReopened<Record<string, unknown> | EventSpecification>(modifiedConversationReopened, trackers);
}
/**
 * Tracks a EvidenceSharedChatOpened event specification.
 * ID: fa0e8c4b-750f-4b4e-a72d-6cf251e7663c
 */
export function trackEvidenceSharedChatOpenedSpec(evidenceSharedChatOpened: EvidenceSharedChatOpened & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'fa0e8c4b-750f-4b4e-a72d-6cf251e7663c',
        version: 2,
        name: 'evidence_shared_chat_opened',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceSharedChatOpened.context)
        ? [...evidenceSharedChatOpened.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceSharedChatOpened = {
        ...evidenceSharedChatOpened,
        context,
    };

    trackEvidenceSharedChatOpened<Record<string, unknown> | EventSpecification>(modifiedEvidenceSharedChatOpened, trackers);
}
/**
 * Tracks a AppStoreReviewPromptRequested event specification.
 * ID: fa506b13-6062-421c-89e8-edb96069d5cc
 */
export function trackAppStoreReviewPromptRequestedSpec(appStoreReviewPromptRequested: AppStoreReviewPromptRequested & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'fa506b13-6062-421c-89e8-edb96069d5cc',
        version: 0,
        name: 'app_store_review_prompt_requested',
        data_product_id: 'cf4f8070-3de5-496f-bed5-5c893b0a27b2',
        data_product_name: 'growth'
    });

    const context = Array.isArray(appStoreReviewPromptRequested.context)
        ? [...appStoreReviewPromptRequested.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAppStoreReviewPromptRequested = {
        ...appStoreReviewPromptRequested,
        context,
    };

    trackAppStoreReviewPromptRequested<Record<string, unknown> | EventSpecification>(modifiedAppStoreReviewPromptRequested, trackers);
}
/**
 * Tracks a DocumentGenerated event specification.
 * ID: fa518699-fd5b-4bd1-a7c6-781cf5032f59
 */
export function trackDocumentGeneratedSpec(documentGenerated: DocumentGenerated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'fa518699-fd5b-4bd1-a7c6-781cf5032f59',
        version: 1,
        name: 'document_generated',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(documentGenerated.context)
        ? [...documentGenerated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDocumentGenerated = {
        ...documentGenerated,
        context,
    };

    trackDocumentGenerated<Record<string, unknown> | EventSpecification>(modifiedDocumentGenerated, trackers);
}
/**
 * Tracks a DoNotContactAdded event specification.
 * ID: fa5666a9-b610-4171-bede-c2d396ff1fc2
 */
export function trackDoNotContactAddedSpec(doNotContactAdded: DoNotContactAdded & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'fa5666a9-b610-4171-bede-c2d396ff1fc2',
        version: 0,
        name: 'do_not_contact_added',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(doNotContactAdded.context)
        ? [...doNotContactAdded.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedDoNotContactAdded = {
        ...doNotContactAdded,
        context,
    };

    trackDoNotContactAdded<Record<string, unknown> | EventSpecification>(modifiedDoNotContactAdded, trackers);
}
/**
 * Tracks a EvidenceRequestAccessClicked event specification.
 * ID: fa57041b-1467-4873-a65e-fa631868d74d
 */
export function trackEvidenceRequestAccessClickedSpec(evidenceRequestAccessClicked: EvidenceRequestAccessClicked & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'fa57041b-1467-4873-a65e-fa631868d74d',
        version: 1,
        name: 'evidence_request_access_clicked',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceRequestAccessClicked.context)
        ? [...evidenceRequestAccessClicked.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceRequestAccessClicked = {
        ...evidenceRequestAccessClicked,
        context,
    };

    trackEvidenceRequestAccessClicked<Record<string, unknown> | EventSpecification>(modifiedEvidenceRequestAccessClicked, trackers);
}
/**
 * Tracks a TranslatorMessageInteraction event specification.
 * ID: fa70e08e-c945-4bd4-a145-8493ab39cadb
 */
export function trackTranslatorMessageInteractionSpec(translatorMessageInteraction: TranslatorMessageInteraction & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'fa70e08e-c945-4bd4-a145-8493ab39cadb',
        version: 0,
        name: 'translator_message_interaction',
        data_product_id: 'c8b267b4-9d23-4287-9efc-5df087f2bb1e',
        data_product_name: 'integrations'
    });

    const context = Array.isArray(translatorMessageInteraction.context)
        ? [...translatorMessageInteraction.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedTranslatorMessageInteraction = {
        ...translatorMessageInteraction,
        context,
    };

    trackTranslatorMessageInteraction<Record<string, unknown> | EventSpecification>(modifiedTranslatorMessageInteraction, trackers);
}
/**
 * Tracks a WorkMessageActioned event specification.
 * ID: fa9add3d-d6d8-5865-950b-c0bbb538d333
 */
export function trackWorkMessageActionedSpec(workMessageActioned: WorkMessageActioned & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'fa9add3d-d6d8-5865-950b-c0bbb538d333',
        version: 0,
        name: 'work_message_actioned',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workMessageActioned.context)
        ? [...workMessageActioned.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkMessageActioned = {
        ...workMessageActioned,
        context,
    };

    trackWorkMessageActioned<Record<string, unknown> | EventSpecification>(modifiedWorkMessageActioned, trackers);
}
/**
 * Tracks a WorkArtifactActioned event specification.
 * ID: faa6755e-d01d-5425-9219-0f42b45c75db
 */
export function trackWorkArtifactActionedSpec(workArtifactActioned: WorkArtifactActioned & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'faa6755e-d01d-5425-9219-0f42b45c75db',
        version: 1,
        name: 'work_artifact_actioned',
        data_product_id: '8e4a4e46-ba1b-480c-a20a-d54fe77a6acd',
        data_product_name: 'agents'
    });

    const context = Array.isArray(workArtifactActioned.context)
        ? [...workArtifactActioned.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedWorkArtifactActioned = {
        ...workArtifactActioned,
        context,
    };

    trackWorkArtifactActioned<Record<string, unknown> | EventSpecification>(modifiedWorkArtifactActioned, trackers);
}
/**
 * Tracks a HaikuTabChanged event specification.
 * ID: fb562ca3-17c8-4b9e-9555-8e222584386f
 */
export function trackHaikuTabChangedSpec(haikuTabChanged: HaikuTabChanged & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'fb562ca3-17c8-4b9e-9555-8e222584386f',
        version: 1,
        name: 'haiku_tab_changed',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(haikuTabChanged.context)
        ? [...haikuTabChanged.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedHaikuTabChanged = {
        ...haikuTabChanged,
        context,
    };

    trackHaikuTabChanged<Record<string, unknown> | EventSpecification>(modifiedHaikuTabChanged, trackers);
}
/**
 * Tracks a OfflineRecordingStarted event specification.
 * ID: fc827302-2bc0-49f2-9bf2-db465376219f
 */
export function trackOfflineRecordingStartedSpec(offlineRecordingStarted: OfflineRecordingStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'fc827302-2bc0-49f2-9bf2-db465376219f',
        version: 1,
        name: 'offline_recording_started',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(offlineRecordingStarted.context)
        ? [...offlineRecordingStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedOfflineRecordingStarted = {
        ...offlineRecordingStarted,
        context,
    };

    trackOfflineRecordingStarted<Record<string, unknown> | EventSpecification>(modifiedOfflineRecordingStarted, trackers);
}
/**
 * Tracks a InboundMessageReceived event specification.
 * ID: fcb82bc1-c39d-485e-989e-44b38ed34dee
 */
export function trackInboundMessageReceivedSpec(inboundMessageReceived: InboundMessageReceived & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'fcb82bc1-c39d-485e-989e-44b38ed34dee',
        version: 0,
        name: 'inbound_message_received',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(inboundMessageReceived.context)
        ? [...inboundMessageReceived.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedInboundMessageReceived = {
        ...inboundMessageReceived,
        context,
    };

    trackInboundMessageReceived<Record<string, unknown> | EventSpecification>(modifiedInboundMessageReceived, trackers);
}
/**
 * Tracks a EvidenceExperienceFeedbackDismissed event specification.
 * ID: fcf70be5-eedc-4586-83a3-2629ca3b9466
 */
export function trackEvidenceExperienceFeedbackDismissedSpec(evidenceExperienceFeedbackDismissed: EvidenceExperienceFeedbackDismissed & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'fcf70be5-eedc-4586-83a3-2629ca3b9466',
        version: 3,
        name: 'evidence_experience_feedback_dismissed',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceExperienceFeedbackDismissed.context)
        ? [...evidenceExperienceFeedbackDismissed.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceExperienceFeedbackDismissed = {
        ...evidenceExperienceFeedbackDismissed,
        context,
    };

    trackEvidenceExperienceFeedbackDismissed<Record<string, unknown> | EventSpecification>(modifiedEvidenceExperienceFeedbackDismissed, trackers);
}
/**
 * Tracks a CallInitiated event specification.
 * ID: fd068bae-9981-433f-ae06-3caffbf71ede
 */
export function trackCallInitiatedSpec(callInitiated: CallInitiated & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'fd068bae-9981-433f-ae06-3caffbf71ede',
        version: 1,
        name: 'call_initiated',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(callInitiated.context)
        ? [...callInitiated.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCallInitiated = {
        ...callInitiated,
        context,
    };

    trackCallInitiated<Record<string, unknown> | EventSpecification>(modifiedCallInitiated, trackers);
}
/**
 * Tracks a AuthManualLinkCopied event specification.
 * ID: feaf24c1-af5f-4e71-a9ea-c012e5837e63
 */
export function trackAuthManualLinkCopiedSpec(authManualLinkCopied: AuthManualLinkCopied & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'feaf24c1-af5f-4e71-a9ea-c012e5837e63',
        version: 1,
        name: 'auth_manual_link_copied',
        data_product_id: '72bf08a4-4c26-4be8-ab05-5af45d920919',
        data_product_name: 'enterprise_core'
    });

    const context = Array.isArray(authManualLinkCopied.context)
        ? [...authManualLinkCopied.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedAuthManualLinkCopied = {
        ...authManualLinkCopied,
        context,
    };

    trackAuthManualLinkCopied<Record<string, unknown> | EventSpecification>(modifiedAuthManualLinkCopied, trackers);
}
/**
 * Tracks a EvidenceNhsContactSalesSubmitted event specification.
 * ID: ff084ea8-77da-4cb5-8517-b00ec20f8d66
 */
export function trackEvidenceNhsContactSalesSubmittedSpec(evidenceNhsContactSalesSubmitted: EvidenceNhsContactSalesSubmitted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ff084ea8-77da-4cb5-8517-b00ec20f8d66',
        version: 3,
        name: 'evidence_nhs_contact_sales_submitted',
        data_product_id: '55c3ff1a-524d-4d4f-8413-267c7e163c13',
        data_product_name: 'models'
    });

    const context = Array.isArray(evidenceNhsContactSalesSubmitted.context)
        ? [...evidenceNhsContactSalesSubmitted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedEvidenceNhsContactSalesSubmitted = {
        ...evidenceNhsContactSalesSubmitted,
        context,
    };

    trackEvidenceNhsContactSalesSubmitted<Record<string, unknown> | EventSpecification>(modifiedEvidenceNhsContactSalesSubmitted, trackers);
}
/**
 * Tracks a CommsSetupStarted event specification.
 * ID: ff2b0c7a-5b15-4855-b401-a30de77c5d6a
 */
export function trackCommsSetupStartedSpec(commsSetupStarted: CommsSetupStarted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'ff2b0c7a-5b15-4855-b401-a30de77c5d6a',
        version: 0,
        name: 'comms_setup_started',
        data_product_id: '497f5932-cdc8-43f6-8853-ce00e8b9d7cc',
        data_product_name: 'comms'
    });

    const context = Array.isArray(commsSetupStarted.context)
        ? [...commsSetupStarted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedCommsSetupStarted = {
        ...commsSetupStarted,
        context,
    };

    trackCommsSetupStarted<Record<string, unknown> | EventSpecification>(modifiedCommsSetupStarted, trackers);
}
/**
 * Tracks a ContextFileDeleted event specification.
 * ID: fff300f2-06ee-4e87-83c8-59e8c144acd7
 */
export function trackContextFileDeletedSpec(contextFileDeleted: ContextFileDeleted & ContextsOrTimestamp, trackers?: string[]){
    const eventSpecificationContext = createEventSpecification({
        id: 'fff300f2-06ee-4e87-83c8-59e8c144acd7',
        version: 1,
        name: 'context_file_deleted',
        data_product_id: 'aa18cf9f-fa01-4fbc-b85e-e7be31721ed5',
        data_product_name: 'scribe'
    });

    const context = Array.isArray(contextFileDeleted.context)
        ? [...contextFileDeleted.context, eventSpecificationContext]
        : [eventSpecificationContext];

    const modifiedContextFileDeleted = {
        ...contextFileDeleted,
        context,
    };

    trackContextFileDeleted<Record<string, unknown> | EventSpecification>(modifiedContextFileDeleted, trackers);
}


